import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { store as googlePlayStore } from 'capacitor-plugin-cdv-purchase';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import CoinIcon from './CoinIcon';
import './App.css';
import { supabase } from './supabase';
import { LEGAL_INFO, isLegalPlaceholder } from './config/legalInfo';
import {
  getGooglePlayProducts,
  initializeGooglePlayBilling,
  purchaseGooglePlaySubscription,
  purchaseGooglePlayCoins,
  restoreGooglePlayPurchases,
} from './services/googlePlayBilling';

const OVERLAY_S = { position:'fixed', top:0, left:0, right:0, bottom:0, width:'100vw', height:'100vh', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.85)', padding:'20px' };
const MODAL_S   = { position:'relative', top:'auto', left:'auto', transform:'none', background:'#0d1117', borderRadius:20, padding:'32px 24px', width:'100%', maxWidth:380, maxHeight:'85vh', overflowY:'auto', textAlign:'center' };
const TRAVELSOUL_LOGO_SRC = '/logo-travelsoul.svg';
const PROFILE_PHOTO_BUCKET = 'profile-photos';

// ── App store links (landing page download section) ──────────────────────────
// Google Play is live; App Store is set to null until the iOS app is
// actually published and a real App Store ID exists — set it then
// (e.g. 'https://apps.apple.com/app/idXXXXXXXXXX'). Nothing else needs to
// change: the QR code and button below already react to this being set.
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.iviprodevelopment.travelsoul';
const APP_STORE_URL = null;

// Audit finding: VideoCallOverlay called supabase.channel('video-calls').send(...)
// to fire its two one-off broadcasts (incoming-call, video-call-ended), but
// .channel() always creates a NEW channel object — never subscribed, never
// removed — so every call start/end leaked one realtime channel for the rest
// of the session. A broadcast-only sender needs exactly one shared channel,
// reused for the page's lifetime; MainApp's separate subscribed `callChannel`
// (for *receiving* these events) already follows the correct subscribe/
// removeChannel lifecycle and is untouched by this.
let _videoCallBroadcastChannel = null;
function getVideoCallBroadcastChannel() {
  if (!supabase) return null;
  if (!_videoCallBroadcastChannel) {
    _videoCallBroadcastChannel = supabase.channel('video-calls');
  }
  return _videoCallBroadcastChannel;
}

// ── Gender-aware DNA trait labels ────────────────────────────────────────────
const TRAVEL_DNA_BG = {
  explorer:   { m: 'Изследовател',  f: 'Изследователка' },
  adventurer: { m: 'Авантюрист',    f: 'Авантюристка' },
  culturist:  { m: 'Културист',     f: 'Културистка' },
  nomad:      { m: 'Номад',         f: 'Номадка' },
};
const TRAVEL_DNA_ES = {
  explorer:   { m: 'Explorador',    f: 'Exploradora' },
  adventurer: { m: 'Aventurero',    f: 'Aventurera' },
  culturist:  { m: 'Culturista',    f: 'Culturista' },
  nomad:      { m: 'Nómada',        f: 'Nómada' },
};
const TRAVEL_DNA_FR = {
  explorer:   { m: 'Explorateur',   f: 'Exploratrice' },
  adventurer: { m: 'Aventurier',    f: 'Aventurière' },
  culturist:  { m: 'Culturiste',    f: 'Culturiste' },
  nomad:      { m: 'Nomade',        f: 'Nomade' },
};
const TRAVEL_DNA_EL = {
  explorer:   { m: 'Εξερευνητής',  f: 'Εξερευνήτρια' },
  adventurer: { m: 'Περιπετειώδης', f: 'Περιπετειώδης' },
  culturist:  { m: 'Λάτρης πολιτισμού', f: 'Λάτρις πολιτισμού' },
  nomad:      { m: 'Νομάς',        f: 'Νομάδισσα' },
};
const TRAVEL_DNA_IT = {
  explorer:   { m: 'Esploratore',  f: 'Esploratrice' },
  adventurer: { m: 'Avventuriero', f: 'Avventuriera' },
  culturist:  { m: 'Culturale',    f: 'Culturale' },
  nomad:      { m: 'Nomade',       f: 'Nomade' },
};
function getDnaTraitLabel(k, profile, lang, t) {
  const isFemale = (profile?.gender === 'female' || profile?.gender === 'f' || profile?.gender === 'жена');
  if (lang === 'bg' && TRAVEL_DNA_BG[k]) return isFemale ? TRAVEL_DNA_BG[k].f : TRAVEL_DNA_BG[k].m;
  if (lang === 'es' && TRAVEL_DNA_ES[k]) return isFemale ? TRAVEL_DNA_ES[k].f : TRAVEL_DNA_ES[k].m;
  if (lang === 'fr' && TRAVEL_DNA_FR[k]) return isFemale ? TRAVEL_DNA_FR[k].f : TRAVEL_DNA_FR[k].m;
  if (lang === 'el' && TRAVEL_DNA_EL[k]) return isFemale ? TRAVEL_DNA_EL[k].f : TRAVEL_DNA_EL[k].m;
  if (lang === 'it' && TRAVEL_DNA_IT[k]) return isFemale ? TRAVEL_DNA_IT[k].f : TRAVEL_DNA_IT[k].m;
  return t?.dna?.traits?.[k] || k;
}

// ── Audio ─────────────────────────────────────────────────────────────────────
function playSound(type) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();

  if (type === 'calling') {
    function ring() {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 440;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    }
    ring();
    setTimeout(ring, 800);
    setTimeout(ring, 1600);
  }

  if (type === 'connected') {
    [523, 659].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.3);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.3);
    });
  }

  if (type === 'endcall') {
    [440, 349, 262].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.2);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.2);
    });
  }

  if (type === 'message') {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  }

  if (type === 'match') {
    [523, 659, 784, 1047].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.1 + 0.25);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + i * 0.1 + 0.25);
    });
  }
}

function startIncomingCallRingtone() {
  if (typeof window === 'undefined') return () => {};
  let stopped = false;
  let intervalId = null;
  let retryBound = false;
  const events = ['pointerdown', 'touchstart', 'keydown'];

  const removeRetry = () => {
    if (!retryBound) return;
    events.forEach(evt => window.removeEventListener(evt, start, true));
    retryBound = false;
  };
  const addRetry = () => {
    if (retryBound || stopped) return;
    events.forEach(evt => window.addEventListener(evt, start, { once: true, capture: true }));
    retryBound = true;
  };
  function ringOnce() {
    try {
      playSound('calling');
      return true;
    } catch (e) {
      return false;
    }
  }
  function start() {
    removeRetry();
    if (stopped) return;
    if (!ringOnce()) {
      addRetry();
      return;
    }
    if (!intervalId) intervalId = setInterval(() => {
      if (!ringOnce()) addRetry();
    }, 2800);
  }

  start();
  return () => {
    stopped = true;
    removeRetry();
    if (intervalId) clearInterval(intervalId);
  };
}

// ── Language detection ────────────────────────────────────────────────────────
const VALID_LANGS = ['bg', 'en', 'es', 'fr', 'de', 'el', 'it'];

function detectLang() {
  const code = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return VALID_LANGS.includes(code) ? code : 'en';
}

function deepMerge(base, override = {}) {
  if (Array.isArray(base) || Array.isArray(override)) return override ?? base;
  if (!base || typeof base !== 'object') return override ?? base;
  const merged = { ...base };
  Object.keys(override || {}).forEach(key => {
    merged[key] = deepMerge(base[key], override[key]);
  });
  return merged;
}

function responseLanguageName(lang) {
  return {
    bg: 'Bulgarian',
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    el: 'Greek',
    it: 'Italian',
  }[lang] || 'English';
}

function localeForLang(lang) {
  return {
    bg: 'bg-BG',
    en: 'en-GB',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    el: 'el-GR',
    it: 'it-IT',
  }[lang] || 'en-GB';
}

// ── Safe localStorage helpers ─────────────────────────────────────────────────
function safeGetJson(key, fallback = null) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
// eslint-disable-next-line no-unused-vars
function safeSetJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}
// Audit finding: the full profile object — including free-text bio — was
// mirrored into localStorage in plaintext, readable by any script or
// browser extension with page access. Every write to 'travelsoul_profile'
// goes through this helper so bio is always stripped first (also purges
// any bio a browser already has stored from before this fix, since every
// write site spreads the previous stored object). Supabase is the real
// source of truth and ProfileScreen re-hydrates bio from there on mount.
function setProfileLocalStorage(value) {
  const { bio, ...rest } = value || {};
  try { localStorage.setItem('travelsoul_profile', JSON.stringify(rest)); } catch {}
}
function safeRemoveTravelSoulData() {
  // Audit finding (4th audit): this list omitted travelsoul_coins,
  // ts_likes_today, travelsoul_onboarded, travelsoul_onboarding_data, and
  // ts_pending_profile — so on logout/account switch on the same browser,
  // the next user to sign in could briefly (or indefinitely, for
  // travelsoul_onboarded, which is never reset) see the PREVIOUS user's coin
  // balance, daily-like count, or skip onboarding entirely. Also a separate
  // SIGNED_OUT auth-event handler only removed 'travelsoul_user' — a second,
  // inconsistent cleanup path. Fixed: this is now the single source of truth
  // for what gets cleared, called from both places.
  const keys = [
    'travelsoul_user','travelsoul_profile','travelsoul_photo',
    'travelsoul_email','travelsoul_pass',
    'travelsoul_password','ts_plan',
    'ts_last_spin','ts_spin_history','ts_incognito','ts_horoscope','ts_horoscope_sign',
    'ts_mood_travel','travelsoul_saved_trips','travelsoul_blocked',
    'travelsoul_coins','ts_likes_today','travelsoul_onboarded',
    'travelsoul_onboarding_data','ts_pending_profile',
  ];
  keys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
  try { localStorage.removeItem('ts_translation_cache'); } catch {}
  try { Object.keys(localStorage).filter(k => k.startsWith('ts_phrases_')).forEach(k => localStorage.removeItem(k)); } catch {}
}

// ── Avatar URL helper ─────────────────────────────────────────────────────────
function getStoragePublicUrl(bucket, path) {
  if (!supabase) return '/default-avatar.png';
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl ? `${data.publicUrl}?t=${Date.now()}` : '/default-avatar.png';
}

function getAvatarUrl(path) {
  if (!path) return '/default-avatar.png';
  if (path.startsWith('data:')) return path; // base64 — no cache-busting needed
  if (path.startsWith('http')) {
    const clean = path.split('?')[0]; // strip stale ?t= params
    return `${clean}?t=${Date.now()}`;
  }
  return getStoragePublicUrl('avatars', path);
}

function getProfilePhotoUrl(path, fallback = '') {
  if (!path) return fallback;
  const url = (path.startsWith('data:') || path.startsWith('http'))
    ? getAvatarUrl(path)
    : getStoragePublicUrl(PROFILE_PHOTO_BUCKET, path);
  return url === '/default-avatar.png' ? fallback : url;
}

function getDisplayProfilePhoto(profile, fallback = '') {
  return profile?.avatar_url || profile?.photo || getProfilePhotoUrl(profile?.photo_url, fallback);
}

function spinLastDateKey(profileId) {
  return profileId ? `ts_last_spin:${profileId}` : 'ts_last_spin:anonymous';
}

function spinHistoryKey(profileId) {
  return profileId ? `ts_spin_history:${profileId}` : 'ts_spin_history:anonymous';
}

function getStoredSpinLastDate(profileId) {
  try { return localStorage.getItem(spinLastDateKey(profileId)); } catch { return null; }
}

function setStoredSpinLastDate(profileId, value) {
  try {
    const key = spinLastDateKey(profileId);
    if (value) localStorage.setItem(key, String(value).slice(0, 10));
    else localStorage.removeItem(key);
  } catch {}
}

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  bg: {
    brand: 'ТравелСоул',
    tagline: ['Намери своята душа', 'чрез пътуването', 'Открийте света заедно'],
    cta: 'Започни пътуването', login: 'Влез',
    features: [
      { icon: '🧬', title: 'Пътнически ДНК', desc: 'Уникален профил на твоята пътническа личност' },
      { icon: '♥', title: 'Съвместимост', desc: 'Срещни пътешественици, чиито мечти съвпадат с твоите' },
      { icon: '✈', title: 'Групови пътувания', desc: 'Присъедини се към внимателно подбрани приключения' },
      { icon: '🛡', title: 'Сигурност', desc: 'Твоята поверителност е наш приоритет номер едно' },
    ],
    nav: { explore: 'Изследвай', connect: 'Свържи се', journeys: 'Пътувания', messages: 'Чат' },
    register: { title: 'Присъедини се', sub: 'Твоето пътуване започва тук', name: 'Пълно име', email: 'Имейл адрес', password: 'Парола', confirmPassword: 'Потвърди парола', passwordMismatch: 'Паролите не съвпадат', passwordMatch: 'Паролите съвпадат ✅', weakPassword: 'Слаба парола', mediumPassword: 'Средна парола', strongPassword: 'Силна парола', country: 'Избери държава', submit: 'Създай профил', login: 'Вече имаш акаунт? Влез', birthDate:'Дата на раждане', ageError:'Трябва да си на 18+ години', selectBirthDate:'Избери дата на раждане' },
    ageVerification: { title: 'Потвърждение на възраст', msg: 'Трябва да потвърдим, че сте на 18+ години, за да продължите да ползвате TravelSoul.', submit: 'Потвърди', saving: 'Запазване...', underageTitle: 'Съжаляваме', underageMsg: 'TravelSoul е достъпен само за потребители на 18+ години. Не можете да продължите да ползвате приложението.', signOut: 'Изход' },
    loginScreen: { title: 'Добре дошъл обратно', sub: 'Влез в профила си', email: 'Имейл адрес', password: 'Парола', submit: 'Влез', register: 'Нямаш акаунт? Регистрирай се', forgot: 'Забравена парола?', resetSent: '✅ Изпратихме имейл за нулиране!', error: 'Грешен имейл или парола', wrongPassword: 'Грешен имейл или парола' },
    explore: { title: 'Открий пътувания', spots: 'свободни места', join: 'Присъедини се', hot: 'Горещо', days: 'дни', aiPicks: 'ПРЕПОРЪЧАНИ ДЕСТИНАЦИИ', refresh: 'Открий още', host: 'Домакин', joined: '✓ Присъединен', aiRecommended: '🤖 Препоръчано' },
    connect: {
      title: 'Намери душата си', sub: 'пътешественика онлайн сега',
      online: 'онлайн', offline: 'офлайн', like: 'Харесай', liked: 'Харесано', pass: 'Пропусни', match: 'Съвместимост', dnaSection: 'Твоят пътнически ДНК', // pass button label - MUST BE: Пропусни (not Пропуски)
      whoViewed: 'Кой е разгледал профила ти', swipeMode: 'Плъзгане', gridMode: 'Мрежа',
      seenAll: 'Видя всички! 🎉', superLikeSent: '✨ Супер харесване изпратено!', itsAMatch: '💫 Съвпадение!',
      sendMessage: 'Изпрати съобщение', keepSwiping: 'Продължи',
      keepGoing: 'Продължи', sendMsg: 'Изпрати съобщение', passedToast: 'Пропуснат ✓',
      boost: 'Буст', boostActive: 'Активен буст', boostDesc: 'Профилът ти е #1 за 30 минути',
      passport: 'Паспорт', passportDesc: 'Разглеждай профили от друг град', viewingIn: 'Разглеждаш профили в',
      incognito: 'Инкогнито', incognitoOn: 'Невидим режим активен', incognitoOff: 'Видим за другите',
      rewindBtn: '↩️ Върни', nothingToRewind: 'Няма какво да върнеш',
      filtersBtn: '🎯 Филтри', premiumFeature: 'Това е Премиум функция', upgrade: 'Надгради до Премиум', seePlans: 'Виж плановете',
      filters: { age:'Възраст:', travelDna:'Пътнически ДНК', minCompat:'Мин. съвместимост:', onlineOnly:'Само онлайн', verifiedOnly:'Само верифицирани', distance:'Разстояние', clear:'Изчисти', apply:'Приложи', dist50:'50км', dist500:'500км', distWorld:'целия свят' },
      swipeLabels: { match:'СЪВПАДЕНИЕ!', liked:'ХАРЕСАН ♥', super:'СУПЕР ⭐', nope:'НЕ ✗' },
    },
    journeys: { title: 'Моите пътувания', tabs: ['Предстоящи', 'Планирани', 'Завършени', 'Класация'], empty: 'Все още нямаш пътувания. Изследвай и се присъедини!', departs: 'Тръгване', tripsLabel: 'Пътувания', countriesLabel: 'Държави', youLabel: 'Ти', statsTab: 'Статистики', stats: { countries: 'Посетени държави', trips: 'Пътувания', km: 'Изминати км', matches: 'Съвпадения', superLikes:'Супер харесвания', leaderboard:'Класация', footprint:'🌍 Пътнически отпечатък', tripsPerMonth:'📅 Пътувания на месец' } },
    messages: { title: 'Съобщения', placeholder: 'Напиши съобщение...', send: 'Изпрати', you: 'Ти', translate: 'Преведи', autoTranslate: 'Авто-превод', directMessages: '💬 Лични съобщения', groupChats: '👥 Групови чатове', joinedGroupChat: '✅ Присъединен! Вече си в груповия чат.', videoCall: 'Видео разговор', calling: 'Обажда се...', connected: 'Свързан', endCall: 'Затвори', mute:'Заглуши', camera:'Камера', chat:'Чат', voiceMsg:'🎤 Гласово съобщение', expiresIn1h:'— изтича след 1ч', voice:'Глас', giftSent:'🎁 Изпрати ти', downloadLoveStory:'Свали Нашата История' },
    dna: { title: 'Твоят пътнически ДНК', sub: 'Открий своята пътническа личност', start: 'Открий моя ДНК', retake: 'Повтори', result: 'Ти си', traits: { explorer: 'Изследовател', adventurer: 'Авантюрист', culturist: 'Културист', nomad: 'Номад' } },
    quiz: [
      { q: 'Коя е идеалната ти дестинация?', opts: ['Дива природа и планини', 'Древни градове и култура', 'Плаж и релакс', 'Всичко, стига да е ново'] },
      { q: 'Как предпочиташ да пътуваш?', opts: ['Напълно спонтанно', 'С приблизителен план', 'Всичко организирано предварително'] },
      { q: 'Какво търсиш в спътника си?', opts: ['Общи интереси', 'Различна гледна точка', 'Предпочитам да пътувам сам'] },
      { q: 'Колко дълго е идеалното ти пътуване?', opts: ['Уикенд бягство', '1–2 седмици', '1 месец или повече'] },
    ],
    profile: { myProfile:'Моят профил', editProfile:'Редактирай профил', saveProfile:'Запази профил', profileSaved:'Профилът е запазен ✅', changePhoto:'Смени снимка', bio:'За мен', age:'Възраст', dreamDest:'Мечтани дестинации', travelStyle:'Стил на пътуване', instagram:'Instagram', lookingFor:'Търся', firstName:'Собствено име', lastName:'Фамилия', city:'Град', travelTags:['Изследовател','Авантюрист','Културист','Номад','Гурме','Фотограф'], dreamPlaceholder:'Япония, Исландия...' },
    notif: { notifications:'Известия', allowNotif:'Разреши известия', notifDesc:'Получавай известия за нови съвпадения', newMatch:'Ново съвпадение!', viewedProfile:'Някой разгледа профила ти', noNotifications:'Няма известия', later:'По-късно', newSuperLike:'Ново Супер харесване!', fromSofia:'от София', sentYou:'ти изпрати' },
    onboarding: { welcome:'Добре дошъл в TravelSoul!', welcomeSub:'Нека настроим профила ти за 1 минута', whatType:'Какъв тип пътник си?', destinations:'Кои дестинации мечтаеш да посетиш?', lookingFor:'Какво търсиш в TravelSoul?', next:'Напред →', finish:'Завърши настройката ✓', skip:'Пропусни', types:[{icon:'🏔️',key:'explorer',name:'Изследовател',desc:'Обичам непознати места'},{icon:'🎭',key:'adventurer',name:'Авантюрист',desc:'Търся адреналин'},{icon:'🎨',key:'culturist',name:'Културист',desc:'Изкуство и история'},{icon:'🌴',key:'nomad',name:'Номад',desc:'Живея без граници'}], goals:[{icon:'✈️',key:'companion',name:'Спътник за пътуване'},{icon:'💕',key:'romance',name:'Романтична връзка'},{icon:'👥',key:'friends',name:'Нови приятели'}] },
    landing: { trusted:'🌟 Доверен от 12,847 пътешественици', watchDemo:'Гледай демо ▶', stats:[{n:'12,847+',l:'Активни пътешественици'},{n:'142',l:'Държави'},{n:'4,832',l:'Съвпадения'},{n:'98%',l:'Удовлетворение'}], howTitle:'Как работи', how:[{icon:'🧬',title:'Създай Travel DNA',desc:'Вземи нашия тест за личност'},{icon:'💫',title:'Намери Soul Match',desc:'Свържи се с подходящи пътешественици'},{icon:'✈️',title:'Пътувай заедно',desc:'Сподели приключението'}], faqTitle:'Въпроси', privacyTitle:'Политика за поверителност', termsTitle:'Условия за ползване', about:'За нас', contact:'Контакт', allRights:'© 2026 IVIPRO. Всички права запазени.', privacyContent:{ lastUpdated:'Последна актуализация: Юни 2026', collect:'Данни, които събираме', collectText:'TravelSoul събира: име, имейл, държава, предпочитания за пътуване и снимки на профила. Данните се съхраняват сигурно чрез Supabase облачна инфраструктура. Използваме Google OAuth за влизане в профила ви.', use:'Как използваме данните', useText:'Данните се използват за съвпадения с пътнически партньори и подобряване на вашето изживяване.', storage:'Съхранение на данни', storageText:'Профилът, съобщенията и снимките ви се съхраняват сигурно на Supabase облачни сървъри. Не споделяме лична информация с трети страни.', thirdParties:'Трети страни', thirdPartiesText:'Използваме Google OAuth (влизане) и Supabase (база данни). Тези услуги имат собствени политики за поверителност.', rights:'Вашите права (GDPR)', rightsText:'Имате право да достъпите, коригирате или изтриете вашите данни по всяко време. Можете да поискате изтриване на акаунта си като се свържете с support@travelsoul.love', cookies:'Бисквитки', cookiesText:'Използваме само функционални бисквитки за съхранение на вашите предпочитания.', contact:'Контакт', contactText:'За въпроси относно поверителността: support@travelsoul.love' }, termsContent:{ lastUpdated:'Последна актуализация: Юни 2026', eligibility:'Допустимост', eligibilityText:'Трябва да сте на 18+ години за да използвате TravelSoul.', conduct:'Поведение на потребителите', conductText:'Потребителите трябва да се държат с уважение. Забранено е тормозенето и неподходящото съдържание.', safety:'Безопасност при пътуване', safetyText:'TravelSoul не носи отговорност за инциденти по време на пътувания. Препоръчваме застраховка за пътуване.', premium:'Премиум услуги', premiumText:'Платените абонаменти се управляват чрез Google Play в Android приложението. Можеш да управляваш или анулираш абонамента си там преди следващата дата на таксуване.', contact:'Контакт', contactText:'Въпроси: support@travelsoul.love' }, refundContent:{ lastUpdated:'Последна актуализация: Юни 2026', title:'Политика за възстановяване', intro:'Искаме да сте доволни от TravelSoul Premium и Elite абонаментите.', eligibility:'Право на възстановяване', eligibilityText:'Можете да поискате пълно възстановяване в рамките на 14 дни от първоначалното плащане, ако не сте удовлетворени, съгласно законодателството за защита на потребителите в ЕС. Възстановяванията се прилагат само за първоначални плащания за абонамент.', process:'Как да поискате', processText:'За да поискате възстановяване, изпратете имейл на support@travelsoul.love с вашия акаунт имейл и дата на покупката. Възстановяванията се обработват в рамките на 5-10 работни дни по оригиналния начин на плащане.', nonRefundable:'Невъзстановими артикули', nonRefundableText:'Виртуалните монети и еднократните цифрови покупки не подлежат на възстановяване след употреба. Подновяванията на абонамент след началния 14-дневен период не подлежат на възстановяване, но можете да анулирате по всяко време.', contact:'Контакт', contactText:'За въпроси относно възстановяване: support@travelsoul.love' }, heroBadge:'✦ Пътуване за душата', demoAlert:'Демото идва скоро! 🎬', travelerStories:'❤️ Истории на пътешественици', appStoreBanner:'📱 Скоро в App Store и Google Play', joinWaitlist:'Присъедини се', footerTagline:'Връзки за душевни пътешественици' },
    extras: { addDest:'+ Добави', selectUpTo5:'Избери до 5', back:'← Назад', photoSaved:'✅ Снимката е запазена!', photoTooBig:'❌ Снимката е твърде голяма', addStory:'Добави', travelers:'пътника', upgradeBtn:'💎 Надгради' },
    badges: { worldExplorer:{name:'Световен Изследовател',desc:'Присъединил се към 3+ пътувания'}, socialButterfly:{name:'Социална Пеперуда',desc:'Изпратил 50+ съобщения'}, superLiker:{name:'Супер Харесвач',desc:'Използвал 10 Супер харесвания'}, onFire:{name:'В Огъня',desc:'Влизал 7 дни подред'}, firstFlight:{name:'Първи Полет',desc:'Присъединил се към първото пътуване'}, mountainClimber:{name:'Алпинист',desc:'Присъедини се към планинско пътуване'}, beachLover:{name:'Плажар',desc:'Присъедини се към 3 плажни пътувания'}, eliteTraveler:{name:'Елитен Пътник',desc:'Надгради до Elite'}, nightOwl:{name:'Нощна Сова',desc:'Използвай Нощен режим 10 пъти'}, cartographer:{name:'Картограф',desc:'Посети 10 държави'} },
    challenges: [{title:'🌏 Свържи се с Азия',desc:'Изпрати съобщение на някой от Азиатска държава тази седмица',goal:1,reward:'🔥 1 Безплатен Буст'},{title:'⭐ Седмица на Супер харесвания',desc:'Изпрати 3 Супер харесвания тази седмица',goal:3,reward:'↩️ Върни x3'},{title:'🌍 Световен Пътник',desc:'Разгледай 10 профила от различни държави',goal:10,reward:'🌍 Паспорт дневен достъп'},{title:'🌙 Нощен Изследовател',desc:'Използвай Нощен режим',goal:1,reward:'⭐ Супер харесване x5'}],
    videoIntro: { recordIntro:'Запиши интро видео', uploadVideo:'Качи видео', videoSuccess:'Видеото е качено! ✅', playIntro:'▶ Интро', verifiedByVideo:'Профилът ти е верифициран с видео ✅', startCamera:'Пусни камерата', previewReady:'Готово за запис', startRecording:'Започни запис', stopRecording:'Спри записа', recording:'Записва се', uploading:'Качва се...', previewVideo:'Прегледай видеото', retake:'Повтори', close:'Затвори', cameraError:'Камерата не е достъпна. Провери настройките.', videoTooShort:'Видеото трябва да е поне 15 секунди', videoTooLong:'Видеото е твърде дълго (макс. 30 сек.)', videoTooBig:'Видеото е твърде голямо (макс. 50MB)', duration15to30:'15–30 секунди', talkAbout:'Разкажи за любимото си пътуване', getVerified:'Получи ✅ значка', minMaxDuration:'Мин. 15 сек · Макс. 30 сек', introInstructions:'Запиши кратко интро видео, за да верифицираш профила си' },
    premiumBullets:{ boost:['Профилът ти е #1 за 30 мин','3× повече изгледи','Ексклузивен златен пръстен'], rewind:['Върни последния пропуск','Втори шанс с добри мачове','Веднъж на ден'], passport:['Разглеждай профили от всеки град','Намери мачове по целия свят','Смени локацията по всяко време'], incognito:['Разглеждай невидимо','Прегледите ти не се записват','Включи/изключи по всяко време'], filters:['Филтрирай по възраст, ДНК, съвместимост','Само онлайн и верифицирани','Контрол на разстоянието'], stats:['Пълно табло с пътнически статистики','График на месечните пътувания','Карта на световния отпечатък'], likes:['Неограничени харесвания всеки ден','Без дневни лимити','Харесвай всеки, когото искаш'], whoViewed:['Виж всеки, разгледал профила ти','Изпрати им съобщение първи','Известия в реално време'], videoChat:['HD видео разговори с мачовете','Налично на всички устройства','Само за Elite'], aiPlanner:['AI маршрут ден по ден','Персонализирано бюджетно планиране','Сподели с пътническата група'], nightMode:['Виж кой е активен тази вечер','Специални нощни профили','Ексклузивен среща-филтър'], compatReport:['Пълен анализ на съвместимостта','5 измерени измерения','Доклад за изтегляне'], voiceMsg:['Изпращай гласови съобщения','Чуй мача си пръв','Аудио в чата'], gifts:['Изпращай виртуални подаръци','Анимирана доставка на подарък','Покажи грижа с думи'], icebreaker:['AI-персонализирани стартери','Базирани на пътнически ДНК','3 предложения + обнови'], dreamMatch:['Мач по мечтани дестинации','Намери своя съ-пътешественик','Базирано на списъка ти с желания'], liveLocation:['Споделяй локация в реално време','Групова координация','Изтича след 1 час'], splitCosts:['Авто изчисление на човек','Проследи всички разходи','Изпрати в груповия чат'], hotelMatch:['Виж кой е резервирал наблизо','Координирай срещи','Запознай се преди пътуването'], aiWriter:['3 AI-написани биографии','Авантюрна/романтична/остроумна','Открои се от тълпата'] },
    pact:{ pactBtn:'🤝 Направи пакт', pactTitle:'Пътнически пакт', pactSub:'Поемете ангажимент за пътуване заедно', pactDestLabel:'Дестинация', pactMonthLabel:'Кога (месец/сезон)', pactSend:'Изпрати пакт', pactDecline:'Откажи', pactAccept:'Приеми пакт', pactPending:'⏳ Пактът чака', pactActive:'✈️ Пактът е активен', pactFulfilled:'✅ Пактът е изпълнен', pactCountdown:'дни до пътуването', pactAiSuggest:'✨ AI предложи дестинация', pactTrophy:'🏆 Пътували заедно', pactLimitFree:'Надгради за да изпращаш пактове', pactLimit1:'Надгради до Elite за неограничени пактове', pactSuccess:'Пактът е изпратен! Чакаме потвърждение...', pactDeclined:'Пактът е отказан' },
    info:{ height:'Ръст', bodyType:'Телосложение', smoking:'Тютюнопушене', alcohol:'Алкохол', children:'Деца', partnerPrefs:'Партньорски предпочитания', goalLabel:'Търся', smoker:'🚬 Пуша', nonSmoker:'🚭 Не пуша', occasionally:'🌿 Понякога', drinker:'🍷 Пия', nonDrinker:'🚱 Не пия', socially:'🥂 Социално' },
  },
  en: {
    brand: 'TravelSoul',
    tagline: ['Find your soul', 'through travel', 'Discover the world together'],
    cta: 'Start your journey', login: 'Sign in',
    features: [
      { icon: '🧬', title: 'Travel DNA', desc: 'A unique map of your traveler personality across 4 core traits' },
      { icon: '♥', title: 'Soul Matching', desc: 'Meet travelers whose dreams and spirit align with yours' },
      { icon: '✈', title: 'Group Trips', desc: 'Join curated group adventures with kindred spirits worldwide' },
      { icon: '🛡', title: 'Safe & Private', desc: 'Your privacy and safety are our absolute top priority' },
    ],
    nav: { explore: 'Explore', connect: 'Connect', journeys: 'Journeys', messages: 'Chat' },
    register: { title: 'Join TravelSoul', sub: 'Your journey starts here', name: 'Full name', email: 'Email address', password: 'Password', confirmPassword: 'Confirm Password', passwordMismatch: "Passwords don't match", passwordMatch: 'Passwords match ✅', weakPassword: 'Weak password', mediumPassword: 'Medium password', strongPassword: 'Strong password', country: 'Select country', submit: 'Create profile', login: 'Already have an account? Sign in', birthDate:'Date of birth', ageError:'You must be 18+ years old', selectBirthDate:'Select date of birth' },
    ageVerification: { title: 'Age Verification', msg: "We need to confirm you're 18 or older to continue using TravelSoul.", submit: 'Confirm', saving: 'Saving...', underageTitle: "We're Sorry", underageMsg: "TravelSoul is only available to users 18 and older. You can't continue using the app.", signOut: 'Sign Out' },
    loginScreen: { title: 'Welcome back', sub: 'Sign in to your account', email: 'Email address', password: 'Password', submit: 'Sign in', register: "Don't have an account? Register", forgot: 'Forgot password?', resetSent: '✅ Password reset email sent!', error: 'Wrong email or password', wrongPassword: 'Wrong email or password' },
    explore: { title: 'Discover Trips', spots: 'spots left', join: 'Join Trip', hot: 'Hot', days: 'days', aiPicks: 'TRENDING DESTINATIONS', refresh: 'Discover more', host: 'Host', joined: '✓ Joined', aiRecommended: '🤖 AI Recommended' },
    connect: {
      title: 'Find Your Soul', sub: 'travelers online now',
      online: 'online', offline: 'offline', like: 'Like', liked: 'Liked', pass: 'Pass', match: 'Match', dnaSection: 'Your Travel DNA',
      whoViewed: 'Who viewed your profile', swipeMode: 'Swipe', gridMode: 'Grid',
      seenAll: "You've seen everyone! 🎉", superLikeSent: '✨ Super Like Sent!', itsAMatch: "💫 It's a Match!",
      sendMessage: 'Send Message', keepSwiping: 'Keep Swiping',
      keepGoing: 'Keep Going', sendMsg: 'Send Message', passedToast: 'Passed ✓',
      boost: 'Boost', boostActive: 'Boosting now', boostDesc: 'Your profile is #1 for 30 minutes',
      passport: 'Passport', passportDesc: 'Browse profiles from another city', viewingIn: 'Viewing profiles in',
      incognito: 'Incognito', incognitoOn: "You're invisible", incognitoOff: 'Visible to others',
      rewindBtn: '↩️ Rewind', nothingToRewind: 'Nothing to rewind',
      filtersBtn: '🎯 Filters', premiumFeature: 'This is a Premium feature', upgrade: 'Upgrade to Premium', seePlans: 'See all plans',
      filters: { age:'Age:', travelDna:'Travel DNA', minCompat:'Min compatibility:', onlineOnly:'Online only', verifiedOnly:'Verified only', distance:'Distance', clear:'Clear', apply:'Apply', dist50:'50km', dist500:'500km', distWorld:'worldwide' },
      swipeLabels: { match:'MATCH!', liked:'LIKED ♥', super:'SUPER ⭐', nope:'NOPE ✗' },
    },
    journeys: { title: 'My Journeys', tabs: ['Upcoming', 'Planned', 'Completed', 'Leaderboard'], empty: 'No journeys yet. Explore and join a trip!', departs: 'Departs', tripsLabel: 'Trips', countriesLabel: 'Countries', youLabel: 'You', statsTab: 'Statistics', stats: { countries: 'Countries visited', trips: 'Total trips', km: 'Km traveled', matches: 'Matches made', superLikes:'Super Likes', leaderboard:'Leaderboard', footprint:'🌍 Travel Footprint', tripsPerMonth:'📅 Trips per Month' } },
    messages: { title: 'Messages', placeholder: 'Write a message...', send: 'Send', you: 'You', translate: 'Translate', autoTranslate: 'Auto-translate', directMessages: '💬 Direct Messages', groupChats: '👥 Group Chats', joinedGroupChat: '✅ Joined! You\'re now in the group chat.', videoCall: 'Video Call', calling: 'Calling...', connected: 'Connected', endCall: 'End Call', mute:'Mute', camera:'Camera', chat:'Chat', voiceMsg:'🎤 Voice message', expiresIn1h:'— expires in 1h', voice:'Voice', giftSent:'🎁 Sent you a', downloadLoveStory:'Download Our Story' },
    dna: { title: 'Your Travel DNA', sub: 'Discover your traveler personality', start: 'Reveal My DNA', retake: 'Retake Quiz', result: 'You are a', traits: { explorer: 'Explorer', adventurer: 'Adventurer', culturist: 'Culturist', nomad: 'Nomad' } },
    quiz: [
      { q: 'What is your ideal destination?', opts: ['Wild nature & mountains', 'Ancient cities & culture', 'Beach & relaxation', "Anything, as long as it's new"] },
      { q: 'How do you prefer to travel?', opts: ['Completely spontaneous', 'With a rough plan', 'Everything organized in advance'] },
      { q: 'What do you look for in a travel companion?', opts: ['Same interests', 'A different perspective', 'I prefer to travel alone'] },
      { q: 'How long is your ideal trip?', opts: ['Weekend getaway', '1–2 weeks', '1 month or more'] },
    ],
    profile: { myProfile:'My Profile', editProfile:'Edit Profile', saveProfile:'Save Profile', profileSaved:'Profile saved ✅', changePhoto:'Change Photo', bio:'About me', age:'Age', dreamDest:'Dream Destinations', travelStyle:'Travel Style', instagram:'Instagram', lookingFor:'Looking for', firstName:'First Name', lastName:'Last Name', city:'City', travelTags:['Explorer','Adventurer','Culturist','Nomad','Foodie','Photographer'], dreamPlaceholder:'Japan, Iceland...' },
    notif: { notifications:'Notifications', allowNotif:'Allow Notifications', notifDesc:'Get notified about new matches', newMatch:'New Match!', viewedProfile:'Someone viewed your profile', noNotifications:'No notifications yet', later:'Later', newSuperLike:'New Super Like!', fromSofia:'from Sofia', sentYou:'sent you' },
    onboarding: { welcome:'Welcome to TravelSoul!', welcomeSub:"Let's set up your profile in 1 minute", whatType:'What type of traveler are you?', destinations:'Which destinations do you dream of visiting?', lookingFor:"What are you looking for on TravelSoul?", next:'Next →', finish:'Finish Setup ✓', skip:'Skip', types:[{icon:'🏔️',key:'explorer',name:'Explorer',desc:'I love undiscovered places'},{icon:'🎭',key:'adventurer',name:'Adventurer',desc:'I seek adrenaline'},{icon:'🎨',key:'culturist',name:'Culturist',desc:'Art and history'},{icon:'🌴',key:'nomad',name:'Nomad',desc:'I live without borders'}], goals:[{icon:'✈️',key:'companion',name:'Travel companion'},{icon:'💕',key:'romance',name:'Romantic connection'},{icon:'👥',key:'friends',name:'New friends'}] },
    landing: { trusted:'🌟 Trusted by 12,847 travelers', watchDemo:'Watch Demo ▶', stats:[{n:'12,847+',l:'Active Travelers'},{n:'142',l:'Countries'},{n:'4,832',l:'Matches Made'},{n:'98%',l:'Satisfaction'}], howTitle:'How It Works', how:[{icon:'🧬',title:'Create your Travel DNA',desc:'Take our personality quiz'},{icon:'💫',title:'Find your Soul Match',desc:'Connect with compatible travelers'},{icon:'✈️',title:'Travel Together',desc:'Share the adventure of a lifetime'}], faqTitle:'FAQ', privacyTitle:'Privacy Policy', termsTitle:'Terms of Service', about:'About', contact:'Contact', allRights:'© 2026 IVIPRO. All rights reserved.', privacyContent:{ lastUpdated:'Last updated: June 2026', collect:'Data We Collect', collectText:'TravelSoul collects: name, email, country, travel preferences, and profile photos. Data is stored securely via Supabase cloud infrastructure. We use Google OAuth for sign-in.', use:'How We Use Your Data', useText:'Your data is used to match you with compatible travelers and improve your experience.', storage:'Data Storage', storageText:'Your profile, messages, and photos are stored securely on Supabase cloud servers. We do not share your personal data with third parties.', thirdParties:'Third Parties', thirdPartiesText:'We use Google OAuth (login) and Supabase (database). These services have their own privacy policies.', rights:'Your Rights (GDPR)', rightsText:'You have the right to access, correct or delete your data at any time. You may request deletion of your account by contacting support@travelsoul.love', cookies:'Cookies', cookiesText:'TravelSoul uses only functional cookies to store your preferences.', contact:'Contact', contactText:'For privacy questions: support@travelsoul.love' }, termsContent:{ lastUpdated:'Last updated: June 2026', eligibility:'Eligibility', eligibilityText:'You must be 18+ years old to use TravelSoul.', conduct:'User Conduct', conductText:'Users must behave respectfully. Harassment and inappropriate content are prohibited.', safety:'Travel Safety', safetyText:'TravelSoul is not responsible for incidents during travel. We recommend travel insurance.', premium:'Premium Services', premiumText:'Paid subscriptions are managed through Google Play in the Android app. You can manage or cancel your subscription there before the next billing date.', contact:'Contact', contactText:'Questions: support@travelsoul.love' }, refundContent:{ lastUpdated:'Last updated: June 2026', title:'Refund Policy', intro:'We want you to be satisfied with TravelSoul Premium and Elite subscriptions.', eligibility:'Refund Eligibility', eligibilityText:'You may request a full refund within 14 days of your initial subscription purchase if you are not satisfied, in line with EU consumer protection law. Refunds apply to first-time subscription payments only.', process:'How to Request', processText:'To request a refund, email support@travelsoul.love with your account email and purchase date. Refunds are processed within 5-10 business days to your original payment method.', nonRefundable:'Non-Refundable Items', nonRefundableText:'Virtual coins and one-time digital purchases are non-refundable once used. Subscription renewals after the initial 14-day period are non-refundable, but you may cancel anytime to prevent future charges.', contact:'Contact', contactText:'For refund questions: support@travelsoul.love' }, heroBadge:'✦ Soul-first travel', demoAlert:'Demo coming soon! 🎬', travelerStories:'❤️ Traveler Stories', appStoreBanner:'📱 Coming soon to App Store & Google Play', joinWaitlist:'Join Waitlist', footerTagline:'Soul-first travel connections' },
    extras: { addDest:'+ Add', selectUpTo5:'Select up to 5', back:'← Back', photoSaved:'✅ Photo saved!', photoTooBig:'❌ Photo too large', addStory:'Add', travelers:'travelers', upgradeBtn:'💎 Upgrade' },
    badges: { worldExplorer:{name:'World Explorer',desc:'Joined 3+ trips'}, socialButterfly:{name:'Social Butterfly',desc:'Sent 50+ messages'}, superLiker:{name:'Super Liker',desc:'Used 10 Super Likes'}, onFire:{name:'On Fire',desc:'Logged in 7 days in a row'}, firstFlight:{name:'First Flight',desc:'Joined your first trip'}, mountainClimber:{name:'Mountain Climber',desc:'Join a hiking trip'}, beachLover:{name:'Beach Lover',desc:'Join 3 beach trips'}, eliteTraveler:{name:'Elite Traveler',desc:'Upgrade to Elite'}, nightOwl:{name:'Night Owl',desc:'Use Night Mode 10 times'}, cartographer:{name:'Cartographer',desc:'Visit 10 countries'} },
    challenges: [{title:'🌏 Connect with Asia',desc:'Message someone from an Asian country this week',goal:1,reward:'🔥 1 Free Boost'},{title:'⭐ Super Like Week',desc:'Send 3 Super Likes this week',goal:3,reward:'↩️ Rewind x3'},{title:'🌍 Globe Trotter',desc:'View 10 profiles from different countries',goal:10,reward:'🌍 Passport day pass'},{title:'🌙 Night Explorer',desc:'Use Night Mode tonight',goal:1,reward:'⭐ Super Like x5'}],
    videoIntro: { recordIntro:'Record Intro Video', uploadVideo:'Upload Video', videoSuccess:'Video uploaded! ✅', playIntro:'▶ Intro', verifiedByVideo:'Your profile is verified with a video ✅', startCamera:'Start Camera', previewReady:'Ready to Record', startRecording:'Start Recording', stopRecording:'Stop Recording', recording:'Recording', uploading:'Uploading...', previewVideo:'Preview Video', retake:'Retake', close:'Close', cameraError:'Camera not available. Please check your settings.', videoTooShort:'Video must be at least 15 seconds', videoTooLong:'Video too long (max 30 sec)', videoTooBig:'Video too large (max 50MB)', duration15to30:'15–30 seconds', talkAbout:'Talk about your favourite trip', getVerified:'Get a ✅ verified badge', minMaxDuration:'Min. 15 sec · Max. 30 sec', introInstructions:'Record a short intro video to verify your profile' },
    premiumBullets:{ boost:['Profile shown first for 30 min','3× more profile views','Exclusive gold ring indicator'], rewind:['Undo your last pass','Second chances with great matches','Available once per day'], passport:['Browse profiles in any city','Match with travelers worldwide','Change location anytime'], incognito:["Browse invisibly — no one knows","Your views aren't recorded","Turn on/off anytime"], filters:['Filter by age, DNA, compatibility','Online-only & verified filter','Distance control'], stats:['Full travel statistics dashboard','Monthly trip chart','World footprint map'], likes:['Unlimited likes every day','No daily caps or wait times','Like everyone you want'], whoViewed:['See everyone who viewed you','Send them a message first','Real-time notifications'], videoChat:['HD video calls with matches','Available on all devices','Elite-only feature'], aiPlanner:['AI day-by-day itinerary','Custom budget planning','Share with your travel group'], nightMode:["See who's active tonight","Special night-only profiles","Exclusive meetup filter"], compatReport:['Full compatibility analysis','5 dimensions scored','Downloadable PDF report'], voiceMsg:['Send voice messages','Hear your match first','Playable in-chat audio'], gifts:['Send virtual gifts','Animated gift delivery','Show you care beyond words'], icebreaker:['AI-personalized openers','Based on travel DNA','3 suggestions + refresh'], dreamMatch:['Match on dream destinations','Find your co-traveler','Based on your wishlist'], liveLocation:['Share real-time location','Group coordination','1-hour expiring share'], splitCosts:['Auto per-person calc','Track all trip expenses','Send to group chat'], hotelMatch:['See who booked nearby','Coordinate pickups','Meet before the trip'], aiWriter:['3 AI-written bio options','Adventurous/romantic/witty','Stand out from the crowd'] },
    pact:{ pactBtn:'🤝 Make a Pact', pactTitle:'Travel Pact', pactSub:'Commit to a journey together', pactDestLabel:'Destination', pactMonthLabel:'When (month/season)', pactSend:'Send Pact', pactDecline:'Decline', pactAccept:'Accept Pact', pactPending:'⏳ Pact Pending', pactActive:'✈️ Pact Active', pactFulfilled:'✅ Pact Fulfilled', pactCountdown:'days to go', pactAiSuggest:'✨ AI Suggest Destination', pactTrophy:'🏆 Traveled Together', pactLimitFree:'Upgrade to send Travel Pacts', pactLimit1:'Upgrade to Elite for unlimited Pacts', pactSuccess:'Pact sent! Waiting for acceptance...', pactDeclined:'Pact declined' },
    info:{ height:'Height', bodyType:'Body type', smoking:'Smoking', alcohol:'Alcohol', children:'Children', partnerPrefs:'Partner preferences', goalLabel:'Looking for', smoker:'🚬 Smoker', nonSmoker:'🚭 Non-smoker', occasionally:'🌿 Occasionally', drinker:'🍷 Drinker', nonDrinker:'🚱 Non-drinker', socially:'🥂 Socially' },
  },
  es: {
    brand: 'TravelSoul',
    tagline: ['Encuentra tu alma', 'a través del viaje', 'Descubre el mundo juntos'],
    cta: 'Empieza tu viaje', login: 'Iniciar sesión',
    features: [
      { icon: '🧬', title: 'ADN Viajero', desc: 'Un mapa único de tu personalidad viajera en 4 rasgos' },
      { icon: '♥', title: 'Compatibilidad', desc: 'Conoce viajeros cuyos sueños coinciden con los tuyos' },
      { icon: '✈', title: 'Viajes en Grupo', desc: 'Únete a aventuras grupales cuidadosamente seleccionadas' },
      { icon: '🛡', title: 'Seguro y Privado', desc: 'Tu privacidad y seguridad son nuestra máxima prioridad' },
    ],
    nav: { explore: 'Explorar', connect: 'Conectar', journeys: 'Viajes', messages: 'Chat' },
    register: { title: 'Únete a TravelSoul', sub: 'Tu viaje comienza aquí', name: 'Nombre completo', email: 'Correo electrónico', password: 'Contraseña', confirmPassword: 'Confirmar contraseña', passwordMismatch: 'Las contraseñas no coinciden', passwordMatch: 'Contraseñas coinciden ✅', weakPassword: 'Contraseña débil', mediumPassword: 'Contraseña media', strongPassword: 'Contraseña fuerte', country: 'Selecciona país', submit: 'Crear perfil', login: '¿Ya tienes cuenta? Inicia sesión', birthDate:'Fecha de nacimiento', ageError:'Debes tener 18+ años', selectBirthDate:'Selecciona fecha de nacimiento' },
    ageVerification: { title: 'Verificación de edad', msg: 'Necesitamos confirmar que tienes 18 años o más para seguir usando TravelSoul.', submit: 'Confirmar', saving: 'Guardando...', underageTitle: 'Lo sentimos', underageMsg: 'TravelSoul solo está disponible para usuarios de 18 años o más. No puedes seguir usando la aplicación.', signOut: 'Cerrar sesión' },
    loginScreen: { title: 'Bienvenido de vuelta', sub: 'Inicia sesión en tu cuenta', email: 'Correo electrónico', password: 'Contraseña', submit: 'Iniciar sesión', register: '¿No tienes cuenta? Regístrate', forgot: '¿Olvidaste tu contraseña?', resetSent: '✅ ¡Email de recuperación enviado!', error: 'Correo o contraseña incorrectos', wrongPassword: 'Email o contraseña incorrectos' },
    explore: { title: 'Descubre Viajes', spots: 'lugares disponibles', join: 'Unirse', hot: 'Popular', days: 'días', aiPicks: 'DESTINOS DE MODA', refresh: 'Descubrir más', host: 'Anfitrión', joined: '✓ Unido', aiRecommended: '🤖 Recomendado por IA' },
    connect: {
      title: 'Encuentra tu Alma', sub: 'viajeros en línea ahora',
      online: 'en línea', offline: 'desconectado', like: 'Me gusta', liked: 'Me gusta ✓', pass: 'Pasar', match: 'Compatibilidad', dnaSection: 'Tu ADN de Viaje',
      whoViewed: 'Quién vio tu perfil', swipeMode: 'Deslizar', gridMode: 'Cuadrícula',
      seenAll: '¡Has visto a todos! 🎉', superLikeSent: '✨ ¡Super Like enviado!', itsAMatch: '💫 ¡Es un Match!',
      sendMessage: 'Enviar Mensaje', keepSwiping: 'Seguir Deslizando',
      keepGoing: 'Seguir', sendMsg: 'Enviar Mensaje', passedToast: 'Pasado ✓',
      boost: 'Impulso', boostActive: 'Impulsando', boostDesc: 'Tu perfil es #1 por 30 minutos',
      passport: 'Pasaporte', passportDesc: 'Ver perfiles de otra ciudad', viewingIn: 'Viendo perfiles en',
      incognito: 'Incógnito', incognitoOn: 'Eres invisible', incognitoOff: 'Visible para otros',
      rewindBtn: '↩️ Deshacer', nothingToRewind: 'Nada que deshacer',
      filtersBtn: '🎯 Filtros', premiumFeature: 'Esta es una función Premium', upgrade: 'Mejorar a Premium', seePlans: 'Ver todos los planes',
      filters: { age:'Edad:', travelDna:'ADN Viajero', minCompat:'Comp. mínima:', onlineOnly:'Solo en línea', verifiedOnly:'Solo verificados', distance:'Distancia', clear:'Limpiar', apply:'Aplicar', dist50:'50km', dist500:'500km', distWorld:'global' },
      swipeLabels: { match:'¡MATCH!', liked:'ME GUSTA ♥', super:'SÚPER ⭐', nope:'NO ✗' },
    },
    journeys: { title: 'Mis Viajes', tabs: ['Próximos', 'Planificados', 'Completados', 'Clasificación'], empty: 'Sin viajes aún. ¡Explora y únete!', departs: 'Sale', tripsLabel: 'Viajes', countriesLabel: 'Países', youLabel: 'Tú', statsTab: 'Estadísticas', stats: { countries: 'Países visitados', trips: 'Viajes totales', km: 'Km recorridos', matches: 'Coincidencias', superLikes:'Super Likes', leaderboard:'Clasificación', footprint:'🌍 Huella de Viaje', tripsPerMonth:'📅 Viajes por Mes' } },
    messages: { title: 'Mensajes', placeholder: 'Escribe un mensaje...', send: 'Enviar', you: 'Tú', translate: 'Traducir', autoTranslate: 'Auto-traducir', directMessages: '💬 Mensajes Directos', groupChats: '👥 Chats de Grupo', joinedGroupChat: '✅ ¡Te uniste! Ya estás en el chat de grupo.', videoCall: 'Videollamada', calling: 'Llamando...', connected: 'Conectado', endCall: 'Colgar', mute:'Silenciar', camera:'Cámara', chat:'Chat', voiceMsg:'🎤 Mensaje de voz', expiresIn1h:'— expira en 1h', voice:'Voz', giftSent:'🎁 Te envió', downloadLoveStory:'Descargar Nuestra Historia' },
    dna: { title: 'Tu ADN de Viaje', sub: 'Descubre tu personalidad viajera', start: 'Revelar Mi ADN', retake: 'Repetir', result: 'Eres un', traits: { explorer: 'Explorador', adventurer: 'Aventurero', culturist: 'Culturista', nomad: 'Nómada' } },
    quiz: [
      { q: '¿Cuál es tu destino ideal?', opts: ['Naturaleza salvaje y montañas', 'Ciudades antiguas y cultura', 'Playa y relajación', 'Cualquier cosa, siempre que sea nueva'] },
      { q: '¿Cómo prefieres viajar?', opts: ['Completamente espontáneo', 'Con un plan aproximado', 'Todo organizado de antemano'] },
      { q: '¿Qué buscas en un compañero de viaje?', opts: ['Intereses comunes', 'Una perspectiva diferente', 'Prefiero viajar solo'] },
      { q: '¿Cuánto dura tu viaje ideal?', opts: ['Escapada de fin de semana', '1–2 semanas', '1 mes o más'] },
    ],
    profile: { myProfile:'Mi Perfil', editProfile:'Editar Perfil', saveProfile:'Guardar Perfil', profileSaved:'Perfil guardado ✅', changePhoto:'Cambiar foto', bio:'Sobre mí', age:'Edad', dreamDest:'Destinos soñados', travelStyle:'Estilo de viaje', instagram:'Instagram', lookingFor:'Busco', firstName:'Nombre', lastName:'Apellido', city:'Ciudad', travelTags:['Explorador','Aventurero','Culturista','Nómada','Gastrónomo','Fotógrafo'], dreamPlaceholder:'Japón, Islandia...' },
    notif: { notifications:'Notificaciones', allowNotif:'Permitir notificaciones', notifDesc:'Recibe avisos de nuevas coincidencias', newMatch:'¡Nueva coincidencia!', viewedProfile:'Alguien vio tu perfil', noNotifications:'Sin notificaciones', later:'Después', newSuperLike:'¡Nuevo Super Like!', fromSofia:'desde Sofía', sentYou:'te envió' },
    onboarding: { welcome:'¡Bienvenido a TravelSoul!', welcomeSub:'Configuremos tu perfil en 1 minuto', whatType:'¿Qué tipo de viajero eres?', destinations:'¿Qué destinos sueñas visitar?', lookingFor:'¿Qué buscas en TravelSoul?', next:'Siguiente →', finish:'Finalizar ✓', skip:'Saltar', types:[{icon:'🏔️',key:'explorer',name:'Explorador',desc:'Me encantan los lugares desconocidos'},{icon:'🎭',key:'adventurer',name:'Aventurero',desc:'Busco adrenalina'},{icon:'🎨',key:'culturist',name:'Culturista',desc:'Arte e historia'},{icon:'🌴',key:'nomad',name:'Nómada',desc:'Vivo sin fronteras'}], goals:[{icon:'✈️',key:'companion',name:'Compañero de viaje'},{icon:'💕',key:'romance',name:'Conexión romántica'},{icon:'👥',key:'friends',name:'Nuevos amigos'}] },
    landing: { trusted:'🌟 De confianza para 12,847 viajeros', watchDemo:'Ver Demo ▶', stats:[{n:'12,847+',l:'Viajeros activos'},{n:'142',l:'Países'},{n:'4,832',l:'Coincidencias'},{n:'98%',l:'Satisfacción'}], howTitle:'Cómo funciona', how:[{icon:'🧬',title:'Crea tu ADN Viajero',desc:'Toma nuestro test de personalidad'},{icon:'💫',title:'Encuentra tu Soul Match',desc:'Conecta con viajeros compatibles'},{icon:'✈️',title:'Viaja Juntos',desc:'Comparte la aventura'}], faqTitle:'Preguntas', privacyTitle:'Política de Privacidad', termsTitle:'Términos de Servicio', about:'Acerca de', contact:'Contacto', allRights:'© 2026 IVIPRO. Todos los derechos reservados.', privacyContent:{ lastUpdated:'Última actualización: junio 2026', collect:'Datos que recopilamos', collectText:'TravelSoul recopila: nombre, correo, país, preferencias de viaje y fotos de perfil. Los datos se almacenan de forma segura en la nube de Supabase. Usamos Google OAuth para el inicio de sesión.', use:'Cómo usamos tus datos', useText:'Tus datos se usan para conectarte con viajeros compatibles y mejorar tu experiencia.', storage:'Almacenamiento de datos', storageText:'Tu perfil, mensajes y fotos se almacenan de forma segura en los servidores en la nube de Supabase. No compartimos tus datos con terceros.', thirdParties:'Terceros', thirdPartiesText:'Usamos Google OAuth (inicio de sesión) y Supabase (base de datos). Estos servicios tienen sus propias políticas de privacidad.', rights:'Tus derechos (GDPR)', rightsText:'Tienes derecho a acceder, corregir o eliminar tus datos en cualquier momento. Puedes solicitar la eliminación de tu cuenta contactando a support@travelsoul.love', cookies:'Cookies', cookiesText:'TravelSoul usa solo cookies funcionales para almacenar tus preferencias.', contact:'Contacto', contactText:'Para preguntas de privacidad: support@travelsoul.love' }, termsContent:{ lastUpdated:'Última actualización: junio 2026', eligibility:'Elegibilidad', eligibilityText:'Debes tener 18+ años para usar TravelSoul.', conduct:'Conducta del usuario', conductText:'Los usuarios deben comportarse con respeto. El acoso y el contenido inapropiado están prohibidos.', safety:'Seguridad en el viaje', safetyText:'TravelSoul no es responsable de incidentes durante el viaje. Recomendamos seguro de viaje.', premium:'Servicios premium', premiumText:'Las suscripciones de pago se gestionan mediante Google Play en la app de Android. Puedes gestionar o cancelar tu suscripción allí antes de la próxima fecha de facturación.', contact:'Contacto', contactText:'Preguntas: support@travelsoul.love' }, refundContent:{ lastUpdated:'Última actualización: junio 2026', title:'Política de Reembolso', intro:'Queremos que estés satisfecho con las suscripciones TravelSoul Premium y Elite.', eligibility:'Elegibilidad para Reembolso', eligibilityText:'Puedes solicitar un reembolso completo dentro de los 14 días de tu compra inicial si no estás satisfecho, de acuerdo con la ley de protección al consumidor de la UE. Los reembolsos aplican solo al primer pago de suscripción.', process:'Cómo Solicitarlo', processText:'Para solicitar un reembolso, envía un correo a support@travelsoul.love con tu correo de cuenta y fecha de compra. Los reembolsos se procesan en 5-10 días hábiles al método de pago original.', nonRefundable:'Artículos No Reembolsables', nonRefundableText:'Las monedas virtuales y compras digitales únicas no son reembolsables una vez utilizadas. Las renovaciones de suscripción después del período inicial de 14 días no son reembolsables, pero puedes cancelar en cualquier momento.', contact:'Contacto', contactText:'Para preguntas sobre reembolsos: support@travelsoul.love' }, heroBadge:'✦ Viaje con alma', demoAlert:'¡Demo próximamente! 🎬', travelerStories:'❤️ Historias de Viajeros', appStoreBanner:'📱 Próximamente en App Store y Google Play', joinWaitlist:'Únete a la lista', footerTagline:'Conexiones de viaje con alma' },
    extras: { addDest:'+ Añadir', selectUpTo5:'Selecciona hasta 5', back:'← Atrás', photoSaved:'✅ ¡Foto guardada!', photoTooBig:'❌ Foto demasiado grande', addStory:'Añadir', travelers:'viajeros', upgradeBtn:'💎 Mejorar' },
    badges: { worldExplorer:{name:'Explorador Mundial',desc:'Unido a 3+ viajes'}, socialButterfly:{name:'Mariposa Social',desc:'Enviados 50+ mensajes'}, superLiker:{name:'Super Liker',desc:'Usados 10 Super Likes'}, onFire:{name:'En llamas',desc:'7 días seguidos conectado'}, firstFlight:{name:'Primer Vuelo',desc:'Unido a tu primer viaje'}, mountainClimber:{name:'Escalador',desc:'Únete a un viaje de senderismo'}, beachLover:{name:'Amante de la playa',desc:'Únete a 3 viajes de playa'}, eliteTraveler:{name:'Viajero Élite',desc:'Actualiza a Elite'}, nightOwl:{name:'Noctámbulo',desc:'Usa el modo nocturno 10 veces'}, cartographer:{name:'Cartógrafo',desc:'Visita 10 países'} },
    challenges: [{title:'🌏 Conecta con Asia',desc:'Envía un mensaje a alguien de un país asiático esta semana',goal:1,reward:'🔥 1 Boost Gratis'},{title:'⭐ Semana Super Like',desc:'Envía 3 Super Likes esta semana',goal:3,reward:'↩️ Rewind x3'},{title:'🌍 Trotamundos',desc:'Ve 10 perfiles de diferentes países',goal:10,reward:'🌍 Pase Pasaporte'},{title:'🌙 Explorador Nocturno',desc:'Usa el modo nocturno esta noche',goal:1,reward:'⭐ Super Like x5'}],
    videoIntro: { recordIntro:'Graba tu vídeo intro', uploadVideo:'Subir vídeo', videoSuccess:'¡Vídeo subido! ✅', playIntro:'▶ Intro', verifiedByVideo:'Tu perfil está verificado con vídeo ✅', startCamera:'Iniciar cámara', previewReady:'Listo para grabar', startRecording:'Empezar grabación', stopRecording:'Detener grabación', recording:'Grabando', uploading:'Subiendo...', previewVideo:'Vista previa', retake:'Repetir', close:'Cerrar', cameraError:'Cámara no disponible. Comprueba la configuración.', videoTooShort:'El vídeo debe ser de al menos 15 segundos', videoTooLong:'Vídeo demasiado largo (máx. 30 seg)', videoTooBig:'Vídeo demasiado grande (máx. 50MB)', duration15to30:'15–30 segundos', talkAbout:'Habla de tu viaje favorito', getVerified:'Obtén la insignia ✅', minMaxDuration:'Mín. 15 seg · Máx. 30 seg', introInstructions:'Graba un breve vídeo intro para verificar tu perfil' },
    premiumBullets:{ boost:['Perfil mostrado primero 30 min','3× más vistas de perfil','Indicador dorado exclusivo'], rewind:['Deshacer tu último pase','Segundas oportunidades','Disponible una vez al día'], passport:['Ver perfiles en cualquier ciudad','Conecta con viajeros mundiales','Cambia ubicación cuando quieras'], incognito:['Navega invisible','Tus vistas no se registran','Activa/desactiva cuando quieras'], filters:['Filtra por edad, ADN, compatibilidad','Filtro solo en línea y verificados','Control de distancia'], stats:['Panel completo de estadísticas','Gráfico mensual de viajes','Mapa de huella mundial'], likes:['Likes ilimitados cada día','Sin límites diarios','Dale like a quien quieras'], whoViewed:['Ve quién vio tu perfil','Envíales mensaje primero','Notificaciones en tiempo real'], videoChat:['Videollamadas HD con matches','Disponible en todos los dispositivos','Solo para Elite'], aiPlanner:['Itinerario IA día a día','Planificación de presupuesto','Comparte con tu grupo'], nightMode:['Ve quién está activo esta noche','Perfiles especiales nocturnos','Filtro de encuentros exclusivo'], compatReport:['Análisis completo de compatibilidad','5 dimensiones puntuadas','Informe descargable'], voiceMsg:['Envía mensajes de voz','Escucha tu match primero','Audio reproducible en chat'], gifts:['Envía regalos virtuales','Entrega de regalo animada','Muestra que te importa'], icebreaker:['Openers personalizados por IA','Basados en ADN de viaje','3 sugerencias + actualizar'], dreamMatch:['Match por destinos soñados','Encuentra tu co-viajero','Basado en tu lista de deseos'], liveLocation:['Comparte ubicación en tiempo real','Coordinación de grupo','Expira en 1 hora'], splitCosts:['Cálculo automático por persona','Rastrea todos los gastos','Envía al chat grupal'], hotelMatch:['Ve quién reservó cerca','Coordina encuentros','Conoce antes del viaje'], aiWriter:['3 opciones de bio escritas por IA','Aventurera/romántica/ingeniosa','Destácate de la multitud'] },
    pact:{ pactBtn:'🤝 Hacer un Pacto', pactTitle:'Pacto de Viaje', pactSub:'Comprometeos a viajar juntos', pactDestLabel:'Destino', pactMonthLabel:'Cuándo (mes/temporada)', pactSend:'Enviar Pacto', pactDecline:'Rechazar', pactAccept:'Aceptar Pacto', pactPending:'⏳ Pacto Pendiente', pactActive:'✈️ Pacto Activo', pactFulfilled:'✅ Pacto Cumplido', pactCountdown:'días para el viaje', pactAiSuggest:'✨ IA Sugerir Destino', pactTrophy:'🏆 Viajamos Juntos', pactLimitFree:'Mejora para enviar Pactos de Viaje', pactLimit1:'Mejora a Elite para pactos ilimitados', pactSuccess:'¡Pacto enviado! Esperando aceptación...', pactDeclined:'Pacto rechazado' },
    info:{ height:'Altura', bodyType:'Tipo de cuerpo', smoking:'Fumador', alcohol:'Alcohol', children:'Hijos', partnerPrefs:'Preferencias de pareja', goalLabel:'Busco', smoker:'🚬 Fumador', nonSmoker:'🚭 No fumador', occasionally:'🌿 Ocasionalmente', drinker:'🍷 Bebedor', nonDrinker:'🚱 No bebe', socially:'🥂 Socialmente' },
  },
  fr: {
    brand: 'TravelSoul',
    tagline: ['Trouve ton âme', 'à travers le voyage', 'Découvre le monde ensemble'],
    cta: 'Commence ton voyage', login: 'Se connecter',
    features: [
      { icon: '🧬', title: 'ADN de Voyage', desc: 'Une carte unique de ta personnalité de voyageur en 4 traits' },
      { icon: '♥', title: 'Compatibilité des Âmes', desc: 'Rencontre des voyageurs dont les rêves rejoignent les tiens' },
      { icon: '✈', title: 'Voyages en Groupe', desc: 'Rejoins des aventures de groupe soigneusement sélectionnées' },
      { icon: '🛡', title: 'Sûr et Privé', desc: 'Ta vie privée et ta sécurité sont notre priorité absolue' },
    ],
    nav: { explore: 'Explorer', connect: 'Connecter', journeys: 'Voyages', messages: 'Chat' },
    register: { title: 'Rejoins TravelSoul', sub: 'Ton voyage commence ici', name: 'Nom complet', email: 'Adresse e-mail', password: 'Mot de passe', confirmPassword: 'Confirmer le mot de passe', passwordMismatch: 'Les mots de passe ne correspondent pas', passwordMatch: 'Mots de passe correspondent ✅', weakPassword: 'Mot de passe faible', mediumPassword: 'Mot de passe moyen', strongPassword: 'Mot de passe fort', country: 'Sélectionne un pays', submit: 'Créer un profil', login: 'Déjà un compte ? Se connecter', birthDate:'Date de naissance', ageError:'Vous devez avoir 18+ ans', selectBirthDate:'Sélectionnez la date de naissance' },
    ageVerification: { title: "Vérification de l'âge", msg: "Nous devons confirmer que vous avez 18 ans ou plus pour continuer à utiliser TravelSoul.", submit: 'Confirmer', saving: 'Enregistrement...', underageTitle: 'Nous sommes désolés', underageMsg: "TravelSoul est réservé aux utilisateurs de 18 ans ou plus. Vous ne pouvez pas continuer à utiliser l'application.", signOut: 'Déconnexion' },
    loginScreen: { title: 'Content de te revoir', sub: 'Connecte-toi à ton compte', email: 'Adresse e-mail', password: 'Mot de passe', submit: 'Se connecter', register: 'Pas de compte ? Inscris-toi', forgot: 'Mot de passe oublié ?', resetSent: '✅ Email de réinitialisation envoyé !', error: 'Email ou mot de passe incorrect', wrongPassword: 'Email ou mot de passe incorrect' },
    explore: { title: 'Découvrir des Voyages', spots: 'places disponibles', join: 'Rejoindre', hot: 'Populaire', days: 'jours', aiPicks: 'DESTINATIONS TENDANCE', refresh: 'Découvrir plus', host: 'Hôte', joined: '✓ Rejoint', aiRecommended: '🤖 Recommandé par IA' },
    connect: {
      title: 'Trouve ton Âme', sub: 'voyageurs en ligne maintenant',
      online: 'en ligne', offline: 'hors ligne', like: "J'aime", liked: 'Aimé', pass: 'Passer', match: 'Compatibilité', dnaSection: 'Ton ADN de Voyage',
      whoViewed: 'Qui a vu ton profil', swipeMode: 'Glisser', gridMode: 'Grille',
      seenAll: "Tu as tout vu ! 🎉", superLikeSent: '✨ Super Like envoyé !', itsAMatch: '💫 C\'est un Match !',
      sendMessage: 'Envoyer un message', keepSwiping: 'Continuer',
      keepGoing: 'Continuer', sendMsg: 'Envoyer Message', passedToast: 'Passé ✓',
      boost: 'Booster', boostActive: 'En boost', boostDesc: 'Ton profil est #1 pendant 30 minutes',
      passport: 'Passeport', passportDesc: "Voir des profils d'une autre ville", viewingIn: 'Voir des profils à',
      incognito: 'Incognito', incognitoOn: 'Tu es invisible', incognitoOff: 'Visible pour les autres',
      rewindBtn: '↩️ Annuler', nothingToRewind: 'Rien à annuler',
      filtersBtn: '🎯 Filtres', premiumFeature: "C'est une fonctionnalité Premium", upgrade: 'Passer à Premium', seePlans: 'Voir tous les plans',
      filters: { age:'Âge:', travelDna:'ADN Voyage', minCompat:'Compat. min.:', onlineOnly:'En ligne seulement', verifiedOnly:'Vérifiés seulement', distance:'Distance', clear:'Effacer', apply:'Appliquer', dist50:'50km', dist500:'500km', distWorld:'mondial' },
      swipeLabels: { match:'MATCH!', liked:'AIMÉ ♥', super:'SUPER ⭐', nope:'PASSER ✗' },
    },
    journeys: { title: 'Mes Voyages', tabs: ['À venir', 'Planifiés', 'Complétés', 'Classement'], empty: 'Pas encore de voyages. Explore et rejoins !', departs: 'Départ', tripsLabel: 'Voyages', countriesLabel: 'Pays', youLabel: 'Toi', statsTab: 'Statistiques', stats: { countries: 'Pays visités', trips: 'Voyages totaux', km: 'Km parcourus', matches: 'Correspondances', superLikes:'Super Likes', leaderboard:'Classement', footprint:'🌍 Empreinte Voyage', tripsPerMonth:'📅 Voyages par Mois' } },
    messages: { title: 'Messages', placeholder: 'Écrire un message...', send: 'Envoyer', you: 'Toi', translate: 'Traduire', autoTranslate: 'Traduction auto', directMessages: '💬 Messages Directs', groupChats: '👥 Chats de Groupe', joinedGroupChat: '✅ Rejoint ! Tu es maintenant dans le chat de groupe.', videoCall: 'Appel vidéo', calling: 'Appel en cours...', connected: 'Connecté', endCall: 'Raccrocher', mute:'Muet', camera:'Caméra', chat:'Chat', voiceMsg:'🎤 Message vocal', expiresIn1h:'— expire dans 1h', voice:'Voix', giftSent:"🎁 T'a envoyé", downloadLoveStory:'Télécharger Notre Histoire' },
    dna: { title: 'Ton ADN de Voyage', sub: 'Découvre ta personnalité de voyageur', start: 'Révéler Mon ADN', retake: 'Recommencer', result: 'Tu es un', traits: { explorer: 'Explorateur', adventurer: 'Aventurier', culturist: 'Culturiste', nomad: 'Nomade' } },
    quiz: [
      { q: 'Quelle est ta destination idéale ?', opts: ['Nature sauvage et montagnes', 'Villes anciennes et culture', 'Plage et relaxation', "N'importe quoi, tant que c'est nouveau"] },
      { q: 'Comment préfères-tu voyager ?', opts: ['Complètement spontané', 'Avec un plan approximatif', "Tout organisé à l'avance"] },
      { q: 'Que cherches-tu chez un compagnon de voyage ?', opts: ['Mêmes intérêts', 'Une perspective différente', 'Je préfère voyager seul(e)'] },
      { q: 'Quelle est la durée idéale de ton voyage ?', opts: ['Escapade week-end', '1–2 semaines', '1 mois ou plus'] },
    ],
    profile: { myProfile:'Mon Profil', editProfile:'Modifier Profil', saveProfile:'Sauvegarder', profileSaved:'Profil sauvegardé ✅', changePhoto:'Changer photo', bio:'À propos', age:'Âge', dreamDest:'Destinations rêvées', travelStyle:'Style de voyage', instagram:'Instagram', lookingFor:'Je cherche', firstName:'Prénom', lastName:'Nom', city:'Ville', travelTags:['Explorateur','Aventurier','Culturiste','Nomade','Gastronome','Photographe'], dreamPlaceholder:'Japon, Islande...' },
    notif: { notifications:'Notifications', allowNotif:'Autoriser notifications', notifDesc:'Soyez notifié des nouvelles correspondances', newMatch:'Nouveau match!', viewedProfile:'Quelqu\'un a vu votre profil', noNotifications:'Pas de notifications', later:'Plus tard', newSuperLike:'Nouveau Super Like !', fromSofia:'de Sofia', sentYou:'t\'a envoyé' },
    onboarding: { welcome:'Bienvenue sur TravelSoul!', welcomeSub:'Configurons ton profil en 1 minute', whatType:'Quel type de voyageur es-tu?', destinations:'Quelles destinations rêves-tu de visiter?', lookingFor:'Que cherches-tu sur TravelSoul?', next:'Suivant →', finish:'Terminer ✓', skip:'Passer', types:[{icon:'🏔️',key:'explorer',name:'Explorateur',desc:"J'aime les endroits inconnus"},{icon:'🎭',key:'adventurer',name:'Aventurier',desc:"Je cherche l'adrénaline"},{icon:'🎨',key:'culturist',name:'Culturiste',desc:'Art et histoire'},{icon:'🌴',key:'nomad',name:'Nomade',desc:'Je vis sans frontières'}], goals:[{icon:'✈️',key:'companion',name:'Compagnon de voyage'},{icon:'💕',key:'romance',name:'Connexion romantique'},{icon:'👥',key:'friends',name:'Nouveaux amis'}] },
    landing: { trusted:'🌟 Approuvé par 12,847 voyageurs', watchDemo:'Voir Démo ▶', stats:[{n:'12,847+',l:'Voyageurs actifs'},{n:'142',l:'Pays'},{n:'4,832',l:'Correspondances'},{n:'98%',l:'Satisfaction'}], howTitle:'Comment ça marche', how:[{icon:'🧬',title:'Crée ton ADN de Voyage',desc:'Passe notre quiz de personnalité'},{icon:'💫',title:'Trouve ton Soul Match',desc:'Connecte avec des voyageurs compatibles'},{icon:'✈️',title:'Voyagez Ensemble',desc:'Partagez l\'aventure'}], faqTitle:'FAQ', privacyTitle:'Politique de Confidentialité', termsTitle:'Conditions d\'utilisation', about:'À propos', contact:'Contact', allRights:'© 2026 IVIPRO. Tous droits réservés.', privacyContent:{ lastUpdated:'Dernière mise à jour : juin 2026', collect:'Données collectées', collectText:'TravelSoul collecte : nom, e-mail, pays, préférences de voyage et photos de profil. Les données sont stockées de manière sécurisée via Supabase. Nous utilisons Google OAuth pour la connexion.', use:'Utilisation de vos données', useText:'Vos données sont utilisées pour vous connecter avec des voyageurs compatibles et améliorer votre expérience.', storage:'Stockage des données', storageText:'Votre profil, vos messages et vos photos sont stockés de manière sécurisée sur les serveurs cloud de Supabase. Nous ne partageons pas vos données avec des tiers.', thirdParties:'Tiers', thirdPartiesText:'Nous utilisons Google OAuth (connexion) et Supabase (base de données). Ces services ont leurs propres politiques de confidentialité.', rights:'Vos droits (RGPD)', rightsText:'Vous avez le droit d\'accéder, corriger ou supprimer vos données à tout moment. Vous pouvez demander la suppression de votre compte en contactant support@travelsoul.love', cookies:'Cookies', cookiesText:'TravelSoul utilise uniquement des cookies fonctionnels pour stocker vos préférences.', contact:'Contact', contactText:'Questions de confidentialité : support@travelsoul.love' }, termsContent:{ lastUpdated:'Dernière mise à jour : juin 2026', eligibility:'Éligibilité', eligibilityText:'Vous devez avoir 18+ ans pour utiliser TravelSoul.', conduct:'Comportement des utilisateurs', conductText:'Les utilisateurs doivent se comporter avec respect. Le harcèlement et le contenu inapproprié sont interdits.', safety:'Sécurité en voyage', safetyText:'TravelSoul n\'est pas responsable des incidents pendant le voyage. Nous recommandons une assurance voyage.', premium:'Services premium', premiumText:"Les abonnements payants sont gérés via Google Play dans l'application Android. Vous pouvez y gérer ou annuler votre abonnement avant la prochaine date de facturation.", contact:'Contact', contactText:'Questions : support@travelsoul.love' }, refundContent:{ lastUpdated:"Dernière mise à jour : juin 2026", title:"Politique de Remboursement", intro:"Nous voulons que vous soyez satisfait des abonnements TravelSoul Premium et Elite.", eligibility:"Conditions de Remboursement", eligibilityText:"Vous pouvez demander un remboursement complet dans les 14 jours suivant votre achat initial si vous n'êtes pas satisfait, conformément à la législation européenne. Les remboursements s'appliquent uniquement aux premiers paiements d'abonnement.", process:"Comment Demander", processText:"Pour demander un remboursement, envoyez un e-mail à support@travelsoul.love avec l'e-mail de votre compte et la date d'achat. Les remboursements sont traités en 5 à 10 jours ouvrables.", nonRefundable:"Articles Non Remboursables", nonRefundableText:"Les pièces virtuelles et les achats numériques uniques ne sont pas remboursables une fois utilisés. Les renouvellements après la période initiale de 14 jours ne sont pas remboursables, mais vous pouvez annuler à tout moment.", contact:"Contact", contactText:"Pour les questions de remboursement : support@travelsoul.love" }, heroBadge:"✦ Voyage centré sur l'âme", demoAlert:'Démo bientôt disponible ! 🎬', travelerStories:'❤️ Histoires de Voyageurs', appStoreBanner:'📱 Bientôt sur App Store et Google Play', joinWaitlist:'Rejoindre la liste', footerTagline:'Connexions voyage avec âme' },
    extras: { addDest:'+ Ajouter', selectUpTo5:'Sélectionne jusqu\'à 5', back:'← Retour', photoSaved:'✅ Photo sauvegardée !', photoTooBig:'❌ Photo trop grande', addStory:'Ajouter', travelers:'voyageurs', upgradeBtn:'💎 Améliorer' },
    badges: { worldExplorer:{name:'Explorateur Mondial',desc:'Rejoint 3+ voyages'}, socialButterfly:{name:'Papillon Social',desc:'Envoyé 50+ messages'}, superLiker:{name:'Super Liker',desc:'Utilisé 10 Super Likes'}, onFire:{name:'En feu',desc:'Connecté 7 jours de suite'}, firstFlight:{name:'Premier Vol',desc:'Rejoint ton premier voyage'}, mountainClimber:{name:'Alpiniste',desc:'Rejoins un voyage de randonnée'}, beachLover:{name:'Amoureux de la plage',desc:'Rejoins 3 voyages balnéaires'}, eliteTraveler:{name:'Voyageur Élite',desc:'Passe à Elite'}, nightOwl:{name:'Oiseau de nuit',desc:'Utilise le mode nuit 10 fois'}, cartographer:{name:'Cartographe',desc:'Visite 10 pays'} },
    challenges: [{title:'🌏 Connecte avec l\'Asie',desc:'Envoie un message à quelqu\'un d\'un pays asiatique cette semaine',goal:1,reward:'🔥 1 Boost Gratuit'},{title:'⭐ Semaine Super Like',desc:'Envoie 3 Super Likes cette semaine',goal:3,reward:'↩️ Rewind x3'},{title:'🌍 Globe-Trotteur',desc:'Vois 10 profils de différents pays',goal:10,reward:'🌍 Pass Passeport'},{title:'🌙 Explorateur Nocturne',desc:'Utilise le mode nuit ce soir',goal:1,reward:'⭐ Super Like x5'}],
    videoIntro: { recordIntro:'Enregistre ta vidéo intro', uploadVideo:'Téléverser la vidéo', videoSuccess:'Vidéo envoyée ! ✅', playIntro:'▶ Intro', verifiedByVideo:'Ton profil est vérifié par vidéo ✅', startCamera:'Démarrer la caméra', previewReady:'Prêt à enregistrer', startRecording:'Commencer', stopRecording:'Arrêter', recording:'Enregistrement', uploading:'Envoi en cours...', previewVideo:'Aperçu vidéo', retake:'Recommencer', close:'Fermer', cameraError:'Caméra non disponible. Vérifie tes paramètres.', videoTooShort:'La vidéo doit durer au moins 15 secondes', videoTooLong:'Vidéo trop longue (max 30 sec)', videoTooBig:'Vidéo trop volumineuse (max 50 Mo)', duration15to30:'15–30 secondes', talkAbout:'Parle de ton voyage préféré', getVerified:'Obtiens le badge ✅', minMaxDuration:'Min. 15 sec · Max. 30 sec', introInstructions:'Enregistre une courte vidéo intro pour vérifier ton profil' },
    premiumBullets:{ boost:['Profil affiché en premier 30 min','3× plus de vues de profil','Indicateur doré exclusif'], rewind:['Annuler ton dernier passe','Deuxièmes chances avec de bons matchs','Disponible une fois par jour'], passport:['Voir des profils dans toute ville','Connecte avec voyageurs mondiaux','Changer de lieu quand tu veux'], incognito:['Naviguer invisiblement','Tes vues ne sont pas enregistrées','Activer/désactiver à tout moment'], filters:['Filtrer par âge, ADN, compatibilité','Filtre en ligne et vérifié','Contrôle de distance'], stats:['Tableau de bord statistiques complet','Graphique mensuel de voyages','Carte empreinte mondiale'], likes:['Likes illimités chaque jour','Sans limites quotidiennes','Aime qui tu veux'], whoViewed:['Voir qui a vu ton profil','Envoie-leur un message en premier','Notifications en temps réel'], videoChat:['Appels vidéo HD avec matchs','Disponible sur tous les appareils','Fonctionnalité Elite uniquement'], aiPlanner:['Itinéraire IA jour par jour','Planification de budget personnalisée','Partage avec ton groupe de voyage'], nightMode:['Voir qui est actif ce soir','Profils spéciaux nocturnes','Filtre de rencontre exclusif'], compatReport:['Analyse complète de compatibilité','5 dimensions notées','Rapport téléchargeable'], voiceMsg:['Envoyer des messages vocaux','Écouter ton match en premier','Audio lisible dans le chat'], gifts:['Envoyer des cadeaux virtuels','Livraison de cadeau animée','Montre que tu y tiens'], icebreaker:['Openers personnalisés par IA','Basés sur ADN de voyage','3 suggestions + actualiser'], dreamMatch:['Match sur destinations rêvées','Trouve ton co-voyageur','Basé sur ta liste de souhaits'], liveLocation:['Partager la position en temps réel','Coordination de groupe','Partage expirant en 1 heure'], splitCosts:['Calcul auto par personne','Suivi des dépenses du voyage','Envoyer au chat de groupe'], hotelMatch:['Voir qui a réservé à proximité','Coordonner les trajets','Se rencontrer avant le voyage'], aiWriter:['3 options de bio écrites par IA','Aventureuse/romantique/spirituelle','Démarque-toi de la foule'] },
    pact:{ pactBtn:'🤝 Faire un Pacte', pactTitle:'Pacte de Voyage', pactSub:'Engagez-vous à voyager ensemble', pactDestLabel:'Destination', pactMonthLabel:'Quand (mois/saison)', pactSend:'Envoyer le Pacte', pactDecline:'Refuser', pactAccept:'Accepter le Pacte', pactPending:'⏳ Pacte en attente', pactActive:'✈️ Pacte Actif', pactFulfilled:'✅ Pacte Accompli', pactCountdown:'jours avant le voyage', pactAiSuggest:'✨ IA Suggérer Destination', pactTrophy:'🏆 Voyagé Ensemble', pactLimitFree:'Améliorez pour envoyer des Pactes', pactLimit1:"Améliorez vers Elite pour des Pactes illimités", pactSuccess:"Pacte envoyé ! En attente d'acceptation...", pactDeclined:'Pacte refusé' },
    info:{ height:'Taille', bodyType:'Morphologie', smoking:'Tabac', alcohol:'Alcool', children:'Enfants', partnerPrefs:'Préférences partenaire', goalLabel:'Je cherche', smoker:'🚬 Fumeur', nonSmoker:'🚭 Non-fumeur', occasionally:'🌿 Occasionnellement', drinker:'🍷 Buveur', nonDrinker:'🚱 Ne boit pas', socially:'🥂 Socialement' },
  },
  de: {
    brand: 'TravelSoul',
    tagline: ['Finde deine Seele', 'durch das Reisen', 'Entdecke die Welt gemeinsam'],
    cta: 'Starte deine Reise', login: 'Anmelden',
    features: [
      { icon: '🧬', title: 'Reise-DNA', desc: 'Eine einzigartige Karte deiner Reisepersönlichkeit in 4 Merkmalen' },
      { icon: '♥', title: 'Seelen-Matching', desc: 'Triff Reisende, deren Träume mit deinen übereinstimmen' },
      { icon: '✈', title: 'Gruppenreisen', desc: 'Nimm an sorgfältig ausgewählten Gruppenabenteuern teil' },
      { icon: '🛡', title: 'Sicher & Privat', desc: 'Deine Privatsphäre und Sicherheit haben höchste Priorität' },
    ],
    nav: { explore: 'Entdecken', connect: 'Verbinden', journeys: 'Reisen', messages: 'Chat' },
    register: { title: 'Tritt TravelSoul bei', sub: 'Deine Reise beginnt hier', name: 'Vollständiger Name', email: 'E-Mail-Adresse', password: 'Passwort', confirmPassword: 'Passwort bestätigen', passwordMismatch: 'Passwörter stimmen nicht überein', passwordMatch: 'Passwörter stimmen überein ✅', weakPassword: 'Schwaches Passwort', mediumPassword: 'Mittleres Passwort', strongPassword: 'Starkes Passwort', country: 'Land auswählen', submit: 'Profil erstellen', login: 'Bereits ein Konto? Anmelden', birthDate:'Geburtsdatum', ageError:'Du musst 18+ Jahre alt sein', selectBirthDate:'Geburtsdatum auswählen' },
    ageVerification: { title: 'Altersverifizierung', msg: 'Wir müssen bestätigen, dass Sie 18 Jahre oder älter sind, um TravelSoul weiter zu nutzen.', submit: 'Bestätigen', saving: 'Wird gespeichert...', underageTitle: 'Es tut uns leid', underageMsg: 'TravelSoul ist nur für Nutzer ab 18 Jahren verfügbar. Sie können die App nicht weiter nutzen.', signOut: 'Abmelden' },
    loginScreen: { title: 'Willkommen zurück', sub: 'Melde dich in deinem Konto an', email: 'E-Mail-Adresse', password: 'Passwort', submit: 'Anmelden', register: 'Kein Konto? Registrieren', forgot: 'Passwort vergessen?', resetSent: '✅ Passwort-Reset-E-Mail gesendet!', error: 'Falsche E-Mail oder falsches Passwort', wrongPassword: 'Falsche E-Mail oder Passwort' },
    explore: { title: 'Reisen entdecken', spots: 'Plätze übrig', join: 'Beitreten', hot: 'Beliebt', days: 'Tage', aiPicks: 'ANGESAGTE REISEZIELE', refresh: 'Mehr entdecken', host: 'Gastgeber', joined: '✓ Beigetreten', aiRecommended: '🤖 KI-Empfehlung' },
    connect: {
      title: 'Finde deine Seele', sub: 'Reisende jetzt online',
      online: 'online', offline: 'offline', like: 'Mögen', liked: 'Gefällt mir', pass: 'Weiter', match: 'Kompatibilität', dnaSection: 'Deine Reise-DNA',
      whoViewed: 'Wer hat dein Profil gesehen', swipeMode: 'Wischen', gridMode: 'Raster',
      seenAll: 'Du hast alle gesehen! 🎉', superLikeSent: '✨ Super Like gesendet!', itsAMatch: '💫 Es ist ein Match!',
      sendMessage: 'Nachricht senden', keepSwiping: 'Weiter wischen',
      keepGoing: 'Weiter', sendMsg: 'Nachricht senden', passedToast: 'Übersprungen ✓',
      boost: 'Boost', boostActive: 'Aktiver Boost', boostDesc: 'Dein Profil ist #1 für 30 Minuten',
      passport: 'Reisepass', passportDesc: 'Profile aus einer anderen Stadt ansehen', viewingIn: 'Profile ansehen in',
      incognito: 'Inkognito', incognitoOn: 'Du bist unsichtbar', incognitoOff: 'Für andere sichtbar',
      rewindBtn: '↩️ Rückgängig', nothingToRewind: 'Nichts rückgängig zu machen',
      filtersBtn: '🎯 Filter', premiumFeature: 'Dies ist eine Premium-Funktion', upgrade: 'Auf Premium upgraden', seePlans: 'Alle Pläne anzeigen',
      filters: { age:'Alter:', travelDna:'Reise-DNA', minCompat:'Min. Kompatibilität:', onlineOnly:'Nur Online', verifiedOnly:'Nur Verifizierte', distance:'Entfernung', clear:'Löschen', apply:'Anwenden', dist50:'50km', dist500:'500km', distWorld:'weltweit' },
      swipeLabels: { match:'MATCH!', liked:'GEMOCHT ♥', super:'SUPER ⭐', nope:'NEIN ✗' },
    },
    journeys: { title: 'Meine Reisen', tabs: ['Bevorstehend', 'Geplant', 'Abgeschlossen', 'Rangliste'], empty: 'Noch keine Reisen. Entdecke und tritt bei!', departs: 'Abfahrt', tripsLabel: 'Reisen', countriesLabel: 'Länder', youLabel: 'Du', statsTab: 'Statistiken', stats: { countries: 'Besuchte Länder', trips: 'Reisen gesamt', km: 'Km gereist', matches: 'Übereinstimmungen', superLikes:'Super Likes', leaderboard:'Rangliste', footprint:'🌍 Reise-Fußabdruck', tripsPerMonth:'📅 Reisen pro Monat' } },
    messages: { title: 'Nachrichten', placeholder: 'Schreibe eine Nachricht...', send: 'Senden', you: 'Du', translate: 'Übersetzen', autoTranslate: 'Auto-übersetzen', directMessages: '💬 Direktnachrichten', groupChats: '👥 Gruppenchats', joinedGroupChat: '✅ Beigetreten! Du bist jetzt im Gruppenchat.', videoCall: 'Videoanruf', calling: 'Wird angerufen...', connected: 'Verbunden', endCall: 'Auflegen', mute:'Stummschalten', camera:'Kamera', chat:'Chat', voiceMsg:'🎤 Sprachnachricht', expiresIn1h:'— läuft in 1h ab', voice:'Stimme', giftSent:'🎁 Schickte dir', downloadLoveStory:'Unsere Geschichte laden' },
    dna: { title: 'Deine Reise-DNA', sub: 'Entdecke deine Reisepersönlichkeit', start: 'Meine DNA enthüllen', retake: 'Wiederholen', result: 'Du bist ein', traits: { explorer: 'Entdecker', adventurer: 'Abenteurer', culturist: 'Kulturist', nomad: 'Nomade' } },
    quiz: [
      { q: 'Was ist dein ideales Reiseziel?', opts: ['Wilde Natur & Berge', 'Alte Städte & Kultur', 'Strand & Entspannung', 'Alles, solange es neu ist'] },
      { q: 'Wie reist du am liebsten?', opts: ['Völlig spontan', 'Mit einem groben Plan', 'Alles im Voraus organisiert'] },
      { q: 'Was suchst du in einem Reisegefährten?', opts: ['Gleiche Interessen', 'Eine andere Perspektive', 'Ich reise lieber allein'] },
      { q: 'Wie lange ist deine ideale Reise?', opts: ['Wochenend-Ausflug', '1–2 Wochen', '1 Monat oder länger'] },
    ],
    profile: { myProfile:'Mein Profil', editProfile:'Profil bearbeiten', saveProfile:'Profil speichern', profileSaved:'Profil gespeichert ✅', changePhoto:'Foto ändern', bio:'Über mich', age:'Alter', dreamDest:'Traumziele', travelStyle:'Reisestil', instagram:'Instagram', lookingFor:'Ich suche', firstName:'Vorname', lastName:'Nachname', city:'Stadt', travelTags:['Entdecker','Abenteurer','Kulturist','Nomade','Feinschmecker','Fotograf'], dreamPlaceholder:'Japan, Island...' },
    notif: { notifications:'Benachrichtigungen', allowNotif:'Benachrichtigungen erlauben', notifDesc:'Erhalte Benachrichtigungen für neue Matches', newMatch:'Neues Match!', viewedProfile:'Jemand hat dein Profil gesehen', noNotifications:'Keine Benachrichtigungen', later:'Später', newSuperLike:'Neues Super Like!', fromSofia:'aus Sofia', sentYou:'hat dir gesendet' },
    onboarding: { welcome:'Willkommen bei TravelSoul!', welcomeSub:'Lass uns dein Profil in 1 Minute einrichten', whatType:'Welcher Reisetyp bist du?', destinations:'Welche Reiseziele träumst du zu besuchen?', lookingFor:'Was suchst du bei TravelSoul?', next:'Weiter →', finish:'Setup abschließen ✓', skip:'Überspringen', types:[{icon:'🏔️',key:'explorer',name:'Entdecker',desc:'Ich liebe unentdeckte Orte'},{icon:'🎭',key:'adventurer',name:'Abenteurer',desc:'Ich suche Adrenalin'},{icon:'🎨',key:'culturist',name:'Kulturist',desc:'Kunst und Geschichte'},{icon:'🌴',key:'nomad',name:'Nomade',desc:'Ich lebe ohne Grenzen'}], goals:[{icon:'✈️',key:'companion',name:'Reisebegleiter'},{icon:'💕',key:'romance',name:'Romantische Verbindung'},{icon:'👥',key:'friends',name:'Neue Freunde'}] },
    landing: { trusted:'🌟 Von 12.847 Reisenden vertraut', watchDemo:'Demo ansehen ▶', stats:[{n:'12.847+',l:'Aktive Reisende'},{n:'142',l:'Länder'},{n:'4.832',l:'Matches'},{n:'98%',l:'Zufriedenheit'}], howTitle:'So funktioniert es', how:[{icon:'🧬',title:'Erstelle deine Reise-DNA',desc:'Mach unseren Persönlichkeitstest'},{icon:'💫',title:'Finde deinen Soul Match',desc:'Verbinde dich mit kompatiblen Reisenden'},{icon:'✈️',title:'Reise Gemeinsam',desc:'Teile das Abenteuer deines Lebens'}], faqTitle:'FAQ', privacyTitle:'Datenschutzrichtlinie', termsTitle:'Nutzungsbedingungen', about:'Über uns', contact:'Kontakt', allRights:'© 2026 IVIPRO. Alle Rechte vorbehalten.', privacyContent:{ lastUpdated:'Letzte Aktualisierung: Juni 2026', collect:'Erhobene Daten', collectText:'TravelSoul erhebt: Name, E-Mail, Land, Reisepräferenzen und Profilfotos. Die Daten werden sicher über die Supabase-Cloud-Infrastruktur gespeichert. Wir verwenden Google OAuth für die Anmeldung.', use:'Verwendung deiner Daten', useText:'Deine Daten werden verwendet, um dich mit kompatiblen Reisenden zu verbinden und deine Erfahrung zu verbessern.', storage:'Datenspeicherung', storageText:'Dein Profil, Nachrichten und Fotos werden sicher auf den Cloud-Servern von Supabase gespeichert. Wir teilen deine Daten nicht mit Dritten.', thirdParties:'Drittanbieter', thirdPartiesText:'Wir verwenden Google OAuth (Anmeldung) und Supabase (Datenbank). Diese Dienste haben eigene Datenschutzrichtlinien.', rights:'Deine Rechte (DSGVO)', rightsText:'Du hast jederzeit das Recht, auf deine Daten zuzugreifen, sie zu korrigieren oder zu löschen. Du kannst die Löschung deines Kontos beantragen, indem du support@travelsoul.love kontaktierst.', cookies:'Cookies', cookiesText:'TravelSoul verwendet nur funktionale Cookies zum Speichern deiner Einstellungen.', contact:'Kontakt', contactText:'Für Datenschutzfragen: support@travelsoul.love' }, termsContent:{ lastUpdated:'Letzte Aktualisierung: Juni 2026', eligibility:'Berechtigung', eligibilityText:'Du musst 18+ Jahre alt sein, um TravelSoul zu nutzen.', conduct:'Nutzerverhalten', conductText:'Nutzer müssen sich respektvoll verhalten. Belästigung und unangemessene Inhalte sind verboten.', safety:'Reisesicherheit', safetyText:'TravelSoul ist nicht verantwortlich für Vorfälle während der Reise. Wir empfehlen eine Reiseversicherung.', premium:'Premium-Dienste', premiumText:'Bezahlte Abonnements werden über Google Play in der Android-App verwaltet. Du kannst dein Abonnement dort vor dem nächsten Abrechnungsdatum verwalten oder kündigen.', contact:'Kontakt', contactText:'Fragen: support@travelsoul.love' }, refundContent:{ lastUpdated:'Letzte Aktualisierung: Juni 2026', title:'Rückerstattungsrichtlinie', intro:'Wir möchten, dass Sie mit den TravelSoul Premium und Elite Abonnements zufrieden sind.', eligibility:'Rückerstattungsberechtigung', eligibilityText:'Sie können innerhalb von 14 Tagen nach Ihrem ersten Abonnementkauf eine vollständige Rückerstattung beantragen, wenn Sie nicht zufrieden sind, gemäß dem EU-Verbraucherschutzrecht. Rückerstattungen gelten nur für Erstzahlungen.', process:'Wie Sie Anfragen', processText:'Um eine Rückerstattung zu beantragen, senden Sie eine E-Mail an support@travelsoul.love mit Ihrer Konto-E-Mail und dem Kaufdatum. Rückerstattungen werden innerhalb von 5-10 Werktagen verarbeitet.', nonRefundable:'Nicht Erstattungsfähige Artikel', nonRefundableText:'Virtuelle Münzen und einmalige digitale Käufe sind nach der Verwendung nicht erstattungsfähig. Abonnementverlängerungen nach dem anfänglichen 14-Tage-Zeitraum sind nicht erstattungsfähig, aber Sie können jederzeit kündigen.', contact:'Kontakt', contactText:'Für Rückerstattungsfragen: support@travelsoul.love' }, heroBadge:'✦ Seele zuerst reisen', demoAlert:'Demo kommt bald! 🎬', travelerStories:'❤️ Reisegeschichten', appStoreBanner:'📱 Bald im App Store & Google Play', joinWaitlist:'Warteliste beitreten', footerTagline:'Seelenvolle Reiseverbindungen' },
    extras: { addDest:'+ Hinzufügen', selectUpTo5:'Wähle bis zu 5', back:'← Zurück', photoSaved:'✅ Foto gespeichert!', photoTooBig:'❌ Foto zu groß', addStory:'Hinzufügen', travelers:'Reisende', upgradeBtn:'💎 Upgraden' },
    badges: { worldExplorer:{name:'Weltentdecker',desc:'3+ Reisen beigetreten'}, socialButterfly:{name:'Sozialer Schmetterling',desc:'50+ Nachrichten gesendet'}, superLiker:{name:'Super Liker',desc:'10 Super Likes verwendet'}, onFire:{name:'In Flammen',desc:'7 Tage in Folge eingeloggt'}, firstFlight:{name:'Erster Flug',desc:'Deiner ersten Reise beigetreten'}, mountainClimber:{name:'Bergsteiger',desc:'Einer Wanderreise beitreten'}, beachLover:{name:'Strandliebhaber',desc:'3 Strandreisen beitreten'}, eliteTraveler:{name:'Elite-Reisender',desc:'Auf Elite upgraden'}, nightOwl:{name:'Nachteule',desc:'10x Nachtmodus nutzen'}, cartographer:{name:'Kartograf',desc:'10 Länder besuchen'} },
    challenges: [{title:'🌏 Mit Asien verbinden',desc:'Schreibe diese Woche jemandem aus einem asiatischen Land',goal:1,reward:'🔥 1 kostenloser Boost'},{title:'⭐ Super-Like-Woche',desc:'Sende diese Woche 3 Super Likes',goal:3,reward:'↩️ Rewind x3'},{title:'🌍 Weltenbummler',desc:'Sieh dir 10 Profile aus verschiedenen Ländern an',goal:10,reward:'🌍 Reisepass-Tagespass'},{title:'🌙 Nacht-Entdecker',desc:'Nutze heute Nacht den Nachtmodus',goal:1,reward:'⭐ Super Like x5'}],
    videoIntro: { recordIntro:'Intro-Video aufnehmen', uploadVideo:'Video hochladen', videoSuccess:'Video hochgeladen! ✅', playIntro:'▶ Intro', verifiedByVideo:'Dein Profil ist per Video verifiziert ✅', startCamera:'Kamera starten', previewReady:'Bereit zur Aufnahme', startRecording:'Aufnahme starten', stopRecording:'Aufnahme stoppen', recording:'Aufnahme läuft', uploading:'Wird hochgeladen...', previewVideo:'Videovorschau', retake:'Wiederholen', close:'Schließen', cameraError:'Kamera nicht verfügbar. Überprüfe deine Einstellungen.', videoTooShort:'Video muss mindestens 15 Sekunden lang sein', videoTooLong:'Video zu lang (max. 30 Sek.)', videoTooBig:'Video zu groß (max. 50 MB)', duration15to30:'15–30 Sekunden', talkAbout:'Erzähl von deiner Lieblingsreise', getVerified:'Hol dir das ✅ Badge', minMaxDuration:'Mind. 15 Sek · Max. 30 Sek', introInstructions:'Nimm ein kurzes Intro-Video auf, um dein Profil zu verifizieren' },
    premiumBullets:{ boost:['Profil 30 Min. zuerst angezeigt','3× mehr Profilaufrufe','Exklusiver Goldring-Indikator'], rewind:['Letzten Pass rückgängig machen','Zweite Chancen mit guten Matches','Einmal täglich verfügbar'], passport:['Profile in jeder Stadt ansehen','Reisende weltweit matchen','Standort jederzeit ändern'], incognito:['Unsichtbar browsen','Deine Aufrufe werden nicht gespeichert','Jederzeit ein-/ausschalten'], filters:['Nach Alter, DNA, Kompatibilität filtern','Nur-Online & verifiziert Filter','Entfernungskontrolle'], stats:['Vollständiges Reise-Dashboard','Monatliches Reisediagramm','Weltweite Fußabdruckkarte'], likes:['Unbegrenzte Likes täglich','Keine täglichen Limits','Like jeden den du willst'], whoViewed:['Sieh alle die dein Profil sahen','Schreib ihnen zuerst','Echtzeit-Benachrichtigungen'], videoChat:['HD-Videoanrufe mit Matches','Auf allen Geräten verfügbar','Nur für Elite'], aiPlanner:['KI Tag-für-Tag Reiseplan','Individuelle Budgetplanung','Mit Reisegruppe teilen'], nightMode:['Sieh wer heute Nacht aktiv ist','Spezielle Nachtprofile','Exklusiver Treffpunkt-Filter'], compatReport:['Vollständige Kompatibilitätsanalyse','5 Dimensionen bewertet','Downloadbarer PDF-Bericht'], voiceMsg:['Sprachnachrichten senden','Deinen Match zuerst hören','Im Chat abspielbar'], gifts:['Virtuelle Geschenke senden','Animierte Geschenklieferung','Zeig dass du dich kümmerst'], icebreaker:['KI-personalisierte Eröffner','Basierend auf Reise-DNA','3 Vorschläge + aktualisieren'], dreamMatch:['Match auf Traumziele','Finde deinen Mitreisenden','Basierend auf deiner Wunschliste'], liveLocation:['Echtzeit-Standort teilen','Gruppenkoordination','Läuft in 1 Stunde ab'], splitCosts:['Automatische Pro-Person-Berechnung','Alle Reisekosten verfolgen','An Gruppenchat senden'], hotelMatch:['Sieh wer in der Nähe gebucht hat','Transfers koordinieren','Treffen vor der Reise'], aiWriter:['3 KI-geschriebene Bio-Optionen','Abenteuerlich/romantisch/witzig','Hebe dich aus der Menge ab'] },
    pact:{ pactBtn:'🤝 Pakt schließen', pactTitle:'Reisepakt', pactSub:'Verpflichtet euch gemeinsam zu reisen', pactDestLabel:'Reiseziel', pactMonthLabel:'Wann (Monat/Saison)', pactSend:'Pakt senden', pactDecline:'Ablehnen', pactAccept:'Pakt annehmen', pactPending:'⏳ Pakt ausstehend', pactActive:'✈️ Pakt aktiv', pactFulfilled:'✅ Pakt erfüllt', pactCountdown:'Tage bis zur Reise', pactAiSuggest:'✨ KI Ziel vorschlagen', pactTrophy:'🏆 Zusammen gereist', pactLimitFree:'Upgrade um Reisepakte zu senden', pactLimit1:'Upgrade auf Elite für unbegrenzte Pakte', pactSuccess:'Pakt gesendet! Warte auf Bestätigung...', pactDeclined:'Pakt abgelehnt' },
    info:{ height:'Größe', bodyType:'Körpertyp', smoking:'Rauchen', alcohol:'Alkohol', children:'Kinder', partnerPrefs:'Partnerpräferenzen', goalLabel:'Ich suche', smoker:'🚬 Raucher', nonSmoker:'🚭 Nichtraucher', occasionally:'🌿 Gelegentlich', drinker:'🍷 Trinker', nonDrinker:'🚱 Trinkt nicht', socially:'🥂 Gesellschaftlich' },
  },
};

const EXTRA_MAIN_T = {
  el: {
    brand: 'TravelSoul',
    tagline: ['Βρες την ψυχή σου', 'μέσα από τα ταξίδια', 'Ανακαλύψτε τον κόσμο μαζί'],
    cta: 'Ξεκίνα το ταξίδι', login: 'Σύνδεση',
    challenges: [{title:'🌏 Συνδέσου με την Ασία',desc:'Στείλε μήνυμα σε κάποιον από ασιατική χώρα αυτή την εβδομάδα',goal:1,reward:'🔥 1 Δωρεάν Boost'},{title:'⭐ Εβδομάδα Super Like',desc:'Στείλε 3 Super Likes αυτή την εβδομάδα',goal:3,reward:'↩️ Rewind x3'},{title:'🌍 Παγκόσμιος Ταξιδιώτης',desc:'Δες 10 προφίλ από διαφορετικές χώρες',goal:10,reward:'🌍 Ημερήσιο πάσο Διαβατηρίου'},{title:'🌙 Νυχτερινός Εξερευνητής',desc:'Χρησιμοποίησε τη Νυχτερινή λειτουργία απόψε',goal:1,reward:'⭐ Super Like x5'}],
    premiumBullets:{ boost:['Το προφίλ σου εμφανίζεται πρώτο για 30 λεπτά','3× περισσότερες προβολές προφίλ','Αποκλειστική χρυσή ένδειξη'], rewind:['Ακύρωσε την τελευταία σου παράλειψη','Δεύτερες ευκαιρίες με καλά ταιριάσματα','Διαθέσιμο μία φορά την ημέρα'], passport:['Περιήγηση σε προφίλ από κάθε πόλη','Ταίριασε με ταξιδιώτες παγκοσμίως','Άλλαξε τοποθεσία όποτε θέλεις'], incognito:['Περιηγήσου αόρατα','Οι προβολές σου δεν καταγράφονται','Ενεργοποίηση/απενεργοποίηση όποτε θέλεις'], filters:['Φίλτρο κατά ηλικία, DNA, συμβατότητα','Φίλτρο μόνο online & επαληθευμένοι','Έλεγχος απόστασης'], stats:['Πλήρης πίνακας ταξιδιωτικών στατιστικών','Μηνιαίο γράφημα ταξιδιών','Χάρτης παγκόσμιου αποτυπώματος'], likes:['Απεριόριστα likes κάθε μέρα','Χωρίς ημερήσια όρια','Like σε όποιον θέλεις'], whoViewed:['Δες όλους όσους είδαν το προφίλ σου','Στείλε τους μήνυμα πρώτος','Ειδοποιήσεις σε πραγματικό χρόνο'], videoChat:['HD βιντεοκλήσεις με ταιριάσματα','Διαθέσιμο σε όλες τις συσκευές','Μόνο για Elite'], aiPlanner:['AI δρομολόγιο μέρα με τη μέρα','Προσαρμοσμένος σχεδιασμός προϋπολογισμού','Μοιράσου με την ταξιδιωτική σου παρέα'], nightMode:['Δες ποιος είναι ενεργός απόψε','Ειδικά νυχτερινά προφίλ','Αποκλειστικό φίλτρο συναντήσεων'], compatReport:['Πλήρης ανάλυση συμβατότητας','5 μετρημένες διαστάσεις','Λήψη αναφοράς'], voiceMsg:['Στείλε φωνητικά μηνύματα','Άκου πρώτος το ταίριασμά σου','Ήχος στο chat'], gifts:['Στείλε εικονικά δώρα','Κινούμενη παράδοση δώρου','Δείξε ότι νοιάζεσαι'], icebreaker:['AI-προσαρμοσμένα ανοίγματα','Βασισμένα στο ταξιδιωτικό DNA','3 προτάσεις + ανανέωση'], dreamMatch:['Ταίριασμα βάσει ονειρεμένων προορισμών','Βρες τον συνταξιδιώτη σου','Βασισμένο στη λίστα επιθυμιών σου'], liveLocation:['Μοιράσου τοποθεσία σε πραγματικό χρόνο','Ομαδικός συντονισμός','Λήγει μετά από 1 ώρα'], splitCosts:['Αυτόματος υπολογισμός ανά άτομο','Παρακολούθησε όλα τα έξοδα','Στείλε στο ομαδικό chat'], hotelMatch:['Δες ποιος έχει κάνει κράτηση κοντά','Συντόνισε συναντήσεις','Γνωριστείτε πριν το ταξίδι'], aiWriter:['3 βιογραφικά γραμμένα από AI','Περιπετειώδες/ρομαντικό/πνευματώδες','Ξεχώρισε από το πλήθος'] },
    features: [
      { icon: '🧬', title: 'Ταξιδιωτικό DNA', desc: 'Ένα μοναδικό προφίλ της ταξιδιωτικής σου προσωπικότητας' },
      { icon: '♥', title: 'Συμβατότητα', desc: 'Γνώρισε ταξιδιώτες με όνειρα που ταιριάζουν με τα δικά σου' },
      { icon: '✈', title: 'Ομαδικά ταξίδια', desc: 'Μπες σε επιλεγμένες περιπέτειες με ανθρώπους που σου ταιριάζουν' },
      { icon: '🛡', title: 'Ασφάλεια', desc: 'Η ιδιωτικότητά σου είναι η πρώτη μας προτεραιότητα' },
    ],
    nav: { explore: 'Εξερεύνηση', connect: 'Σύνδεση', journeys: 'Ταξίδια', messages: 'Μηνύματα' },
    register: { title: 'Γίνε μέλος', sub: 'Το ταξίδι σου ξεκινά εδώ', name: 'Ονοματεπώνυμο', email: 'Email', password: 'Κωδικός', confirmPassword: 'Επιβεβαίωση κωδικού', passwordMismatch: 'Οι κωδικοί δεν ταιριάζουν', passwordMatch: 'Οι κωδικοί ταιριάζουν ✅', weakPassword: 'Αδύναμος κωδικός', mediumPassword: 'Μέτριος κωδικός', strongPassword: 'Ισχυρός κωδικός', country: 'Επίλεξε χώρα', submit: 'Δημιουργία προφίλ', login: 'Έχεις ήδη λογαριασμό; Σύνδεση', birthDate:'Ημερομηνία γέννησης', ageError:'Πρέπει να είσαι 18+ ετών', selectBirthDate:'Επίλεξε ημερομηνία γέννησης' },
    ageVerification: { title: 'Επιβεβαίωση ηλικίας', msg: 'Πρέπει να επιβεβαιώσουμε ότι είστε 18 ετών ή άνω για να συνεχίσετε να χρησιμοποιείτε το TravelSoul.', submit: 'Επιβεβαίωση', saving: 'Αποθήκευση...', underageTitle: 'Λυπούμαστε', underageMsg: 'Το TravelSoul είναι διαθέσιμο μόνο για χρήστες 18 ετών και άνω. Δεν μπορείτε να συνεχίσετε να χρησιμοποιείτε την εφαρμογή.', signOut: 'Αποσύνδεση' },
    loginScreen: { title: 'Καλώς ήρθες ξανά', sub: 'Συνδέσου στον λογαριασμό σου', email: 'Email', password: 'Κωδικός', submit: 'Σύνδεση', register: 'Δεν έχεις λογαριασμό; Εγγραφή', forgot: 'Ξέχασες τον κωδικό;', resetSent: '✅ Στείλαμε email επαναφοράς!', error: 'Λάθος email ή κωδικός', wrongPassword: 'Λάθος email ή κωδικός' },
    explore: { title: 'Ανακάλυψε ταξίδια', spots: 'θέσεις διαθέσιμες', join: 'Συμμετοχή', hot: 'Δημοφιλές', days: 'ημέρες', aiPicks: 'ΔΗΜΟΦΙΛΕΙΣ ΠΡΟΟΡΙΣΜΟΙ', refresh: 'Ανακάλυψε περισσότερα', host: 'Οικοδεσπότης', joined: '✓ Συμμετέχεις', aiRecommended: '🤖 Πρόταση AI' },
    connect: {
      title: 'Βρες την ταξιδιωτική σου ψυχή', sub: 'ταξιδιώτες online τώρα',
      online: 'online', offline: 'offline', like: 'Μου αρέσει', liked: 'Άρεσε', pass: 'Παράλειψη', match: 'Συμβατότητα', dnaSection: 'Το ταξιδιωτικό σου DNA',
      whoViewed: 'Ποιος είδε το προφίλ σου', swipeMode: 'Swipe', gridMode: 'Πλέγμα',
      seenAll: 'Τα είδες όλα! 🎉', superLikeSent: '✨ Super Like στάλθηκε!', itsAMatch: '💫 Ταιριάξατε!',
      sendMessage: 'Στείλε μήνυμα', keepSwiping: 'Συνέχισε', keepGoing: 'Συνέχισε', sendMsg: 'Στείλε μήνυμα', passedToast: 'Παραλείφθηκε ✓',
      boost: 'Boost', boostActive: 'Boost ενεργό', boostDesc: 'Το προφίλ σου εμφανίζεται πρώτο για 30 λεπτά',
      passport: 'Διαβατήριο', passportDesc: 'Δες προφίλ από άλλη πόλη', viewingIn: 'Βλέπεις προφίλ σε',
      incognito: 'Ανώνυμη λειτουργία', incognitoOn: 'Η ανώνυμη λειτουργία είναι ενεργή', incognitoOff: 'Ορατός στους άλλους',
      rewindBtn: '↩️ Πίσω', nothingToRewind: 'Δεν υπάρχει κάτι για επιστροφή',
      filtersBtn: '🎯 Φίλτρα', premiumFeature: 'Premium λειτουργία', upgrade: 'Αναβάθμιση σε Premium', seePlans: 'Δες τα πλάνα',
      filters: { age:'Ηλικία:', travelDna:'Ταξιδιωτικό DNA', minCompat:'Ελάχιστη συμβατότητα:', onlineOnly:'Μόνο online', verifiedOnly:'Μόνο επαληθευμένοι', distance:'Απόσταση', clear:'Καθαρισμός', apply:'Εφαρμογή', dist50:'50 χλμ', dist500:'500 χλμ', distWorld:'όλος ο κόσμος' },
      swipeLabels: { match:'ΤΑΙΡΙΑΣΜΑ!', liked:'ΑΡΕΣΕ ♥', super:'SUPER ⭐', nope:'ΟΧΙ ✗' },
    },
    journeys: { title: 'Τα ταξίδια μου', tabs: ['Επερχόμενα', 'Σχεδιασμένα', 'Ολοκληρωμένα', 'Κατάταξη'], empty: 'Δεν έχεις ακόμη ταξίδια. Εξερεύνησε και μπες σε ένα!', departs: 'Αναχώρηση', tripsLabel: 'Ταξίδια', countriesLabel: 'Χώρες', youLabel: 'Εσύ', statsTab: 'Στατιστικά', stats: { countries: 'Χώρες που επισκέφθηκες', trips: 'Ταξίδια', km: 'Χιλιόμετρα', matches: 'Ταιριάσματα', superLikes:'Super Likes', leaderboard:'Κατάταξη', footprint:'🌍 Ταξιδιωτικό αποτύπωμα', tripsPerMonth:'📅 Ταξίδια ανά μήνα' } },
    messages: { title: 'Μηνύματα', placeholder: 'Γράψε μήνυμα...', send: 'Αποστολή', you: 'Εσύ', translate: 'Μετάφραση', autoTranslate: 'Αυτόματη μετάφραση', directMessages: '💬 Προσωπικά μηνύματα', groupChats: '👥 Ομαδικές συνομιλίες', joinedGroupChat: '✅ Μπήκες στην ομαδική συνομιλία.', videoCall: 'Βιντεοκλήση', calling: 'Κλήση...', connected: 'Συνδέθηκε', endCall: 'Τερματισμός', mute:'Σίγαση', camera:'Κάμερα', chat:'Chat', voiceMsg:'🎤 Φωνητικό μήνυμα', expiresIn1h:'— λήγει σε 1 ώρα', voice:'Φωνή', giftSent:'🎁 Σου έστειλε', downloadLoveStory:'Λήψη της ιστορίας μας' },
    dna: { title: 'Το ταξιδιωτικό σου DNA', sub: 'Ανακάλυψε την ταξιδιωτική σου προσωπικότητα', start: 'Αποκάλυψε το DNA μου', retake: 'Ξανά', result: 'Είσαι', traits: { explorer: 'Εξερευνητής', adventurer: 'Περιπετειώδης', culturist: 'Λάτρης πολιτισμού', nomad: 'Νομάς' } },
    quiz: [
      { q: 'Ποιος είναι ο ιδανικός προορισμός σου;', opts: ['Άγρια φύση και βουνά', 'Αρχαίες πόλεις και πολιτισμός', 'Παραλία και χαλάρωση', 'Οτιδήποτε, αρκεί να είναι νέο'] },
      { q: 'Πώς προτιμάς να ταξιδεύεις;', opts: ['Εντελώς αυθόρμητα', 'Με ένα γενικό πλάνο', 'Όλα οργανωμένα από πριν'] },
      { q: 'Τι αναζητάς σε έναν συνταξιδιώτη;', opts: ['Κοινά ενδιαφέροντα', 'Μια διαφορετική οπτική', 'Προτιμώ να ταξιδεύω μόνος/η'] },
      { q: 'Πόσο διαρκεί το ιδανικό σου ταξίδι;', opts: ['Απόδραση Σαββατοκύριακου', '1–2 εβδομάδες', '1 μήνα ή περισσότερο'] },
    ],
    profile: { myProfile:'Το προφίλ μου', editProfile:'Επεξεργασία προφίλ', saveProfile:'Αποθήκευση προφίλ', profileSaved:'Το προφίλ αποθηκεύτηκε ✅', changePhoto:'Αλλαγή φωτογραφίας', bio:'Σχετικά με μένα', age:'Ηλικία', dreamDest:'Ονειρεμένοι προορισμοί', travelStyle:'Στυλ ταξιδιού', instagram:'Instagram', lookingFor:'Αναζητώ', firstName:'Όνομα', lastName:'Επώνυμο', city:'Πόλη', travelTags:['Εξερευνητής','Περιπετειώδης','Πολιτισμός','Νομάς','Foodie','Φωτογράφος'], dreamPlaceholder:'Ιαπωνία, Ισλανδία...' },
    notif: { notifications:'Ειδοποιήσεις', allowNotif:'Να επιτρέπονται ειδοποιήσεις', notifDesc:'Λάβε ειδοποιήσεις για νέα ταιριάσματα', newMatch:'Νέο ταίριασμα!', viewedProfile:'Κάποιος είδε το προφίλ σου', noNotifications:'Δεν υπάρχουν ειδοποιήσεις', later:'Αργότερα', newSuperLike:'Νέο Super Like!', fromSofia:'από τη Σόφια', sentYou:'σου έστειλε' },
    onboarding: { welcome:'Καλώς ήρθες στο TravelSoul!', welcomeSub:'Ας ρυθμίσουμε το προφίλ σου σε 1 λεπτό', whatType:'Τι τύπος ταξιδιώτη είσαι;', destinations:'Ποιους προορισμούς ονειρεύεσαι;', lookingFor:'Τι αναζητάς στο TravelSoul;', next:'Επόμενο →', finish:'Ολοκλήρωση ✓', skip:'Παράλειψη', types:[{icon:'🏔️',key:'explorer',name:'Εξερευνητής',desc:'Αγαπώ άγνωστα μέρη'},{icon:'🎭',key:'adventurer',name:'Περιπετειώδης',desc:'Ψάχνω αδρεναλίνη'},{icon:'🎨',key:'culturist',name:'Πολιτισμός',desc:'Τέχνη και ιστορία'},{icon:'🌴',key:'nomad',name:'Νομάς',desc:'Ζω χωρίς σύνορα'}], goals:[{icon:'✈️',key:'companion',name:'Συνταξιδιώτης'},{icon:'💕',key:'romance',name:'Ρομαντική σχέση'},{icon:'👥',key:'friends',name:'Νέοι φίλοι'}] },
    landing: { trusted:'🌟 Εμπιστοσύνη από 12.847 ταξιδιώτες', watchDemo:'Δες το demo ▶', stats:[{n:'12.847+',l:'Ενεργοί ταξιδιώτες'},{n:'142',l:'Χώρες'},{n:'4.832',l:'Ταιριάσματα'},{n:'98%',l:'Ικανοποίηση'}], howTitle:'Πώς λειτουργεί', how:[{icon:'🧬',title:'Δημιούργησε το Travel DNA σου',desc:'Κάνε το τεστ προσωπικότητας'},{icon:'💫',title:'Βρες το Soul Match σου',desc:'Συνδέσου με συμβατούς ταξιδιώτες'},{icon:'✈️',title:'Ταξιδέψτε μαζί',desc:'Μοιράσου την περιπέτεια'}], faqTitle:'Συχνές ερωτήσεις', privacyTitle:'Πολιτική απορρήτου', termsTitle:'Όροι χρήσης', about:'Σχετικά', contact:'Επικοινωνία', allRights:'© 2026 IVIPRO. Με επιφύλαξη παντός δικαιώματος.', heroBadge:'✦ Ταξίδια με ψυχή', demoAlert:'Το demo έρχεται σύντομα! 🎬', travelerStories:'❤️ Ιστορίες ταξιδιωτών', appStoreBanner:'📱 Σύντομα σε App Store & Google Play', joinWaitlist:'Μπες στη λίστα', footerTagline:'Συνδέσεις ταξιδιωτών με ψυχή' },
    extras: { addDest:'+ Προσθήκη', selectUpTo5:'Επίλεξε έως 5', back:'← Πίσω', photoSaved:'✅ Η φωτογραφία αποθηκεύτηκε!', photoTooBig:'❌ Η φωτογραφία είναι πολύ μεγάλη', addStory:'Προσθήκη', travelers:'ταξιδιώτες', upgradeBtn:'💎 Αναβάθμιση' },
    badges: { worldExplorer:{name:'Εξερευνητής κόσμου',desc:'Συμμετοχή σε 3+ ταξίδια'}, socialButterfly:{name:'Κοινωνικός ταξιδιώτης',desc:'Έστειλε 50+ μηνύματα'}, superLiker:{name:'Super Liker',desc:'Χρήση 10 Super Likes'}, onFire:{name:'Σε φόρμα',desc:'Σύνδεση 7 μέρες στη σειρά'}, firstFlight:{name:'Πρώτη πτήση',desc:'Πρώτο ταξίδι'}, mountainClimber:{name:'Ορειβάτης',desc:'Μπες σε πεζοπορικό ταξίδι'}, beachLover:{name:'Λάτρης παραλίας',desc:'Μπες σε 3 παραλιακά ταξίδια'}, eliteTraveler:{name:'Elite ταξιδιώτης',desc:'Αναβάθμιση σε Elite'}, nightOwl:{name:'Νυχτερινός τύπος',desc:'Χρήση Night Mode 10 φορές'}, cartographer:{name:'Χαρτογράφος',desc:'Επισκέψου 10 χώρες'} },
    videoIntro: { recordIntro:'Εγγραφή intro βίντεο', uploadVideo:'Μεταφόρτωση βίντεο', videoSuccess:'Το βίντεο ανέβηκε! ✅', playIntro:'▶ Intro', verifiedByVideo:'Το προφίλ επαληθεύτηκε με βίντεο ✅', startCamera:'Άνοιγμα κάμερας', previewReady:'Έτοιμο για εγγραφή', startRecording:'Έναρξη εγγραφής', stopRecording:'Διακοπή εγγραφής', recording:'Γίνεται εγγραφή', uploading:'Μεταφόρτωση...', previewVideo:'Προεπισκόπηση βίντεο', retake:'Ξανά', close:'Κλείσιμο', cameraError:'Η κάμερα δεν είναι διαθέσιμη.', videoTooShort:'Το βίντεο πρέπει να είναι τουλάχιστον 15 δευτ.', videoTooLong:'Πολύ μεγάλο βίντεο (μέγ. 30 δευτ.)', videoTooBig:'Πολύ μεγάλο αρχείο (μέγ. 50 MB)', duration15to30:'15–30 δευτερόλεπτα', talkAbout:'Μίλησε για το αγαπημένο σου ταξίδι', getVerified:'Πάρε το ✅ badge', minMaxDuration:'Ελάχ. 15 δευτ · Μέγ. 30 δευτ', introInstructions:'Γράψε ένα σύντομο intro βίντεο για επαλήθευση' },
    pact:{ pactBtn:'🤝 Δημιουργία pact', pactTitle:'Travel Pact', pactSub:'Δεσμευτείτε να ταξιδέψετε μαζί', pactDestLabel:'Προορισμός', pactMonthLabel:'Πότε (μήνας/εποχή)', pactSend:'Αποστολή pact', pactDecline:'Απόρριψη', pactAccept:'Αποδοχή pact', pactPending:'⏳ Pact σε αναμονή', pactActive:'✈️ Pact ενεργό', pactFulfilled:'✅ Pact ολοκληρώθηκε', pactCountdown:'μέρες απομένουν', pactAiSuggest:'✨ AI πρόταση προορισμού', pactTrophy:'🏆 Ταξιδέψατε μαζί', pactLimitFree:'Αναβάθμισε για Travel Pacts', pactLimit1:'Elite για απεριόριστα Pacts', pactSuccess:'Το pact στάλθηκε!', pactDeclined:'Το pact απορρίφθηκε' },
    info:{ height:'Ύψος', bodyType:'Σωματότυπος', smoking:'Κάπνισμα', alcohol:'Αλκοόλ', children:'Παιδιά', partnerPrefs:'Προτιμήσεις συντρόφου', goalLabel:'Αναζητώ', smoker:'🚬 Καπνιστής', nonSmoker:'🚭 Μη καπνιστής', occasionally:'🌿 Περιστασιακά', drinker:'🍷 Πίνει', nonDrinker:'🚱 Δεν πίνει', socially:'🥂 Κοινωνικά' },
  },
  it: {
    brand: 'TravelSoul',
    tagline: ['Trova la tua anima', 'attraverso il viaggio', 'Scoprite il mondo insieme'],
    cta: 'Inizia il viaggio', login: 'Accedi',
    challenges: [{title:'🌏 Connettiti con l\'Asia',desc:'Scrivi a qualcuno di un paese asiatico questa settimana',goal:1,reward:'🔥 1 Boost gratuito'},{title:'⭐ Settimana Super Like',desc:'Invia 3 Super Like questa settimana',goal:3,reward:'↩️ Rewind x3'},{title:'🌍 Giramondo',desc:'Visualizza 10 profili da paesi diversi',goal:10,reward:'🌍 Pass Passaporto giornaliero'},{title:'🌙 Esploratore notturno',desc:'Usa la modalità notte stasera',goal:1,reward:'⭐ Super Like x5'}],
    premiumBullets:{ boost:['Profilo mostrato per primo per 30 minuti','3× più visualizzazioni del profilo','Indicatore dorato esclusivo'], rewind:['Annulla il tuo ultimo scarto','Seconde possibilità con buoni match','Disponibile una volta al giorno'], passport:['Sfoglia profili in qualsiasi città','Incontra viaggiatori in tutto il mondo','Cambia posizione in qualsiasi momento'], incognito:['Naviga in modo invisibile','Le tue visualizzazioni non vengono registrate','Attiva/disattiva quando vuoi'], filters:['Filtra per età, DNA, compatibilità','Filtro solo online e verificati','Controllo della distanza'], stats:['Pannello completo di statistiche di viaggio','Grafico mensile dei viaggi','Mappa dell\'impronta mondiale'], likes:['Like illimitati ogni giorno','Senza limiti giornalieri','Metti like a chiunque tu voglia'], whoViewed:['Vedi chiunque abbia visto il tuo profilo','Scrivi loro per primo','Notifiche in tempo reale'], videoChat:['Videochiamate HD con i match','Disponibile su tutti i dispositivi','Funzione solo per Elite'], aiPlanner:['Itinerario IA giorno per giorno','Pianificazione del budget personalizzata','Condividi con il tuo gruppo di viaggio'], nightMode:['Vedi chi è attivo stasera','Profili speciali notturni','Filtro incontri esclusivo'], compatReport:['Analisi completa della compatibilità','5 dimensioni valutate','Report scaricabile'], voiceMsg:['Invia messaggi vocali','Ascolta per primo il tuo match','Audio riproducibile in chat'], gifts:['Invia regali virtuali','Consegna animata del regalo','Dimostra che ci tieni'], icebreaker:['Aperture personalizzate dall\'IA','Basate sul DNA di viaggio','3 suggerimenti + aggiorna'], dreamMatch:['Match basato sulle destinazioni dei sogni','Trova il tuo compagno di viaggio','Basato sulla tua lista dei desideri'], liveLocation:['Condividi la posizione in tempo reale','Coordinamento di gruppo','Scade dopo 1 ora'], splitCosts:['Calcolo automatico a persona','Traccia tutte le spese di viaggio','Invia alla chat di gruppo'], hotelMatch:['Vedi chi ha prenotato nelle vicinanze','Coordina gli incontri','Conoscetevi prima del viaggio'], aiWriter:['3 bio scritte dall\'IA','Avventurosa/romantica/spiritosa','Distinguiti dalla massa'] },
    features: [
      { icon: '🧬', title: 'DNA di viaggio', desc: 'Un profilo unico della tua personalità da viaggiatore' },
      { icon: '♥', title: 'Compatibilità', desc: 'Incontra viaggiatori con sogni simili ai tuoi' },
      { icon: '✈', title: 'Viaggi di gruppo', desc: 'Unisciti ad avventure curate con persone affini' },
      { icon: '🛡', title: 'Sicurezza', desc: 'La tua privacy è la nostra priorità' },
    ],
    nav: { explore: 'Esplora', connect: 'Connetti', journeys: 'Viaggi', messages: 'Messaggi' },
    register: { title: 'Unisciti a TravelSoul', sub: 'Il tuo viaggio inizia qui', name: 'Nome completo', email: 'Email', password: 'Password', confirmPassword: 'Conferma password', passwordMismatch: 'Le password non coincidono', passwordMatch: 'Le password coincidono ✅', weakPassword: 'Password debole', mediumPassword: 'Password media', strongPassword: 'Password forte', country: 'Seleziona paese', submit: 'Crea profilo', login: 'Hai già un account? Accedi', birthDate:'Data di nascita', ageError:'Devi avere almeno 18 anni', selectBirthDate:'Seleziona data di nascita' },
    ageVerification: { title: "Verifica dell'età", msg: "Dobbiamo confermare che hai 18 anni o più per continuare a usare TravelSoul.", submit: 'Conferma', saving: 'Salvataggio...', underageTitle: 'Siamo spiacenti', underageMsg: "TravelSoul è disponibile solo per utenti di 18 anni o più. Non puoi continuare a usare l'app.", signOut: 'Disconnetti' },
    loginScreen: { title: 'Bentornato', sub: 'Accedi al tuo account', email: 'Email', password: 'Password', submit: 'Accedi', register: 'Non hai un account? Registrati', forgot: 'Password dimenticata?', resetSent: '✅ Email di reset inviata!', error: 'Email o password errati', wrongPassword: 'Email o password errati' },
    explore: { title: 'Scopri viaggi', spots: 'posti disponibili', join: 'Unisciti', hot: 'Popolare', days: 'giorni', aiPicks: 'DESTINAZIONI DI TENDENZA', refresh: 'Scopri di più', host: 'Host', joined: '✓ Iscritto', aiRecommended: '🤖 Consigliato da AI' },
    connect: {
      title: 'Trova la tua anima viaggiatrice', sub: 'viaggiatori online ora',
      online: 'online', offline: 'offline', like: 'Mi piace', liked: 'Piaciuto', pass: 'Salta', match: 'Compatibilità', dnaSection: 'Il tuo DNA di viaggio',
      whoViewed: 'Chi ha visto il tuo profilo', swipeMode: 'Swipe', gridMode: 'Griglia',
      seenAll: 'Li hai visti tutti! 🎉', superLikeSent: '✨ Super Like inviato!', itsAMatch: '💫 È un match!',
      sendMessage: 'Invia messaggio', keepSwiping: 'Continua', keepGoing: 'Continua', sendMsg: 'Invia messaggio', passedToast: 'Saltato ✓',
      boost: 'Boost', boostActive: 'Boost attivo', boostDesc: 'Il tuo profilo è in cima per 30 minuti',
      passport: 'Passaporto', passportDesc: 'Vedi profili in un’altra città', viewingIn: 'Stai vedendo profili a',
      incognito: 'Incognito', incognitoOn: 'Modalità invisibile attiva', incognitoOff: 'Visibile agli altri',
      rewindBtn: '↩️ Indietro', nothingToRewind: 'Niente da recuperare',
      filtersBtn: '🎯 Filtri', premiumFeature: 'Funzione Premium', upgrade: 'Passa a Premium', seePlans: 'Vedi piani',
      filters: { age:'Età:', travelDna:'DNA di viaggio', minCompat:'Compatibilità min.:', onlineOnly:'Solo online', verifiedOnly:'Solo verificati', distance:'Distanza', clear:'Cancella', apply:'Applica', dist50:'50 km', dist500:'500 km', distWorld:'tutto il mondo' },
      swipeLabels: { match:'MATCH!', liked:'PIACIUTO ♥', super:'SUPER ⭐', nope:'NO ✗' },
    },
    journeys: { title: 'I miei viaggi', tabs: ['Prossimi', 'Pianificati', 'Completati', 'Classifica'], empty: 'Non hai ancora viaggi. Esplora e unisciti!', departs: 'Partenza', tripsLabel: 'Viaggi', countriesLabel: 'Paesi', youLabel: 'Tu', statsTab: 'Statistiche', stats: { countries: 'Paesi visitati', trips: 'Viaggi', km: 'Km percorsi', matches: 'Match', superLikes:'Super Like', leaderboard:'Classifica', footprint:'🌍 Impronta di viaggio', tripsPerMonth:'📅 Viaggi al mese' } },
    messages: { title: 'Messaggi', placeholder: 'Scrivi un messaggio...', send: 'Invia', you: 'Tu', translate: 'Traduci', autoTranslate: 'Traduzione automatica', directMessages: '💬 Messaggi diretti', groupChats: '👥 Chat di gruppo', joinedGroupChat: '✅ Sei nella chat di gruppo.', videoCall: 'Videochiamata', calling: 'Chiamata...', connected: 'Connesso', endCall: 'Termina', mute:'Muto', camera:'Fotocamera', chat:'Chat', voiceMsg:'🎤 Messaggio vocale', expiresIn1h:'— scade tra 1h', voice:'Voce', giftSent:'🎁 Ti ha inviato', downloadLoveStory:'Scarica la nostra storia' },
    dna: { title: 'Il tuo DNA di viaggio', sub: 'Scopri la tua personalità da viaggiatore', start: 'Rivela il mio DNA', retake: 'Rifai', result: 'Sei', traits: { explorer: 'Esploratore', adventurer: 'Avventuriero', culturist: 'Culturale', nomad: 'Nomade' } },
    quiz: [
      { q: 'Qual è la tua destinazione ideale?', opts: ['Natura selvaggia e montagne', 'Città antiche e cultura', 'Spiaggia e relax', 'Qualsiasi cosa, purché sia nuova'] },
      { q: 'Come preferisci viaggiare?', opts: ['Totalmente spontaneo', 'Con un piano flessibile', 'Tutto organizzato in anticipo'] },
      { q: 'Cosa cerchi in un compagno di viaggio?', opts: ['Interessi comuni', 'Una prospettiva diversa', 'Preferisco viaggiare da solo/a'] },
      { q: 'Quanto dura il tuo viaggio ideale?', opts: ['Weekend', '1–2 settimane', '1 mese o più'] },
    ],
    profile: { myProfile:'Il mio profilo', editProfile:'Modifica profilo', saveProfile:'Salva profilo', profileSaved:'Profilo salvato ✅', changePhoto:'Cambia foto', bio:'Su di me', age:'Età', dreamDest:'Destinazioni da sogno', travelStyle:'Stile di viaggio', instagram:'Instagram', lookingFor:'Cerco', firstName:'Nome', lastName:'Cognome', city:'Città', travelTags:['Esploratore','Avventuriero','Culturale','Nomade','Foodie','Fotografo'], dreamPlaceholder:'Giappone, Islanda...' },
    notif: { notifications:'Notifiche', allowNotif:'Consenti notifiche', notifDesc:'Ricevi notifiche per nuovi match', newMatch:'Nuovo match!', viewedProfile:'Qualcuno ha visto il tuo profilo', noNotifications:'Nessuna notifica', later:'Più tardi', newSuperLike:'Nuovo Super Like!', fromSofia:'da Sofia', sentYou:'ti ha inviato' },
    onboarding: { welcome:'Benvenuto in TravelSoul!', welcomeSub:'Configuriamo il tuo profilo in 1 minuto', whatType:'Che tipo di viaggiatore sei?', destinations:'Quali destinazioni sogni?', lookingFor:'Cosa cerchi su TravelSoul?', next:'Avanti →', finish:'Completa ✓', skip:'Salta', types:[{icon:'🏔️',key:'explorer',name:'Esploratore',desc:'Amo luoghi sconosciuti'},{icon:'🎭',key:'adventurer',name:'Avventuriero',desc:'Cerco adrenalina'},{icon:'🎨',key:'culturist',name:'Culturale',desc:'Arte e storia'},{icon:'🌴',key:'nomad',name:'Nomade',desc:'Vivo senza confini'}], goals:[{icon:'✈️',key:'companion',name:'Compagno di viaggio'},{icon:'💕',key:'romance',name:'Relazione romantica'},{icon:'👥',key:'friends',name:'Nuovi amici'}] },
    landing: { trusted:'🌟 Scelto da 12.847 viaggiatori', watchDemo:'Guarda il demo ▶', stats:[{n:'12.847+',l:'Viaggiatori attivi'},{n:'142',l:'Paesi'},{n:'4.832',l:'Match'},{n:'98%',l:'Soddisfazione'}], howTitle:'Come funziona', how:[{icon:'🧬',title:'Crea il tuo Travel DNA',desc:'Fai il test di personalità'},{icon:'💫',title:'Trova il tuo Soul Match',desc:'Connettiti con viaggiatori compatibili'},{icon:'✈️',title:'Viaggiate insieme',desc:'Condividi l’avventura'}], faqTitle:'FAQ', privacyTitle:'Informativa privacy', termsTitle:'Termini di servizio', about:'Chi siamo', contact:'Contatti', allRights:'© 2026 IVIPRO. Tutti i diritti riservati.', heroBadge:'✦ Viaggi soul-first', demoAlert:'Demo in arrivo! 🎬', travelerStories:'❤️ Storie di viaggiatori', appStoreBanner:'📱 Presto su App Store e Google Play', joinWaitlist:'Lista d’attesa', footerTagline:'Connessioni di viaggio con anima' },
    extras: { addDest:'+ Aggiungi', selectUpTo5:'Seleziona fino a 5', back:'← Indietro', photoSaved:'✅ Foto salvata!', photoTooBig:'❌ Foto troppo grande', addStory:'Aggiungi', travelers:'viaggiatori', upgradeBtn:'💎 Aggiorna' },
    badges: { worldExplorer:{name:'Esploratore del mondo',desc:'3+ viaggi'}, socialButterfly:{name:'Social butterfly',desc:'50+ messaggi inviati'}, superLiker:{name:'Super Liker',desc:'10 Super Like usati'}, onFire:{name:'In forma',desc:'7 giorni consecutivi'}, firstFlight:{name:'Primo volo',desc:'Primo viaggio'}, mountainClimber:{name:'Scalatore',desc:'Unisciti a un viaggio trekking'}, beachLover:{name:'Amante della spiaggia',desc:'Unisciti a 3 viaggi al mare'}, eliteTraveler:{name:'Viaggiatore Elite',desc:'Passa a Elite'}, nightOwl:{name:'Nottambulo',desc:'Usa Night Mode 10 volte'}, cartographer:{name:'Cartografo',desc:'Visita 10 paesi'} },
    videoIntro: { recordIntro:'Registra video intro', uploadVideo:'Carica video', videoSuccess:'Video caricato! ✅', playIntro:'▶ Intro', verifiedByVideo:'Profilo verificato con video ✅', startCamera:'Avvia fotocamera', previewReady:'Pronto per registrare', startRecording:'Inizia registrazione', stopRecording:'Ferma registrazione', recording:'Registrazione', uploading:'Caricamento...', previewVideo:'Anteprima video', retake:'Rifai', close:'Chiudi', cameraError:'Fotocamera non disponibile.', videoTooShort:'Il video deve durare almeno 15 secondi', videoTooLong:'Video troppo lungo (max 30 sec.)', videoTooBig:'Video troppo grande (max 50 MB)', duration15to30:'15–30 secondi', talkAbout:'Racconta il tuo viaggio preferito', getVerified:'Ottieni il badge ✅', minMaxDuration:'Min. 15 sec · Max. 30 sec', introInstructions:'Registra un breve video intro per verificare il profilo' },
    pact:{ pactBtn:'🤝 Crea un patto', pactTitle:'Patto di viaggio', pactSub:'Impegnatevi a viaggiare insieme', pactDestLabel:'Destinazione', pactMonthLabel:'Quando (mese/stagione)', pactSend:'Invia patto', pactDecline:'Rifiuta', pactAccept:'Accetta patto', pactPending:'⏳ Patto in attesa', pactActive:'✈️ Patto attivo', pactFulfilled:'✅ Patto completato', pactCountdown:'giorni mancanti', pactAiSuggest:'✨ AI suggerisci destinazione', pactTrophy:'🏆 Avete viaggiato insieme', pactLimitFree:'Aggiorna per inviare Patti', pactLimit1:'Elite per Patti illimitati', pactSuccess:'Patto inviato!', pactDeclined:'Patto rifiutato' },
    info:{ height:'Altezza', bodyType:'Fisico', smoking:'Fumo', alcohol:'Alcol', children:'Figli', partnerPrefs:'Preferenze partner', goalLabel:'Cerco', smoker:'🚬 Fumatore', nonSmoker:'🚭 Non fumatore', occasionally:'🌿 Occasionalmente', drinker:'🍷 Beve', nonDrinker:'🚱 Non beve', socially:'🥂 Socialmente' },
  },
};
T.el = deepMerge(T.en, EXTRA_MAIN_T.el);
T.it = deepMerge(T.en, EXTRA_MAIN_T.it);
T.el = deepMerge(T.el, {
  landing: {
    privacyContent: {
      lastUpdated:'Τελευταία ενημέρωση: Ιούνιος 2026',
      collect:'Δεδομένα που συλλέγουμε',
      collectText:'Το TravelSoul συλλέγει: όνομα, email, χώρα, ταξιδιωτικές προτιμήσεις και φωτογραφίες προφίλ. Τα δεδομένα αποθηκεύονται με ασφάλεια μέσω της υποδομής cloud της Supabase. Χρησιμοποιούμε Google OAuth για τη σύνδεση στον λογαριασμό.',
      use:'Πώς χρησιμοποιούμε τα δεδομένα σου',
      useText:'Τα δεδομένα σου χρησιμοποιούνται για να σε ταιριάξουμε με συμβατούς ταξιδιώτες και να βελτιώσουμε την εμπειρία σου.',
      storage:'Αποθήκευση δεδομένων',
      storageText:'Το προφίλ, τα μηνύματα και οι φωτογραφίες σου αποθηκεύονται με ασφάλεια σε cloud διακομιστές της Supabase. Δεν κοινοποιούμε τα προσωπικά σου δεδομένα σε τρίτους.',
      thirdParties:'Τρίτα μέρη',
      thirdPartiesText:'Χρησιμοποιούμε Google OAuth για σύνδεση και Supabase για τη βάση δεδομένων. Αυτές οι υπηρεσίες έχουν τις δικές τους πολιτικές απορρήτου.',
      rights:'Τα δικαιώματά σου (GDPR)',
      rightsText:'Έχεις δικαίωμα πρόσβασης, διόρθωσης ή διαγραφής των δεδομένων σου οποιαδήποτε στιγμή. Μπορείς να ζητήσεις διαγραφή του λογαριασμού σου επικοινωνώντας στο support@travelsoul.love',
      cookies:'Cookies',
      cookiesText:'Το TravelSoul χρησιμοποιεί μόνο λειτουργικά cookies για την αποθήκευση των προτιμήσεών σου.',
      contact:'Επικοινωνία',
      contactText:'Για ερωτήσεις σχετικά με το απόρρητο: support@travelsoul.love',
    },
    termsContent: {
      lastUpdated:'Τελευταία ενημέρωση: Ιούνιος 2026',
      eligibility:'Καταλληλότητα',
      eligibilityText:'Πρέπει να είσαι 18+ ετών για να χρησιμοποιήσεις το TravelSoul.',
      conduct:'Συμπεριφορά χρηστών',
      conductText:'Οι χρήστες πρέπει να συμπεριφέρονται με σεβασμό. Η παρενόχληση και το ακατάλληλο περιεχόμενο απαγορεύονται.',
      safety:'Ασφάλεια ταξιδιού',
      safetyText:'Το TravelSoul δεν ευθύνεται για περιστατικά κατά τη διάρκεια ταξιδιών. Συνιστούμε ταξιδιωτική ασφάλιση.',
      premium:'Premium υπηρεσίες',
      premiumText:'Οι συνδρομές επί πληρωμή διαχειρίζονται μέσω Google Play στην εφαρμογή Android. Μπορείς να διαχειριστείς ή να ακυρώσεις τη συνδρομή σου εκεί πριν από την επόμενη ημερομηνία χρέωσης.',
      contact:'Επικοινωνία',
      contactText:'Ερωτήσεις: support@travelsoul.love',
    },
    refundContent: {
      lastUpdated:'Τελευταία ενημέρωση: Ιούνιος 2026',
      title:'Πολιτική επιστροφών',
      intro:'Θέλουμε να είσαι ικανοποιημένος/η με τις συνδρομές TravelSoul Premium και Elite.',
      eligibility:'Δικαίωμα επιστροφής',
      eligibilityText:'Μπορείς να ζητήσεις πλήρη επιστροφή χρημάτων εντός 14 ημερών από την αρχική αγορά συνδρομής, αν δεν είσαι ικανοποιημένος/η, σύμφωνα με τη νομοθεσία προστασίας καταναλωτών της ΕΕ. Οι επιστροφές ισχύουν μόνο για την πρώτη πληρωμή συνδρομής.',
      process:'Πώς να ζητήσεις επιστροφή',
      processText:'Για να ζητήσεις επιστροφή, στείλε email στο support@travelsoul.love με το email του λογαριασμού σου και την ημερομηνία αγοράς. Οι επιστροφές επεξεργάζονται μέσα σε 5-10 εργάσιμες ημέρες στον αρχικό τρόπο πληρωμής.',
      nonRefundable:'Μη επιστρέψιμα στοιχεία',
      nonRefundableText:'Τα εικονικά νομίσματα και οι εφάπαξ ψηφιακές αγορές δεν επιστρέφονται μετά τη χρήση. Οι ανανεώσεις συνδρομής μετά την αρχική περίοδο 14 ημερών δεν επιστρέφονται, αλλά μπορείς να ακυρώσεις οποιαδήποτε στιγμή για να αποτρέψεις μελλοντικές χρεώσεις.',
      contact:'Επικοινωνία',
      contactText:'Για ερωτήσεις σχετικά με επιστροφές: support@travelsoul.love',
    },
  },
});
T.it = deepMerge(T.it, {
  landing: {
    privacyContent: {
      lastUpdated:'Ultimo aggiornamento: giugno 2026',
      collect:'Dati che raccogliamo',
      collectText:'TravelSoul raccoglie: nome, email, paese, preferenze di viaggio e foto del profilo. I dati sono archiviati in modo sicuro tramite l’infrastruttura cloud di Supabase. Usiamo Google OAuth per l’accesso.',
      use:'Come usiamo i tuoi dati',
      useText:'I tuoi dati vengono usati per abbinarti a viaggiatori compatibili e migliorare la tua esperienza.',
      storage:'Archiviazione dei dati',
      storageText:'Il tuo profilo, i messaggi e le foto sono archiviati in modo sicuro sui server cloud di Supabase. Non condividiamo i tuoi dati personali con terze parti.',
      thirdParties:'Terze parti',
      thirdPartiesText:'Usiamo Google OAuth per l’accesso e Supabase per il database. Questi servizi hanno le proprie informative sulla privacy.',
      rights:'I tuoi diritti (GDPR)',
      rightsText:'Hai il diritto di accedere, correggere o eliminare i tuoi dati in qualsiasi momento. Puoi richiedere l’eliminazione del tuo account contattando support@travelsoul.love',
      cookies:'Cookie',
      cookiesText:'TravelSoul usa solo cookie funzionali per salvare le tue preferenze.',
      contact:'Contatti',
      contactText:'Per domande sulla privacy: support@travelsoul.love',
    },
    termsContent: {
      lastUpdated:'Ultimo aggiornamento: giugno 2026',
      eligibility:'Idoneità',
      eligibilityText:'Devi avere almeno 18 anni per usare TravelSoul.',
      conduct:'Condotta degli utenti',
      conductText:'Gli utenti devono comportarsi con rispetto. Molestie e contenuti inappropriati sono vietati.',
      safety:'Sicurezza in viaggio',
      safetyText:'TravelSoul non è responsabile per incidenti durante i viaggi. Consigliamo un’assicurazione di viaggio.',
      premium:'Servizi Premium',
      premiumText:'Gli abbonamenti a pagamento sono gestiti tramite Google Play nell’app Android. Puoi gestire o annullare l’abbonamento lì prima della prossima data di fatturazione.',
      contact:'Contatti',
      contactText:'Domande: support@travelsoul.love',
    },
    refundContent: {
      lastUpdated:'Ultimo aggiornamento: giugno 2026',
      title:'Politica di rimborso',
      intro:'Vogliamo che tu sia soddisfatto degli abbonamenti TravelSoul Premium ed Elite.',
      eligibility:'Idoneità al rimborso',
      eligibilityText:'Puoi richiedere un rimborso completo entro 14 giorni dall’acquisto iniziale dell’abbonamento se non sei soddisfatto, in linea con la normativa UE sulla protezione dei consumatori. I rimborsi si applicano solo al primo pagamento dell’abbonamento.',
      process:'Come richiederlo',
      processText:'Per richiedere un rimborso, invia un’email a support@travelsoul.love con l’email del tuo account e la data di acquisto. I rimborsi vengono elaborati entro 5-10 giorni lavorativi sul metodo di pagamento originale.',
      nonRefundable:'Elementi non rimborsabili',
      nonRefundableText:'Le monete virtuali e gli acquisti digitali una tantum non sono rimborsabili dopo l’uso. I rinnovi dell’abbonamento dopo il periodo iniziale di 14 giorni non sono rimborsabili, ma puoi annullare in qualsiasi momento per evitare addebiti futuri.',
      contact:'Contatti',
      contactText:'Per domande sui rimborsi: support@travelsoul.love',
    },
  },
});

const LANDING_OFFER_COPY = {
  bg: {
    title: '🎁 Специална оферта!',
    first: 'Първите',
    usersGet: 'потребители получават',
    premiumFree: 'Premium безплатно',
    forDays: 'за',
    days: 'дни',
    spotsLeft: (count) => `Остават само ${count} места!`,
  },
  en: {
    title: '🎁 Special Offer!',
    first: 'First',
    usersGet: 'users get',
    premiumFree: 'Premium free',
    forDays: 'for',
    days: 'days',
    spotsLeft: (count) => `Only ${count} spots left!`,
  },
  es: {
    title: '🎁 ¡Oferta Especial!',
    first: 'Los primeros',
    usersGet: 'usuarios obtienen',
    premiumFree: 'Premium gratis',
    forDays: 'por',
    days: 'días',
    spotsLeft: (count) => `¡Solo quedan ${count} plazas!`,
  },
  fr: {
    title: '🎁 Offre Spéciale !',
    first: 'Les',
    usersGet: 'premiers utilisateurs reçoivent',
    premiumFree: 'Premium gratuit',
    forDays: 'pendant',
    days: 'jours',
    spotsLeft: (count) => `Plus que ${count} places !`,
  },
  de: {
    title: '🎁 Sonderangebot!',
    first: 'Die ersten',
    usersGet: 'Nutzer erhalten',
    premiumFree: 'Premium kostenlos',
    forDays: 'für',
    days: 'Tage',
    spotsLeft: (count) => `Nur noch ${count} Plätze!`,
  },
  el: {
    title: '🎁 Ειδική προσφορά!',
    first: 'Οι πρώτοι',
    usersGet: 'χρήστες παίρνουν',
    premiumFree: 'δωρεάν Premium',
    forDays: 'για',
    days: 'ημέρες',
    spotsLeft: (count) => `Απομένουν μόνο ${count} θέσεις!`,
  },
  it: {
    title: '🎁 Offerta speciale!',
    first: 'I primi',
    usersGet: 'utenti ricevono',
    premiumFree: 'Premium gratis',
    forDays: 'per',
    days: 'giorni',
    spotsLeft: (count) => `Restano solo ${count} posti!`,
  },
};

const LANG_META = {
  bg:{flag:'🇧🇬',label:'BG',name:'Български'},
  en:{flag:'🇬🇧',label:'EN',name:'English'},
  es:{flag:'🇪🇸',label:'ES',name:'Español'},
  fr:{flag:'🇫🇷',label:'FR',name:'Français'},
  de:{flag:'🇩🇪',label:'DE',name:'Deutsch'},
  el:{flag:'🇬🇷',label:'EL',name:'Ελληνικά'},
  it:{flag:'🇮🇹',label:'IT',name:'Italiano'},
};

const LEGAL_UI_T = {
  bg: {
    serviceDescription:'TravelSoul е платформа за запознанства и пътувания без съдържание за възрастни, предназначена за потребители на 18+ години, която помага на хора с интерес към пътуванията да се срещат, общуват и планират пътувания.',
    footerLinks:{ terms:'Условия', privacy:'Поверителност', refund:'Политика за възстановяване', pricing:'Цени', contact:'Контакт' },
    company:'ИВИ ПРО ДЕВЕЛОПМЕНТ ЕООД',
    legalForm:'ЕООД',
    address:'ул. Екзарх Йосиф 29, ет. 1, ап. 1, 1000 София, България',
    notVatRegistered:'Не е регистрирано по ДДС',
    labels:{ brand:'Бранд', company:'Компания', legalForm:'Правна форма', supportEmail:'Имейл за поддръжка', website:'Уебсайт', registration:'ЕИК / регистрационен номер', vat:'ДДС', address:'Регистриран адрес', phone:'Телефон или контакт на живо' },
    complianceTitle:'Безопасност и правила на платформата',
    complianceText:'TravelSoul е платформа за запознанства и пътувания без съдържание за възрастни. Не допускаме съдържание за възрастни, порнография, еротично съдържание, голота или сексуални предложения, ескорт услуги, проституция, платени срещи, sugar dating, услуги тип mail-order bride, хазарт, финансови услуги, незаконни продукти или незаконни услуги.',
    prohibitedTitle:'Забранено съдържание и услуги',
    prohibitedItems:['съдържание за възрастни','порнография','еротично съдържание','голота или сексуални предложения','ескорт услуги','проституция','платени срещи','sugar dating','услуги тип mail-order bride','хазарт','финансови услуги','незаконни продукти или услуги','тормоз','обиди','измами','фалшиви профили','експлоатация'],
    refundExtra:{ cancellation:'Анулиране', cancellationText:'Абонаментите, закупени в Android приложението, се управляват и анулират чрез Google Play.', renewals:'Подновяване на абонаменти', renewalsText:'Подновяванията след първоначалния 14-дневен период не подлежат на възстановяване, но можете да анулирате преди следващата дата на таксуване, за да предотвратите бъдещи плащания.', termsPrivacy:'Условия и поверителност', termsPrivacyText:'Моля, прегледайте {terms} и {privacy}, преди да закупите платена услуга.' },
    pricingSummary:{ plan:'План', price:'Цена', billingPeriod:'Период на таксуване', annual:'годишен', monthly:'месечен', autoRenewal:'Автоматично подновяване', paidLaunch:'Управлява се от Google Play в Android приложението.', cancellation:'Анулиране', cancellationText:'Управлявай или анулирай абонамента си чрез Google Play в Android приложението.', refund:'Възстановяване', refundText:'Първоначалните плащания за абонамент може да подлежат на възстановяване в рамките на 14 дни според политиката за възстановяване.', paymentsSoon:'Абонирай се в Android приложението чрез Google Play', webToAndroidHint:'Нямаш приложението? Инсталирай го от Google Play, абонирай се там и после влез тук със същия акаунт през бутона „Вход с QR код от телефона“ на екрана за вход.' },
    contactTitle:'Контакт с TravelSoul',
  },
  en: {
    serviceDescription:'TravelSoul is a non-adult dating and travel social platform for adults aged 18+ that helps travel-minded people meet, chat, and plan trips.',
    footerLinks:{ terms:'Terms', privacy:'Privacy', refund:'Refund Policy', pricing:'Pricing', contact:'Contact' },
    company:'IVI PRO DEVELOPMENT LTD',
    legalForm:'LTD',
    address:'29 Ekzarh Yosif Street, Floor 1, Apt. 1, 1000 Sofia, Bulgaria',
    notVatRegistered:'Not VAT registered',
    labels:{ brand:'Brand', company:'Company', legalForm:'Legal form', supportEmail:'Support email', website:'Website', registration:'EIK / business registration number', vat:'VAT', address:'Registered business address', phone:'Phone or live contact' },
    complianceTitle:'Safety and platform rules',
    complianceText:'TravelSoul is a non-adult dating and travel social platform. We do not allow adult content, pornography, erotic content, nudity or sexual solicitation, escort services, prostitution, paid meetings, sugar dating, mail-order bride services, gambling, financial services, illegal products or illegal services.',
    prohibitedTitle:'Prohibited Content and Services',
    prohibitedItems:['adult content','pornography','erotic content','nudity or sexual solicitation','escort services','prostitution','paid meetings','sugar dating','mail-order bride services','gambling','financial services','illegal products or services','harassment','abuse','scams','fake profiles','exploitation'],
    refundExtra:{ cancellation:'Cancellation', cancellationText:'Subscriptions purchased in the Android app are managed and canceled through Google Play.', renewals:'Subscription renewals', renewalsText:'Subscription renewals after the initial 14-day period are non-refundable, but you may cancel anytime before the next billing date to prevent future charges.', termsPrivacy:'Terms and privacy', termsPrivacyText:'Please review the {terms} and {privacy} before purchasing any paid service.' },
    pricingSummary:{ plan:'Plan', price:'Price', billingPeriod:'Billing period', annual:'annual', monthly:'monthly', autoRenewal:'Auto-renewal', paidLaunch:'Managed by Google Play in the Android app.', cancellation:'Cancellation', cancellationText:'Manage or cancel your subscription through Google Play in the Android app.', refund:'Refund', refundText:'Initial subscription payments may be refundable within 14 days under the refund policy.', paymentsSoon:'Subscribe in the Android app through Google Play', webToAndroidHint:'No app yet? Install it from Google Play, subscribe there, then come back and log in here with the same account using the "Log in with QR code from phone" button on the login screen.' },
    contactTitle:'Contact TravelSoul',
  },
  es: {
    serviceDescription:'TravelSoul es una plataforma de citas y viajes sin contenido para adultos, dirigida a personas mayores de 18 años, que ayuda a gente viajera a conocerse, chatear y planificar viajes.',
    footerLinks:{ terms:'Términos', privacy:'Privacidad', refund:'Política de reembolso', pricing:'Precios', contact:'Contacto' },
    company:'IVI PRO DEVELOPMENT LTD',
    legalForm:'LTD',
    address:'29 Ekzarh Yosif Street, Floor 1, Apt. 1, 1000 Sofia, Bulgaria',
    notVatRegistered:'No registrado a efectos de IVA',
    labels:{ brand:'Marca', company:'Empresa', legalForm:'Forma jurídica', supportEmail:'Correo de soporte', website:'Sitio web', registration:'EIK / número de registro mercantil', vat:'IVA', address:'Dirección registrada', phone:'Teléfono o contacto en vivo' },
    complianceTitle:'Seguridad y normas de la plataforma',
    complianceText:'TravelSoul es una plataforma de citas y viajes sin contenido para adultos. No permitimos contenido para adultos, pornografía, contenido erótico, desnudez o solicitud sexual, servicios de escort, prostitución, encuentros pagados, sugar dating, servicios de mail-order bride, juegos de azar, servicios financieros, productos ilegales o servicios ilegales.',
    prohibitedTitle:'Contenido y servicios prohibidos',
    prohibitedItems:['contenido para adultos','pornografía','contenido erótico','desnudez o solicitud sexual','servicios de escort','prostitución','encuentros pagados','sugar dating','servicios de mail-order bride','juegos de azar','servicios financieros','productos o servicios ilegales','acoso','abuso','estafas','perfiles falsos','explotación'],
    refundExtra:{ cancellation:'Cancelación', cancellationText:'Las suscripciones compradas en la app de Android se gestionan y cancelan mediante Google Play.', renewals:'Renovaciones de suscripción', renewalsText:'Las renovaciones después del período inicial de 14 días no son reembolsables, pero puedes cancelar antes de la próxima fecha de facturación para evitar futuros cargos.', termsPrivacy:'Términos y privacidad', termsPrivacyText:'Revisa los {terms} y la {privacy} antes de comprar cualquier servicio de pago.' },
    pricingSummary:{ plan:'Plan', price:'Precio', billingPeriod:'Período de facturación', annual:'anual', monthly:'mensual', autoRenewal:'Renovación automática', paidLaunch:'Gestionada por Google Play en la app de Android.', cancellation:'Cancelación', cancellationText:'Gestiona o cancela tu suscripción mediante Google Play en la app de Android.', refund:'Reembolso', refundText:'Los pagos iniciales de suscripción pueden ser reembolsables dentro de 14 días según la política de reembolso.', paymentsSoon:'Suscríbete en la app de Android mediante Google Play', webToAndroidHint:'¿Aún no tienes la app? Instálala desde Google Play, suscríbete allí y luego vuelve aquí e inicia sesión con la misma cuenta usando el botón "Iniciar sesión con código QR desde el teléfono" en la pantalla de inicio de sesión.' },
    contactTitle:'Contacto de TravelSoul',
  },
  fr: {
    serviceDescription:'TravelSoul est une plateforme de rencontres et de voyage sans contenu adulte, destinée aux personnes de 18 ans et plus, qui aide les passionnés de voyage à se rencontrer, discuter et organiser des voyages.',
    footerLinks:{ terms:'Conditions', privacy:'Confidentialité', refund:'Politique de remboursement', pricing:'Tarifs', contact:'Contact' },
    company:'IVI PRO DEVELOPMENT LTD',
    legalForm:'LTD',
    address:'29 Ekzarh Yosif Street, Floor 1, Apt. 1, 1000 Sofia, Bulgaria',
    notVatRegistered:'Non assujetti à la TVA',
    labels:{ brand:'Marque', company:'Société', legalForm:'Forme juridique', supportEmail:'E-mail de support', website:'Site web', registration:'EIK / numéro d’immatriculation', vat:'TVA', address:'Adresse enregistrée', phone:'Téléphone ou contact direct' },
    complianceTitle:'Sécurité et règles de la plateforme',
    complianceText:'TravelSoul est une plateforme de rencontres et de voyage sans contenu adulte. Nous n’autorisons pas le contenu adulte, la pornographie, le contenu érotique, la nudité ou la sollicitation sexuelle, les services d’escorte, la prostitution, les rencontres rémunérées, le sugar dating, les services de type mail-order bride, les jeux d’argent, les services financiers, les produits illégaux ou les services illégaux.',
    prohibitedTitle:'Contenus et services interdits',
    prohibitedItems:['contenu adulte','pornographie','contenu érotique','nudité ou sollicitation sexuelle','services d’escorte','prostitution','rencontres rémunérées','sugar dating','services de type mail-order bride','jeux d’argent','services financiers','produits ou services illégaux','harcèlement','abus','arnaques','faux profils','exploitation'],
    refundExtra:{ cancellation:'Annulation', cancellationText:'Les abonnements achetés dans l’application Android sont gérés et annulés via Google Play.', renewals:'Renouvellements d’abonnement', renewalsText:'Les renouvellements après la période initiale de 14 jours ne sont pas remboursables, mais vous pouvez annuler avant la prochaine date de facturation afin d’éviter de futurs frais.', termsPrivacy:'Conditions et confidentialité', termsPrivacyText:'Veuillez consulter les {terms} et la {privacy} avant d’acheter un service payant.' },
    pricingSummary:{ plan:'Formule', price:'Prix', billingPeriod:'Période de facturation', annual:'annuelle', monthly:'mensuelle', autoRenewal:'Renouvellement automatique', paidLaunch:'Géré par Google Play dans l’application Android.', cancellation:'Annulation', cancellationText:'Gérez ou annulez votre abonnement via Google Play dans l’application Android.', refund:'Remboursement', refundText:'Les premiers paiements d’abonnement peuvent être remboursables dans un délai de 14 jours selon la politique de remboursement.', paymentsSoon:'Abonnez-vous dans l’application Android via Google Play', webToAndroidHint:'Vous n\'avez pas encore l\'application ? Installez-la depuis Google Play, abonnez-vous, puis revenez ici et connectez-vous avec le même compte via le bouton « Connexion par code QR depuis le téléphone » sur l\'écran de connexion.' },
    contactTitle:'Contacter TravelSoul',
  },
  de: {
    serviceDescription:'TravelSoul ist eine nicht-erwachsene Dating- und Reiseplattform für Erwachsene ab 18 Jahren, die reisebegeisterten Menschen hilft, sich kennenzulernen, zu chatten und Reisen zu planen.',
    footerLinks:{ terms:'Bedingungen', privacy:'Datenschutz', refund:'Rückerstattung', pricing:'Preise', contact:'Kontakt' },
    company:'IVI PRO DEVELOPMENT LTD',
    legalForm:'LTD',
    address:'29 Ekzarh Yosif Street, Floor 1, Apt. 1, 1000 Sofia, Bulgaria',
    notVatRegistered:'Nicht umsatzsteuerregistriert',
    labels:{ brand:'Marke', company:'Unternehmen', legalForm:'Rechtsform', supportEmail:'Support-E-Mail', website:'Website', registration:'EIK / Handelsregisternummer', vat:'USt.', address:'Registrierte Geschäftsadresse', phone:'Telefon oder Live-Kontakt' },
    complianceTitle:'Sicherheit und Plattformregeln',
    complianceText:'TravelSoul ist eine nicht-erwachsene Dating- und Reiseplattform. Wir erlauben keine Erwachseneninhalte, Pornografie, erotischen Inhalte, Nacktheit oder sexuelle Anbahnung, Escort-Dienste, Prostitution, bezahlte Treffen, Sugar Dating, Mail-Order-Bride-Dienste, Glücksspiel, Finanzdienstleistungen, illegalen Produkte oder illegalen Dienstleistungen.',
    prohibitedTitle:'Verbotene Inhalte und Dienste',
    prohibitedItems:['Erwachseneninhalte','Pornografie','erotische Inhalte','Nacktheit oder sexuelle Anbahnung','Escort-Dienste','Prostitution','bezahlte Treffen','Sugar Dating','Mail-Order-Bride-Dienste','Glücksspiel','Finanzdienstleistungen','illegale Produkte oder Dienstleistungen','Belästigung','Missbrauch','Betrug','gefälschte Profile','Ausbeutung'],
    refundExtra:{ cancellation:'Kündigung', cancellationText:'In der Android-App gekaufte Abonnements werden über Google Play verwaltet und gekündigt.', renewals:'Abo-Verlängerungen', renewalsText:'Abo-Verlängerungen nach dem ersten 14-Tage-Zeitraum sind nicht erstattungsfähig. Du kannst jedoch vor dem nächsten Abrechnungsdatum kündigen, um künftige Zahlungen zu vermeiden.', termsPrivacy:'Bedingungen und Datenschutz', termsPrivacyText:'Bitte lies die {terms} und den {privacy}, bevor du einen kostenpflichtigen Dienst kaufst.' },
    pricingSummary:{ plan:'Plan', price:'Preis', billingPeriod:'Abrechnungszeitraum', annual:'jährlich', monthly:'monatlich', autoRenewal:'Automatische Verlängerung', paidLaunch:'Wird von Google Play in der Android-App verwaltet.', cancellation:'Kündigung', cancellationText:'Verwalte oder kündige dein Abonnement über Google Play in der Android-App.', refund:'Rückerstattung', refundText:'Erste Abo-Zahlungen können gemäß Rückerstattungsrichtlinie innerhalb von 14 Tagen erstattungsfähig sein.', paymentsSoon:'In der Android-App über Google Play abonnieren', webToAndroidHint:'Noch keine App? Installiere sie über Google Play, abonniere dort und melde dich dann hier mit demselben Konto über die Schaltfläche „Mit QR-Code vom Smartphone anmelden“ auf dem Anmeldebildschirm an.' },
    contactTitle:'TravelSoul kontaktieren',
  },
};

LEGAL_UI_T.el = deepMerge(LEGAL_UI_T.en, {
  serviceDescription:'Το TravelSoul είναι μια μη ενήλικη κοινωνική πλατφόρμα γνωριμιών και ταξιδιών για άτομα 18+ που βοηθά ανθρώπους με αγάπη για τα ταξίδια να γνωρίζονται, να συνομιλούν και να σχεδιάζουν ταξίδια.',
  footerLinks:{ terms:'Όροι', privacy:'Απόρρητο', refund:'Πολιτική επιστροφών', pricing:'Τιμές', contact:'Επικοινωνία' },
  company:'IVI PRO DEVELOPMENT LTD',
  legalForm:'LTD',
  notVatRegistered:'Δεν είναι εγγεγραμμένη στο ΦΠΑ',
  labels:{ brand:'Επωνυμία', company:'Εταιρεία', legalForm:'Νομική μορφή', supportEmail:'Email υποστήριξης', website:'Ιστότοπος', registration:'EIK / αριθμός μητρώου', vat:'ΦΠΑ', address:'Εγγεγραμμένη διεύθυνση', phone:'Τηλέφωνο ή ζωντανή επαφή' },
  complianceTitle:'Ασφάλεια και κανόνες πλατφόρμας',
  complianceText:'Το TravelSoul είναι πλατφόρμα γνωριμιών και ταξιδιών χωρίς περιεχόμενο ενηλίκων. Δεν επιτρέπουμε πορνογραφία, ερωτικό περιεχόμενο, γυμνό ή σεξουαλική προσέγγιση, escort υπηρεσίες, πορνεία, πληρωμένες συναντήσεις, sugar dating, τζόγο, χρηματοοικονομικές υπηρεσίες ή παράνομα προϊόντα/υπηρεσίες.',
  prohibitedTitle:'Απαγορευμένο περιεχόμενο και υπηρεσίες',
  prohibitedItems:['περιεχόμενο ενηλίκων','πορνογραφία','ερωτικό περιεχόμενο','γυμνό ή σεξουαλική προσέγγιση','escort υπηρεσίες','πορνεία','πληρωμένες συναντήσεις','sugar dating','τζόγος','χρηματοοικονομικές υπηρεσίες','παράνομα προϊόντα ή υπηρεσίες','παρενόχληση','κακοποίηση','απάτες','ψεύτικα προφίλ','εκμετάλλευση'],
  refundExtra:{ cancellation:'Ακύρωση', cancellationText:'Οι συνδρομές που αγοράζονται στην εφαρμογή Android διαχειρίζονται και ακυρώνονται μέσω Google Play.', renewals:'Ανανεώσεις συνδρομών', renewalsText:'Οι ανανεώσεις μετά την αρχική περίοδο 14 ημερών δεν επιστρέφονται, αλλά μπορείς να ακυρώσεις πριν από την επόμενη χρέωση.', termsPrivacy:'Όροι και απόρρητο', termsPrivacyText:'Διάβασε τους {terms} και την {privacy} πριν αγοράσεις υπηρεσία επί πληρωμή.' },
  pricingSummary:{ plan:'Πλάνο', price:'Τιμή', billingPeriod:'Περίοδος χρέωσης', annual:'ετήσιο', monthly:'μηνιαίο', autoRenewal:'Αυτόματη ανανέωση', paidLaunch:'Η διαχείριση γίνεται από το Google Play στην εφαρμογή Android.', cancellation:'Ακύρωση', cancellationText:'Διαχειρίσου ή ακύρωσε τη συνδρομή σου μέσω Google Play στην εφαρμογή Android.', refund:'Επιστροφή', refundText:'Οι αρχικές πληρωμές συνδρομής μπορεί να επιστραφούν εντός 14 ημερών σύμφωνα με την πολιτική επιστροφών.', paymentsSoon:'Εγγραφείτε στην εφαρμογή Android μέσω Google Play', webToAndroidHint:'Δεν έχεις ακόμα την εφαρμογή; Εγκατέστησέ την από το Google Play, εγγράψου εκεί και μετά επέστρεψε εδώ για να συνδεθείς με τον ίδιο λογαριασμό μέσω του κουμπιού «Σύνδεση με κωδικό QR από το τηλέφωνο» στην οθόνη σύνδεσης.' },
  contactTitle:'Επικοινωνία με TravelSoul',
});

LEGAL_UI_T.it = deepMerge(LEGAL_UI_T.en, {
  serviceDescription:'TravelSoul è una piattaforma social di incontri e viaggi senza contenuti per adulti, per persone dai 18 anni in su, che aiuta chi ama viaggiare a conoscersi, chattare e pianificare viaggi.',
  footerLinks:{ terms:'Termini', privacy:'Privacy', refund:'Rimborsi', pricing:'Prezzi', contact:'Contatti' },
  company:'IVI PRO DEVELOPMENT LTD',
  legalForm:'LTD',
  notVatRegistered:'Non registrata IVA',
  labels:{ brand:'Marchio', company:'Società', legalForm:'Forma legale', supportEmail:'Email supporto', website:'Sito web', registration:'EIK / numero registrazione', vat:'IVA', address:'Indirizzo registrato', phone:'Telefono o contatto live' },
  complianceTitle:'Sicurezza e regole della piattaforma',
  complianceText:'TravelSoul è una piattaforma di incontri e viaggi senza contenuti per adulti. Non consentiamo contenuti per adulti, pornografia, contenuti erotici, nudità o sollecitazioni sessuali, escort, prostituzione, incontri a pagamento, sugar dating, gioco d’azzardo, servizi finanziari o prodotti/servizi illegali.',
  prohibitedTitle:'Contenuti e servizi vietati',
  prohibitedItems:['contenuti per adulti','pornografia','contenuti erotici','nudità o sollecitazione sessuale','servizi escort','prostituzione','incontri a pagamento','sugar dating','gioco d’azzardo','servizi finanziari','prodotti o servizi illegali','molestie','abusi','truffe','profili falsi','sfruttamento'],
  refundExtra:{ cancellation:'Cancellazione', cancellationText:'Gli abbonamenti acquistati nell’app Android vengono gestiti e annullati tramite Google Play.', renewals:'Rinnovi abbonamento', renewalsText:'I rinnovi dopo il periodo iniziale di 14 giorni non sono rimborsabili, ma puoi annullare prima della prossima data di fatturazione.', termsPrivacy:'Termini e privacy', termsPrivacyText:'Consulta i {terms} e la {privacy} prima di acquistare un servizio a pagamento.' },
  pricingSummary:{ plan:'Piano', price:'Prezzo', billingPeriod:'Periodo di fatturazione', annual:'annuale', monthly:'mensile', autoRenewal:'Rinnovo automatico', paidLaunch:'Gestito da Google Play nell’app Android.', cancellation:'Cancellazione', cancellationText:'Gestisci o annulla l’abbonamento tramite Google Play nell’app Android.', refund:'Rimborso', refundText:'I pagamenti iniziali dell’abbonamento possono essere rimborsabili entro 14 giorni secondo la politica di rimborso.', paymentsSoon:'Abbonati nell’app Android tramite Google Play', webToAndroidHint:'Non hai ancora l\'app? Installala da Google Play, abbonati lì e poi torna qui per accedere con lo stesso account tramite il pulsante "Accedi con codice QR dal telefono" nella schermata di accesso.' },
  contactTitle:'Contatta TravelSoul',
});

function legalUi(lang) {
  return LEGAL_UI_T[lang] || LEGAL_UI_T.en;
}

function legalCompanyName(lang) {
  return legalUi(lang).company;
}

function legalDisplay(value) {
  return isLegalPlaceholder(value) ? null : value;
}

function PublicFooter({ lang = 'en', onNavigate }) {
  const legal = legalUi(lang);
  const go = (screen, path) => (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate(screen);
    else window.location.href = path;
  };
  return (
    <footer className="ts-footer">
      <div className="ts-footer-brand">
        <span className="ts-brand-icon">✈</span>
        <span className="ts-brand-name ts-serif">{LEGAL_INFO.brandName}</span>
        <p className="ts-footer-tagline">{legal.serviceDescription}</p>
        <p style={{color:'#8b949e',fontSize:'0.84rem',marginTop:8}}>
          <a href={`mailto:${LEGAL_INFO.supportEmail}`} style={{color:'#c9a84c',textDecoration:'none'}}>{LEGAL_INFO.supportEmail}</a>
        </p>
      </div>
      <div className="ts-footer-links">
        <a href="/terms" className="ts-footer-link" onClick={go('terms','/terms')}>{legal.footerLinks.terms}</a>
        <a href="/privacy" className="ts-footer-link" onClick={go('privacy','/privacy')}>{legal.footerLinks.privacy}</a>
        <a href="/account-deletion" className="ts-footer-link" onClick={go('accountDeletion','/account-deletion')}>{lang==='bg'?'Изтриване на акаунт':lang==='es'?'Eliminación de cuenta':lang==='fr'?'Suppression de compte':lang==='de'?'Kontolöschung':lang==='el'?'Διαγραφή λογαριασμού':lang==='it'?'Eliminazione account':'Account deletion'}</a>
        <a href="/child-safety" className="ts-footer-link" onClick={go('childSafety','/child-safety')}>{lang==='bg'?'Безопасност на деца':lang==='es'?'Seguridad infantil':lang==='fr'?'Sécurité des enfants':lang==='de'?'Kindersicherheit':lang==='el'?'Ασφάλεια παιδιών':lang==='it'?'Sicurezza dei minori':'Child safety'}</a>
        <a href="/refund" className="ts-footer-link" onClick={go('refund','/refund')}>{legal.footerLinks.refund}</a>
        <a href="/pricing" className="ts-footer-link" onClick={go('pricing','/pricing')}>{legal.footerLinks.pricing}</a>
        <a href="/contact" className="ts-footer-link" onClick={go('contact','/contact')}>{legal.footerLinks.contact}</a>
      </div>
      <p className="ts-footer-copy">© 2026 TravelSoul — {legalCompanyName(lang)}</p>
    </footer>
  );
}

function ComplianceNotice({ lang = 'en' }) {
  const legal = legalUi(lang);
  return (
    <section style={{border:'1px solid rgba(201,168,76,0.28)',background:'rgba(201,168,76,0.08)',borderRadius:12,padding:'16px 18px',margin:'24px auto',maxWidth:900}}>
      <h2 className="ts-serif" style={{fontSize:'1.15rem',color:'#c9a84c',margin:'0 0 8px'}}>{legal.complianceTitle}</h2>
      <p style={{color:'#f0f6fc',fontSize:'0.92rem',lineHeight:1.65,margin:0}}>{legal.complianceText}</p>
    </section>
  );
}

function LegalDetailsBlock({ lang = 'en' }) {
  const legal = legalUi(lang);
  const vatDisplay = isLegalPlaceholder(LEGAL_INFO.vatNumber) ? null : (
    LEGAL_INFO.vatNumber === 'Not VAT registered' ? legal.notVatRegistered : LEGAL_INFO.vatNumber
  );
  const rows = [
    [legal.labels.brand, LEGAL_INFO.brandName],
    [legal.labels.company, legalCompanyName(lang)],
    [legal.labels.legalForm, legal.legalForm],
    [legal.labels.supportEmail, LEGAL_INFO.supportEmail],
    [legal.labels.website, LEGAL_INFO.website],
    [legal.labels.registration, legalDisplay(LEGAL_INFO.registrationNumber)],
    [legal.labels.vat, vatDisplay],
    [legal.labels.address, legalDisplay(lang === 'bg' ? legal.address : LEGAL_INFO.registeredAddress)],
    [legal.labels.phone, legalDisplay(LEGAL_INFO.phoneOrLiveContact)],
  ].filter(([, value]) => value);
  return (
    <div style={{display:'grid',gap:10,margin:'18px 0'}}>
      {rows.map(([label, value]) => (
        <div key={label} style={{display:'grid',gridTemplateColumns:'minmax(150px, 220px) 1fr',gap:12,borderBottom:'1px solid rgba(255,255,255,0.08)',paddingBottom:8}}>
          <strong style={{color:'#c9a84c'}}>{label}</strong>
          <span style={{color:'#f0f6fc',wordBreak:'break-word'}}>{value}</span>
        </div>
      ))}
    </div>
  );
}

const COUNTRIES = [
  { code:'BG', lang:'bg', flag:'🇧🇬', name:{bg:'България',en:'Bulgaria',es:'Bulgaria',fr:'Bulgarie',de:'Bulgarien'} },
  { code:'GB', lang:'en', flag:'🇬🇧', name:{bg:'Великобритания',en:'United Kingdom',es:'Reino Unido',fr:'Royaume-Uni',de:'Vereinigtes Königreich'} },
  { code:'US', lang:'en', flag:'🇺🇸', name:{bg:'САЩ',en:'United States',es:'Estados Unidos',fr:'États-Unis',de:'Vereinigte Staaten'} },
  { code:'CA', lang:'en', flag:'🇨🇦', name:{bg:'Канада',en:'Canada',es:'Canadá',fr:'Canada',de:'Kanada'} },
  { code:'AU', lang:'en', flag:'🇦🇺', name:{bg:'Австралия',en:'Australia',es:'Australia',fr:'Australie',de:'Australien'} },
  { code:'DE', lang:'de', flag:'🇩🇪', name:{bg:'Германия',en:'Germany',es:'Alemania',fr:'Allemagne',de:'Deutschland'} },
  { code:'AT', lang:'de', flag:'🇦🇹', name:{bg:'Австрия',en:'Austria',es:'Austria',fr:'Autriche',de:'Österreich'} },
  { code:'CH', lang:'de', flag:'🇨🇭', name:{bg:'Швейцария',en:'Switzerland',es:'Suiza',fr:'Suisse',de:'Schweiz'} },
  { code:'FR', lang:'fr', flag:'🇫🇷', name:{bg:'Франция',en:'France',es:'Francia',fr:'France',de:'Frankreich'} },
  { code:'BE', lang:'fr', flag:'🇧🇪', name:{bg:'Белгия',en:'Belgium',es:'Bélgica',fr:'Belgique',de:'Belgien'} },
  { code:'ES', lang:'es', flag:'🇪🇸', name:{bg:'Испания',en:'Spain',es:'España',fr:'Espagne',de:'Spanien'} },
  { code:'MX', lang:'es', flag:'🇲🇽', name:{bg:'Мексико',en:'Mexico',es:'México',fr:'Mexique',de:'Mexiko'} },
  { code:'AR', lang:'es', flag:'🇦🇷', name:{bg:'Аржентина',en:'Argentina',es:'Argentina',fr:'Argentine',de:'Argentinien'} },
  { code:'CO', lang:'es', flag:'🇨🇴', name:{bg:'Колумбия',en:'Colombia',es:'Colombia',fr:'Colombie',de:'Kolumbien'} },
  { code:'JP', lang:'en', flag:'🇯🇵', name:{bg:'Япония',en:'Japan',es:'Japón',fr:'Japon',de:'Japan'} },
  { code:'IT', lang:'en', flag:'🇮🇹', name:{bg:'Италия',en:'Italy',es:'Italia',fr:'Italie',de:'Italien'} },
  { code:'GR', lang:'en', flag:'🇬🇷', name:{bg:'Гърция',en:'Greece',es:'Grecia',fr:'Grèce',de:'Griechenland'} },
  { code:'BR', lang:'en', flag:'🇧🇷', name:{bg:'Бразилия',en:'Brazil',es:'Brasil',fr:'Brésil',de:'Brasilien'} },
  { code:'MA', lang:'fr', flag:'🇲🇦', name:{bg:'Мароко',en:'Morocco',es:'Marruecos',fr:'Maroc',de:'Marokko'} },
  { code:'NL', lang:'en', flag:'🇳🇱', name:{bg:'Нидерландия',en:'Netherlands',es:'Países Bajos',fr:'Pays-Bas',de:'Niederlande'} },
  { code:'PL', lang:'en', flag:'🇵🇱', name:{bg:'Полша',en:'Poland',es:'Polonia',fr:'Pologne',de:'Polen'} },
  { code:'RO', lang:'en', flag:'🇷🇴', name:{bg:'Румъния',en:'Romania',es:'Rumanía',fr:'Roumanie',de:'Rumänien'} },
  { code:'ID', lang:'en', flag:'🇮🇩', name:{bg:'Индонезия',en:'Indonesia',es:'Indonesia',fr:'Indonésie',de:'Indonesien'} },
  { code:'IS', lang:'en', flag:'🇮🇸', name:{bg:'Исландия',en:'Iceland',es:'Islandia',fr:'Islande',de:'Island'} },
  { code:'TH', lang:'en', flag:'🇹🇭', name:{bg:'Тайланд',en:'Thailand',es:'Tailandia',fr:'Thaïlande',de:'Thailand'} },
  { code:'PT', lang:'en', flag:'🇵🇹', name:{bg:'Португалия',en:'Portugal',es:'Portugal',fr:'Portugal',de:'Portugal'} },
  { code:'NZ', lang:'en', flag:'🇳🇿', name:{bg:'Нова Зеландия',en:'New Zealand',es:'Nueva Zelanda',fr:'Nouvelle-Zélande',de:'Neuseeland'} },
  { code:'NO', lang:'en', flag:'🇳🇴', name:{bg:'Норвегия',en:'Norway',es:'Noruega',fr:'Norvège',de:'Norwegen'} },
  { code:'VN', lang:'en', flag:'🇻🇳', name:{bg:'Виетнам',en:'Vietnam',es:'Vietnam',fr:'Viêt Nam',de:'Vietnam'} },
  { code:'NP', lang:'en', flag:'🇳🇵', name:{bg:'Непал',en:'Nepal',es:'Nepal',fr:'Népal',de:'Nepal'} },
  { code:'PE', lang:'en', flag:'🇵🇪', name:{bg:'Перу',en:'Peru',es:'Perú',fr:'Pérou',de:'Peru'} },
  { code:'JO', lang:'en', flag:'🇯🇴', name:{bg:'Йордания',en:'Jordan',es:'Jordania',fr:'Jordanie',de:'Jordanien'} },
  { code:'IN', lang:'en', flag:'🇮🇳', name:{bg:'Индия',en:'India',es:'India',fr:'Inde',de:'Indien'} },
  { code:'TR', lang:'en', flag:'🇹🇷', name:{bg:'Турция',en:'Turkey',es:'Turquía',fr:'Turquie',de:'Türkei'} },
  { code:'EG', lang:'en', flag:'🇪🇬', name:{bg:'Египет',en:'Egypt',es:'Egipto',fr:'Égypte',de:'Ägypten'} },
  { code:'ZA', lang:'en', flag:'🇿🇦', name:{bg:'Южна Африка',en:'South Africa',es:'Sudáfrica',fr:'Afrique du Sud',de:'Südafrika'} },
  { code:'MV', lang:'en', flag:'🇲🇻', name:{bg:'Малдиви',en:'Maldives',es:'Maldivas',fr:'Maldives',de:'Malediven'} },
  { code:'CU', lang:'en', flag:'🇨🇺', name:{bg:'Куба',en:'Cuba',es:'Cuba',fr:'Cuba',de:'Kuba'} },
  { code:'KH', lang:'en', flag:'🇰🇭', name:{bg:'Камбоджа',en:'Cambodia',es:'Camboya',fr:'Cambodge',de:'Kambodscha'} },
  { code:'PH', lang:'en', flag:'🇵🇭', name:{bg:'Филипини',en:'Philippines',es:'Filipinas',fr:'Philippines',de:'Philippinen'} },
  { code:'LK', lang:'en', flag:'🇱🇰', name:{bg:'Шри Ланка',en:'Sri Lanka',es:'Sri Lanka',fr:'Sri Lanka',de:'Sri Lanka'} },
  { code:'TZ', lang:'en', flag:'🇹🇿', name:{bg:'Танзания',en:'Tanzania',es:'Tanzania',fr:'Tanzanie',de:'Tansania'} },
  { code:'CR', lang:'en', flag:'🇨🇷', name:{bg:'Коста Рика',en:'Costa Rica',es:'Costa Rica',fr:'Costa Rica',de:'Costa Rica'} },
];

function getCountryDisplayName(country, lang) {
  if (!country) return '';
  const code = country.code || country;
  const staticName = country.name?.[lang] || country.name?.en;
  try {
    const localized = new Intl.DisplayNames([lang], { type: 'region' }).of(code);
    if (localized) return localized;
  } catch {}
  return staticName || code;
}

// ── Profile field translation lookups ────────────────────────────────────────
const BODY_TYPE_LABELS = {
  bg:{ slim:'Слаб/а', athletic:'Атлетичен/а', average:'Среден/а', curvy:'Закръглен/а', plus:'Едър/а', large:'Едро' },
  en:{ slim:'Slim', athletic:'Athletic', average:'Average', curvy:'Curvy', plus:'Plus size', large:'Large' },
  es:{ slim:'Delgado/a', athletic:'Atlético/a', average:'Promedio', curvy:'Con curvas', plus:'Talla grande', large:'Grande' },
  fr:{ slim:'Mince', athletic:'Athlétique', average:'Moyen/ne', curvy:'Pulpeux/se', plus:'Grande taille', large:'Fort' },
  de:{ slim:'Schlank', athletic:'Athletisch', average:'Durchschnittlich', curvy:'Kurvig', plus:'Plus-size', large:'Kräftig' },
  el:{ slim:'Λεπτός/ή', athletic:'Αθλητικός/ή', average:'Μέσος/η', curvy:'Με καμπύλες', plus:'Plus size', large:'Μεγαλόσωμος/η' },
  it:{ slim:'Snello/a', athletic:'Atletico/a', average:'Normale', curvy:'Curvy', plus:'Taglia forte', large:'Robusto/a' },
};
const EDUCATION_LABELS = {
  bg:{ high_school:'Средно образование', bachelors:'Бакалавър', masters:'Магистър', master:'Магистър', phd:'Доктор', vocational:'Професионално' },
  en:{ high_school:'High school', bachelors:"Bachelor's", masters:"Master's", master:"Master's", phd:'PhD', vocational:'Vocational' },
  es:{ high_school:'Bachillerato', bachelors:'Licenciatura', masters:'Máster', master:'Máster', phd:'Doctorado', vocational:'Formación profesional' },
  fr:{ high_school:'Lycée', bachelors:'Licence', masters:'Master', master:'Master', phd:'Doctorat', vocational:'Formation professionnelle' },
  de:{ high_school:'Abitur', bachelors:'Bachelor', masters:'Master', master:'Master', phd:'Promotion', vocational:'Berufsausbildung' },
  el:{ high_school:'Λύκειο', bachelors:'Πτυχίο', masters:'Μεταπτυχιακό', master:'Μεταπτυχιακό', phd:'Διδακτορικό', vocational:'Επαγγελματική εκπαίδευση' },
  it:{ high_school:'Liceo', bachelors:'Laurea', masters:'Laurea magistrale', master:'Laurea magistrale', phd:'Dottorato', vocational:'Formazione professionale' },
};
const CHILDREN_LABELS = {
  bg:{ none:'Няма деца', have:'Има деца', has:'Има деца', want:'Иска деца', 'dont-want':'Не иска деца', open:'Отворен/а' },
  en:{ none:'No children', have:'Has children', has:'Has children', want:'Wants children', 'dont-want':"Doesn't want children", open:'Open to it' },
  es:{ none:'Sin hijos', have:'Tiene hijos', has:'Tiene hijos', want:'Quiere hijos', 'dont-want':'No quiere hijos', open:'Abierto/a' },
  fr:{ none:'Sans enfants', have:'A des enfants', has:'A des enfants', want:'Veut des enfants', 'dont-want':"Ne veut pas d'enfants", open:'Ouvert/e' },
  de:{ none:'Keine Kinder', have:'Hat Kinder', has:'Hat Kinder', want:'Möchte Kinder', 'dont-want':'Möchte keine Kinder', open:'Offen dafür' },
  el:{ none:'Δεν έχει παιδιά', have:'Έχει παιδιά', has:'Έχει παιδιά', want:'Θέλει παιδιά', 'dont-want':'Δεν θέλει παιδιά', open:'Ανοιχτός/ή σε αυτό' },
  it:{ none:'Senza figli', have:'Ha figli', has:'Ha figli', want:'Vuole figli', 'dont-want':'Non vuole figli', open:"Aperto/a all'idea" },
};
const GOAL_LABELS = {
  bg:{ friendship:'Приятелство', friends:'Приятели', dating:'Запознанство', relationship:'Връзка', travel_buddy:'Спътник за пътуване', open:'Отворен/а' },
  en:{ friendship:'Friendship', friends:'Friends', dating:'Dating', relationship:'Relationship', travel_buddy:'Travel buddy', open:'Open' },
  es:{ friendship:'Amistad', friends:'Amigos', dating:'Citas', relationship:'Relación', travel_buddy:'Compañero de viaje', open:'Abierto/a' },
  fr:{ friendship:'Amitié', friends:'Amis', dating:'Rencontres', relationship:'Relation', travel_buddy:'Compagnon de voyage', open:'Ouvert/e' },
  de:{ friendship:'Freundschaft', friends:'Freunde', dating:'Dating', relationship:'Beziehung', travel_buddy:'Reisepartner', open:'Offen' },
  el:{ friendship:'Φιλία', friends:'Φίλοι', dating:'Γνωριμίες', relationship:'Σχέση', travel_buddy:'Συνταξιδιώτης', open:'Ανοιχτός/ή' },
  it:{ friendship:'Amicizia', friends:'Amici', dating:'Incontri', relationship:'Relazione', travel_buddy:'Compagno di viaggio', open:'Aperto/a' },
};
const ALCOHOL_LABELS = {
  bg:{ yes:'Пия алкохол', no:'Не пия алкохол', social:'Само социално' },
  en:{ yes:'Drinks', no:'Non-drinker', social:'Social drinker' },
  es:{ yes:'Bebe', no:'No bebe', social:'Bebe socialmente' },
  fr:{ yes:"Boit de l'alcool", no:'Ne boit pas', social:'Boit socialement' },
  de:{ yes:'Trinkt Alkohol', no:'Trinkt nicht', social:'Trinkt sozial' },
  el:{ yes:'Πίνει αλκοόλ', no:'Δεν πίνει αλκοόλ', social:'Κοινωνικά μόνο' },
  it:{ yes:'Beve', no:'Non beve', social:'Beve socialmente' },
};
const SMOKING_LABELS = {
  bg:{ yes:'Пуши', no:'Не пуши', sometimes:'Пуши понякога', never:'Не пуши', occasionally:'Пуши понякога', regularly:'Пуши редовно' },
  en:{ yes:'Smoker', no:'Non-smoker', sometimes:'Occasional smoker', never:'Non-smoker', occasionally:'Occasional smoker', regularly:'Regular smoker' },
  es:{ yes:'Fumador/a', no:'No fumador/a', sometimes:'Fumador/a ocasional', never:'No fumador/a', occasionally:'Fumador/a ocasional', regularly:'Fumador/a regular' },
  fr:{ yes:'Fumeur/se', no:'Non-fumeur/se', sometimes:'Fumeur/se occasionnel/le', never:'Non-fumeur/se', occasionally:'Fumeur/se occasionnel/le', regularly:'Fumeur/se régulier/ère' },
  de:{ yes:'Raucher/in', no:'Nichtraucher/in', sometimes:'Gelegenheitsraucher/in', never:'Nichtraucher/in', occasionally:'Gelegenheitsraucher/in', regularly:'Regelmäßiger Raucher' },
  el:{ yes:'Καπνίζει', no:'Δεν καπνίζει', sometimes:'Καπνίζει περιστασιακά', never:'Δεν καπνίζει', occasionally:'Καπνίζει περιστασιακά', regularly:'Καπνίζει τακτικά' },
  it:{ yes:'Fumatore/trice', no:'Non fumatore/trice', sometimes:'Fumatore/trice occasionale', never:'Non fumatore/trice', occasionally:'Fumatore/trice occasionale', regularly:'Fumatore/trice abituale' },
};

// ── DNA Quiz ──────────────────────────────────────────────────────────────────
const QUIZ_SCORES = [
  [[5,8,0,5],[3,0,10,2],[2,2,3,5],[8,5,3,8]],
  [[5,5,0,10],[5,5,5,5],[3,0,8,0]],
  [[3,3,8,2],[8,5,5,5],[5,8,5,10]],
  [[5,5,3,0],[5,8,8,3],[5,5,3,10]],
];
const DNA_TRAIT_KEYS = ['explorer','adventurer','culturist','nomad'];
const DNA_COLORS = ['#c9a84c','#e07b39','#8e44ad','#1abc9c'];

function calcDNA(answers) {
  const totals = [0,0,0,0];
  answers.forEach((ai, qi) => QUIZ_SCORES[qi][ai].forEach((v,i) => { totals[i] += v; }));
  const max = Math.max(...totals) || 1;
  return totals.map(v => Math.round((v / max) * 100));
}

// ── Supabase REST helpers (bypass JS client session issues) ──────────────────
let authTokenCache = { accessToken: null, userId: null };

function cacheAuthSession(session) {
  const accessToken = session?.access_token || null;
  const userId = session?.user?.id || null;
  const expiresAt = session?.expires_at || null;
  if (!accessToken || !userId) return;
  authTokenCache = { accessToken, userId, expiresAt };
}

function clearAuthTokenCache() {
  authTokenCache = { accessToken: null, userId: null, expiresAt: null };
}

function removeSupabaseAuthStorage() {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
      .forEach(k => localStorage.removeItem(k));
  } catch(e) {}
}

// Audit finding: photo uploads (avatar, gallery, verification selfie/front)
// only relied on the HTML <input accept="image/*"> attribute, which is a UI
// hint only and trivially bypassed — the actual file was never checked
// before being uploaded to Supabase Storage. This validates real MIME type
// and size. Client-side validation is a UX convenience, not a security
// boundary by itself — the storage bucket's own policies are the real
// enforcement and should mirror these same limits server-side.
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
function isAllowedImageFile(file) {
  if (!file || typeof file.type !== 'string') return false;
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return false;
  if (typeof file.size === 'number' && file.size > MAX_IMAGE_BYTES) return false;
  return true;
}

function getAuthToken() {
  try {
    const key = Object.keys(localStorage).find(
      k => k.startsWith('sb-') && k.endsWith('-auth-token')
    );
    if (!key) return authTokenCache;
    const raw = localStorage.getItem(key);
    if (!raw) return authTokenCache;
    const parsed = JSON.parse(raw);
    const token = {
      accessToken: parsed?.access_token || null,
      userId: parsed?.user?.id || null,
      expiresAt: parsed?.expires_at || null,
    };
    if (token.accessToken && token.userId) authTokenCache = token;
    return token.accessToken && token.userId ? token : authTokenCache;
  } catch(e) {
    return authTokenCache;
  }
}

function profileIdKey(id) {
  return id == null ? '' : String(id);
}

function isAuthTokenExpired(token) {
  if (!token?.expiresAt) return false;
  return (Number(token.expiresAt) * 1000) <= Date.now() + 60000;
}

async function getAuthTokenSafe(options = {}) {
  const { forceFresh = false } = options;
  const cached = getAuthToken();
  if (cached.accessToken && cached.userId && !forceFresh && !isAuthTokenExpired(cached)) return cached;
  if (!supabase) return cached;
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise(resolve => setTimeout(() => resolve('__timeout__'), 2500)),
    ]);
    const session = result === '__timeout__' ? null : result?.data?.session;
    if (session?.access_token && session?.user?.id) {
      cacheAuthSession(session);
      return { accessToken: session.access_token, userId: session.user.id, expiresAt: session.expires_at || null };
    }
  } catch(e) {}
  if (forceFresh) {
    try {
      const refreshed = await Promise.race([
        supabase.auth.refreshSession(),
        new Promise(resolve => setTimeout(() => resolve('__timeout__'), 2500)),
      ]);
      const session = refreshed === '__timeout__' ? null : refreshed?.data?.session;
      if (session?.access_token && session?.user?.id) {
        cacheAuthSession(session);
        return { accessToken: session.access_token, userId: session.user.id, expiresAt: session.expires_at || null };
      }
    } catch(e) {}
  }
  return getAuthToken();
}

async function getActiveProfileId(options = {}) {
  const auth = await getAuthTokenSafe(options);
  const sessionProfileId = profileIdKey(auth?.userId);
  if (!sessionProfileId || !auth?.accessToken) return null;
  try {
    const rows = await sbFetch('GET', 'profiles', `id=eq.${sessionProfileId}&select=id&limit=1`);
    const profileId = profileIdKey(Array.isArray(rows) && rows[0]?.id);
    return profileId || null;
  } catch (e) {
    return null;
  }
}

async function sbFetch(method, table, query, body, extraHeaders) {
  const { accessToken } = await getAuthTokenSafe();
  const base = process.env.REACT_APP_SUPABASE_URL;
  const apiKey = process.env.REACT_APP_SUPABASE_KEY;
  const url = `${base}/rest/v1/${table}${query ? '?' + query : ''}`;
  const headers = {
    'apikey': apiKey,
    'Authorization': `Bearer ${accessToken}`,
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(extraHeaders || {}),
  };
  const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (res.status === 401) window.dispatchEvent(new CustomEvent('ts-401'));
  if (method === 'GET') {
    if (res.ok) return res.json();
    const err = new Error('sbFetch GET failed: ' + res.status);
    err.status = res.status;
    throw err;
  }
  return res;
}

async function sbRpc(functionName, body = {}) {
  const res = await sbFetch('POST', `rpc/${functionName}`, '', body);
  await ensureSbWriteOk(res, `rpc/${functionName}`);
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

async function ensureSbWriteOk(res, label, okStatuses = []) {
  if (res?.ok || okStatuses.includes(res?.status)) return;
  let body = '';
  let parsed = null;
  try { body = await res.text(); } catch (e) {}
  try { parsed = body ? JSON.parse(body) : null; } catch (e) {}
  const message = parsed?.message || body;
  const err = new Error(`${label} failed${res?.status ? `: ${res.status}` : ''}${message ? ` ${message}` : ''}`);
  err.status = res?.status;
  err.code = parsed?.code;
  err.details = parsed?.details;
  err.hint = parsed?.hint;
  err.body = body;
  throw err;
}

function isDuplicateWriteError(err) {
  const message = String(err?.message || '').toLowerCase();
  return err?.status === 409 || err?.code === '23505' || message.includes('duplicate key') || message.includes('already exists');
}

function getVerificationStatus(profile) {
  if (!profile) return 'unverified';
  if (profile.is_admin || profile.is_moderator || profile.verified === true) return 'approved';
  return ['unverified', 'pending', 'approved', 'rejected'].includes(profile.verification_status)
    ? profile.verification_status
    : 'unverified';
}

function canUseSocialFeatures(profile) {
  return getVerificationStatus(profile) === 'approved';
}

function getVerificationRequiredMessage(lang) {
  return lang==='bg'?'Трябва първо да верифицираш профила си със селфи снимка, за да използваш тази функция.':lang==='es'?'Primero debes verificar tu perfil con una selfie para usar esta función.':lang==='fr'?'Vous devez d\'abord vérifier votre profil avec un selfie pour utiliser cette fonction.':lang==='de'?'Du musst dein Profil zuerst mit einem Selfie verifizieren, um diese Funktion zu nutzen.':lang==='el'?'Πρέπει πρώτα να επαληθεύσεις το προφίλ σου με μια selfie για να χρησιμοποιήσεις αυτή τη λειτουργία.':lang==='it'?'Devi prima verificare il tuo profilo con un selfie per usare questa funzione.':'You need to verify your profile with a selfie photo before using this feature.';
}

function getVerificationStatusText(profile, lang) {
  // 4th audit finding: this was bg/en-only with no es/fr/de branches,
  // unlike the rest of the file's fully-converted 5-language pattern.
  const status = getVerificationStatus(profile);
  if (lang === 'bg') {
    if (status === 'approved') return 'Верифициран профил';
    if (status === 'pending') return 'Верификацията чака одобрение';
    if (status === 'rejected') return 'Профилът не беше одобрен';
    return 'Профилът не е верифициран';
  }
  if (lang === 'es') {
    if (status === 'approved') return 'Perfil verificado';
    if (status === 'pending') return 'Verificación pendiente de revisión';
    if (status === 'rejected') return 'El perfil no fue aprobado';
    return 'El perfil no está verificado';
  }
  if (lang === 'fr') {
    if (status === 'approved') return 'Profil vérifié';
    if (status === 'pending') return 'Vérification en cours d\'examen';
    if (status === 'rejected') return 'Le profil n\'a pas été approuvé';
    return 'Le profil n\'est pas vérifié';
  }
  if (lang === 'de') {
    if (status === 'approved') return 'Verifiziertes Profil';
    if (status === 'pending') return 'Verifizierung wird überprüft';
    if (status === 'rejected') return 'Profil wurde nicht genehmigt';
    return 'Profil ist nicht verifiziert';
  }
  if (lang === 'el') {
    if (status === 'approved') return 'Επαληθευμένο προφίλ';
    if (status === 'pending') return 'Η επαλήθευση εκκρεμεί';
    if (status === 'rejected') return 'Το προφίλ δεν εγκρίθηκε';
    return 'Το προφίλ δεν είναι επαληθευμένο';
  }
  if (lang === 'it') {
    if (status === 'approved') return 'Profilo verificato';
    if (status === 'pending') return 'Verifica in attesa di revisione';
    if (status === 'rejected') return 'Il profilo non è stato approvato';
    return 'Il profilo non è verificato';
  }
  if (status === 'approved') return 'Verified profile';
  if (status === 'pending') return 'Verification is pending review';
  if (status === 'rejected') return 'Profile was not approved';
  return 'Profile is not verified';
}

async function getActiveSocialProfile() {
  const activeProfileId = await getActiveProfileId({ forceFresh: true });
  if (!activeProfileId) return null;
  const rows = await sbFetch('GET', 'profiles', `id=eq.${activeProfileId}&select=id,name,verified,verification_status,is_admin,is_moderator,verification_rejection_reason&limit=1`);
  return Array.isArray(rows) && rows[0] ? rows[0] : null;
}

function getBlockLabels(lang) {
  const labels = {
    bg: {
      title: 'Блокирай потребителя',
      body: 'Той/тя повече няма да може да ти пише или да се показва в съвпаденията и откриването. Можеш да отблокираш по-късно.',
      cancel: 'Отказ',
      block: 'Блокирай потребителя',
      blockShort: 'Блокирай',
      blocked: 'Потребителят е блокиран',
      unavailable: 'Този потребител не е наличен за съобщения.',
      blockedState: 'Този чат е блокиран. Съобщенията са изключени.',
      safetyTitle: 'Безопасност',
      blockedUsers: 'Блокирани потребители',
      unblock: 'Отблокирай',
      noBlocked: 'Няма блокирани потребители.',
      loadError: 'Блокираните потребители не можаха да се заредят.',
    },
    en: {
      title: 'Block user',
      body: 'They will no longer be able to message you or appear in your matches/discovery. You can unblock them later.',
      cancel: 'Cancel',
      block: 'Block user',
      blockShort: 'Block user',
      blocked: 'User blocked',
      unavailable: 'This user is not available for messaging.',
      blockedState: 'This chat is blocked. Messaging is disabled.',
      safetyTitle: 'Safety',
      blockedUsers: 'Blocked users',
      unblock: 'Unblock',
      noBlocked: 'No blocked users.',
      loadError: 'Blocked users could not be loaded.',
    },
    es: {
      title: 'Bloquear usuario',
      body: 'Ya no podrá enviarte mensajes ni aparecer en tus coincidencias o descubrimiento. Puedes desbloquearlo más tarde.',
      cancel: 'Cancelar',
      block: 'Bloquear usuario',
      blockShort: 'Bloquear',
      blocked: 'Usuario bloqueado',
      unavailable: 'Este usuario no está disponible para mensajes.',
      blockedState: 'Este chat está bloqueado. Los mensajes están desactivados.',
      safetyTitle: 'Seguridad',
      blockedUsers: 'Usuarios bloqueados',
      unblock: 'Desbloquear',
      noBlocked: 'No hay usuarios bloqueados.',
      loadError: 'No se pudieron cargar los usuarios bloqueados.',
    },
    fr: {
      title: 'Bloquer l’utilisateur',
      body: 'Cette personne ne pourra plus vous envoyer de messages ni apparaître dans vos matchs ou découvertes. Vous pourrez la débloquer plus tard.',
      cancel: 'Annuler',
      block: 'Bloquer l’utilisateur',
      blockShort: 'Bloquer',
      blocked: 'Utilisateur bloqué',
      unavailable: 'Cet utilisateur n’est pas disponible pour les messages.',
      blockedState: 'Ce chat est bloqué. Les messages sont désactivés.',
      safetyTitle: 'Sécurité',
      blockedUsers: 'Utilisateurs bloqués',
      unblock: 'Débloquer',
      noBlocked: 'Aucun utilisateur bloqué.',
      loadError: 'Impossible de charger les utilisateurs bloqués.',
    },
    de: {
      title: 'Nutzer blockieren',
      body: 'Diese Person kann dir keine Nachrichten mehr senden und erscheint nicht mehr in deinen Matches oder Entdeckungen. Du kannst sie später entsperren.',
      cancel: 'Abbrechen',
      block: 'Nutzer blockieren',
      blockShort: 'Blockieren',
      blocked: 'Nutzer blockiert',
      unavailable: 'Dieser Nutzer ist für Nachrichten nicht verfügbar.',
      blockedState: 'Dieser Chat ist blockiert. Nachrichten sind deaktiviert.',
      safetyTitle: 'Sicherheit',
      blockedUsers: 'Blockierte Nutzer',
      unblock: 'Entsperren',
      noBlocked: 'Keine blockierten Nutzer.',
      loadError: 'Blockierte Nutzer konnten nicht geladen werden.',
    },
    el: {
      title: 'Αποκλεισμός χρήστη',
      body: 'Δεν θα μπορεί πλέον να σου στέλνει μηνύματα ή να εμφανίζεται στα matches/ανακάλυψη. Μπορείς να τον ξεμπλοκάρεις αργότερα.',
      cancel: 'Άκυρο',
      block: 'Αποκλεισμός χρήστη',
      blockShort: 'Αποκλεισμός',
      blocked: 'Ο χρήστης αποκλείστηκε',
      unavailable: 'Αυτός ο χρήστης δεν είναι διαθέσιμος για μηνύματα.',
      blockedState: 'Αυτή η συνομιλία είναι μπλοκαρισμένη. Τα μηνύματα είναι απενεργοποιημένα.',
      safetyTitle: 'Ασφάλεια',
      blockedUsers: 'Αποκλεισμένοι χρήστες',
      unblock: 'Άρση αποκλεισμού',
      noBlocked: 'Δεν υπάρχουν αποκλεισμένοι χρήστες.',
      loadError: 'Δεν ήταν δυνατή η φόρτωση των αποκλεισμένων χρηστών.',
    },
    it: {
      title: 'Blocca utente',
      body: 'Non potrà più inviarti messaggi né apparire nei tuoi match o nella scoperta. Puoi sbloccarlo più tardi.',
      cancel: 'Annulla',
      block: 'Blocca utente',
      blockShort: 'Blocca',
      blocked: 'Utente bloccato',
      unavailable: 'Questo utente non è disponibile per i messaggi.',
      blockedState: 'Questa chat è bloccata. I messaggi sono disattivati.',
      safetyTitle: 'Sicurezza',
      blockedUsers: 'Utenti bloccati',
      unblock: 'Sblocca',
      noBlocked: 'Nessun utente bloccato.',
      loadError: 'Impossibile caricare gli utenti bloccati.',
    },
  };
  return labels[lang] || labels.en;
}

function isBlockedWriteError(err) {
  const text = String(err?.message || err?.body || err?.details || err || '').toLowerCase();
  return err?.code === '42501'
    || err?.status === 401
    || err?.status === 403
    || text.includes('not available for messaging')
    || text.includes('this profile is not available')
    || text.includes('row-level security');
}

async function getBlockedProfileIds(activeProfileId = null) {
  const uid = profileIdKey(activeProfileId) || await getActiveProfileId({ forceFresh: true });
  if (!uid) return new Set();
  const rows = await sbFetch('GET', 'user_blocks', `blocker_id=eq.${uid}&select=blocked_id`);
  return new Set((Array.isArray(rows) ? rows : []).map(row => profileIdKey(row.blocked_id)).filter(Boolean));
}

async function isProfilePairBlocked(profileA, profileB) {
  const a = profileIdKey(profileA);
  const b = profileIdKey(profileB);
  if (!a || !b || a === b) return false;
  try {
    return !!(await sbRpc('is_profile_pair_blocked', { profile_a: a, profile_b: b }));
  } catch (e) {
    const rows = await sbFetch('GET', 'user_blocks', `blocker_id=eq.${a}&blocked_id=eq.${b}&select=id&limit=1`);
    return Array.isArray(rows) && rows.length > 0;
  }
}

async function blockProfile(blockedProfileId, reason = null) {
  const blockerId = await getActiveProfileId({ forceFresh: true });
  const blockedId = profileIdKey(blockedProfileId);
  if (!blockerId || !blockedId || blockerId === blockedId) return false;
  const payload = { blocker_id: blockerId, blocked_id: blockedId };
  if (reason) payload.reason = reason;
  const res = await sbFetch('POST', 'user_blocks', '', payload, { Prefer: 'return=minimal' });
  await ensureSbWriteOk(res, 'user block insert', [409]);
  try {
    await Promise.all([
      sbFetch('DELETE', 'matches', `or=(and(user1.eq.${blockerId},user2.eq.${blockedId}),and(user1.eq.${blockedId},user2.eq.${blockerId}))`, null, { Prefer: 'return=minimal' }).catch(() => null),
      sbFetch('DELETE', 'likes', `or=(and(from_user.eq.${blockerId},to_user.eq.${blockedId}),and(from_user.eq.${blockedId},to_user.eq.${blockerId}))`, null, { Prefer: 'return=minimal' }).catch(() => null),
    ]);
  } catch (e) {}
  try {
    const localBlocked = JSON.parse(localStorage.getItem('travelsoul_blocked') || '[]');
    if (!localBlocked.includes(blockedId)) localStorage.setItem('travelsoul_blocked', JSON.stringify([...localBlocked, blockedId]));
  } catch {}
  return true;
}

async function unblockProfile(blockedProfileId) {
  const blockerId = await getActiveProfileId({ forceFresh: true });
  const blockedId = profileIdKey(blockedProfileId);
  if (!blockerId || !blockedId) return false;
  const res = await sbFetch('DELETE', 'user_blocks', `blocker_id=eq.${blockerId}&blocked_id=eq.${blockedId}`, null, { Prefer: 'return=minimal' });
  await ensureSbWriteOk(res, 'user block delete');
  try {
    const localBlocked = JSON.parse(localStorage.getItem('travelsoul_blocked') || '[]');
    localStorage.setItem('travelsoul_blocked', JSON.stringify(localBlocked.filter(id => profileIdKey(id) !== blockedId)));
  } catch {}
  return true;
}

function BlockConfirmModal({ lang, onCancel, onConfirm, busy }) {
  const labels = getBlockLabels(lang);
  return (
    <div style={{...OVERLAY_S, zIndex:12000}} onClick={onCancel}>
      <div style={{...MODAL_S, maxWidth:360, textAlign:'left'}} onClick={e=>e.stopPropagation()}>
        <h2 className="ts-serif" style={{fontSize:'1.1rem',color:'#f85149',margin:'0 0 10px'}}>{labels.title}</h2>
        <p style={{color:'#c9d1d9',fontSize:'0.9rem',lineHeight:1.55,margin:'0 0 18px'}}>{labels.body}</p>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="ts-btn-ghost-sm" onClick={onCancel} disabled={busy}>{labels.cancel}</button>
          <button className="ts-btn-ghost-sm" style={{color:'#fff',background:'#f85149',borderColor:'#f85149'}} onClick={onConfirm} disabled={busy}>
            {busy ? '...' : labels.block}
          </button>
        </div>
      </div>
    </div>
  );
}

function serializeLikeSaveError(err) {
  if (!err) return null;
  return {
    message: err?.message || String(err),
    code: err?.code || null,
    details: err?.details || null,
    hint: err?.hint || null,
    status: err?.status || null,
    name: err?.name || null,
  };
}

function stringifyLikeSaveDebug(debugObject) {
  try {
    return JSON.stringify(debugObject, null, 2);
  } catch (jsonErr) {
    return JSON.stringify({
      message: 'LikeSave debug stringify failed',
      stringifyError: jsonErr?.message || String(jsonErr),
    }, null, 2);
  }
}

function logLikeSaveError(stage, err, context = {}) {
  const debugObject = {
    stage,
    message: err?.message || String(err),
    code: err?.code || null,
    details: err?.details || null,
    hint: err?.hint || null,
    status: err?.status || null,
    currentUserId: context.userId || null,
    targetProfileId: context.profileId || null,
    insertPayload: context.payload || null,
    supabaseClientInsertAttempted: !!context.supabaseClientInsertAttempted,
    supabaseClientResult: context.supabaseClientResult || null,
    supabaseClientError: context.supabaseClientError || null,
    restFallbackAttempted: !!context.restFallbackAttempted,
    restFallbackResult: context.restFallbackResult || null,
    restFallbackError: context.restFallbackError || serializeLikeSaveError(err),
    authUserId: context.authUserId || context.userId || null,
    sessionExists: !!context.sessionExists,
    accessTokenExists: !!context.accessToken,
  };
  console.error(`[LikeSave] ${stringifyLikeSaveDebug(debugObject)}`);
}

function getLikeSupabaseError(err) {
  return err?.supabaseError || err?.restFallbackError || err?.supabaseClientError || err || null;
}

function logLikeSaveDetailedError(err, context = {}) {
  const supabaseError = getLikeSupabaseError(err);
  console.error('[LIKE_SAVE_ERROR]', {
    authUserId: context.authUserId || null,
    activeProfileId: context.activeProfileId || context.userId || null,
    activeProfileName: context.activeProfileName || null,
    activeProfile: context.activeProfile || null,
    targetProfileId: context.targetProfileId || context.profileId || null,
    targetProfileName: context.targetProfileName || null,
    targetProfile: context.targetProfile || null,
    source: context.source || 'unknown',
    insertPayload: context.payload || null,
    supabaseError,
    supabaseErrorCode: supabaseError?.code || err?.code || null,
    supabaseErrorMessage: supabaseError?.message || err?.message || String(err),
    supabaseErrorDetails: supabaseError?.details || err?.details || null,
    supabaseErrorHint: supabaseError?.hint || err?.hint || null,
  });
}

function getLikeProfileDebug(profile) {
  if (!profile) return null;
  return {
    id: profile.id || null,
    name: profile.name || null,
    verified: profile.verified ?? null,
    verification_status: profile.verification_status || null,
    role: profile.role || null,
    is_admin: profile.is_admin ?? null,
    is_moderator: profile.is_moderator ?? null,
    duplicate_of_profile_id: profile.duplicate_of_profile_id || null,
    deleted_at: profile.deleted_at || null,
    is_blocked: profile.is_blocked ?? null,
  };
}

async function loadLikeProfileDebug(profileId) {
  const id = profileIdKey(profileId);
  if (!id) return null;
  try {
    const rows = await sbFetch(
      'GET',
      'profiles',
      `id=eq.${id}&select=id,name,verified,verification_status,is_admin,is_moderator,duplicate_of_profile_id,deleted_at,is_blocked&limit=1`
    );
    return getLikeProfileDebug(Array.isArray(rows) ? rows[0] : null);
  } catch (e) {
    try {
      const rows = await sbFetch(
        'GET',
        'profiles',
        `id=eq.${id}&select=id,name,verified,verification_status,is_admin,is_moderator&limit=1`
      );
      return {
        ...getLikeProfileDebug(Array.isArray(rows) ? rows[0] : null),
        optionalFlagsLoadError: e?.message || String(e),
        optionalFlagsStatus: e?.status || null,
        optionalFlagsCode: e?.code || null,
      };
    } catch (fallbackError) {
      return { id, loadError: fallbackError?.message || String(fallbackError), status: fallbackError?.status || null, code: fallbackError?.code || null };
    }
  }
}

function logLikeSaveDiagnostic(stage, context = {}) {
  console.info('[LIKE_SAVE_DIAGNOSTIC]', {
    stage,
    source: context.source || 'unknown',
    authUserId: context.authUserId || null,
    activeProfileId: context.activeProfileId || context.userId || null,
    activeProfileName: context.activeProfileName || null,
    activeProfile: context.activeProfile || null,
    targetProfileId: context.targetProfileId || context.profileId || null,
    targetProfileName: context.targetProfileName || null,
    targetProfile: context.targetProfile || null,
    insertPayload: context.payload || null,
    existingLikeCount: context.existingLikeCount ?? null,
    reverseLikeCount: context.reverseLikeCount ?? null,
    matchExists: context.matchExists ?? null,
    result: context.result || null,
  });
}

function getLikeSaveFailureToast(err, lang) {
  if (err?.code === 'VERIFICATION_REQUIRED') return getVerificationRequiredMessage(lang);
  if (err?.code === 'PROFILE_BLOCKED') return getBlockLabels(lang).unavailable;
  const supabaseError = getLikeSupabaseError(err);
  const code = supabaseError?.code || err?.code || '';
  const status = supabaseError?.status || err?.status || null;
  const message = String(supabaseError?.message || err?.message || err || '').toLowerCase();
  if (code === '42501' || status === 401 || status === 403 || message.includes('row-level security') || message.includes('permission denied')) {
    return lang==='bg'?'Харесването не беше запазено: достъпът е блокиран.':lang==='es'?'No se pudo guardar el me gusta: acceso bloqueado.':lang==='fr'?'Le like n\'a pas été enregistré : accès bloqué.':lang==='de'?'Der Like wurde nicht gespeichert: Zugriff blockiert.':lang==='el'?'Το like δεν αποθηκεύτηκε: η πρόσβαση είναι αποκλεισμένη.':lang==='it'?'Il like non è stato salvato: accesso bloccato.':'Like was not saved: access blocked.';
  }
  if (code === '23503' || message.includes('foreign key')) {
    return lang==='bg'?'Харесването не беше запазено: профилът не е активен.':lang==='es'?'No se pudo guardar el me gusta: el perfil no está activo.':lang==='fr'?'Le like n\'a pas été enregistré : le profil n\'est pas actif.':lang==='de'?'Der Like wurde nicht gespeichert: Profil ist nicht aktiv.':lang==='el'?'Το like δεν αποθηκεύτηκε: το προφίλ δεν είναι ενεργό.':lang==='it'?'Il like non è stato salvato: il profilo non è attivo.':'Like was not saved: profile is not active.';
  }
  if (message.includes('duplicate') || code === '23505') {
    return lang==='bg'?'Профилът вече е харесан.':lang==='es'?'Ya has dado me gusta a este perfil.':lang==='fr'?'Ce profil a déjà été liké.':lang==='de'?'Dieses Profil wurde bereits geliked.':lang==='el'?'Το προφίλ έχει ήδη like.':lang==='it'?'Questo profilo ha già ricevuto un like.':'Profile already liked.';
  }
  // Server-side daily free-like cap (4th audit finding: this was previously
  // client-only / localStorage-only and trivially bypassable).
  if (message.includes('daily_like_limit_reached')) {
    return lang==='bg'?'Достигна дневния лимит харесвания за безплатен план.':lang==='es'?'Has alcanzado el límite diario de likes del plan gratuito.':lang==='fr'?'Vous avez atteint la limite quotidienne de likes du plan gratuit.':lang==='de'?'Du hast das tägliche Like-Limit des kostenlosen Plans erreicht.':lang==='el'?'Έφτασες το ημερήσιο όριο like του δωρεάν πλάνου.':lang==='it'?'Hai raggiunto il limite giornaliero di like del piano gratuito.':'You\'ve reached the free plan\'s daily like limit.';
  }
  // Server-side Super Like balance enforcement (7th audit finding: this was
  // previously unenforced client-side — free and unlimited).
  if (message.includes('no_super_likes_remaining')) {
    return lang==='bg'?'Нямаш повече супер харесвания. Спечели още от колелото на късмета!':lang==='es'?'No te quedan Super Likes. ¡Gana más en la ruleta!':lang==='fr'?'Plus de Super Likes. Gagnez-en avec la roue de la fortune !':lang==='de'?'Keine Super Likes mehr. Gewinne mehr am Glücksrad!':lang==='el'?'Δεν έχεις άλλα Super Likes. Κέρδισε περισσότερα στον Τροχό της Τύχης!':lang==='it'?'Niente più Super Like. Vincine altri alla Ruota della Fortuna!':'No Super Likes left. Win more from the daily spin!';
  }
  const isDebug = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_LIKES === '1';
  if (isDebug) {
    const debugCode = code || 'unknown';
    const debugMessage = supabaseError?.message || err?.message || String(err);
    return `Like save failed: ${debugCode} ${debugMessage}`;
  }
  return lang==='bg'?'Харесването не беше запазено.':lang==='es'?'No se pudo guardar el me gusta.':lang==='fr'?'Le like n\'a pas été enregistré.':lang==='de'?'Der Like wurde nicht gespeichert.':lang==='el'?'Το like δεν αποθηκεύτηκε.':lang==='it'?'Il like non è stato salvato.':'Like was not saved.';
}

async function insertSupabaseRow(table, payload, context, label) {
  let clientError = null;
  const debugContext = {
    ...context,
    payload,
    supabaseClientInsertAttempted: false,
    supabaseClientResult: null,
    supabaseClientError: null,
    restFallbackAttempted: false,
    restFallbackResult: null,
    restFallbackError: null,
  };
  if (supabase) {
    debugContext.supabaseClientInsertAttempted = true;
    try {
      const result = await Promise.race([
        supabase.from(table).insert(payload),
        new Promise(resolve => setTimeout(() => resolve('__timeout__'), 5000)),
      ]);
      if (result === '__timeout__') {
        clientError = new Error(`${label} Supabase client insert timed out`);
        debugContext.supabaseClientResult = { timedOut: true };
        debugContext.supabaseClientError = serializeLikeSaveError(clientError);
      } else if (result?.error) {
        clientError = result.error;
        debugContext.supabaseClientResult = {
          status: result.status || null,
          statusText: result.statusText || null,
          hasData: !!result.data,
        };
        debugContext.supabaseClientError = serializeLikeSaveError(clientError);
        if (isDuplicateWriteError(clientError)) return { duplicate: true, source: 'supabase-js' };
      } else {
        return { duplicate: false, source: 'supabase-js' };
      }
    } catch (err) {
      clientError = err;
      debugContext.supabaseClientError = serializeLikeSaveError(err);
      if (isDuplicateWriteError(err)) return { duplicate: true, source: 'supabase-js' };
    }
  }

  debugContext.restFallbackAttempted = true;
  const res = await sbFetch('POST', table, '', payload, { 'Prefer': 'return=minimal' });
  debugContext.restFallbackResult = {
    ok: !!res?.ok,
    status: res?.status || null,
    statusText: res?.statusText || null,
  };
  try {
    await ensureSbWriteOk(res, label, [409]);
    return { duplicate: res?.status === 409, source: 'rest' };
  } catch (restError) {
    debugContext.restFallbackError = serializeLikeSaveError(restError);
    if (clientError) debugContext.supabaseClientError = serializeLikeSaveError(clientError);
    logLikeSaveError(`${label} failed`, restError, debugContext);
    restError.supabaseError = debugContext.restFallbackError;
    restError.supabaseClientError = debugContext.supabaseClientError;
    restError.restFallbackError = debugContext.restFallbackError;
    throw restError;
  }
}

function logAdiMatchDebug(payload) {
  const activeProfileId = profileIdKey(payload.activeProfileId);
  const targetProfileId = profileIdKey(payload.targetProfileId);
  console.info('[ADI_MATCH_DEBUG]', {
    activeProfileId,
    activeProfileName: payload.activeProfileName || null,
    targetProfileId,
    targetProfileName: payload.targetProfileName || payload.targetName || null,
    isSelf: !!activeProfileId && activeProfileId === targetProfileId,
    targetIsAdmin: !!payload.targetIsAdmin,
    alreadyLiked: !!payload.liked || !!payload.alreadyLiked,
    reverseLiked: !!payload.reverseLiked,
    matchExists: !!payload.matchRow || !!payload.matchExists,
    isRealMatched: !!payload.isRealMatched,
  });
}

function logLikeTargetDebug(activeProfileId, targetProfileId, targetProfileName, source) {
  console.info('[LIKE_TARGET_DEBUG]', {
    activeProfileId: profileIdKey(activeProfileId) || null,
    targetProfileId: profileIdKey(targetProfileId) || null,
    targetProfileName: targetProfileName || null,
    source: source || 'unknown',
  });
}

function logLikeSaveAttempt(activeProfileId, activeProfileName, targetProfileId, targetProfileName, source) {
  const activeId = profileIdKey(activeProfileId);
  const targetId = profileIdKey(targetProfileId);
  console.info('[LIKE_SAVE_ATTEMPT]', {
    activeProfileId: activeId || null,
    activeProfileName: activeProfileName || null,
    targetProfileId: targetId || null,
    targetProfileName: targetProfileName || null,
    source: source || 'unknown',
    isSelf: !!activeId && activeId === targetId,
  });
}

function matchRowForPair(rows, activeProfileId, targetProfileId) {
  const activeId = profileIdKey(activeProfileId);
  const targetId = profileIdKey(targetProfileId);
  if (!activeId || !targetId || !Array.isArray(rows)) return null;
  return rows.find(row => (
    (profileIdKey(row.user1) === activeId && profileIdKey(row.user2) === targetId) ||
    (profileIdKey(row.user1) === targetId && profileIdKey(row.user2) === activeId)
  )) || null;
}

async function resolveProfileMatchState(activeProfileId, targetProfileId, targetName, options = {}) {
  const activeId = profileIdKey(activeProfileId);
  const targetId = profileIdKey(targetProfileId);
  if (!activeId || !targetId || activeId === targetId) {
    const empty = { liked: false, reverseLiked: false, matchRow: null, isRealMatched: false };
    if (options.log) logAdiMatchDebug({ activeProfileId: activeId || null, activeProfileName: options.activeProfileName || null, targetProfileId: targetId || null, targetProfileName: targetName || null, targetIsAdmin: options.targetIsAdmin, ...empty });
    return empty;
  }
  if (await isProfilePairBlocked(activeId, targetId).catch(() => false)) {
    const blocked = { liked: false, reverseLiked: false, matchRow: null, isRealMatched: false, blocked: true };
    if (options.log) logAdiMatchDebug({ activeProfileId: activeId, activeProfileName: options.activeProfileName || null, targetProfileId: targetId, targetProfileName: targetName || null, targetIsAdmin: options.targetIsAdmin, ...blocked });
    return blocked;
  }

  let likedRows = [];
  let reverseRows = [];
  let matchRows = [];
  try {
    [likedRows, reverseRows, matchRows] = await Promise.all([
      sbFetch('GET', 'likes', `from_user=eq.${activeId}&to_user=eq.${targetId}&select=id&limit=1`),
      sbFetch('GET', 'likes', `from_user=eq.${targetId}&to_user=eq.${activeId}&select=id&limit=1`),
      sbFetch('GET', 'matches', `or=(and(user1.eq.${activeId},user2.eq.${targetId}),and(user1.eq.${targetId},user2.eq.${activeId}))&select=id,user1,user2&limit=1`),
    ]);
  } catch (e) {}

  const liked = Array.isArray(likedRows) && likedRows.length > 0;
  const reverseLiked = Array.isArray(reverseRows) && reverseRows.length > 0;
  const matchRow = matchRowForPair(matchRows, activeId, targetId);
  const isRealMatched = !!matchRow || (liked && reverseLiked);
  const result = { liked, reverseLiked, matchRow, isRealMatched };
  if (options.log) {
    logAdiMatchDebug({
      activeProfileId: activeId,
      activeProfileName: options.activeProfileName || null,
      targetProfileId: targetId,
      targetProfileName: targetName || null,
      targetIsAdmin: options.targetIsAdmin,
      liked,
      reverseLiked,
      matchRow,
      isRealMatched,
    });
  }
  return result;
}

async function ensureReciprocalMatchRow(activeProfileId, targetProfileId, targetName, context = {}) {
  const activeId = profileIdKey(activeProfileId);
  const targetId = profileIdKey(targetProfileId);
  const state = await resolveProfileMatchState(activeId, targetId, targetName, context);
  if (!state.reverseLiked || state.matchRow || !state.liked) {
    logAdiMatchDebug({ activeProfileId: activeId, activeProfileName: context.activeProfileName || null, targetProfileId: targetId, targetProfileName: targetName || null, targetIsAdmin: context.targetIsAdmin, ...state });
    return state;
  }

  const { accessToken } = await getAuthTokenSafe({ forceFresh: true });
  await insertSupabaseRow(
    'matches',
    { user1: activeId, user2: targetId },
    { userId: activeId, accessToken, profileId: targetId, authUserId: activeId, sessionExists: !!accessToken, ...context },
    'matches insert'
  );
  const nextState = await resolveProfileMatchState(activeId, targetId, targetName, context);
  logAdiMatchDebug({ activeProfileId: activeId, activeProfileName: context.activeProfileName || null, targetProfileId: targetId, targetProfileName: targetName || null, targetIsAdmin: context.targetIsAdmin, ...nextState });
  return nextState;
}

async function persistLikeAndMatch(profileOrId, options = {}) {
  const { accessToken, userId: authUserId } = await getAuthTokenSafe({ forceFresh: true });
  const activeProfileId = await getActiveProfileId({ forceFresh: true });
  const targetProfile = typeof profileOrId === 'object' && profileOrId ? profileOrId : null;
  const targetProfileId = profileIdKey(targetProfile?.id || profileOrId);
  const targetProfileName = targetProfile?.name || null;
  const targetIsAdmin = !!targetProfile?.is_admin;
  const source = options.source || targetProfile?.__likeSource || 'unknown';
  let activeProfileName = null;
  let activeSocialProfile = null;
  let activeProfileDebug = null;
  let targetProfileDebug = null;
  try {
    const rows = activeProfileId ? await sbFetch('GET', 'profiles', `id=eq.${activeProfileId}&select=id,name,verified,verification_status,is_admin,is_moderator,verification_rejection_reason&limit=1`) : [];
    activeSocialProfile = Array.isArray(rows) && rows[0] ? rows[0] : null;
    activeProfileName = activeSocialProfile?.name || null;
    activeProfileDebug = getLikeProfileDebug(activeSocialProfile);
  } catch (e) {}
  activeProfileDebug = await loadLikeProfileDebug(activeProfileId) || activeProfileDebug;
  targetProfileDebug = await loadLikeProfileDebug(targetProfileId);
  logLikeTargetDebug(activeProfileId, targetProfileId, targetProfileName, source);
  logLikeSaveAttempt(activeProfileId, activeProfileName, targetProfileId, targetProfileName, source);
  const likeDebugContext = {
    authUserId,
    activeProfileId,
    activeProfileName,
    activeProfile: activeProfileDebug,
    targetProfileId,
    targetProfileName,
    targetProfile: targetProfileDebug,
    source,
  };
  const likePayload = { from_user: activeProfileId, to_user: targetProfileId, is_super_like: !!options.isSuperLike };
  logLikeSaveDiagnostic('before-save', { ...likeDebugContext, payload: likePayload });
  if (!activeProfileId || !accessToken || !targetProfileId || activeProfileId === targetProfileId) {
    const missingError = new Error('Cannot persist like without authenticated user and target profile, or self-like was blocked.');
    logLikeSaveDetailedError(missingError, likeDebugContext);
    logAdiMatchDebug({
      activeProfileId,
      activeProfileName,
      targetProfileId,
      targetProfileName,
      targetIsAdmin,
      liked: false,
      reverseLiked: false,
      matchRow: null,
      isRealMatched: false,
    });
    logLikeSaveError('missing auth or target', missingError, { userId: activeProfileId, accessToken, profileId: targetProfileId, authUserId });
    return { liked: false, matched: false };
  }
  if (!canUseSocialFeatures(activeSocialProfile)) {
    const verificationError = new Error('Profile verification is required before using social features.');
    verificationError.code = 'VERIFICATION_REQUIRED';
    logLikeSaveDetailedError(verificationError, likeDebugContext);
    throw verificationError;
  }
  if (await isProfilePairBlocked(activeProfileId, targetProfileId).catch(() => false)) {
    const blockedError = new Error('This profile is not available.');
    blockedError.code = 'PROFILE_BLOCKED';
    logLikeSaveDetailedError(blockedError, likeDebugContext);
    throw blockedError;
  }
  const context = { userId: activeProfileId, accessToken, profileId: targetProfileId, authUserId, sessionExists: !!(activeProfileId && accessToken), activeProfileName, targetProfileName, targetIsAdmin, source, activeProfile: activeProfileDebug, targetProfile: targetProfileDebug };

  let existingLike = [];
  try {
    existingLike = await sbFetch('GET', 'likes', `from_user=eq.${activeProfileId}&to_user=eq.${targetProfileId}&select=id&limit=1`);
  } catch (e) {
    logLikeSaveDiagnostic('existing-like-check-failed', { ...likeDebugContext, payload: likePayload, result: serializeLikeSaveError(e) });
    existingLike = [];
  }
  logLikeSaveDiagnostic('existing-like-check', { ...likeDebugContext, payload: likePayload, existingLikeCount: Array.isArray(existingLike) ? existingLike.length : null });
  if (!Array.isArray(existingLike) || existingLike.length === 0) {
    try {
      const insertResult = await insertSupabaseRow('likes', likePayload, context, 'likes insert');
      logLikeSaveDiagnostic('like-insert-result', { ...likeDebugContext, payload: likePayload, result: insertResult });
    } catch (err) {
      if (isDuplicateWriteError(err)) {
        existingLike = [{ duplicate: true }];
        logLikeSaveDiagnostic('like-duplicate-treated-as-success', { ...likeDebugContext, payload: likePayload, result: serializeLikeSaveError(err) });
      } else {
        logLikeSaveDetailedError(err, likeDebugContext);
        throw err;
      }
    }
  } else {
    logLikeSaveDiagnostic('like-already-exists-treated-as-success', { ...likeDebugContext, payload: likePayload, existingLikeCount: existingLike.length });
  }

  let matchState = { liked: true, reverseLiked: false, matchRow: null, isRealMatched: false };
  try {
    matchState = await ensureReciprocalMatchRow(activeProfileId, targetProfileId, targetProfileName, context);
  } catch (err) {
    logLikeSaveDetailedError(err, { ...likeDebugContext, source: `${source}:match-ensure-nonfatal`, payload: likePayload });
    logLikeSaveDiagnostic('match-ensure-failed-like-kept', { ...likeDebugContext, payload: likePayload, result: serializeLikeSaveError(err) });
    try {
      matchState = await resolveProfileMatchState(activeProfileId, targetProfileId, targetProfileName, context);
    } catch (stateErr) {
      logLikeSaveDiagnostic('post-match-failure-state-check-failed', { ...likeDebugContext, payload: likePayload, result: serializeLikeSaveError(stateErr) });
    }
  }
  logLikeSaveDiagnostic('like-save-complete', { ...likeDebugContext, payload: likePayload, reverseLikeCount: matchState.reverseLiked ? 1 : 0, matchExists: !!matchState.matchRow, result: { liked: true, matched: !!matchState.isRealMatched } });
  return { liked: true, matched: matchState.isRealMatched, activeProfileId, targetProfileId, matchState };
}

// Timezone-stable local calendar day as 'YYYY-MM-DD'. Avoids the UTC drift of
// toISOString() (which can store *yesterday* for +TZ users when spinning in the
// early local hours) and the UTC-midnight parse trap of new Date('YYYY-MM-DD').
// Used for the daily spin-lock on both write and read.
function localDay(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// A stored spin_last_date (DATE 'YYYY-MM-DD' or ISO timestamp) counts as "today"
// when its calendar-date prefix matches the local day. Since we always write
// localDay(), the stored prefix represents the local day regardless of column type.
function spunToday(value) {
  return !!value && String(value).slice(0, 10) === localDay();
}

async function patchProfile(fields) {
  const { userId, accessToken } = getAuthToken();
  if (!userId || !accessToken) { devLog('[patchProfile] no auth token, skipping', fields); return false; }
  try {
    await sbFetch('PATCH', 'profiles', `id=eq.${userId}`, fields, { Prefer: 'return=minimal' });
    return true;
  } catch (e) {
    console.error('[patchProfile] failed:', fields, e);
    return false;
  }
}

// Age in whole years from a 'YYYY-MM-DD' birth date, as of local now. Returns 0 for falsy input.
function calcAge(bd) {
  if (!bd) return 0;
  const d = new Date(bd);
  const n = new Date();
  let a = n.getFullYear() - d.getFullYear();
  const m = n.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < d.getDate())) a--;
  return a;
}

// ── Supabase mappers ──────────────────────────────────────────────────────────
const COUNTRY_FLAGS = { Spain:'🇪🇸', Japan:'🇯🇵', Nigeria:'🇳🇬', Brazil:'🇧🇷', France:'🇫🇷', Morocco:'🇲🇦', Germany:'🇩🇪', Bulgaria:'🇧🇬', 'United Kingdom':'🇬🇧', 'United States':'🇺🇸', Italy:'🇮🇹', Greece:'🇬🇷' };
const PROFILE_PUBLIC_SELECT = [
  'id','name','age','country','city','photo_url','online','travel_dna','dream_destinations','bio',
  'verified','verification_status','video_url','photos','video_intro','instagram','looking_for','lang',
  'gender','dna_title','bucket_list','map_public','custom_badge','highlighted_story','plan','is_admin',
  'is_moderator','height','body_type','smoking','alcohol','children','zodiac_sign','education','occupation',
  'preferred_smoking','preferred_alcohol','preferred_height_min','preferred_height_max','preferred_body_type',
  'relationship_goal','boost_until'
].join(',');

function mapSupabaseProfile(p, myCompat) {
  // Audit finding: compat used to be hardcoded to 75 for every real profile —
  // see the call site comment at the discovery-profile loader. When the
  // caller has the current user's compat-relevant fields (myCompat), compute
  // a real per-pair score; otherwise fall back to the old flat value rather
  // than crashing (e.g. mapSupabaseProfile called before login resolves).
  const realCompat = myCompat ? overallCompatibility(myCompat, p) : 75;
  return {
    id: p.id, name: p.name||'Traveler', age: p.age||null,
    flag: COUNTRY_FLAGS[p.country]||'🌍', country: p.country||'', city: p.city||'',
    photo: getProfilePhotoUrl(p.photo_url, null),
    online: p.online||false, traits: [...new Set((p.travel_dna||[]).map(s=>s.toLowerCase()))].slice(0,2),
    scores: [70, 65, 60, 55],
    compat: realCompat, dreams: p.dream_destinations||[], bio: p.bio||'',
    verified: canUseSocialFeatures(p), video_url: p.video_url||null,
    verification_status: getVerificationStatus(p),
    verification_selfie_url: p.verification_selfie_url || null,
    verification_front_url: p.verification_front_url || null,
    verification_submitted_at: p.verification_submitted_at || null,
    verification_rejection_reason: p.verification_rejection_reason || null,
    photos: p.photos || [], video_intro: p.video_intro || null,
    instagram: p.instagram||'', looking_for: p.looking_for||[], lang: p.lang||'',
    gender: p.gender || 'male',
    dna_title: p.dna_title || null,
    bucket_list: Array.isArray(p.bucket_list) ? p.bucket_list : [],
    map_public: p.map_public || false,
    custom_badge: p.custom_badge || null,
    highlighted_story: p.highlighted_story || null,
    // Audit fix (8th audit): boost_until>now() must outrank plan so an
    // active Boost actually surfaces a profile higher, matching the new
    // server-side ordering in get_visible_discovery_profiles() — otherwise
    // this client-side re-sort would silently undo that ordering.
    priority: (p.boost_until && new Date(p.boost_until) > new Date()) ? 0 : (p.plan === 'elite' ? 1 : 2),
    boost_until: p.boost_until || null,
    plan: p.plan || 'free',
    is_admin: p.is_admin || false,
    is_moderator: p.is_moderator || false,
    height: p.height || null,
    body_type: p.body_type || null,
    smoking: p.smoking || null,
    alcohol: p.alcohol || null,
    children: p.children || null,
    zodiac_sign: p.zodiac_sign || null,
    education: p.education || null,
    occupation: p.occupation || null,
    preferred_smoking: p.preferred_smoking || null,
    preferred_alcohol: p.preferred_alcohol || null,
    preferred_height_min: p.preferred_height_min || null,
    preferred_height_max: p.preferred_height_max || null,
    preferred_body_type: Array.isArray(p.preferred_body_type) ? p.preferred_body_type : null,
    relationship_goal: p.relationship_goal || null,
  };
}
const TRIP_GRADIENTS = ['linear-gradient(to top,#1a0533 0%,transparent 60%)','linear-gradient(to top,#001a0a 0%,transparent 60%)','linear-gradient(to top,#001533 0%,transparent 60%)','linear-gradient(to top,#1a0a00 0%,transparent 60%)','linear-gradient(to top,#000d1a 0%,transparent 60%)','linear-gradient(to top,#0a1a00 0%,transparent 60%)'];
const TRIP_FALLBACK_IMAGES = ['https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=700&q=80','https://images.unsplash.com/photo-1501854140801-50d01698950b?w=700&q=80','https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=700&q=80','https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=700&q=80','https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=700&q=80','https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=700&q=80'];
function mapSupabaseTrip(trip, idx) {
  const dateStr = trip.start_date ? new Date(trip.start_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : null;
  return { id:trip.id, _idx:idx, user_id:trip.user_id||null, name:trip.title||trip.destination||'—', location:trip.destination||'', image:TRIP_FALLBACK_IMAGES[idx%6], days:trip.days||7, spots:trip.spots??5, price:trip.budget?`€${trip.budget}`:'$999', hot:idx<2, gradient:TRIP_GRADIENTS[idx%6], start_date:trip.start_date||null, date:dateStr, members:trip.group_size||null, aiPlan:trip.plan||null };
}

// ── Static data ───────────────────────────────────────────────────────────────
const PROFILES = [
  { id:1, name:'Sofia',  age:28, flag:'🇪🇸', country:'Spain',   photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&q=80', online:true,  traits:['explorer','culturist'],  scores:[82,35,74,45], compat:87, dreams:['Patagonia','Morocco'] },
  { id:2, name:'Kenji',  age:31, flag:'🇯🇵', country:'Japan',   photo:'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400&q=80', online:true,  traits:['culturist','nomad'],     scores:[60,50,90,40], compat:79, dreams:['Rome','Istanbul','Prague'] },
  { id:3, name:'Amara',  age:26, flag:'🇳🇬', country:'Nigeria', photo:'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&q=80', online:false, traits:['adventurer','nomad'],    scores:[40,90,20,80], compat:71, dreams:['Iceland','Nepal','Peru'] },
  { id:4, name:'Lucas',  age:33, flag:'🇧🇷', country:'Brazil',  photo:'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&q=80', online:true,  traits:['nomad','adventurer'],   scores:[70,60,40,90], compat:83, dreams:['Vietnam','Jordan','Portugal'] },
  { id:5, name:'Léa',   age:29, flag:'🇫🇷', country:'France',  photo:'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=400&q=80', online:false, traits:['culturist','explorer'],  scores:[50,20,95,30], compat:76, dreams:['Santorini','Bali','Havana'] },
  { id:6, name:'Omar',   age:27, flag:'🇲🇦', country:'Morocco', photo:'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80', online:true,  traits:['explorer','nomad'],     scores:[90,70,50,85], compat:91, dreams:['Tokyo','New York','Cairo'] },
];


// eslint-disable-next-line no-unused-vars
const JOURNEYS = {
  upcoming:  [],
  planned:   [{ id:2, name:'Iceland Northern Lights', location:'Reykjavik, Iceland',   image:'https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=400&q=80', date:'Nov 12, 2026', members:5 }, { id:3, name:'Patagonia Trek', location:'Patagonia, Argentina', image:'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=400&q=80', date:'Jan 8, 2027', members:6 }],
  completed: [{ id:4, name:'Bali Spiritual Journey',  location:'Ubud, Bali',           image:'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=80', date:'Dec 3, 2025',  members:10 }],
};

// ── NEW: Stories data ─────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const STORIES = [
  { id:1, name:'Maria',  flag:'🇪🇸', photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=120&q=80', story:'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80' },
  { id:2, name:'Kenji',  flag:'🇯🇵', photo:'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=120&q=80', story:'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80' },
  { id:3, name:'Amara',  flag:'🇳🇬', photo:'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=120&q=80', story:'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80' },
  { id:4, name:'Lucas',  flag:'🇧🇷', photo:'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=120&q=80', story:'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=800&q=80' },
  { id:5, name:'Nina',   flag:'🇫🇷', photo:'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=120&q=80', story:'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80' },
];

// ── Trending Destinations data ────────────────────────────────────────────────
const TRENDING_DESTINATIONS = [
  { name: 'Santorini', nameBg: 'Санторини', country: 'Greece', countryBg: 'Гърция', tag: '🔥 Trending', tagBg: '🔥 Трендинг', desc: 'Iconic sunsets & white villages', descBg: 'Емблематични залези и бели села', visitors: '2.1M/year', bestTime: 'Apr–Oct', bestTimeBg: 'Апр–Окт', temp: '25°C', img: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=400&q=80', photos: ['https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80','https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&q=80','https://images.unsplash.com/photo-1555993539-1732b0258235?w=800&q=80'], dnaMatch: 'explorer' },
  { name: 'Kyoto', nameBg: 'Киото', country: 'Japan', countryBg: 'Япония', tag: '🌸 Seasonal', tagBg: '🌸 Сезонно', desc: 'Ancient temples & cherry blossoms', descBg: 'Древни храмове и черешови цветове', visitors: '3.4M/year', bestTime: 'Mar–May', bestTimeBg: 'Мар–Май', temp: '18°C', img: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=400&q=80', photos: ['https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80','https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80','https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=800&q=80'], dnaMatch: 'culturist' },
  { name: 'Bali', nameBg: 'Бали', country: 'Indonesia', countryBg: 'Индонезия', tag: '💫 Popular', tagBg: '💫 Популярно', desc: 'Soul & serenity in paradise', descBg: 'Душа и спокойствие в рая', visitors: '6.3M/year', bestTime: 'May–Sep', bestTimeBg: 'Май–Сеп', temp: '28°C', img: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=80', photos: ['https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80','https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=800&q=80','https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=800&q=80'], dnaMatch: 'nomad' },
  { name: 'Patagonia', nameBg: 'Патагония', country: 'Argentina', countryBg: 'Аржентина', tag: '🏔️ Adventure', tagBg: '🏔️ Приключение', desc: 'Wild & untouched wilderness', descBg: 'Дива и непокътната природа', visitors: '0.8M/year', bestTime: 'Nov–Mar', bestTimeBg: 'Ное–Мар', temp: '12°C', img: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=400&q=80', photos: ['https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80','https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80','https://images.unsplash.com/photo-1526772662000-3f88f10405ff?w=800&q=80'], dnaMatch: 'adventurer' },
  { name: 'Marrakech', nameBg: 'Маракеш', country: 'Morocco', countryBg: 'Мароко', tag: '🌟 Hidden gem', tagBg: '🌟 Скрит бисер', desc: 'Colors, spices & magic', descBg: 'Цветове, подправки и магия', visitors: '1.9M/year', bestTime: 'Mar–May', bestTimeBg: 'Мар–Май', temp: '22°C', img: 'https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=400&q=80', photos: ['https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=800&q=80','https://images.unsplash.com/photo-1572204097183-e1ab140342dc?w=800&q=80','https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=800&q=80'], dnaMatch: 'culturist' },
  { name: 'Iceland', nameBg: 'Исландия', country: 'Iceland', countryBg: 'Исландия', tag: '🌌 Bucket list', tagBg: '🌌 Задължително', desc: 'Northern lights & glaciers', descBg: 'Северно сияние и ледници', visitors: '2.3M/year', bestTime: 'Jun–Aug', bestTimeBg: 'Юни–Авг', temp: '10°C', img: 'https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=400&q=80', photos: ['https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=800&q=80','https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80','https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=800&q=80'], dnaMatch: 'explorer' },
  { name: 'Maldives', nameBg: 'Малдиви', country: 'Maldives', countryBg: 'Малдиви', tag: '🏝️ Paradise', tagBg: '🏝️ Рай', desc: 'Crystal waters & overwater villas', descBg: 'Кристални води и вили над водата', visitors: '1.7M/year', bestTime: 'Nov–Apr', bestTimeBg: 'Ное–Апр', temp: '30°C', img: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=400&q=80', photos: ['https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80','https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=80','https://images.unsplash.com/photo-1540202404-a2f29016b523?w=800&q=80'], dnaMatch: 'nomad' },
  { name: 'Lisbon', nameBg: 'Лисабон', country: 'Portugal', countryBg: 'Португалия', tag: '🎭 Vibrant', tagBg: '🎭 Колоритно', desc: 'Fado, hills & pastel buildings', descBg: 'Фадо, хълмове и пастелни сгради', visitors: '4.1M/year', bestTime: 'Mar–May', bestTimeBg: 'Мар–Май', temp: '20°C', img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80', photos: ['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80','https://images.unsplash.com/photo-1585208798174-6cedd4454619?w=800&q=80','https://images.unsplash.com/photo-1536992266094-82847e1fd431?w=800&q=80'], dnaMatch: 'culturist' },
  { name: 'Machu Picchu', nameBg: 'Мачу Пикчу', country: 'Peru', countryBg: 'Перу', tag: '🏔️ Iconic', tagBg: '🏔️ Емблематично', desc: 'Lost city of the Incas', descBg: 'Изгубеният град на инките', visitors: '1.5M/year', bestTime: 'May–Oct', bestTimeBg: 'Май–Окт', temp: '15°C', img: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=400&q=80', photos: ['https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=800&q=80','https://images.unsplash.com/photo-1526392060635-9d6019884377?w=800&q=80','https://images.unsplash.com/photo-1580502304784-8985b7eb7260?w=800&q=80'], dnaMatch: 'adventurer' },
  { name: 'Dubai', nameBg: 'Дубай', country: 'UAE', countryBg: 'ОАЕ', tag: '✨ Luxury', tagBg: '✨ Лукс', desc: 'Futuristic skyline & desert', descBg: 'Футуристичен хоризонт и пустиня', visitors: '14.4M/year', bestTime: 'Nov–Mar', bestTimeBg: 'Ное–Мар', temp: '25°C', img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=80', photos: ['https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80','https://images.unsplash.com/photo-1582672060674-bc2bd808a8b5?w=800&q=80','https://images.unsplash.com/photo-1518684079-3c830dcef090?w=800&q=80'], dnaMatch: 'explorer' },
  { name: 'Amalfi Coast', nameBg: 'Амалфи', country: 'Italy', countryBg: 'Италия', tag: '🌊 Romantic', tagBg: '🌊 Романтично', desc: 'Cliffside villages & azure sea', descBg: 'Крайбрежни села и лазурно море', visitors: '3.2M/year', bestTime: 'May–Oct', bestTimeBg: 'Май–Окт', temp: '26°C', img: 'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?w=400&q=80', photos: ['https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?w=800&q=80','https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=800&q=80','https://images.unsplash.com/photo-1612698093738-03e3e3a4b7b3?w=800&q=80'], dnaMatch: 'romantic' },
  { name: 'New Zealand', nameBg: 'Нова Зеландия', country: 'New Zealand', countryBg: 'Нова Зеландия', tag: '🌿 Epic', tagBg: '🌿 Епично', desc: 'Fjords, mountains & Hobbiton', descBg: 'Фиорди, планини и Хобитон', visitors: '3.9M/year', bestTime: 'Dec–Feb', bestTimeBg: 'Дек–Фев', temp: '18°C', img: 'https://images.unsplash.com/photo-1469521669194-babb45599def?w=400&q=80', photos: ['https://images.unsplash.com/photo-1469521669194-babb45599def?w=800&q=80','https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=800&q=80','https://images.unsplash.com/photo-1589802829985-817e51171b92?w=800&q=80'], dnaMatch: 'adventurer' },
  { name: 'Prague', nameBg: 'Прага', country: 'Czech Republic', countryBg: 'Чехия', tag: '🏰 Fairytale', tagBg: '🏰 Приказно', desc: 'Gothic spires & cobblestones', descBg: 'Готически кули и калдъръм', visitors: '8.2M/year', bestTime: 'Apr–Jun', bestTimeBg: 'Апр–Юни', temp: '16°C', img: 'https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=400&q=80', photos: ['https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&q=80','https://images.unsplash.com/photo-1541849546-216549ae216d?w=800&q=80','https://images.unsplash.com/photo-1592906209472-a36b1f3782ef?w=800&q=80'], dnaMatch: 'culturist' },
  { name: 'Thailand', nameBg: 'Тайланд', country: 'Thailand', countryBg: 'Тайланд', tag: '🙏 Spiritual', tagBg: '🙏 Духовно', desc: 'Temples, beaches & street food', descBg: 'Храмове, плажове и улична храна', visitors: '39M/year', bestTime: 'Nov–Apr', bestTimeBg: 'Ное–Апр', temp: '29°C', img: 'https://images.unsplash.com/photo-1506665531195-3566af2b4dfa?w=400&q=80', photos: ['https://images.unsplash.com/photo-1506665531195-3566af2b4dfa?w=800&q=80','https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&q=80','https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80'], dnaMatch: 'nomad' },
  { name: 'Tuscany', nameBg: 'Тоскана', country: 'Italy', countryBg: 'Италия', tag: '🍷 Dreamy', tagBg: '🍷 Мечтано', desc: 'Rolling hills, wine & art', descBg: 'Хълмове, вино и изкуство', visitors: '12M/year', bestTime: 'Apr–Jun', bestTimeBg: 'Апр–Юни', temp: '22°C', img: 'https://images.unsplash.com/photo-1534308143481-c55f00be8bd7?w=400&q=80', photos: ['https://images.unsplash.com/photo-1534308143481-c55f00be8bd7?w=800&q=80','https://images.unsplash.com/photo-1568797629192-789acf8e4df3?w=800&q=80','https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=800&q=80'], dnaMatch: 'romantic' },
  { name: 'Safari Kenya', nameBg: 'Сафари Кения', country: 'Kenya', countryBg: 'Кения', tag: '🦁 Wild', tagBg: '🦁 Диво', desc: 'Big Five & endless savanna', descBg: 'Голямата петорка и безкрайна савана', visitors: '2.0M/year', bestTime: 'Jul–Oct', bestTimeBg: 'Юли–Окт', temp: '24°C', img: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=400&q=80', photos: ['https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=800&q=80','https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=800&q=80','https://images.unsplash.com/photo-1551009175-15bdf9dcb580?w=800&q=80'], dnaMatch: 'adventurer' },
  { name: 'Havana', nameBg: 'Хавана', country: 'Cuba', countryBg: 'Куба', tag: '🎺 Exotic', tagBg: '🎺 Екзотично', desc: 'Vintage cars & salsa rhythms', descBg: 'Ретро коли и салса ритми', visitors: '1.2M/year', bestTime: 'Dec–Apr', bestTimeBg: 'Дек–Апр', temp: '26°C', img: 'https://images.unsplash.com/photo-1500759285222-a95626b934cb?w=400&q=80', photos: ['https://images.unsplash.com/photo-1500759285222-a95626b934cb?w=800&q=80','https://images.unsplash.com/photo-1523531294919-4bcd7c65e216?w=800&q=80','https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=800&q=80'], dnaMatch: 'explorer' },
  { name: 'Swiss Alps', nameBg: 'Швейцарски Алпи', country: 'Switzerland', countryBg: 'Швейцария', tag: '⛷️ Alpine', tagBg: '⛷️ Алпийско', desc: 'Pristine peaks & chocolate', descBg: 'Нетронати върхове и шоколад', visitors: '11M/year', bestTime: 'Dec–Mar', bestTimeBg: 'Дек–Мар', temp: '5°C', img: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=400&q=80', photos: ['https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80','https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80','https://images.unsplash.com/photo-1491555103944-7c647fd857e6?w=800&q=80'], dnaMatch: 'adventurer' },
  { name: 'Petra', nameBg: 'Петра', country: 'Jordan', countryBg: 'Йордания', tag: '🏛️ Ancient', tagBg: '🏛️ Древно', desc: 'Rose-red city carved in rock', descBg: 'Розово-червен град издялан в скала', visitors: '0.9M/year', bestTime: 'Mar–May', bestTimeBg: 'Мар–Май', temp: '20°C', img: 'https://images.unsplash.com/photo-1548786811-dd6e453ccca7?w=400&q=80', photos: ['https://images.unsplash.com/photo-1548786811-dd6e453ccca7?w=800&q=80','https://images.unsplash.com/photo-1579606032821-4d6a3015a4df?w=800&q=80','https://images.unsplash.com/photo-1544948503-7ad532427327?w=800&q=80'], dnaMatch: 'explorer' },
  { name: 'Amsterdam', nameBg: 'Амстердам', country: 'Netherlands', countryBg: 'Нидерландия', tag: '🚲 Free spirit', tagBg: '🚲 Свободен дух', desc: 'Canals, bikes & tulips', descBg: 'Канали, велосипеди и лалета', visitors: '20M/year', bestTime: 'Apr–May', bestTimeBg: 'Апр–Май', temp: '17°C', img: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=400&q=80', photos: ['https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&q=80','https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80','https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=800&q=80'], dnaMatch: 'culturist' },
];

// ── NEW: Who Viewed data ──────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const WHO_VIEWED = [
  { id:1, name:'Ana',  photo:'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80', locked:false },
  { id:2, name:'Max',  photo:'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80', locked:false },
  { id:3, name:'???',  photo:'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80', locked:true  },
  { id:4, name:'???',  photo:'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&q=80', locked:true  },
  { id:5, name:'???',  photo:'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=200&q=80', locked:true  },
];

// ── NEW: Leaderboard data ─────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const LEADERBOARD = [
  { rank:1,  name:'Marco',    flag:'🇮🇹', photo:'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80', trips:47, countries:32 },
  { rank:2,  name:'Yuki',     flag:'🇯🇵', photo:'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&q=80', trips:41, countries:28 },
  { rank:3,  name:'Isabella', flag:'🇧🇷', photo:'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100&q=80', trips:38, countries:25 },
  { rank:4,  name:'Amara',    flag:'🇳🇬', photo:'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100&q=80', trips:35, countries:22 },
  { rank:5,  name:'Sofia',    flag:'🇪🇸', photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=100&q=80', trips:33, countries:24 },
  { rank:6,  name:'Lucas',    flag:'🇧🇷', photo:'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&q=80', trips:30, countries:19 },
  { rank:7,  name:'Nina',     flag:'🇫🇷', photo:'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=100&q=80', trips:27, countries:17 },
  { rank:8,  name:'Kenji',    flag:'🇯🇵', photo:'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&q=80', trips:24, countries:15 },
  { rank:9,  name:'Ana',      flag:'🇲🇽', photo:'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&q=80', trips:21, countries:14 },
  { rank:10, name:'you',      flag:'🌍', photo:null, trips:3,  countries:2  },
];

// ── NEW: Conversations + Group Chat factory ────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const INITIAL_CONVOS = [
  { id:1, profile:PROFILES[0], messages:[
    { id:1, from:'them', text:"Hey! I saw you're also going to Patagonia! 🏔️", time:'14:22' },
    { id:2, from:'me',   text:'Yes! Super excited. Have you been to South America before?', time:'14:25' },
    { id:3, from:'them', text:'Once, just Argentina. Patagonia is on my bucket list!', time:'14:26' },
    { id:4, from:'me',   text:'Same here! We should explore together 🗺️',           time:'14:30' },
    { id:5, from:'them', text:'Definitely! ✨',                                       time:'14:31' },
  ]},
  { id:2, profile:PROFILES[3], messages:[
    { id:1, from:'them', text:"Your Travel DNA matches mine 91%! That's wild 😄", time:'10:05' },
    { id:2, from:'me',   text:'I know! Nomad + Adventurer combo is rare to find',  time:'10:08' },
    { id:3, from:'them', text:'We should do a trip to Vietnam together sometime!',  time:'10:09' },
  ]},
  { id:3, profile:PROFILES[1], messages:[
    { id:1, from:'them', text:'Do you know any hidden gems in Morocco beyond the tourist spots?', time:'Yesterday' },
    { id:2, from:'me',   text:'The blue streets of Chefchaouen are magical! And the Fes medina 🕌', time:'Yesterday' },
    { id:3, from:'them', text:'Adding both to the list. Shukran! 🙏',                               time:'Yesterday' },
  ]},
];

function makeGroupChat(trip) {
  return {
    id: `group-${trip.id}`, isGroup: true,
    tripName: trip.name, location: trip.location, image: trip.image,
    messages: [],
  };
}

// ── Translator (GPT-4o-mini via /api/translate, localStorage cache) ───────────
const TS_TRANS_CACHE = 'ts_translation_cache';
function _hashKey(text, lang) { let h=0; const s=lang+'|'+text; for(let i=0;i<s.length;i++){h=((h<<5)-h+s.charCodeAt(i))|0;} return 'k'+h; }
function _readTransCache() { try { return JSON.parse(localStorage.getItem(TS_TRANS_CACHE) || '{}'); } catch { return {}; } }
function _writeTransCache(obj) {
  try {
    const keys = Object.keys(obj);
    if (keys.length > 800) { keys.slice(0, Math.floor(keys.length/3)).forEach(k=>delete obj[k]); }
    localStorage.setItem(TS_TRANS_CACHE, JSON.stringify(obj));
  } catch {}
}
function getCachedTranslation(text, lang) { const c=_readTransCache(); return c[_hashKey(text,lang)] || null; }
async function translateText(text, targetLang) {
  if (!text || !targetLang) return text;
  const cached = getCachedTranslation(text, targetLang);
  if (cached) return cached;
  try {
    const { accessToken } = await getAuthTokenSafe();
    if (!accessToken) return text;
    const res = await fetch(`${API_BASE}/api/translate`, {
      method:'POST', headers:{'Content-Type':'application/json', 'Authorization': `Bearer ${accessToken}`},
      body: JSON.stringify({ text, targetLang }),
    });
    const data = await res.json();
    const translation = data?.translation || text;
    const c = _readTransCache(); c[_hashKey(text,targetLang)] = translation; _writeTransCache(c);
    return translation;
  } catch { return text; }
}

// ── Premium: Cities for Passport ─────────────────────────────────────────────
const CITIES = [
  { city:'Tokyo',      flag:'🇯🇵', photo:'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=80' },
  { city:'Paris',      flag:'🇫🇷', photo:'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&q=80' },
  { city:'New York',   flag:'🇺🇸', photo:'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&q=80' },
  { city:'Barcelona',  flag:'🇪🇸', photo:'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=400&q=80' },
  { city:'Bali',       flag:'🇮🇩', photo:'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=80' },
  { city:'Sydney',     flag:'🇦🇺', photo:'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=400&q=80' },
  { city:'Dubai',      flag:'🇦🇪', photo:'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&q=80' },
  { city:'Rio',        flag:'🇧🇷', photo:'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=400&q=80' },
  { city:'Istanbul',   flag:'🇹🇷', photo:'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=400&q=80' },
  { city:'Amsterdam',  flag:'🇳🇱', photo:'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=400&q=80' },
  { city:'Cape Town',  flag:'🇿🇦', photo:'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=400&q=80' },
  { city:'Marrakech',  flag:'🇲🇦', photo:'https://images.unsplash.com/photo-1539020140153-e479b8c22e79?w=400&q=80' },
];

// ── Premium: Travel Stats data ────────────────────────────────────────────────
const STATS_DATA = {
  countries:7, trips:12, km:48320, matches:34, superLikes:8, rank:47,
  footprint:['🇧🇬','🇯🇵','🇪🇸','🇫🇷','🇩🇪','🇬🇷','🇮🇹'],
  monthly:[2,1,3,2,0,1,2,3,1,0,2,1],
};
const MONTHS = {
  bg: ['Яну','Фев','Мар','Апр','Май','Юни','Юли','Авг','Сеп','Окт','Ное','Дек'],
  en: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
  es: ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'],
  fr: ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'],
  de: ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'],
  el: ['Ιαν','Φεβ','Μαρ','Απρ','Μάι','Ιουν','Ιουλ','Αυγ','Σεπ','Οκτ','Νοε','Δεκ'],
  it: ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'],
};

function monthLabels(lang) {
  return MONTHS[lang] || MONTHS.en;
}

// ── Premium: Pricing translations ────────────────────────────────────────────
// Paid plan prices require Daniel confirmation before production payments launch.
const PRICING_T = {
  bg: { title:'Избери план', premium:'Премиум', elite:'Елит', monthly:'Месечно', annual:'Годишно', save:'Спести 17%', mostPopular:'НАЙ-ПОПУЛЯРЕН', subscribe:'Виж план', back:'← Назад', perMonth:'/месец', perYear:'/год.', promo:'🎁 Начална цена',
    premiumFeatures:['Неограничени харесвания и съобщения','Виж кой те харесал','1 Boost на ден','Върни последния пропуск','Паспорт режим','🕵️ Инкогнито режим','Разширени филтри','Статистики за пътуване','Превод в реално време','🔮 Пътнически хороскоп','🗣️ Фрази за пътника','👁️ Кой те е видял','🎭 Премиум стикери','🤝 1 Пътнически пакт','🫙 Пътническа капсула (5 съобщения)'],
    premiumMissing:['Видео чат, Личен асистент'],
    eliteFeatures:['Всичко от Премиум','Видео чат','Елит 💎 значка','Организирай групови пътувания','ВИП Личен асистент','AI планиране на пътуване','Приоритетно показване 🎯','Последна минута спътник 🔔','Нашата история 💝','Верифицирана значка ✅ (само Елит)','✈️ AI пътен дневник','✨ AI оптимизатор на профил','🎨 Персонална значка','📌 Закачена история','🧬 Пътнически ДНК тест','🗺️ Карта на спомените','🪣 Bucket List с мачове','🎭 Mood Travel препоръки','🚪 Частни пътни стаи','🤖 Soul Match AI','💎 Елит анимирани стикери','🤝 Неограничени пактове + AI дестинация','🫙 Неограничена капсула'],
  },
  en: { title:'Choose Your Plan', monthly:'Monthly', annual:'Annual', save:'Save 17%', mostPopular:'MOST POPULAR', subscribe:'View plan', back:'← Back', perMonth:'/month', perYear:'/yr', promo:'🎁 Intro price',
    premiumFeatures:['Unlimited likes & messages','See who liked you','1 Boost per day','Rewind last pass','Passport mode','🕵️ Incognito mode','Advanced filters','Travel Stats','Real-time translation','🔮 Travel Horoscope','🗣️ Traveler Phrases','👁️ Who Viewed You','🎭 Premium stickers','🤝 1 Travel Pact','🫙 Travel Capsule (5 messages)'],
    premiumMissing:['Video chat, Concierge'],
    eliteFeatures:['Everything in Premium','Video chat','Elite 💎 badge','Organize group trips','VIP concierge','AI trip planner','Priority matching 🎯','Last minute companion 🔔','Our Story timeline 💝','Verified traveler badge ✅ (Elite only)','✈️ AI travel diary','✨ AI Profile Optimizer','🎨 Custom Travel Badge','📌 Pinned Story Highlight','🧬 Travel DNA Test','🗺️ Travel Memories Map','🪣 Bucket List Matching','🎭 Mood Travel Picks','🚪 Private Travel Rooms','🤖 Soul Match AI','💎 Elite animated stickers','🤝 Unlimited Pacts + AI destination','🫙 Unlimited Capsule'],
  },
  es: { title:'Elige tu Plan', monthly:'Mensual', annual:'Anual', save:'Ahorra 17%', mostPopular:'MÁS POPULAR', subscribe:'Ver plan', back:'← Volver', perMonth:'/mes', perYear:'/año', promo:'🎁 Precio introductorio',
    premiumFeatures:['Likes y mensajes ilimitados','Ver quién te gustó','1 Boost por día','Deshacer último pase','Modo pasaporte','🕵️ Modo incógnito','Filtros avanzados','Estadísticas de viaje','Traducción en tiempo real','🔮 Horóscopo de viaje','🗣️ Frases del viajero','👁️ Quién te vio','🎭 Stickers Premium','🤝 1 Pacto de Viaje','🫙 Cápsula de Viaje (5 mensajes)'],
    premiumMissing:['Video chat, Conserjería'],
    eliteFeatures:['Todo en Premium','Video chat','Insignia Elite 💎','Organiza viajes grupales','Conserje VIP','Planificador IA','Prioridad en búsqueda 🎯','Compañero de última hora 🔔','Nuestra historia 💝','Insignia verificado ✅ (solo Elite)','✈️ Diario de viaje IA','✨ Optimizador de perfil IA','🎨 Insignia personalizada','📌 Historia destacada','🧬 Test ADN de viaje','🗺️ Mapa de recuerdos','🪣 Lista de deseos con coincidencias','🎭 Recomendaciones por humor','🚪 Salas de viaje privadas','🤖 Soul Match IA','💎 Stickers Elite animados','🤝 Pactos ilimitados + destino IA','🫙 Cápsula ilimitada'],
  },
  fr: { title:'Choisir un Plan', monthly:'Mensuel', annual:'Annuel', save:'Économise 17%', mostPopular:'PLUS POPULAIRE', subscribe:'Voir le plan', back:'← Retour', perMonth:'/mois', perYear:'/an', promo:'🎁 Prix de lancement',
    premiumFeatures:['Likes et messages illimités','Voir qui vous a aimé','1 Boost par jour','Annuler dernier passe','Mode passeport','🕵️ Mode incognito','Filtres avancés','Statistiques de voyage','Traduction en temps réel','🔮 Horoscope de voyage','🗣️ Phrases du voyageur','👁️ Qui vous a vu','🎭 Stickers Premium','🤝 1 Pacte de Voyage','🫙 Capsule de Voyage (5 messages)'],
    premiumMissing:['Chat vidéo, Conciergerie'],
    eliteFeatures:['Tout en Premium','Chat vidéo','Badge Elite 💎','Organiser voyages groupés','Concierge VIP','Planificateur IA','Correspondances prioritaires 🎯','Compagnon de dernière minute 🔔','Notre histoire 💝','Badge vérifié ✅ (Elite uniquement)','✈️ Journal de voyage IA','✨ Optimiseur de profil IA','🎨 Badge personnalisé','📌 Histoire épinglée','🧬 Test ADN de voyage','🗺️ Carte des souvenirs','🪣 Liste de souhaits avec correspondances','🎭 Recommandations par humeur','🚪 Salles de voyage privées','🤖 Soul Match IA','💎 Stickers Elite animés','🤝 Pactes illimités + destination IA','🫙 Capsule illimitée'],
  },
  de: { title:'Plan wählen', monthly:'Monatlich', annual:'Jährlich', save:'17% sparen', mostPopular:'BELIEBTESTER', subscribe:'Plan ansehen', back:'← Zurück', perMonth:'/Monat', perYear:'/Jahr', promo:'🎁 Einführungspreis',
    premiumFeatures:['Unbegrenzte Likes & Nachrichten','Sieh wer dich gemocht hat','1 Boost pro Tag','Letzten Pass rückgängig','Reisepass-Modus','🕵️ Inkognito-Modus','Erweiterte Filter','Reise-Statistiken','Echtzeit-Übersetzung','🔮 Reisehoroskop','🗣️ Reisephrasen','👁️ Wer hat dich gesehen','🎭 Premium-Sticker','🤝 1 Reisepakt','🫙 Reisekapsel (5 Nachrichten)'],
    premiumMissing:['Video-Chat, Concierge'],
    eliteFeatures:['Alles aus Premium','Video-Chat','Elite 💎 Abzeichen','Gruppenreisen organisieren','VIP-Concierge','KI-Reiseplaner','Prioritäts-Matching 🎯','Spontan-Reisebegleiter 🔔','Unsere Geschichte 💝','Verifiziertes Abzeichen ✅ (nur Elite)','✈️ KI-Reisetagebuch','✨ KI-Profiloptimierung','🎨 Persönliches Abzeichen','📌 Angeheftete Geschichte','🧬 Reise-DNA-Test','🗺️ Erinnerungskarte','🪣 Wunschliste mit Matches','🎭 Stimmungsreise-Tipps','🚪 Private Reiseräume','🤖 Soul Match KI','💎 Elite animierte Sticker','🤝 Unbegrenzte Pakte + KI-Ziel','🫙 Unbegrenzte Kapsel'],
  },
};

PRICING_T.el = deepMerge(PRICING_T.en, {
  title:'Επίλεξε πλάνο', monthly:'Μηνιαίο', annual:'Ετήσιο', save:'Εξοικονόμηση 17%', mostPopular:'ΠΙΟ ΔΗΜΟΦΙΛΕΣ', subscribe:'Δες πλάνο', back:'← Πίσω', perMonth:'/μήνα', perYear:'/έτος', promo:'🎁 Τιμή γνωριμίας',
  premiumFeatures:['Απεριόριστα likes και μηνύματα','Δες ποιος σε έκανε like','1 Boost την ημέρα','Αναίρεση τελευταίας παράλειψης','Λειτουργία διαβατηρίου','🕵️ Incognito mode','Προηγμένα φίλτρα','Στατιστικά ταξιδιών','Μετάφραση σε πραγματικό χρόνο','🔮 Ταξιδιωτικό ωροσκόπιο','🗣️ Φράσεις ταξιδιώτη','👁️ Ποιος σε είδε','🎭 Premium stickers','🤝 1 Travel Pact','🫙 Travel Capsule (5 μηνύματα)'],
  premiumMissing:['Video chat, Concierge'],
  eliteFeatures:['Όλα του Premium','Video chat','Elite 💎 badge','Οργάνωση ομαδικών ταξιδιών','VIP concierge','AI planner ταξιδιού','Προτεραιότητα στα matches 🎯','Συνταξιδιώτης τελευταίας στιγμής 🔔','Η ιστορία μας 💝','Επαληθευμένο badge ✅ (μόνο Elite)','✈️ AI ταξιδιωτικό ημερολόγιο','✨ AI βελτιστοποιητής προφίλ','🎨 Προσωπικό travel badge','📌 Καρφιτσωμένη ιστορία','🧬 Travel DNA Test','🗺️ Χάρτης αναμνήσεων','🪣 Bucket List Matching','🎭 Mood Travel Picks','🚪 Ιδιωτικά ταξιδιωτικά δωμάτια','🤖 Soul Match AI','💎 Elite animated stickers','🤝 Απεριόριστα Pacts + AI προορισμός','🫙 Απεριόριστη Capsule'],
});

PRICING_T.it = deepMerge(PRICING_T.en, {
  title:'Scegli il tuo piano', monthly:'Mensile', annual:'Annuale', save:'Risparmia 17%', mostPopular:'PIÙ POPOLARE', subscribe:'Vedi piano', back:'← Indietro', perMonth:'/mese', perYear:'/anno', promo:'🎁 Prezzo lancio',
  premiumFeatures:['Like e messaggi illimitati','Vedi chi ti ha messo like','1 Boost al giorno','Annulla ultimo pass','Modalità passaporto','🕵️ Modalità incognito','Filtri avanzati','Statistiche di viaggio','Traduzione in tempo reale','🔮 Oroscopo di viaggio','🗣️ Frasi del viaggiatore','👁️ Chi ti ha visto','🎭 Sticker Premium','🤝 1 Patto di viaggio','🫙 Capsula di viaggio (5 messaggi)'],
  premiumMissing:['Video chat, Concierge'],
  eliteFeatures:['Tutto in Premium','Video chat','Badge Elite 💎','Organizza viaggi di gruppo','Concierge VIP','Planner viaggio AI','Priorità nei match 🎯','Compagno last minute 🔔','La nostra storia 💝','Badge verificato ✅ (solo Elite)','✈️ Diario di viaggio AI','✨ Ottimizzatore profilo AI','🎨 Badge personalizzato','📌 Storia in evidenza','🧬 Test DNA di viaggio','🗺️ Mappa dei ricordi','🪣 Bucket List Matching','🎭 Suggerimenti Mood Travel','🚪 Stanze di viaggio private','🤖 Soul Match AI','💎 Sticker Elite animati','🤝 Patti illimitati + destinazione AI','🫙 Capsula illimitata'],
});

// ── Premium: Default filters ──────────────────────────────────────────────────
const DEFAULT_FILTERS = { ageMin:18, ageMax:65, dnaTypes:[], compatMin:50, onlineOnly:false, verifiedOnly:false, distance:'worldwide', smoking:'any', alcohol:'any', children:'any', zodiac:[], goal:'', heightMin:150, heightMax:210, bodyType:[] };

function countActiveFilters(f) {
  let n = 0;
  if (f.ageMin>18||f.ageMax<65) n++;
  if (f.dnaTypes.length>0) n++;
  if (f.compatMin>50) n++;
  if (f.onlineOnly) n++;
  if (f.verifiedOnly) n++;
  if (f.distance!=='worldwide') n++;
  return n;
}

function applyFilters(profiles, f) {
  return profiles.filter(p => {
    if (p.age != null && (p.age<f.ageMin||p.age>f.ageMax)) return false;
    if (f.dnaTypes.length>0 && !p.traits.some(t=>f.dnaTypes.includes(t))) return false;
    if (p.compat<f.compatMin) return false;
    if (f.onlineOnly && !p.online) return false;
    if (f.verifiedOnly && !p.verified) return false;
    return true;
  });
}

// ── Premium: Like limit tracking ─────────────────────────────────────────────
const FREE_LIKE_LIMIT = 10;
function getLikesData() {
  return JSON.parse(localStorage.getItem('ts_likes_today') || '{"date":"","count":0}');
}
function trackLike() {
  const today = new Date().toDateString();
  const saved = getLikesData();
  const next = saved.date!==today ? {date:today,count:1} : {date:today,count:saved.count+1};
  localStorage.setItem('ts_likes_today', JSON.stringify(next));
  patchProfile({ likes_last_date: new Date().toISOString(), likes_today_count: next.count });
  return next;
}

// ── Feature translations (indexed by lang) ────────────────────────────────────
const SPIN_T = {
  bg: { dailySpin:'Завърти', spinResult:'Спечели!', comeBack:'Ела утре!', alreadySpun:'Вече завъртя днес', spinBtn:'ЗАВЪРТИ!', prize1Boost:'1 Буст 🔥', prize3SuperLikes:'3 Супер харесвания ⭐', prizeTryAgain:'Опитай пак 😅', prize1Rewind:'1 Върни ↩️', prizePremium:'Премиум 1ч 💎', prize5Likes:'5 Харесвания ❤️' },
  en: { dailySpin:'Daily Spin', spinResult:'You won!', comeBack:'Come back tomorrow!', alreadySpun:'Already spun today', spinBtn:'SPIN!', prize1Boost:'1 Boost 🔥', prize3SuperLikes:'3 Super Likes ⭐', prizeTryAgain:'Try Again 😅', prize1Rewind:'1 Rewind ↩️', prizePremium:'Premium 1hr 💎', prize5Likes:'5 Likes ❤️' },
  es: { dailySpin:'Giro diario', spinResult:'¡Ganaste!', comeBack:'¡Vuelve mañana!', alreadySpun:'Ya giraste hoy', spinBtn:'¡GIRAR!', prize1Boost:'1 Impulso 🔥', prize3SuperLikes:'3 Super Likes ⭐', prizeTryAgain:'Inténtalo 😅', prize1Rewind:'1 Rebobinar ↩️', prizePremium:'Premium 1h 💎', prize5Likes:'5 Likes ❤️' },
  fr: { dailySpin:'Tour quotidien', spinResult:'Vous avez gagné!', comeBack:'Revenez demain!', alreadySpun:"Déjà tourné aujourd'hui", spinBtn:'TOURNER!', prize1Boost:'1 Boost 🔥', prize3SuperLikes:'3 Super Likes ⭐', prizeTryAgain:'Réessayer 😅', prize1Rewind:'1 Retour ↩️', prizePremium:'Premium 1h 💎', prize5Likes:'5 Likes ❤️' },
  de: { dailySpin:'Tägliches Drehen', spinResult:'Gewonnen!', comeBack:'Komm morgen wieder!', alreadySpun:'Heute schon gedreht', spinBtn:'DREHEN!', prize1Boost:'1 Boost 🔥', prize3SuperLikes:'3 Super Likes ⭐', prizeTryAgain:'Nochmal 😅', prize1Rewind:'1 Zurück ↩️', prizePremium:'Premium 1Std 💎', prize5Likes:'5 Likes ❤️' },
  el: { dailySpin:'Ημερήσιος τροχός', spinResult:'Κέρδισες!', comeBack:'Έλα ξανά αύριο!', alreadySpun:'Το γύρισες ήδη σήμερα', spinBtn:'ΓΥΡΙΣΕ!', prize1Boost:'1 Boost 🔥', prize3SuperLikes:'3 Super Likes ⭐', prizeTryAgain:'Δοκίμασε ξανά 😅', prize1Rewind:'1 Rewind ↩️', prizePremium:'Premium 1 ώρα 💎', prize5Likes:'5 Likes ❤️' },
  it: { dailySpin:'Giro giornaliero', spinResult:'Hai vinto!', comeBack:'Torna domani!', alreadySpun:'Hai già girato oggi', spinBtn:'GIRA!', prize1Boost:'1 Boost 🔥', prize3SuperLikes:'3 Super Like ⭐', prizeTryAgain:'Riprova 😅', prize1Rewind:'1 Rewind ↩️', prizePremium:'Premium 1 ora 💎', prize5Likes:'5 Like ❤️' },
};

const COUNTRY_INFO = {
  'Малдиви': { weather:'Тропически', currency:'Малдивска руфия', language:'Дивехи', timezone:'UTC+5', currencyBg:'Малдивска руфия', bestTime:'Ное–Апр', visitors:'1.7M/год.' },
  'Maldives': { weather:'Tropical', currency:'MVR Rf', language:'Dhivehi', timezone:'UTC+5', currencyBg:'Малдивска руфия', bestTime:'Nov–Apr', visitors:'1.7M/yr' },
  'Мароко': { weather:'Средиземноморски/Пустинен', currency:'Мароканска дирхама', language:'Арабски/Берберски', timezone:'UTC+1', currencyBg:'Мароканска дирхама', bestTime:'Мар–Май', visitors:'13M/год.' },
  'Morocco': { weather:'Mediterranean/Desert', currency:'MAD Dh', language:'Arabic/Berber', timezone:'UTC+1', currencyBg:'Мароканска дирхама', bestTime:'Mar–May', visitors:'13M/yr' },
  'Маракеш': { weather:'Полупустинен', currency:'Мароканска дирхама', language:'Арабски', timezone:'UTC+1', currencyBg:'Мароканска дирхама', bestTime:'Мар–Май', visitors:'2M/год.' },
  'Marrakech': { weather:'Semi-desert', currency:'MAD Dh', language:'Arabic', timezone:'UTC+1', currencyBg:'Мароканска дирхама', bestTime:'Mar–May', visitors:'2M/yr' },
  'Амстердам': { weather:'Умерен морски', currency:'Евро €', language:'Нидерландски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Май', visitors:'20M/год.' },
  'Amsterdam': { weather:'Temperate oceanic', currency:'EUR €', language:'Dutch', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–May', visitors:'20M/yr' },
  'Япония': { weather:'Умерен', currency:'Японска йена ¥', language:'Японски', timezone:'UTC+9', currencyBg:'Японска йена ¥', bestTime:'Мар–Май', visitors:'25M/год.' },
  'Japan': { weather:'Temperate', currency:'JPY ¥', language:'Japanese', timezone:'UTC+9', currencyBg:'Японска йена ¥', bestTime:'Mar–May', visitors:'25M/yr' },
  'Киото': { weather:'Умерен', currency:'Японска йена ¥', language:'Японски', timezone:'UTC+9', currencyBg:'Японска йена ¥', bestTime:'Мар–Май', visitors:'3.4M/год.' },
  'Kyoto': { weather:'Temperate', currency:'JPY ¥', language:'Japanese', timezone:'UTC+9', currencyBg:'Японска йена ¥', bestTime:'Mar–May', visitors:'3.4M/yr' },
  'Гърция': { weather:'Средиземноморски', currency:'Евро €', language:'Гръцки', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Апр–Окт', visitors:'32M/год.' },
  'Greece': { weather:'Mediterranean', currency:'EUR €', language:'Greek', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Apr–Oct', visitors:'32M/yr' },
  'Санторини': { weather:'Средиземноморски', currency:'Евро €', language:'Гръцки', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Апр–Окт', visitors:'2.1M/год.' },
  'Santorini': { weather:'Mediterranean', currency:'EUR €', language:'Greek', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Apr–Oct', visitors:'2.1M/yr' },
  'Испания': { weather:'Средиземноморски', currency:'Евро €', language:'Испански', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Юни', visitors:'83M/год.' },
  'Spain': { weather:'Mediterranean', currency:'EUR €', language:'Spanish', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Jun', visitors:'83M/yr' },
  'Барселона': { weather:'Средиземноморски', currency:'Евро €', language:'Каталунски/Испански', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Май–Юни', visitors:'19M/год.' },
  'Barcelona': { weather:'Mediterranean', currency:'EUR €', language:'Catalan/Spanish', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'May–Jun', visitors:'19M/yr' },
  'Франция': { weather:'Умерен', currency:'Евро €', language:'Френски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Юни', visitors:'90M/год.' },
  'France': { weather:'Temperate', currency:'EUR €', language:'French', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Jun', visitors:'90M/yr' },
  'Париж': { weather:'Умерен', currency:'Евро €', language:'Френски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Юни', visitors:'44M/год.' },
  'Paris': { weather:'Temperate', currency:'EUR €', language:'French', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Jun', visitors:'44M/yr' },
  'Италия': { weather:'Средиземноморски', currency:'Евро €', language:'Италиански', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Юни', visitors:'65M/год.' },
  'Italy': { weather:'Mediterranean', currency:'EUR €', language:'Italian', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Jun', visitors:'65M/yr' },
  'Рим': { weather:'Средиземноморски', currency:'Евро €', language:'Италиански', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Юни', visitors:'14M/год.' },
  'Rome': { weather:'Mediterranean', currency:'EUR €', language:'Italian', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Jun', visitors:'14M/yr' },
  'Тоскана': { weather:'Средиземноморски', currency:'Евро €', language:'Италиански', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Юни', visitors:'12M/год.' },
  'Tuscany': { weather:'Mediterranean', currency:'EUR €', language:'Italian', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Jun', visitors:'12M/yr' },
  'Германия': { weather:'Умерен', currency:'Евро €', language:'Немски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Май–Сеп', visitors:'40M/год.' },
  'Germany': { weather:'Temperate', currency:'EUR €', language:'German', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'May–Sep', visitors:'40M/yr' },
  'България': { weather:'Умерен континентален', currency:'Euro €', language:'Български', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Юни–Авг', visitors:'9M/год.' },
  'Bulgaria': { weather:'Temperate continental', currency:'Euro €', language:'Bulgarian', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Jun–Aug', visitors:'9M/yr' },
  'Турция': { weather:'Средиземноморски', currency:'Турска лира', language:'Турски', timezone:'UTC+3', currencyBg:'Турска лира', bestTime:'Апр–Юни', visitors:'51M/год.' },
  'Turkey': { weather:'Mediterranean', currency:'TRY ₺', language:'Turkish', timezone:'UTC+3', currencyBg:'Турска лира', bestTime:'Apr–Jun', visitors:'51M/yr' },
  'Египет': { weather:'Пустинен', currency:'Египетска лира', language:'Арабски', timezone:'UTC+2', currencyBg:'Египетска лира', bestTime:'Окт–Апр', visitors:'15M/год.' },
  'Egypt': { weather:'Desert', currency:'EGP £', language:'Arabic', timezone:'UTC+2', currencyBg:'Египетска лира', bestTime:'Oct–Apr', visitors:'15M/yr' },
  'Тайланд': { weather:'Тропически', currency:'Тайландски бат', language:'Тайски', timezone:'UTC+7', currencyBg:'Тайландски бат', bestTime:'Ное–Апр', visitors:'39M/год.' },
  'Thailand': { weather:'Tropical', currency:'THB ฿', language:'Thai', timezone:'UTC+7', currencyBg:'Тайландски бат', bestTime:'Nov–Apr', visitors:'39M/yr' },
  'Бали': { weather:'Тропически', currency:'Индонезийска рупия', language:'Балийски', timezone:'UTC+8', currencyBg:'Индонезийска рупия', bestTime:'Май–Сеп', visitors:'6.3M/год.' },
  'Bali': { weather:'Tropical', currency:'IDR Rp', language:'Balinese', timezone:'UTC+8', currencyBg:'Индонезийска рупия', bestTime:'May–Sep', visitors:'6.3M/yr' },
  'Индия': { weather:'Тропически/Умерен', currency:'Индийска рупия', language:'Хинди/Английски', timezone:'UTC+5:30', currencyBg:'Индийска рупия', bestTime:'Окт–Мар', visitors:'18M/год.' },
  'India': { weather:'Tropical/Temperate', currency:'INR ₹', language:'Hindi/English', timezone:'UTC+5:30', currencyBg:'Индийска рупия', bestTime:'Oct–Mar', visitors:'18M/yr' },
  'Китай': { weather:'Умерен/Тропически', currency:'Китайски юан', language:'Китайски', timezone:'UTC+8', currencyBg:'Китайски юан', bestTime:'Апр–Юни', visitors:'65M/год.' },
  'China': { weather:'Temperate/Tropical', currency:'CNY ¥', language:'Mandarin', timezone:'UTC+8', currencyBg:'Китайски юан', bestTime:'Apr–Jun', visitors:'65M/yr' },
  'Австралия': { weather:'Умерен/Тропически', currency:'Австралийски долар', language:'Английски', timezone:'UTC+10', currencyBg:'Австралийски долар', bestTime:'Сеп–Ное', visitors:'9M/год.' },
  'Australia': { weather:'Temperate/Tropical', currency:'AUD $', language:'English', timezone:'UTC+10', currencyBg:'Австралийски долар', bestTime:'Sep–Nov', visitors:'9M/yr' },
  'САЩ': { weather:'Умерен', currency:'Щатски долар', language:'Английски', timezone:'UTC-5 до UTC-8', currencyBg:'Щатски долар', bestTime:'Май–Сеп', visitors:'79M/год.' },
  'USA': { weather:'Temperate', currency:'USD $', language:'English', timezone:'UTC-5 to UTC-8', currencyBg:'Щатски долар', bestTime:'May–Sep', visitors:'79M/yr' },
  'Великобритания': { weather:'Умерен морски', currency:'Британски паунд', language:'Английски', timezone:'UTC+0', currencyBg:'Британски паунд', bestTime:'Май–Сеп', visitors:'40M/год.' },
  'UK': { weather:'Temperate oceanic', currency:'GBP £', language:'English', timezone:'UTC+0', currencyBg:'Британски паунд', bestTime:'May–Sep', visitors:'40M/yr' },
  'Лондон': { weather:'Умерен морски', currency:'Британски паунд', language:'Английски', timezone:'UTC+0', currencyBg:'Британски паунд', bestTime:'Май–Сеп', visitors:'21M/год.' },
  'London': { weather:'Temperate oceanic', currency:'GBP £', language:'English', timezone:'UTC+0', currencyBg:'Британски паунд', bestTime:'May–Sep', visitors:'21M/yr' },
  'Португалия': { weather:'Средиземноморски', currency:'Евро €', language:'Португалски', timezone:'UTC+0', currencyBg:'Евро €', bestTime:'Мар–Май', visitors:'27M/год.' },
  'Portugal': { weather:'Mediterranean', currency:'EUR €', language:'Portuguese', timezone:'UTC+0', currencyBg:'Евро €', bestTime:'Mar–May', visitors:'27M/yr' },
  'Лисабон': { weather:'Средиземноморски', currency:'Евро €', language:'Португалски', timezone:'UTC+0', currencyBg:'Евро €', bestTime:'Мар–Май', visitors:'4.1M/год.' },
  'Lisbon': { weather:'Mediterranean', currency:'EUR €', language:'Portuguese', timezone:'UTC+0', currencyBg:'Евро €', bestTime:'Mar–May', visitors:'4.1M/yr' },
  'Перу': { weather:'Тропически/Планински', currency:'Перуански сол', language:'Испански', timezone:'UTC-5', currencyBg:'Перуански сол', bestTime:'Май–Окт', visitors:'4M/год.' },
  'Peru': { weather:'Tropical/Highland', currency:'PEN S/', language:'Spanish', timezone:'UTC-5', currencyBg:'Перуански сол', bestTime:'May–Oct', visitors:'4M/yr' },
  'Аржентина': { weather:'Умерен/Субполярен', currency:'Аржентинско песо', language:'Испански', timezone:'UTC-3', currencyBg:'Аржентинско песо', bestTime:'Ное–Мар', visitors:'7M/год.' },
  'Argentina': { weather:'Temperate/Subpolar', currency:'ARS $', language:'Spanish', timezone:'UTC-3', currencyBg:'Аржентинско песо', bestTime:'Nov–Mar', visitors:'7M/yr' },
  'Куба': { weather:'Тропически', currency:'Кубински песо', language:'Испански', timezone:'UTC-5', currencyBg:'Кубински песо', bestTime:'Дек–Апр', visitors:'4M/год.' },
  'Cuba': { weather:'Tropical', currency:'CUP $', language:'Spanish', timezone:'UTC-5', currencyBg:'Кубински песо', bestTime:'Dec–Apr', visitors:'4M/yr' },
  'Швейцария': { weather:'Алпийски/Умерен', currency:'Швейцарски франк', language:'Немски/Френски', timezone:'UTC+1', currencyBg:'Швейцарски франк', bestTime:'Юни–Авг', visitors:'11M/год.' },
  'Switzerland': { weather:'Alpine/Temperate', currency:'CHF Fr', language:'German/French', timezone:'UTC+1', currencyBg:'Швейцарски франк', bestTime:'Jun–Aug', visitors:'11M/yr' },
  'Йордания': { weather:'Полупустинен', currency:'Йордански динар', language:'Арабски', timezone:'UTC+3', currencyBg:'Йордански динар', bestTime:'Мар–Май', visitors:'3M/год.' },
  'Jordan': { weather:'Semi-desert', currency:'JOD JD', language:'Arabic', timezone:'UTC+3', currencyBg:'Йордански динар', bestTime:'Mar–May', visitors:'3M/yr' },
  'Нидерландия': { weather:'Умерен морски', currency:'Евро €', language:'Нидерландски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Май', visitors:'20M/год.' },
  'Netherlands': { weather:'Temperate oceanic', currency:'EUR €', language:'Dutch', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–May', visitors:'20M/yr' },
  'Индонезия': { weather:'Тропически', currency:'Индонезийска рупия', language:'Индонезийски', timezone:'UTC+7', currencyBg:'Индонезийска рупия', bestTime:'Май–Сеп', visitors:'16M/год.' },
  'Indonesia': { weather:'Tropical', currency:'IDR Rp', language:'Indonesian', timezone:'UTC+7', currencyBg:'Индонезийска рупия', bestTime:'May–Sep', visitors:'16M/yr' },
  'Кения': { weather:'Тропически/Савана', currency:'Кенийски шилинг', language:'Суахили/Английски', timezone:'UTC+3', currencyBg:'Кенийски шилинг', bestTime:'Юли–Окт', visitors:'2M/год.' },
  'Kenya': { weather:'Tropical/Savanna', currency:'KES Sh', language:'Swahili/English', timezone:'UTC+3', currencyBg:'Кенийски шилинг', bestTime:'Jul–Oct', visitors:'2M/yr' },
  'ОАЕ': { weather:'Пустинен', currency:'Дирхама ОАЕ', language:'Арабски', timezone:'UTC+4', currencyBg:'Дирхама ОАЕ', bestTime:'Ное–Мар', visitors:'17M/год.' },
  'UAE': { weather:'Desert', currency:'AED د.إ', language:'Arabic', timezone:'UTC+4', currencyBg:'Дирхама ОАЕ', bestTime:'Nov–Mar', visitors:'17M/yr' },
  'Дубай': { weather:'Пустинен', currency:'Дирхама ОАЕ', language:'Арабски', timezone:'UTC+4', currencyBg:'Дирхама ОАЕ', bestTime:'Ное–Мар', visitors:'14M/год.' },
  'Dubai': { weather:'Desert', currency:'AED د.إ', language:'Arabic', timezone:'UTC+4', currencyBg:'Дирхама ОАЕ', bestTime:'Nov–Mar', visitors:'14M/yr' },
  'Нова Зеландия': { weather:'Умерен океански', currency:'Новозеландски долар', language:'Английски/Маори', timezone:'UTC+12', currencyBg:'Новозеландски долар', bestTime:'Дек–Фев', visitors:'3.9M/год.' },
  'New Zealand': { weather:'Temperate oceanic', currency:'NZD $', language:'English/Maori', timezone:'UTC+12', currencyBg:'Новозеландски долар', bestTime:'Dec–Feb', visitors:'3.9M/yr' },
  'Чехия': { weather:'Умерен континентален', currency:'Чешка крона', language:'Чешки', timezone:'UTC+1', currencyBg:'Чешка крона', bestTime:'Апр–Юни', visitors:'8M/год.' },
  'Czech Republic': { weather:'Temperate continental', currency:'CZK Kč', language:'Czech', timezone:'UTC+1', currencyBg:'Чешка крона', bestTime:'Apr–Jun', visitors:'8M/yr' },
  'Прага': { weather:'Умерен континентален', currency:'Чешка крона', language:'Чешки', timezone:'UTC+1', currencyBg:'Чешка крона', bestTime:'Апр–Юни', visitors:'8.2M/год.' },
  'Prague': { weather:'Temperate continental', currency:'CZK Kč', language:'Czech', timezone:'UTC+1', currencyBg:'Чешка крона', bestTime:'Apr–Jun', visitors:'8.2M/yr' },
  'Исландия': { weather:'Субарктически', currency:'Исландска крона', language:'Исландски', timezone:'UTC+0', currencyBg:'Исландска крона', bestTime:'Юни–Авг', visitors:'2.3M/год.' },
  'Iceland': { weather:'Subarctic', currency:'ISK kr', language:'Icelandic', timezone:'UTC+0', currencyBg:'Исландска крона', bestTime:'Jun–Aug', visitors:'2.3M/yr' },
  'Сингапур': { weather:'Тропически', currency:'Сингапурски долар', language:'Английски/Малайски', timezone:'UTC+8', currencyBg:'Сингапурски долар', bestTime:'Фев–Апр', visitors:'19M/год.' },
  'Singapore': { weather:'Tropical', currency:'SGD $', language:'English/Malay', timezone:'UTC+8', currencyBg:'Сингапурски долар', bestTime:'Feb–Apr', visitors:'19M/yr' },
  'Виетнам': { weather:'Тропически', currency:'Виетнамски донг', language:'Виетнамски', timezone:'UTC+7', currencyBg:'Виетнамски донг', bestTime:'Фев–Апр', visitors:'18M/год.' },
  'Vietnam': { weather:'Tropical', currency:'VND ₫', language:'Vietnamese', timezone:'UTC+7', currencyBg:'Виетнамски донг', bestTime:'Feb–Apr', visitors:'18M/yr' },
  'Норвегия': { weather:'Субарктически/Умерен', currency:'Норвежка крона', language:'Норвежки', timezone:'UTC+1', currencyBg:'Норвежка крона', bestTime:'Юни–Авг', visitors:'5M/год.' },
  'Norway': { weather:'Subarctic/Temperate', currency:'NOK kr', language:'Norwegian', timezone:'UTC+1', currencyBg:'Норвежка крона', bestTime:'Jun–Aug', visitors:'5M/yr' },
  'Хърватия': { weather:'Средиземноморски', currency:'Евро €', language:'Хърватски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Юни–Авг', visitors:'21M/год.' },
  'Croatia': { weather:'Mediterranean', currency:'EUR €', language:'Croatian', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Jun–Aug', visitors:'21M/yr' },
  'Румъния': { weather:'Умерен континентален', currency:'Румънска лея', language:'Румънски', timezone:'UTC+2', currencyBg:'Румънска лея', bestTime:'Май–Сеп', visitors:'9M/год.' },
  'Romania': { weather:'Temperate continental', currency:'RON lei', language:'Romanian', timezone:'UTC+2', currencyBg:'Румънска лея', bestTime:'May–Sep', visitors:'9M/yr' },
  'Унгария': { weather:'Умерен континентален', currency:'Унгарски форинт', language:'Унгарски', timezone:'UTC+1', currencyBg:'Унгарски форинт', bestTime:'Апр–Юни', visitors:'16M/год.' },
  'Hungary': { weather:'Temperate continental', currency:'HUF Ft', language:'Hungarian', timezone:'UTC+1', currencyBg:'Унгарски форинт', bestTime:'Apr–Jun', visitors:'16M/yr' },
  'Австрия': { weather:'Алпийски/Умерен', currency:'Евро €', language:'Немски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Юни–Авг', visitors:'31M/год.' },
  'Austria': { weather:'Alpine/Temperate', currency:'EUR €', language:'German', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Jun–Aug', visitors:'31M/yr' },
  'Мексико': { weather:'Тропически/Умерен', currency:'Мексикански песо', language:'Испански', timezone:'UTC-6', currencyBg:'Мексикански песо', bestTime:'Дек–Апр', visitors:'45M/год.' },
  'Mexico': { weather:'Tropical/Temperate', currency:'MXN $', language:'Spanish', timezone:'UTC-6', currencyBg:'Мексикански песо', bestTime:'Dec–Apr', visitors:'45M/yr' },
  'Бразилия': { weather:'Тропически', currency:'Бразилски реал', language:'Португалски', timezone:'UTC-3', currencyBg:'Бразилски реал', bestTime:'Май–Сеп', visitors:'6M/год.' },
  'Brazil': { weather:'Tropical', currency:'BRL R$', language:'Portuguese', timezone:'UTC-3', currencyBg:'Бразилски реал', bestTime:'May–Sep', visitors:'6M/yr' },
  'Южна Африка': { weather:'Умерен/Субтропически', currency:'Южноафрикански ранд', language:'Зулу/Английски', timezone:'UTC+2', currencyBg:'Южноафрикански ранд', bestTime:'Май–Сеп', visitors:'10M/год.' },
  'South Africa': { weather:'Temperate/Subtropical', currency:'ZAR R', language:'Zulu/English', timezone:'UTC+2', currencyBg:'Южноафрикански ранд', bestTime:'May–Sep', visitors:'10M/yr' },
  'Непал': { weather:'Планински', currency:'Непалска рупия', language:'Непалски', timezone:'UTC+5:45', currencyBg:'Непалска рупия', bestTime:'Окт–Ное', visitors:'1M/год.' },
  'Nepal': { weather:'Mountain', currency:'NPR Rs', language:'Nepali', timezone:'UTC+5:45', currencyBg:'Непалска рупия', bestTime:'Oct–Nov', visitors:'1M/yr' },
  'Шри Ланка': { weather:'Тропически', currency:'Шриланкийска рупия', language:'Синхалски/Тамилски', timezone:'UTC+5:30', currencyBg:'Шриланкийска рупия', bestTime:'Дек–Мар', visitors:'2M/год.' },
  'Sri Lanka': { weather:'Tropical', currency:'LKR Rs', language:'Sinhala/Tamil', timezone:'UTC+5:30', currencyBg:'Шриланкийска рупия', bestTime:'Dec–Mar', visitors:'2M/yr' },
  'Иран': { weather:'Пустинен/Умерен', currency:'Ирански риал', language:'Персийски', timezone:'UTC+3:30', currencyBg:'Ирански риал', bestTime:'Мар–Май', visitors:'9M/год.' },
  'Iran': { weather:'Desert/Temperate', currency:'IRR ﷼', language:'Persian (Farsi)', timezone:'UTC+3:30', currencyBg:'Ирански риал', bestTime:'Mar–May', visitors:'9M/yr' },
  'Техеран': { weather:'Полупустинен', currency:'Ирански риал', language:'Персийски', timezone:'UTC+3:30', currencyBg:'Ирански риал', bestTime:'Мар–Май', visitors:'3M/год.' },
  'Tehran': { weather:'Semi-desert', currency:'IRR ﷼', language:'Persian (Farsi)', timezone:'UTC+3:30', currencyBg:'Ирански риал', bestTime:'Mar–May', visitors:'3M/yr' },
  'Исфахан': { weather:'Полупустинен', currency:'Ирански риал', language:'Персийски', timezone:'UTC+3:30', currencyBg:'Иранскиريал', bestTime:'Мар–Май', visitors:'2M/год.' },
  'Isfahan': { weather:'Semi-desert', currency:'IRR ﷼', language:'Persian (Farsi)', timezone:'UTC+3:30', currencyBg:'Ирански риал', bestTime:'Mar–May', visitors:'2M/yr' },
  'Русия': { weather:'Умерен/Субарктически', currency:'Руска рубла', language:'Руски', timezone:'UTC+3 до UTC+12', currencyBg:'Руска рубла', bestTime:'Май–Сеп', visitors:'24M/год.' },
  'Russia': { weather:'Temperate/Subarctic', currency:'RUB ₽', language:'Russian', timezone:'UTC+3 to UTC+12', currencyBg:'Руска рубла', bestTime:'May–Sep', visitors:'24M/yr' },
  'Москва': { weather:'Умерен континентален', currency:'Руска рубла', language:'Руски', timezone:'UTC+3', currencyBg:'Руска рубла', bestTime:'Май–Сеп', visitors:'12M/год.' },
  'Moscow': { weather:'Temperate continental', currency:'RUB ₽', language:'Russian', timezone:'UTC+3', currencyBg:'Руска рубла', bestTime:'May–Sep', visitors:'12M/yr' },
  'Саудитска Арабия': { weather:'Пустинен', currency:'Саудитски риал', language:'Арабски', timezone:'UTC+3', currencyBg:'Саудитски риал', bestTime:'Ное–Мар', visitors:'17M/год.' },
  'Saudi Arabia': { weather:'Desert', currency:'SAR ﷼', language:'Arabic', timezone:'UTC+3', currencyBg:'Саудитски риал', bestTime:'Nov–Mar', visitors:'17M/yr' },
  'Израел': { weather:'Средиземноморски/Пустинен', currency:'Израелски шекел', language:'Иврит/Арабски', timezone:'UTC+2', currencyBg:'Израелски шекел', bestTime:'Мар–Май', visitors:'4M/год.' },
  'Israel': { weather:'Mediterranean/Desert', currency:'ILS ₪', language:'Hebrew/Arabic', timezone:'UTC+2', currencyBg:'Израелски шекел', bestTime:'Mar–May', visitors:'4M/yr' },
  'Израел/Тел Авив': { weather:'Средиземноморски', currency:'Израелски шекел', language:'Иврит', timezone:'UTC+2', currencyBg:'Израелски шекел', bestTime:'Апр–Юни', visitors:'3M/год.' },
  'Tel Aviv': { weather:'Mediterranean', currency:'ILS ₪', language:'Hebrew', timezone:'UTC+2', currencyBg:'Израелски шекел', bestTime:'Apr–Jun', visitors:'3M/yr' },
  'Грузия': { weather:'Умерен/Планински', currency:'Грузински лари', language:'Грузински', timezone:'UTC+4', currencyBg:'Грузински лари', bestTime:'Май–Окт', visitors:'7M/год.' },
  'Georgia': { weather:'Temperate/Mountain', currency:'GEL ₾', language:'Georgian', timezone:'UTC+4', currencyBg:'Грузински лари', bestTime:'May–Oct', visitors:'7M/yr' },
  'Тбилиси': { weather:'Умерен', currency:'Грузински лари', language:'Грузински', timezone:'UTC+4', currencyBg:'Грузински лари', bestTime:'Май–Окт', visitors:'2M/год.' },
  'Tbilisi': { weather:'Temperate', currency:'GEL ₾', language:'Georgian', timezone:'UTC+4', currencyBg:'Грузински лари', bestTime:'May–Oct', visitors:'2M/yr' },
  'Армения': { weather:'Умерен континентален', currency:'Арменски драм', language:'Арменски', timezone:'UTC+4', currencyBg:'Арменски драм', bestTime:'Май–Окт', visitors:'1.7M/год.' },
  'Armenia': { weather:'Temperate continental', currency:'AMD ֏', language:'Armenian', timezone:'UTC+4', currencyBg:'Арменски драм', bestTime:'May–Oct', visitors:'1.7M/yr' },
  'Казахстан': { weather:'Умерен/Студен', currency:'Казахстански тенге', language:'Казахски/Руски', timezone:'UTC+5/UTC+6', currencyBg:'Казахстански тенге', bestTime:'Май–Сеп', visitors:'8M/год.' },
  'Kazakhstan': { weather:'Temperate/Cold', currency:'KZT ₸', language:'Kazakh/Russian', timezone:'UTC+5/UTC+6', currencyBg:'Казахстански тенге', bestTime:'May–Sep', visitors:'8M/yr' },
  'Азербайджан': { weather:'Умерен', currency:'Азербайджански манат', language:'Азербайджански', timezone:'UTC+4', currencyBg:'Азербайджански манат', bestTime:'Апр–Юни', visitors:'3M/год.' },
  'Azerbaijan': { weather:'Temperate', currency:'AZN ₼', language:'Azerbaijani', timezone:'UTC+4', currencyBg:'Азербайджански манат', bestTime:'Apr–Jun', visitors:'3M/yr' },
  'Пакистан': { weather:'Тропически/Аридни', currency:'Пакистански рупи', language:'Урду/Английски', timezone:'UTC+5', currencyBg:'Пакистански рупи', bestTime:'Окт–Мар', visitors:'2M/год.' },
  'Pakistan': { weather:'Tropical/Arid', currency:'PKR ₨', language:'Urdu/English', timezone:'UTC+5', currencyBg:'Пакистански рупи', bestTime:'Oct–Mar', visitors:'2M/yr' },
  'Афганистан': { weather:'Аридни/Планински', currency:'Афгански афгани', language:'Пащу/Дари', timezone:'UTC+4:30', currencyBg:'Афгански афгани', bestTime:'Мар–Май', visitors:'0.1M/год.' },
  'Afghanistan': { weather:'Arid/Mountain', currency:'AFN ؋', language:'Pashto/Dari', timezone:'UTC+4:30', currencyBg:'Афгански афгани', bestTime:'Mar–May', visitors:'0.1M/yr' },
  'Сирия': { weather:'Средиземноморски/Пустинен', currency:'Сирийска лира', language:'Арабски', timezone:'UTC+3', currencyBg:'Сирийска лира', bestTime:'Апр–Юни', visitors:'0.5M/год.' },
  'Syria': { weather:'Mediterranean/Desert', currency:'SYP £', language:'Arabic', timezone:'UTC+3', currencyBg:'Сирийска лира', bestTime:'Apr–Jun', visitors:'0.5M/yr' },
  'Ирак': { weather:'Пустинен/Полупустинен', currency:'Иракски динар', language:'Арабски/Кюрдски', timezone:'UTC+3', currencyBg:'Иракски динар', bestTime:'Ное–Мар', visitors:'0.3M/год.' },
  'Iraq': { weather:'Desert/Semi-desert', currency:'IQD ع.د', language:'Arabic/Kurdish', timezone:'UTC+3', currencyBg:'Иракски динар', bestTime:'Nov–Mar', visitors:'0.3M/yr' },
  'Либия': { weather:'Пустинен', currency:'Либийски динар', language:'Арабски', timezone:'UTC+2', currencyBg:'Либийски динар', bestTime:'Ное–Мар', visitors:'0.2M/год.' },
  'Libya': { weather:'Desert', currency:'LYD ل.د', language:'Arabic', timezone:'UTC+2', currencyBg:'Либийски динар', bestTime:'Nov–Mar', visitors:'0.2M/yr' },
  'Тунис': { weather:'Средиземноморски/Пустинен', currency:'Тунизийски динар', language:'Арабски/Френски', timezone:'UTC+1', currencyBg:'Тунизийски динар', bestTime:'Апр–Юни', visitors:'9M/год.' },
  'Tunisia': { weather:'Mediterranean/Desert', currency:'TND د.ت', language:'Arabic/French', timezone:'UTC+1', currencyBg:'Тунизийски динар', bestTime:'Apr–Jun', visitors:'9M/yr' },
  'Алжир': { weather:'Средиземноморски/Пустинен', currency:'Алжирски динар', language:'Арабски/Берберски', timezone:'UTC+1', currencyBg:'Алжирски динар', bestTime:'Мар–Май', visitors:'3M/год.' },
  'Algeria': { weather:'Mediterranean/Desert', currency:'DZD دج', language:'Arabic/Berber', timezone:'UTC+1', currencyBg:'Алжирски динар', bestTime:'Mar–May', visitors:'3M/yr' },
  'Нигерия': { weather:'Тропически', currency:'Нигерийска наира', language:'Английски/Йоруба', timezone:'UTC+1', currencyBg:'Нигерийска наира', bestTime:'Ное–Апр', visitors:'1M/год.' },
  'Nigeria': { weather:'Tropical', currency:'NGN ₦', language:'English/Yoruba', timezone:'UTC+1', currencyBg:'Нигерийска наира', bestTime:'Nov–Apr', visitors:'1M/yr' },
  'Гана': { weather:'Тропически', currency:'Ганийски седи', language:'Английски', timezone:'UTC+0', currencyBg:'Ганийски седи', bestTime:'Ное–Апр', visitors:'1.2M/год.' },
  'Ghana': { weather:'Tropical', currency:'GHS ₵', language:'English', timezone:'UTC+0', currencyBg:'Ганийски седи', bestTime:'Nov–Apr', visitors:'1.2M/yr' },
  'Етиопия': { weather:'Тропически/Планински', currency:'Етиопски бир', language:'Амхарски', timezone:'UTC+3', currencyBg:'Етиопски бир', bestTime:'Окт–Мар', visitors:'1M/год.' },
  'Ethiopia': { weather:'Tropical/Highland', currency:'ETB Br', language:'Amharic', timezone:'UTC+3', currencyBg:'Етиопски бир', bestTime:'Oct–Mar', visitors:'1M/yr' },
  'Танзания': { weather:'Тропически', currency:'Танзанийски шилинг', language:'Суахили/Английски', timezone:'UTC+3', currencyBg:'Танзанийски шилинг', bestTime:'Юли–Окт', visitors:'1.5M/год.' },
  'Tanzania': { weather:'Tropical', currency:'TZS Sh', language:'Swahili/English', timezone:'UTC+3', currencyBg:'Танзанийски шилинг', bestTime:'Jul–Oct', visitors:'1.5M/yr' },
  'Зимбабве': { weather:'Субтропически', currency:'Зимбабвийски долар', language:'Английски/Шона', timezone:'UTC+2', currencyBg:'Зимбабвийски долар', bestTime:'Апр–Окт', visitors:'2M/год.' },
  'Zimbabwe': { weather:'Subtropical', currency:'ZWL $', language:'English/Shona', timezone:'UTC+2', currencyBg:'Зимбабвийски долар', bestTime:'Apr–Oct', visitors:'2M/yr' },
  'Мадагаскар': { weather:'Тропически', currency:'Малгашки ариари', language:'Малгашки/Френски', timezone:'UTC+3', currencyBg:'Малгашки ариари', bestTime:'Апр–Окт', visitors:'0.4M/год.' },
  'Madagascar': { weather:'Tropical', currency:'MGA Ar', language:'Malagasy/French', timezone:'UTC+3', currencyBg:'Малгашки ариари', bestTime:'Apr–Oct', visitors:'0.4M/yr' },
  'Намибия': { weather:'Пустинен/Субтропически', currency:'Намибийски долар', language:'Английски/Африкаанс', timezone:'UTC+2', currencyBg:'Намибийски долар', bestTime:'Май–Окт', visitors:'1.5M/год.' },
  'Namibia': { weather:'Desert/Subtropical', currency:'NAD $', language:'English/Afrikaans', timezone:'UTC+2', currencyBg:'Намибийски долар', bestTime:'May–Oct', visitors:'1.5M/yr' },
  'Колумбия': { weather:'Тропически/Планински', currency:'Колумбийско песо', language:'Испански', timezone:'UTC-5', currencyBg:'Колумбийско песо', bestTime:'Дек–Мар', visitors:'4.5M/год.' },
  'Colombia': { weather:'Tropical/Highland', currency:'COP $', language:'Spanish', timezone:'UTC-5', currencyBg:'Колумбийско песо', bestTime:'Dec–Mar', visitors:'4.5M/yr' },
  'Чили': { weather:'Умерен/Пустинен', currency:'Чилийско песо', language:'Испански', timezone:'UTC-4', currencyBg:'Чилийско песо', bestTime:'Дек–Фев', visitors:'4.5M/год.' },
  'Chile': { weather:'Temperate/Desert', currency:'CLP $', language:'Spanish', timezone:'UTC-4', currencyBg:'Чилийско песо', bestTime:'Dec–Feb', visitors:'4.5M/yr' },
  'Венецуела': { weather:'Тропически', currency:'Венецуелски боливар', language:'Испански', timezone:'UTC-4', currencyBg:'Венецуелски боливар', bestTime:'Дек–Апр', visitors:'0.7M/год.' },
  'Venezuela': { weather:'Tropical', currency:'VES Bs', language:'Spanish', timezone:'UTC-4', currencyBg:'Венецуелски боливар', bestTime:'Dec–Apr', visitors:'0.7M/yr' },
  'Уругвай': { weather:'Умерен', currency:'Уругвайско песо', language:'Испански', timezone:'UTC-3', currencyBg:'Уругвайско песо', bestTime:'Дек–Фев', visitors:'3M/год.' },
  'Uruguay': { weather:'Temperate', currency:'UYU $', language:'Spanish', timezone:'UTC-3', currencyBg:'Уругвайско песо', bestTime:'Dec–Feb', visitors:'3M/yr' },
  'Еквадор': { weather:'Тропически/Планински', currency:'Щатски долар', language:'Испански', timezone:'UTC-5', currencyBg:'Щатски долар', bestTime:'Юни–Авг', visitors:'2M/год.' },
  'Ecuador': { weather:'Tropical/Highland', currency:'USD $', language:'Spanish', timezone:'UTC-5', currencyBg:'Щатски долар', bestTime:'Jun–Aug', visitors:'2M/yr' },
  'Панама': { weather:'Тропически', currency:'Панамска балбоа/USD', language:'Испански', timezone:'UTC-5', currencyBg:'Панамска балбоа', bestTime:'Дек–Апр', visitors:'2M/год.' },
  'Panama': { weather:'Tropical', currency:'PAB/USD', language:'Spanish', timezone:'UTC-5', currencyBg:'Панамска балбоа', bestTime:'Dec–Apr', visitors:'2M/yr' },
  'Коста Рика': { weather:'Тропически', currency:'Костарикански колон', language:'Испански', timezone:'UTC-6', currencyBg:'Костарикански колон', bestTime:'Дек–Апр', visitors:'3.1M/год.' },
  'Costa Rica': { weather:'Tropical', currency:'CRC ₡', language:'Spanish', timezone:'UTC-6', currencyBg:'Костарикански колон', bestTime:'Dec–Apr', visitors:'3.1M/yr' },
  'Филипините': { weather:'Тропически', currency:'Филипинско песо', language:'Тагалски/Английски', timezone:'UTC+8', currencyBg:'Филипинско песо', bestTime:'Ное–Апр', visitors:'8M/год.' },
  'Philippines': { weather:'Tropical', currency:'PHP ₱', language:'Filipino/English', timezone:'UTC+8', currencyBg:'Филипинско песо', bestTime:'Nov–Apr', visitors:'8M/yr' },
  'Малайзия': { weather:'Тропически', currency:'Малайзийски ринггит', language:'Малайски/Английски', timezone:'UTC+8', currencyBg:'Малайзийски ринггит', bestTime:'Май–Юли', visitors:'26M/год.' },
  'Malaysia': { weather:'Tropical', currency:'MYR RM', language:'Malay/English', timezone:'UTC+8', currencyBg:'Малайзийски ринггит', bestTime:'May–Jul', visitors:'26M/yr' },
  'Мианмар': { weather:'Тропически/Мусонен', currency:'Мианмарски кят', language:'Бирмански', timezone:'UTC+6:30', currencyBg:'Мианмарски кят', bestTime:'Ное–Фев', visitors:'4M/год.' },
  'Myanmar': { weather:'Tropical/Monsoon', currency:'MMK K', language:'Burmese', timezone:'UTC+6:30', currencyBg:'Мианмарски кят', bestTime:'Nov–Feb', visitors:'4M/yr' },
  'Камбоджа': { weather:'Тропически', currency:'Камбоджийски риел', language:'Кхмерски', timezone:'UTC+7', currencyBg:'Камбоджийски риел', bestTime:'Ное–Апр', visitors:'6M/год.' },
  'Cambodia': { weather:'Tropical', currency:'KHR ៛', language:'Khmer', timezone:'UTC+7', currencyBg:'Камбоджийски риел', bestTime:'Nov–Apr', visitors:'6M/yr' },
  'Лаос': { weather:'Тропически/Мусонен', currency:'Лаоски кип', language:'Лаоски', timezone:'UTC+7', currencyBg:'Лаоски кип', bestTime:'Ное–Апр', visitors:'4.8M/год.' },
  'Laos': { weather:'Tropical/Monsoon', currency:'LAK ₭', language:'Lao', timezone:'UTC+7', currencyBg:'Лаоски кип', bestTime:'Nov–Apr', visitors:'4.8M/yr' },
  'Монголия': { weather:'Студен/Полупустинен', currency:'Монголски тугрик', language:'Монголски', timezone:'UTC+8', currencyBg:'Монголски тугрик', bestTime:'Юни–Авг', visitors:'0.7M/год.' },
  'Mongolia': { weather:'Cold/Semi-desert', currency:'MNT ₮', language:'Mongolian', timezone:'UTC+8', currencyBg:'Монголски тугрик', bestTime:'Jun–Aug', visitors:'0.7M/yr' },
  'Тайван': { weather:'Субтропически', currency:'Тайвански долар', language:'Мандарин', timezone:'UTC+8', currencyBg:'Тайвански долар', bestTime:'Окт–Дек', visitors:'11M/год.' },
  'Taiwan': { weather:'Subtropical', currency:'TWD $', language:'Mandarin', timezone:'UTC+8', currencyBg:'Тайвански долар', bestTime:'Oct–Dec', visitors:'11M/yr' },
  'Хонконг': { weather:'Субтропически', currency:'Хонконгски долар', language:'Кантонски/Английски', timezone:'UTC+8', currencyBg:'Хонконгски долар', bestTime:'Окт–Дек', visitors:'56M/год.' },
  'Hong Kong': { weather:'Subtropical', currency:'HKD $', language:'Cantonese/English', timezone:'UTC+8', currencyBg:'Хонконгски долар', bestTime:'Oct–Dec', visitors:'56M/yr' },
  'Финландия': { weather:'Субарктически', currency:'Евро €', language:'Фински/Шведски', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Юни–Авг', visitors:'3M/год.' },
  'Finland': { weather:'Subarctic', currency:'EUR €', language:'Finnish/Swedish', timezone:'UTC+2', currencyBg:'Евро €', bestTime:'Jun–Aug', visitors:'3M/yr' },
  'Швеция': { weather:'Умерен/Субарктически', currency:'Шведска крона', language:'Шведски', timezone:'UTC+1', currencyBg:'Шведска крона', bestTime:'Юни–Авг', visitors:'7M/год.' },
  'Sweden': { weather:'Temperate/Subarctic', currency:'SEK kr', language:'Swedish', timezone:'UTC+1', currencyBg:'Шведска крона', bestTime:'Jun–Aug', visitors:'7M/yr' },
  'Дания': { weather:'Умерен морски', currency:'Датска крона', language:'Датски', timezone:'UTC+1', currencyBg:'Датска крона', bestTime:'Юни–Авг', visitors:'10M/год.' },
  'Denmark': { weather:'Temperate oceanic', currency:'DKK kr', language:'Danish', timezone:'UTC+1', currencyBg:'Датска крона', bestTime:'Jun–Aug', visitors:'10M/yr' },
  'Белгия': { weather:'Умерен морски', currency:'Евро €', language:'Нидерландски/Френски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Май–Сеп', visitors:'9M/год.' },
  'Belgium': { weather:'Temperate oceanic', currency:'EUR €', language:'Dutch/French', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'May–Sep', visitors:'9M/yr' },
  'Полша': { weather:'Умерен континентален', currency:'Полска злота', language:'Полски', timezone:'UTC+1', currencyBg:'Полска злота', bestTime:'Май–Сеп', visitors:'21M/год.' },
  'Poland': { weather:'Temperate continental', currency:'PLN zł', language:'Polish', timezone:'UTC+1', currencyBg:'Полска злота', bestTime:'May–Sep', visitors:'21M/yr' },
  'Словакия': { weather:'Умерен континентален', currency:'Евро €', language:'Словашки', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Апр–Окт', visitors:'7M/год.' },
  'Slovakia': { weather:'Temperate continental', currency:'EUR €', language:'Slovak', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Apr–Oct', visitors:'7M/yr' },
  'Словения': { weather:'Умерен/Планински', currency:'Евро €', language:'Словенски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Юни–Авг', visitors:'4M/год.' },
  'Slovenia': { weather:'Temperate/Mountain', currency:'EUR €', language:'Slovenian', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Jun–Aug', visitors:'4M/yr' },
  'Сърбия': { weather:'Умерен континентален', currency:'Сръбски динар', language:'Сръбски', timezone:'UTC+1', currencyBg:'Сръбски динар', bestTime:'Май–Сеп', visitors:'2M/год.' },
  'Serbia': { weather:'Temperate continental', currency:'RSD din', language:'Serbian', timezone:'UTC+1', currencyBg:'Сръбски динар', bestTime:'May–Sep', visitors:'2M/yr' },
  'Черна гора': { weather:'Средиземноморски/Умерен', currency:'Евро €', language:'Черногорски', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Юни–Авг', visitors:'2.5M/год.' },
  'Montenegro': { weather:'Mediterranean/Temperate', currency:'EUR €', language:'Montenegrin', timezone:'UTC+1', currencyBg:'Евро €', bestTime:'Jun–Aug', visitors:'2.5M/yr' },
  'Албания': { weather:'Средиземноморски', currency:'Албански лек', language:'Албански', timezone:'UTC+1', currencyBg:'Албански лек', bestTime:'Юни–Сеп', visitors:'5.7M/год.' },
  'Albania': { weather:'Mediterranean', currency:'ALL L', language:'Albanian', timezone:'UTC+1', currencyBg:'Албански лек', bestTime:'Jun–Sep', visitors:'5.7M/yr' },
  'Северна Македония': { weather:'Умерен континентален', currency:'Македонски денар', language:'Македонски', timezone:'UTC+1', currencyBg:'Македонски денар', bestTime:'Май–Окт', visitors:'0.7M/год.' },
  'North Macedonia': { weather:'Temperate continental', currency:'MKD ден', language:'Macedonian', timezone:'UTC+1', currencyBg:'Македонски денар', bestTime:'May–Oct', visitors:'0.7M/yr' },
};

const TOURISM_URLS = {
  'Малдиви':'https://visitmaldives.com','Maldives':'https://visitmaldives.com',
  'Мароко':'https://www.visitmorocco.com','Morocco':'https://www.visitmorocco.com',
  'Маракеш':'https://www.visitmorocco.com','Marrakech':'https://www.visitmorocco.com',
  'Япония':'https://www.japan.travel','Japan':'https://www.japan.travel',
  'Киото':'https://kyoto.travel/en','Kyoto':'https://kyoto.travel/en',
  'Гърция':'https://www.visitgreece.gr','Greece':'https://www.visitgreece.gr',
  'Санторини':'https://www.visitgreece.gr','Santorini':'https://www.visitgreece.gr',
  'Испания':'https://www.spain.info','Spain':'https://www.spain.info',
  'Франция':'https://www.france.fr','France':'https://www.france.fr',
  'Париж':'https://www.parisinfo.com','Paris':'https://www.parisinfo.com',
  'Италия':'https://www.italia.it','Italy':'https://www.italia.it',
  'Тоскана':'https://www.visittuscany.com','Tuscany':'https://www.visittuscany.com',
  'Амалфи':'https://www.italiantourism.com','Amalfi Coast':'https://www.italiantourism.com',
  'Германия':'https://www.germany.travel','Germany':'https://www.germany.travel',
  'България':'https://www.bulgariatravel.org','Bulgaria':'https://www.bulgariatravel.org',
  'Турция':'https://www.goturkiye.com','Turkey':'https://www.goturkiye.com',
  'Египет':'https://www.egypt.travel','Egypt':'https://www.egypt.travel',
  'Тайланд':'https://www.tourismthailand.org','Thailand':'https://www.tourismthailand.org',
  'Бали':'https://www.indonesia.travel','Bali':'https://www.indonesia.travel',
  'Индия':'https://www.incredibleindia.org','India':'https://www.incredibleindia.org',
  'Австралия':'https://www.australia.com','Australia':'https://www.australia.com',
  'САЩ':'https://www.visittheusa.com','USA':'https://www.visittheusa.com',
  'Великобритания':'https://www.visitbritain.com','UK':'https://www.visitbritain.com',
  'Лондон':'https://www.visitlondon.com','London':'https://www.visitlondon.com',
  'Португалия':'https://www.visitportugal.com','Portugal':'https://www.visitportugal.com',
  'Лисабон':'https://www.visitlisboa.com','Lisbon':'https://www.visitlisboa.com',
  'Перу':'https://www.peru.travel','Peru':'https://www.peru.travel',
  'Мачу Пикчу':'https://www.peru.travel','Machu Picchu':'https://www.peru.travel',
  'Аржентина':'https://www.argentina.travel','Argentina':'https://www.argentina.travel',
  'Патагония':'https://www.patagonia-argentina.com','Patagonia':'https://www.patagonia-argentina.com',
  'Куба':'https://www.cuba.travel','Cuba':'https://www.cuba.travel',
  'Хавана':'https://www.cuba.travel','Havana':'https://www.cuba.travel',
  'Швейцария':'https://www.myswitzerland.com','Switzerland':'https://www.myswitzerland.com',
  'Швейцарски Алпи':'https://www.myswitzerland.com','Swiss Alps':'https://www.myswitzerland.com',
  'Йордания':'https://www.visitjordan.com','Jordan':'https://www.visitjordan.com',
  'Петра':'https://www.visitjordan.com','Petra':'https://www.visitjordan.com',
  'Нидерландия':'https://www.holland.com','Netherlands':'https://www.holland.com',
  'Амстердам':'https://www.iamsterdam.com','Amsterdam':'https://www.iamsterdam.com',
  'Индонезия':'https://www.indonesia.travel','Indonesia':'https://www.indonesia.travel',
  'Кения':'https://magicalkenya.com','Kenya':'https://magicalkenya.com',
  'Сафари Кения':'https://magicalkenya.com','Safari Kenya':'https://magicalkenya.com',
  'ОАЕ':'https://www.visitdubai.com','UAE':'https://www.visitdubai.com',
  'Дубай':'https://www.visitdubai.com','Dubai':'https://www.visitdubai.com',
  'Нова Зеландия':'https://www.newzealand.com','New Zealand':'https://www.newzealand.com',
  'Чехия':'https://www.prague.eu','Czech Republic':'https://www.prague.eu',
  'Прага':'https://www.prague.eu','Prague':'https://www.prague.eu',
  'Исландия':'https://www.visiticeland.com','Iceland':'https://www.visiticeland.com',
  'Китай':'https://www.cnto.org','China':'https://www.cnto.org',
  'Русия':'https://www.russiatourism.ru','Russia':'https://www.russiatourism.ru',
  'Норвегия':'https://www.visitnorway.com','Norway':'https://www.visitnorway.com',
  'Швеция':'https://www.visitsweden.com','Sweden':'https://www.visitsweden.com',
  'Хърватия':'https://croatia.hr','Croatia':'https://croatia.hr',
  'Черна гора':'https://www.montenegro.travel','Montenegro':'https://www.montenegro.travel',
  'Сърбия':'https://serbia.travel','Serbia':'https://serbia.travel',
  'Румъния':'https://www.romaniatourism.com','Romania':'https://www.romaniatourism.com',
  'Унгария':'https://www.visithungary.com','Hungary':'https://www.visithungary.com',
  'Будапеща':'https://www.visithungary.com','Budapest':'https://www.visithungary.com',
  'Австрия':'https://www.austria.info','Austria':'https://www.austria.info',
  'Виена':'https://www.wien.info','Vienna':'https://www.wien.info',
  'Белгия':'https://www.visitbelgium.com','Belgium':'https://www.visitbelgium.com',
  'Мексико':'https://www.visitmexico.com','Mexico':'https://www.visitmexico.com',
  'Бразилия':'https://www.visitbrasil.com','Brazil':'https://www.visitbrasil.com',
  'Колумбия':'https://colombia.travel','Colombia':'https://colombia.travel',
  'Южна Африка':'https://www.southafrica.net','South Africa':'https://www.southafrica.net',
  'Тунис':'https://www.discovertunisia.com','Tunisia':'https://www.discovertunisia.com',
  'Израел':'https://www.goisrael.com','Israel':'https://www.goisrael.com',
  'Сингапур':'https://www.visitsingapore.com','Singapore':'https://www.visitsingapore.com',
  'Виетнам':'https://vietnam.travel','Vietnam':'https://vietnam.travel',
  'Камбоджа':'https://www.tourismcambodia.com','Cambodia':'https://www.tourismcambodia.com',
  'Филипини':'https://www.itsmorefuninthephilippines.com','Philippines':'https://www.itsmorefuninthephilippines.com',
  'Шри Ланка':'https://www.srilanka.travel','Sri Lanka':'https://www.srilanka.travel',
  'Непал':'https://www.welcomenepal.com','Nepal':'https://www.welcomenepal.com',
};

const FT = {
  bg: { travelStyleMatch:'Стил на пътуване', personalityFit:'Съвместимост на характера', destinationDreams:'Мечтани дестинации', communication:'Комуникация', tripTiming:'Синхрон на пътуване', perfectCompanions:'Вие двамата бихте били перфектни спътници!', verifyPhoto:'📸 Верификация', verified:'Верифициран', verifyStep1:'Вдигни ръка и се усмихни', verifyDone:'✅ Профилът е верифициран!', compatReport:'📝 Пълен доклад', reportTitle:'Съвместимост с', aiWriter:'✨ AI Биография', generateBio:'Генерирай', describeYourself:'Опиши се', dreamDest:'Мечтана дестинация', hotelMatch:'🏨 Мач', alsoIn:'Също в', locationSharing:'споделя локация', stopSharing:'Спри', sendGift:'Изпрати подарък', gifts:{rose:'Роза',heart:'Сърце',ring:'Пръстен',plane:'Самолет',champagne:'Шампанско',star:'Звезда',kiss:'Целувка',diamond:'Диамант',flower:'Цвете',moon:'Луна',music:'Музика',island:'Остров',rocket:'Ракета',theatre:'Театър',worldmap:'Карта',circus:'Цирк',butterfly:'Пеперуда',earth:'Земя',crown:'Корона',fireworks:'Фойерверки'}, icebreaker:'🗣️ Ледоразбивач', icebreakerTitle:'Опитай:', icebreakerUse:'Използвай →', icebreakerRefresh:'🔄 Ново', aiPlannerBtn:'🗺️ ТравелСоул Планиране', splitCostsBtn:'💰 Разходи', badgesTab:'🏅 Значки', earned:'Спечелено', locked:'Заключено', generateTrip:'Генерирай ✨', generating:'Планираме...', shareWithGroup:'Сподели с групата', saveJourney:'Запази', destination:'Дестинация', budget:'Бюджет', duration:'Продължителност', travelStyle:'Стил', groupSize:'Група', nightMode:'🌙', nightModeOn:'Нощен режим вкл.', nightModeOff:'Нощен режим изкл.', dreamMatch:'🔮 Мечтани дестинации', dreamMatchBadge:'И двамата мечтаем за', connectBtn:'Свържи', weeklyChallenge:'🎯 Предизвикателство', challengeDays:'дни', challengeReward:'Награда', shareLocation:'📍 Локация', people:'Хора:', itemName:'Име на разход', addItem:'+ Добави', sendToGroupChat:'Изпрати в груповия чат', fromDate:'От дата', toDate:'До дата', inviteFriends:'Покани приятели', joinTrip:'Присъедини се', deleteTrip:'Изтрий', topPlaces:'🗺️ Топ места', allCategories:'Всички', nature:'Природа', culture:'Култура', food:'Храна', hidden:'Скрити бижута', mapBtn:'📍 Карта', infoBtn:'📖 Инфо', bestTimeVisit:'Най-добро време', days:'дни', styleLabels:{Adventure:'Приключение',Culture:'Култура',Relax:'Релакс',Foodie:'Храна',Nightlife:'Нощен живот'}, budgetBreakdown:'РАЗБИВКА НА БЮДЖЕТА', hotel:'Хотел', flights:'Полети', activities:'Активности', localTips:'💡 Местни съвети', allCat:'Всички', natureCat:'🏔️ Природа', cultureCat:'🏛️ Култура', foodCat:'🍜 Храна', hiddenCat:'💎 Скрити бижута', day:'Ден', morning:'Сутрин', afternoon:'Следобед', evening:'Вечер', travelers:'пътника', spotsLeft:'свободни места', newPlan:'← Ново', saved:'✅ Запазено!', free:'безплатно', noAttrInCat:'Няма места в тази категория.', noAttractionsMsg:'Генерирай ново пътуване за да видиш топ места', aiError:'AI отговорът беше прекъснат. Опитай с по-кратка дестинация или по-малко дни.', untitled:'Без заглавие', destPlaceholder:'Токио, Бали, Париж...', fromCity:'От град', transport:'Транспорт', plane:'✈️ Самолет', car:'🚗 Кола', train:'🚂 Влак', bus:'🚌 Автобус', distanceKm:'Разстояние', travelTime:'Време за пътуване', sightseeingTime:'Обиколка', hours:'ч.', km:'км', transportTips:'Съвет', officialSite:'🌐 Официален сайт', travelCalc:'🧭 Калкулатор', visaInfo:'🛂 Визова информация', visaRequired:'Виза необходима', visaNotRequired:'Без виза', visaType:'Тип виза', visaWhere:'Къде да кандидатствате', visaDays:'Обработка', visaCost:'Цена', vaccines:'💉 Ваксини', vaccinesRequired:'Задължителни', vaccinesRecommended:'Препоръчителни', healthTip:'Здравен съвет', photos:'📷 Снимки', addPhoto:'+ Добави снимка', noPhotos:'Няма снимки още', videoIntro:'🎬 Видео представяне', addVideo:'+ Добави видео', deleteVideo:'Изтрий видео', uploading:'Качване...', uploadError:'Грешка при качване', realData:'📡 Реални данни', aiEstimate:'🤖 AI оценка', ticketsInfo:'🎟️ Билети и инфо', copyLink:'✅ Линкът е копиран!' },
  en: { travelStyleMatch:'Travel Style Match', personalityFit:'Personality Fit', destinationDreams:'Destination Dreams', communication:'Communication', tripTiming:'Trip Timing', perfectCompanions:'You two would be perfect travel companions!', verifyPhoto:'📸 Verify Photo', verified:'Verified', verifyStep1:'Hold up your hand and smile', verifyDone:'✅ Photo Verified!', compatReport:'📝 Full Report', reportTitle:'Compatibility with', aiWriter:'✨ AI Write My Bio', generateBio:'Generate Bio', describeYourself:'Describe yourself', dreamDest:'Your dream destination', hotelMatch:'🏨 Match', alsoIn:'Also in', locationSharing:'sharing live location', stopSharing:'Stop sharing', sendGift:'Send Gift', gifts:{rose:'Rose',heart:'Heart',ring:'Ring',plane:'Airplane',champagne:'Champagne',star:'Star',kiss:'Kiss',diamond:'Diamond',flower:'Flower',moon:'Moon',music:'Music',island:'Island',rocket:'Rocket',theatre:'Theatre',worldmap:'World Map',circus:'Circus',butterfly:'Butterfly',earth:'Earth',crown:'Crown',fireworks:'Fireworks'}, icebreaker:'🗣️ Icebreaker', icebreakerTitle:'Try saying:', icebreakerUse:'Use →', icebreakerRefresh:'🔄 New suggestions', aiPlannerBtn:'🗺️ TravelSoul Planner', splitCostsBtn:'💰 Split Costs', badgesTab:'🏅 Badges', earned:'Earned', locked:'Locked', generateTrip:'Generate My Trip ✨', generating:'Planning your trip...', shareWithGroup:'Share with Group', saveJourney:'Save to My Journeys', destination:'Destination', budget:'Budget', duration:'Duration', travelStyle:'Style', groupSize:'Group size', nightMode:'🌙 Night Mode', nightModeOn:'Night Mode On', nightModeOff:'Night Mode Off', dreamMatch:'🔮 Dream Matches', dreamMatchBadge:'Both dream of', connectBtn:'Connect', weeklyChallenge:'🎯 Weekly Challenge', challengeDays:'days left', challengeReward:'Reward', shareLocation:'📍 Share Location', people:'People:', itemName:'Item name', addItem:'+ Add', sendToGroupChat:'Send to Group Chat', fromDate:'From date', toDate:'To date', inviteFriends:'Invite friends', joinTrip:'Join Trip', deleteTrip:'Delete', topPlaces:'🗺️ Top Places', allCategories:'All', nature:'Nature', culture:'Culture', food:'Food', hidden:'Hidden Gems', mapBtn:'📍 Map', infoBtn:'📖 Info', bestTimeVisit:'Best time', days:'days', styleLabels:{Adventure:'Adventure',Culture:'Culture',Relax:'Relax',Foodie:'Foodie',Nightlife:'Nightlife'}, budgetBreakdown:'BUDGET BREAKDOWN', hotel:'Hotel', flights:'Flights', activities:'Activities', localTips:'💡 Local Tips', allCat:'All', natureCat:'🏔️ Nature', cultureCat:'🏛️ Culture', foodCat:'🍜 Food', hiddenCat:'💎 Hidden Gems', day:'Day', morning:'Morning', afternoon:'Afternoon', evening:'Evening', travelers:'travelers', spotsLeft:'spots left', newPlan:'← New', saved:'✅ Saved!', free:'free', noAttrInCat:'No attractions in this category.', noAttractionsMsg:'Generate a new trip to see top places', aiError:'AI response was cut off. Try a shorter destination or fewer days.', untitled:'Untitled', destPlaceholder:'Tokyo, Bali, Paris...', fromCity:'From city', transport:'Transport', plane:'✈️ Plane', car:'🚗 Car', train:'🚂 Train', bus:'🚌 Bus', distanceKm:'Distance', travelTime:'Travel time', sightseeingTime:'Sightseeing', hours:'h.', km:'km', transportTips:'Tip', officialSite:'🌐 Official site', travelCalc:'🧭 Calculator', visaInfo:'🛂 Visa Information', visaRequired:'Visa required', visaNotRequired:'No visa required', visaType:'Visa type', visaWhere:'Where to apply', visaDays:'Processing', visaCost:'Cost', vaccines:'💉 Vaccines', vaccinesRequired:'Required', vaccinesRecommended:'Recommended', healthTip:'Health tip', photos:'📷 Photos', addPhoto:'+ Add Photo', noPhotos:'No photos yet', videoIntro:'🎬 Video Introduction', addVideo:'+ Add Video', deleteVideo:'Delete video', uploading:'Uploading...', uploadError:'Upload error', realData:'📡 Real data', aiEstimate:'🤖 AI estimate', ticketsInfo:'🎟️ Tickets & Info', copyLink:'✅ Link copied!' },
  es: { travelStyleMatch:'Estilo de viaje', personalityFit:'Compatibilidad de carácter', destinationDreams:'Destinos soñados', communication:'Comunicación', tripTiming:'Sincronía de viaje', perfectCompanions:'¡Serían compañeros de viaje perfectos!', verifyPhoto:'📸 Verificar', verified:'Verificado', verifyStep1:'Levanta la mano y sonríe', verifyDone:'✅ ¡Foto verificada!', compatReport:'📝 Informe', reportTitle:'Compatibilidad con', aiWriter:'✨ IA Bio', generateBio:'Generar Bio', describeYourself:'Descríbete', dreamDest:'Destino soñado', hotelMatch:'🏨 Match', alsoIn:'También en', locationSharing:'compartiendo ubicación', stopSharing:'Dejar de compartir', sendGift:'Enviar Regalo', gifts:{rose:'Rosa',heart:'Corazón',ring:'Anillo',plane:'Avión',champagne:'Champán',star:'Estrella',kiss:'Beso',diamond:'Diamante',flower:'Flor',moon:'Luna',music:'Música',island:'Isla',rocket:'Cohete',theatre:'Teatro',worldmap:'Mapa Mundial',circus:'Circo',butterfly:'Mariposa',earth:'Tierra',crown:'Corona',fireworks:'Fuegos Artificiales'}, icebreaker:'🗣️ Rompe-hielos', icebreakerTitle:'Prueba diciendo:', icebreakerUse:'Usar →', icebreakerRefresh:'🔄 Nuevas sugerencias', aiPlannerBtn:'🗺️ TravelSoul Planificador', splitCostsBtn:'💰 Dividir Costos', badgesTab:'🏅 Insignias', earned:'Ganados', locked:'Bloqueados', generateTrip:'Generar viaje ✨', generating:'Planificando...', shareWithGroup:'Compartir con grupo', saveJourney:'Guardar', destination:'Destino', budget:'Presupuesto', duration:'Duración', travelStyle:'Estilo', groupSize:'Tamaño grupo', nightMode:'🌙 Modo Noche', nightModeOn:'Modo noche activado', nightModeOff:'Modo noche desactivado', dreamMatch:'🔮 Destinos Soñados', dreamMatchBadge:'Ambos sueñan con', connectBtn:'Conectar', weeklyChallenge:'🎯 Desafío Semanal', challengeDays:'días', challengeReward:'Recompensa', shareLocation:'📍 Ubicación', people:'Personas:', itemName:'Nombre del gasto', addItem:'+ Añadir', sendToGroupChat:'Enviar al chat grupal', fromDate:'Desde', toDate:'Hasta', inviteFriends:'Invitar amigos', joinTrip:'Unirse al viaje', deleteTrip:'Eliminar', topPlaces:'🗺️ Lugares Top', allCategories:'Todos', nature:'Naturaleza', culture:'Cultura', food:'Comida', hidden:'Joyas Ocultas', mapBtn:'📍 Mapa', infoBtn:'📖 Info', bestTimeVisit:'Mejor momento', days:'días', styleLabels:{Adventure:'Aventura',Culture:'Cultura',Relax:'Relax',Foodie:'Gastronomía',Nightlife:'Vida nocturna'}, budgetBreakdown:'DESGLOSE DEL PRESUPUESTO', hotel:'Hotel', flights:'Vuelos', activities:'Actividades', localTips:'💡 Consejos Locales', allCat:'Todos', natureCat:'🏔️ Naturaleza', cultureCat:'🏛️ Cultura', foodCat:'🍜 Comida', hiddenCat:'💎 Joyas Ocultas', day:'Día', morning:'Mañana', afternoon:'Tarde', evening:'Noche', travelers:'viajeros', spotsLeft:'plazas libres', newPlan:'← Nuevo', saved:'✅ Guardado!', free:'gratis', noAttrInCat:'Sin atracciones en esta categoría.', noAttractionsMsg:'Genera un nuevo viaje para ver los mejores lugares', aiError:'La respuesta de IA fue cortada. Prueba un destino más corto o menos días.', untitled:'Sin título', destPlaceholder:'Tokio, Bali, París...', fromCity:'Ciudad origen', transport:'Transporte', plane:'✈️ Avión', car:'🚗 Coche', train:'🚂 Tren', bus:'🚌 Autobús', distanceKm:'Distancia', travelTime:'Tiempo viaje', sightseeingTime:'Visitas', hours:'h.', km:'km', transportTips:'Consejo', officialSite:'🌐 Sitio oficial', travelCalc:'🧭 Calculadora', visaInfo:'🛂 Información de Visa', visaRequired:'Visa requerida', visaNotRequired:'Sin visa', visaType:'Tipo de visa', visaWhere:'Dónde solicitar', visaDays:'Procesamiento', visaCost:'Costo', vaccines:'💉 Vacunas', vaccinesRequired:'Requeridas', vaccinesRecommended:'Recomendadas', healthTip:'Consejo salud', photos:'📷 Fotos', addPhoto:'+ Añadir foto', noPhotos:'Sin fotos aún', videoIntro:'🎬 Video de presentación', addVideo:'+ Añadir video', deleteVideo:'Eliminar video', uploading:'Subiendo...', uploadError:'Error de subida', realData:'📡 Datos reales', aiEstimate:'🤖 Estimación IA', ticketsInfo:'🎟️ Entradas e Info', copyLink:'✅ ¡Enlace copiado!' },
  fr: { travelStyleMatch:'Style de voyage', personalityFit:'Compatibilité de caractère', destinationDreams:'Destinations rêvées', communication:'Communication', tripTiming:'Synchronie de voyage', perfectCompanions:'Vous seriez des compagnons de voyage parfaits!', verifyPhoto:'📸 Vérifier', verified:'Vérifié', verifyStep1:'Lève la main et souris', verifyDone:'✅ Photo vérifiée!', compatReport:'📝 Rapport', reportTitle:'Compatibilité avec', aiWriter:'✨ IA Bio', generateBio:'Générer bio', describeYourself:'Décris-toi', dreamDest:'Destination rêvée', hotelMatch:'🏨 Match', alsoIn:'Aussi à', locationSharing:'partage sa position', stopSharing:'Arrêter', sendGift:'Envoyer Cadeau', gifts:{rose:'Rose',heart:'Cœur',ring:'Bague',plane:'Avion',champagne:'Champagne',star:'Étoile',kiss:'Bisou',diamond:'Diamant',flower:'Fleur',moon:'Lune',music:'Musique',island:'Île',rocket:'Fusée',theatre:'Théâtre',worldmap:'Carte du Monde',circus:'Cirque',butterfly:'Papillon',earth:'Terre',crown:'Couronne',fireworks:"Feux d'Artifice"}, icebreaker:'🗣️ Brise-glace', icebreakerTitle:'Essaie de dire:', icebreakerUse:'Utiliser →', icebreakerRefresh:'🔄 Nouvelles idées', aiPlannerBtn:'🗺️ TravelSoul Planificateur', splitCostsBtn:'💰 Partager coûts', badgesTab:'🏅 Badges', earned:'Gagnés', locked:'Verrouillés', generateTrip:'Générer voyage ✨', generating:'Planification...', shareWithGroup:'Partager au groupe', saveJourney:'Sauvegarder', destination:'Destination', budget:'Budget', duration:'Durée', travelStyle:'Style', groupSize:'Taille groupe', nightMode:'🌙 Mode Nuit', nightModeOn:'Mode nuit activé', nightModeOff:'Mode nuit désactivé', dreamMatch:'🔮 Destinations Rêvées', dreamMatchBadge:'Vous deux rêvez de', connectBtn:'Connecter', weeklyChallenge:'🎯 Défi Hebdo', challengeDays:'jours', challengeReward:'Récompense', shareLocation:'📍 Position', people:'Personnes:', itemName:'Nom de la dépense', addItem:'+ Ajouter', sendToGroupChat:'Envoyer au chat de groupe', fromDate:'Du', toDate:'Au', inviteFriends:'Inviter des amis', joinTrip:'Rejoindre le voyage', deleteTrip:'Supprimer', topPlaces:'🗺️ Top Lieux', allCategories:'Tous', nature:'Nature', culture:'Culture', food:'Cuisine', hidden:'Trésors Cachés', mapBtn:'📍 Carte', infoBtn:'📖 Info', bestTimeVisit:'Meilleur moment', days:'jours', styleLabels:{Adventure:'Aventure',Culture:'Culture',Relax:'Détente',Foodie:'Gastronomie',Nightlife:'Vie nocturne'}, budgetBreakdown:'RÉPARTITION DU BUDGET', hotel:'Hôtel', flights:'Vols', activities:'Activités', localTips:'💡 Conseils Locaux', allCat:'Tous', natureCat:'🏔️ Nature', cultureCat:'🏛️ Culture', foodCat:'🍜 Cuisine', hiddenCat:'💎 Trésors Cachés', day:'Jour', morning:'Matin', afternoon:'Après-midi', evening:'Soir', travelers:'voyageurs', spotsLeft:'places libres', newPlan:'← Nouveau', saved:'✅ Sauvegardé!', free:'gratuit', noAttrInCat:'Aucune attraction dans cette catégorie.', noAttractionsMsg:'Générez un nouveau voyage pour voir les meilleurs endroits', aiError:"La réponse de l'IA a été coupée. Essaie une destination plus courte ou moins de jours.", untitled:'Sans titre', destPlaceholder:'Tokyo, Bali, Paris...', fromCity:'Ville départ', transport:'Transport', plane:'✈️ Avion', car:'🚗 Voiture', train:'🚂 Train', bus:'🚌 Bus', distanceKm:'Distance', travelTime:'Temps trajet', sightseeingTime:'Visites', hours:'h.', km:'km', transportTips:'Conseil', officialSite:'🌐 Site officiel', travelCalc:'🧭 Calculateur', visaInfo:'🛂 Information Visa', visaRequired:'Visa requis', visaNotRequired:'Sans visa', visaType:'Type de visa', visaWhere:'Où postuler', visaDays:'Traitement', visaCost:'Coût', vaccines:'💉 Vaccins', vaccinesRequired:'Obligatoires', vaccinesRecommended:'Recommandés', healthTip:'Conseil santé', photos:'📷 Photos', addPhoto:'+ Ajouter photo', noPhotos:'Pas encore de photos', videoIntro:'🎬 Vidéo de présentation', addVideo:'+ Ajouter vidéo', deleteVideo:'Supprimer vidéo', uploading:'Chargement...', uploadError:'Erreur de chargement', realData:'📡 Données réelles', aiEstimate:'🤖 Estimation IA', ticketsInfo:'🎟️ Billets & Infos', copyLink:'✅ Lien copié!' },
  de: { travelStyleMatch:'Reisestil', personalityFit:'Charakterkompatibilität', destinationDreams:'Traumziele', communication:'Kommunikation', tripTiming:'Reisesynchronität', perfectCompanions:'Ihr wärt perfekte Reisegefährten!', verifyPhoto:'📸 Verifizieren', verified:'Verifiziert', verifyStep1:'Hebe die Hand und lächle', verifyDone:'✅ Foto verifiziert!', compatReport:'📝 Bericht', reportTitle:'Kompatibilität mit', aiWriter:'✨ KI Bio', generateBio:'Bio generieren', describeYourself:'Beschreibe dich', dreamDest:'Traumreiseziel', hotelMatch:'🏨 Match', alsoIn:'Auch in', locationSharing:'teilt Standort', stopSharing:'Stoppen', sendGift:'Geschenk senden', gifts:{rose:'Rose',heart:'Herz',ring:'Ring',plane:'Flugzeug',champagne:'Champagner',star:'Stern',kiss:'Kuss',diamond:'Diamant',flower:'Blume',moon:'Mond',music:'Musik',island:'Insel',rocket:'Rakete',theatre:'Theater',worldmap:'Weltkarte',circus:'Zirkus',butterfly:'Schmetterling',earth:'Erde',crown:'Krone',fireworks:'Feuerwerk'}, icebreaker:'🗣️ Eisbrecher', icebreakerTitle:'Probier zu sagen:', icebreakerUse:'Verwenden →', icebreakerRefresh:'🔄 Neue Vorschläge', aiPlannerBtn:'🗺️ TravelSoul Planer', splitCostsBtn:'💰 Kosten teilen', badgesTab:'🏅 Abzeichen', earned:'Verdient', locked:'Gesperrt', generateTrip:'Reise planen ✨', generating:'Wird geplant...', shareWithGroup:'Mit Gruppe teilen', saveJourney:'Speichern', destination:'Reiseziel', budget:'Budget', duration:'Dauer', travelStyle:'Stil', groupSize:'Gruppengröße', nightMode:'🌙 Nachtmodus', nightModeOn:'Nachtmodus an', nightModeOff:'Nachtmodus aus', dreamMatch:'🔮 Traumziele', dreamMatchBadge:'Beide träumen von', connectBtn:'Verbinden', weeklyChallenge:'🎯 Wöchentl. Challenge', challengeDays:'Tage', challengeReward:'Belohnung', shareLocation:'📍 Standort', people:'Personen:', itemName:'Ausgabenname', addItem:'+ Hinzufügen', sendToGroupChat:'An Gruppenchat senden', fromDate:'Von', toDate:'Bis', inviteFriends:'Freunde einladen', joinTrip:'Reise beitreten', deleteTrip:'Löschen', topPlaces:'🗺️ Top Orte', allCategories:'Alle', nature:'Natur', culture:'Kultur', food:'Essen', hidden:'Geheimtipps', mapBtn:'📍 Karte', infoBtn:'📖 Info', bestTimeVisit:'Beste Zeit', days:'Tage', styleLabels:{Adventure:'Abenteuer',Culture:'Kultur',Relax:'Entspannung',Foodie:'Kulinarik',Nightlife:'Nachtleben'}, budgetBreakdown:'BUDGETAUFSCHLÜSSELUNG', hotel:'Hotel', flights:'Flüge', activities:'Aktivitäten', localTips:'💡 Lokale Tipps', allCat:'Alle', natureCat:'🏔️ Natur', cultureCat:'🏛️ Kultur', foodCat:'🍜 Essen', hiddenCat:'💎 Geheimtipps', day:'Tag', morning:'Morgen', afternoon:'Nachmittag', evening:'Abend', travelers:'Reisende', spotsLeft:'Plätze frei', newPlan:'← Neu', saved:'✅ Gespeichert!', free:'kostenlos', noAttrInCat:'Keine Attraktionen in dieser Kategorie.', noAttractionsMsg:'Erstelle eine neue Reise, um Top-Orte zu sehen', aiError:'KI-Antwort wurde abgeschnitten. Versuche ein kürzeres Ziel oder weniger Tage.', untitled:'Ohne Titel', destPlaceholder:'Tokio, Bali, Paris...', fromCity:'Startstadt', transport:'Transport', plane:'✈️ Flugzeug', car:'🚗 Auto', train:'🚂 Zug', bus:'🚌 Bus', distanceKm:'Entfernung', travelTime:'Reisezeit', sightseeingTime:'Besichtigungen', hours:'Std.', km:'km', transportTips:'Tipp', officialSite:'🌐 Offizielle Seite', travelCalc:'🧭 Rechner', visaInfo:'🛂 Visa-Information', visaRequired:'Visum erforderlich', visaNotRequired:'Kein Visum', visaType:'Visumtyp', visaWhere:'Wo beantragen', visaDays:'Bearbeitung', visaCost:'Kosten', vaccines:'💉 Impfungen', vaccinesRequired:'Pflichtimpfungen', vaccinesRecommended:'Empfohlen', healthTip:'Gesundheitstipp', photos:'📷 Fotos', addPhoto:'+ Foto hinzufügen', noPhotos:'Noch keine Fotos', videoIntro:'🎬 Video-Vorstellung', addVideo:'+ Video hinzufügen', deleteVideo:'Video löschen', uploading:'Wird hochgeladen...', uploadError:'Upload-Fehler', realData:'📡 Echte Daten', aiEstimate:'🤖 KI-Schätzung', ticketsInfo:'🎟️ Tickets & Infos', copyLink:'✅ Link kopiert!' },
};

FT.el = deepMerge(FT.en, {
  travelStyleMatch:'Ταξιδιωτικό στυλ', personalityFit:'Συμβατότητα χαρακτήρα', destinationDreams:'Ονειρεμένοι προορισμοί', communication:'Επικοινωνία', tripTiming:'Συγχρονισμός ταξιδιού', perfectCompanions:'Θα ήσασταν τέλειοι συνταξιδιώτες!',
  verifyPhoto:'📸 Επαλήθευση φωτογραφίας', verified:'Επαληθευμένο', verifyStep1:'Σήκωσε το χέρι και χαμογέλα', verifyDone:'✅ Η φωτογραφία επαληθεύτηκε!', compatReport:'📝 Αναφορά', reportTitle:'Συμβατότητα με',
  aiPlannerBtn:'🗺️ TravelSoul Planner', splitCostsBtn:'💰 Μοίρασμα εξόδων', badgesTab:'🏅 Badges', earned:'Κερδισμένα', locked:'Κλειδωμένα',
  generateTrip:'Δημιουργία ταξιδιού ✨', generating:'Σχεδιάζουμε...', shareWithGroup:'Μοιράσου με την ομάδα', saveJourney:'Αποθήκευση στα ταξίδια μου',
  destination:'Προορισμός', budget:'Προϋπολογισμός', duration:'Διάρκεια', travelStyle:'Στυλ', groupSize:'Μέγεθος ομάδας',
  people:'Άτομα:', itemName:'Όνομα εξόδου', addItem:'+ Προσθήκη', sendToGroupChat:'Αποστολή στην ομαδική συνομιλία', fromDate:'Από', toDate:'Έως', inviteFriends:'Πρόσκληση φίλων',
  joinTrip:'Συμμετοχή στο ταξίδι', deleteTrip:'Διαγραφή', topPlaces:'🗺️ Κορυφαία μέρη', allCategories:'Όλα', nature:'Φύση', culture:'Πολιτισμός', food:'Φαγητό', hidden:'Κρυμμένα διαμάντια',
  mapBtn:'📍 Χάρτης', infoBtn:'📖 Πληροφορίες', bestTimeVisit:'Καλύτερη εποχή', days:'ημέρες',
  styleLabels:{Adventure:'Περιπέτεια',Culture:'Πολιτισμός',Relax:'Χαλάρωση',Foodie:'Γαστρονομία',Nightlife:'Νυχτερινή ζωή'},
  budgetBreakdown:'ΑΝΑΛΥΣΗ ΠΡΟΫΠΟΛΟΓΙΣΜΟΥ', hotel:'Ξενοδοχείο', flights:'Πτήσεις', activities:'Δραστηριότητες', localTips:'💡 Τοπικές συμβουλές',
  allCat:'Όλα', natureCat:'🏔️ Φύση', cultureCat:'🏛️ Πολιτισμός', foodCat:'🍜 Φαγητό', hiddenCat:'💎 Κρυμμένα διαμάντια',
  day:'Ημέρα', morning:'Πρωί', afternoon:'Απόγευμα', evening:'Βράδυ', travelers:'ταξιδιώτες', spotsLeft:'θέσεις διαθέσιμες',
  newPlan:'← Νέο', saved:'✅ Αποθηκεύτηκε!', free:'δωρεάν', noAttrInCat:'Δεν υπάρχουν αξιοθέατα σε αυτή την κατηγορία.', noAttractionsMsg:'Δημιούργησε νέο ταξίδι για να δεις κορυφαία μέρη',
  aiError:'Η απάντηση AI κόπηκε. Δοκίμασε μικρότερο προορισμό ή λιγότερες ημέρες.', untitled:'Χωρίς τίτλο', destPlaceholder:'Τόκιο, Μπαλί, Παρίσι...',
  fromCity:'Από πόλη', transport:'Μεταφορά', plane:'✈️ Αεροπλάνο', car:'🚗 Αυτοκίνητο', train:'🚂 Τρένο', bus:'🚌 Λεωφορείο',
  distanceKm:'Απόσταση', travelTime:'Χρόνος ταξιδιού', sightseeingTime:'Αξιοθέατα', hours:'ώ.', km:'χλμ', transportTips:'Συμβουλή',
  officialSite:'🌐 Επίσημος ιστότοπος', travelCalc:'🧭 Υπολογιστής', visaInfo:'🛂 Πληροφορίες βίζας', visaRequired:'Απαιτείται βίζα', visaNotRequired:'Δεν απαιτείται βίζα', visaType:'Τύπος βίζας', visaWhere:'Πού να κάνεις αίτηση', visaDays:'Επεξεργασία', visaCost:'Κόστος',
  vaccines:'💉 Εμβόλια', vaccinesRequired:'Απαιτούμενα', vaccinesRecommended:'Προτεινόμενα', healthTip:'Συμβουλή υγείας',
  photos:'📷 Φωτογραφίες', addPhoto:'+ Προσθήκη φωτογραφίας', noPhotos:'Δεν υπάρχουν φωτογραφίες', videoIntro:'🎬 Video intro', addVideo:'+ Προσθήκη βίντεο', deleteVideo:'Διαγραφή βίντεο', uploading:'Μεταφόρτωση...', uploadError:'Σφάλμα μεταφόρτωσης', realData:'📡 Πραγματικά δεδομένα', aiEstimate:'🤖 Εκτίμηση AI', ticketsInfo:'🎟️ Εισιτήρια & πληροφορίες', copyLink:'✅ Ο σύνδεσμος αντιγράφηκε!',
  hotelMatch:'🏨 Ταίριασμα', sendGift:'Αποστολή δώρου', icebreakerRefresh:'🔄 Νέες προτάσεις', dreamDest:'Η ονειρεμένη σου δεστίνασιον', nightModeOff:'Νυχτερινή λειτουργία ανενεργή', generateBio:'Δημιουργία βιογραφικού', challengeReward:'Ανταμοιβή', alsoIn:'Επίσης σε', shareLocation:'📍 Κοινοποίηση τοποθεσίας', aiWriter:'✨ AI γράψε το βιογραφικό μου', icebreakerUse:'Χρήση →', locationSharing:'κοινοποιεί τοποθεσία σε πραγματικό χρόνο', icebreaker:'🗣️ Icebreaker', nightModeOn:'Νυχτερινή λειτουργία ενεργή', nightMode:'🌙 Νυχτερινή λειτουργία', gifts:{rose:'Τριαντάφυλλο',heart:'Καρδιά',ring:'Δαχτυλίδι',plane:'Αεροπλάνο',champagne:'Σαμπάνια',star:'Αστέρι',kiss:'Φιλί',diamond:'Διαμάντι',flower:'Λουλούδι',moon:'Φεγγάρι',music:'Μουσική',island:'Νησί',rocket:'Πύραυλος',theatre:'Θέατρο',worldmap:'Παγκόσμιος χάρτης',circus:'Τσίρκο',butterfly:'Πεταλούδα',earth:'Γη',crown:'Στέμμα',fireworks:'Πυροτεχνήματα'}, dreamMatchBadge:'Και οι δύο ονειρεύεστε', connectBtn:'Σύνδεση', stopSharing:'Διακοπή κοινοποίησης', dreamMatch:'🔮 Ονειρεμένα ταιριάσματα', challengeDays:'ημέρες απομένουν', describeYourself:'Περιέγραψε τον εαυτό σου', weeklyChallenge:'🎯 Εβδομαδιαία πρόκληση', icebreakerTitle:'Δοκίμασε να πεις:',
});

FT.it = deepMerge(FT.en, {
  travelStyleMatch:'Stile di viaggio', personalityFit:'Compatibilità caratteriale', destinationDreams:'Destinazioni da sogno', communication:'Comunicazione', tripTiming:'Tempismo del viaggio', perfectCompanions:'Sareste compagni di viaggio perfetti!',
  verifyPhoto:'📸 Verifica foto', verified:'Verificato', verifyStep1:'Alza la mano e sorridi', verifyDone:'✅ Foto verificata!', compatReport:'📝 Rapporto', reportTitle:'Compatibilità con',
  aiPlannerBtn:'🗺️ Planner TravelSoul', splitCostsBtn:'💰 Dividi costi', badgesTab:'🏅 Badge', earned:'Ottenuti', locked:'Bloccati',
  generateTrip:'Genera viaggio ✨', generating:'Pianificazione...', shareWithGroup:'Condividi con il gruppo', saveJourney:'Salva nei miei viaggi',
  destination:'Destinazione', budget:'Budget', duration:'Durata', travelStyle:'Stile', groupSize:'Dimensione gruppo',
  people:'Persone:', itemName:'Nome spesa', addItem:'+ Aggiungi', sendToGroupChat:'Invia alla chat di gruppo', fromDate:'Da', toDate:'A', inviteFriends:'Invita amici',
  joinTrip:'Unisciti al viaggio', deleteTrip:'Elimina', topPlaces:'🗺️ Luoghi top', allCategories:'Tutti', nature:'Natura', culture:'Cultura', food:'Cibo', hidden:'Gemme nascoste',
  mapBtn:'📍 Mappa', infoBtn:'📖 Info', bestTimeVisit:'Periodo migliore', days:'giorni',
  styleLabels:{Adventure:'Avventura',Culture:'Cultura',Relax:'Relax',Foodie:'Gastronomia',Nightlife:'Vita notturna'},
  budgetBreakdown:'DETTAGLIO BUDGET', hotel:'Hotel', flights:'Voli', activities:'Attività', localTips:'💡 Consigli locali',
  allCat:'Tutti', natureCat:'🏔️ Natura', cultureCat:'🏛️ Cultura', foodCat:'🍜 Cibo', hiddenCat:'💎 Gemme nascoste',
  day:'Giorno', morning:'Mattina', afternoon:'Pomeriggio', evening:'Sera', travelers:'viaggiatori', spotsLeft:'posti disponibili',
  newPlan:'← Nuovo', saved:'✅ Salvato!', free:'gratis', noAttrInCat:'Nessuna attrazione in questa categoria.', noAttractionsMsg:'Genera un nuovo viaggio per vedere i luoghi migliori',
  aiError:'La risposta AI è stata interrotta. Prova una destinazione più breve o meno giorni.', untitled:'Senza titolo', destPlaceholder:'Tokyo, Bali, Parigi...',
  fromCity:'Da città', transport:'Trasporto', plane:'✈️ Aereo', car:'🚗 Auto', train:'🚂 Treno', bus:'🚍 Bus',
  distanceKm:'Distanza', travelTime:'Tempo viaggio', sightseeingTime:'Visite', hours:'h', km:'km', transportTips:'Consiglio',
  officialSite:'🌐 Sito ufficiale', travelCalc:'🧭 Calcolatore', visaInfo:'🛂 Informazioni visto', visaRequired:'Visto richiesto', visaNotRequired:'Nessun visto richiesto', visaType:'Tipo di visto', visaWhere:'Dove fare domanda', visaDays:'Elaborazione', visaCost:'Costo',
  vaccines:'💉 Vaccini', vaccinesRequired:'Obbligatori', vaccinesRecommended:'Consigliati', healthTip:'Consiglio salute',
  photos:'📷 Foto', addPhoto:'+ Aggiungi foto', noPhotos:'Ancora nessuna foto', videoIntro:'🎬 Video introduttivo', addVideo:'+ Aggiungi video', deleteVideo:'Elimina video', uploading:'Caricamento...', uploadError:'Errore caricamento', realData:'📡 Dati reali', aiEstimate:'🤖 Stima AI', ticketsInfo:'🎟️ Biglietti & info', copyLink:'✅ Link copiato!',
  hotelMatch:'🏨 Match', sendGift:'Invia regalo', icebreakerRefresh:'🔄 Nuovi suggerimenti', dreamDest:'La tua destinazione da sogno', nightModeOff:'Modalità notte disattivata', generateBio:'Genera bio', challengeReward:'Premio', alsoIn:'Anche a', shareLocation:'📍 Condividi posizione', aiWriter:'✨ AI scrivi la mia bio', icebreakerUse:'Usa →', locationSharing:'condivide la posizione in tempo reale', icebreaker:'🗣️ Rompighiaccio', nightModeOn:'Modalità notte attiva', nightMode:'🌙 Modalità notte', gifts:{rose:'Rosa',heart:'Cuore',ring:'Anello',plane:'Aereo',champagne:'Champagne',star:'Stella',kiss:'Bacio',diamond:'Diamante',flower:'Fiore',moon:'Luna',music:'Musica',island:'Isola',rocket:'Razzo',theatre:'Teatro',worldmap:'Mappa del mondo',circus:'Circo',butterfly:'Farfalla',earth:'Terra',crown:'Corona',fireworks:'Fuochi d\'artificio'}, dreamMatchBadge:'Sognate entrambi', connectBtn:'Connetti', stopSharing:'Interrompi condivisione', dreamMatch:'🔮 Match dei sogni', challengeDays:'giorni rimasti', describeYourself:'Descriviti', weeklyChallenge:'🎯 Sfida settimanale', icebreakerTitle:'Prova a dire:',
});

const MONTH_NAMES = {
  bg: ['Яну','Фев','Мар','Апр','Май','Юни','Юли','Авг','Сеп','Окт','Ное','Дек'],
  en: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
  es: ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'],
  fr: ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'],
  de: ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'],
  el: ['Ιαν','Φεβ','Μαρ','Απρ','Μάι','Ιουν','Ιουλ','Αυγ','Σεπ','Οκτ','Νοε','Δεκ'],
  it: ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'],
};

const EXPLORE_COPY = {
  bg: {
    searchPlaceholder:'Търси дестинация...', searchNotFound:'Дестинацията не е намерена.', noTripsTitle:'Все още няма пътувания', noTripsSub:'Бъди първият, който създаде!',
    planTripTo:'Планирай пътуване до', weather:'Климат', currency:'Валута', language:'Език', timezone:'Часова зона', bestSeason:'Най-добър сезон', visitors:'Посетители', facts:'✨ Интересни факти', tourismSite:'Туристически сайт', hotels:'Хотели', flights:'Полети', apartments:'Апартаменти', activities:'Активности',
    moodToday:'Как се чувстваш днес?', moodTitle:'Как се чувстваш?', moodSubtitle:'Избери настроение и AI ще намери идеалната дестинация', moodLoading:'AI търси идеалната дестинация…', changeMood:'Смени настроение', generationError:'Грешка при генериране. Провери връзката.',
    moods:{ happy:'Щастлив', sad:'Тъжен', stressed:'Стресиран', excited:'Развълнуван', tired:'Уморен', romantic:'Романтичен', curious:'Любопитен', adventurous:'Авантюристичен' },
    horoscopeFab:'Пътнически хороскоп', horoscopeTitle:'Пътнически Хороскоп', week:'Седмица', chooseSign:'Избери своя зодиакален знак:', change:'Смени', destinationThisWeek:'Дестинация тази седмица:', premiumFeature:'Premium функция', unlockHoroscope:'Вземи Premium за да видиш пълния хороскоп', upgradeNow:'Upgrade', readingStars:'Консултираме звездите…', tryAgain:'Опитай отново', compatibleSigns:'🤝 Съвместими знаци:', vibe:'Вибрация: ', updatesWeekly:'🔄 Обновява се всяка седмица', horoscopeError:'Грешка. Опитай отново.',
    multiTripTitle:'Планирай Мулти Трип', multiTripSub:'Многодестинационно пътуване', premium:'Премиум', checkingDestination:'Проверяваме дестинацията...', destinationFound:'Дестинацията е намерена.', destinationNotFound:'Дестинацията не е намерена.',
    tags:{ Trending:'Трендинг', Seasonal:'Сезонно', Popular:'Популярно', Adventure:'Приключение', 'Hidden gem':'Скрит бисер', 'Bucket list':'Задължително', Paradise:'Рай', Vibrant:'Колоритно', Iconic:'Емблематично', Luxury:'Лукс', Romantic:'Романтично', Epic:'Епично', Fairytale:'Приказно', Spiritual:'Духовно', Dreamy:'Мечтано', Wild:'Диво', Exotic:'Екзотично', Alpine:'Алпийско', Ancient:'Древно', 'Free spirit':'Свободен дух', City:'Град', Destination:'Дестинация', Country:'Държава' },
    zodiac:{ aries:'Овен', taurus:'Телец', gemini:'Близнаци', cancer:'Рак', leo:'Лъв', virgo:'Дева', libra:'Везни', scorpio:'Скорпион', sagittarius:'Стрелец', capricorn:'Козирог', aquarius:'Водолей', pisces:'Риби' },
  },
  en: {
    searchPlaceholder:'Search destination...', searchNotFound:'Destination not found.', noTripsTitle:'No trips yet', noTripsSub:'Be the first to create one!',
    planTripTo:'Plan trip to', weather:'Weather', currency:'Currency', language:'Language', timezone:'Timezone', bestSeason:'Best time', visitors:'Visitors', facts:'✨ Interesting Facts', tourismSite:'Tourism Site', hotels:'Hotels', flights:'Flights', apartments:'Vrbo', activities:'Activities',
    moodToday:'How are you feeling today?', moodTitle:'How are you feeling?', moodSubtitle:'Pick a mood and AI finds your perfect destination', moodLoading:'AI is finding your perfect destination…', changeMood:'Change mood', generationError:'Generation error. Check connection.',
    moods:{ happy:'Happy', sad:'Sad', stressed:'Stressed', excited:'Excited', tired:'Tired', romantic:'Romantic', curious:'Curious', adventurous:'Adventurous' },
    horoscopeFab:'Travel Horoscope', horoscopeTitle:'Travel Horoscope', week:'Week', chooseSign:'Choose your zodiac sign:', change:'Change', destinationThisWeek:'Your destination this week:', premiumFeature:'Premium Feature', unlockHoroscope:'Get Premium to unlock your full horoscope', upgradeNow:'Upgrade Now', readingStars:'Reading the stars…', tryAgain:'Try again', compatibleSigns:'🤝 Compatible signs:', vibe:'Vibe: ', updatesWeekly:'🔄 Updates every week', horoscopeError:'Error. Please try again.',
    multiTripTitle:'Plan Multi-Trip', multiTripSub:'Multi-destination trip', premium:'Premium', checkingDestination:'Checking destination...', destinationFound:'Destination found.', destinationNotFound:'Destination not found.',
    tags:{ Trending:'Trending', Seasonal:'Seasonal', Popular:'Popular', Adventure:'Adventure', 'Hidden gem':'Hidden gem', 'Bucket list':'Bucket list', Paradise:'Paradise', Vibrant:'Vibrant', Iconic:'Iconic', Luxury:'Luxury', Romantic:'Romantic', Epic:'Epic', Fairytale:'Fairytale', Spiritual:'Spiritual', Dreamy:'Dreamy', Wild:'Wild', Exotic:'Exotic', Alpine:'Alpine', Ancient:'Ancient', 'Free spirit':'Free spirit', City:'City', Destination:'Destination', Country:'Country' },
    zodiac:{ aries:'Aries', taurus:'Taurus', gemini:'Gemini', cancer:'Cancer', leo:'Leo', virgo:'Virgo', libra:'Libra', scorpio:'Scorpio', sagittarius:'Sagittarius', capricorn:'Capricorn', aquarius:'Aquarius', pisces:'Pisces' },
  },
  es: {
    searchPlaceholder:'Buscar destino...', searchNotFound:'Destino no encontrado.', noTripsTitle:'Aún no hay viajes', noTripsSub:'¡Sé el primero en crear uno!',
    planTripTo:'Planear viaje a', weather:'Clima', currency:'Moneda', language:'Idioma', timezone:'Zona horaria', bestSeason:'Mejor época', visitors:'Visitantes', facts:'✨ Datos interesantes', tourismSite:'Sitio turístico', hotels:'Hoteles', flights:'Vuelos', apartments:'Vrbo', activities:'Actividades',
    moodToday:'¿Cómo te sientes hoy?', moodTitle:'¿Cómo te sientes?', moodSubtitle:'Elige un estado de ánimo y la IA encontrará tu destino perfecto', moodLoading:'La IA está buscando tu destino perfecto…', changeMood:'Cambiar estado', generationError:'Error al generar. Revisa la conexión.',
    moods:{ happy:'Feliz', sad:'Triste', stressed:'Estresado', excited:'Emocionado', tired:'Cansado', romantic:'Romántico', curious:'Curioso', adventurous:'Aventurero' },
    horoscopeFab:'Horóscopo de viaje', horoscopeTitle:'Horóscopo de Viaje', week:'Semana', chooseSign:'Elige tu signo zodiacal:', change:'Cambiar', destinationThisWeek:'Tu destino esta semana:', premiumFeature:'Función Premium', unlockHoroscope:'Obtén Premium para desbloquear tu horóscopo completo', upgradeNow:'Actualizar ahora', readingStars:'Consultando las estrellas…', tryAgain:'Intentar de nuevo', compatibleSigns:'🤝 Signos compatibles:', vibe:'Vibra: ', updatesWeekly:'🔄 Se actualiza cada semana', horoscopeError:'Error. Inténtalo de nuevo.',
    multiTripTitle:'Planifica Multi-Viaje', multiTripSub:'Viaje multi-destino', premium:'Premium', checkingDestination:'Comprobando destino...', destinationFound:'Destino encontrado.', destinationNotFound:'Destino no encontrado.',
    tags:{ Trending:'Tendencia', Seasonal:'De temporada', Popular:'Popular', Adventure:'Aventura', 'Hidden gem':'Joya oculta', 'Bucket list':'Imprescindible', Paradise:'Paraíso', Vibrant:'Vibrante', Iconic:'Icónico', Luxury:'Lujo', Romantic:'Romántico', Epic:'Épico', Fairytale:'De cuento', Spiritual:'Espiritual', Dreamy:'Soñado', Wild:'Salvaje', Exotic:'Exótico', Alpine:'Alpino', Ancient:'Antiguo', 'Free spirit':'Espíritu libre', City:'Ciudad', Destination:'Destino', Country:'País' },
    zodiac:{ aries:'Aries', taurus:'Tauro', gemini:'Géminis', cancer:'Cáncer', leo:'Leo', virgo:'Virgo', libra:'Libra', scorpio:'Escorpio', sagittarius:'Sagitario', capricorn:'Capricornio', aquarius:'Acuario', pisces:'Piscis' },
  },
  fr: {
    searchPlaceholder:'Chercher une destination...', searchNotFound:'Destination introuvable.', noTripsTitle:'Pas encore de voyages', noTripsSub:'Sois le premier à en créer un!',
    planTripTo:'Planifier un voyage à', weather:'Climat', currency:'Devise', language:'Langue', timezone:'Fuseau horaire', bestSeason:'Meilleure période', visitors:'Visiteurs', facts:'✨ Faits intéressants', tourismSite:'Site touristique', hotels:'Hôtels', flights:'Vols', apartments:'Vrbo', activities:'Activités',
    moodToday:'Comment tu te sens aujourd’hui?', moodTitle:'Comment tu te sens?', moodSubtitle:'Choisis une humeur et l’IA trouvera ta destination parfaite', moodLoading:'L’IA cherche ta destination parfaite…', changeMood:'Changer d’humeur', generationError:'Erreur de génération. Vérifie la connexion.',
    moods:{ happy:'Heureux', sad:'Triste', stressed:'Stressé', excited:'Enthousiaste', tired:'Fatigué', romantic:'Romantique', curious:'Curieux', adventurous:'Aventureux' },
    horoscopeFab:'Horoscope de voyage', horoscopeTitle:'Horoscope de Voyage', week:'Semaine', chooseSign:'Choisis ton signe du zodiaque:', change:'Changer', destinationThisWeek:'Ta destination cette semaine:', premiumFeature:'Fonction Premium', unlockHoroscope:'Passe à Premium pour débloquer ton horoscope complet', upgradeNow:'Passer maintenant', readingStars:'Lecture des étoiles…', tryAgain:'Réessayer', compatibleSigns:'🤝 Signes compatibles:', vibe:'Vibe: ', updatesWeekly:'🔄 Mis à jour chaque semaine', horoscopeError:'Erreur. Réessaie.',
    multiTripTitle:'Planifier Multi-Voyage', multiTripSub:'Voyage multi-destination', premium:'Premium', checkingDestination:'Vérification de la destination...', destinationFound:'Destination trouvée.', destinationNotFound:'Destination introuvable.',
    tags:{ Trending:'Tendance', Seasonal:'Saisonnier', Popular:'Populaire', Adventure:'Aventure', 'Hidden gem':'Pépite cachée', 'Bucket list':'À faire absolument', Paradise:'Paradis', Vibrant:'Vibrant', Iconic:'Iconique', Luxury:'Luxe', Romantic:'Romantique', Epic:'Épique', Fairytale:'Conte de fées', Spiritual:'Spirituel', Dreamy:'De rêve', Wild:'Sauvage', Exotic:'Exotique', Alpine:'Alpin', Ancient:'Ancien', 'Free spirit':'Esprit libre', City:'Ville', Destination:'Destination', Country:'Pays' },
    zodiac:{ aries:'Bélier', taurus:'Taureau', gemini:'Gémeaux', cancer:'Cancer', leo:'Lion', virgo:'Vierge', libra:'Balance', scorpio:'Scorpion', sagittarius:'Sagittaire', capricorn:'Capricorne', aquarius:'Verseau', pisces:'Poissons' },
  },
  de: {
    searchPlaceholder:'Ziel suchen...', searchNotFound:'Reiseziel nicht gefunden.', noTripsTitle:'Noch keine Reisen', noTripsSub:'Erstelle als Erste*r eine Reise!',
    planTripTo:'Reise planen nach', weather:'Wetter', currency:'Währung', language:'Sprache', timezone:'Zeitzone', bestSeason:'Beste Reisezeit', visitors:'Besucher', facts:'✨ Interessante Fakten', tourismSite:'Tourismus-Seite', hotels:'Hotels', flights:'Flüge', apartments:'Vrbo', activities:'Aktivitäten',
    moodToday:'Wie fühlst du dich heute?', moodTitle:'Wie fühlst du dich?', moodSubtitle:'Wähle eine Stimmung und die KI findet dein perfektes Reiseziel', moodLoading:'Die KI findet dein perfektes Reiseziel…', changeMood:'Stimmung ändern', generationError:'Fehler beim Generieren. Prüfe die Verbindung.',
    moods:{ happy:'Glücklich', sad:'Traurig', stressed:'Gestresst', excited:'Aufgeregt', tired:'Müde', romantic:'Romantisch', curious:'Neugierig', adventurous:'Abenteuerlustig' },
    horoscopeFab:'Reisehoroskop', horoscopeTitle:'Reisehoroskop', week:'Woche', chooseSign:'Wähle dein Sternzeichen:', change:'Ändern', destinationThisWeek:'Dein Reiseziel diese Woche:', premiumFeature:'Premium-Funktion', unlockHoroscope:'Hol dir Premium, um dein vollständiges Horoskop freizuschalten', upgradeNow:'Jetzt upgraden', readingStars:'Die Sterne werden gelesen…', tryAgain:'Erneut versuchen', compatibleSigns:'🤝 Kompatible Zeichen:', vibe:'Stimmung: ', updatesWeekly:'🔄 Wird jede Woche aktualisiert', horoscopeError:'Fehler. Bitte erneut versuchen.',
    multiTripTitle:'Multi-Trip Planen', multiTripSub:'Mehrziel-Reise', premium:'Premium', checkingDestination:'Reiseziel wird geprüft...', destinationFound:'Reiseziel gefunden.', destinationNotFound:'Reiseziel nicht gefunden.',
    tags:{ Trending:'Im Trend', Seasonal:'Saisonal', Popular:'Beliebt', Adventure:'Abenteuer', 'Hidden gem':'Geheimtipp', 'Bucket list':'Bucket List', Paradise:'Paradies', Vibrant:'Lebendig', Iconic:'Ikonisch', Luxury:'Luxus', Romantic:'Romantisch', Epic:'Episch', Fairytale:'Märchenhaft', Spiritual:'Spirituell', Dreamy:'Traumhaft', Wild:'Wild', Exotic:'Exotisch', Alpine:'Alpin', Ancient:'Antik', 'Free spirit':'Freigeist', City:'Stadt', Destination:'Reiseziel', Country:'Land' },
    zodiac:{ aries:'Widder', taurus:'Stier', gemini:'Zwillinge', cancer:'Krebs', leo:'Löwe', virgo:'Jungfrau', libra:'Waage', scorpio:'Skorpion', sagittarius:'Schütze', capricorn:'Steinbock', aquarius:'Wassermann', pisces:'Fische' },
  },
  el: {
    searchPlaceholder:'Αναζήτηση προορισμού...', searchNotFound:'Ο προορισμός δεν βρέθηκε.', noTripsTitle:'Δεν υπάρχουν ακόμη ταξίδια', noTripsSub:'Γίνε ο πρώτος που θα δημιουργήσει ένα!',
    planTripTo:'Σχεδίασε ταξίδι σε', weather:'Κλίμα', currency:'Νόμισμα', language:'Γλώσσα', timezone:'Ζώνη ώρας', bestSeason:'Καλύτερη εποχή', visitors:'Επισκέπτες', facts:'✨ Ενδιαφέροντα στοιχεία', tourismSite:'Τουριστικός ιστότοπος', hotels:'Ξενοδοχεία', flights:'Πτήσεις', apartments:'Vrbo', activities:'Δραστηριότητες',
    moodToday:'Πώς νιώθεις σήμερα;', moodTitle:'Πώς νιώθεις;', moodSubtitle:'Διάλεξε διάθεση και η AI θα βρει τον ιδανικό προορισμό', moodLoading:'Η AI βρίσκει τον ιδανικό προορισμό…', changeMood:'Αλλαγή διάθεσης', generationError:'Σφάλμα δημιουργίας. Έλεγξε τη σύνδεση.',
    moods:{ happy:'Χαρούμενος', sad:'Λυπημένος', stressed:'Στρεσαρισμένος', excited:'Ενθουσιασμένος', tired:'Κουρασμένος', romantic:'Ρομαντικός', curious:'Περίεργος', adventurous:'Περιπετειώδης' },
    horoscopeFab:'Ταξιδιωτικό ωροσκόπιο', horoscopeTitle:'Ταξιδιωτικό Ωροσκόπιο', week:'Εβδομάδα', chooseSign:'Επίλεξε ζώδιο:', change:'Αλλαγή', destinationThisWeek:'Ο προορισμός σου αυτή την εβδομάδα:', premiumFeature:'Premium λειτουργία', unlockHoroscope:'Πάρε Premium για πλήρες ωροσκόπιο', upgradeNow:'Αναβάθμιση', readingStars:'Διαβάζουμε τα αστέρια…', tryAgain:'Δοκίμασε ξανά', compatibleSigns:'🤝 Συμβατά ζώδια:', vibe:'Διάθεση: ', updatesWeekly:'🔄 Ενημερώνεται κάθε εβδομάδα', horoscopeError:'Σφάλμα. Δοκίμασε ξανά.',
    multiTripTitle:'Σχεδίασε Multi-Trip', multiTripSub:'Ταξίδι πολλών προορισμών', premium:'Premium', checkingDestination:'Έλεγχος προορισμού...', destinationFound:'Ο προορισμός βρέθηκε.', destinationNotFound:'Ο προορισμός δεν βρέθηκε.',
    tags:{ Trending:'Τάση', Seasonal:'Εποχικό', Popular:'Δημοφιλές', Adventure:'Περιπέτεια', 'Hidden gem':'Κρυμμένο διαμάντι', 'Bucket list':'Bucket list', Paradise:'Παράδεισος', Vibrant:'Ζωντανό', Iconic:'Εμβληματικό', Luxury:'Πολυτέλεια', Romantic:'Ρομαντικό', Epic:'Επικό', Fairytale:'Παραμυθένιο', Spiritual:'Πνευματικό', Dreamy:'Ονειρικό', Wild:'Άγριο', Exotic:'Εξωτικό', Alpine:'Αλπικό', Ancient:'Αρχαίο', 'Free spirit':'Ελεύθερο πνεύμα', City:'Πόλη', Destination:'Προορισμός', Country:'Χώρα' },
    zodiac:{ aries:'Κριός', taurus:'Ταύρος', gemini:'Δίδυμοι', cancer:'Καρκίνος', leo:'Λέων', virgo:'Παρθένος', libra:'Ζυγός', scorpio:'Σκορπιός', sagittarius:'Τοξότης', capricorn:'Αιγόκερως', aquarius:'Υδροχόος', pisces:'Ιχθύες' },
  },
  it: {
    searchPlaceholder:'Cerca destinazione...', searchNotFound:'Destinazione non trovata.', noTripsTitle:'Ancora nessun viaggio', noTripsSub:'Sii il primo a crearne uno!',
    planTripTo:'Pianifica viaggio a', weather:'Clima', currency:'Valuta', language:'Lingua', timezone:'Fuso orario', bestSeason:'Periodo migliore', visitors:'Visitatori', facts:'✨ Curiosità', tourismSite:'Sito turistico', hotels:'Hotel', flights:'Voli', apartments:'Vrbo', activities:'Attività',
    moodToday:'Come ti senti oggi?', moodTitle:'Come ti senti?', moodSubtitle:'Scegli un umore e l’AI troverà la destinazione perfetta', moodLoading:'L’AI sta trovando la destinazione perfetta…', changeMood:'Cambia umore', generationError:'Errore di generazione. Controlla la connessione.',
    moods:{ happy:'Felice', sad:'Triste', stressed:'Stressato', excited:'Entusiasta', tired:'Stanco', romantic:'Romantico', curious:'Curioso', adventurous:'Avventuroso' },
    horoscopeFab:'Oroscopo di viaggio', horoscopeTitle:'Oroscopo di viaggio', week:'Settimana', chooseSign:'Scegli il tuo segno zodiacale:', change:'Cambia', destinationThisWeek:'La tua destinazione questa settimana:', premiumFeature:'Funzione Premium', unlockHoroscope:'Passa a Premium per l’oroscopo completo', upgradeNow:'Aggiorna ora', readingStars:'Lettura delle stelle…', tryAgain:'Riprova', compatibleSigns:'🤝 Segni compatibili:', vibe:'Vibe: ', updatesWeekly:'🔄 Aggiornato ogni settimana', horoscopeError:'Errore. Riprova.',
    multiTripTitle:'Pianifica Multi-Trip', multiTripSub:'Viaggio multi-destinazione', premium:'Premium', checkingDestination:'Controllo destinazione...', destinationFound:'Destinazione trovata.', destinationNotFound:'Destinazione non trovata.',
    tags:{ Trending:'Di tendenza', Seasonal:'Stagionale', Popular:'Popolare', Adventure:'Avventura', 'Hidden gem':'Gemma nascosta', 'Bucket list':'Da fare', Paradise:'Paradiso', Vibrant:'Vivace', Iconic:'Iconico', Luxury:'Lusso', Romantic:'Romantico', Epic:'Epico', Fairytale:'Fiabesco', Spiritual:'Spirituale', Dreamy:'Da sogno', Wild:'Selvaggio', Exotic:'Esotico', Alpine:'Alpino', Ancient:'Antico', 'Free spirit':'Spirito libero', City:'Città', Destination:'Destinazione', Country:'Paese' },
    zodiac:{ aries:'Ariete', taurus:'Toro', gemini:'Gemelli', cancer:'Cancro', leo:'Leone', virgo:'Vergine', libra:'Bilancia', scorpio:'Scorpione', sagittarius:'Sagittario', capricorn:'Capricorno', aquarius:'Acquario', pisces:'Pesci' },
  },
};

const exploreCopy = (lang) => EXPLORE_COPY[lang] || EXPLORE_COPY.en;

const COUNTRY_I18N = {
  Greece:{ bg:'Гърция', en:'Greece', es:'Grecia', fr:'Grèce', de:'Griechenland', el:'Ελλάδα', it:'Grecia' },
  Japan:{ bg:'Япония', en:'Japan', es:'Japón', fr:'Japon', de:'Japan', el:'Ιαπωνία', it:'Giappone' },
  Indonesia:{ bg:'Индонезия', en:'Indonesia', es:'Indonesia', fr:'Indonésie', de:'Indonesien', el:'Ινδονησία', it:'Indonesia' },
  Argentina:{ bg:'Аржентина', en:'Argentina', es:'Argentina', fr:'Argentine', de:'Argentinien', el:'Αργεντινή', it:'Argentina' },
  Morocco:{ bg:'Мароко', en:'Morocco', es:'Marruecos', fr:'Maroc', de:'Marokko', el:'Μαρόκο', it:'Marocco' },
  Iceland:{ bg:'Исландия', en:'Iceland', es:'Islandia', fr:'Islande', de:'Island', el:'Ισλανδία', it:'Islanda' },
  Maldives:{ bg:'Малдиви', en:'Maldives', es:'Maldivas', fr:'Maldives', de:'Malediven', el:'Μαλδίβες', it:'Maldive' },
  Portugal:{ bg:'Португалия', en:'Portugal', es:'Portugal', fr:'Portugal', de:'Portugal', el:'Πορτογαλία', it:'Portogallo' },
  Peru:{ bg:'Перу', en:'Peru', es:'Perú', fr:'Pérou', de:'Peru', el:'Περού', it:'Perù' },
  UAE:{ bg:'ОАЕ', en:'UAE', es:'EAU', fr:'EAU', de:'VAE', el:'ΗΑΕ', it:'EAU' },
  Italy:{ bg:'Италия', en:'Italy', es:'Italia', fr:'Italie', de:'Italien', el:'Ιταλία', it:'Italia' },
  'New Zealand':{ bg:'Нова Зеландия', en:'New Zealand', es:'Nueva Zelanda', fr:'Nouvelle-Zélande', de:'Neuseeland', el:'Νέα Ζηλανδία', it:'Nuova Zelanda' },
  'Czech Republic':{ bg:'Чехия', en:'Czech Republic', es:'República Checa', fr:'Tchéquie', de:'Tschechien', el:'Τσεχία', it:'Repubblica Ceca' },
  Thailand:{ bg:'Тайланд', en:'Thailand', es:'Tailandia', fr:'Thaïlande', de:'Thailand', el:'Ταϊλάνδη', it:'Tailandia' },
  Kenya:{ bg:'Кения', en:'Kenya', es:'Kenia', fr:'Kenya', de:'Kenia', el:'Κένυα', it:'Kenya' },
  Cuba:{ bg:'Куба', en:'Cuba', es:'Cuba', fr:'Cuba', de:'Kuba', el:'Κούβα', it:'Cuba' },
  Switzerland:{ bg:'Швейцария', en:'Switzerland', es:'Suiza', fr:'Suisse', de:'Schweiz', el:'Ελβετία', it:'Svizzera' },
  Jordan:{ bg:'Йордания', en:'Jordan', es:'Jordania', fr:'Jordanie', de:'Jordanien', el:'Ιορδανία', it:'Giordania' },
  Netherlands:{ bg:'Нидерландия', en:'Netherlands', es:'Países Bajos', fr:'Pays-Bas', de:'Niederlande', el:'Ολλανδία', it:'Paesi Bassi' },
  Bulgaria:{ bg:'България', en:'Bulgaria', es:'Bulgaria', fr:'Bulgarie', de:'Bulgarien', el:'Βουλγαρία', it:'Bulgaria' },
};

const CITY_I18N = {
  'София': { bg:'София', en:'Sofia', es:'Sofía', fr:'Sofia', de:'Sofia', el:'Σόφια', it:'Sofia' },
  'Варна': { bg:'Варна', en:'Varna', es:'Varna', fr:'Varna', de:'Warna', el:'Βάρνα', it:'Varna' },
  'Пловдив': { bg:'Пловдив', en:'Plovdiv', es:'Plovdiv', fr:'Plovdiv', de:'Plowdiw', el:'Πλόβντιβ', it:'Plovdiv' },
  'Бургас': { bg:'Бургас', en:'Burgas', es:'Burgas', fr:'Bourgas', de:'Burgas', el:'Μπουργκάς', it:'Burgas' },
  'Банско': { bg:'Банско', en:'Bansko', es:'Bansko', fr:'Bansko', de:'Bansko', el:'Μπάνσκο', it:'Bansko' },
};

const DESTINATION_NAME_I18N = {
  'New Zealand': { bg:'Нова Зеландия', en:'New Zealand', es:'Nueva Zelanda', fr:'Nouvelle-Zélande', de:'Neuseeland', el:'Νέα Ζηλανδία', it:'Nuova Zelanda' },
  Prague: { bg:'Прага', en:'Prague', es:'Praga', fr:'Prague', de:'Prag', el:'Πράγα', it:'Praga' },
  Tuscany: { bg:'Тоскана', en:'Tuscany', es:'Toscana', fr:'Toscane', de:'Toskana', el:'Τοσκάνη', it:'Toscana' },
  Maldives: { bg:'Малдиви', en:'Maldives', es:'Maldivas', fr:'Maldives', de:'Malediven', el:'Μαλδίβες', it:'Maldive' },
  Kyoto: { bg:'Киото', en:'Kyoto', es:'Kioto', fr:'Kyoto', de:'Kyoto', el:'Κιότο', it:'Kyoto' },
  'Machu Picchu': { bg:'Мачу Пикчу', en:'Machu Picchu', es:'Machu Picchu', fr:'Machu Picchu', de:'Machu Picchu', el:'Μάτσου Πίτσου', it:'Machu Picchu' },
  Havana: { bg:'Хавана', en:'Havana', es:'La Habana', fr:'La Havane', de:'Havanna', el:'Αβάνα', it:'L\'Avana' },
  Lisbon: { bg:'Лисабон', en:'Lisbon', es:'Lisboa', fr:'Lisbonne', de:'Lissabon', el:'Λισαβόνα', it:'Lisbona' },
  Petra: { bg:'Петра', en:'Petra', es:'Petra', fr:'Pétra', de:'Petra', el:'Πέτρα', it:'Petra' },
  Santorini: { bg:'Санторини', en:'Santorini', es:'Santorini', fr:'Santorin', de:'Santorin', el:'Σαντορίνη', it:'Santorini' },
};

const DESTINATION_ALIAS_TO_KEY = Object.entries(DESTINATION_NAME_I18N).reduce((acc, [key, names]) => {
  [key, ...Object.values(names)].forEach(name => { if (name) acc[String(name).toLowerCase()] = key; });
  return acc;
}, {});

const DESTINATION_DETAIL_I18N = {
  'New Zealand': {
    weather:{ bg:'Умерен океански', en:'Temperate oceanic', es:'Oceánico templado', fr:'Océanique tempéré', de:'Gemäßigt ozeanisch', el:'Εύκρατο ωκεάνιο', it:'Oceanico temperato' },
    language:{ bg:'Английски/Маори', en:'English/Māori', es:'Inglés/Maorí', fr:'Anglais/Maori', de:'Englisch/Māori', el:'Αγγλικά/Μαορί', it:'Inglese/Maori' },
    facts:{
      bg:['🎬 Там е сниман „Властелинът на пръстените“','🥝 Единствената страна с птицата киви','🌋 Активен вулкан Вакаари','🏉 Родина на All Blacks'],
      en:['🎬 Filmed Lord of the Rings','🥝 Only country with kiwi bird','🌋 Active Whakaari volcano','🏉 Home of the All Blacks'],
      es:['🎬 Allí se filmó El Señor de los Anillos','🥝 Único país con el ave kiwi','🌋 Volcán Whakaari activo','🏉 Hogar de los All Blacks'],
      fr:['🎬 Le Seigneur des Anneaux y a été tourné','🥝 Seul pays avec l’oiseau kiwi','🌋 Volcan Whakaari actif','🏉 Pays des All Blacks'],
      de:['🎬 Drehort von Der Herr der Ringe','🥝 Einziges Land mit dem Kiwi-Vogel','🌋 Aktiver Vulkan Whakaari','🏉 Heimat der All Blacks'], el:['🎬 Εκεί γυρίστηκε ο «Άρχοντας των Δαχτυλιδιών»','🥝 Η μόνη χώρα με το πουλί κίουι','🌋 Ενεργό ηφαίστειο Βακαάρι','🏉 Πατρίδα των All Blacks'], it:['🎬 Qui è stato girato il Signore degli Anelli','🥝 Unico paese con l\'uccello kiwi','🌋 Vulcano attivo Whakaari','🏉 Patria degli All Blacks'],
    },
  },
  Prague: {
    weather:{ bg:'Умерен континентален', en:'Temperate continental', es:'Continental templado', fr:'Continental tempéré', de:'Gemäßigt kontinental', el:'Εύκρατο ηπειρωτικό', it:'Continentale temperato' },
    language:{ bg:'Чешки', en:'Czech', es:'Checo', fr:'Tchèque', de:'Tschechisch', el:'Τσεχικά', it:'Ceco' },
    facts:{ bg:['🍺 Най-много бира на глава в света','🌉 Карловият мост е от 1357 г.','🏰 Пражкият замък е сред най-големите в света','👻 Популярни нощни турове с легенди'], en:['🍺 Most beer per capita','🌉 Charles Bridge dates to 1357','🏰 Prague Castle is among the world’s largest','👻 Popular ghost night tours'], es:['🍺 Mayor consumo de cerveza per cápita','🌉 El Puente de Carlos data de 1357','🏰 El Castillo de Praga está entre los más grandes del mundo','👻 Tours nocturnos de leyendas muy populares'], fr:['🍺 Plus forte consommation de bière par habitant','🌉 Le pont Charles date de 1357','🏰 Le château de Prague compte parmi les plus grands du monde','👻 Visites nocturnes de légendes très populaires'], de:['🍺 Höchster Bierkonsum pro Kopf','🌉 Die Karlsbrücke stammt aus dem Jahr 1357','🏰 Die Prager Burg zählt zu den größten der Welt','👻 Beliebte nächtliche Legendentouren'], el:['🍺 Η μεγαλύτερη κατανάλωση μπύρας κατά κεφαλή','🌉 Η γέφυρα Κάρολου χρονολογείται από το 1357','🏰 Το κάστρο της Πράγας είναι από τα μεγαλύτερα στον κόσμο','👻 Δημοφιλείς νυχτερινές περιηγήσεις με θρύλους'], it:['🍺 Il maggior consumo di birra pro capite','🌉 Il Ponte Carlo risale al 1357','🏰 Il Castello di Praga è tra i più grandi al mondo','👻 Popolari tour notturni sulle leggende'] },
  },
  Tuscany: {
    weather:{ bg:'Средиземноморски', en:'Mediterranean', es:'Mediterráneo', fr:'Méditerranéen', de:'Mediterran', el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ bg:'Италиански', en:'Italian', es:'Italiano', fr:'Italien', de:'Italienisch', el:'Ιταλικά', it:'Italiano' },
    facts:{ bg:['🍷 Регионът Кианти е символ на тосканското вино','🌻 Хълмове с кипариси и слънчогледи','🎨 Люлка на Ренесанса','🏰 Средновековни градчета по върховете'], en:['🍷 Chianti wine region','🌻 Sunflower and cypress hills','🎨 Birthplace of the Renaissance','🏰 Medieval hilltop towns'], es:['🍷 Región vinícola de Chianti','🌻 Colinas de girasoles y cipreses','🎨 Cuna del Renacimiento','🏰 Pueblos medievales en colinas'], fr:['🍷 Région viticole du Chianti','🌻 Collines de tournesols et de cyprès','🎨 Berceau de la Renaissance','🏰 Villages médiévaux perchés'], de:['🍷 Weinregion Chianti','🌻 Hügel mit Sonnenblumen und Zypressen','🎨 Wiege der Renaissance','🏰 Mittelalterliche Orte auf Hügeln'], el:['🍷 Η περιοχή Κιάντι είναι σύμβολο του τοσκανικού κρασιού','🌻 Λόφοι με κυπαρίσσια και ηλιοτρόπια','🎨 Γενέτειρα της Αναγέννησης','🏰 Μεσαιωνικές πόλεις σε λόφους'], it:['🍷 La regione del Chianti è simbolo del vino toscano','🌻 Colline di cipressi e girasoli','🎨 Culla del Rinascimento','🏰 Borghi medievali sulle colline'] },
  },
  Maldives: {
    weather:{ bg:'Тропически', en:'Tropical', es:'Tropical', fr:'Tropical', de:'Tropisch', el:'Τροπικό', it:'Tropicale' },
    language:{ bg:'Дивехи', en:'Dhivehi', es:'Divehi', fr:'Maldivien', de:'Dhivehi', el:'Ντιβέχι', it:'Dhivehi' },
    facts:{ bg:['🏝️ 1192 острова','🐠 Биолуминесцентни плажове','🦈 Китови акули целогодишно','🌊 Най-ниската страна в света'], en:['🏝️ 1192 islands','🐠 Bioluminescent beaches','🦈 Whale sharks year-round','🌊 Lowest country in the world'], es:['🏝️ 1192 islas','🐠 Playas bioluminiscentes','🦈 Tiburones ballena todo el año','🌊 El país más bajo del mundo'], fr:['🏝️ 1192 îles','🐠 Plages bioluminescentes','🦈 Requins-baleines toute l’année','🌊 Pays le plus bas du monde'], de:['🏝️ 1192 Inseln','🐠 Biolumineszente Strände','🦈 Walhaie das ganze Jahr','🌊 Niedrigstes Land der Welt'], el:['🏝️ 1192 νησιά','🐠 Βιοφωσφορίζουσες παραλίες','🦈 Καρχαρίες φάλαινες όλο τον χρόνο','🌊 Η χαμηλότερη χώρα στον κόσμο'], it:['🏝️ 1192 isole','🐠 Spiagge bioluminescenti','🦈 Squali balena tutto l\'anno','🌊 Il paese più basso del mondo'] },
  },
  Kyoto: {
    weather:{ bg:'Умерен с 4 сезона', en:'Temperate, four seasons', es:'Templado, cuatro estaciones', fr:'Tempéré, quatre saisons', de:'Gemäßigt, vier Jahreszeiten', el:'Εύκρατο, τέσσερις εποχές', it:'Temperato, quattro stagioni' },
    language:{ bg:'Японски', en:'Japanese', es:'Japonés', fr:'Japonais', de:'Japanisch', el:'Ιαπωνικά', it:'Giapponese' },
    facts:{ bg:['⛩️ Над 1600 будистки храма','🌸 Сакурата цъфти през март-април','🍵 Родина на чаената церемония','🦌 Свещени елени в близката Нара'], en:['⛩️ Over 1600 Buddhist temples','🌸 Cherry blossom season: March-April','🍵 Birthplace of the tea ceremony','🦌 Sacred deer in nearby Nara'], es:['⛩️ Más de 1600 templos budistas','🌸 Temporada de sakura: marzo-abril','🍵 Cuna de la ceremonia del té','🦌 Ciervos sagrados en la cercana Nara'], fr:['⛩️ Plus de 1600 temples bouddhistes','🌸 Saison des cerisiers: mars-avril','🍵 Berceau de la cérémonie du thé','🦌 Cerfs sacrés à Nara toute proche'], de:['⛩️ Über 1600 buddhistische Tempel','🌸 Kirschblüte: März-April','🍵 Ursprung der Teezeremonie','🦌 Heilige Hirsche im nahen Nara'], el:['⛩️ Πάνω από 1600 βουδιστικοί ναοί','🌸 Εποχή ανθοφορίας κερασιάς: Μάρτιος-Απρίλιος','🍵 Γενέτειρα της τελετής του τσαγιού','🦌 Ιερά ελάφια στην κοντινή Νάρα'], it:['⛩️ Oltre 1600 templi buddisti','🌸 Stagione dei ciliegi: marzo-aprile','🍵 Culla della cerimonia del tè','🦌 Cervi sacri nella vicina Nara'] },
  },
  'Machu Picchu': {
    weather:{ bg:'Умерен', en:'Temperate', es:'Templado', fr:'Tempéré', de:'Gemäßigt', el:'Εύκρατο', it:'Temperato' },
    language:{ bg:'Испански/Кечуа', en:'Spanish/Quechua', es:'Español/Quechua', fr:'Espagnol/Quechua', de:'Spanisch/Quechua', el:'Ισπανικά/Κέτσουα', it:'Spagnolo/Quechua' },
    facts:{ bg:['🏔️ Изгубеният град на инките','🌿 Екосистема на облачна гора','⭐ Едно от новите седем чудеса','🦙 Лами се разхождат из руините'], en:['🏔️ Lost city of the Incas','🌿 Cloud forest ecosystem','⭐ New Seven Wonders site','🦙 Llamas roam the ruins'], es:['🏔️ Ciudad perdida de los incas','🌿 Ecosistema de bosque nuboso','⭐ Una de las nuevas siete maravillas','🦙 Llamas recorren las ruinas'], fr:['🏔️ Cité perdue des Incas','🌿 Écosystème de forêt de nuages','⭐ Une des nouvelles sept merveilles','🦙 Des lamas parcourent les ruines'], de:['🏔️ Verlorene Stadt der Inka','🌿 Nebelwald-Ökosystem','⭐ Eines der neuen sieben Weltwunder','🦙 Lamas laufen durch die Ruinen'], el:['🏔️ Η χαμένη πόλη των Ίνκας','🌿 Οικοσύστημα νεφοδάσους','⭐ Ένα από τα νέα επτά θαύματα','🦙 Λάμα περιφέρονται στα ερείπια'], it:['🏔️ Città perduta degli Inca','🌿 Ecosistema di foresta nebulare','⭐ Una delle nuove sette meraviglie','🦙 I lama vagano tra le rovine'] },
  },
  Havana: {
    weather:{ bg:'Тропически', en:'Tropical', es:'Tropical', fr:'Tropical', de:'Tropisch', el:'Τροπικό', it:'Tropicale' },
    language:{ bg:'Испански', en:'Spanish', es:'Español', fr:'Espagnol', de:'Spanisch', el:'Ισπανικά', it:'Spagnolo' },
    facts:{ bg:['🚗 Американски ретро коли от 50-те','🎺 Родно място на салса и румба','🏛️ Старата Хавана е под закрилата на ЮНЕСКО','🍹 Дом на мохито и дайкири'], en:['🚗 1950s American classic cars','🎺 Birthplace of salsa and rumba','🏛️ UNESCO Old Havana','🍹 Home of mojito and daiquiri'], es:['🚗 Coches clásicos estadounidenses de los años 50','🎺 Cuna de la salsa y la rumba','🏛️ La Habana Vieja es Patrimonio UNESCO','🍹 Hogar del mojito y el daiquirí'], fr:['🚗 Voitures américaines classiques des années 1950','🎺 Berceau de la salsa et de la rumba','🏛️ Vieille Havane classée UNESCO','🍹 Patrie du mojito et du daiquiri'], de:['🚗 Amerikanische Oldtimer aus den 1950ern','🎺 Geburtsort von Salsa und Rumba','🏛️ Alt-Havanna ist UNESCO-Welterbe','🍹 Heimat von Mojito und Daiquiri'], el:['🚗 Αμερικανικά κλασικά αυτοκίνητα της δεκαετίας του \'50','🎺 Γενέτειρα της salsa και της rumba','🏛️ Η Παλιά Αβάνα είναι μνημείο της UNESCO','🍹 Πατρίδα του mojito και του daiquiri'], it:['🚗 Auto d\'epoca americane degli anni \'50','🎺 Culla della salsa e della rumba','🏛️ L\'Avana Vecchia è patrimonio UNESCO','🍹 Patria del mojito e del daiquiri'] },
  },
  Lisbon: {
    weather:{ bg:'Средиземноморски', en:'Mediterranean', es:'Mediterráneo', fr:'Méditerranéen', de:'Mediterran', el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ bg:'Португалски', en:'Portuguese', es:'Portugués', fr:'Portugais', de:'Portugiesisch', el:'Πορτογαλικά', it:'Portoghese' },
    facts:{ bg:['🚃 Легендарни трамваи по хълмовете','🎵 Фадо е наследство на ЮНЕСКО','🐟 Стотици рецепти с бакалао','🌉 Мостът напомня Голдън Гейт'], en:['🚃 Legendary hill trams','🎵 Fado is UNESCO heritage','🐟 Hundreds of bacalhau recipes','🌉 Bridge reminiscent of Golden Gate'], es:['🚃 Tranvías legendarios por las colinas','🎵 El fado es patrimonio UNESCO','🐟 Cientos de recetas de bacalao','🌉 Puente que recuerda al Golden Gate'], fr:['🚃 Tramways légendaires sur les collines','🎵 Le fado est classé UNESCO','🐟 Des centaines de recettes de bacalhau','🌉 Pont rappelant le Golden Gate'], de:['🚃 Legendäre Straßenbahnen auf Hügeln','🎵 Fado gehört zum UNESCO-Erbe','🐟 Hunderte Bacalhau-Rezepte','🌉 Brücke erinnert an die Golden Gate Bridge'], el:['🚃 Θρυλικά τραμ στους λόφους','🎵 Το φάδο είναι κληρονομιά της UNESCO','🐟 Εκατοντάδες συνταγές με μπακαλιάρο','🌉 Η γέφυρα θυμίζει Γκόλντεν Γκέιτ'], it:['🚃 Leggendari tram sulle colline','🎵 Il fado è patrimonio UNESCO','🐟 Centinaia di ricette di baccalà','🌉 Il ponte ricorda il Golden Gate'] },
  },
  Petra: {
    weather:{ bg:'Пустинен', en:'Desert', es:'Desértico', fr:'Désertique', de:'Wüstenklima', el:'Ερημικό', it:'Desertico' },
    language:{ bg:'Арабски', en:'Arabic', es:'Árabe', fr:'Arabe', de:'Arabisch', el:'Αραβικά', it:'Arabo' },
    facts:{ bg:['🌹 Розово-червен град, издялан в скала','🐪 Древна столица на набатеите','⭐ Едно от новите седем чудеса','🌅 Най-красива при златна светлина'], en:['🌹 Rose-red city carved in rock','🐪 Ancient Nabataean capital','⭐ New Seven Wonders site','🌅 Best in golden light'], es:['🌹 Ciudad rosada tallada en la roca','🐪 Antigua capital nabatea','⭐ Una de las nuevas siete maravillas','🌅 Mejor con luz dorada'], fr:['🌹 Cité rose taillée dans la roche','🐪 Ancienne capitale nabatéenne','⭐ Une des nouvelles sept merveilles','🌅 Superbe à la lumière dorée'], de:['🌹 Rosarote Felsenstadt','🐪 Alte Hauptstadt der Nabatäer','⭐ Eines der neuen sieben Weltwunder','🌅 Am schönsten im goldenen Licht'], el:['🌹 Ροζ-κόκκινη πόλη σκαλισμένη στον βράχο','🐪 Αρχαία πρωτεύουσα των Ναβαταίων','⭐ Ένα από τα νέα επτά θαύματα','🌅 Πιο όμορφη στο χρυσό φως'], it:['🌹 Città rosa scolpita nella roccia','🐪 Antica capitale dei Nabatei','⭐ Una delle nuove sette meraviglie','🌅 Più bella con la luce dorata'] },
  },
  Santorini: {
    weather:{ bg:'Средиземноморски', en:'Mediterranean', es:'Mediterráneo', fr:'Méditerranéen', de:'Mediterran', el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ bg:'Гръцки', en:'Greek', es:'Griego', fr:'Grec', de:'Griechisch', el:'Ελληνικά', it:'Greco' },
    facts:{ bg:['🌅 Калдера — вулканична лагуна','🍷 Известно с бялото вино Асиртико','🏛️ Над 3500 години история','📸 Оя е сред най-сниманите места'], en:['🌅 Caldera volcanic lagoon','🍷 Famous for white Assyrtiko wine','🏛️ Over 3500 years of history','📸 Oia is highly photographed'], es:['🌅 Caldera, laguna volcánica','🍷 Famosa por el vino blanco Assyrtiko','🏛️ Más de 3500 años de historia','📸 Oia es uno de los lugares más fotografiados'], fr:['🌅 Caldeira, lagune volcanique','🍷 Célèbre pour le vin blanc Assyrtiko','🏛️ Plus de 3500 ans d’histoire','📸 Oia compte parmi les lieux les plus photographiés'], de:['🌅 Caldera, vulkanische Lagune','🍷 Berühmt für weißen Assyrtiko-Wein','🏛️ Über 3500 Jahre Geschichte','📸 Oia zählt zu den meistfotografierten Orten'], el:['🌅 Καλντέρα — ηφαιστειακή λιμνοθάλασσα','🍷 Διάσημη για το λευκό κρασί Ασύρτικο','🏛️ Πάνω από 3500 χρόνια ιστορίας','📸 Η Οία είναι από τα πιο φωτογραφημένα μέρη'], it:['🌅 Caldera, laguna vulcanica','🍷 Famosa per il vino bianco Assyrtiko','🏛️ Oltre 3500 anni di storia','📸 Oia è tra i luoghi più fotografati'] },
  },
  Bali: {
    weather:{ bg:'Тропически', en:'Tropical', es:'Tropical', fr:'Tropical', de:'Tropisch', el:'Τροπικό', it:'Tropicale' },
    language:{ bg:'Балийски/Индонезийски', en:'Balinese/Indonesian', es:'Balinés/Indonesio', fr:'Balinais/Indonésien', de:'Balinesisch/Indonesisch', el:'Μπαλινεζικά/Ινδονησιακά', it:'Balinese/Indonesiano' },
    facts:{ bg:['🌺 Остров на боговете','🏄 Световна столица на сърфа','🕍 Хиляди индуистки храмове','🌿 Оризови тераси на ЮНЕСКО'], en:['🌺 Island of the Gods','🏄 World surfing capital','🕍 Thousands of Hindu temples','🌿 UNESCO rice terraces'], es:['🌺 Isla de los Dioses','🏄 Capital mundial del surf','🕍 Miles de templos hindúes','🌿 Terrazas de arroz UNESCO'], fr:['🌺 Île des Dieux','🏄 Capitale mondiale du surf','🕍 Des milliers de temples hindous','🌿 Rizières classées UNESCO'], de:['🌺 Insel der Götter','🏄 Welthauptstadt des Surfens','🕍 Tausende hinduistische Tempel','🌿 UNESCO-Reisterrassen'], el:['🌺 Το νησί των θεών','🏄 Παγκόσμια πρωτεύουσα του σερφ','🕍 Χιλιάδες ινδουιστικοί ναοί','🌿 Αναβαθμίδες ρυζιού της UNESCO'], it:['🌺 Isola degli dei','🏄 Capitale mondiale del surf','🕍 Migliaia di templi indù','🌿 Terrazze di riso UNESCO'] },
  },
  Patagonia: {
    weather:{ bg:'Субполярен', en:'Subpolar', es:'Subpolar', fr:'Subpolaire', de:'Subpolar', el:'Υποπολικό', it:'Subpolare' },
    language:{ bg:'Испански', en:'Spanish', es:'Español', fr:'Espagnol', de:'Spanisch', el:'Ισπανικά', it:'Spagnolo' },
    facts:{ bg:['🏔️ Дом на Торес дел Пайне','🐧 Колонии от магеланови пингвини','💧 Ледникови езера и чиста вода','🦅 Андски кондор с огромен размах'], en:['🏔️ Home of Torres del Paine','🐧 Magellanic penguin colonies','💧 Glacial lakes and pure water','🦅 Andean condors with huge wingspans'], es:['🏔️ Hogar de Torres del Paine','🐧 Colonias de pingüinos magallánicos','💧 Lagos glaciares y agua pura','🦅 Cóndores andinos de gran envergadura'], fr:['🏔️ Terre du Torres del Paine','🐧 Colonies de manchots de Magellan','💧 Lacs glaciaires et eau pure','🦅 Condors des Andes à grande envergure'], de:['🏔️ Heimat von Torres del Paine','🐧 Kolonien von Magellanpinguinen','💧 Gletscherseen und reines Wasser','🦅 Andenkondore mit großer Spannweite'], el:['🏔️ Πατρίδα του Τόρες ντελ Πάινε','🐧 Αποικίες πιγκουίνων Μαγγελάνου','💧 Παγετωνικές λίμνες και καθαρό νερό','🦅 Κόνδορες των Άνδεων με τεράστιο άνοιγμα φτερών'], it:['🏔️ Terra delle Torres del Paine','🐧 Colonie di pinguini di Magellano','💧 Laghi glaciali e acqua pura','🦅 Condor delle Ande dall\'enorme apertura alare'] },
  },
  Marrakech: {
    weather:{ bg:'Полупустинен', en:'Semi-desert', es:'Semidesértico', fr:'Semi-désertique', de:'Halbwüstenklima', el:'Ημιερημικό', it:'Semidesertico' },
    language:{ bg:'Арабски/Берберски', en:'Arabic/Berber', es:'Árabe/Bereber', fr:'Arabe/Berbère', de:'Arabisch/Berberisch', el:'Αραβικά/Βερβερικά', it:'Arabo/Berbero' },
    facts:{ bg:['🏺 Джема ел-Фна е площад на ЮНЕСКО','🌹 Градините Мажорел са синьо чудо','🛍️ Медина от XI век','🐪 Врати към Сахара'], en:['🏺 Djemaa el-Fna is a UNESCO square','🌹 Majorelle Gardens are a blue wonder','🛍️ 11th-century medina','🐪 Gateway to Sahara trips'], es:['🏺 Jemaa el-Fna es una plaza UNESCO','🌹 Los Jardines Majorelle son una maravilla azul','🛍️ Medina del siglo XI','🐪 Puerta a excursiones al Sáhara'], fr:['🏺 Jemaa el-Fna est une place UNESCO','🌹 Les jardins Majorelle sont une merveille bleue','🛍️ Médina du XIe siècle','🐪 Porte vers les excursions au Sahara'], de:['🏺 Djemaa el-Fna ist ein UNESCO-Platz','🌹 Die Majorelle-Gärten sind ein blaues Wunder','🛍️ Medina aus dem 11. Jahrhundert','🐪 Tor zu Sahara-Ausflügen'], el:['🏺 Η πλατεία Τζεμάα ελ-Φνα είναι μνημείο της UNESCO','🌹 Οι κήποι Μαζορέλ είναι ένα μπλε θαύμα','🛍️ Μεδίνα του 11ου αιώνα','🐪 Πύλη για εκδρομές στη Σαχάρα'], it:['🏺 Piazza Jemaa el-Fna è patrimonio UNESCO','🌹 I Giardini Majorelle sono una meraviglia blu','🛍️ Medina dell\'XI secolo','🐪 Porta per le escursioni nel Sahara'] },
  },
  Iceland: {
    weather:{ bg:'Субарктически', en:'Subarctic', es:'Subártico', fr:'Subarctique', de:'Subarktisch', el:'Υποαρκτικό', it:'Subartico' },
    language:{ bg:'Исландски', en:'Icelandic', es:'Islandés', fr:'Islandais', de:'Isländisch', el:'Ισλανδικά', it:'Islandese' },
    facts:{ bg:['🌋 Страната на огъня и леда','♨️ Над 100 вулкана','☀️ Среднощно слънце през лятото','🐋 Популярни турове за китове'], en:['🌋 Land of fire and ice','♨️ More than 100 volcanoes','☀️ Midnight sun in summer','🐋 Popular whale-watching tours'], es:['🌋 Tierra de fuego y hielo','♨️ Más de 100 volcanes','☀️ Sol de medianoche en verano','🐋 Tours populares para ver ballenas'], fr:['🌋 Terre de feu et de glace','♨️ Plus de 100 volcans','☀️ Soleil de minuit en été','🐋 Excursions baleines populaires'], de:['🌋 Land aus Feuer und Eis','♨️ Mehr als 100 Vulkane','☀️ Mitternachtssonne im Sommer','🐋 Beliebte Walbeobachtungstouren'], el:['🌋 Η χώρα της φωτιάς και του πάγου','♨️ Πάνω από 100 ηφαίστεια','☀️ Μεσονύχτιος ήλιος το καλοκαίρι','🐋 Δημοφιλείς εκδρομές παρατήρησης φαλαινών'], it:['🌋 Terra di fuoco e ghiaccio','♨️ Oltre 100 vulcani','☀️ Sole di mezzanotte in estate','🐋 Tour per l\'avvistamento delle balene molto popolari'] },
  },
  Dubai: {
    weather:{ bg:'Пустинен', en:'Desert', es:'Desértico', fr:'Désertique', de:'Wüstenklima', el:'Ερημικό', it:'Desertico' },
    language:{ bg:'Арабски', en:'Arabic', es:'Árabe', fr:'Arabe', de:'Arabisch', el:'Αραβικά', it:'Arabo' },
    facts:{ bg:['🏗️ Бурж Халифа е 828 м','🎿 Ски писта в мол','🌡️ Много горещо лято','🛳️ Изкуствени острови Палм'], en:['🏗️ Burj Khalifa is 828m tall','🎿 Ski slope inside a mall','🌡️ Very hot summers','🛳️ Palm artificial islands'], es:['🏗️ Burj Khalifa mide 828 m','🎿 Pista de esquí dentro de un centro comercial','🌡️ Veranos muy calurosos','🛳️ Islas artificiales Palm'], fr:['🏗️ Burj Khalifa mesure 828 m','🎿 Piste de ski dans un centre commercial','🌡️ Étés très chauds','🛳️ Îles artificielles Palm'], de:['🏗️ Burj Khalifa ist 828 m hoch','🎿 Skipiste in einem Einkaufszentrum','🌡️ Sehr heiße Sommer','🛳️ Künstliche Palm-Inseln'], el:['🏗️ Το Μπουρτζ Χαλίφα είναι 828 μ.','🎿 Πίστα σκι μέσα σε εμπορικό κέντρο','🌡️ Πολύ ζεστά καλοκαίρια','🛳️ Τεχνητά νησιά Palm'], it:['🏗️ Il Burj Khalifa è alto 828 m','🎿 Pista da sci dentro un centro commerciale','🌡️ Estati molto calde','🛳️ Isole artificiali Palm'] },
  },
  'Amalfi Coast': {
    weather:{ bg:'Средиземноморски', en:'Mediterranean', es:'Mediterráneo', fr:'Méditerranéen', de:'Mediterran', el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ bg:'Италиански', en:'Italian', es:'Italiano', fr:'Italien', de:'Italienisch', el:'Ιταλικά', it:'Italiano' },
    facts:{ bg:['🚤 Някои села са най-лесни с лодка','🍋 Световноизвестно лимончело','🏰 Средновековни наблюдателни кули','🌊 Синьо Тиренско море'], en:['🚤 Some villages are easiest by boat','🍋 World-famous limoncello','🏰 Medieval watchtowers','🌊 Blue Tyrrhenian Sea'], es:['🚤 Algunos pueblos se visitan mejor en barco','🍋 Limoncello famoso en todo el mundo','🏰 Torres medievales de vigilancia','🌊 Mar Tirreno azul'], fr:['🚤 Certains villages se visitent mieux en bateau','🍋 Limoncello mondialement célèbre','🏰 Tours de guet médiévales','🌊 Mer Tyrrhénienne bleue'], de:['🚤 Einige Dörfer erreicht man am besten per Boot','🍋 Weltberühmter Limoncello','🏰 Mittelalterliche Wachtürme','🌊 Blaues Tyrrhenisches Meer'], el:['🚤 Ορισμένα χωριά προσεγγίζονται πιο εύκολα με βάρκα','🍋 Παγκοσμίως διάσημο λιμοντσέλο','🏰 Μεσαιωνικοί πύργοι φρούρησης','🌊 Γαλάζια Τυρρηνική Θάλασσα'], it:['🚤 Alcuni borghi si raggiungono meglio in barca','🍋 Limoncello famoso in tutto il mondo','🏰 Torri di guardia medievali','🌊 Il blu Mar Tirreno'] },
  },
  Thailand: {
    weather:{ bg:'Тропически', en:'Tropical', es:'Tropical', fr:'Tropical', de:'Tropisch', el:'Τροπικό', it:'Tropicale' },
    language:{ bg:'Тайландски', en:'Thai', es:'Tailandés', fr:'Thaï', de:'Thailändisch', el:'Ταϊλανδικά', it:'Thailandese' },
    facts:{ bg:['🙏 Страната на усмивките','🐘 Етични светилища за слонове','🌴 Хиляди острови','🍜 Рай за улична храна'], en:['🙏 Land of smiles','🐘 Ethical elephant sanctuaries','🌴 Thousands of islands','🍜 Street-food paradise'], es:['🙏 Tierra de sonrisas','🐘 Santuarios éticos de elefantes','🌴 Miles de islas','🍜 Paraíso de comida callejera'], fr:['🙏 Pays du sourire','🐘 Sanctuaires éthiques pour éléphants','🌴 Des milliers d’îles','🍜 Paradis de la cuisine de rue'], de:['🙏 Land des Lächelns','🐘 Ethische Elefantenschutzgebiete','🌴 Tausende Inseln','🍜 Paradies für Streetfood'], el:['🙏 Η χώρα των χαμόγελων','🐘 Ηθικά καταφύγια ελεφάντων','🌴 Χιλιάδες νησιά','🍜 Παράδεισος street food'], it:['🙏 Terra dei sorrisi','🐘 Santuari etici per elefanti','🌴 Migliaia di isole','🍜 Paradiso dello street food'] },
  },
  'Safari Kenya': {
    weather:{ bg:'Тропически', en:'Tropical', es:'Tropical', fr:'Tropical', de:'Tropisch', el:'Τροπικό', it:'Tropicale' },
    language:{ bg:'Суахили/Английски', en:'Swahili/English', es:'Suajili/Inglés', fr:'Swahili/Anglais', de:'Suaheli/Englisch', el:'Σουαχίλι/Αγγλικά', it:'Swahili/Inglese' },
    facts:{ bg:['🦁 Голямата петорка в Масай Мара','🦩 Езера с фламинго','🏔️ Планината Кения е 5199 м','🌍 Люлка на човечеството'], en:['🦁 Big Five in Masai Mara','🦩 Flamingo lakes','🏔️ Mount Kenya is 5199m','🌍 Cradle of humanity'], es:['🦁 Los Cinco Grandes en Masái Mara','🦩 Lagos con flamencos','🏔️ El Monte Kenia mide 5199 m','🌍 Cuna de la humanidad'], fr:['🦁 Big Five au Masai Mara','🦩 Lacs de flamants roses','🏔️ Le mont Kenya culmine à 5199 m','🌍 Berceau de l’humanité'], de:['🦁 Big Five in der Masai Mara','🦩 Flamingoseen','🏔️ Mount Kenya ist 5199 m hoch','🌍 Wiege der Menschheit'], el:['🦁 Η μεγάλη πεντάδα στη Μασάι Μάρα','🦩 Λίμνες με φλαμίνγκο','🏔️ Το όρος Κένυα είναι 5199 μ.','🌍 Κοιτίδα της ανθρωπότητας'], it:['🦁 I Big Five nel Masai Mara','🦩 Laghi dei fenicotteri','🏔️ Il Monte Kenya è alto 5199 m','🌍 Culla dell\'umanità'] },
  },
  'Swiss Alps': {
    weather:{ bg:'Алпийски', en:'Alpine', es:'Alpino', fr:'Alpin', de:'Alpin', el:'Αλπικό', it:'Alpino' },
    language:{ bg:'Немски/Френски', en:'German/French', es:'Alemán/Francés', fr:'Allemand/Français', de:'Deutsch/Französisch', el:'Γερμανικά/Γαλλικά', it:'Tedesco/Francese' },
    facts:{ bg:['🏔️ Върхове над 4000 м','🍫 Шоколад и сирена','⌚ Луксозно часовникарство','🚞 Живописни планински влакове'], en:['🏔️ Peaks above 4000m','🍫 Chocolate and cheeses','⌚ Luxury watchmaking','🚞 Scenic mountain trains'], es:['🏔️ Picos de más de 4000 m','🍫 Chocolate y quesos','⌚ Relojería de lujo','🚞 Trenes panorámicos de montaña'], fr:['🏔️ Sommets de plus de 4000 m','🍫 Chocolat et fromages','⌚ Horlogerie de luxe','🚞 Trains panoramiques de montagne'], de:['🏔️ Gipfel über 4000 m','🍫 Schokolade und Käse','⌚ Luxusuhrmacherei','🚞 Malerische Bergbahnen'], el:['🏔️ Κορυφές πάνω από 4000 μ.','🍫 Σοκολάτα και τυριά','⌚ Πολυτελής ωρολογοποιία','🚞 Γραφικά ορεινά τρένα'], it:['🏔️ Vette oltre i 4000 m','🍫 Cioccolato e formaggi','⌚ Orologeria di lusso','🚞 Pittoreschi treni di montagna'] },
  },
  Amsterdam: {
    weather:{ bg:'Умерен морски', en:'Temperate oceanic', es:'Oceánico templado', fr:'Océanique tempéré', de:'Gemäßigt ozeanisch', el:'Εύκρατο ωκεάνιο', it:'Oceanico temperato' },
    language:{ bg:'Нидерландски', en:'Dutch', es:'Neerlandés', fr:'Néerlandais', de:'Niederländisch', el:'Ολλανδικά', it:'Olandese' },
    facts:{ bg:['🚲 Повече велосипеди отколкото хора','🌷 Милиони лалета в Кьокенхоф','🏠 Една от най-тесните къщи е около 2 м','🎨 Родина на Рембранд и Ван Гог'], en:['🚲 More bikes than people','🌷 Millions of tulips in Keukenhof','🏠 One narrow house is about 2m wide','🎨 Homeland of Rembrandt and Van Gogh'], es:['🚲 Más bicicletas que personas','🌷 Millones de tulipanes en Keukenhof','🏠 Una casa estrecha mide unos 2 m','🎨 Tierra de Rembrandt y Van Gogh'], fr:['🚲 Plus de vélos que d’habitants','🌷 Millions de tulipes à Keukenhof','🏠 Une maison étroite mesure environ 2 m','🎨 Terre de Rembrandt et Van Gogh'], de:['🚲 Mehr Fahrräder als Menschen','🌷 Millionen Tulpen in Keukenhof','🏠 Ein schmales Haus ist etwa 2 m breit','🎨 Heimat von Rembrandt und Van Gogh'], el:['🚲 Περισσότερα ποδήλατα από ανθρώπους','🌷 Εκατομμύρια τουλίπες στο Κόικενχοφ','🏠 Ένα στενό σπίτι είναι περίπου 2 μ.','🎨 Πατρίδα του Ρέμπραντ και του Βαν Γκογκ'], it:['🚲 Più biciclette che abitanti','🌷 Milioni di tulipani a Keukenhof','🏠 Una casa stretta è larga circa 2 m','🎨 Patria di Rembrandt e Van Gogh'] },
  },
};

Object.assign(COUNTRY_I18N, {
  Greece:{ ...COUNTRY_I18N.Greece, el:'Ελλάδα', it:'Grecia' },
  Japan:{ ...COUNTRY_I18N.Japan, el:'Ιαπωνία', it:'Giappone' },
  Indonesia:{ ...COUNTRY_I18N.Indonesia, el:'Ινδονησία', it:'Indonesia' },
  Argentina:{ ...COUNTRY_I18N.Argentina, el:'Αργεντινή', it:'Argentina' },
  Morocco:{ ...COUNTRY_I18N.Morocco, el:'Μαρόκο', it:'Marocco' },
  Iceland:{ ...COUNTRY_I18N.Iceland, el:'Ισλανδία', it:'Islanda' },
  Maldives:{ ...COUNTRY_I18N.Maldives, el:'Μαλδίβες', it:'Maldive' },
  Portugal:{ ...COUNTRY_I18N.Portugal, el:'Πορτογαλία', it:'Portogallo' },
  Peru:{ ...COUNTRY_I18N.Peru, el:'Περού', it:'Perù' },
  UAE:{ ...COUNTRY_I18N.UAE, el:'ΗΑΕ', it:'EAU' },
  Italy:{ ...COUNTRY_I18N.Italy, el:'Ιταλία', it:'Italia' },
  'New Zealand':{ ...COUNTRY_I18N['New Zealand'], el:'Νέα Ζηλανδία', it:'Nuova Zelanda' },
  'Czech Republic':{ ...COUNTRY_I18N['Czech Republic'], el:'Τσεχία', it:'Repubblica Ceca' },
  Thailand:{ ...COUNTRY_I18N.Thailand, el:'Ταϊλάνδη', it:'Thailandia' },
  Kenya:{ ...COUNTRY_I18N.Kenya, el:'Κένυα', it:'Kenya' },
  Cuba:{ ...COUNTRY_I18N.Cuba, el:'Κούβα', it:'Cuba' },
  Switzerland:{ ...COUNTRY_I18N.Switzerland, el:'Ελβετία', it:'Svizzera' },
  Jordan:{ ...COUNTRY_I18N.Jordan, el:'Ιορδανία', it:'Giordania' },
  Netherlands:{ ...COUNTRY_I18N.Netherlands, el:'Ολλανδία', it:'Paesi Bassi' },
  Bulgaria:{ ...COUNTRY_I18N.Bulgaria, el:'Βουλγαρία', it:'Bulgaria' },
});

Object.assign(CITY_I18N, {
  'София': { ...CITY_I18N['София'], el:'Σόφια', it:'Sofia' },
  'Варна': { ...CITY_I18N['Варна'], el:'Βάρνα', it:'Varna' },
  'Пловдив': { ...CITY_I18N['Пловдив'], el:'Πλόβντιβ', it:'Plovdiv' },
  'Бургас': { ...CITY_I18N['Бургас'], el:'Μπουργκάς', it:'Burgas' },
  'Банско': { ...CITY_I18N['Банско'], el:'Μπάνσκο', it:'Bansko' },
});

Object.assign(DESTINATION_NAME_I18N, {
  'New Zealand': { ...DESTINATION_NAME_I18N['New Zealand'], el:'Νέα Ζηλανδία', it:'Nuova Zelanda' },
  Prague: { ...DESTINATION_NAME_I18N.Prague, el:'Πράγα', it:'Praga' },
  Tuscany: { ...DESTINATION_NAME_I18N.Tuscany, el:'Τοσκάνη', it:'Toscana' },
  Maldives: { ...DESTINATION_NAME_I18N.Maldives, el:'Μαλδίβες', it:'Maldive' },
  Kyoto: { ...DESTINATION_NAME_I18N.Kyoto, el:'Κιότο', it:'Kyoto' },
  'Machu Picchu': { ...DESTINATION_NAME_I18N['Machu Picchu'], el:'Μάτσου Πίτσου', it:'Machu Picchu' },
  Havana: { ...DESTINATION_NAME_I18N.Havana, el:'Αβάνα', it:'L’Avana' },
  Lisbon: { ...DESTINATION_NAME_I18N.Lisbon, el:'Λισαβόνα', it:'Lisbona' },
  Petra: { ...DESTINATION_NAME_I18N.Petra, el:'Πέτρα', it:'Petra' },
  Santorini: { ...DESTINATION_NAME_I18N.Santorini, el:'Σαντορίνη', it:'Santorini' },
});

const DESTINATION_DETAIL_EXTRA = {
  'New Zealand': {
    weather:{ el:'Εύκρατο ωκεάνιο', it:'Oceanico temperato' },
    language:{ el:'Αγγλικά/Μαορί', it:'Inglese/Māori' },
    facts:{ el:['🎬 Εκεί γυρίστηκε ο Άρχοντας των Δαχτυλιδιών','🥝 Η μόνη χώρα με το πτηνό κίουι','🌋 Ενεργό ηφαίστειο Whakaari','🏉 Πατρίδα των All Blacks'], it:['🎬 Qui è stato girato Il Signore degli Anelli','🥝 Unico paese con l’uccello kiwi','🌋 Vulcano attivo Whakaari','🏉 Casa degli All Blacks'] },
  },
  Prague: {
    weather:{ el:'Εύκρατο ηπειρωτικό', it:'Continentale temperato' },
    language:{ el:'Τσεχικά', it:'Ceco' },
    facts:{ el:['🍺 Η μεγαλύτερη κατανάλωση μπύρας ανά κάτοικο','🌉 Η Γέφυρα του Καρόλου χρονολογείται από το 1357','🏰 Το Κάστρο της Πράγας είναι από τα μεγαλύτερα στον κόσμο','👻 Δημοφιλείς νυχτερινές περιηγήσεις με θρύλους'], it:['🍺 Maggior consumo di birra pro capite','🌉 Il Ponte Carlo risale al 1357','🏰 Il Castello di Praga è tra i più grandi al mondo','👻 Tour notturni di leggende molto popolari'] },
  },
  Tuscany: {
    weather:{ el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ el:'Ιταλικά', it:'Italiano' },
    facts:{ el:['🍷 Περιοχή κρασιού Chianti','🌻 Λόφοι με ηλιοτρόπια και κυπαρίσσια','🎨 Κοιτίδα της Αναγέννησης','🏰 Μεσαιωνικά χωριά σε λόφους'], it:['🍷 Regione del vino Chianti','🌻 Colline con girasoli e cipressi','🎨 Culla del Rinascimento','🏰 Borghi medievali sulle colline'] },
  },
  Maldives: {
    weather:{ el:'Τροπικό', it:'Tropicale' },
    language:{ el:'Ντιβέχι', it:'Divehi' },
    facts:{ el:['🏝️ 1192 νησιά','🐠 Βιοφωταυγείς παραλίες','🦈 Φαλαινοκαρχαρίες όλο τον χρόνο','🌊 Η χαμηλότερη χώρα στον κόσμο'], it:['🏝️ 1192 isole','🐠 Spiagge bioluminescenti','🦈 Squali balena tutto l’anno','🌊 Il paese più basso del mondo'] },
  },
  Kyoto: {
    weather:{ el:'Εύκρατο, τέσσερις εποχές', it:'Temperato, quattro stagioni' },
    language:{ el:'Ιαπωνικά', it:'Giapponese' },
    facts:{ el:['⛩️ Πάνω από 1600 βουδιστικοί ναοί','🌸 Περίοδος sakura: Μάρτιος-Απρίλιος','🍵 Γενέτειρα της τελετής τσαγιού','🦌 Ιερά ελάφια στη γειτονική Νάρα'], it:['⛩️ Oltre 1600 templi buddisti','🌸 Sakura: marzo-aprile','🍵 Culla della cerimonia del tè','🦌 Cervi sacri nella vicina Nara'] },
  },
  'Machu Picchu': {
    weather:{ el:'Εύκρατο', it:'Temperato' },
    language:{ el:'Ισπανικά/Κέτσουα', it:'Spagnolo/Quechua' },
    facts:{ el:['🏔️ Η χαμένη πόλη των Ίνκας','🌿 Οικοσύστημα νεφοδάσους','⭐ Ένα από τα νέα επτά θαύματα','🦙 Λάμα περιφέρονται στα ερείπια'], it:['🏔️ Città perduta degli Inca','🌿 Ecosistema di foresta nebulosa','⭐ Una delle nuove sette meraviglie','🦙 I lama girano tra le rovine'] },
  },
  Havana: {
    weather:{ el:'Τροπικό', it:'Tropicale' },
    language:{ el:'Ισπανικά', it:'Spagnolo' },
    facts:{ el:['🚗 Κλασικά αμερικανικά αυτοκίνητα της δεκαετίας του 1950','🎺 Γενέτειρα της salsa και της rumba','🏛️ Η Παλιά Αβάνα είναι μνημείο UNESCO','🍹 Πατρίδα του mojito και daiquiri'], it:['🚗 Auto classiche americane anni ’50','🎺 Culla di salsa e rumba','🏛️ L’Avana Vecchia è UNESCO','🍹 Casa di mojito e daiquiri'] },
  },
  Lisbon: {
    weather:{ el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ el:'Πορτογαλικά', it:'Portoghese' },
    facts:{ el:['🚃 Θρυλικά τραμ στους λόφους','🎵 Το fado είναι κληρονομιά UNESCO','🐟 Εκατοντάδες συνταγές bacalhau','🌉 Γέφυρα που θυμίζει Golden Gate'], it:['🚃 Tram leggendari sulle colline','🎵 Il fado è patrimonio UNESCO','🐟 Centinaia di ricette di bacalhau','🌉 Ponte che ricorda il Golden Gate'] },
  },
  Petra: {
    weather:{ el:'Ερημικό', it:'Desertico' },
    language:{ el:'Αραβικά', it:'Arabo' },
    facts:{ el:['🌹 Ροδόχρωμη πόλη λαξευμένη στον βράχο','🐪 Αρχαία πρωτεύουσα των Ναβαταίων','⭐ Ένα από τα νέα επτά θαύματα','🌅 Υπέροχη στο χρυσό φως'], it:['🌹 Città rosa scolpita nella roccia','🐪 Antica capitale nabatea','⭐ Una delle nuove sette meraviglie','🌅 Bellissima con la luce dorata'] },
  },
  Santorini: {
    weather:{ el:'Μεσογειακό', it:'Mediterraneo' },
    language:{ el:'Ελληνικά', it:'Greco' },
    facts:{ el:['🌅 Καλντέρα, ηφαιστειακή λιμνοθάλασσα','🍷 Διάσημη για το λευκό Ασύρτικο','🏛️ Πάνω από 3500 χρόνια ιστορίας','📸 Η Οία είναι από τα πιο φωτογραφημένα μέρη'], it:['🌅 Caldera, laguna vulcanica','🍷 Famosa per il vino bianco Assyrtiko','🏛️ Oltre 3500 anni di storia','📸 Oia è tra i luoghi più fotografati'] },
  },
};

Object.entries(DESTINATION_DETAIL_EXTRA).forEach(([key, data]) => {
  if (!DESTINATION_DETAIL_I18N[key]) DESTINATION_DETAIL_I18N[key] = {};
  ['weather', 'language', 'facts'].forEach(field => {
    DESTINATION_DETAIL_I18N[key][field] = {
      ...(DESTINATION_DETAIL_I18N[key][field] || {}),
      ...(data[field] || {}),
    };
  });
});

function localizeCountry(country, lang) {
  if (!country) return country;
  return COUNTRY_I18N[country]?.[lang] || COUNTRY_I18N[country]?.en || country;
}

function destinationKeyFor(destOrName) {
  const values = typeof destOrName === 'string'
    ? [destOrName]
    : [destOrName?.name, destOrName?.nameEn, destOrName?.nameBg].filter(Boolean);
  for (const value of values) {
    const key = DESTINATION_ALIAS_TO_KEY[String(value).toLowerCase()];
    if (key) return key;
  }
  return values[0] || '';
}

function localizeDestinationName(destOrName, lang) {
  const key = destinationKeyFor(destOrName);
  const raw = typeof destOrName === 'string' ? destOrName : (lang === 'bg' ? destOrName?.nameBg : destOrName?.name);
  return DESTINATION_NAME_I18N[key]?.[lang] || CITY_I18N[raw]?.[lang] || CITY_I18N[destOrName?.nameBg]?.[lang] || raw || key;
}

function localizeCityName(city, lang) {
  if (!city || lang === 'bg') return city;
  return CITY_I18N[city]?.[lang] || city;
}

function localizeMonthRange(range, lang) {
  if (!range || lang === 'en') return range;
  const monthIndex = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
  return String(range).replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g, m => MONTH_NAMES[lang]?.[monthIndex[m]] || m);
}

function localizeVisitors(value, lang) {
  if (!value || value === '—') return value;
  const suffix = lang === 'bg' ? '/год.' : lang === 'es' ? '/año' : lang === 'fr' ? '/an' : lang === 'de' ? '/Jahr' : lang === 'el' ? '/έτος' : lang === 'it' ? '/anno' : '/year';
  return String(value).replace(/\/\s*(year|yr|год\.)\b/gi, suffix);
}

function localizeTripText(text, lang) {
  if (!text || lang === 'bg') return text;
  let value = String(text);
  Object.keys(CITY_I18N).forEach(city => {
    value = value.replace(new RegExp(city, 'g'), localizeCityName(city, lang));
  });
  const templates = {
    adventure: { bg:'Приключенски', en:'Adventure trip to', es:'Viaje de aventura a', fr:'Voyage d’aventure à', de:'Abenteuerreise nach', el:'Ταξίδι περιπέτειας στην/στο', it:'Viaggio avventura a' },
    cultural: { bg:'Културно', en:'Cultural trip to', es:'Viaje cultural a', fr:'Voyage culturel à', de:'Kulturreise nach', el:'Πολιτιστικό ταξίδι στην/στο', it:'Viaggio culturale a' },
    romantic: { bg:'Романтично', en:'Romantic trip to', es:'Viaje romántico a', fr:'Voyage romantique à', de:'Romantische Reise nach', el:'Ρομαντικό ταξίδι στην/στο', it:'Viaggio romantico a' },
    beach: { bg:'Плажно', en:'Beach trip to', es:'Viaje de playa a', fr:'Voyage balnéaire à', de:'Strandreise nach', el:'Ταξίδι σε παραλία στην/στο', it:'Viaggio al mare a' },
    mountain: { bg:'Планинско', en:'Mountain trip to', es:'Viaje de montaña a', fr:'Voyage à la montagne à', de:'Bergreise nach', el:'Ορεινό ταξίδι στην/στο', it:'Viaggio in montagna a' },
    generic: { bg:'Пътуване', en:'Trip to', es:'Viaje a', fr:'Voyage à', de:'Reise nach', el:'Ταξίδι στην/στο', it:'Viaggio a' },
  };
  for (const tpl of Object.values(templates)) {
    const re = new RegExp(`^${tpl.bg}\\s+пътуване\\s+до\\s+(.+)$`, 'i');
    const match = value.match(re);
    if (match) return `${tpl[lang] || tpl.en} ${match[1]}`;
  }
  const generic = value.match(/^Пътуване\s+до\s+(.+)$/i);
  if (generic) return `${templates.generic[lang] || templates.generic.en} ${generic[1]}`;
  return value;
}

const KNOWN_TRIP_TEXT_I18N = {
  'Открийте красотата на София': {
    bg:'Открийте красотата на София с култура, история и гледки към планината.',
    en:'Discover the beauty of Sofia through culture, history, and mountain views.',
    es:'Descubre la belleza de Sofía entre cultura, historia y vistas a la montaña.',
    fr:'Découvrez la beauté de Sofia entre culture, histoire et vues sur la montagne.',
    de:'Entdecke die Schönheit Sofias mit Kultur, Geschichte und Bergblick.',
    el:'Ανακάλυψε την ομορφιά της Σόφιας μέσα από τον πολιτισμό, την ιστορία και τη θέα στο βουνό.',
    it:'Scopri la bellezza di Sofia attraverso cultura, storia e viste sulla montagna.',
  },
  'Пристигане и стара част': {
    bg:'Пристигане и стара част',
    en:'Arrival and old town',
    es:'Llegada y casco antiguo',
    fr:'Arrivée et vieille ville',
    de:'Ankunft und Altstadt',
    el:'Άφιξη και παλιά πόλη',
    it:'Arrivo e centro storico',
  },
  'Стара част': {
    bg:'Стара част',
    en:'Old town',
    es:'Casco antiguo',
    fr:'Vieille ville',
    de:'Altstadt',
    el:'Παλιά πόλη',
    it:'Centro storico',
  },
  'Плажен ден': {
    bg:'Плажен ден',
    en:'Beach day',
    es:'Día de playa',
    fr:'Journée plage',
    de:'Strandtag',
    el:'Μέρα στην παραλία',
    it:'Giornata in spiaggia',
  },
  'Arrival & old town': {
    bg:'Пристигане и стара част',
    en:'Arrival and old town',
    es:'Llegada y casco antiguo',
    fr:'Arrivée et vieille ville',
    de:'Ankunft und Altstadt',
    el:'Άφιξη και παλιά πόλη',
    it:'Arrivo e centro storico',
  },
  'Beach day': {
    bg:'Плажен ден',
    en:'Beach day',
    es:'Día de playa',
    fr:'Journée plage',
    de:'Strandtag',
    el:'Μέρα στην παραλία',
    it:'Giornata in spiaggia',
  },
  'Сложете слънцезащитен крем': {
    bg:'Сложете слънцезащитен крем и носете вода през летните дни.',
    en:'Wear sunscreen and carry water on summer days.',
    es:'Ponte protector solar y lleva agua durante los días de verano.',
    fr:'Mettez de la crème solaire et emportez de l’eau pendant les journées d’été.',
    de:'Trage Sonnenschutz auf und nimm an Sommertagen Wasser mit.',
    el:'Φόρεσε αντηλιακό και κουβάλα νερό τις καλοκαιρινές ημέρες.',
    it:'Metti la protezione solare e porta acqua nei giorni estivi.',
  },
  'Носете удобни обувки': {
    bg:'Носете удобни обувки за разходките из центъра.',
    en:'Wear comfortable shoes for walks around the center.',
    es:'Lleva calzado cómodo para pasear por el centro.',
    fr:'Portez des chaussures confortables pour marcher dans le centre.',
    de:'Trage bequeme Schuhe für Spaziergänge im Zentrum.',
    el:'Φόρεσε άνετα παπούτσια για τους περιπάτους στο κέντρο.',
    it:'Indossa scarpe comode per le passeggiate nel centro.',
  },
  'Безплатно': {
    bg:'Безплатно',
    en:'free',
    es:'gratis',
    fr:'gratuit',
    de:'kostenlos',
    el:'δωρεάν',
    it:'gratis',
  },
  none: {
    bg:'няма',
    en:'none',
    es:'ninguna',
    fr:'aucun',
    de:'keine',
    el:'καμία',
    it:'nessuno',
  },
  'one health tip': {
    bg:'Проверете актуалните здравни препоръки преди пътуване.',
    en:'Check current health guidance before traveling.',
    es:'Consulta las recomendaciones sanitarias actuales antes de viajar.',
    fr:'Consultez les recommandations sanitaires à jour avant de voyager.',
    de:'Prüfe vor der Reise die aktuellen Gesundheitshinweise.',
    el:'Έλεγξε τις τρέχουσες υγειονομικές συστάσεις πριν το ταξίδι.',
    it:'Controlla le indicazioni sanitarie attuali prima di viaggiare.',
  },
  'Планина Витоша': {
    bg:'Планина Витоша',
    en:'Vitosha Mountain',
    es:'Montaña Vitosha',
    fr:'Mont Vitosha',
    de:'Witoscha-Gebirge',
    el:'Βουνό Βίτοσα',
    it:'Monte Vitosha',
  },
  'Витоша': {
    bg:'Витоша',
    en:'Vitosha',
    es:'Vitosha',
    fr:'Vitosha',
    de:'Witoscha',
    el:'Βίτοσα',
    it:'Vitosha',
  },
  'Исторически забележителности': {
    bg:'Исторически забележителности',
    en:'Historic landmarks',
    es:'Lugares históricos',
    fr:'Sites historiques',
    de:'Historische Sehenswürdigkeiten',
    el:'Ιστορικά αξιοθέατα',
    it:'Monumenti storici',
  },
  'Култура и изкуство': {
    bg:'Култура и изкуство',
    en:'Culture and art',
    es:'Cultura y arte',
    fr:'Culture et art',
    de:'Kultur und Kunst',
    el:'Πολιτισμός και τέχνη',
    it:'Cultura e arte',
  },
  'Пешеходни турове': {
    bg:'Пешеходни турове',
    en:'Walking tours',
    es:'Rutas de senderismo',
    fr:'Randonnées et visites à pied',
    de:'Wanderungen und Stadtrundgänge',
    el:'Περιηγήσεις με τα πόδια',
    it:'Tour a piedi',
  },
  'Софийски излети': {
    bg:'Софийски излети',
    en:'Sofia day trips',
    es:'Excursiones por Sofía',
    fr:'Excursions autour de Sofia',
    de:'Ausflüge rund um Sofia',
    el:'Εκδρομές γύρω από τη Σόφια',
    it:'Escursioni da Sofia',
  },
  'Разходка из центъра': {
    bg:'Разходка из центъра',
    en:'Walk through the center',
    es:'Paseo por el centro',
    fr:'Promenade dans le centre',
    de:'Spaziergang durch das Zentrum',
    el:'Βόλτα στο κέντρο',
    it:'Passeggiata nel centro',
  },
  'Посещение на забележителности': {
    bg:'Посещение на забележителности',
    en:'Landmark visits',
    es:'Visita a lugares de interés',
    fr:'Visite des sites incontournables',
    de:'Besuch der Sehenswürdigkeiten',
    el:'Επισκέψεις σε αξιοθέατα',
    it:'Visite ai monumenti',
  },
  'Проверете работното време предварително': {
    bg:'Проверете работното време предварително.',
    en:'Check opening hours in advance.',
    es:'Consulta los horarios de apertura con antelación.',
    fr:'Vérifiez les horaires d’ouverture à l’avance.',
    de:'Prüfe die Öffnungszeiten im Voraus.',
    el:'Έλεγξε τις ώρες λειτουργίας εκ των προτέρων.',
    it:'Controlla gli orari di apertura in anticipo.',
  },
  'Използвайте градски транспорт': {
    bg:'Използвайте градски транспорт за лесно придвижване.',
    en:'Use public transport for easy movement around the city.',
    es:'Usa el transporte público para moverte fácilmente por la ciudad.',
    fr:'Utilisez les transports en commun pour vous déplacer facilement.',
    de:'Nutze den öffentlichen Verkehr, um dich leicht in der Stadt zu bewegen.',
    el:'Χρησιμοποίησε τα μέσα μαζικής μεταφοράς για εύκολη μετακίνηση.',
    it:'Usa i mezzi pubblici per spostarti facilmente in città.',
  },
  'Носете вода': {
    bg:'Носете вода по време на разходките.',
    en:'Carry water during walks.',
    es:'Lleva agua durante los paseos.',
    fr:'Emportez de l’eau pendant les promenades.',
    de:'Nimm bei Spaziergängen Wasser mit.',
    el:'Κουβάλα νερό κατά τους περιπάτους.',
    it:'Porta acqua durante le passeggiate.',
  },
};

const CYRILLIC_TRIP_FALLBACK = {
  bg:'Акценти от пътуването',
  en:'Trip highlights',
  es:'Momentos destacados del viaje',
  fr:'Temps forts du voyage',
  de:'Reisehöhepunkte',
  el:'Σημεία ταξιδιού',
  it:'Momenti salienti del viaggio',
};

const KNOWN_TRIP_PHRASE_I18N = [
  ['Планина Витоша', { en:'Vitosha Mountain', es:'Montaña Vitosha', fr:'Mont Vitosha', de:'Witoscha-Gebirge', el:'Βουνό Βίτοσα', it:'Monte Vitosha' }],
  ['планина Витоша', { en:'Vitosha Mountain', es:'Montaña Vitosha', fr:'Mont Vitosha', de:'Witoscha-Gebirge', el:'βουνό Βίτοσα', it:'monte Vitosha' }],
  ['Парк Витоша', { en:'Vitosha Park', es:'Parque Vitosha', fr:'Parc Vitosha', de:'Witoscha-Park', el:'Πάρκο Βίτοσα', it:'Parco Vitosha' }],
  ['Исторически забележителности', { en:'Historic landmarks', es:'Lugares históricos', fr:'Sites historiques', de:'Historische Sehenswürdigkeiten', el:'Ιστορικά αξιοθέατα', it:'Monumenti storici' }],
  ['исторически забележителности', { en:'historic landmarks', es:'lugares históricos', fr:'sites historiques', de:'historische Sehenswürdigkeiten', el:'ιστορικά αξιοθέατα', it:'monumenti storici' }],
  ['Култура и изкуство', { en:'Culture and art', es:'Cultura y arte', fr:'Culture et art', de:'Kultur und Kunst', el:'Πολιτισμός και τέχνη', it:'Cultura e arte' }],
  ['култура и изкуство', { en:'culture and art', es:'cultura y arte', fr:'culture et art', de:'Kultur und Kunst', el:'πολιτισμός και τέχνη', it:'cultura e arte' }],
  ['Пешеходни турове', { en:'Walking tours', es:'Rutas de senderismo', fr:'Randonnées et visites à pied', de:'Wanderungen und Stadtrundgänge', el:'Περιηγήσεις με τα πόδια', it:'Tour a piedi' }],
  ['пешеходни турове', { en:'walking tours', es:'rutas de senderismo', fr:'randonnées et visites à pied', de:'Wanderungen und Stadtrundgänge', el:'περιηγήσεις με τα πόδια', it:'tour a piedi' }],
  ['Софийски излети', { en:'Sofia day trips', es:'Excursiones por Sofía', fr:'Excursions autour de Sofia', de:'Ausflüge rund um Sofia', el:'Εκδρομές γύρω από τη Σόφια', it:'Escursioni da Sofia' }],
  ['софийски излети', { en:'Sofia day trips', es:'excursiones por Sofía', fr:'excursions autour de Sofia', de:'Ausflüge rund um Sofia', el:'εκδρομές γύρω από τη Σόφια', it:'escursioni da Sofia' }],
  ['София', { en:'Sofia', es:'Sofía', fr:'Sofia', de:'Sofia', el:'Σόφια', it:'Sofia' }],
  ['софия', { en:'Sofia', es:'Sofía', fr:'Sofia', de:'Sofia', el:'Σόφια', it:'Sofia' }],
  ['Софийски', { en:'Sofia', es:'de Sofía', fr:'de Sofia', de:'Sofia', el:'της Σόφιας', it:'di Sofia' }],
  ['софийски', { en:'Sofia', es:'de Sofía', fr:'de Sofia', de:'Sofia', el:'της Σόφιας', it:'di Sofia' }],
  ['стара част', { en:'old town', es:'casco antiguo', fr:'vieille ville', de:'Altstadt', el:'παλιά πόλη', it:'centro storico' }],
  ['центъра', { en:'the center', es:'el centro', fr:'le centre', de:'das Zentrum', el:'το κέντρο', it:'il centro' }],
  ['забележителности', { en:'landmarks', es:'lugares de interés', fr:'sites incontournables', de:'Sehenswürdigkeiten', el:'αξιοθέατα', it:'monumenti' }],
  ['музеи', { en:'museums', es:'museos', fr:'musées', de:'Museen', el:'μουσεία', it:'musei' }],
  ['галерии', { en:'galleries', es:'galerías', fr:'galeries', de:'Galerien', el:'γκαλερί', it:'gallerie' }],
  ['изкуство', { en:'art', es:'arte', fr:'art', de:'Kunst', el:'τέχνη', it:'arte' }],
  ['култура', { en:'culture', es:'cultura', fr:'culture', de:'Kultur', el:'πολιτισμός', it:'cultura' }],
  ['разходка', { en:'walk', es:'paseo', fr:'promenade', de:'Spaziergang', el:'βόλτα', it:'passeggiata' }],
  ['гледки', { en:'views', es:'vistas', fr:'vues', de:'Aussichten', el:'θέα', it:'viste' }],
  ['планината', { en:'the mountain', es:'la montaña', fr:'la montagne', de:'den Berg', el:'το βουνό', it:'la montagna' }],
  ['парк', { en:'park', es:'parque', fr:'parc', de:'Park', el:'πάρκο', it:'parco' }],
  ['катедрала', { en:'cathedral', es:'catedral', fr:'cathédrale', de:'Kathedrale', el:'καθεδρικός', it:'cattedrale' }],
];

const KNOWN_ATTRACTION_I18N = {
  'Александър Невски катедрала': {
    name:{ bg:'Катедрала „Александър Невски“', en:'Alexander Nevsky Cathedral', es:'Catedral Alexander Nevsky', fr:'Cathédrale Alexandre-Nevski', de:'Alexander-Newski-Kathedrale' },
    description:{
      bg:'Емблематичната катедрала е един от символите на София със златни куполи и внушителен интериор.',
      en:'This iconic cathedral is one of Sofia’s symbols, with golden domes and an impressive interior.',
      es:'Esta catedral icónica es uno de los símbolos de Sofía, con cúpulas doradas y un interior imponente.',
      fr:'Cette cathédrale emblématique est l’un des symboles de Sofia, avec ses dômes dorés et son intérieur imposant.',
      de:'Diese ikonische Kathedrale ist eines der Wahrzeichen Sofias, mit goldenen Kuppeln und eindrucksvollem Innenraum.',
    },
  },
  'Катедрала „Александър Невски“': {
    name:{ bg:'Катедрала „Александър Невски“', en:'Alexander Nevsky Cathedral', es:'Catedral Alexander Nevsky', fr:'Cathédrale Alexandre-Nevski', de:'Alexander-Newski-Kathedrale' },
    description:{
      bg:'Емблематичната катедрала е един от символите на София със златни куполи и внушителен интериор.',
      en:'This iconic cathedral is one of Sofia’s symbols, with golden domes and an impressive interior.',
      es:'Esta catedral icónica es uno de los símbolos de Sofía, con cúpulas doradas y un interior imponente.',
      fr:'Cette cathédrale emblématique est l’un des symboles de Sofia, avec ses dômes dorés et son intérieur imposant.',
      de:'Diese ikonische Kathedrale ist eines der Wahrzeichen Sofias, mit goldenen Kuppeln und eindrucksvollem Innenraum.',
    },
  },
  'Alexander Nevsky Cathedral': {
    name:{ bg:'Катедрала „Александър Невски“', en:'Alexander Nevsky Cathedral', es:'Catedral Alexander Nevsky', fr:'Cathédrale Alexandre-Nevski', de:'Alexander-Newski-Kathedrale' },
    description:{
      bg:'Емблематичната катедрала е един от символите на София със златни куполи и внушителен интериор.',
      en:'This iconic cathedral is one of Sofia’s symbols, with golden domes and an impressive interior.',
      es:'Esta catedral icónica es uno de los símbolos de Sofía, con cúpulas doradas y un interior imponente.',
      fr:'Cette cathédrale emblématique est l’un des symboles de Sofia, avec ses dômes dorés et son intérieur imposant.',
      de:'Diese ikonische Kathedrale ist eines der Wahrzeichen Sofias, mit goldenen Kuppeln und eindrucksvollem Innenraum.',
    },
  },
  'Парк Витоша': {
    name:{ bg:'Парк Витоша', en:'Vitosha Park', es:'Parque Vitosha', fr:'Parc Vitosha', de:'Witoscha-Park' },
    description:{
      bg:'Планината Витоша предлага лесно бягство от града с панорамни гледки, пътеки и чист въздух.',
      en:'Vitosha mountain offers an easy escape from the city with panoramic views, trails, and fresh air.',
      es:'La montaña Vitosha ofrece una escapada fácil de la ciudad con vistas panorámicas, senderos y aire puro.',
      fr:'La montagne Vitosha offre une échappée facile depuis la ville, avec vues panoramiques, sentiers et air pur.',
      de:'Der Witoscha-Berg bietet eine einfache Auszeit von der Stadt mit Panoramablick, Wegen und frischer Luft.',
    },
  },
  'Планина Витоша': {
    name:{ bg:'Планина Витоша', en:'Vitosha Mountain', es:'Montaña Vitosha', fr:'Mont Vitosha', de:'Witoscha-Gebirge' },
    description:{
      bg:'Витоша е планината до София с пешеходни маршрути, свеж въздух и панорамни гледки към града.',
      en:'Vitosha is the mountain beside Sofia, with hiking routes, fresh air, and panoramic city views.',
      es:'Vitosha es la montaña junto a Sofía, con rutas de senderismo, aire puro y vistas panorámicas de la ciudad.',
      fr:'Vitosha est la montagne aux portes de Sofia, avec sentiers de randonnée, air pur et vues panoramiques sur la ville.',
      de:'Witoscha ist der Berg bei Sofia mit Wanderwegen, frischer Luft und Panoramablick auf die Stadt.',
    },
  },
  'Vitosha Mountain': {
    name:{ bg:'Планина Витоша', en:'Vitosha Mountain', es:'Montaña Vitosha', fr:'Mont Vitosha', de:'Witoscha-Gebirge' },
    description:{
      bg:'Витоша е планината до София с пешеходни маршрути, свеж въздух и панорамни гледки към града.',
      en:'Vitosha is the mountain beside Sofia, with hiking routes, fresh air, and panoramic city views.',
      es:'Vitosha es la montaña junto a Sofía, con rutas de senderismo, aire puro y vistas panorámicas de la ciudad.',
      fr:'Vitosha est la montagne aux portes de Sofia, avec sentiers de randonnée, air pur et vues panoramiques sur la ville.',
      de:'Witoscha ist der Berg bei Sofia mit Wanderwegen, frischer Luft und Panoramablick auf die Stadt.',
    },
  },
  'Исторически забележителности': {
    name:{ bg:'Исторически забележителности', en:'Historic landmarks', es:'Lugares históricos', fr:'Sites historiques', de:'Historische Sehenswürdigkeiten' },
    description:{
      bg:'Разгледайте най-важните исторически места в София и проследете пластовете от римско, средновековно и модерно наследство.',
      en:'Explore Sofia’s key historic places and trace its Roman, medieval, and modern heritage.',
      es:'Explora los lugares históricos clave de Sofía y descubre su herencia romana, medieval y moderna.',
      fr:'Explorez les principaux sites historiques de Sofia et son héritage romain, médiéval et moderne.',
      de:'Erkunde Sofias wichtigste historische Orte und ihr römisches, mittelalterliches und modernes Erbe.',
    },
  },
  'Култура и изкуство': {
    name:{ bg:'Култура и изкуство', en:'Culture and art', es:'Cultura y arte', fr:'Culture et art', de:'Kultur und Kunst' },
    description:{
      bg:'Музеи, галерии и артистични пространства разкриват творческата страна на София.',
      en:'Museums, galleries, and creative spaces reveal Sofia’s artistic side.',
      es:'Museos, galerías y espacios creativos revelan el lado artístico de Sofía.',
      fr:'Musées, galeries et lieux créatifs révèlent le côté artistique de Sofia.',
      de:'Museen, Galerien und kreative Orte zeigen Sofias künstlerische Seite.',
    },
  },
  'Пешеходни турове': {
    name:{ bg:'Пешеходни турове', en:'Walking tours', es:'Rutas de senderismo', fr:'Randonnées et visites à pied', de:'Wanderungen und Stadtrundgänge' },
    description:{
      bg:'Открийте града пеша с маршрути през центъра, паркове и любими местни места.',
      en:'Discover the city on foot through routes across the center, parks, and local favorites.',
      es:'Descubre la ciudad a pie con rutas por el centro, parques y rincones locales favoritos.',
      fr:'Découvrez la ville à pied à travers le centre, les parcs et les lieux appréciés des habitants.',
      de:'Entdecke die Stadt zu Fuß durch das Zentrum, Parks und beliebte lokale Orte.',
    },
  },
  'Софийски излети': {
    name:{ bg:'Софийски излети', en:'Sofia day trips', es:'Excursiones por Sofía', fr:'Excursions autour de Sofia', de:'Ausflüge rund um Sofia' },
    description:{
      bg:'Кратки излети около София съчетават природа, манастири, гледки и спокойни местни маршрути.',
      en:'Short trips around Sofia combine nature, monasteries, views, and relaxed local routes.',
      es:'Las excursiones cortas por Sofía combinan naturaleza, monasterios, vistas y rutas locales tranquilas.',
      fr:'Les courtes excursions autour de Sofia mêlent nature, monastères, vues et parcours locaux paisibles.',
      de:'Kurze Ausflüge rund um Sofia verbinden Natur, Klöster, Ausblicke und entspannte lokale Routen.',
    },
  },
  'Vitosha Park': {
    name:{ bg:'Парк Витоша', en:'Vitosha Park', es:'Parque Vitosha', fr:'Parc Vitosha', de:'Witoscha-Park' },
    description:{
      bg:'Планината Витоша предлага лесно бягство от града с панорамни гледки, пътеки и чист въздух.',
      en:'Vitosha mountain offers an easy escape from the city with panoramic views, trails, and fresh air.',
      es:'La montaña Vitosha ofrece una escapada fácil de la ciudad con vistas panorámicas, senderos y aire puro.',
      fr:'La montagne Vitosha offre une échappée facile depuis la ville, avec vues panoramiques, sentiers et air pur.',
      de:'Der Witoscha-Berg bietet eine einfache Auszeit von der Stadt mit Panoramablick, Wegen und frischer Luft.',
    },
  },
};

const KNOWN_TRIP_TEXT_EXTRA = {
  'Открийте красотата на София': { el:'Ανακάλυψε την ομορφιά της Σόφιας μέσα από πολιτισμό, ιστορία και θέα στο βουνό.', it:'Scopri la bellezza di Sofia tra cultura, storia e viste sulla montagna.' },
  'Пристигане и стара част': { el:'Άφιξη και παλιά πόλη', it:'Arrivo e centro storico' },
  'Стара част': { el:'Παλιά πόλη', it:'Centro storico' },
  'Плажен ден': { el:'Ημέρα παραλίας', it:'Giornata in spiaggia' },
  'Arrival & old town': { el:'Άφιξη και παλιά πόλη', it:'Arrivo e centro storico' },
  'Beach day': { el:'Ημέρα παραλίας', it:'Giornata in spiaggia' },
  'Сложете слънцезащитен крем': { el:'Βάλε αντηλιακό και πάρε νερό τις καλοκαιρινές ημέρες.', it:'Metti la crema solare e porta acqua nelle giornate estive.' },
  'Носете удобни обувки': { el:'Φόρεσε άνετα παπούτσια για βόλτες στο κέντρο.', it:'Indossa scarpe comode per passeggiare in centro.' },
  'Безплатно': { el:'δωρεάν', it:'gratis' },
  none: { el:'κανένα', it:'nessuno' },
  'one health tip': { el:'Έλεγξε τις τρέχουσες οδηγίες υγείας πριν ταξιδέψεις.', it:'Controlla le indicazioni sanitarie aggiornate prima di viaggiare.' },
  'Планина Витоша': { el:'Βουνό Βίτοσα', it:'Monte Vitosha' },
  'Витоша': { el:'Βίτοσα', it:'Vitosha' },
  'Исторически забележителности': { el:'Ιστορικά αξιοθέατα', it:'Monumenti storici' },
  'Култура и изкуство': { el:'Πολιτισμός και τέχνη', it:'Cultura e arte' },
  'Пешеходни турове': { el:'Περιηγήσεις με τα πόδια', it:'Tour a piedi' },
  'Софийски излети': { el:'Εκδρομές γύρω από τη Σόφια', it:'Escursioni da Sofia' },
  'Разходка из центъра': { el:'Βόλτα στο κέντρο', it:'Passeggiata in centro' },
  'Посещение на забележителности': { el:'Επισκέψεις σε αξιοθέατα', it:'Visite ai monumenti' },
  'Проверете работното време предварително': { el:'Έλεγξε το ωράριο εκ των προτέρων.', it:'Controlla gli orari di apertura in anticipo.' },
  'Използвайте градски транспорт': { el:'Χρησιμοποίησε δημόσια συγκοινωνία για εύκολη μετακίνηση στην πόλη.', it:'Usa i mezzi pubblici per muoverti facilmente in città.' },
  'Носете вода': { el:'Πάρε νερό κατά τη διάρκεια των περιπάτων.', it:'Porta acqua durante le passeggiate.' },
};

Object.entries(KNOWN_TRIP_TEXT_EXTRA).forEach(([key, copy]) => {
  KNOWN_TRIP_TEXT_I18N[key] = { ...(KNOWN_TRIP_TEXT_I18N[key] || {}), ...copy };
});

KNOWN_TRIP_PHRASE_I18N.forEach(([, copy]) => {
  if (copy.en === 'Vitosha Mountain') Object.assign(copy, { el:'Βουνό Βίτοσα', it:'Monte Vitosha' });
  if (copy.en === 'Vitosha Park') Object.assign(copy, { el:'Πάρκο Βίτοσα', it:'Parco Vitosha' });
  if (copy.en === 'Historic landmarks') Object.assign(copy, { el:'Ιστορικά αξιοθέατα', it:'Monumenti storici' });
  if (copy.en === 'historic landmarks') Object.assign(copy, { el:'ιστορικά αξιοθέατα', it:'monumenti storici' });
  if (copy.en === 'Culture and art') Object.assign(copy, { el:'Πολιτισμός και τέχνη', it:'Cultura e arte' });
  if (copy.en === 'culture and art') Object.assign(copy, { el:'πολιτισμός και τέχνη', it:'cultura e arte' });
  if (copy.en === 'Walking tours') Object.assign(copy, { el:'Περιηγήσεις με τα πόδια', it:'Tour a piedi' });
  if (copy.en === 'walking tours') Object.assign(copy, { el:'περιηγήσεις με τα πόδια', it:'tour a piedi' });
  if (copy.en === 'Sofia day trips') Object.assign(copy, { el:'Εκδρομές γύρω από τη Σόφια', it:'Escursioni da Sofia' });
  if (copy.en === 'Sofia') Object.assign(copy, { el:'Σόφια', it:'Sofia' });
  if (copy.en === 'old town') Object.assign(copy, { el:'παλιά πόλη', it:'centro storico' });
  if (copy.en === 'the center') Object.assign(copy, { el:'το κέντρο', it:'il centro' });
  if (copy.en === 'landmarks') Object.assign(copy, { el:'αξιοθέατα', it:'monumenti' });
  if (copy.en === 'museums') Object.assign(copy, { el:'μουσεία', it:'musei' });
  if (copy.en === 'galleries') Object.assign(copy, { el:'γκαλερί', it:'gallerie' });
  if (copy.en === 'art') Object.assign(copy, { el:'τέχνη', it:'arte' });
  if (copy.en === 'culture') Object.assign(copy, { el:'πολιτισμός', it:'cultura' });
  if (copy.en === 'walk') Object.assign(copy, { el:'βόλτα', it:'passeggiata' });
  if (copy.en === 'views') Object.assign(copy, { el:'θέα', it:'viste' });
  if (copy.en === 'the mountain') Object.assign(copy, { el:'το βουνό', it:'la montagna' });
  if (copy.en === 'park') Object.assign(copy, { el:'πάρκο', it:'parco' });
  if (copy.en === 'cathedral') Object.assign(copy, { el:'καθεδρικός', it:'cattedrale' });
});

const KNOWN_ATTRACTION_EXTRA = {
  'Александър Невски катедрала': {
    name:{ el:'Καθεδρικός Αλεξάντερ Νέφσκι', it:'Cattedrale Alexander Nevsky' },
    description:{ el:'Ο εμβληματικός καθεδρικός είναι ένα από τα σύμβολα της Σόφιας, με χρυσούς τρούλους και εντυπωσιακό εσωτερικό.', it:'Questa cattedrale iconica è uno dei simboli di Sofia, con cupole dorate e interni imponenti.' },
  },
  'Катедрала „Александър Невски“': {
    name:{ el:'Καθεδρικός Αλεξάντερ Νέφσκι', it:'Cattedrale Alexander Nevsky' },
    description:{ el:'Ο εμβληματικός καθεδρικός είναι ένα από τα σύμβολα της Σόφιας, με χρυσούς τρούλους και εντυπωσιακό εσωτερικό.', it:'Questa cattedrale iconica è uno dei simboli di Sofia, con cupole dorate e interni imponenti.' },
  },
  'Alexander Nevsky Cathedral': {
    name:{ el:'Καθεδρικός Αλεξάντερ Νέφσκι', it:'Cattedrale Alexander Nevsky' },
    description:{ el:'Ο εμβληματικός καθεδρικός είναι ένα από τα σύμβολα της Σόφιας, με χρυσούς τρούλους και εντυπωσιακό εσωτερικό.', it:'Questa cattedrale iconica è uno dei simboli di Sofia, con cupole dorate e interni imponenti.' },
  },
  'Парк Витоша': {
    name:{ el:'Πάρκο Βίτοσα', it:'Parco Vitosha' },
    description:{ el:'Το βουνό Βίτοσα προσφέρει εύκολη απόδραση από την πόλη με πανοραμική θέα, μονοπάτια και καθαρό αέρα.', it:'Il monte Vitosha offre una fuga facile dalla città con viste panoramiche, sentieri e aria fresca.' },
  },
  'Планина Витоша': {
    name:{ el:'Βουνό Βίτοσα', it:'Monte Vitosha' },
    description:{ el:'Η Βίτοσα είναι το βουνό δίπλα στη Σόφια, με πεζοπορικές διαδρομές, καθαρό αέρα και πανοραμική θέα στην πόλη.', it:'Vitosha è la montagna accanto a Sofia, con percorsi escursionistici, aria fresca e viste panoramiche sulla città.' },
  },
  'Vitosha Mountain': {
    name:{ el:'Βουνό Βίτοσα', it:'Monte Vitosha' },
    description:{ el:'Η Βίτοσα είναι το βουνό δίπλα στη Σόφια, με πεζοπορικές διαδρομές, καθαρό αέρα και πανοραμική θέα στην πόλη.', it:'Vitosha è la montagna accanto a Sofia, con percorsi escursionistici, aria fresca e viste panoramiche sulla città.' },
  },
  'Исторически забележителности': {
    name:{ el:'Ιστορικά αξιοθέατα', it:'Monumenti storici' },
    description:{ el:'Εξερεύνησε τα βασικά ιστορικά σημεία της Σόφιας και την ρωμαϊκή, μεσαιωνική και σύγχρονη κληρονομιά της.', it:'Esplora i principali luoghi storici di Sofia e la sua eredità romana, medievale e moderna.' },
  },
  'Култура и изкуство': {
    name:{ el:'Πολιτισμός και τέχνη', it:'Cultura e arte' },
    description:{ el:'Μουσεία, γκαλερί και δημιουργικοί χώροι αποκαλύπτουν την καλλιτεχνική πλευρά της Σόφιας.', it:'Musei, gallerie e spazi creativi rivelano il lato artistico di Sofia.' },
  },
  'Пешеходни турове': {
    name:{ el:'Περιηγήσεις με τα πόδια', it:'Tour a piedi' },
    description:{ el:'Ανακάλυψε την πόλη με τα πόδια μέσα από διαδρομές στο κέντρο, πάρκα και αγαπημένα τοπικά σημεία.', it:'Scopri la città a piedi con percorsi nel centro, parchi e luoghi amati dai locali.' },
  },
  'Софийски излети': {
    name:{ el:'Εκδρομές γύρω από τη Σόφια', it:'Escursioni da Sofia' },
    description:{ el:'Σύντομες εκδρομές γύρω από τη Σόφια συνδυάζουν φύση, μοναστήρια, θέα και χαλαρές τοπικές διαδρομές.', it:'Brevi escursioni da Sofia combinano natura, monasteri, panorami e itinerari locali tranquilli.' },
  },
  'Vitosha Park': {
    name:{ el:'Πάρκο Βίτοσα', it:'Parco Vitosha' },
    description:{ el:'Το βουνό Βίτοσα προσφέρει εύκολη απόδραση από την πόλη με πανοραμική θέα, μονοπάτια και καθαρό αέρα.', it:'Il monte Vitosha offre una fuga facile dalla città con viste panoramiche, sentieri e aria fresca.' },
  },
};

Object.entries(KNOWN_ATTRACTION_EXTRA).forEach(([key, extra]) => {
  if (!KNOWN_ATTRACTION_I18N[key]) return;
  KNOWN_ATTRACTION_I18N[key].name = { ...KNOWN_ATTRACTION_I18N[key].name, ...extra.name };
  KNOWN_ATTRACTION_I18N[key].description = { ...KNOWN_ATTRACTION_I18N[key].description, ...extra.description };
});

const KNOWN_ATTRACTION_ALIASES = Object.entries(KNOWN_ATTRACTION_I18N).reduce((acc, [key, data]) => {
  [key, ...Object.values(data.name || {})].forEach(name => { if (name) acc[String(name).toLowerCase()] = key; });
  return acc;
}, {});

function localizeKnownTripContent(text, lang) {
  if (!text) return text;
  let value = localizeTripText(String(text), lang);
  value = value.replace(/^(Ден|Day|Día|Jour|Tag)\s+\d+\s*[—-]\s*/i, '');
  const exact = KNOWN_TRIP_TEXT_I18N[value] || KNOWN_TRIP_TEXT_I18N[String(text)];
  if (exact) return exact[lang] || exact.en || value;
  for (const [key, copy] of Object.entries(KNOWN_TRIP_TEXT_I18N)) {
    if (String(text).startsWith(key) || value.startsWith(key)) return copy[lang] || copy.en || value;
  }
  value = value.replace(/\b(Sofia|София)\b/g, localizeCityName('София', lang));
  value = value.replace(/\b(Varna|Варна)\b/g, localizeCityName('Варна', lang));
  if (lang !== 'bg') {
    KNOWN_TRIP_PHRASE_I18N.forEach(([phrase, copy]) => {
      value = value.split(phrase).join(copy[lang] || copy.en || phrase);
    });
    if (/[Ѐ-ӿ]/.test(value)) return CYRILLIC_TRIP_FALLBACK[lang] || CYRILLIC_TRIP_FALLBACK.en;
  }
  return localizeMonthRange(value, lang);
}

function localizeAttractionField(attraction, field, lang) {
  if (!attraction) return '';
  const rawName = attraction.name || '';
  const key = KNOWN_ATTRACTION_ALIASES[String(rawName).toLowerCase()];
  if (key) {
    const data = KNOWN_ATTRACTION_I18N[key];
    if (field === 'name') return data.name?.[lang] || data.name?.en || rawName;
    if (field === 'description') return data.description?.[lang] || data.description?.en || attraction.description;
  }
  return localizeKnownTripContent(attraction[field], lang);
}

// ── New feature data ───────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const DREAM_PROFILES = [
  { id:1, name:'Isabella', flag:'🇮🇹', photo:'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=200&q=80', dest:'🇯🇵 Japan' },
  { id:2, name:'Riku',     flag:'🇯🇵', photo:'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=200&q=80', dest:'🇯🇵 Japan' },
  { id:3, name:'Maria',    flag:'🇪🇸', photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=200&q=80', dest:'🇯🇵 Japan' },
];

const BADGES_DATA = [
  { id:1, key:'worldExplorer',  icon:'🌍', name:'World Explorer',   desc:'Joined 3+ trips',          earned:true  },
  { id:2, key:'socialButterfly',icon:'💬', name:'Social Butterfly', desc:'Sent 50+ messages',         earned:true  },
  { id:3, key:'superLiker',     icon:'⭐', name:'Super Liker',       desc:'Used 10 Super Likes',       earned:true  },
  { id:4, key:'onFire',         icon:'🔥', name:'On Fire',           desc:'Logged in 7 days in a row', earned:true  },
  { id:5, key:'firstFlight',    icon:'✈️', name:'First Flight',      desc:'Joined your first trip',    earned:true  },
  { id:6, key:'mountainClimber',icon:'🏔️', name:'Mountain Climber', desc:'Join a hiking trip',         earned:false },
  { id:7, key:'beachLover',     icon:'🏖️', name:'Beach Lover',       desc:'Join 3 beach trips',         earned:false },
  { id:8, key:'eliteTraveler',  icon:'💎', name:'Elite Traveler',    desc:'Upgrade to Elite',           earned:false },
  { id:9, key:'nightOwl',       icon:'🌙', name:'Night Owl',         desc:'Use Night Mode 10 times',    earned:false },
  { id:10,key:'cartographer',   icon:'🗺️', name:'Cartographer',     desc:'Visit 10 countries',         earned:false },
];

function getSpinPrizes() {
  return [
    { label:'Super Like x1',   short:'🔥 x1',  emoji:'🔥', color:'#c9a84c', superLikes:1 },
    { label:'500 монети',      short:'🪙 500',  emoji:'🪙', color:'#f59e0b', coins:500 },
    { label:'Boost x1',        short:'🔄 x1',   emoji:'🔄', color:'#1abc9c', boost:true },
    { label:'Like x5',         short:'❤️ x5',   emoji:'❤️', color:'#e53935', likesX5:true },
    { label:'250 монети',      short:'🪙 250',  emoji:'🪙', color:'#9333ea', coins:250 },
    { label:'Free Trip Entry', short:'✈️ 1x',   emoji:'✈️', color:'#0ea5e9', freeTripEntry:true },
    { label:'Mystery Gift',    short:'🎁 ???',  emoji:'🎁', color:'#7c3aed', mysteryGift:true },
  ];
}

const WEEKLY_CHALLENGES = [
  { title:'🌏 Connect with Asia',  desc:'Message someone from an Asian country this week', goal:1, reward:'🔥 1 Free Boost' },
  { title:'⭐ Super Like Week',    desc:'Send 3 Super Likes this week',                     goal:3, reward:'↩️ Rewind x3' },
  { title:'🌍 Globe Trotter',      desc:'View 10 profiles from different countries',        goal:10,reward:'🌍 Passport day pass' },
  { title:'🌙 Night Explorer',     desc:'Use Night Mode tonight',                           goal:1, reward:'⭐ Super Like x5' },
];

const GIFTS = [
  { emoji:'🌹', name:'rose',       color:'#ff6b6b', price:10  },
  { emoji:'🌸', name:'flower',     color:'#ff6b9d', price:10  },
  { emoji:'⭐', name:'star',       color:'#ffd700', price:15  },
  { emoji:'💋', name:'kiss',       color:'#ff4757', price:20  },
  { emoji:'🎵', name:'music',      color:'#7b9e87', price:20  },
  { emoji:'💝', name:'heart',      color:'#ff4757', price:25  },
  { emoji:'🍾', name:'champagne',  color:'#f8b423', price:30  },
  { emoji:'🦋', name:'butterfly',  color:'#ff6b9d', price:30  },
  { emoji:'🌙', name:'moon',       color:'#a8edea', price:35  },
  { emoji:'🎪', name:'circus',     color:'#ff6b6b', price:45  },
  { emoji:'✈️', name:'plane',      color:'#4facfe', price:50  },
  { emoji:'🌍', name:'earth',      color:'#4facfe', price:60  },
  { emoji:'🎭', name:'theatre',    color:'#c9a84c', price:75  },
  { emoji:'🗺️', name:'worldmap',   color:'#7b9e87', price:80  },
  { emoji:'🏖️', name:'island',     color:'#f8b423', price:99  },
  { emoji:'💍', name:'ring',       color:'#ffd700', price:100 },
  { emoji:'🚀', name:'rocket',     color:'#4facfe', price:150 },
  { emoji:'🎆', name:'fireworks',  color:'#ff4757', price:200 },
  { emoji:'👑', name:'crown',      color:'#ffd700', price:300 },
  { emoji:'💎', name:'diamond',    color:'#a8edea', price:500 },
];

function getIcebreakerSuggestions(lang, profile) {
  const dest = profile?.dreams?.[0] || profile?.destination || (lang==='bg'?'пътуване':lang==='es'?'viajar':lang==='fr'?'voyager':lang==='de'?'Reisen':lang==='el'?'Ταξίδια':lang==='it'?'Viaggi':'travel');
  const trait = profile?.traits?.[0] || 'explorer';
  const all = {
    bg: {
      explorer:   [`Виждам, че мечтаеш за ${dest}! Кое е най-неочакваното нещо, което си открил? 🗺️`, `Съпътешественик! Кое пътуване напълно промени твоята перспектива? ✨`, `Кое е твоето любимо скрито съкровище, което си намерил по света? 🔍`, `Ако можеш да се телепортираш някъде сега, къде би отишъл? 🚀`],
      adventurer: [`Кое е най-лудото нещо, което си правил по време на пътуване? 😄`, `Плаж или планини? Разкажи ми за любимото си място! 🏖️⛰️`, `Какво задължително слагаш в куфара си преди всяко пътуване? 🎒`, `Кой е любимият ти местен ресторант, открит случайно в чужбина? 🍜`],
      culturist:  [`Ако трябваше да живееш в друга държава, коя би избрал? 🌍`, `Кое пътуване те е направило по-добър човек? 💫`, `Предпочиташ ли да планираш всичко предварително или да импровизираш? 🗓️`, `Какъв е твоят любим начин да опознаеш нова дестинация? 🚶`],
      nomad:      [`Разкажи ми за момента, в който се влюби в дадено място! ❤️`, `Кое е най-красивото изгрево или залез, което си виждал? 🌅`, `Какъв сувенир е задължителен за теб от всяко пътуване? 🎁`],
    },
    en: {
      explorer:   [`I see you dream of ${dest}! What's the most unexpected thing you've discovered? 🗺️`, `What hidden gem are you most proud of finding? I'm always hunting for those 🔍`, `Fellow explorer! Which destination completely changed your perspective? ✨`],
      adventurer: [`That adventure photo is insane! Did you go solo or with a group? ⛰️`, `As a fellow adventurer — what's the most extreme thing you've done? 🪂`, `You look like someone who'd book a one-way ticket. Where next? 🌍`],
      culturist:  [`Fellow culture lover! What museum completely blew your mind? 🎨`, `Paris or Rome for culture? I genuinely can't decide 😄`, `Which ancient site made you feel most connected to history? 🏛️`],
      nomad:      [`How long have you been living the nomad life? I'm just starting! 🌍`, `What's the best co-working city you've lived in? 💻`, `Any tips for managing time zones while staying connected? ⏰`],
    },
    es: {
      explorer:   [`¡Veo que sueñas con ${dest}! ¿Cuál es lo más inesperado que has descubierto? 🗺️`, `¿Cuál es el lugar secreto del que más orgulloso estás? Siempre busco esos lugares 🔍`, `¡Compañero viajero! ¿Qué destino cambió completamente tu perspectiva? ✨`],
      adventurer: [`¡Esa foto de aventura es increíble! ¿Fuiste solo o en grupo? ⛰️`, `Como aventurero — ¿cuál es lo más extremo que has hecho? 🪂`, `Pareces alguien que reservaría un billete de ida. ¿A dónde después? 🌍`],
      culturist:  [`¡Amante de la cultura! ¿Qué museo te dejó sin palabras? 🎨`, `¿Kioto o Roma para cultura? Genuinamente no puedo decidir 😄`, `¿Qué lugar antiguo te hizo sentir más conectado con la historia? 🏛️`],
      nomad:      [`¿Cuánto tiempo llevas viviendo la vida nómada? ¡Yo acabo de empezar! 🌍`, `¿Cuál es la mejor ciudad para trabajar remotamente? 💻`, `¿Algún consejo para gestionar zonas horarias? ⏰`],
    },
    fr: {
      explorer:   [`Je vois que tu rêves de ${dest}! Quelle est la chose la plus inattendue que tu as découverte? 🗺️`, `Quel endroit secret es-tu le plus fier d'avoir trouvé? Je les cherche toujours 🔍`, `Compagnon voyageur! Quelle destination a complètement changé ta perspective? ✨`],
      adventurer: [`Cette photo d'aventure est incroyable! Tu es parti seul ou en groupe? ⛰️`, `En tant qu'aventurier — quelle est la chose la plus extrême que tu aies faite? 🪂`, `Tu ressembles à quelqu'un qui réserverait un billet aller simple. Où ensuite? 🌍`],
      culturist:  [`Amateur de culture! Quel musée t'a complètement époustouflé? 🎨`, `Paris ou Rome pour la culture? Je n'arrive vraiment pas à décider 😄`, `Quel site antique t'a fait te sentir le plus connecté à l'histoire? 🏛️`],
      nomad:      [`Depuis combien de temps vis-tu la vie de nomade? Je commence tout juste! 🌍`, `Quelle est la meilleure ville de co-working où tu as vécu? 💻`, `Des conseils pour gérer les fuseaux horaires? ⏰`],
    },
    de: {
      explorer:   [`Ich sehe du träumst von ${dest}! Was ist das Unerwartetste was du entdeckt hast? 🗺️`, `Welcher geheime Ort macht dich am stolzesten? Ich suche immer nach solchen Plätzen 🔍`, `Mitreisender! Welches Reiseziel hat deine Perspektive völlig verändert? ✨`],
      adventurer: [`Dieses Abenteuerfoto ist der Wahnsinn! Bist du alleine oder mit einer Gruppe gegangen? ⛰️`, `Als Abenteurer — was ist das Extremste was du je gemacht hast? 🪂`, `Du siehst aus wie jemand der ein Einwegticket buchen würde. Wohin als nächstes? 🌍`],
      culturist:  [`Kulturliebhaber! Welches Museum hat dich völlig umgehauen? 🎨`, `Paris oder Rom für Kultur? Ich kann mich wirklich nicht entscheiden 😄`, `Welche antike Stätte hat dich am meisten mit der Geschichte verbunden? 🏛️`],
      nomad:      [`Wie lange lebst du schon das Nomadenleben? Ich fange gerade an! 🌍`, `Was ist die beste Co-Working-Stadt in der du gelebt hast? 💻`, `Irgendwelche Tipps für das Verwalten von Zeitzonen? ⏰`],
    },
  };
  const langMap = all[lang] || all.en;
  return [...(langMap[trait] || langMap.explorer)].sort(() => Math.random() - 0.5);
}

const STYLE_OPTS = ['Adventure','Culture','Relax','Foodie','Nightlife'];
const LOCALE_MAP = { bg:'bg-BG', en:'en-GB', es:'es-ES', fr:'fr-FR', de:'de-DE', el:'el-GR', it:'it-IT' };
const EMOJI_CATEGORIES = {
  bg: { travel:'✈️ Пътуване', romance:'❤️ Романтика', fun:'😄 Забавно', nature:'🌿 Природа' },
  en: { travel:'✈️ Travel', romance:'❤️ Romance', fun:'😄 Fun', nature:'🌿 Nature' },
  es: { travel:'✈️ Viaje', romance:'❤️ Romance', fun:'😄 Diversión', nature:'🌿 Naturaleza' },
  fr: { travel:'✈️ Voyage', romance:'❤️ Romance', fun:'😄 Amusement', nature:'🌿 Nature' },
  de: { travel:'✈️ Reise', romance:'❤️ Romantik', fun:'😄 Spaß', nature:'🌿 Natur' },
  el: { travel:'✈️ Ταξίδι', romance:'❤️ Ρομαντισμός', fun:'😄 Διασκέδαση', nature:'🌿 Φύση' },
  it: { travel:'✈️ Viaggio', romance:'❤️ Romanticismo', fun:'😄 Divertimento', nature:'🌿 Natura' },
};
const EMOJIS = {
  travel:  ['✈️','🌍','🗺️','🧳','🏖️','🏔️','🗼','🌅','🚢','🛵','🎒','🏕️','🌴','🗽','🏯','🎡','🚂','🛸','🌋','🏝️','🎠','⛵','🚁','🌠'],
  romance: ['❤️','💕','💫','🌹','💍','🥂','💑','👫','💌','🫀','🌸','💝','🫦','💞','🥰','😍','💏','🌺','💐','🎶','🕯️','🍷','✨','🌙'],
  fun:     ['😄','🤣','😎','🥳','🎉','🎊','🤩','😏','🙈','🤪','🎭','🎪','🃏','🎲','🎯','🏆','🥇','🎸','🎺','🎨','🖼️','🎬','🎤','🪄'],
  nature:  ['🌿','🦋','🐬','🦜','🌊','🌸','🍀','🌞','🌈','⭐','🌙','🪸','🦁','🐘','🦒','🌵','🍂','❄️','🌺','🦅','🐋','🌻','🍄','🪄'],
};

const STICKER_PACKS = [
  {
    key: 'elite',
    icon: '💎',
    tabLabel: { bg:'💎 Elite', en:'💎 Elite', es:'💎 Elite', fr:'💎 Elite', de:'💎 Elite', el:'💎 Elite', it:'💎 Elite' },
    stickers: [
      { emoji:'✈️', anim:'ts-sk-fly',          label:'Airplane' },
      { emoji:'🌍', anim:'ts-sk-spin',         label:'Globe' },
      { emoji:'💝', anim:'ts-sk-pulse',        label:'Heart' },
      { emoji:'🧳', anim:'ts-sk-bounce',       label:'Suitcase' },
      { emoji:'📍', anim:'ts-sk-sparkle',      label:'Pin' },
      { emoji:'📘', anim:'ts-sk-flip',         label:'Passport' },
      { emoji:'📸', anim:'ts-sk-flash',        label:'Camera' },
      { emoji:'🗺️', anim:'ts-sk-unfold',       label:'Map' },
      { emoji:'⛰️', anim:'ts-sk-snowglimmer',  label:'Mountain' },
      { emoji:'🌴', anim:'ts-sk-sway',         label:'Palm' },
      { emoji:'🧭', anim:'ts-sk-compass',      label:'Compass' },
      { emoji:'🎫', anim:'ts-sk-stamp',        label:'Ticket' },
      { emoji:'🏖️', anim:'ts-sk-wave',         label:'Beach' },
      { emoji:'🎈', anim:'ts-sk-balloon',      label:'Balloon' },
      { emoji:'🌟', anim:'ts-sk-starburst',    label:'Star' },
    ],
  },
  {
    key: 'romance',
    icon: '🔥',
    tabLabel: { bg:'🔥 Романс', en:'🔥 Romance', es:'🔥 Romance', fr:'🔥 Romance', de:'🔥 Romanze', el:'🔥 Ρομάντζο', it:'🔥 Romanticismo' },
    stickers: [
      { emoji:'❤️', anim:'ts-sk-float',        label:'Heart' },
      { emoji:'💋', anim:'ts-sk-glow',         label:'Kiss' },
      { emoji:'🌹', anim:'ts-sk-spin',         label:'Rose' },
      { emoji:'🎆', anim:'ts-sk-burst',        label:'Fireworks' },
      { emoji:'💃', anim:'ts-sk-dance',        label:'Dance' },
      { emoji:'🦋', anim:'ts-sk-flutter',      label:'Butterfly' },
      { emoji:'⭐', anim:'ts-sk-twinkle',      label:'Stars' },
      { emoji:'🌙', anim:'ts-sk-moonpulse',    label:'Moon' },
      { emoji:'🥂', anim:'ts-sk-bubble',       label:'Champagne' },
      { emoji:'🌅', anim:'ts-sk-colorshift',   label:'Sunset' },
      { emoji:'🌈', anim:'ts-sk-shimmer',      label:'Rainbow' },
      { emoji:'🕊️', anim:'ts-sk-soar',         label:'Dove' },
      { emoji:'💎', anim:'ts-sk-sparklerotate',label:'Diamond' },
      { emoji:'🕯️', anim:'ts-sk-flicker',      label:'Candle' },
      { emoji:'🎵', anim:'ts-sk-floatup',      label:'Music' },
    ],
  },
];



// ── FAQ data ──────────────────────────────────────────────────────────────────
const FAQ_DATA = [
  { q:'How do TravelSoul subscriptions work?', a:'Premium and Elite subscriptions are available in the Android app through Google Play. Web access reflects the entitlement stored for your account.' },
  { q:'How does the Travel DNA matching work?', a:'Our AI analyzes your travel style, dream destinations and personality to find compatible travel companions.' },
  { q:'Is it safe to travel with strangers?', a:'All profiles are verified. We recommend video chatting before meeting and following our safety guidelines.' },
  { q:"Can I use TravelSoul for friendship only?", a:'Absolutely! TravelSoul is for everyone looking for travel companions, whether for friendship or romance.' },
  { q:'What countries is TravelSoul available in?', a:'TravelSoul is available worldwide in 7 languages: Bulgarian, English, Spanish, French, German, Greek and Italian.' },
];

const FAQ_DATA_BY_LANG = {
  bg: [
    { q:'Как работят абонаментите на TravelSoul?', a:'Абонаментите Premium и Elite са достъпни в Android приложението чрез Google Play. Достъпът в уеб отразява правата, записани за акаунта ти.' },
    { q:'Как работи съвпадението по Travel DNA?', a:'Нашият AI анализира твоя стил на пътуване, мечтани дестинации и личност, за да намери съвместими пътнически спътници.' },
    { q:'Безопасно ли е да пътувам с непознати?', a:'Всички профили са верифицирани. Препоръчваме видео чат преди среща и следване на нашите насоки за безопасност.' },
    { q:'Мога ли да използвам TravelSoul само за приятелство?', a:'Абсолютно! TravelSoul е за всеки, който търси пътнически спътници — за приятелство или романтика.' },
    { q:'В кои държави е достъпен TravelSoul?', a:'TravelSoul е достъпен по целия свят на 7 езика: Български, Английски, Испански, Френски, Немски, Гръцки и Италиански.' },
  ],
  en: FAQ_DATA,
  es: [
    { q:'¿Cómo funcionan las suscripciones de TravelSoul?', a:'Las suscripciones Premium y Elite están disponibles en la app de Android mediante Google Play. El acceso web refleja los derechos guardados para tu cuenta.' },
    { q:'¿Cómo funciona el matching por Travel DNA?', a:'Nuestra IA analiza tu estilo de viaje, destinos soñados y personalidad para encontrar compañeros compatibles.' },
    { q:'¿Es seguro viajar con desconocidos?', a:'Todos los perfiles están verificados. Recomendamos hacer videollamada antes de conocerse.' },
    { q:'¿Puedo usar TravelSoul solo para hacer amigos?', a:'¡Por supuesto! TravelSoul es para todos los que buscan compañeros de viaje, ya sea para amistad o romance.' },
    { q:'¿En qué países está disponible TravelSoul?', a:'TravelSoul está disponible en todo el mundo en 7 idiomas: Búlgaro, Inglés, Español, Francés, Alemán, Griego e Italiano.' },
  ],
  fr: [
    { q:'Comment fonctionnent les abonnements TravelSoul ?', a:'Les abonnements Premium et Elite sont disponibles dans l’application Android via Google Play. L’accès web reflète les droits enregistrés pour votre compte.' },
    { q:'Comment fonctionne le matching Travel DNA ?', a:"Notre IA analyse ton style de voyage, tes destinations de rêve et ta personnalité pour trouver des compagnons compatibles." },
    { q:'Est-il sûr de voyager avec des inconnus ?', a:'Tous les profils sont vérifiés. Nous recommandons une vidéo-conférence avant de se rencontrer.' },
    { q:'Puis-je utiliser TravelSoul uniquement pour me faire des amis ?', a:"Absolument ! TravelSoul est pour tous ceux qui cherchent des compagnons de voyage, que ce soit pour l'amitié ou la romance." },
    { q:'Dans quels pays TravelSoul est-il disponible ?', a:'TravelSoul est disponible dans le monde entier en 7 langues : Bulgare, Anglais, Espagnol, Français, Allemand, Grec et Italien.' },
  ],
  de: [
    { q:'Wie funktionieren TravelSoul-Abonnements?', a:'Premium- und Elite-Abonnements sind in der Android-App über Google Play verfügbar. Der Webzugang entspricht der für dein Konto gespeicherten Berechtigung.' },
    { q:'Wie funktioniert das Travel DNA-Matching?', a:'Unsere KI analysiert deinen Reisestil, Traumziele und Persönlichkeit, um kompatible Reisegefährten zu finden.' },
    { q:'Ist es sicher, mit Fremden zu reisen?', a:'Alle Profile sind verifiziert. Wir empfehlen einen Video-Chat vor dem Treffen.' },
    { q:'Kann ich TravelSoul nur für Freundschaften nutzen?', a:'Absolut! TravelSoul ist für jeden, der Reisegefährten sucht — für Freundschaft oder Romantik.' },
    { q:'In welchen Ländern ist TravelSoul verfügbar?', a:'TravelSoul ist weltweit in 7 Sprachen verfügbar: Bulgarisch, Englisch, Spanisch, Französisch, Deutsch, Griechisch und Italienisch.' },
  ],
  el: [
    { q:'Πώς λειτουργούν οι συνδρομές TravelSoul;', a:'Οι συνδρομές Premium και Elite είναι διαθέσιμες στην εφαρμογή Android μέσω Google Play. Η πρόσβαση στο web αντιστοιχεί στα δικαιώματα που έχουν αποθηκευτεί για τον λογαριασμό σου.' },
    { q:'Πώς λειτουργεί η αντιστοίχιση Travel DNA;', a:'Η τεχνητή νοημοσύνη μας αναλύει το στυλ ταξιδιού, τους ονειρικούς προορισμούς και την προσωπικότητά σου, για να βρει συμβατούς συνταξιδιώτες.' },
    { q:'Είναι ασφαλές να ταξιδεύω με αγνώστους;', a:'Όλα τα προφίλ επαληθεύονται. Συνιστούμε βιντεοκλήση πριν από οποιαδήποτε συνάντηση και τήρηση των οδηγιών ασφαλείας μας.' },
    { q:'Μπορώ να χρησιμοποιήσω το TravelSoul μόνο για φιλία;', a:'Φυσικά! Το TravelSoul απευθύνεται σε όσους αναζητούν συνταξιδιώτες, είτε για φιλία είτε για σχέση.' },
    { q:'Σε ποιες χώρες είναι διαθέσιμο το TravelSoul;', a:'Το TravelSoul είναι διαθέσιμο παγκοσμίως.' },
  ],
  it: [
    { q:'Come funzionano gli abbonamenti TravelSoul?', a:'Gli abbonamenti Premium ed Elite sono disponibili nell’app Android tramite Google Play. L’accesso web riflette i diritti salvati per il tuo account.' },
    { q:'Come funziona la compatibilità Travel DNA?', a:'La nostra IA analizza il tuo stile di viaggio, le destinazioni dei tuoi sogni e la tua personalità per trovare compagni di viaggio compatibili.' },
    { q:'È sicuro viaggiare con persone sconosciute?', a:'Tutti i profili sono verificati. Consigliamo una videochiamata prima di incontrarsi e di seguire le nostre linee guida sulla sicurezza.' },
    { q:'Posso usare TravelSoul solo per fare amicizia?', a:'Certamente! TravelSoul è per chiunque cerchi compagni di viaggio, sia per amicizia sia per una relazione.' },
    { q:'In quali Paesi è disponibile TravelSoul?', a:'TravelSoul è disponibile in tutto il mondo.' },
  ],
};

const DREAM_DESTINATIONS = [
  {flag:'🇯🇵',name:'Japan'},{flag:'🇮🇸',name:'Iceland'},{flag:'🇧🇷',name:'Brazil'},{flag:'🇦🇺',name:'Australia'},
  {flag:'🇲🇦',name:'Morocco'},{flag:'🇹🇭',name:'Thailand'},{flag:'🇵🇹',name:'Portugal'},{flag:'🇳🇴',name:'Norway'},
  {flag:'🇮🇳',name:'India'},{flag:'🇨🇺',name:'Cuba'},{flag:'🇳🇿',name:'New Zealand'},{flag:'🇹🇷',name:'Turkey'},
];

// ── Notification system ───────────────────────────────────────────────────────
function sendNotification(title, body) {
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try { new Notification(title, { body, icon: '/favicon.ico' }); } catch {}
  }
}

function NotificationPermissionModal({ t, onAllow, onLater }) {
  const n = t.notif;
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:340}}>
        <div style={{fontSize:'3rem',marginBottom:'0.75rem'}}>🔔</div>
        <h2 className="ts-serif" style={{color:'var(--gold)',marginBottom:'0.5rem',fontSize:'1.2rem'}}>
          {n.allowNotif}
        </h2>
        <p style={{color:'var(--muted)',fontSize:'0.9rem',marginBottom:'1.5rem'}}>{n.notifDesc}</p>
        <button className="ts-btn-gold-full" onClick={onAllow}>{n.allowNotif} ✓</button>
        <button className="ts-btn-ghost-sm" style={{marginTop:'0.75rem',width:'100%'}} onClick={onLater}>{n.later}</button>
      </div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const devLog = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

async function registerPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    devLog('[push-client] ServiceWorker or PushManager not supported');
    return;
  }
  try {
    devLog('[push-client] Registering SW...');
    await navigator.serviceWorker.register('/sw.js');
    // Wait for SW to be fully active before subscribing
    const reg = await navigator.serviceWorker.ready;
    devLog('[push-client] SW ready, scope:', reg.scope);

    const permission = await Notification.requestPermission();
    devLog('[push-client] Notification permission:', permission);
    if (permission !== 'granted') return;

    devLog('[push-client] Subscribing to push manager...');
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array('BBh_X2b0bq2ut2w3OJykHHFymwR63EvEzdepJ8HoG5k7dpC0T7qBf3np-b03Wz5twabLxVnX-p0tV_AC5wGTj_U'),
    });
    devLog('[push-client] Subscription created:', JSON.stringify(sub));

    // getAuthToken() reads localStorage — fall back to live session if empty
    let { userId } = getAuthToken();
    if (!userId && supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id || null;
    }
    devLog('[push-client] userId:', userId);

    if (userId && supabase) {
      const { error } = await supabase
        .from('profiles')
        .update({ push_subscription: JSON.stringify(sub) })
        .eq('id', userId);
      if (error) {
        devLog('[push-client] Supabase update ERROR:', error.message);
      } else {
        devLog('[push-client] Subscription saved to Supabase ✅');
      }
    } else {
      devLog('[push-client] No userId — subscription NOT saved to Supabase');
    }
  } catch (e) {
    devLog('[push-client] registerPush error:', e.message);
  }
}

function formatNotifBody(body) {
  if (!body) return '';
  if (body.startsWith('GIFT:')) {
    const parts = body.split(':');
    const emoji = parts[1] || '🎁';
    const name = parts[2] || 'Gift';
    return `${emoji} ${name.charAt(0).toUpperCase() + name.slice(1)}`;
  }
  if (body.startsWith('VOICE:')) return '🎙 Voice message';
  if (body.startsWith('STICKER:')) return '🎨 Sticker';
  return body;
}

function formatNotifTime(timestamp, lang = 'bg') {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now - 86400000);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const locale = localeForLang(lang);
  const time = d.toLocaleTimeString(locale, {hour:'2-digit', minute:'2-digit'});
  const todayStr =     { bg:'Днес', en:'Today', es:'Hoy', fr:"Aujourd'hui", de:'Heute', el:'Σήμερα', it:'Oggi' }[lang] || 'Today';
  const yesterdayStr = { bg:'Вчера', en:'Yesterday', es:'Ayer', fr:'Hier', de:'Gestern', el:'Χθες', it:'Ieri' }[lang] || 'Yesterday';
  if (isToday) return `${todayStr} ${time}`;
  if (isYesterday) return `${yesterdayStr} ${time}`;
  return `${d.getDate()}.${d.getMonth()+1}.${d.getFullYear()} ${time}`;
}

function AnnouncementBanner({ lang }) {
  const [ann, setAnn] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { userId } = getAuthToken();
        if (!userId) return;
        const rows = await sbFetch('GET', 'announcements', 'select=id,content,created_at&order=created_at.desc&limit=1');
        const latest = Array.isArray(rows) && rows[0];
        if (!latest) return;
        const reads = await sbFetch('GET', 'announcement_reads', `select=id&user_id=eq.${userId}&announcement_id=eq.${latest.id}&limit=1`);
        if (!cancelled && !(Array.isArray(reads) && reads.length)) setAnn(latest);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);
  if (!ann) return null;
  const dismiss = async () => {
    try {
      const { userId } = getAuthToken();
      await sbFetch('POST', 'announcement_reads', '', { user_id: userId, announcement_id: ann.id, read_at: new Date().toISOString() });
    } catch (e) {}
    setAnn(null);
  };
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background:'linear-gradient(135deg,#c9a84c,#e0c068)',color:'#1a1a1a',fontSize:14,fontWeight:600,position:'relative',zIndex:50}}>
      <span style={{fontSize:18}}>📢</span>
      <span style={{flex:1}}>{ann.content}</span>
      <button onClick={dismiss} style={{background:'rgba(0,0,0,0.15)',border:'none',borderRadius:8,width:28,height:28,cursor:'pointer',color:'#1a1a1a',fontSize:16,fontWeight:700,flexShrink:0}}>✕</button>
    </div>
  );
}

function NotificationBell({ t, lang, notifications, onClearAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const n = t.notif;
  const count = notifications.length;
  useEffect(() => {
    function h(e){
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  }, []);
  const dropdown = (
    <div ref={dropdownRef} className="ts-notif-dropdown">
      <div style={{padding:'16px',borderBottom:'1px solid rgba(255,255,255,0.1)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <p className="ts-notif-title" style={{margin:0}}>{n.notifications}</p>
      </div>
      <div className="ts-notif-list">
        {notifications.length === 0
          ? <p className="ts-notif-empty">{n.noNotifications}</p>
          : notifications.map((notif, i) => (
            <div key={i} className="ts-notif-item">
              <span className="ts-notif-icon">{notif.icon}</span>
              <div><p className="ts-notif-text">{notif.title}</p><p className="ts-notif-sub">{formatNotifBody(notif.body)}</p><p style={{fontSize:'0.72rem',color:'var(--muted)',margin:'2px 0 0'}}>{formatNotifTime(notif.time, lang)}</p></div>
            </div>
          ))
        }
      </div>
      <div style={{flexShrink:0,padding:'8px 12px',borderTop:'1px solid rgba(255,255,255,0.15)',background:'#1a1a2e'}}>
        <button onClick={()=>{ onClearAll&&onClearAll(); setOpen(false); }} style={{width:'100%',padding:'10px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#94a3b8',cursor:'pointer',fontSize:'0.85rem'}}>🗑️ Изчисти всички</button>
      </div>
    </div>
  );
  return (
    <div ref={ref} style={{position:'relative'}}>
      <button type="button" className="ts-notif-bell" onClick={()=>setOpen(o=>!o)} aria-label={n.notifications}>
        🔔
        {count > 0 && <span className="ts-notif-badge">{count > 9 ? '9+' : count}</span>}
      </button>
      {open && createPortal(dropdown, document.body)}
    </div>
  );
}

// ── Profile Screen ────────────────────────────────────────────────────────────
const TRAVEL_TAGS = ['Explorer','Adventurer','Culturist','Nomad','Foodie','Photographer'];

// ── AI Profile Optimizer ──────────────────────────────────────────────────────
function ProfileOptimizerModal({ lang, plan, profileData, onClose }) {
  const isBg = lang === 'bg';
  const isElite = plan === 'elite';

  const [loading, setLoading] = useState(false);
  const [tips, setTips] = useState(null);
  const [error, setError] = useState('');

  // Deterministic local score (no AI needed)
  const score = (() => {
    let s = 0;
    if (profileData.photo) s += 15;
    if ((profileData.photos || []).length >= 3) s += 5;
    const bioLen = (profileData.bio || '').length;
    if (bioLen >= 100) s += 20;
    else if (bioLen >= 30) s += 12;
    else if (bioLen > 0) s += 5;
    const tags = profileData.tags || [];
    if (tags.length >= 3) s += 15;
    else if (tags.length >= 1) s += 8;
    const dreams = profileData.dreams || [];
    if (dreams.length >= 3) s += 15;
    else if (dreams.length >= 1) s += 8;
    if (profileData.videoIntro) s += 10;
    if (profileData.verified) s += 10;
    const bl = profileData.bucketList || [];
    if (bl.length >= 6) s += 5;
    else if (bl.length >= 1) s += 3;
    if (profileData.dnaTitle) s += 5;
    return Math.min(s, 100);
  })();

  useEffect(() => {
    if (!isElite) return;
    generateTips();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function generateTips() {
    setLoading(true); setError('');
    const langName = responseLanguageName(lang);
    const summary = [
      `Photo: ${profileData.photo ? 'yes' : 'no'}`,
      `Gallery photos: ${(profileData.photos || []).length}`,
      `Bio length: ${(profileData.bio || '').length} chars`,
      `Travel styles selected: ${(profileData.tags || []).length}`,
      `Dream destinations: ${(profileData.dreams || []).length}`,
      `Video intro: ${profileData.videoIntro ? 'yes' : 'no'}`,
      `Verified badge: ${profileData.verified ? 'yes' : 'no'}`,
      `Bucket list items: ${(profileData.bucketList || []).length}`,
      `DNA profile title: ${profileData.dnaTitle ? 'set' : 'not set'}`,
      `Overall score: ${score}/100`,
    ].join('\n');
    const prompt = `You are a TravelSoul travel companion profile optimizer. Analyze this travel companion profile and give exactly 4 specific, actionable improvement tips in ${langName}. Be direct, concrete, and mention real percentage impact. Return ONLY a JSON array, no other text:\n[{"icon":"emoji","tip":"specific improvement tip","impact":"+X% more matches or specific benefit"}]\n\nProfile:\n${summary}`;
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role: 'user', content: prompt }], max_tokens: 500 }),
      });
      const data = await res.json();
      const text = data.choices?.[0]?.message?.content?.trim() || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error('parse');
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed) && parsed.length) setTips(parsed);
      else throw new Error('empty');
    } catch {
      setError(lang==='bg'?'Грешка при анализа. Опитай отново.':lang==='es'?'Error en el análisis. Inténtalo de nuevo.':lang==='fr'?'Erreur d\'analyse. Réessayez.':lang==='de'?'Analysefehler. Bitte versuche es erneut.':lang==='el'?'Σφάλμα ανάλυσης. Δοκίμασε ξανά.':lang==='it'?'Errore di analisi. Riprova.':'Analysis error. Please try again.');
    }
    setLoading(false);
  }

  const scoreColor = score >= 80 ? '#22c55e' : score >= 55 ? '#f59e0b' : '#f87171';
  const scoreLbl = score >= 80
    ? (lang==='bg'?'Отличен профил':lang==='es'?'Perfil excelente':lang==='fr'?'Profil excellent':lang==='de'?'Ausgezeichnetes Profil':lang==='el'?'Εξαιρετικό προφίλ':lang==='it'?'Profilo eccellente':'Excellent profile')
    : score >= 55
    ? (lang==='bg'?'Добър профил':lang==='es'?'Buen perfil':lang==='fr'?'Bon profil':lang==='de'?'Gutes Profil':lang==='el'?'Καλό προφίλ':lang==='it'?'Buon profilo':'Good profile')
    : (lang==='bg'?'Нуждае се от подобрение':lang==='es'?'Necesita mejorar':lang==='fr'?'À améliorer':lang==='de'?'Verbesserungsbedürftig':lang==='el'?'Χρειάζεται βελτίωση':lang==='it'?'Da migliorare':'Needs improvement');

  const R = 48, C = 2 * Math.PI * R;
  const dash = (score / 100) * C;

  const fakeTips = [
    { icon: '📸', tip: lang==='bg'?'Добави поне 3 снимки в галерията':lang==='es'?'Añade al menos 3 fotos a tu galería':lang==='fr'?'Ajoute au moins 3 photos à ta galerie':lang==='de'?'Füge mindestens 3 Fotos zu deiner Galerie hinzu':lang==='el'?'Πρόσθεσε τουλάχιστον 3 φωτογραφίες στη γκαλερί σου':lang==='it'?'Aggiungi almeno 3 foto alla tua galleria':'Add at least 3 photos to your gallery', impact: '+35%' },
    { icon: '📝', tip: lang==='bg'?'Напиши 100+ знака в биографията':lang==='es'?'Escribe 100+ caracteres en tu biografía':lang==='fr'?'Écris 100+ caractères dans ta bio':lang==='de'?'Schreibe 100+ Zeichen in deine Bio':lang==='el'?'Γράψε 100+ χαρακτήρες στο βιογραφικό σου':lang==='it'?'Scrivi 100+ caratteri nella tua bio':'Write 100+ characters in your bio', impact: '+28%' },
  ];

  return (
    <div style={{...OVERLAY_S, zIndex:10400}} onClick={onClose}>
      <div style={{...MODAL_S, maxWidth:400, maxHeight:'88vh', textAlign:'left', padding:'24px 20px 20px'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center',marginBottom:'0.5rem'}}>
          ✨ {isBg ? 'AI Оптимизатор на профил' : lang==='es' ? 'Optimizador de Perfil IA' : lang==='fr' ? 'Optimiseur de Profil IA' : lang==='de' ? 'KI-Profiloptimizer' : lang==='el' ? 'Βελτιστοποιητής προφίλ AI' : lang==='it' ? 'Ottimizzatore di profilo IA' : 'AI Profile Optimizer'}
        </h2>
        <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.78rem',marginBottom:'1.25rem'}}>
          {lang==='bg'?'Анализираме профила ти за повече мачове':lang==='es'?'Analizando tu perfil para más coincidencias':lang==='fr'?'Analyse de ton profil pour plus de matchs':lang==='de'?'Wir analysieren dein Profil für mehr Matches':lang==='el'?'Αναλύουμε το προφίλ σου για περισσότερα ταιριάσματα':lang==='it'?'Analizziamo il tuo profilo per più match':'Analyzing your profile for more matches'}
        </p>

        {/* Score ring */}
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',marginBottom:'1.25rem'}}>
          <div style={{position:'relative',width:120,height:120}}>
            <svg width={120} height={120} style={{transform:'rotate(-90deg)'}}>
              <circle cx={60} cy={60} r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={10} />
              <circle cx={60} cy={60} r={R} fill="none"
                stroke={isElite ? scoreColor : 'rgba(255,255,255,0.12)'}
                strokeWidth={10}
                strokeDasharray={`${isElite ? dash : 0} ${C}`}
                strokeLinecap="round" />
            </svg>
            <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
              {isElite ? (
                <>
                  <span style={{fontSize:'1.7rem',fontWeight:800,color:scoreColor,lineHeight:1}}>{score}</span>
                  <span style={{fontSize:'0.68rem',color:'var(--muted)'}}>/100</span>
                </>
              ) : (
                <span style={{fontSize:'1.8rem',color:'var(--muted)',filter:'blur(7px)',userSelect:'none'}}>??</span>
              )}
            </div>
          </div>
          {isElite && <span style={{marginTop:'0.5rem',fontSize:'0.82rem',fontWeight:600,color:scoreColor}}>{scoreLbl}</span>}
        </div>

        {/* Non-Elite — blurred preview + upgrade */}
        {!isElite && (
          <div style={{position:'relative'}}>
            <div style={{filter:'blur(4px)',pointerEvents:'none',userSelect:'none',display:'flex',flexDirection:'column',gap:8}}>
              {fakeTips.map((tip,i) => (
                <div key={i} className="ts-optim-tip-card">
                  <span className="ts-optim-tip-icon">{tip.icon}</span>
                  <p className="ts-optim-tip-text" style={{flex:1,margin:0}}>{tip.tip}</p>
                  <span className="ts-optim-tip-impact">{tip.impact}</span>
                </div>
              ))}
            </div>
            <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(13,17,23,0.8)',borderRadius:12}}>
              <span style={{fontSize:'1.6rem',marginBottom:'0.3rem'}}>💎</span>
              <p style={{color:'var(--gold)',fontWeight:700,fontSize:'0.9rem',margin:'0 0 0.2rem'}}>
                {lang==='bg'?'Elite функция':lang==='es'?'Función Elite':lang==='fr'?'Fonction Elite':lang==='de'?'Elite-Funktion':lang==='el'?'Λειτουργία Elite':lang==='it'?'Funzione Elite':'Elite Feature'}
              </p>
              <p style={{color:'var(--muted)',fontSize:'0.78rem',margin:'0 0 0.75rem',textAlign:'center',padding:'0 1.5rem'}}>
                {lang==='bg'?'Elite за пълен AI анализ и персонален score':lang==='es'?'Elite para análisis IA completo y puntuación personal':lang==='fr'?'Elite pour une analyse IA complète et un score personnel':lang==='de'?'Elite für vollständige KI-Analyse und persönlichen Score':lang==='el'?'Elite για πλήρη ανάλυση AI και προσωπικό σκορ':lang==='it'?'Elite per analisi IA completa e punteggio personale':'Get Elite for full AI analysis and personal score'}
              </p>
              <button className="ts-btn-gold-full" style={{padding:'0.45rem 1.4rem',fontSize:'0.83rem'}} onClick={onClose}>
                💎 {lang==='bg'?'Upgrade към Elite':lang==='es'?'Mejorar a Elite':lang==='fr'?'Passer à Elite':lang==='de'?'Auf Elite upgraden':lang==='el'?'Αναβάθμιση σε Elite':lang==='it'?'Passa a Elite':'Upgrade to Elite'}
              </button>
            </div>
          </div>
        )}

        {/* Elite — AI tips */}
        {isElite && (
          loading ? (
            <div style={{textAlign:'center',padding:'2rem 0',color:'var(--muted)'}}>
              <div style={{width:32,height:32,margin:'0 auto 0.75rem',borderRadius:'50%',border:'3px solid rgba(232,200,112,0.2)',borderTopColor:'var(--gold)',animation:'ts-spin 0.9s linear infinite'}}/>
              <p style={{fontSize:'0.85rem'}}>{lang==='bg'?'AI анализира профила…':lang==='es'?'La IA está analizando el perfil…':lang==='fr'?'L\'IA analyse le profil…':lang==='de'?'Die KI analysiert das Profil…':lang==='el'?'Το AI αναλύει το προφίλ…':lang==='it'?'L\'IA sta analizzando il profilo…':'AI is analyzing your profile…'}</p>
            </div>
          ) : error ? (
            <div style={{textAlign:'center',padding:'1rem 0'}}>
              <p style={{color:'#f87171',fontSize:'0.85rem',marginBottom:'0.75rem'}}>{error}</p>
              <button className="ts-btn-ghost-sm" onClick={generateTips}>{lang==='bg'?'Опитай отново':lang==='es'?'Inténtalo de nuevo':lang==='fr'?'Réessayer':lang==='de'?'Erneut versuchen':lang==='el'?'Δοκίμασε ξανά':lang==='it'?'Riprova':'Try again'}</button>
            </div>
          ) : tips ? (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {tips.map((tip, i) => (
                <div key={i} className="ts-optim-tip-card">
                  <span className="ts-optim-tip-icon">{tip.icon}</span>
                  <p className="ts-optim-tip-text" style={{flex:1,margin:0}}>{tip.tip}</p>
                  <span className="ts-optim-tip-impact">{tip.impact}</span>
                </div>
              ))}
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

// ── BucketListSection ─────────────────────────────────────────────────────────
function BucketListSection({ lang, plan }) {
  const STATUS_LBL   = {
    bg: { want:'Искам да посетя', planned:'Планирано', visited:'Посетено' },
    en: { want:'Want to visit',   planned:'Planned',   visited:'Visited'  },
  };
  const sl = s => (STATUS_LBL[lang] || STATUS_LBL.en)[s] || s;

  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('travelsoul_profile')||'{}').bucket_list || []; } catch { return []; }
  });
  const [input,     setInput]     = useState('');
  const [addStatus, setAddStatus] = useState('want');
  const [saving,    setSaving]    = useState(false);
  const [aiSug,     setAiSug]     = useState('');
  const [aiLoad,    setAiLoad]    = useState(false);
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    (async () => {
      const { userId } = getAuthToken();
      if (!userId) return;
      const rows = await sbFetch('GET', 'profiles', `id=eq.${userId}&select=bucket_list&limit=1`);
      const bl = Array.isArray(rows) && rows[0]?.bucket_list;
      if (bl?.length) {
        setItems(bl);
        try {
          const lsP = JSON.parse(localStorage.getItem('travelsoul_profile')||'{}');
          setProfileLocalStorage({ ...lsP, bucket_list: bl });
        } catch {}
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function persist(newItems) {
    const { userId } = getAuthToken();
    if (!userId) { setSaving(false); return; }
    setSaving(true);
    await sbFetch('PATCH', 'profiles', `id=eq.${userId}`, { bucket_list: newItems }, { Prefer:'return=minimal' });
    try {
      const lsP = JSON.parse(localStorage.getItem('travelsoul_profile')||'{}');
      setProfileLocalStorage({ ...lsP, bucket_list: newItems });
    } catch {}
    setSaving(false);
  }

  async function addItem() {
    const dest = input.trim();
    if (!dest || items.length >= 20 || items.some(i => i.destination.toLowerCase() === dest.toLowerCase())) return;
    setLookupError('');
    setSaving(true);
    const resolved = await fetchCreateDestination(dest, lang);
    if (!resolved) {
      setSaving(false);
      setLookupError(lang==='bg'?'Дестинацията не е намерена.':lang==='es'?'Destino no encontrado.':lang==='fr'?'Destination introuvable.':lang==='de'?'Reiseziel nicht gefunden.':lang==='el'?'Ο προορισμός δεν βρέθηκε.':lang==='it'?'Destinazione non trovata.':'Destination not found.');
      return;
    }
    const resolvedName = lang === 'bg'
      ? (resolved.nameBg || resolved.name || dest)
      : (resolved.nameEn || resolved.name || resolved.nameBg || dest);
    if (items.some(i => i.destination.toLowerCase() === resolvedName.toLowerCase())) {
      setSaving(false);
      setInput('');
      return;
    }
    const next = [...items, { destination: resolvedName, status: addStatus, destinationInfo: resolved }];
    setItems(next); setInput(''); await persist(next);
  }

  function removeItem(dest) {
    const next = items.filter(i => i.destination !== dest);
    setItems(next); persist(next);
  }

  function changeStatus(dest, newSt) {
    const next = items.map(i => i.destination === dest ? { ...i, status: newSt } : i);
    setItems(next); persist(next);
  }

  async function getAiSuggestion() {
    if (items.length < 2 || aiLoad) return;
    setAiLoad(true); setAiSug('');
    const dests = items.map(i => i.destination).join(', ');
    const prompt = `Имам следните дестинации в моя bucket list: ${dests}. Препоръчай ми 4-5 подобни дестинации. Отговори САМО с: "Хора с твоя bucket list обичат също: [д1], [д2], [д3], [д4]" — без допълнителен текст.`;
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role: 'user', content: prompt }], max_tokens: 120 }),
      });
      const data = await res.json();
      setAiSug(data.choices?.[0]?.message?.content?.trim() || '');
    } catch {}
    setAiLoad(false);
  }

  return (
    <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
        <p className="ts-label" style={{margin:0}}>🪣 Bucket List</p>
        <span style={{fontSize:'0.72rem',color:'var(--muted)'}}>{items.length}/20 {saving && '💾…'}</span>
      </div>

      {items.length < 20 && (
        <div style={{display:'flex',gap:6,marginBottom:'0.75rem',flexWrap:'wrap'}}>
          <input className="ts-input" style={{flex:1,minWidth:120}} value={input} onChange={e=>{setInput(e.target.value);setLookupError('');}} onKeyDown={e=>{if(e.key==='Enter') addItem();}} placeholder={lang==='bg'?'Дестинация…':lang==='es'?'Destino…':lang==='fr'?'Destination…':lang==='de'?'Reiseziel…':lang==='el'?'Προορισμός…':lang==='it'?'Destinazione…':'Destination…'} maxLength={60} />
          <select value={addStatus} onChange={e=>setAddStatus(e.target.value)} style={{background:'#161b22',border:'1px solid #30363d',borderRadius:10,color:'#f0f6fc',padding:'10px',fontSize:13,outline:'none'}}>
            <option value="want">🌍 {lang==='bg'?'Искам':lang==='es'?'Quiero':lang==='fr'?'Je veux':lang==='de'?'Ich möchte':lang==='el'?'Θέλω να':lang==='it'?'Voglio':'Want'}</option>
            <option value="planned">✈️ {lang==='bg'?'Планирано':lang==='es'?'Planeado':lang==='fr'?'Prévu':lang==='de'?'Geplant':lang==='el'?'Προγραμματισμένο':lang==='it'?'Pianificato':'Planned'}</option>
            <option value="visited">✅ {lang==='bg'?'Посетено':lang==='es'?'Visitado':lang==='fr'?'Visité':lang==='de'?'Besucht':lang==='el'?'Επισκέφθηκα':lang==='it'?'Visitato':'Visited'}</option>
          </select>
          <button className="ts-btn-ghost-sm" onClick={addItem} disabled={!input.trim() || saving}>{saving ? '…' : '+'}</button>
        </div>
      )}
      {lookupError && <p style={{color:'#f85149',fontSize:'0.78rem',margin:'-0.35rem 0 0.75rem'}}>{lookupError}</p>}

      {items.length === 0 ? (
        <p style={{color:'var(--muted)',fontSize:'0.85rem',textAlign:'center',padding:'0.75rem 0'}}>{lang==='bg'?'Добави своите мечтани дестинации':lang==='es'?'Añade tus destinos soñados':lang==='fr'?'Ajoutez vos destinations de rêve':lang==='de'?'Füge deine Traumziele hinzu':lang==='el'?'Πρόσθεσε τους προορισμούς των ονείρων σου':lang==='it'?'Aggiungi le tue destinazioni da sogno':'Add your dream destinations'}</p>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:7,marginBottom:'0.75rem'}}>
          {items.map(item => (
            <div key={item.destination} className="ts-bucket-item">
              <select value={item.status} onChange={e=>changeStatus(item.destination,e.target.value)} style={{background:'transparent',border:'none',color:'#f0f6fc',fontSize:14,outline:'none',cursor:'pointer',padding:0,minWidth:22}}>
                <option value="want">🌍</option>
                <option value="planned">✈️</option>
                <option value="visited">✅</option>
              </select>
              <span style={{flex:1,fontSize:'0.88rem',color:'var(--text)'}}>{item.destination}</span>
              <span style={{fontSize:'0.7rem',color:'var(--muted)',whiteSpace:'nowrap'}}>{sl(item.status)}</span>
              <button onClick={()=>removeItem(item.destination)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:11,padding:'0 2px',lineHeight:1}}>✕</button>
            </div>
          ))}
        </div>
      )}

      {plan==='elite' && items.length >= 2 && (
        <div>
          <button className="ts-btn-ghost-sm" style={{fontSize:'0.78rem',width:'100%'}} onClick={getAiSuggestion} disabled={aiLoad}>
            {aiLoad ? '…' : '✨ '+(lang==='bg'?'AI препоръки':lang==='es'?'Sugerencias de IA':lang==='fr'?'Suggestions IA':lang==='de'?'KI-Vorschläge':lang==='el'?'Προτάσεις AI':lang==='it'?'Suggerimenti IA':'AI suggestions')}
          </button>
          {aiSug && <p style={{fontSize:'0.82rem',color:'#b47fff',marginTop:'0.5rem',lineHeight:1.5,fontStyle:'italic'}}>{aiSug}</p>}
        </div>
      )}
    </div>
  );
}

const ZODIAC_CALC = [
  { sign:'♈', bgName:'Овен',     enName:'Aries',       esName:'Aries',       frName:'Bélier',     deName:'Widder',       elName:'Κριός',       itName:'Ariete'      },
  { sign:'♉', bgName:'Телец',    enName:'Taurus',      esName:'Tauro',       frName:'Taureau',    deName:'Stier',        elName:'Ταύρος',      itName:'Toro'        },
  { sign:'♊', bgName:'Близнаци', enName:'Gemini',      esName:'Géminis',     frName:'Gémeaux',    deName:'Zwillinge',    elName:'Δίδυμοι',     itName:'Gemelli'     },
  { sign:'♋', bgName:'Рак',      enName:'Cancer',      esName:'Cáncer',      frName:'Cancer',     deName:'Krebs',        elName:'Καρκίνος',    itName:'Cancro'      },
  { sign:'♌', bgName:'Лъв',      enName:'Leo',         esName:'Leo',         frName:'Lion',       deName:'Löwe',         elName:'Λέων',        itName:'Leone'       },
  { sign:'♍', bgName:'Дева',     enName:'Virgo',       esName:'Virgo',       frName:'Vierge',     deName:'Jungfrau',     elName:'Παρθένος',    itName:'Vergine'     },
  { sign:'♎', bgName:'Везни',    enName:'Libra',       esName:'Libra',       frName:'Balance',    deName:'Waage',        elName:'Ζυγός',       itName:'Bilancia'    },
  { sign:'♏', bgName:'Скорпион', enName:'Scorpio',     esName:'Escorpio',    frName:'Scorpion',   deName:'Skorpion',     elName:'Σκορπιός',    itName:'Scorpione'   },
  { sign:'♐', bgName:'Стрелец',  enName:'Sagittarius', esName:'Sagitario',   frName:'Sagittaire', deName:'Schütze',      elName:'Τοξότης',     itName:'Sagittario'  },
  { sign:'♑', bgName:'Козирог',  enName:'Capricorn',   esName:'Capricornio', frName:'Capricorne', deName:'Steinbock',    elName:'Αιγόκερως',   itName:'Capricorno'  },
  { sign:'♒', bgName:'Водолей',  enName:'Aquarius',    esName:'Acuario',     frName:'Verseau',    deName:'Wassermann',   elName:'Υδροχόος',    itName:'Acquario'    },
  { sign:'♓', bgName:'Риби',     enName:'Pisces',      esName:'Piscis',      frName:'Poissons',   deName:'Fische',       elName:'Ιχθύες',      itName:'Pesci'       },
];
function getZodiacSign(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr); if (isNaN(d.getTime())) return '';
  const m = d.getMonth()+1, day = d.getDate();
  if ((m===3&&day>=21)||(m===4&&day<=19)) return '♈';
  if ((m===4&&day>=20)||(m===5&&day<=20)) return '♉';
  if ((m===5&&day>=21)||(m===6&&day<=20)) return '♊';
  if ((m===6&&day>=21)||(m===7&&day<=22)) return '♋';
  if ((m===7&&day>=23)||(m===8&&day<=22)) return '♌';
  if ((m===8&&day>=23)||(m===9&&day<=22)) return '♍';
  if ((m===9&&day>=23)||(m===10&&day<=22)) return '♎';
  if ((m===10&&day>=23)||(m===11&&day<=21)) return '♏';
  if ((m===11&&day>=22)||(m===12&&day<=21)) return '♐';
  if ((m===12&&day>=22)||(m===1&&day<=19)) return '♑';
  if ((m===1&&day>=20)||(m===2&&day<=18)) return '♒';
  return '♓';
}
function getZodiacName(sign, lang) {
  const z = ZODIAC_CALC.find(x=>x.sign===sign);
  return z ? (lang==='bg'?z.bgName:lang==='es'?z.esName:lang==='fr'?z.frName:lang==='de'?z.deName:lang==='el'?z.elName:lang==='it'?z.itName:z.enName) : '';
}

function BlockedUsersPanel({ lang }) {
  const labels = getBlockLabels(lang);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  async function loadBlockedUsers() {
    setLoading(true);
    setError('');
    try {
      const activeProfileId = await getActiveProfileId({ forceFresh: true });
      if (!activeProfileId) { setRows([]); return; }
      const blocks = await sbFetch('GET', 'user_blocks', `blocker_id=eq.${activeProfileId}&select=id,blocked_id,created_at&order=created_at.desc`);
      const ids = (Array.isArray(blocks) ? blocks : []).map(row => profileIdKey(row.blocked_id)).filter(Boolean);
      let profiles = [];
      if (ids.length > 0) {
        profiles = await sbFetch('GET', 'profiles', `id=in.(${ids.join(',')})&select=id,name,photo_url,country,city`);
      }
      const profileMap = {};
      (Array.isArray(profiles) ? profiles : []).forEach(p => { profileMap[profileIdKey(p.id)] = p; });
      setRows((Array.isArray(blocks) ? blocks : []).map(row => ({ ...row, profile: profileMap[profileIdKey(row.blocked_id)] || null })));
    } catch (e) {
      setError(labels.loadError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) loadBlockedUsers();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUnblock(blockedId) {
    const id = profileIdKey(blockedId);
    if (!id) return;
    setBusyId(id);
    try {
      await unblockProfile(id);
      setRows(list => list.filter(row => profileIdKey(row.blocked_id) !== id));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
      <button className="ts-hotel-btn" style={{width:'100%',justifyContent:'space-between'}} onClick={()=>setOpen(v=>!v)}>
        <span>🛡️ {labels.blockedUsers}</span>
        <span style={{color:'var(--muted)'}}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{marginTop:10,background:'#0d1117',border:'1px solid #30363d',borderRadius:12,padding:12}}>
          {loading ? (
            <div style={{color:'#8b949e',fontSize:'0.85rem',textAlign:'center',padding:12}}>...</div>
          ) : error ? (
            <div style={{color:'#f85149',fontSize:'0.85rem',textAlign:'center',padding:12}}>{error}</div>
          ) : rows.length === 0 ? (
            <div style={{color:'#8b949e',fontSize:'0.85rem',textAlign:'center',padding:12}}>{labels.noBlocked}</div>
          ) : rows.map(row => {
            const p = row.profile;
            const blockedId = profileIdKey(row.blocked_id);
            return (
              <div key={row.id || blockedId} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #21262d'}}>
                {p?.photo_url
                  ? <img src={getProfilePhotoUrl(p.photo_url, '')} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover'}}/>
                  : <div style={{width:36,height:36,borderRadius:'50%',background:'#21262d',display:'flex',alignItems:'center',justifyContent:'center'}}>👤</div>
                }
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:'#f0f6fc',fontSize:'0.88rem',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p?.name || (lang==='bg'?'Потребител':lang==='es'?'Usuario':lang==='fr'?'Utilisateur':lang==='de'?'Benutzer':lang==='el'?'Χρήστης':lang==='it'?'Utente':'User')}</div>
                  {(p?.city || p?.country) && <div style={{color:'#8b949e',fontSize:'0.75rem'}}>{[p.city, p.country].filter(Boolean).join(', ')}</div>}
                </div>
                <button className="ts-btn-ghost-sm" disabled={busyId === blockedId} onClick={()=>handleUnblock(blockedId)}>
                  {busyId === blockedId ? '...' : labels.unblock}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProfileScreen({ t, lang, onBack, joinedTrips, plan = 'free' }) {
  const p = t.profile;
  const savedUser = safeGetJson('travelsoul_user', {});
  const savedProfile = safeGetJson('travelsoul_profile', {});
  const savedPhoto = localStorage.getItem('travelsoul_photo') || null;

  const [form, setForm] = useState({
    firstName: savedUser.firstName || savedProfile.firstName || '',
    lastName: savedUser.lastName || savedProfile.lastName || '',
    age: savedProfile.age || '',
    city: savedProfile.city || '',
    country: savedUser.country || savedProfile.country || '',
    bio: savedProfile.bio || '',
    tags: savedProfile.tags || [],
    dreams: savedProfile.dreams || [],
    dreamInput: '',
    instagram: savedProfile.instagram || '',
    lookingFor: savedProfile.lookingFor || [],
    gender: savedProfile.gender || savedUser.gender || 'male',
  });
  const [photo, setPhoto] = useState(savedPhoto);
  const [photos, setPhotos] = useState([]);
  const [videoIntro, setVideoIntro] = useState('');
  const [toast, setToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [verified, setVerified] = useState(() => {
    return !!(safeGetJson('travelsoul_user', {})).verified;
  });
  const [verificationStatus, setVerificationStatus] = useState('unverified');
  const [verificationSelfieUrl, setVerificationSelfieUrl] = useState('');
  const [verificationFrontUrl, setVerificationFrontUrl] = useState('');
  const [verificationRejectionReason, setVerificationRejectionReason] = useState('');
  const [verificationUploading, setVerificationUploading] = useState(null);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [showDnaModal, setShowDnaModal] = useState(false);
  const [dnaTitle, setDnaTitle] = useState(savedProfile.dna_title || '');
  const [showMapModal, setShowMapModal] = useState(false);
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [customBadge, setCustomBadge] = useState(() => {
    return safeGetJson('travelsoul_profile', {}).custom_badge || null;
  });
  const [showBadgeCreator, setShowBadgeCreator] = useState(false);
  const [incognito, setIncognito] = useState(() => localStorage.getItem('ts_incognito') === '1');
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [cropPreviewUrl, setCropPreviewUrl] = useState(null);
  const [cropOffsetX, setCropOffsetX] = useState(0);
  const [cropOffsetY, setCropOffsetY] = useState(0);
  const [cropScale, setCropScale] = useState(1);
  const [lifestyle, setLifestyle] = useState({
    birthDate: savedProfile.birth_date||'', height: savedProfile.height||170,
    bodyType: savedProfile.body_type||'', education: savedProfile.education||'',
    occupation: savedProfile.occupation||'', smoking: savedProfile.smoking||'',
    alcohol: savedProfile.alcohol||'', children: savedProfile.children||'',
    pets: savedProfile.pets||[], diet: savedProfile.diet||'',
  });
  const [prefs, setPrefs] = useState({
    ageMin: savedProfile.preferred_age_min||18, ageMax: savedProfile.preferred_age_max||65,
    zodiac: savedProfile.preferred_zodiac||[], smoking: savedProfile.preferred_smoking||'ok',
    alcohol: savedProfile.preferred_alcohol||'ok', children: savedProfile.preferred_children||'any',
    heightMin: savedProfile.preferred_height_min||150, heightMax: savedProfile.preferred_height_max||210,
    bodyType: savedProfile.preferred_body_type||[], goal: savedProfile.relationship_goal||'',
  });
  const zodiacSign = getZodiacSign(lifestyle.birthDate);
  const fileRef = useRef(null);
  const galleryPhotoRef = useRef(null);
  const verificationSelfieRef = useRef(null);
  const verificationFrontRef = useRef(null);
  const videoRef = useRef(null);
  const cropDragRef = useRef(null);

  // Load real profile data from Supabase on mount
  useEffect(() => {
    async function loadProfile() {
      const { userId, accessToken } = await getAuthTokenSafe({ forceFresh: true });
      if (!userId || !accessToken) return;
      let rows;
      try { rows = await sbFetch('GET', 'profiles', `id=eq.${userId}&select=*&limit=1`); } catch (e) { return; }
      const row = Array.isArray(rows) ? rows[0] : null;
      if (!row) return;
      const nameParts = (row.name || '').trim().split(' ');
      setForm(f => ({
        ...f,
        firstName: nameParts[0] || f.firstName,
        lastName: nameParts.slice(1).join(' ') || f.lastName,
        age: row.age != null ? String(row.age) : f.age,
        city: row.city || f.city,
        country: row.country || f.country,
        bio: row.bio || f.bio,
        tags: row.travel_dna || f.tags,
        dreams: row.dream_destinations || f.dreams,
        instagram: row.instagram || f.instagram,
        lookingFor: row.looking_for || f.lookingFor,
        gender: row.gender || f.gender,
      }));
      if (row.photo_url) {
        const displayUrl = getProfilePhotoUrl(row.photo_url, '');
        setPhoto(displayUrl);
        localStorage.setItem('travelsoul_photo', displayUrl);
      }
      if (row.photos) setPhotos(row.photos);
      if (row.video_intro) setVideoIntro(row.video_intro);
      if (row.dna_title) setDnaTitle(row.dna_title);
      if (row.custom_badge) setCustomBadge(row.custom_badge);
      setVerificationStatus(getVerificationStatus(row));
      setVerificationSelfieUrl(row.verification_selfie_url || '');
      setVerificationFrontUrl(row.verification_front_url || '');
      setVerificationRejectionReason(row.verification_rejection_reason || '');
      if (canUseSocialFeatures(row)) {
        setVerified(true);
        try {
          const lsUser = JSON.parse(localStorage.getItem('travelsoul_user') || '{}');
          if (!lsUser.verified) localStorage.setItem('travelsoul_user', JSON.stringify({ ...lsUser, verified: true }));
        } catch {}
      }
    }
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function loadViewers() {
      const { userId } = getAuthToken();
      if (!userId) return;
      setViewersLoading(true);
      const limit = plan === 'free' ? 10 : 30;
      const data = await sbFetch('GET', 'profile_views',
        `viewed_id=eq.${userId}&select=viewer_id,viewed_at&order=viewed_at.desc&limit=${limit}`
      );
      if (!Array.isArray(data) || !data.length) { setViewersLoading(false); return; }
      const ids = [...new Set(data.map(r => r.viewer_id))];
      const profiles = await sbFetch('GET', 'profiles', `id=in.(${ids.join(',')})&select=id,name,photo_url`);
      const profileMap = {};
      if (Array.isArray(profiles)) profiles.forEach(p => { profileMap[p.id] = p; });
      setViewers(data.map(r => ({ ...r, profile: profileMap[r.viewer_id] || null })));
      setViewersLoading(false);
    }
    loadViewers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function showPhotoToast(msg) { setToastMsg(msg); setToast(true); setTimeout(() => setToast(false), 2500); }

  // eslint-disable-next-line no-unused-vars
  function compressImage(file) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const reader = new FileReader();
      reader.onload = e => {
        img.onload = () => {
          const maxSize = 200;
          let w = img.width, h = img.height;
          if (w > h) { if (w > maxSize) { h *= maxSize / w; w = maxSize; } }
          else { if (h > maxSize) { w *= maxSize / h; h = maxSize; } }
          canvas.width = w; canvas.height = h;
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function handlePhoto(e) {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    if (!isAllowedImageFile(file)) {
      showPhotoToast(lang==='bg'?'❌ Само JPEG/PNG/WebP снимки до 10MB':lang==='es'?'❌ Solo se permiten imágenes JPEG/PNG/WebP de hasta 10MB':lang==='fr'?'❌ Seules les images JPEG/PNG/WebP jusqu\'à 10 Mo sont autorisées':lang==='de'?'❌ Nur JPEG/PNG/WebP-Bilder bis 10MB sind erlaubt':lang==='el'?'❌ Επιτρέπονται μόνο εικόνες JPEG/PNG/WebP έως 10MB':lang==='it'?'❌ Sono consentite solo immagini JPEG/PNG/WebP fino a 10MB':'❌ Only JPEG/PNG/WebP images up to 10MB are allowed');
      return;
    }
    setCropFile(file);
    setCropPreviewUrl(URL.createObjectURL(file));
    setCropOffsetX(0); setCropOffsetY(0); setCropScale(1);
  }

  function onCropMouseDown(e) {
    e.preventDefault();
    cropDragRef.current = { startX: e.clientX, startY: e.clientY, startOX: cropOffsetX, startOY: cropOffsetY };
    function onMove(ev) {
      setCropOffsetX(cropDragRef.current.startOX + ev.clientX - cropDragRef.current.startX);
      setCropOffsetY(cropDragRef.current.startOY + ev.clientY - cropDragRef.current.startY);
    }
    function onUp() { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }
  function onCropTouchStart(e) {
    if (e.touches.length !== 1) return;
    const t0 = e.touches[0];
    cropDragRef.current = { startX: t0.clientX, startY: t0.clientY, startOX: cropOffsetX, startOY: cropOffsetY };
  }
  function onCropTouchMove(e) {
    if (!cropDragRef.current || e.touches.length !== 1) return;
    const t0 = e.touches[0];
    setCropOffsetX(cropDragRef.current.startOX + t0.clientX - cropDragRef.current.startX);
    setCropOffsetY(cropDragRef.current.startOY + t0.clientY - cropDragRef.current.startY);
  }
  function cancelCrop() { setCropFile(null); setCropPreviewUrl(null); }

  async function confirmCrop() {
    if (!cropPreviewUrl || !cropFile) return;
    try {
      const img = new Image();
      img.src = cropPreviewUrl;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      const canvas = document.createElement('canvas');
      canvas.width = 200; canvas.height = 200;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0d1117';
      ctx.fillRect(0, 0, 200, 200);
      const displayedW = 200 * cropScale;
      const displayedH = (img.naturalHeight / img.naturalWidth) * displayedW;
      const imgLeft = 100 + cropOffsetX - displayedW / 2;
      const imgTop  = 100 + cropOffsetY - displayedH / 2;
      const sx = (-imgLeft) * img.naturalWidth  / displayedW;
      const sy = (-imgTop)  * img.naturalHeight / displayedH;
      const sw = 200 * img.naturalWidth  / displayedW;
      const sh = 200 * img.naturalHeight / displayedH;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 200, 200);
      const croppedBase64 = canvas.toDataURL('image/jpeg', 0.85);
      cancelCrop();
      // Upload cropped image
      if (supabase) {
        const { userId, accessToken } = await getAuthTokenSafe({ forceFresh: true });
        if (!userId || !accessToken) throw new Error('Missing active Supabase session for photo upload.');
        const fetchRes = await fetch(croppedBase64);
        const blob = await fetchRes.blob();
        const croppedFile = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
        const path = `${userId}/avatar.jpg`;
        const { error: upErr } = await supabase.storage.from(PROFILE_PHOTO_BUCKET).upload(path, croppedFile, { upsert: true, contentType: 'image/jpeg' });
        if (upErr) {
          console.error('[PROFILE_AVATAR_UPLOAD_ERROR]', {
            bucket: PROFILE_PHOTO_BUCKET,
            path,
            stage: 'storage-upload',
            errorCode: upErr?.code,
            errorMessage: upErr?.message,
            errorDetails: upErr?.details,
            errorHint: upErr?.hint,
            status: upErr?.status,
          });
          throw upErr;
        }
        const { data: urlData } = supabase.storage.from(PROFILE_PHOTO_BUCKET).getPublicUrl(path);
        const photoUrl = urlData?.publicUrl;
        if (!photoUrl) throw new Error('Supabase did not return a public avatar URL.');
        const saveRes = await sbFetch('POST', 'profiles', '', { id: userId, photo_url: photoUrl }, { 'Prefer': 'resolution=merge-duplicates' });
        await ensureSbWriteOk(saveRes, 'profile avatar save');
        const displayUrl = getProfilePhotoUrl(photoUrl, '');
        localStorage.setItem('travelsoul_photo', displayUrl);
        setPhoto(displayUrl);
        showPhotoToast(t.extras?.photoSaved || '✅ Photo saved!');
        return;
      }
      localStorage.setItem('travelsoul_photo', croppedBase64);
      setPhoto(croppedBase64);
      showPhotoToast(t.extras?.photoSaved || '✅ Photo saved!');
    } catch (err) {
      console.error('[PROFILE_AVATAR_SAVE_ERROR]', {
        bucket: PROFILE_PHOTO_BUCKET,
        stage: 'profile-avatar-save',
        errorCode: err?.code,
        errorMessage: err?.message,
        errorDetails: err?.details,
        errorHint: err?.hint,
        status: err?.status,
      });
      showPhotoToast(`❌ ${err?.message || ((FT[lang]||FT.en).uploadError || 'Upload error')}`);
      cancelCrop();
    }
  }

  async function handleGalleryPhoto(e) {
    const file = e.target.files[0]; if (!file) return;
    if (photos.length >= 6) return;
    const ft = FT[lang] || FT.en;
    // Audit finding: the <input accept="image/*"> attribute is a UI hint only
    // and is trivially bypassed (devtools, direct file picker override, or a
    // direct call into this handler) — validate the actual file here.
    if (!isAllowedImageFile(file)) {
      showPhotoToast(ft.uploadError || '❌ Only JPEG/PNG/WebP images up to 10MB are allowed');
      return;
    }
    setUploadingPhoto(true);
    try {
      const { userId, accessToken } = await getAuthTokenSafe({ forceFresh: true });
      if (!userId || !accessToken || !supabase) throw new Error('no auth');
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${userId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('profile-photos').upload(path, file, { contentType: file.type });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(path);
      const url = urlData?.publicUrl;
      if (!url) throw new Error('no url');
      const newPhotos = [...photos, url];
      await sbFetch('POST', 'profiles', '', { id: userId, photos: newPhotos }, { 'Prefer': 'resolution=merge-duplicates', 'Authorization': `Bearer ${accessToken}` });
      setPhotos(newPhotos);
      showPhotoToast(ft.saved || '✅ Saved!');
    } catch (err) {
      console.error('Gallery upload failed:', err);
      showPhotoToast(`❌ ${err?.message || ((FT[lang]||FT.en).uploadError || 'Upload error')}`);
    }
    setUploadingPhoto(false);
  }

  async function handleVerificationPhoto(e, kind) {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    if (!isAllowedImageFile(file)) {
      showPhotoToast(lang==='bg'?'❌ Само JPEG/PNG/WebP снимки до 10MB':lang==='es'?'❌ Solo se permiten imágenes JPEG/PNG/WebP de hasta 10MB':lang==='fr'?'❌ Seules les images JPEG/PNG/WebP jusqu\'à 10 Mo sont autorisées':lang==='de'?'❌ Nur JPEG/PNG/WebP-Bilder bis 10MB sind erlaubt':lang==='el'?'❌ Επιτρέπονται μόνο εικόνες JPEG/PNG/WebP έως 10MB':lang==='it'?'❌ Sono consentite solo immagini JPEG/PNG/WebP fino a 10MB':'❌ Only JPEG/PNG/WebP images up to 10MB are allowed');
      return;
    }
    setVerificationUploading(kind);
    try {
      const { userId, accessToken } = await getAuthTokenSafe({ forceFresh: true });
      if (!userId || !accessToken || !supabase) throw new Error('no auth');
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const safeKind = kind === 'front' ? 'front' : 'selfie';
      const path = `${userId}/verification-${safeKind}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('profile-photos').upload(path, file, { contentType: file.type || 'image/jpeg', upsert: false });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(path);
      const url = urlData?.publicUrl;
      if (!url) throw new Error('no url');
      if (safeKind === 'front') setVerificationFrontUrl(url);
      else setVerificationSelfieUrl(url);
      showPhotoToast(lang==='bg'?'✅ Снимката е качена':lang==='es'?'✅ Foto subida':lang==='fr'?'✅ Photo téléchargée':lang==='de'?'✅ Foto hochgeladen':lang==='el'?'✅ Η φωτογραφία ανέβηκε':lang==='it'?'✅ Foto caricata':'✅ Photo uploaded');
    } catch (err) {
      console.error('[VerificationUpload] failed:', err);
      showPhotoToast(`❌ ${err?.message || ((FT[lang]||FT.en).uploadError || 'Upload error')}`);
    } finally {
      setVerificationUploading(null);
    }
  }

  async function submitVerification() {
    if (!verificationSelfieUrl || !verificationFrontUrl || verificationSubmitting) return;
    setVerificationSubmitting(true);
    try {
      const res = await sbFetch('POST', 'rpc/submit_profile_verification', '', {
        selfie_url: verificationSelfieUrl,
        front_url: verificationFrontUrl,
      });
      await ensureSbWriteOk(res, 'submit profile verification');
      let status = 'pending';
      try {
        const body = await res.text();
        status = body ? JSON.parse(body) : status;
      } catch (e) {}
      if (!['pending', 'approved', 'rejected'].includes(status)) {
        throw new Error(status || 'verification_submit_failed');
      }
      setVerificationStatus(status || 'pending');
      if (status === 'pending') {
        setVerificationRejectionReason('');
        showPhotoToast(lang==='bg'?'✅ Изпратено за верификация':lang==='es'?'✅ Enviado para verificación':lang==='fr'?'✅ Envoyé pour vérification':lang==='de'?'✅ Zur Verifizierung gesendet':lang==='el'?'✅ Στάλθηκε για επαλήθευση':lang==='it'?'✅ Inviata per la verifica':'✅ Sent for verification');
      } else {
        showPhotoToast(getVerificationStatusText({ verification_status: status }, lang));
      }
    } catch (err) {
      console.error('[VerificationSubmit] failed:', err);
      showPhotoToast(`❌ ${err?.message || ((FT[lang]||FT.en).uploadError || 'Verification error')}`);
    } finally {
      setVerificationSubmitting(false);
    }
  }

  async function handleDeletePhoto(url) {
    const ft = FT[lang] || FT.en;
    const { userId, accessToken } = getAuthToken();
    if (!userId || !accessToken) return;
    const newPhotos = photos.filter(u => u !== url);
    setPhotos(newPhotos);
    await sbFetch('POST', 'profiles', '', { id: userId, photos: newPhotos }, { 'Prefer': 'resolution=merge-duplicates', 'Authorization': `Bearer ${accessToken}` });
    showPhotoToast(ft.deleteVideo || '🗑️ Deleted');
  }

  async function handleVideo(e) {
    const file = e.target.files[0]; if (!file) return;
    const ft = FT[lang] || FT.en;
    if (file.size > 30 * 1024 * 1024) { showPhotoToast(ft.uploadError || '❌ Max 30MB'); return; }
    setUploadingVideo(true);
    try {
      const { userId, accessToken } = getAuthToken();
      if (!userId || !accessToken || !supabase) throw new Error('no auth');
      const ext = file.name.split('.').pop() || 'mp4';
      const path = `${userId}/intro.${ext}`;
      const { error: upErr } = await supabase.storage.from('profile-videos').upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('profile-videos').getPublicUrl(path);
      const url = urlData?.publicUrl;
      if (!url) throw new Error('no url');
      setVideoIntro(url);
      await sbFetch('POST', 'profiles', '', { id: userId, video_intro: url }, { 'Prefer': 'resolution=merge-duplicates', 'Authorization': `Bearer ${accessToken}` });
      showPhotoToast(ft.saved || '✅ Saved!');
    } catch (err) {
      console.error('Video upload failed:', err);
      showPhotoToast((FT[lang]||FT.en).uploadError || '❌ Upload error');
    }
    setUploadingVideo(false);
  }

  async function handleDeleteVideo() {
    const ft = FT[lang] || FT.en;
    const { userId, accessToken } = getAuthToken();
    if (!userId || !accessToken) return;
    setVideoIntro('');
    await sbFetch('POST', 'profiles', '', { id: userId, video_intro: null }, { 'Prefer': 'resolution=merge-duplicates', 'Authorization': `Bearer ${accessToken}` });
    showPhotoToast(ft.deleteVideo || '🗑️ Deleted');
  }

  function toggleTag(tag) {
    setForm(f => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter(x=>x!==tag) : [...f.tags, tag] }));
  }

  function addDream() {
    const d = form.dreamInput.trim(); if (!d || form.dreams.includes(d) || form.dreams.length >= 5) return;
    setForm(f => ({ ...f, dreams: [...f.dreams, d], dreamInput: '' }));
  }

  function removeDream(d) { setForm(f => ({ ...f, dreams: f.dreams.filter(x=>x!==d) })); }

  function toggleGoal(k) {
    setForm(f => ({ ...f, lookingFor: f.lookingFor.includes(k) ? f.lookingFor.filter(x=>x!==k) : [...f.lookingFor, k] }));
  }

  async function save() {
    // Audit finding: setSaving(true) used to fire only at the very end,
    // AFTER the network round-trip to Supabase already resolved — so the
    // Save button stayed enabled during the actual request and a slow
    // connection let a double-click fire two duplicate upserts. Guard and
    // set saving=true up front instead.
    if (saving) return;
    if (lifestyle.birthDate && calcAge(lifestyle.birthDate) < 18) {
      setToastMsg(t.register.ageError);
      setToast(true);
      setTimeout(() => setToast(false), 4000);
      return;
    }
    setSaving(true);
    const profile = {
      // Audit finding: the full profile — including free-text bio — was
      // mirrored into localStorage in plaintext, readable by any script or
      // browser extension with page access (XSS blast radius). bio is
      // deliberately left out of this localStorage mirror; Supabase (via
      // payload below) is the real source of truth and ProfileScreen already
      // re-hydrates bio from there on mount, so this only costs a brief
      // empty textarea before that fetch resolves, not lost data.
      firstName:form.firstName, lastName:form.lastName, age:form.age, city:form.city, country:form.country,
      tags:form.tags, dreams:form.dreams, instagram:form.instagram, lookingFor:form.lookingFor, gender:form.gender,
      birth_date:lifestyle.birthDate||null, zodiac_sign:zodiacSign||null, height:lifestyle.height||null,
      body_type:lifestyle.bodyType||null, education:lifestyle.education||null, occupation:lifestyle.occupation||null,
      smoking:lifestyle.smoking||null, alcohol:lifestyle.alcohol||null, children:lifestyle.children||null,
      pets:lifestyle.pets.length?lifestyle.pets:null, diet:lifestyle.diet||null,
      preferred_age_min:prefs.ageMin, preferred_age_max:prefs.ageMax,
      preferred_zodiac:prefs.zodiac.length?prefs.zodiac:null,
      preferred_smoking:prefs.smoking||null, preferred_alcohol:prefs.alcohol||null,
      preferred_children:prefs.children||null,
      preferred_height_min:prefs.heightMin, preferred_height_max:prefs.heightMax,
      preferred_body_type:prefs.bodyType.length?prefs.bodyType:null,
      relationship_goal:prefs.goal||null,
    };
    setProfileLocalStorage(profile);
    const lsUser = JSON.parse(localStorage.getItem('travelsoul_user') || '{}');
    localStorage.setItem('travelsoul_user', JSON.stringify({ ...lsUser, firstName:form.firstName, lastName:form.lastName, gender:form.gender }));
    if (supabase) {
      // Read access token directly from localStorage — bypasses getSession() client state
      const { accessToken, userId } = getAuthToken();
      if (userId && accessToken) {
        const payload = {
          id: userId,
          name: `${form.firstName} ${form.lastName}`.trim(),
          age: form.age ? parseInt(form.age, 10) : null,
          city: form.city || null,
          country: form.country || null,
          bio: form.bio || null,
          travel_dna: form.tags.length ? form.tags : null,
          instagram: form.instagram || null,
          looking_for: form.lookingFor.length ? form.lookingFor : null,
          dream_destinations: form.dreams.length ? form.dreams : null,
          photo_url: photo || null,
          lang: lang || null,
          gender: form.gender || 'male',
          online: true,
          birth_date: lifestyle.birthDate || null,
          zodiac_sign: zodiacSign || null,
          height: lifestyle.height || null,
          body_type: lifestyle.bodyType || null,
          education: lifestyle.education || null,
          occupation: lifestyle.occupation || null,
          smoking: lifestyle.smoking || null,
          alcohol: lifestyle.alcohol || null,
          children: lifestyle.children || null,
          pets: lifestyle.pets.length ? lifestyle.pets : null,
          diet: lifestyle.diet || null,
          preferred_age_min: prefs.ageMin,
          preferred_age_max: prefs.ageMax,
          preferred_zodiac: prefs.zodiac.length ? prefs.zodiac : null,
          preferred_smoking: prefs.smoking || null,
          preferred_alcohol: prefs.alcohol || null,
          preferred_children: prefs.children || null,
          preferred_height_min: prefs.heightMin,
          preferred_height_max: prefs.heightMax,
          preferred_body_type: prefs.bodyType.length ? prefs.bodyType : null,
          relationship_goal: prefs.goal || null,
        };
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Prefer': 'resolution=merge-duplicates',
          },
          body: JSON.stringify(payload),
        });
        const resText = await res.text();
        if (!res.ok) {
          console.error('[ProfileSave] FAILED:', res.status, resText);
          setToastMsg('❌ ' + (JSON.parse(resText || '{}').message || res.status));
          setToast(true);
          setSaving(false);
          setTimeout(() => setToast(false), 4000);
          return;
        }
      } else {
        devLog('[ProfileSave] missing userId or accessToken — skipping upsert', { userId, hasToken: !!accessToken });
      }
    }
    setToastMsg(p.profileSaved); setToast(true);
    setTimeout(() => { setSaving(false); setToast(false); }, 2000);
  }

  const ob = t.onboarding;

  const saveLbl   = lang==='bg'?'Запази':lang==='es'?'Guardar':lang==='fr'?'Enregistrer':lang==='de'?'Speichern':lang==='el'?'Αποθήκευση':lang==='it'?'Salva':'Save';
  const cancelLbl = lang==='bg'?'Отказ':lang==='es'?'Cancelar':lang==='fr'?'Annuler':lang==='de'?'Abbrechen':lang==='el'?'Ακύρωση':lang==='it'?'Annulla':'Cancel';
  const cropTitle = lang==='bg'?'Изрежи снимка':lang==='es'?'Recortar foto':lang==='fr'?'Recadrer la photo':lang==='de'?'Foto zuschneiden':lang==='el'?'Περικοπή φωτογραφίας':lang==='it'?'Ritaglia foto':'Crop Photo';
  const verificationProfile = { verified, verification_status: verificationStatus };
  const verificationApproved = canUseSocialFeatures(verificationProfile);
  const verificationPending = verificationStatus === 'pending';
  const verificationRejected = verificationStatus === 'rejected';

  return (
    <div className="ts-app" style={{overflowY:'auto'}}>
      {cropFile && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <div style={{background:'#0d1117',border:'1px solid var(--border)',borderRadius:20,padding:'24px 20px',width:'100%',maxWidth:340,display:'flex',flexDirection:'column',alignItems:'center',gap:'0.75rem'}}>
            <h3 className="ts-serif" style={{color:'var(--gold)',margin:0,fontSize:'1.2rem'}}>{cropTitle}</h3>
            <div
              style={{width:200,height:200,borderRadius:'50%',overflow:'hidden',position:'relative',cursor:'grab',userSelect:'none',border:'2px solid var(--gold)',flexShrink:0}}
              onMouseDown={onCropMouseDown}
              onTouchStart={onCropTouchStart}
              onTouchMove={onCropTouchMove}
            >
              <img
                src={cropPreviewUrl} alt="crop" draggable={false}
                style={{position:'absolute',top:'50%',left:'50%',transform:`translate(-50%,-50%) translate(${cropOffsetX}px,${cropOffsetY}px) scale(${cropScale})`,transformOrigin:'center',width:'100%',height:'auto',maxWidth:'none',pointerEvents:'none',userSelect:'none'}}
              />
            </div>
            <div style={{width:'100%',display:'flex',alignItems:'center',gap:'0.5rem'}}>
              <span style={{fontSize:'0.75rem',color:'var(--muted)'}}>🔍</span>
              <input type="range" min={1} max={3} step={0.1} value={cropScale}
                onChange={e=>setCropScale(parseFloat(e.target.value))}
                style={{flex:1,accentColor:'var(--gold)'}}/>
              <span style={{fontSize:'0.75rem',color:'var(--muted)',minWidth:'2.5rem'}}>{cropScale.toFixed(1)}×</span>
            </div>
            <div style={{display:'flex',gap:'0.75rem',width:'100%'}}>
              <button style={{flex:1,background:'rgba(255,255,255,0.06)',border:'1px solid var(--border)',color:'var(--muted)',borderRadius:10,padding:'0.7rem',fontWeight:600,fontSize:'0.9rem',cursor:'pointer'}} onClick={cancelCrop}>{cancelLbl}</button>
              <button style={{flex:1,background:'linear-gradient(135deg,var(--gold),#e8c870)',color:'#050a14',border:'none',borderRadius:10,padding:'0.7rem',fontWeight:700,fontSize:'0.9rem',cursor:'pointer'}} onClick={confirmCrop}>{saveLbl}</button>
            </div>
          </div>
        </div>
      )}
      <div className="ts-profile-screen-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h2 className="ts-serif" style={{color:'var(--gold)'}}>{p.myProfile}</h2>
        <div style={{width:80}}/>
      </div>

      <div className="ts-profile-screen-body">
        {/* Photo */}
        <div style={{position:'relative',width:120,height:120,margin:'0 auto 1.5rem'}}>
          <div className="ts-profile-photo-wrap" style={{margin:0}} onClick={()=>fileRef.current?.click()}>
            {photo
              ? <img src={photo} alt="avatar" className="ts-profile-avatar-large"/>
              : <div className="ts-profile-avatar-placeholder">👤</div>
            }
            <div className="ts-profile-photo-overlay">📷 {p.changePhoto}</div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handlePhoto}/>
          </div>
          {verified && <span className="ts-verified-dot">✓</span>}
        </div>

        <div style={{background:'rgba(201,168,76,0.08)',border:'1px solid rgba(201,168,76,0.28)',borderRadius:14,padding:'12px 14px',margin:'0 0 1rem'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginBottom:8}}>
            <div>
              <div style={{color:verificationApproved?'#3fb950':verificationPending?'#f59e0b':'var(--gold)',fontWeight:800,fontSize:'0.9rem'}}>
                {verificationApproved ? '✅ ' : verificationPending ? '⏳ ' : verificationRejected ? '⚠️ ' : '🛡️ '}
                {getVerificationStatusText(verificationProfile, lang)}
              </div>
              <p style={{color:'var(--muted)',fontSize:'0.78rem',margin:'3px 0 0',lineHeight:1.4}}>
                {verificationApproved
                  ? (lang==='bg'?'Социалните функции са активни.':lang==='es'?'Las funciones sociales están activas.':lang==='fr'?'Les fonctions sociales sont actives.':lang==='de'?'Soziale Funktionen sind aktiv.':lang==='el'?'Οι κοινωνικές λειτουργίες είναι ενεργές.':lang==='it'?'Le funzioni social sono attive.':'Social features are active.')
                  : (lang==='bg'?'Харесвания, съобщения и пактове изискват селфи верификация.':lang==='es'?'Los me gusta, mensajes y pactos requieren verificación con selfie.':lang==='fr'?'Les likes, messages et pactes nécessitent une vérification par selfie.':lang==='de'?'Likes, Nachrichten und Pakte erfordern eine Selfie-Verifizierung.':lang==='el'?'Τα likes, τα μηνύματα και οι συμφωνίες απαιτούν επαλήθευση με selfie.':lang==='it'?'Like, messaggi e patti richiedono la verifica con selfie.':'Likes, messages and pacts require selfie verification.')}
              </p>
            </div>
          </div>
          {verificationRejected && (
            <p style={{color:'#f85149',fontSize:'0.78rem',margin:'0 0 8px',lineHeight:1.4}}>
              {verificationRejectionReason
                ? verificationRejectionReason
                : (lang==='bg'?'Този профил не беше одобрен. Ако смяташ, че има грешка, свържи се с поддръжка.':lang==='es'?'Este perfil no fue aprobado. Contacta con soporte si crees que es un error.':lang==='fr'?'Ce profil n\'a pas été approuvé. Contactez le support si vous pensez qu\'il s\'agit d\'une erreur.':lang==='de'?'Dieses Profil wurde nicht genehmigt. Kontaktiere den Support, wenn du glaubst, dass dies ein Fehler ist.':lang==='el'?'Αυτό το προφίλ δεν εγκρίθηκε. Επικοινώνησε με την υποστήριξη αν πιστεύεις ότι πρόκειται για λάθος.':lang==='it'?'Questo profilo non è stato approvato. Contatta l\'assistenza se pensi si tratti di un errore.':'This profile was not approved. Contact support if you think this is a mistake.')}
            </p>
          )}
          {!verificationApproved && !verificationPending && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <button type="button" onClick={()=>verificationSelfieRef.current?.click()}
                  style={{background:'#161b22',border:'1px solid #c9a84c55',borderRadius:10,color:'#c9a84c',padding:'8px 6px',fontSize:'0.75rem',cursor:'pointer'}}>
                  {verificationUploading==='selfie' ? '...' : (verificationSelfieUrl ? '✅ ' : '📸 ') + (lang==='bg'?'Селфи':lang==='es'?'Selfie':lang==='fr'?'Selfie':lang==='de'?'Selfie':lang==='el'?'Selfie':lang==='it'?'Selfie':'Selfie')}
                </button>
                <button type="button" onClick={()=>verificationFrontRef.current?.click()}
                  style={{background:'#161b22',border:'1px solid #c9a84c55',borderRadius:10,color:'#c9a84c',padding:'8px 6px',fontSize:'0.75rem',cursor:'pointer'}}>
                  {verificationUploading==='front' ? '...' : (verificationFrontUrl ? '✅ ' : '🪪 ') + (lang==='bg'?'Анфас':lang==='es'?'Foto de frente':lang==='fr'?'Photo de face':lang==='de'?'Frontfoto':lang==='el'?'Μπροστινή φωτογραφία':lang==='it'?'Foto frontale':'Front photo')}
                </button>
              </div>
              <input ref={verificationSelfieRef} type="file" accept="image/*" hidden onChange={e=>handleVerificationPhoto(e, 'selfie')}/>
              <input ref={verificationFrontRef} type="file" accept="image/*" hidden onChange={e=>handleVerificationPhoto(e, 'front')}/>
              {(verificationSelfieUrl || verificationFrontUrl) && (
                <div style={{display:'flex',gap:8,marginBottom:8}}>
                  {verificationSelfieUrl && <img src={verificationSelfieUrl} alt="" style={{width:48,height:48,borderRadius:8,objectFit:'cover',border:'1px solid #30363d'}}/>}
                  {verificationFrontUrl && <img src={verificationFrontUrl} alt="" style={{width:48,height:48,borderRadius:8,objectFit:'cover',border:'1px solid #30363d'}}/>}
                </div>
              )}
              <button className="ts-btn-gold-full" onClick={submitVerification}
                disabled={verificationSubmitting || !verificationSelfieUrl || !verificationFrontUrl}
                style={{width:'100%',opacity:(!verificationSelfieUrl || !verificationFrontUrl || verificationSubmitting)?0.55:1}}>
                {verificationSubmitting ? '...' : (lang==='bg'?'Изпрати за верификация':lang==='es'?'Enviar para verificación':lang==='fr'?'Envoyer pour vérification':lang==='de'?'Zur Verifizierung senden':lang==='el'?'Αποστολή για επαλήθευση':lang==='it'?'Invia per la verifica':'Send for verification')}
              </button>
            </>
          )}
          {verificationPending && (
            <p style={{color:'#f59e0b',fontSize:'0.78rem',margin:0}}>
              {lang==='bg'?'Екипът ще прегледа снимките ръчно.':lang==='es'?'El equipo revisará tus fotos manualmente.':lang==='fr'?'L\'équipe examinera vos photos manuellement.':lang==='de'?'Das Team prüft deine Fotos manuell.':lang==='el'?'Η ομάδα ελέγχει τις φωτογραφίες σου χειροκίνητα.':lang==='it'?'Il team controlla le tue foto manualmente.':'The team will review your photos manually.'}
            </p>
          )}
        </div>

        {/* Name */}
        <div className="ts-pform-row">
          <label className="ts-label" style={{flex:1}}>{p.firstName}<input className="ts-input" value={form.firstName} onChange={e=>setForm(f=>({...f,firstName:e.target.value}))}/></label>
          <label className="ts-label" style={{flex:1}}>{p.lastName}<input className="ts-input" value={form.lastName} onChange={e=>setForm(f=>({...f,lastName:e.target.value}))}/></label>
        </div>

        <div className="ts-pform-row">
          <label className="ts-label" style={{flex:1}}>{p.age}<input className="ts-input" type="number" min="18" max="65" value={form.age} onChange={e=>setForm(f=>({...f,age:e.target.value}))}/></label>
          <label className="ts-label" style={{flex:1}}>{p.city}<input className="ts-input" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))}/></label>
        </div>

        {/* Bio */}
        <label className="ts-label">{p.bio}
          <textarea className="ts-input ts-textarea" maxLength={150} value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} rows={3} placeholder="..."/>
          <span style={{fontSize:'0.72rem',color:'var(--muted)',textAlign:'right',display:'block'}}>{form.bio.length}/150</span>
        </label>

        {/* Gender */}
        <div style={{marginBottom:'1rem'}}>
          <label style={{color:'#8b949e',fontSize:12,marginBottom:6,display:'block'}}>
            {lang==='bg'?'Пол':lang==='es'?'Género':lang==='fr'?'Genre':lang==='de'?'Geschlecht':lang==='el'?'Φύλο':lang==='it'?'Genere':'Gender'}
          </label>
          <select value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))}
            style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:10,color:'#f0f6fc',padding:'10px 14px',fontSize:14,outline:'none'}}>
            <option value="male">{lang==='bg'?'👨 Мъж':lang==='es'?'👨 Hombre':lang==='fr'?'👨 Homme':lang==='de'?'👨 Mann':lang==='el'?'👨 Άνδρας':lang==='it'?'👨 Uomo':'👨 Male'}</option>
            <option value="female">{lang==='bg'?'👩 Жена':lang==='es'?'👩 Mujer':lang==='fr'?'👩 Femme':lang==='de'?'👩 Frau':lang==='el'?'👩 Γυναίκα':lang==='it'?'👩 Donna':'👩 Female'}</option>
          </select>
        </div>

        {/* Travel tags */}
        <p className="ts-label">{p.travelStyle}</p>
        <div className="ts-tags-wrap">
          {TRAVEL_TAGS.map((tag, i) => (
            <button key={tag} className={`ts-tag-pill ${form.tags.includes(tag)?'active':''}`} onClick={()=>toggleTag(tag)}>{p.travelTags?.[i] || tag}</button>
          ))}
        </div>

        {/* Dream destinations */}
        <p className="ts-label">{p.dreamDest}</p>
        <div style={{display:'flex',gap:'0.5rem',marginBottom:'0.5rem'}}>
          <input className="ts-input" style={{flex:1}} value={form.dreamInput} onChange={e=>setForm(f=>({...f,dreamInput:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addDream()} placeholder={p.dreamPlaceholder||'Japan, Iceland...'}/>
          <button className="ts-btn-ghost-sm" onClick={addDream}>{t.extras?.addDest || '+ Add'}</button>
        </div>
        <div className="ts-tags-wrap" style={{marginBottom:'0.75rem'}}>
          {form.dreams.map(d=>(
            <span key={d} className="ts-tag-pill active" style={{cursor:'pointer'}} onClick={()=>removeDream(d)}>{d} ✕</span>
          ))}
        </div>

        {/* Looking for */}
        <p className="ts-label">{p.lookingFor}</p>
        <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap',marginBottom:'1rem'}}>
          {ob.goals.map(g=>(
            <button key={g.key} className={`ts-tag-pill ${form.lookingFor.includes(g.key)?'active':''}`} onClick={()=>toggleGoal(g.key)}>{g.icon} {g.name}</button>
          ))}
        </div>

        {/* Instagram */}
        <label className="ts-label">{p.instagram}
          <input className="ts-input" value={form.instagram} onChange={e=>setForm(f=>({...f,instagram:e.target.value}))} placeholder="@username"/>
        </label>

        {/* Personal Details */}
        <div style={{marginBottom:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <p className="ts-label" style={{fontWeight:700,marginBottom:'0.75rem',color:'var(--gold)'}}>👤 {lang==='bg'?'Лични данни':lang==='es'?'Datos personales':lang==='fr'?'Informations personnelles':lang==='de'?'Persönliche Angaben':lang==='el'?'Προσωπικά στοιχεία':lang==='it'?'Dati personali':'Personal Details'}</p>
          <div className="ts-pform-row">
            <label className="ts-label" style={{flex:1}}>
              {lang==='bg'?'Дата на раждане':lang==='es'?'Fecha de nacimiento':lang==='fr'?'Date de naissance':lang==='de'?'Geburtsdatum':lang==='el'?'Ημερομηνία γέννησης':lang==='it'?'Data di nascita':'Date of Birth'}
              <input type="date" className="ts-input" value={lifestyle.birthDate}
                max={new Date(Date.now()-18*365.25*24*3600*1000).toISOString().split('T')[0]}
                style={{colorScheme:'dark'}}
                onChange={e=>{
                  const val=e.target.value; setLifestyle(l=>({...l,birthDate:val}));
                  if(val){const age=Math.floor((Date.now()-new Date(val).getTime())/(365.25*24*3600*1000));if(age>=18&&age<=100)setForm(f=>({...f,age:age}));}
                }}/>
            </label>
            {zodiacSign && (
              <div style={{flex:1}}>
                <span className="ts-label">{lang==='bg'?'Зодия':lang==='es'?'Signo zodiacal':lang==='fr'?'Signe zodiacal':lang==='de'?'Sternzeichen':lang==='el'?'Ζώδιο':lang==='it'?'Segno zodiacale':'Zodiac'}</span>
                <div style={{background:'#161b22',border:'1px solid #30363d',borderRadius:10,padding:'10px 14px',fontSize:'1rem',marginTop:4}}>
                  {zodiacSign} {getZodiacName(zodiacSign,lang)}
                </div>
              </div>
            )}
          </div>
          <label className="ts-label">
            {lang==='bg'?'Ръст':lang==='es'?'Altura':lang==='fr'?'Taille':lang==='de'?'Größe':lang==='el'?'Ύψος':lang==='it'?'Altezza':'Height'}: <b style={{color:'var(--gold)'}}>{lifestyle.height} {lang==='bg'?'см':lang==='es'?'cm':lang==='fr'?'cm':lang==='de'?'cm':lang==='el'?'εκ.':lang==='it'?'cm':'cm'}</b>
            <input type="range" min={150} max={210} value={lifestyle.height} onChange={e=>setLifestyle(l=>({...l,height:+e.target.value}))} className="ts-range" style={{width:'100%',marginTop:6}}/>
          </label>
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Телосложение':lang==='es'?'Tipo de cuerpo':lang==='fr'?'Morphologie':lang==='de'?'Körperbau':lang==='el'?'Σωματότυπος':lang==='it'?'Corporatura':'Body Type'}</p>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[{k:'athletic',bg:'💪 Атлетично',en:'💪 Athletic',es:'💪 Atlético',fr:'💪 Athlétique',de:'💪 Athletisch',el:'💪 Αθλητικό',it:'💪 Atletico'},{k:'slim',bg:'🪶 Слабо',en:'🪶 Slim',es:'🪶 Delgado',fr:'🪶 Mince',de:'🪶 Schlank',el:'🪶 Λεπτό',it:'🪶 Snello'},{k:'average',bg:'🧍 Средно',en:'🧍 Average',es:'🧍 Promedio',fr:'🧍 Moyen',de:'🧍 Durchschnittlich',el:'🧍 Μέτριο',it:'🧍 Medio'},{k:'large',bg:'🐻 Едро',en:'🐻 Large',es:'🐻 Grande',fr:'🐻 Grand',de:'🐻 Groß',el:'🐻 Μεγαλόσωμο',it:'🐻 Robusto'}].map(b=>(
                <button key={b.k} className={`ts-tag-pill ${lifestyle.bodyType===b.k?'active':''}`} onClick={()=>setLifestyle(l=>({...l,bodyType:b.k}))}>{lang==='bg'?b.bg:lang==='es'?b.es:lang==='fr'?b.fr:lang==='de'?b.de:lang==='el'?b.el:lang==='it'?b.it:b.en}</button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:'0.75rem'}}>
            <label className="ts-label" style={{display:'block',marginBottom:6}}>{lang==='bg'?'Образование':lang==='es'?'Educación':lang==='fr'?'Études':lang==='de'?'Bildung':lang==='el'?'Εκπαίδευση':lang==='it'?'Istruzione':'Education'}</label>
            <select value={lifestyle.education} onChange={e=>setLifestyle(l=>({...l,education:e.target.value}))}
              style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:10,color:'#f0f6fc',padding:'10px 14px',fontSize:14,outline:'none'}}>
              <option value="">—</option>
              <option value="highschool">{lang==='bg'?'Средно':lang==='es'?'Secundaria':lang==='fr'?'Lycée':lang==='de'?'Abitur':lang==='el'?'Λύκειο':lang==='it'?'Diploma':'High School'}</option>
              <option value="bachelor">{lang==='bg'?'Бакалавър':lang==='es'?'Licenciatura':lang==='fr'?'Licence':lang==='de'?'Bachelor':lang==='el'?'Πτυχίο':lang==='it'?'Laurea triennale':'Bachelor'}</option>
              <option value="master">{lang==='bg'?'Магистър':lang==='es'?'Máster':lang==='fr'?'Master':lang==='de'?'Master':lang==='el'?'Μεταπτυχιακό':lang==='it'?'Laurea magistrale':'Master'}</option>
              <option value="phd">{lang==='bg'?'Докторат':lang==='es'?'Doctorado':lang==='fr'?'Doctorat':lang==='de'?'Promotion':lang==='el'?'Διδακτορικό':lang==='it'?'Dottorato':'PhD'}</option>
            </select>
          </div>
          <label className="ts-label">{lang==='bg'?'Професия':lang==='es'?'Profesión':lang==='fr'?'Profession':lang==='de'?'Beruf':lang==='el'?'Επάγγελμα':lang==='it'?'Professione':'Occupation'}
            <input className="ts-input" value={lifestyle.occupation} onChange={e=>setLifestyle(l=>({...l,occupation:e.target.value}))} placeholder={lang==='bg'?'Дизайнер, Програмист...':lang==='es'?'Diseñador, Programador...':lang==='fr'?'Designer, Développeur...':lang==='de'?'Designer, Entwickler...':lang==='el'?'Σχεδιαστής, προγραμματιστής...':lang==='it'?'Designer, sviluppatore...':'Designer, Developer...'}/>
          </label>
        </div>

        {/* Lifestyle */}
        <div style={{marginBottom:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <p className="ts-label" style={{fontWeight:700,marginBottom:'0.75rem',color:'var(--gold)'}}>🌿 {lang==='bg'?'Начин на живот':lang==='es'?'Estilo de vida':lang==='fr'?'Style de vie':lang==='de'?'Lebensstil':lang==='el'?'Τρόπος ζωής':lang==='it'?'Stile di vita':'Lifestyle'}</p>
          {[
            {label:lang==='bg'?'Тютюнопушене':lang==='es'?'Tabaquismo':lang==='fr'?'Tabagisme':lang==='de'?'Rauchen':lang==='el'?'Κάπνισμα':lang==='it'?'Fumo':'Smoking', key:'smoking', opts:[{k:'yes',l:'🚬 '+(lang==='bg'?'Пуша':lang==='es'?'Fumador':lang==='fr'?'Fumeur':lang==='de'?'Raucher':lang==='el'?'Καπνιστής':lang==='it'?'Fumatore':'Smoker')},{k:'no',l:'🚭 '+(lang==='bg'?'Не пуша':lang==='es'?'No fumador':lang==='fr'?'Non-fumeur':lang==='de'?'Nichtraucher':lang==='el'?'Μη καπνιστής':lang==='it'?'Non fumatore':'Non-smoker')},{k:'sometimes',l:'🌿 '+(lang==='bg'?'Понякога':lang==='es'?'A veces':lang==='fr'?'Parfois':lang==='de'?'Manchmal':lang==='el'?'Μερικές φορές':lang==='it'?'A volte':'Occasionally')}]},
            {label:lang==='bg'?'Алкохол':lang==='es'?'Alcohol':lang==='fr'?'Alcool':lang==='de'?'Alkohol':lang==='el'?'Αλκοόλ':lang==='it'?'Alcol':'Alcohol',       key:'alcohol', opts:[{k:'yes',l:'🍷 '+(lang==='bg'?'Пия':lang==='es'?'Bebedor':lang==='fr'?'Buveur':lang==='de'?'Trinker':lang==='el'?'Πίνω':lang==='it'?'Bevitore':'Drinker')},{k:'no',l:'🚱 '+(lang==='bg'?'Не пия':lang==='es'?'No bebedor':lang==='fr'?'Non-buveur':lang==='de'?'Trinkt nicht':lang==='el'?'Δεν πίνω':lang==='it'?'Non beve':'Non-drinker')},{k:'social',l:'🥂 '+(lang==='bg'?'Социално':lang==='es'?'Socialmente':lang==='fr'?'Occasionnellement':lang==='de'?'Gesellig':lang==='el'?'Κοινωνικά':lang==='it'?'Socievole':'Socially')}]},
            {label:lang==='bg'?'Деца':lang==='es'?'Hijos':lang==='fr'?'Enfants':lang==='de'?'Kinder':lang==='el'?'Παιδιά':lang==='it'?'Figli':'Children',         key:'children',opts:[{k:'has',l:'👶 '+(lang==='bg'?'Имам деца':lang==='es'?'Tengo hijos':lang==='fr'?'J\'ai des enfants':lang==='de'?'Habe Kinder':lang==='el'?'Έχω παιδιά':lang==='it'?'Ho figli':'Have children')},{k:'none',l:'🚫 '+(lang==='bg'?'Нямам':lang==='es'?'Ninguno':lang==='fr'?'Aucun':lang==='de'?'Keine':lang==='el'?'Κανένα':lang==='it'?'Nessuno':'None')},{k:'wants',l:'💭 '+(lang==='bg'?'Искам':lang==='es'?'Quiero tener':lang==='fr'?'J\'en veux':lang==='de'?'Möchte welche':lang==='el'?'Θέλω να αποκτήσω':lang==='it'?'Li vorrei':'Want some')}]},
            {label:lang==='bg'?'Диета':lang==='es'?'Dieta':lang==='fr'?'Régime':lang==='de'?'Ernährung':lang==='el'?'Διατροφή':lang==='it'?'Alimentazione':'Diet',            key:'diet',    opts:[{k:'meat',l:'🥩 '+(lang==='bg'?'Месоядец':lang==='es'?'Omnívoro':lang==='fr'?'Omnivore':lang==='de'?'Allesesser':lang==='el'?'Παμφάγος':lang==='it'?'Onnivoro':'Omnivore')},{k:'vegetarian',l:'🥗 '+(lang==='bg'?'Вегетарианец':lang==='es'?'Vegetariano':lang==='fr'?'Végétarien':lang==='de'?'Vegetarier':lang==='el'?'Χορτοφάγος':lang==='it'?'Vegetariano':'Vegetarian')},{k:'vegan',l:'🌱 '+(lang==='bg'?'Веган':lang==='es'?'Vegano':lang==='fr'?'Végane':lang==='de'?'Veganer':lang==='el'?'Vegan':lang==='it'?'Vegano':'Vegan')}]},
          ].map(({label,key,opts})=>(
            <div key={key} style={{marginBottom:'0.75rem'}}>
              <p className="ts-label" style={{marginBottom:6}}>{label}</p>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {opts.map(x=><button key={x.k} className={`ts-tag-pill ${lifestyle[key]===x.k?'active':''}`} onClick={()=>setLifestyle(l=>({...l,[key]:x.k}))}>{x.l}</button>)}
              </div>
            </div>
          ))}
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Домашни животни':lang==='es'?'Mascotas':lang==='fr'?'Animaux de compagnie':lang==='de'?'Haustiere':lang==='el'?'Κατοικίδια':lang==='it'?'Animali domestici':'Pets'}</p>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[{k:'dog',l:'🐕 '+(lang==='bg'?'Куче':lang==='es'?'Perro':lang==='fr'?'Chien':lang==='de'?'Hund':lang==='el'?'Σκύλος':lang==='it'?'Cane':'Dog')},{k:'cat',l:'🐈 '+(lang==='bg'?'Котка':lang==='es'?'Gato':lang==='fr'?'Chat':lang==='de'?'Katze':lang==='el'?'Γάτα':lang==='it'?'Gatto':'Cat')},{k:'other',l:'🐾 '+(lang==='bg'?'Имам':lang==='es'?'Tengo':lang==='fr'?'J\'en ai':lang==='de'?'Habe welche':lang==='el'?'Έχω':lang==='it'?'Ne ho':'Have pets')},{k:'none',l:'🚫 '+(lang==='bg'?'Нямам':lang==='es'?'Ninguno':lang==='fr'?'Aucun':lang==='de'?'Keine':lang==='el'?'Κανένα':lang==='it'?'Nessuno':'None')}].map(x=>(
                <button key={x.k} className={`ts-tag-pill ${lifestyle.pets.includes(x.k)?'active':''}`}
                  onClick={()=>setLifestyle(l=>({...l,pets:l.pets.includes(x.k)?l.pets.filter(p=>p!==x.k):[...l.pets,x.k]}))}>
                  {x.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Match Preferences */}
        <div style={{marginBottom:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <p className="ts-label" style={{fontWeight:700,marginBottom:'0.75rem',color:'var(--gold)'}}>💝 {lang==='bg'?'Търся партньор който...':lang==='es'?'Busco a alguien que...':lang==='fr'?'Je recherche quelqu\'un qui...':lang==='de'?'Ich suche jemanden, der...':lang==='el'?'Ψάχνω κάποιον που...':lang==='it'?'Cerco qualcuno che...':'Looking For Someone Who...'}</p>
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Възраст':lang==='es'?'Edad':lang==='fr'?'Âge':lang==='de'?'Alter':lang==='el'?'Ηλικία':lang==='it'?'Età':'Age'}: <b style={{color:'var(--gold)'}}>{prefs.ageMin} – {prefs.ageMax}</b></p>
            <div className="ts-range-row">
              <input type="range" min={18} max={80} value={prefs.ageMin} onChange={e=>setPrefs(p=>({...p,ageMin:Math.min(+e.target.value,p.ageMax-1)}))} className="ts-range"/>
              <input type="range" min={18} max={80} value={prefs.ageMax} onChange={e=>setPrefs(p=>({...p,ageMax:Math.max(+e.target.value,p.ageMin+1)}))} className="ts-range"/>
            </div>
          </div>
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Ръст':lang==='es'?'Altura':lang==='fr'?'Taille':lang==='de'?'Größe':lang==='el'?'Ύψος':lang==='it'?'Altezza':'Height'}: <b style={{color:'var(--gold)'}}>{prefs.heightMin} – {prefs.heightMax} {lang==='bg'?'см':lang==='es'?'cm':lang==='fr'?'cm':lang==='de'?'cm':lang==='el'?'εκ.':lang==='it'?'cm':'cm'}</b></p>
            <div className="ts-range-row">
              <input type="range" min={150} max={210} value={prefs.heightMin} onChange={e=>setPrefs(p=>({...p,heightMin:Math.min(+e.target.value,p.heightMax-5)}))} className="ts-range"/>
              <input type="range" min={150} max={210} value={prefs.heightMax} onChange={e=>setPrefs(p=>({...p,heightMax:Math.max(+e.target.value,p.heightMin+5)}))} className="ts-range"/>
            </div>
          </div>
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Цел на връзката':lang==='es'?'Objetivo de relación':lang==='fr'?'Objectif de la relation':lang==='de'?'Beziehungsziel':lang==='el'?'Στόχος σχέσης':lang==='it'?'Obiettivo di relazione':'Relationship Goal'}</p>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[{k:'serious',l:'💑 '+(lang==='bg'?'Сериозна връзка':lang==='es'?'Relación seria':lang==='fr'?'Sérieuse':lang==='de'?'Feste Beziehung':lang==='el'?'Σοβαρή σχέση':lang==='it'?'Relazione stabile':'Serious')},{k:'travel',l:'🌍 '+(lang==='bg'?'Пътен партньор':lang==='es'?'Compañero de viaje':lang==='fr'?'Partenaire de voyage':lang==='de'?'Reisepartner':lang==='el'?'Σύντροφος ταξιδιού':lang==='it'?'Compagno di viaggio':'Travel Partner')},{k:'friends',l:'👫 '+(lang==='bg'?'Приятелство':lang==='es'?'Amistad':lang==='fr'?'Amitié':lang==='de'?'Freundschaft':lang==='el'?'Φιλία':lang==='it'?'Amicizia':'Friendship')},{k:'flirt',l:'🔥 '+(lang==='bg'?'Флирт':lang==='es'?'Ligar':lang==='fr'?'Flirt':lang==='de'?'Flirten':lang==='el'?'Φλερτ':lang==='it'?'Flirt':'Flirt')}].map(x=>(
                <button key={x.k} className={`ts-tag-pill ${prefs.goal===x.k?'active':''}`} onClick={()=>setPrefs(p=>({...p,goal:x.k}))}>{x.l}</button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Предпочитани зодии':lang==='es'?'Signos zodiacales preferidos':lang==='fr'?'Signes du zodiaque préférés':lang==='de'?'Bevorzugte Sternzeichen':lang==='el'?'Προτιμώμενα ζώδια':lang==='it'?'Segni zodiacali preferiti':'Preferred Zodiac Signs'}</p>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              {ZODIAC_CALC.map(z=>(
                <button key={z.sign} className={`ts-tag-pill ${prefs.zodiac.includes(z.sign)?'active':''}`}
                  title={lang==='bg'?z.bgName:lang==='es'?z.esName:lang==='fr'?z.frName:lang==='de'?z.deName:lang==='el'?z.elName:lang==='it'?z.itName:z.enName}
                  onClick={()=>setPrefs(p=>({...p,zodiac:p.zodiac.includes(z.sign)?p.zodiac.filter(s=>s!==z.sign):[...p.zodiac,z.sign]}))}>
                  {z.sign}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:'0.75rem'}}>
            <p className="ts-label" style={{marginBottom:6}}>{lang==='bg'?'Телосложение':lang==='es'?'Tipo de cuerpo':lang==='fr'?'Morphologie':lang==='de'?'Körperbau':lang==='el'?'Σωματότυπος':lang==='it'?'Corporatura':'Body Type'}</p>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[{k:'athletic',bg:'💪 Атлетично',en:'💪 Athletic',es:'💪 Atlético',fr:'💪 Athlétique',de:'💪 Athletisch',el:'💪 Αθλητικό',it:'💪 Atletico'},{k:'slim',bg:'🪶 Слабо',en:'🪶 Slim',es:'🪶 Delgado',fr:'🪶 Mince',de:'🪶 Schlank',el:'🪶 Λεπτό',it:'🪶 Snello'},{k:'average',bg:'🧍 Средно',en:'🧍 Average',es:'🧍 Promedio',fr:'🧍 Moyen',de:'🧍 Durchschnittlich',el:'🧍 Μέτριο',it:'🧍 Medio'},{k:'large',bg:'🐻 Едро',en:'🐻 Large',es:'🐻 Grande',fr:'🐻 Grand',de:'🐻 Groß',el:'🐻 Μεγαλόσωμο',it:'🐻 Robusto'}].map(b=>(
                <button key={b.k} className={`ts-tag-pill ${prefs.bodyType.includes(b.k)?'active':''}`}
                  onClick={()=>setPrefs(p=>({...p,bodyType:p.bodyType.includes(b.k)?p.bodyType.filter(x=>x!==b.k):[...p.bodyType,b.k]}))}>
                  {lang==='bg'?b.bg:lang==='es'?b.es:lang==='fr'?b.fr:lang==='de'?b.de:lang==='el'?b.el:lang==='it'?b.it:b.en}
                </button>
              ))}
            </div>
          </div>
          {[
            {label:lang==='bg'?'Пуши':lang==='es'?'Fuma':lang==='fr'?'Fume':lang==='de'?'Raucht':lang==='el'?'Καπνίζει':lang==='it'?'Fuma':'Smoking',  key:'smoking',  opts:[{k:'ok',l:'✅ '+(lang==='bg'?'Без значение':lang==='es'?'Cualquiera':lang==='fr'?'Peu importe':lang==='de'?'Egal':lang==='el'?'Αδιάφορο':lang==='it'?'Indifferente':'Any')},{k:'no',l:'🚭 '+(lang==='bg'?'Не пуши':lang==='es'?'No fuma':lang==='fr'?'Ne fume pas':lang==='de'?'Nichtraucher':lang==='el'?'Μη καπνιστής':lang==='it'?'Non fumatore':'Non-smoker')},{k:'sometimes',l:'🌿 '+(lang==='bg'?'Понякога':lang==='es'?'A veces':lang==='fr'?'Parfois':lang==='de'?'Manchmal':lang==='el'?'Μερικές φορές':lang==='it'?'A volte':'Occasionally')}]},
            {label:lang==='bg'?'Алкохол':lang==='es'?'Alcohol':lang==='fr'?'Alcool':lang==='de'?'Alkohol':lang==='el'?'Αλκοόλ':lang==='it'?'Alcol':'Alcohol',key:'alcohol',  opts:[{k:'ok',l:'✅ '+(lang==='bg'?'Без значение':lang==='es'?'Cualquiera':lang==='fr'?'Peu importe':lang==='de'?'Egal':lang==='el'?'Αδιάφορο':lang==='it'?'Indifferente':'Any')},{k:'no',l:'🚱 '+(lang==='bg'?'Не пие':lang==='es'?'No bebe':lang==='fr'?'Ne boit pas':lang==='de'?'Trinkt nicht':lang==='el'?'Δεν πίνω':lang==='it'?'Non beve':'Non-drinker')},{k:'social',l:'🥂 '+(lang==='bg'?'Социално':lang==='es'?'Socialmente':lang==='fr'?'Occasionnellement':lang==='de'?'Gesellig':lang==='el'?'Κοινωνικά':lang==='it'?'Socievole':'Socially')}]},
            {label:lang==='bg'?'Деца':lang==='es'?'Hijos':lang==='fr'?'Enfants':lang==='de'?'Kinder':lang==='el'?'Παιδιά':lang==='it'?'Figli':'Children',  key:'children', opts:[{k:'any',l:'✅ '+(lang==='bg'?'Без значение':lang==='es'?'Cualquiera':lang==='fr'?'Peu importe':lang==='de'?'Egal':lang==='el'?'Αδιάφορο':lang==='it'?'Indifferente':'Any')},{k:'has',l:'👶 '+(lang==='bg'?'Има деца':lang==='es'?'Tiene hijos':lang==='fr'?'A des enfants':lang==='de'?'Hat Kinder':lang==='el'?'Έχει παιδιά':lang==='it'?'Ha figli':'Has children')},{k:'none',l:'🚫 '+(lang==='bg'?'Няма деца':lang==='es'?'Sin hijos':lang==='fr'?'Sans enfants':lang==='de'?'Keine Kinder':lang==='el'?'Δεν έχει παιδιά':lang==='it'?'Nessun figlio':'No children')},{k:'wants',l:'💭 '+(lang==='bg'?'Иска деца':lang==='es'?'Quiere hijos':lang==='fr'?'Veut des enfants':lang==='de'?'Möchte Kinder':lang==='el'?'Θέλει παιδιά':lang==='it'?'Vuole figli':'Wants children')}]},
          ].map(({label,key,opts})=>(
            <div key={key} style={{marginBottom:'0.75rem'}}>
              <p className="ts-label" style={{marginBottom:6}}>{label}</p>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {opts.map(x=><button key={x.k} className={`ts-tag-pill ${prefs[key]===x.k?'active':''}`} onClick={()=>setPrefs(p=>({...p,[key]:x.k}))}>{x.l}</button>)}
              </div>
            </div>
          ))}
        </div>

        {/* Video intro */}
        {(() => {
          const ft = FT[lang] || FT.en;
          return (
            <div style={{marginBottom:'1rem'}}>
              <p className="ts-label" style={{marginBottom:6}}>{ft.videoIntro}</p>
              {videoIntro ? (
                <div style={{position:'relative'}}>
                  <video src={videoIntro} controls style={{width:'100%',borderRadius:12,maxHeight:200,background:'#000'}}/>
                  <button onClick={handleDeleteVideo} style={{marginTop:6,background:'#21262d',border:'1px solid #c9a84c33',borderRadius:8,color:'#e53935',padding:'6px 14px',fontSize:12,cursor:'pointer',width:'100%'}}>
                    🗑️ {ft.deleteVideo}
                  </button>
                </div>
              ) : (
                <button onClick={()=>videoRef.current?.click()} disabled={uploadingVideo}
                  style={{width:'100%',background:'#161b22',border:'1px dashed #c9a84c55',borderRadius:10,color:'#c9a84c',padding:'10px',fontSize:13,cursor:'pointer'}}>
                  {uploadingVideo ? ft.uploading : ft.addVideo}
                </button>
              )}
              <input ref={videoRef} type="file" accept="video/*" hidden onChange={handleVideo}/>
            </div>
          );
        })()}

        {/* Photo gallery */}
        {(() => {
          const ft = FT[lang] || FT.en;
          return (
            <div style={{marginBottom:'1rem'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                <p className="ts-label" style={{margin:0}}>{ft.photos}</p>
                {photos.length < 6 && (
                  <button onClick={()=>galleryPhotoRef.current?.click()} disabled={uploadingPhoto}
                    style={{background:'#161b22',border:'1px solid #c9a84c55',borderRadius:8,color:'#c9a84c',padding:'5px 12px',fontSize:12,cursor:'pointer'}}>
                    {uploadingPhoto ? ft.uploading : ft.addPhoto}
                  </button>
                )}
              </div>
              <input ref={galleryPhotoRef} type="file" accept="image/*" hidden onChange={handleGalleryPhoto}/>
              {photos.length === 0 ? (
                <div style={{textAlign:'center',color:'var(--muted)',fontSize:13,padding:'20px 0',border:'1px dashed #30363d',borderRadius:10}}>
                  📷 {ft.noPhotos}
                </div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:6}}>
                  {photos.map((url, i) => (
                    <div key={url} style={{position:'relative'}}>
                      <img src={url} alt="" onClick={()=>setLightboxIdx(i)}
                        style={{width:'100%',height:100,objectFit:'cover',borderRadius:8,cursor:'pointer',display:'block'}}/>
                      <button
                        onClick={e=>{ e.stopPropagation(); e.preventDefault(); handleDeletePhoto(url); }}
                        onTouchEnd={e=>{ e.stopPropagation(); e.preventDefault(); handleDeletePhoto(url); }}
                        style={{position:'absolute',top:0,right:0,background:'rgba(0,0,0,0.7)',border:'none',borderRadius:'50%',color:'#fff',width:44,height:44,fontSize:14,cursor:'pointer',lineHeight:'44px',padding:0,textAlign:'center',zIndex:10,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Lightbox */}
        {lightboxIdx !== null && (
          <div onClick={()=>setLightboxIdx(null)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.92)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <button onClick={()=>setLightboxIdx(null)} style={{position:'absolute',top:16,right:16,background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>✕</button>
            {lightboxIdx > 0 && (
              <button onClick={e=>{e.stopPropagation();setLightboxIdx(i=>i-1);}} style={{position:'absolute',left:16,background:'none',border:'none',color:'#fff',fontSize:32,cursor:'pointer'}}>‹</button>
            )}
            <img src={photos[lightboxIdx]} alt="" onClick={e=>e.stopPropagation()} style={{maxWidth:'90vw',maxHeight:'85vh',borderRadius:12,objectFit:'contain'}}/>
            {lightboxIdx < photos.length - 1 && (
              <button onClick={e=>{e.stopPropagation();setLightboxIdx(i=>i+1);}} style={{position:'absolute',right:16,background:'none',border:'none',color:'#fff',fontSize:32,cursor:'pointer'}}>›</button>
            )}
          </div>
        )}

        <button className="ts-btn-gold-full" style={{marginTop:'1rem',transition:'all 0.2s'}} onClick={save} disabled={saving}>
          {saving ? `✓ ${p.profileSaved}` : `💾 ${p.saveProfile}`}
        </button>
        {toast && <div className="ts-toast" style={{position:'relative',marginTop:'0.75rem',bottom:'auto',left:'auto',transform:'none'}}>{toastMsg}</div>}

        {/* Bucket List */}
        <BucketListSection lang={lang} plan={plan} />

        {/* DNA Test */}
        <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          {dnaTitle && <p style={{fontSize:'0.82rem',color:'#b47fff',marginBottom:'0.5rem',textAlign:'center'}}>🧬 {dnaTitle}</p>}
          <button className="ts-hotel-btn" style={{width:'100%',justifyContent:'center'}} onClick={()=>setShowDnaModal(true)}>
            🧬 {lang==='bg'?'Моята пътническа ДНК':lang==='es'?'Mi ADN de Viaje':lang==='fr'?'Mon ADN de Voyage':lang==='de'?'Meine Reise-DNA':lang==='el'?'Το Ταξιδιωτικό μου DNA':lang==='it'?'Il mio DNA di viaggio':'My Travel DNA'}
          </button>
        </div>

        {/* Travel Map */}
        <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <button className="ts-hotel-btn" style={{width:'100%',justifyContent:'center'}} onClick={()=>setShowMapModal(true)}>
            🗺️ {lang==='bg'?'Моята карта':lang==='es'?'Mi Mapa':lang==='fr'?'Ma Carte':lang==='de'?'Meine Karte':lang==='el'?'Ο χάρτης μου':lang==='it'?'La mia mappa':'My Travel Map'}
          </button>
        </div>

        {/* AI Profile Optimizer */}
        <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <button className="ts-hotel-btn ts-optim-btn" style={{width:'100%',justifyContent:'center'}} onClick={()=>setShowOptimizer(true)}>
            ✨ {lang==='bg'?'Оптимизирай профила':lang==='es'?'Optimizar Perfil':lang==='fr'?'Optimiser Profil':lang==='de'?'Profil optimieren':lang==='el'?'Βελτιστοποίηση προφίλ':lang==='it'?'Ottimizza profilo':'Optimize My Profile'}
            {plan!=='elite'&&<span style={{marginLeft:6,fontSize:'0.7rem',background:'rgba(232,200,112,0.15)',border:'1px solid rgba(232,200,112,0.3)',borderRadius:20,padding:'1px 7px',color:'var(--gold)'}}>Elite</span>}
          </button>
        </div>

        {/* Custom Badge */}
        <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          {customBadge && (() => {
            const cb = customBadge;
            const cbCol = BADGE_COLORS.find(c=>c.key===cb.color)||BADGE_COLORS[0];
            return (
              <div style={{display:'flex',justifyContent:'center',marginBottom:'0.75rem'}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,background:cbCol.bg,border:`2px solid ${cbCol.border}`,borderRadius:14,padding:'10px 22px',textAlign:'center'}}>
                  <span style={{fontSize:'1.6rem',lineHeight:1}}>{cb.emoji}</span>
                  <span style={{fontSize:'0.75rem',fontWeight:700,color:cbCol.hex}}>{cb.name}</span>
                </div>
              </div>
            );
          })()}
          <button className="ts-hotel-btn" style={{width:'100%',justifyContent:'center'}} onClick={()=>setShowBadgeCreator(true)}>
            🎨 {customBadge
              ? (lang==='bg'?'Редактирай значката':lang==='es'?'Editar Insignia':lang==='fr'?'Modifier Badge':lang==='de'?'Abzeichen bearbeiten':lang==='el'?'Επεξεργασία κονκάρδας':lang==='it'?'Modifica distintivo':'Edit My Badge')
              : (lang==='bg'?'Създай своя значка':lang==='es'?'Crear Insignia':lang==='fr'?'Créer Badge':lang==='de'?'Abzeichen erstellen':lang==='el'?'Δημιουργία κονκάρδας':lang==='it'?'Crea distintivo':'Create Custom Badge')}
            {plan!=='elite'&&<span style={{marginLeft:6,fontSize:'0.7rem',background:'rgba(232,200,112,0.15)',border:'1px solid rgba(232,200,112,0.3)',borderRadius:20,padding:'1px 7px',color:'var(--gold)'}}>Elite</span>}
          </button>
        </div>

        {/* Incognito Mode Toggle */}
        <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <div className="ts-incognito-toggle-row">
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                <span style={{fontWeight:600,color:'var(--text)',fontSize:'0.9rem'}}>
                  👀 {lang==='bg'?'Анонимен режим':lang==='es'?'Modo Anónimo':lang==='fr'?'Mode Anonyme':lang==='de'?'Anonymer Modus':lang==='el'?'Ανώνυμη λειτουργία':lang==='it'?'Modalità anonima':'Incognito Mode'}
                </span>
                {plan==='free'&&<span style={{fontSize:'0.7rem',background:'rgba(232,200,112,0.15)',border:'1px solid rgba(232,200,112,0.3)',borderRadius:20,padding:'1px 7px',color:'var(--gold)'}}>Premium</span>}
              </div>
              <p style={{color:'var(--muted)',fontSize:'0.8rem',margin:0,lineHeight:1.4}}>
                {lang==='bg'?'Разглеждай профили без да се появяваш в "Кой те е видял"':lang==='es'?'Explora perfiles sin aparecer en "Quién te vio"':lang==='fr'?'Parcourez sans apparaître dans "Qui vous a vu"':lang==='de'?'Profile durchsuchen ohne zu erscheinen':lang==='el'?'Περιήγηση σε προφίλ χωρίς να εμφανίζεσαι':lang==='it'?'Sfoglia i profili senza comparire':'Browse profiles without appearing in others\' who viewed you'}
              </p>
            </div>
            <button
              className={`ts-incognito-switch${incognito?' ts-incognito-switch-on':''}`}
              onClick={()=>{
                if(plan==='free') return;
                const next = !incognito;
                setIncognito(next);
                localStorage.setItem('ts_incognito', next?'1':'0');
                patchProfile({ anonymous_mode: next });
              }}
              disabled={plan==='free'}
            >
              <span className="ts-incognito-switch-knob"/>
            </button>
          </div>
        </div>

        {/* Who Viewed You — compact summary row */}
        <div style={{marginTop:'1rem',borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          {viewersLoading ? (
            <div style={{textAlign:'center',color:'var(--muted)',padding:'0.75rem',fontSize:'0.85rem'}}>
              <span className="ts-spin-inline">⟳</span>
            </div>
          ) : viewers.length === 0 ? (
            <div style={{textAlign:'center',color:'var(--muted)',padding:'1rem',fontSize:'0.85rem',border:'1px dashed #30363d',borderRadius:10}}>
              👁️ {lang==='bg'?'Все още никой не е видял профила ти':lang==='es'?'Todavía nadie ha visto tu perfil':lang==='fr'?'Personne n\'a encore consulté votre profil':lang==='de'?'Noch hat niemand dein Profil angesehen':lang==='el'?'Κανείς δεν έχει δει ακόμα το προφίλ σου':lang==='it'?'Nessuno ha ancora visto il tuo profilo':'No one has viewed your profile yet'}
            </div>
          ) : (
            <button
              onClick={()=>setShowViewersModal(true)}
              style={{width:'100%',display:'flex',alignItems:'center',gap:10,background:'#161b22',border:'1px solid #30363d',borderRadius:12,padding:'10px 14px',cursor:'pointer',textAlign:'left'}}>
              <span style={{fontSize:'1rem'}}>👁️</span>
              <div style={{display:'flex',alignItems:'center',marginRight:4}}>
                {viewers.slice(0,3).map((v,i)=>(
                  getProfilePhotoUrl(v.profile?.photo_url, '')
                    ? <img key={i} src={getProfilePhotoUrl(v.profile.photo_url, '')} alt="" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',border:'2px solid #161b22',marginLeft: i===0?0:-10}}/>
                    : <div key={i} style={{width:32,height:32,borderRadius:'50%',background:'#30363d',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,border:'2px solid #161b22',marginLeft: i===0?0:-10}}>👤</div>
                ))}
              </div>
              <span style={{flex:1,color:'var(--text)',fontSize:'0.85rem'}}>
                {viewers.length > 3
                  ? `+${viewers.length - 3} ${lang==='bg'?'души са видели профила ти':lang==='es'?'personas vieron tu perfil':lang==='fr'?'personnes ont vu ton profil':lang==='de'?'Personen haben dein Profil gesehen':lang==='el'?'άτομα είδαν το προφίλ σου':lang==='it'?'persone hanno visto il tuo profilo':'people viewed your profile'}`
                  : (lang==='bg'?'души са видели профила ти':lang==='es'?'personas vieron tu perfil':lang==='fr'?'personnes ont vu ton profil':lang==='de'?'Personen haben dein Profil gesehen':lang==='el'?'άτομα είδαν το προφίλ σου':lang==='it'?'persone hanno visto il tuo profilo':'people viewed your profile')}
              </span>
              <span style={{color:'var(--muted)',fontSize:'1rem'}}>→</span>
            </button>
          )}
        </div>

        {/* Viewers modal */}
        {showViewersModal && (
          <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'flex-end',justifyContent:'center'}} onClick={()=>setShowViewersModal(false)}>
            <div style={{background:'#0d1117',borderRadius:'20px 20px 0 0',width:'100%',maxWidth:480,maxHeight:'75vh',overflowY:'auto',padding:'20px 16px 32px'}} onClick={e=>e.stopPropagation()}>
              <div style={{width:36,height:4,background:'#30363d',borderRadius:2,margin:'0 auto 16px'}}/>
              <h3 style={{margin:'0 0 16px',fontSize:'0.95rem',fontWeight:600,color:'var(--text)'}}>
                👁️ {lang==='bg'?'Кой те е видял':lang==='es'?'Quién te vio':lang==='fr'?'Qui vous a vu':lang==='de'?'Wer hat dich gesehen':lang==='el'?'Ποιος σε είδε':lang==='it'?'Chi ti ha visto':'Who Viewed You'}
                {plan==='free'&&<span style={{marginLeft:8,fontSize:'0.7rem',color:'var(--muted)'}}>{lang==='bg'?'Последните 10':lang==='es'?'Últimos 10':lang==='fr'?'10 derniers':lang==='de'?'Letzte 10':lang==='el'?'Τελευταίοι 10':lang==='it'?'Ultimi 10':'Last 10'}</span>}
              </h3>
              <div className="ts-viewers-list">
                {viewers.map((v, i) => {
                  const pr = v.profile;
                  const isBlurred = plan === 'free' && i >= 3;
                  const timeAgo = (() => {
                    const diff = Date.now() - new Date(v.viewed_at).getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 60) return (lang==='bg'?`${mins} мин.`:lang==='es'?`hace ${mins} min`:lang==='fr'?`il y a ${mins} min`:lang==='de'?`vor ${mins} Min.`:lang==='el'?`πριν ${mins} λεπ.`:lang==='it'?`${mins} min fa`:`${mins}m ago`);
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return (lang==='bg'?`${hrs} ч.`:lang==='es'?`hace ${hrs} h`:lang==='fr'?`il y a ${hrs} h`:lang==='de'?`vor ${hrs} Std.`:lang==='el'?`πριν ${hrs} ώ.`:lang==='it'?`${hrs} h fa`:`${hrs}h ago`);
                    return (lang==='bg'?`${Math.floor(hrs/24)} д.`:lang==='es'?`hace ${Math.floor(hrs/24)} d`:lang==='fr'?`il y a ${Math.floor(hrs/24)} j`:lang==='de'?`vor ${Math.floor(hrs/24)} T.`:lang==='el'?`πριν ${Math.floor(hrs/24)} ημ.`:lang==='it'?`${Math.floor(hrs/24)} gg fa`:`${Math.floor(hrs/24)}d ago`);
                  })();
                  return (
                    <div key={i} className={`ts-viewer-item${isBlurred?' ts-viewer-blurred':''}`}>
                      <div className="ts-viewer-photo">
                        {getProfilePhotoUrl(pr?.photo_url, '')
                          ? <img src={getProfilePhotoUrl(pr.photo_url, '')} alt="" style={{width:42,height:42,borderRadius:'50%',objectFit:'cover'}}/>
                          : <div style={{width:42,height:42,borderRadius:'50%',background:'#30363d',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>👤</div>
                        }
                      </div>
                      <div className="ts-viewer-info">
                        <span className="ts-viewer-name">{isBlurred ? '••••••' : (pr?.name || '?')}</span>
                        <span className="ts-viewer-time">{timeAgo}</span>
                      </div>
                      {isBlurred && (
                        <span style={{fontSize:'0.7rem',color:'var(--gold)',background:'rgba(232,200,112,0.1)',border:'1px solid rgba(232,200,112,0.25)',borderRadius:12,padding:'2px 8px'}}>
                          🔒 Premium
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {plan === 'elite' && (
          <div style={{marginTop:'1.5rem',borderTop:'1px solid var(--border)',paddingTop:'1.5rem'}}>
            <h3 style={{color:'var(--gold)',fontSize:'0.95rem',marginBottom:'0.5rem'}}>
              {verified
                ? (lang==='bg'?'✅ Верифициран пътешественик':lang==='es'?'✅ Viajero verificado':lang==='fr'?'✅ Voyageur vérifié':lang==='de'?'✅ Verifizierter Reisender':lang==='el'?'✅ Επαληθευμένος ταξιδιώτης':lang==='it'?'✅ Viaggiatore verificato':'✅ Verified Traveler')
                : (lang==='bg'?'✅ Получи Verified значка':lang==='es'?'✅ Obtén insignia verificada':lang==='fr'?'✅ Obtenir badge vérifié':lang==='de'?'✅ Verifiziertes Abzeichen erhalten':lang==='el'?'✅ Αποκτήθηκε επαληθευμένη κονκάρδα':lang==='it'?'✅ Distintivo verificato ottenuto':'✅ Get Verified Badge')}
            </h3>
            {verified
              ? <p style={{color:'var(--muted)',fontSize:'0.85rem'}}>{lang==='bg'?'Твоят профил е верифициран! ✅':lang==='es'?'¡Tu perfil está verificado! ✅':lang==='fr'?'Votre profil est vérifié ! ✅':lang==='de'?'Dein Profil ist verifiziert! ✅':lang==='el'?'Το προφίλ σου είναι επαληθευμένο! ✅':lang==='it'?'Il tuo profilo è verificato! ✅':'Your profile is verified! ✅'}</p>
              : <>
                  <p style={{color:'var(--muted)',fontSize:'0.85rem',marginBottom:'0.75rem'}}>
                    {lang==='bg'?'Запиши кратко видео за да верифицираш своя профил и получи ✅ значка.':lang==='es'?'Graba un video corto para verificar tu perfil.':lang==='fr'?'Enregistrez une courte vidéo pour vérifier votre profil.':lang==='de'?'Nehmen Sie ein kurzes Video auf, um Ihr Profil zu verifizieren.':lang==='el'?'Τράβηξε ένα σύντομο βίντεο για να επαληθεύσεις το προφίλ σου.':lang==='it'?'Registra un breve video per verificare il tuo profilo.':'Record a short video to verify your profile and earn the ✅ badge.'}
                  </p>
                  <button className="ts-btn-gold-full" onClick={()=>setShowVerify(true)}>
                    🎥 {lang==='bg'?'Верифицирай профила':lang==='es'?'Verificar perfil':lang==='fr'?'Vérifier le profil':lang==='de'?'Profil verifizieren':lang==='el'?'Επαλήθευση προφίλ':lang==='it'?'Verifica profilo':'Verify Profile'}
                  </button>
                </>
            }
          </div>
        )}
      </div>
      <BlockedUsersPanel lang={lang} />
      {/* Delete Account */}
      <div style={{marginTop:'2rem',borderTop:'1px solid #30363d',paddingTop:'1.5rem',textAlign:'center'}}>
        <button
          onClick={()=>{ setDeleteStep(1); setShowDeleteModal(true); }}
          style={{background:'none',border:'1px solid #6e3030',color:'#e05555',borderRadius:10,padding:'10px 24px',fontSize:'0.85rem',cursor:'pointer',width:'100%'}}>
          🗑️ {lang==='bg'?'Изтрий профила':lang==='es'?'Eliminar cuenta':lang==='fr'?'Supprimer le compte':lang==='de'?'Konto löschen':lang==='el'?'Διαγραφή λογαριασμού':lang==='it'?'Elimina account':'Delete Account'}
        </button>
      </div>

      {showDeleteModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setShowDeleteModal(false)}>
          <div style={{background:'#0d1117',border:'1px solid #30363d',borderRadius:20,padding:'28px 24px',maxWidth:340,width:'100%'}} onClick={e=>e.stopPropagation()}>
            {deleteStep === 1 ? (
              <>
                <p style={{fontSize:'1.3rem',textAlign:'center',marginBottom:8}}>💔</p>
                <h3 style={{color:'var(--text)',fontSize:'1rem',textAlign:'center',marginBottom:8}}>
                  {lang==='bg'?'Наистина ли искате да изтриете профила си от TravelSoul?':lang==='es'?'¿Realmente quieres eliminar tu perfil de TravelSoul?':lang==='fr'?'Voulez-vous vraiment supprimer votre profil TravelSoul ?':lang==='de'?'Möchtest du dein TravelSoul-Profil wirklich löschen?':lang==='el'?'Θέλεις πραγματικά να διαγράψεις το προφίλ σου στο TravelSoul;':lang==='it'?'Vuoi davvero eliminare il tuo profilo TravelSoul?':'Do you really want to delete your TravelSoul profile?'}
                </h3>
                <button
                  onClick={()=>setShowDeleteModal(false)}
                  style={{width:'100%',padding:'12px',borderRadius:12,border:'1px solid #30363d',background:'none',color:'var(--text)',fontSize:'0.9rem',cursor:'pointer',marginBottom:10}}>
                  {lang==='bg'?'Отказ':lang==='es'?'Cancelar':lang==='fr'?'Annuler':lang==='de'?'Abbrechen':lang==='el'?'Ακύρωση':lang==='it'?'Annulla':'Cancel'}
                </button>
                <button
                  onClick={()=>setDeleteStep(2)}
                  style={{width:'100%',padding:'10px',borderRadius:12,border:'1px solid #6e3030',background:'none',color:'#e05555',fontSize:'0.85rem',cursor:'pointer'}}>
                  {lang==='bg'?'Изтрий профила ми':lang==='es'?'Eliminar mi cuenta':lang==='fr'?'Supprimer mon compte':lang==='de'?'Mein Konto löschen':lang==='el'?'Διαγραφή του λογαριασμού μου':lang==='it'?'Elimina il mio account':'Delete my account'}
                </button>
              </>
            ) : (
              <>
                <p style={{fontSize:'1.3rem',textAlign:'center',marginBottom:8}}>😢</p>
                <h3 style={{color:'var(--text)',fontSize:'1rem',textAlign:'center',marginBottom:8}}>
                  {lang==='bg'?'Сигурни ли сте?':lang==='es'?'¿Estás seguro?':lang==='fr'?'Êtes-vous sûr ?':lang==='de'?'Bist du sicher?':lang==='el'?'Είσαι σίγουρος/η;':lang==='it'?'Sei sicuro/a?':'Are you sure?'}
                </h3>
                <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem',marginBottom:20}}>
                  {lang==='bg'?'Това ще изтрие профила ви завинаги.':lang==='es'?'Esto eliminará tu perfil de forma permanente.':lang==='fr'?'Cela supprimera définitivement votre profil.':lang==='de'?'Dadurch wird dein Profil dauerhaft gelöscht.':lang==='el'?'Αυτό θα διαγράψει οριστικά το προφίλ σου.':lang==='it'?'Questo eliminerà definitivamente il tuo profilo.':'This will permanently delete your profile.'}
                </p>
                <button
                  onClick={()=>setShowDeleteModal(false)}
                  style={{width:'100%',padding:'12px',borderRadius:12,border:'1px solid #30363d',background:'none',color:'var(--text)',fontSize:'0.9rem',cursor:'pointer',marginBottom:10}}>
                  {lang==='bg'?'Отказ':lang==='es'?'Cancelar':lang==='fr'?'Annuler':lang==='de'?'Abbrechen':lang==='el'?'Ακύρωση':lang==='it'?'Annulla':'Cancel'}
                </button>
                <button
                  onClick={async()=>{
                    try {
                      if (supabase) {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (session?.access_token) {
                          const resp = await fetch(`${API_BASE}/api/delete-account`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${session.access_token}` },
                          });
                          if (!resp.ok) {
                            alert(lang==='bg'?'Грешка при изтриване. Опитайте пак или пишете на support@travelsoul.love':lang==='es'?'Error al eliminar. Inténtalo de nuevo o escribe a support@travelsoul.love':lang==='fr'?'Erreur lors de la suppression. Réessayez ou contactez support@travelsoul.love':lang==='de'?'Fehler beim Löschen. Versuche es erneut oder wende dich an support@travelsoul.love':lang==='el'?'Σφάλμα διαγραφής. Δοκίμασε ξανά ή επικοινώνησε με το support@travelsoul.love':lang==='it'?'Errore durante l\'eliminazione. Riprova o contatta support@travelsoul.love':'Error deleting. Try again or contact support@travelsoul.love');
                            return;
                          }
                          try { await supabase.auth.signOut({ scope: 'global' }); } catch(e) {}
                          safeRemoveTravelSoulData();
                          setShowDeleteModal(false);
                          window.location.replace('https://travelsoul.love/?deleted=1');
                          return;
                        }
                        // No valid session — sign out locally and redirect to landing
                        try { await supabase.auth.signOut({ scope: 'local' }); } catch(e) {}
                      }
                      safeRemoveTravelSoulData();
                      setShowDeleteModal(false);
                      window.dispatchEvent(new CustomEvent('ts-401'));
                    } catch (err) {
                      alert(lang==='bg'?'Грешка при изтриване.':lang==='es'?'Error al eliminar la cuenta.':lang==='fr'?'Erreur lors de la suppression du compte.':lang==='de'?'Fehler beim Löschen des Kontos.':lang==='el'?'Σφάλμα κατά τη διαγραφή του λογαριασμού.':lang==='it'?'Errore durante l\'eliminazione dell\'account.':'Error deleting account.');
                    }
                  }}
                  style={{width:'100%',padding:'12px',borderRadius:12,border:'none',background:'#6e3030',color:'#fff',fontWeight:700,fontSize:'0.9rem',cursor:'pointer'}}>
                  {lang==='bg'?'Да, изтрий':lang==='es'?'Sí, eliminar':lang==='fr'?'Oui, supprimer':lang==='de'?'Ja, löschen':lang==='el'?'Ναι, διαγραφή':lang==='it'?'Sì, elimina':'Yes, delete'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showVerify && <VideoIntroModal t={t} lang={lang} onClose={()=>setShowVerify(false)} onVerified={()=>{setVerified(true);setShowVerify(false);}} />}
      {showDnaModal && <TravelDnaModal lang={lang} plan={plan} onClose={()=>setShowDnaModal(false)} onSaved={title=>setDnaTitle(title)} />}
      {showMapModal && (
        <TravelMapModal
          bucketList={JSON.parse(localStorage.getItem('travelsoul_profile')||'{}').bucket_list || []}
          lang={lang} plan={plan} isOwn={true}
          onClose={()=>setShowMapModal(false)}
          joinedTrips={joinedTrips||[]}
        />
      )}
      {showBadgeCreator && (
        <CustomBadgeCreatorModal
          lang={lang} plan={plan} existingBadge={customBadge}
          onClose={()=>setShowBadgeCreator(false)}
          onSaved={badge=>setCustomBadge(badge)}
        />
      )}
      {showOptimizer && (
        <ProfileOptimizerModal
          lang={lang} plan={plan}
          profileData={{
            photo,
            photos,
            bio: form.bio,
            tags: form.tags,
            dreams: form.dreams,
            videoIntro,
            verified,
            dnaTitle,
            bucketList: JSON.parse(localStorage.getItem('travelsoul_profile')||'{}').bucket_list || [],
          }}
          onClose={()=>setShowOptimizer(false)}
        />
      )}
    </div>
  );
}

// ── Onboarding Screen ─────────────────────────────────────────────────────────
function OnboardingScreen({ t, lang, onComplete }) {
  const ob = t.onboarding;
  const [step, setStep] = useState(0);
  const [dnaType, setDnaType] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [goals, setGoals] = useState([]);
  const [celebrating, setCelebrating] = useState(false);

  function toggleDest(name) {
    setDestinations(d => d.includes(name) ? d.filter(x=>x!==name) : d.length < 5 ? [...d, name] : d);
  }
  function toggleGoal(k) {
    setGoals(g => g.includes(k) ? g.filter(x=>x!==k) : [...g, k]);
  }

  async function finish() {
    localStorage.setItem('travelsoul_onboarding_data', JSON.stringify({ dnaType, destinations, goals }));
    localStorage.setItem('travelsoul_onboarded', 'true');
    const existingProfile = JSON.parse(localStorage.getItem('travelsoul_profile') || '{}');
    setProfileLocalStorage({ ...existingProfile, tags:[dnaType].filter(Boolean), dreams:destinations, lookingFor:goals });
    patchProfile({
      travel_dna: dnaType ? [dnaType] : [],
      dream_destinations: destinations.length ? destinations : null,
      looking_for: goals.length ? goals : null,
      quiz_completed: true,
    });
    setCelebrating(true);
    setTimeout(onComplete, 2000);
  }

  function skip() { localStorage.setItem('travelsoul_onboarded','true'); patchProfile({ quiz_completed: true }); onComplete(); }

  const dots = [0,1,2,3];

  return (
    <div className="ts-onboarding">
      <button className="ts-onboarding-skip" onClick={skip}>{ob.skip}</button>
      <div className="ts-onboarding-dots">
        {dots.map(i=><div key={i} className={`ts-ob-dot ${i===step?'active':i<step?'done':''}`}/>)}
      </div>

      {celebrating && (
        <div className="ts-ob-celebrate">
          {['🎉','✨','🌟','🎊','💫','🎉','✨','🌟'].map((e,i)=>(
            <span key={i} className="ts-confetti" style={{'--delay':`${i*0.15}s`,'--x':`${Math.random()*100}%`}}>{e}</span>
          ))}
          <h2 className="ts-serif ts-ob-congrats">🎉 {ob.welcome || 'Welcome!'}</h2>
        </div>
      )}

      {!celebrating && (
        <div className="ts-ob-content">
          {step === 0 && (
            <div className="ts-ob-step">
              <div className="ts-ob-plane">✈️</div>
              <h2 className="ts-serif ts-ob-title">{ob.welcome}</h2>
              <p className="ts-ob-sub">{ob.welcomeSub}</p>
              <button className="ts-btn-gold-full ts-ob-btn" onClick={()=>setStep(1)}>{ob.next}</button>
            </div>
          )}

          {step === 1 && (
            <div className="ts-ob-step">
              <h2 className="ts-serif ts-ob-title">{ob.whatType}</h2>
              <div className="ts-ob-type-grid">
                {ob.types.map(type=>(
                  <div key={type.key} className={`ts-ob-type-card ${dnaType===type.key?'selected':''}`} onClick={()=>setDnaType(type.key)}>
                    <div className="ts-ob-type-icon">{type.icon}</div>
                    <div className="ts-ob-type-name">{type.name}</div>
                    <div className="ts-ob-type-desc">{type.desc}</div>
                  </div>
                ))}
              </div>
              <button className="ts-btn-gold-full ts-ob-btn" disabled={!dnaType} style={{opacity:dnaType?1:0.45}} onClick={()=>setStep(2)}>{ob.next}</button>
            </div>
          )}

          {step === 2 && (
            <div className="ts-ob-step">
              <h2 className="ts-serif ts-ob-title">{ob.destinations}</h2>
              <p style={{color:'var(--muted)',fontSize:'0.85rem',marginBottom:'1rem'}}>{t.extras?.selectUpTo5 || 'Select up to 5'}</p>
              <div className="ts-ob-dest-grid">
                {DREAM_DESTINATIONS.map(d=>(
                  <button key={d.name} className={`ts-ob-dest-pill ${destinations.includes(d.name)?'selected':''}`} onClick={()=>toggleDest(d.name)}>
                    {d.flag} {d.name} {destinations.includes(d.name)&&'✓'}
                  </button>
                ))}
              </div>
              <button className="ts-btn-gold-full ts-ob-btn" disabled={destinations.length===0} style={{opacity:destinations.length>0?1:0.45}} onClick={()=>setStep(3)}>{ob.next}</button>
            </div>
          )}

          {step === 3 && (
            <div className="ts-ob-step">
              <h2 className="ts-serif ts-ob-title">{ob.lookingFor}</h2>
              <div className="ts-ob-goal-grid">
                {ob.goals.map(g=>(
                  <div key={g.key} className={`ts-ob-goal-card ${goals.includes(g.key)?'selected':''}`} onClick={()=>toggleGoal(g.key)}>
                    <div className="ts-ob-goal-icon">{g.icon}</div>
                    <div className="ts-ob-goal-name">{g.name}</div>
                  </div>
                ))}
              </div>
              <button className="ts-btn-gold-full ts-ob-btn" disabled={goals.length===0} style={{opacity:goals.length>0?1:0.45}} onClick={finish}>{ob.finish}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Privacy & Terms Screens ───────────────────────────────────────────────────
function PrivacyScreen({ lang = 'en', setLang, t, onBack, onNavigate }) {
  const lnd = t.landing || {};
  const pc = lnd.privacyContent || {};
  const en = T.en.landing.privacyContent;
  return (
    <div className="ts-legal-screen">
      <div className="ts-legal-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h1 className="ts-serif" style={{color:'var(--gold)'}}>🔒 {lnd.privacyTitle || 'Privacy Policy'}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
      </div>
      <div className="ts-legal-body">
        <p><strong>{pc.lastUpdated || en.lastUpdated}</strong></p>
        <h3>{pc.collect || en.collect}</h3>
        <p>{pc.collectText || en.collectText}</p>
        <h3>{pc.use || en.use}</h3>
        <p>{pc.useText || en.useText}</p>
        <h3>{pc.storage || en.storage}</h3>
        <p>{pc.storageText || en.storageText}</p>
        <h3>{pc.thirdParties || en.thirdParties}</h3>
        <p>{pc.thirdPartiesText || en.thirdPartiesText}</p>
        <h3>{pc.rights || en.rights}</h3>
        <p>{pc.rightsText || en.rightsText}</p>
        <h3>{pc.cookies || en.cookies}</h3>
        <p>{pc.cookiesText || en.cookiesText}</p>
        <h3>{pc.contact || en.contact}</h3>
        <p>{pc.contactText || en.contactText}</p>
        <ComplianceNotice lang={lang} />
      </div>
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

function AccountDeletionScreen({ lang = 'en', setLang, t, onBack, onNavigate }) {
  const bg = lang === 'bg';
  const copy = bg ? {
    title: 'Изтриване на акаунт',
    intro: 'Можете да изтриете своя TravelSoul акаунт по всяко време.',
    inAppTitle: 'Изтриване в приложението',
    inAppText: 'Влезте в TravelSoul и отворете Профил → Изтрий профила. Потвърдете изтриването на двата екрана за потвърждение.',
    emailTitle: 'Заявка по имейл',
    emailBefore: 'Ако нямате достъп до приложението, изпратете заявка от имейл адреса на акаунта си до ',
    emailAfter: '. Моля, посочете, че желаете изтриване на TravelSoul акаунта си.',
    dataTitle: 'Какво се случва с данните ви',
    dataText: 'Изтриваме профила и свързаните с него данни. Някои записи може да бъдат запазени само когато това се изисква или е разрешено от приложимото право, за предотвратяване на измами, сигурност или защита на законни права.',
    subscriptionTitle: 'Google Play абонаменти',
    subscriptionText: 'Изтриването на TravelSoul акаунта не отменя автоматично абонамент в Google Play. Управлявайте или отменете абонамента си отделно в Google Play, за да предотвратите бъдещи таксувания.',
  } : {
    title: 'Account deletion',
    intro: 'You can delete your TravelSoul account at any time.',
    inAppTitle: 'Delete in the app',
    inAppText: 'Sign in to TravelSoul and open Profile → Delete Account. Confirm deletion in the two confirmation screens.',
    emailTitle: 'Request deletion by email',
    emailBefore: 'If you cannot access the app, send a request from your account email address to ',
    emailAfter: '. Please state that you want your TravelSoul account deleted.',
    dataTitle: 'What happens to your data',
    dataText: 'We delete your profile and associated account data. Some records may be retained only where required or permitted by applicable law, fraud prevention, security, or the protection of legal rights.',
    subscriptionTitle: 'Google Play subscriptions',
    subscriptionText: 'Deleting your TravelSoul account does not automatically cancel a Google Play subscription. Manage or cancel your subscription separately in Google Play to prevent future charges.',
  };
  return (
    <div className="ts-legal-screen">
      <div className="ts-legal-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h1 className="ts-serif" style={{color:'var(--gold)'}}>🗑️ {copy.title}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
      </div>
      <div className="ts-legal-body">
        <p><strong>{copy.intro}</strong></p>
        <h3>{copy.inAppTitle}</h3>
        <p>{copy.inAppText}</p>
        <h3>{copy.emailTitle}</h3>
        <p>{copy.emailBefore}<a href="mailto:support@travelsoul.love">support@travelsoul.love</a>{copy.emailAfter}</p>
        <h3>{copy.dataTitle}</h3>
        <p>{copy.dataText}</p>
        <h3>{copy.subscriptionTitle}</h3>
        <p>{copy.subscriptionText}</p>
      </div>
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

function ChildSafetyScreen({ lang = 'en', setLang, t, onBack, onNavigate }) {
  const bg = lang === 'bg';
  const copy = bg ? {
    title: 'Стандарти за безопасност на деца',
    intro: 'TravelSoul е платформа само за лица на 18 и повече години.',
    ageTitle: 'Само за пълнолетни',
    ageText: 'Не позволяваме регистрация или използване на TravelSoul от лица под 18 години.',
    prohibitedTitle: 'Строго забранено съдържание',
    prohibitedText: 'Сексуалната злоупотреба и експлоатация на деца (CSAE), както и материали със сексуална злоупотреба с деца (CSAM), са строго забранени.',
    reportTitle: 'Как да подадете сигнал',
    reportBefore: 'Подайте сигнал за проблем, свързан с безопасността на деца, чрез инструментите за докладване или блокиране в приложението или чрез имейл до ',
    reportAfter: '.',
    actionTitle: 'Нашите действия',
    actionText: 'Преглеждаме сигналите, премахваме съдържание и акаунти, които нарушават правилата, и може да докладваме потвърден CSAM на компетентните органи, когато това се изисква от закона.',
    contactTitle: 'Контакт за child safety',
    contactText: 'Google Play може да се свърже с определения контакт на TravelSoul относно въпроси за безопасността на деца. За такива въпроси пишете на ',
  } : {
    title: 'Child Safety Standards',
    intro: 'TravelSoul is a platform for adults aged 18 and over only.',
    ageTitle: 'Adults only',
    ageText: 'We do not allow people under 18 to register for or use TravelSoul.',
    prohibitedTitle: 'Strictly prohibited content',
    prohibitedText: 'Child sexual abuse and exploitation (CSAE), including child sexual abuse material (CSAM), is strictly prohibited.',
    reportTitle: 'How to report a concern',
    reportBefore: 'Report a child-safety concern using the in-app report or block tools, or email ',
    reportAfter: '.',
    actionTitle: 'Our response',
    actionText: 'We review reports, remove content and accounts that violate our standards, and may report confirmed CSAM to relevant authorities where required by law.',
    contactTitle: 'Child safety contact',
    contactText: 'Google Play may contact TravelSoul’s designated point of contact about child-safety issues. For these matters, email ',
  };
  return (
    <div className="ts-legal-screen">
      <div className="ts-legal-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h1 className="ts-serif" style={{color:'var(--gold)'}}>🛡️ {copy.title}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
      </div>
      <div className="ts-legal-body">
        <p><strong>{copy.intro}</strong></p>
        <h3>{copy.ageTitle}</h3>
        <p>{copy.ageText}</p>
        <h3>{copy.prohibitedTitle}</h3>
        <p>{copy.prohibitedText}</p>
        <h3>{copy.reportTitle}</h3>
        <p>{copy.reportBefore}<a href="mailto:support@travelsoul.love">support@travelsoul.love</a>{copy.reportAfter}</p>
        <h3>{copy.actionTitle}</h3>
        <p>{copy.actionText}</p>
        <h3>{copy.contactTitle}</h3>
        <p>{copy.contactText}<a href="mailto:support@travelsoul.love">support@travelsoul.love</a>.</p>
      </div>
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

function TermsScreen({ lang = 'en', setLang, t, onBack, onNavigate }) {
  const legal = legalUi(lang);
  const lnd = t.landing || {};
  const tc = lnd.termsContent || {};
  const en = T.en.landing.termsContent;
  return (
    <div className="ts-legal-screen">
      <div className="ts-legal-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h1 className="ts-serif" style={{color:'var(--gold)'}}>📋 {lnd.termsTitle || 'Terms of Service'}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
      </div>
      <div className="ts-legal-body">
        <p><strong>{tc.lastUpdated || en.lastUpdated}</strong></p>
        <h3>{tc.eligibility || en.eligibility}</h3>
        <p>{tc.eligibilityText || en.eligibilityText}</p>
        <h3>{tc.conduct || en.conduct}</h3>
        <p>{tc.conductText || en.conductText}</p>
        <h3>{legal.prohibitedTitle}</h3>
        <p>{legal.complianceText}</p>
        <ul>
          {legal.prohibitedItems.map(item => <li key={item}>{item}</li>)}
        </ul>
        <h3>{tc.safety || en.safety}</h3>
        <p>{tc.safetyText || en.safetyText}</p>
        <h3>{tc.premium || en.premium}</h3>
        <p>{tc.premiumText || en.premiumText}</p>
        <h3>{tc.contact || en.contact}</h3>
        <p>{tc.contactText || en.contactText}</p>
      </div>
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

function RefundScreen({ lang = 'en', setLang, t, onBack, onNavigate }) {
  const legal = legalUi(lang);
  const lnd = t.landing || {};
  const rc = lnd.refundContent || {};
  const en = T.en.landing.refundContent;
  const openPolicy = (screen) => (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate(screen);
  };
  return (
    <div className="ts-legal-screen">
      <div className="ts-legal-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h1 className="ts-serif" style={{color:'var(--gold)'}}>💳 {rc.title || en.title}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
      </div>
      <div className="ts-legal-body">
        <p><strong>{rc.lastUpdated || en.lastUpdated}</strong></p>
        <p>{rc.intro || en.intro}</p>
        <h3>{rc.eligibility || en.eligibility}</h3>
        <p>{rc.eligibilityText || en.eligibilityText}</p>
        <h3>{rc.process || en.process}</h3>
        <p>{rc.processText || en.processText}</p>
        <h3>{legal.refundExtra.cancellation}</h3>
        <p>{legal.refundExtra.cancellationText}</p>
        <h3>{legal.refundExtra.renewals}</h3>
        <p>{legal.refundExtra.renewalsText}</p>
        <h3>{legal.refundExtra.termsPrivacy}</h3>
        <p>
          {legal.refundExtra.termsPrivacyText.split('{terms}')[0]}
          <a href="/terms" onClick={openPolicy('terms')}>{legal.footerLinks.terms}</a>
          {legal.refundExtra.termsPrivacyText.split('{terms}')[1].split('{privacy}')[0]}
          <a href="/privacy" onClick={openPolicy('privacy')}>{legal.footerLinks.privacy}</a>
          {legal.refundExtra.termsPrivacyText.split('{privacy}')[1]}
        </p>
        <h3>{rc.nonRefundable || en.nonRefundable}</h3>
        <p>{rc.nonRefundableText || en.nonRefundableText}</p>
        <h3>{rc.contact || en.contact}</h3>
        <p>{rc.contactText || en.contactText}</p>
      </div>
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

function ContactScreen({ lang = 'en', setLang, t, onBack, onNavigate }) {
  const legal = legalUi(lang);
  return (
    <div className="ts-legal-screen">
      <div className="ts-legal-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{t.extras?.back || '← Back'}</button>
        <h1 className="ts-serif" style={{color:'var(--gold)'}}>{legal.contactTitle}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
      </div>
      <div className="ts-legal-body">
        <p>{legal.serviceDescription}</p>
        <LegalDetailsBlock lang={lang} />
        <ComplianceNotice lang={lang} />
      </div>
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

// ── New Components ─────────────────────────────────────────────────────────────

function SpinWheelModal({ lang, onClose, coinBalance, setCoinBalance }) {
  const activeProfileId = getAuthToken().userId;
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [grantFailed, setGrantFailed] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(spinHistoryKey(activeProfileId)) || '[]'); } catch { return []; }
  });
  const st = SPIN_T[lang] || SPIN_T.en;
  const SPIN_PRIZES = getSpinPrizes();
  const today = localDay();
  const [storedSpinDate, setStoredSpinDateState] = useState(() => getStoredSpinLastDate(activeProfileId));
  // Init synchronously from localStorage so the FIRST render already reflects a
  // persisted lock for this profile (don't wait for the async DB fetch to re-disable the button).
  const [serverSpun, setServerSpun] = useState(() => spunToday(getStoredSpinLastDate(activeProfileId)));
  // Hardened gate: consult Supabase spin_last_date directly, not just localStorage.
  // A failed persist or cleared localStorage must not re-enable the wheel.
  useEffect(() => {
    if (!supabase) return;
    const { userId } = getAuthToken();
    if (!userId) return;
    supabase.from('profiles').select('spin_last_date').eq('id', userId).single()
      .then(({ data }) => {
        if (spunToday(data?.spin_last_date)) {
          setStoredSpinLastDate(userId, localDay());
          setStoredSpinDateState(localDay());
          setServerSpun(true);
        } else {
          setStoredSpinLastDate(userId, null);
          setStoredSpinDateState(null);
          setServerSpun(false);
        }
      })
      .catch(() => {});
  }, []);
  const alreadySpunLocal = spunToday(storedSpinDate);
  const alreadySpun = !result && (serverSpun || alreadySpunLocal);
  const historyLabel = lang==='bg'?'Моите награди':lang==='es'?'Mis premios':lang==='fr'?'Mes prix':lang==='de'?'Meine Preise':lang==='el'?'Τα δώρα μου':lang==='it'?'I miei premi':'My Prizes';

  useEffect(() => {
    if (!result) return;
    const timer = setTimeout(() => onClose(), grantFailed ? 8000 : 5000);
    return () => clearTimeout(timer);
  }, [result, grantFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  async function spin() {
    if (spinning) return;
    if (serverSpun || spunToday(getStoredSpinLastDate(activeProfileId))) return;
    // Audit finding (5th audit, CRITICAL): the prize itself used to be
    // chosen client-side (Math.random() over SPIN_PRIZES), then the client
    // just told the server which reward to grant via adjust_profile_counter/
    // grant_profile_boost — RPCs that only checked the caller owned the
    // target profile, not that a prize was actually won. Any authenticated
    // user could call grant_profile_boost (or adjust_profile_counter with a
    // positive delta) directly for unlimited free rewards.
    // Fix: claim_daily_spin() now picks the prize SERVER-SIDE and stores it
    // as a one-shot pending grant; redeem_spin_prize() atomically consumes
    // that pending grant and applies it. The client only uses the returned
    // prize to drive the animation/label — it never supplies the prize.
    if (!supabase) return;
    setSpinning(true);
    let serverPrize = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setSpinning(false); return; }
      // Audit finding (5th audit): the server previously compared against
      // its own UTC calendar day, not the player's local day, letting users
      // far from UTC claim two spins within one local day near the UTC
      // boundary. Pass the client's UTC offset (minutes east of UTC) so the
      // server can compute "today" in the player's own timezone instead —
      // the RPC clamps this to real-world offsets, so it can't be abused to
      // grant more than one spin per real day.
      const { data: claimResult, error: claimErr } = await supabase.rpc('claim_daily_spin', {
        p_user_id: session.user.id,
        p_utc_offset_minutes: -new Date().getTimezoneOffset(),
      });
      if (claimErr || !claimResult?.claimed) {
        setStoredSpinLastDate(activeProfileId, today);
        setStoredSpinDateState(today);
        setServerSpun(true);
        setSpinning(false);
        return;
      }
      serverPrize = claimResult.prize;
    } catch (e) {
      console.error('[SpinWheel] claim_daily_spin failed:', e);
      setSpinning(false);
      return;
    }
    // Map the server-chosen prize key to the matching client display entry,
    // so the wheel animation lands on the segment that was ACTUALLY won.
    const idx = Math.max(0, SPIN_PRIZES.findIndex(p => {
      if (serverPrize?.key === 'coins500') return p.coins === 500;
      if (serverPrize?.key === 'coins250') return p.coins === 250;
      if (serverPrize?.key === 'superLikes1') return !!p.superLikes;
      if (serverPrize?.key === 'boost') return !!p.boost;
      if (serverPrize?.key === 'likesX5') return !!p.likesX5;
      if (serverPrize?.key === 'freeTripEntry') return !!p.freeTripEntry;
      if (serverPrize?.key === 'mysteryGift') return !!p.mysteryGift;
      return false;
    }));
    const seg = 360 / SPIN_PRIZES.length;
    // angle that brings the CENTER of segment idx to the top pointer (0deg),
    // segments run clockwise from top so we rotate counter to that:
    const targetAngle = 360 - (idx * seg) - (seg / 2);
    // keep spinning forward several full turns from current position, landing on target:
    const base = rotation - (rotation % 360);
    const newRotation = base + 1440 + targetAngle;
    setRotation(newRotation);
    setTimeout(async () => {
      const prize = SPIN_PRIZES[idx];
      setResult(prize);
      setGrantFailed(false);
      setStoredSpinLastDate(activeProfileId, today);
      setStoredSpinDateState(today);
      setServerSpun(true);

      // Save to history
      const entry = { date: new Date().toLocaleDateString(), prize: prize.label, emoji: prize.emoji };
      const newHistory = [entry, ...history].slice(0, 5);
      setHistory(newHistory);
      localStorage.setItem(spinHistoryKey(activeProfileId), JSON.stringify(newHistory));

      // Redeem the server-chosen, one-shot pending prize. redeem_spin_prize()
      // reads-and-clears profiles.pending_spin_prize and applies the actual
      // grant atomically — the client supplies nothing but the user id.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: redeemResult, error } = await supabase.rpc('redeem_spin_prize', {
            p_user_id: session.user.id,
          });
          if (error || !redeemResult?.redeemed) {
            console.error('[SpinWheel] redeem_spin_prize failed:', error);
            setGrantFailed(true);
          } else {
            if (typeof redeemResult.new_coins === 'number') {
              setCoinBalance && setCoinBalance(redeemResult.new_coins);
              localStorage.setItem('travelsoul_coins', String(redeemResult.new_coins));
            }
            if (prize.mysteryGift && redeemResult.prize?.resolved) {
              const resolved = redeemResult.prize.resolved;
              const mysteryLabel = resolved.coins ? `🪙 ${resolved.coins} монети` : resolved.boost ? '🔄 Boost 30 мин' : `🔥 Super Like x${resolved.superLikes}`;
              setResult(prev => ({ ...prev, mysteryLabel }));
            }
          }
        }
      } catch (e) {
        console.error('[SpinWheel] redeem_spin_prize failed:', e);
        setGrantFailed(true);
      }

      setSpinning(false);
    }, 4000);
  }

  const N = SPIN_PRIZES.length;
  const seg = 360 / N;

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-spin-modal" style={{...MODAL_S, position:'relative'}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">🎰 {st.dailySpin}</h2>

        {alreadySpun ? (
          <div className="ts-spin-state">
            <div style={{fontSize:'3rem',textAlign:'center'}}>⏰</div>
            <p style={{color:'var(--muted)',textAlign:'center',marginTop:'0.5rem'}}>{st.comeBack}</p>
          </div>
        ) : (
          <>
            <div className="ts-spin-wheel-wrap">
              {/* Golden pointer arrow */}
              <div style={{position:'relative',zIndex:3,marginBottom:'-10px',textAlign:'center',fontSize:'2rem',color:'#c9a84c',filter:'drop-shadow(0 0 8px #c9a84caa)',lineHeight:1}}>▼</div>

              {/* Wheel disc */}
              <div className="ts-spin-disc" style={{
                background:`conic-gradient(${SPIN_PRIZES.map((p,i)=>`${p.color} ${i*seg}deg ${(i+1)*seg}deg`).join(',')})`,
                transform:`rotate(${rotation}deg)`,
                transition: spinning ? 'transform 4s cubic-bezier(0.17,0.67,0.12,0.99)' : 'none',
              }}>
                {SPIN_PRIZES.map((p,i) => (
                  <div key={i} style={{
                    position:'absolute', left:'50%', top:'50%',
                    transform:`rotate(${i*seg+seg/2}deg) translateY(-100px) rotate(${-(i*seg+seg/2)}deg) translate(-50%,-50%)`,
                    fontSize:'11px', fontWeight:800, color:'#fff',
                    textShadow:'0 1px 4px rgba(0,0,0,0.95)', whiteSpace:'nowrap', pointerEvents:'none',
                  }}>{p.short}</div>
                ))}
                {/* Center jewel */}
                <div style={{
                  position:'absolute', left:'50%', top:'50%', transform:'translate(-50%,-50%)',
                  width:26, height:26, borderRadius:'50%',
                  background:'radial-gradient(circle at 35% 35%,#fff 10%,#c9a84c 60%,#8b6914 100%)',
                  boxShadow:'0 0 14px #c9a84c, 0 2px 4px rgba(0,0,0,0.6)',
                  zIndex:2,
                }}/>
              </div>
            </div>

            <button className="ts-btn-gold-full" onClick={spin} disabled={spinning || serverSpun || alreadySpunLocal} style={{marginTop:'1.25rem'}}>
              {spinning ? '🌀 Spinning...' : `🎰 ${st.spinBtn}`}
            </button>

            {/* Result overlay */}
            {result && (
              <div style={{
                position:'absolute', inset:0, background:'rgba(13,17,23,0.96)',
                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                borderRadius:20, animation:'ts-pop 0.4s ease', zIndex:10,
              }}>
                <div style={{fontSize:'1.8rem',letterSpacing:'0.4rem',marginBottom:'0.5rem'}}>🎊 🎉 🎊</div>
                <div style={{fontSize:'5rem',marginBottom:'0.5rem',lineHeight:1}}>{result.emoji}</div>
                <div style={{fontSize:'1.25rem',fontWeight:700,color:'var(--gold)',marginBottom:'0.4rem',textAlign:'center',padding:'0 1rem'}}>{result.label}</div>
                <div style={{fontSize:'1rem',color:'var(--text)',fontWeight:600,marginBottom:'0.5rem'}}>🎉 {st.spinResult}</div>
                {result.coins && (
                  <div style={{background:'rgba(245,158,11,0.15)',border:'1px solid #f59e0b',borderRadius:12,padding:'8px 18px',fontSize:'0.9rem',color:'#fbbf24',fontWeight:700,marginBottom:'0.5rem'}}>
                    🎉 +{result.coins} {lang==='bg'?'монети добавени към баланса ти!':lang==='es'?'monedas añadidas a tu saldo!':lang==='fr'?'pièces ajoutées à votre solde !':lang==='de'?'Münzen zu deinem Guthaben hinzugefügt!':lang==='el'?'Προστέθηκαν νομίσματα στο υπόλοιπό σου!':lang==='it'?'Monete aggiunte al tuo saldo!':'coins added to your balance!'}
                  </div>
                )}
                {result.mysteryLabel && (
                  <div style={{background:'rgba(124,58,237,0.15)',border:'1px solid #7c3aed',borderRadius:12,padding:'8px 18px',fontSize:'0.9rem',color:'#a78bfa',fontWeight:700,marginBottom:'0.5rem'}}>
                    🎁 {lang==='bg'?'Получи:':lang==='es'?'Has recibido:':lang==='fr'?'Vous avez reçu :':lang==='de'?'Du hast erhalten:':lang==='el'?'Έλαβες:':lang==='it'?'Hai ricevuto:':'You got:'} {result.mysteryLabel}
                  </div>
                )}
                {grantFailed && (
                  <div style={{background:'rgba(248,81,73,0.15)',border:'1px solid #f85149',borderRadius:12,padding:'8px 18px',fontSize:'0.8rem',color:'#f85149',fontWeight:600,marginBottom:'0.5rem',textAlign:'center'}}>
                    ⚠️ {lang==='bg'?'Наградата може да не се е записала. Виж баланса си или пробвай отново по-късно.':lang==='es'?"Es posible que la recompensa no se haya guardado. Revisa tu saldo o inténtalo más tarde.":lang==='fr'?"La récompense n'a peut-être pas été enregistrée. Vérifiez votre solde ou réessayez plus tard.":lang==='de'?"Die Belohnung wurde möglicherweise nicht gespeichert. Prüfe dein Guthaben oder versuche es später erneut.":lang==='el'?"Η ανταμοιβή ενδέχεται να μην αποθηκεύτηκε. Έλεγξε το υπόλοιπό σου ή δοκίμασε ξανά αργότερα.":lang==='it'?"La ricompensa potrebbe non essere stata salvata. Controlla il tuo saldo o riprova più tardi.":"The reward may not have saved. Check your balance or try again later."}
                  </div>
                )}
                <div style={{fontSize:'1.8rem',letterSpacing:'0.4rem',marginTop:'0.25rem'}}>🎊 🎉 🎊</div>
              </div>
            )}
          </>
        )}

        {/* Rewards history */}
        {history.length > 0 && (
          <div style={{marginTop:'1.25rem',borderTop:'1px solid #21262d',paddingTop:'0.75rem'}}>
            <p style={{fontSize:'0.75rem',color:'var(--muted)',fontWeight:600,marginBottom:'0.5rem',textAlign:'left'}}>🏆 {historyLabel}</p>
            {history.map((h,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:'1px solid #21262d22'}}>
                <span style={{fontSize:'1.2rem'}}>{h.emoji}</span>
                <span style={{fontSize:'0.78rem',color:'var(--text)',flex:1,textAlign:'left'}}>{h.prize}</span>
                <span style={{fontSize:'0.7rem',color:'var(--muted)'}}>{h.date}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoIntroModal({ lang, t, onClose }) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const videoPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const vi = t.videoIntro || {};

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(tr => tr.stop());
      if (timerRef.current) clearInterval(timerRef.current);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((step === 1 || step === 2) && streamRef.current && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
      videoPreviewRef.current.play().catch(() => {});
    }
  }, [step]);

  function handleClose() {
    if (streamRef.current) streamRef.current.getTracks().forEach(tr => tr.stop());
    if (timerRef.current) clearInterval(timerRef.current);
    onClose();
  }

  async function startCamera() {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setStep(1);
    } catch {
      setError(vi.cameraError || 'Camera not available. Please check your settings.');
    }
  }

  function startRecording() {
    chunksRef.current = [];
    setDuration(0);
    setError('');
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
      ? 'video/webm;codecs=vp8,opus'
      : MediaRecorder.isTypeSupported('video/webm')
      ? 'video/webm'
      : 'video/mp4';
    const mr = new MediaRecorder(streamRef.current, { mimeType });
    mediaRecorderRef.current = mr;
    mr.ondataavailable = e => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const type = mr.mimeType || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setBlobUrl(url);
      setStep(3);
    };
    mr.start(100);
    setStep(2);
    timerRef.current = setInterval(() => {
      setDuration(d => {
        if (d >= 30) { stopRecording(); return d; }
        return d + 1;
      });
    }, 1000);
  }

  function stopRecording() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) streamRef.current.getTracks().forEach(tr => tr.stop());
  }

  async function uploadVideo() {
    if (!recordedBlob) return;
    if (recordedBlob.size > 50 * 1024 * 1024) { setError(vi.videoTooBig || 'Video too large (max 50MB)'); return; }
    if (duration < 15) { setError(vi.videoTooShort || 'Video must be at least 15 seconds'); return; }
    setStep(4);
    const { userId, accessToken } = getAuthToken();
    if (!userId || !accessToken) { setError('Not logged in'); setStep(3); return; }
    const ext = recordedBlob.type.includes('mp4') ? 'mp4' : 'webm';
    const path = `${userId}/intro.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('profile-videos')
      .upload(path, recordedBlob, { contentType: recordedBlob.type, upsert: true });
    if (upErr) { setError(upErr.message); setStep(3); return; }
    const { data: { publicUrl } } = supabase.storage.from('profile-videos').getPublicUrl(path);
    await sbFetch('POST', 'profiles', '', { id: userId, video_url: publicUrl }, { 'Prefer': 'resolution=merge-duplicates' });
    const lsUser = JSON.parse(localStorage.getItem('travelsoul_user') || '{}');
    localStorage.setItem('travelsoul_user', JSON.stringify({ ...lsUser, video_url: publicUrl }));
    setStep(5);
  }

  function retake() {
    if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
    setRecordedBlob(null);
    setError('');
    setDuration(0);
    startCamera();
  }

  const mins = String(Math.floor(duration / 60)).padStart(2, '0');
  const secs = String(duration % 60).padStart(2, '0');

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={step < 2 ? handleClose : undefined}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:400}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={handleClose}>✕</button>

        {step === 0 && <>
          <div style={{fontSize:'3rem',textAlign:'center',margin:'0.5rem 0'}}>🎥</div>
          <h2 className="ts-serif ts-modal-title">{vi.recordIntro||'Record Intro Video'}</h2>
          <p className="ts-modal-sub">{vi.introInstructions||'Record a short intro video to verify your profile'}</p>
          <ul style={{textAlign:'left',color:'var(--muted)',fontSize:'0.85rem',margin:'1rem 0',paddingLeft:'1.2rem'}}>
            <li>⏱ {vi.duration15to30||'15–30 seconds'}</li>
            <li>💬 {vi.talkAbout||'Talk about your favourite trip'}</li>
            <li>✅ {vi.getVerified||'Get a verified badge'}</li>
          </ul>
          {error && <p style={{color:'#e74c3c',fontSize:'0.85rem',marginBottom:'0.5rem'}}>{error}</p>}
          <button className="ts-btn-gold-full" style={{marginTop:'0.5rem'}} onClick={startCamera}>
            {vi.startCamera||'Start Camera'} 📷
          </button>
        </>}

        {(step === 1 || step === 2) && <>
          <h2 className="ts-serif ts-modal-title" style={{marginBottom:'0.5rem'}}>
            {step === 2 ? `⏺ ${vi.recording||'Recording'} ${mins}:${secs}` : `📷 ${vi.previewReady||'Ready to Record'}`}
          </h2>
          <video ref={videoPreviewRef} autoPlay muted playsInline
            style={{width:'100%',borderRadius:12,background:'#000',maxHeight:260,objectFit:'cover',display:'block'}} />
          <div style={{display:'flex',gap:'0.75rem',marginTop:'0.75rem'}}>
            {step === 1 && (
              <button className="ts-btn-gold-full" style={{flex:1}} onClick={startRecording}>
                ⏺ {vi.startRecording||'Start Recording'}
              </button>
            )}
            {step === 2 && (
              <button className="ts-btn-gold-full" style={{flex:1,background:'#c0392b'}} onClick={stopRecording}>
                ⏹ {vi.stopRecording||'Stop Recording'}
              </button>
            )}
          </div>
          {step === 2 && <p style={{color:'var(--muted)',fontSize:'0.8rem',textAlign:'center',marginTop:'0.5rem'}}>
            {vi.minMaxDuration||'Min. 15 sec · Max. 30 sec'}
          </p>}
        </>}

        {step === 3 && blobUrl && <>
          <h2 className="ts-serif ts-modal-title" style={{marginBottom:'0.5rem'}}>{vi.previewVideo||'Preview Video'}</h2>
          <video src={blobUrl} controls playsInline
            style={{width:'100%',borderRadius:12,background:'#000',maxHeight:260,objectFit:'cover',display:'block'}} />
          {error && <p style={{color:'#e74c3c',fontSize:'0.85rem',margin:'0.5rem 0'}}>{error}</p>}
          <p style={{color:'var(--muted)',fontSize:'0.8rem',textAlign:'center',marginTop:'0.5rem'}}>{duration}s</p>
          <div style={{display:'flex',gap:'0.75rem',marginTop:'0.5rem'}}>
            <button className="ts-btn-ghost-sm" style={{flex:1}} onClick={retake}>↩ {vi.retake||'Retake'}</button>
            <button className="ts-btn-gold-full" style={{flex:1}} onClick={uploadVideo}>↑ {vi.uploadVideo||'Upload Video'}</button>
          </div>
        </>}

        {step === 4 && (
          <div className="ts-verify-processing">
            <div className="ts-loading-dots"><span/><span/><span/></div>
            <p style={{color:'var(--muted)',textAlign:'center'}}>{vi.uploading||'Uploading...'}</p>
          </div>
        )}

        {step === 5 && (
          <div className="ts-spin-state">
            <div style={{fontSize:'4rem',textAlign:'center'}}>✅</div>
            <p className="ts-spin-won-title">{vi.videoSuccess||'Video uploaded!'}</p>
            <p style={{color:'var(--muted)',fontSize:'0.85rem',textAlign:'center',marginTop:'0.5rem'}}>{lang==='bg'?'Видеото е качено. Ръчната верификация се прави със селфи снимките в профила.':lang==='es'?'Video subido. La verificación manual usa las fotos selfie de tu perfil.':lang==='fr'?'Vidéo téléchargée. La vérification manuelle utilise les selfies de votre profil.':lang==='de'?'Video hochgeladen. Die manuelle Verifizierung nutzt die Selfie-Fotos in deinem Profil.':lang==='el'?'Το βίντεο ανέβηκε. Η χειροκίνητη επαλήθευση χρησιμοποιεί τις selfie φωτογραφίες του προφίλ σου.':lang==='it'?'Video caricato. La verifica manuale utilizza le foto selfie del tuo profilo.':'Video uploaded. Manual verification uses the selfie photos on your profile.'}</p>
            <button className="ts-btn-gold-full" style={{marginTop:'1rem'}} onClick={handleClose}>{vi.close||'Close'} ✕</button>
          </div>
        )}
      </div>
    </div>
  );
}

function VideoPlayerModal({ profile, t, onClose }) {
  const vi = t.videoIntro || {};
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:380}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h3 className="ts-serif" style={{color:'var(--gold)',marginBottom:'0.75rem',textAlign:'center',fontSize:'1.1rem'}}>
          {profile.name} — {vi.playIntro||'Intro Video'}
        </h3>
        <video src={profile.video_url} autoPlay controls playsInline
          style={{width:'100%',borderRadius:12,background:'#000',maxHeight:420,objectFit:'cover',display:'block'}} />
      </div>
    </div>
  );
}

// ── Real compatibility scoring ───────────────────────────────────────────────
// Computes 5 dimension scores from actual profile data shared between two
// users, instead of hardcoded demo numbers. Every score is derived and
// explainable; no field comparison is invented data about either person.
function jaccardOverlap(a, b) {
  const setA = new Set((Array.isArray(a) ? a : []).map(x => String(x).toLowerCase().trim()).filter(Boolean));
  const setB = new Set((Array.isArray(b) ? b : []).map(x => String(x).toLowerCase().trim()).filter(Boolean));
  if (setA.size === 0 || setB.size === 0) return null; // no signal — not 0% incompatible, just unknown
  let shared = 0;
  setA.forEach(v => { if (setB.has(v)) shared++; });
  const union = new Set([...setA, ...setB]).size;
  return union === 0 ? null : shared / union;
}

function calcRealCompatibility(me, other) {
  // `me` comes from a raw Supabase select (field names as in the DB: travel_dna,
  // dream_destinations). `other` may instead be the output of mapSupabaseProfile,
  // which renames travel_dna -> traits and dream_destinations -> dreams — read
  // both shapes so scoring works regardless of which one was passed in.
  const meTravelDna = me?.travel_dna || me?.traits || [];
  const otherTravelDna = other?.travel_dna || other?.traits || [];
  const meRawDreams = me?.dream_destinations || me?.dreams || [];
  const otherRawDreams = other?.dream_destinations || other?.dreams || [];

  const meBucket = (me?.bucket_list || []).map(b => b?.destination).filter(Boolean);
  const otherBucket = (other?.bucket_list || []).map(b => b?.destination).filter(Boolean);
  const meDreams = [...meBucket, ...meRawDreams];
  const otherDreams = [...otherBucket, ...otherRawDreams];

  // 1. Travel Style Match — overlap of travel_dna tags
  const styleOverlap = jaccardOverlap(meTravelDna, otherTravelDna);
  const travelStyleMatch = styleOverlap === null ? 60 : Math.round(40 + styleOverlap * 60);

  // 2. Personality Fit — zodiac match (classic compatibility pairing) + relationship_goal alignment
  const ZODIAC_COMPAT = { aries:['leo','sagittarius','gemini','aquarius'], taurus:['virgo','capricorn','cancer','pisces'], gemini:['libra','aquarius','aries','leo'], cancer:['scorpio','pisces','taurus','virgo'], leo:['aries','sagittarius','gemini','libra'], virgo:['taurus','capricorn','cancer','scorpio'], libra:['gemini','aquarius','leo','sagittarius'], scorpio:['cancer','pisces','virgo','capricorn'], sagittarius:['aries','leo','libra','aquarius'], capricorn:['taurus','virgo','scorpio','pisces'], aquarius:['gemini','libra','aries','sagittarius'], pisces:['cancer','scorpio','taurus','capricorn'] };
  const myZodiac = (me?.zodiac_sign || '').toLowerCase();
  const otherZodiac = (other?.zodiac_sign || '').toLowerCase();
  let personalityFit = 65; // baseline when data is missing
  if (myZodiac && otherZodiac) {
    personalityFit = myZodiac === otherZodiac ? 80 : (ZODIAC_COMPAT[myZodiac]||[]).includes(otherZodiac) ? 88 : 55;
  }
  if (me?.relationship_goal && other?.relationship_goal) {
    personalityFit = me.relationship_goal === other.relationship_goal
      ? Math.min(97, personalityFit + 10)
      : Math.max(40, personalityFit - 10);
  }

  // 3. Destination Dreams — overlap of bucket list + dream destinations
  const destOverlap = jaccardOverlap(meDreams, otherDreams);
  const destinationDreams = destOverlap === null ? 55 : Math.round(35 + destOverlap * 65);

  // 4. Communication — lifestyle compatibility proxy (smoking/alcohol/children stance alignment)
  let communicationPoints = 0, communicationFactors = 0;
  [['smoking','smoking'],['alcohol','alcohol'],['children','children']].forEach(([mk,ok]) => {
    if (me?.[mk] && other?.[ok]) {
      communicationFactors++;
      communicationPoints += me[mk] === other[ok] ? 1 : 0.4;
    }
  });
  const communication = communicationFactors > 0
    ? Math.round(45 + (communicationPoints / communicationFactors) * 50)
    : 65;

  // 5. Trip Timing — looking_for overlap (what each is on the app for)
  const lookingOverlap = jaccardOverlap(me?.looking_for, other?.looking_for);
  const tripTiming = lookingOverlap === null ? 60 : Math.round(40 + lookingOverlap * 60);

  const clamp = n => Math.max(30, Math.min(97, n));
  return {
    travelStyleMatch: clamp(travelStyleMatch),
    personalityFit: clamp(personalityFit),
    destinationDreams: clamp(destinationDreams),
    communication: clamp(communication),
    tripTiming: clamp(tripTiming),
  };
}

// Single overall % — same formula CompatibilityReportModal already uses
// (plain average of the 5 sub-scores), factored out so it can be computed
// for every profile card, not just inside the detailed report modal.
function overallCompatibility(me, other) {
  const scores = calcRealCompatibility(me, other);
  const vals = Object.values(scores);
  return Math.round(vals.reduce((a, s) => a + s, 0) / vals.length);
}

// Bug report: "Свали PDF" worked on desktop/web but silently did nothing in
// the native Android app. jsPDF's doc.save() creates a blob: URL and
// simulates a click on a hidden <a download> — Chrome/desktop handles that
// fine, but Android's WebView (what the Capacitor-bundled app actually runs)
// does not reliably trigger a real download for a blob-URL anchor click, so
// nothing visibly happened. On native platforms, write the PDF to the app's
// cache dir via Filesystem (base64) and hand it to the native Share sheet
// instead — the user can save it, send it via any app, etc. On web, keep
// using jsPDF's own .save() (an ordinary, working browser download there).
async function saveOrSharePdf(doc, filename) {
  if (!Capacitor.isNativePlatform()) {
    doc.save(filename);
    return;
  }
  try {
    const dataUri = doc.output('datauristring');
    const base64 = dataUri.split(',')[1];
    const write = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    await Share.share({
      title: filename,
      url: write.uri,
    });
  } catch (e) {
    console.error('[saveOrSharePdf] native save/share failed:', e);
    // Fall back to the browser path — won't work in WebView either, but
    // better than silently doing nothing if Filesystem/Share ever reject.
    try { doc.save(filename); } catch (e2) {}
  }
}

// ── Compatibility PDF generator ──────────────────────────────────────────────
// Lightweight, text/vector based (no html2canvas) — the report is simple
// enough not to need a DOM screenshot. Only the viewer's own data + the
// profile they are viewing is included, never a third party's.
async function generateCompatibilityPDF(sections, overall, myName, otherName, lang) {
  const { jsPDF } = await import('jspdf');
  const { ROBOTO_REGULAR_BASE64 } = await import('./assets/roboto-400-base64');
  const { ROBOTO_BOLD_BASE64 } = await import('./assets/roboto-700-base64');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  // jsPDF's built-in fonts (helvetica etc.) only cover Latin-1 — Cyrillic text
  // renders as garbage glyphs. Embed a Latin+Cyrillic Roboto subset instead,
  // so Bulgarian report text (and names) render correctly. Loaded dynamically
  // (not a top-level import) so the ~160KB of base64 font data only enters
  // the bundle when a PDF is actually generated.
  doc.addFileToVFS('Roboto-Regular.ttf', ROBOTO_REGULAR_BASE64);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  doc.addFileToVFS('Roboto-Bold.ttf', ROBOTO_BOLD_BASE64);
  doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');

  const pageW = doc.internal.pageSize.getWidth();
  let y = 70;

  doc.setFillColor(13, 17, 23);
  doc.rect(0, 0, pageW, doc.internal.pageSize.getHeight(), 'F');

  doc.setTextColor(232, 200, 112);
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(22);
  const title = lang === 'bg' ? `Съвместимост: ${myName} & ${otherName}` : lang === 'es' ? `Compatibilidad: ${myName} y ${otherName}` : lang === 'fr' ? `Compatibilité : ${myName} et ${otherName}` : lang === 'de' ? `Kompatibilität: ${myName} & ${otherName}` : lang === 'el' ? `Συμβατότητα: ${myName} & ${otherName}` : lang === 'it' ? `Compatibilità: ${myName} e ${otherName}` : `Compatibility: ${myName} & ${otherName}`;
  doc.text(title, pageW / 2, y, { align: 'center' });
  y += 50;

  doc.setFontSize(40);
  doc.text(`${overall}%`, pageW / 2, y, { align: 'center' });
  y += 50;

  doc.setFontSize(12);
  doc.setFont('Roboto', 'normal');
  sections.forEach(s => {
    doc.setTextColor(230, 230, 230);
    doc.text(s.label, 60, y);
    doc.setTextColor(232, 200, 112);
    doc.text(`${s.score}%`, pageW - 60, y, { align: 'right' });
    y += 14;
    doc.setDrawColor(60, 60, 60);
    doc.setLineWidth(6);
    doc.line(60, y, pageW - 60, y);
    doc.setDrawColor(232, 200, 112);
    doc.line(60, y, 60 + (pageW - 120) * (s.score / 100), y);
    y += 30;
  });

  y += 10;
  doc.setTextColor(232, 200, 112);
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(12);
  const footer = lang==='bg'?'Генерирано от TravelSoul':lang==='es'?'Generado por TravelSoul':lang==='fr'?'Généré par TravelSoul':lang==='de'?'Erstellt von TravelSoul':lang==='el'?'Δημιουργήθηκε από το TravelSoul':lang==='it'?'Creato da TravelSoul':'Generated by TravelSoul';
  doc.text(footer, pageW / 2, y, { align: 'center' });

  await saveOrSharePdf(doc, `TravelSoul-Compatibility-${otherName || 'report'}.pdf`);
}

const MY_COMPAT_PROFILE_SELECT = 'id,name,travel_dna,bucket_list,dream_destinations,zodiac_sign,smoking,alcohol,children,looking_for,relationship_goal';

async function fetchMyCompatibilityProfile() {
  const { userId } = getAuthToken();
  if (!userId) return null;
  try {
    const rows = await sbFetch('GET', 'profiles', `id=eq.${userId}&select=${MY_COMPAT_PROFILE_SELECT}&limit=1`);
    return Array.isArray(rows) && rows[0] ? rows[0] : null;
  } catch (e) {
    console.error('[fetchMyCompatibilityProfile] failed:', e);
    return null;
  }
}

function CompatibilityReportModal({ profile, myProfile, lang, onClose }) {
  const ft = FT[lang] || FT.en;
  const [pdfLoading, setPdfLoading] = useState(false);
  const scores = calcRealCompatibility(myProfile, profile);
  const sections = [
    { icon:'✈️', label: ft.travelStyleMatch||'Travel Style Match', score: scores.travelStyleMatch },
    { icon:'🎯', label: ft.personalityFit||'Personality Fit',      score: scores.personalityFit },
    { icon:'🌍', label: ft.destinationDreams||'Destination Dreams', score: scores.destinationDreams },
    { icon:'💬', label: ft.communication||'Communication',          score: scores.communication },
    { icon:'🕐', label: ft.tripTiming||'Trip Timing',               score: scores.tripTiming },
  ];
  const overall = Math.round(sections.reduce((a,s) => a + s.score, 0) / sections.length);
  return (
    <div className="ts-modal-overlay ts-report-overlay" style={{...OVERLAY_S, zIndex:99999, alignItems:'flex-end', padding:0}} onClick={onClose}>
      <div className="ts-modal ts-report-modal" style={{...MODAL_S, maxWidth:420, width:'100%', borderRadius:'20px 20px 0 0', maxHeight:'90vh'}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{ft.reportTitle} {profile.name}</h2>
        <div className="ts-report-ring-wrap"><div className="ts-report-ring"><span>{overall}%</span></div></div>
        {sections.map(s => (
          <div key={s.label} className="ts-report-row">
            <span className="ts-report-icon">{s.icon}</span>
            <div className="ts-report-row-body">
              <div className="ts-report-row-top"><span>{s.label}</span><span className="ts-report-score">{s.score}%</span></div>
              <div className="ts-report-bar-track"><div className="ts-report-bar-fill" style={{width:`${s.score}%`}}/></div>
            </div>
          </div>
        ))}
        <p style={{color:'var(--gold)',textAlign:'center',marginTop:'1rem',fontStyle:'italic'}}>💫 {ft.perfectCompanions||'You two would be perfect travel companions!'}</p>
        <button
          className="ts-btn-ghost-sm"
          style={{marginTop:'0.5rem'}}
          disabled={pdfLoading}
          onClick={async () => {
            setPdfLoading(true);
            try {
              const myName = myProfile?.name || (lang==='bg'?'Аз':lang==='es'?'Yo':lang==='fr'?'Moi':lang==='de'?'Ich':lang==='el'?'Εγώ':lang==='it'?'Io':'Me');
              await generateCompatibilityPDF(sections, overall, myName, profile.name, lang);
            } catch (e) {
              console.error('[CompatibilityReportModal] PDF generation failed:', e);
            } finally {
              setPdfLoading(false);
            }
          }}
        >
          {pdfLoading ? (lang==='bg'?'Генерира се...':lang==='es'?'Generando...':lang==='fr'?'Génération en cours...':lang==='de'?'Wird erstellt...':lang==='el'?'Δημιουργείται...':lang==='it'?'Creazione in corso...':'Generating...') : <>📄 {lang==='bg'?'Свали PDF':lang==='es'?'Descargar PDF':lang==='fr'?'Télécharger le PDF':lang==='de'?'PDF herunterladen':lang==='el'?'Λήψη PDF':lang==='it'?'Scarica PDF':'Download PDF'}</>}
        </button>
      </div>
    </div>
  );
}

// Backend API base URL — all secret-bearing calls go here, never direct to 3rd parties
const API_BASE = process.env.REACT_APP_API_URL || '';

async function sendPush(recipientId, title, body, url = '/') {
  if (!recipientId || !API_BASE) return;
  try {
    const { accessToken } = await getAuthTokenSafe();
    if (!accessToken) return;
    await fetch(`${API_BASE}/api/push/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ recipientId, title: title.slice(0, 100), body: (body || '').slice(0, 200), url }),
    });
  } catch {}
}

// ── Soul Match AI Modal ───────────────────────────────────────────────────────
function SoulMatchModal({ profile, plan, lang, onClose, onNeedPremium }) {
  const [explanation, setExplanation] = useState('');
  const [loading, setLoading] = useState(plan === 'elite');

  useEffect(() => {
    if (plan !== 'elite') return;
    (async () => {
      try {
        const lsUser  = JSON.parse(localStorage.getItem('travelsoul_user')    || '{}');
        const lsProf  = JSON.parse(localStorage.getItem('travelsoul_profile') || '{}');
        // Audit finding: this prompt previously sent the user's full free-text
        // bio to a third-party AI API (OpenAI) unfiltered and untruncated. A
        // bio is open-ended user-authored text — it can contain far more than
        // "about me" (other people's names, locations, health mentions,
        // anything the person happened to type), none of which this feature
        // needs: the compatibility blurb only needs structured preference
        // tags (traits/dreams/looking-for), not prose. Bio is no longer sent.
        const myName   = lsUser.name || lsUser.full_name || 'You';
        const myTraits = (lsProf.travel_dna || []).join(', ') || 'traveler';
        const myDreams = (lsProf.dream_destinations || []).join(', ') || '';
        const myLooking = (lsProf.looking_for || []).join(', ') || '';
        const theirTraits = (profile.traits || []).join(', ') || 'traveler';
        const theirDreams = (profile.dreams || []).join(', ') || '';
        const systemPrompt = `You are TravelSoul's Soul Match AI. Write exactly 3 short, warm, specific sentences explaining why these two travelers are compatible. Write in ${responseLanguageName(lang)}. Address the current user as "you/ти" and use the match's first name. Be personal and travel-focused.`;
        const userPrompt = `Me: ${myName}, traits: ${myTraits}${myDreams?', dreams: '+myDreams:''}${myLooking?', looking for: '+myLooking:''}\nMatch: ${profile.name}${profile.age?', age '+profile.age:''}, traits: ${theirTraits}${theirDreams?', dreams: '+theirDreams:''}, compatibility: ${profile.compat}%\nWhy are we a soul match?`;
        const res = await fetch(`${API_BASE}/api/openai/plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
          body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role:'system', content:systemPrompt }, { role:'user', content:userPrompt }], max_tokens: 200 }),
        });
        const data = await res.json();
        setExplanation(data.choices?.[0]?.message?.content || (lang==='bg'?'Не може да се зареди обяснение.':lang==='es'?'No se pudo cargar la explicación.':lang==='fr'?'Impossible de charger l\'explication.':lang==='de'?'Erklärung konnte nicht geladen werden.':lang==='el'?'Η εξήγηση δεν μπόρεσε να φορτωθεί.':lang==='it'?'Impossibile caricare la spiegazione.':'Could not load explanation.'));
      } catch {
        setExplanation(lang==='bg'?'Грешка при зареждане. Опитай отново.':lang==='es'?'Error al cargar. Inténtalo de nuevo.':lang==='fr'?'Erreur de chargement. Veuillez réessayer.':lang==='de'?'Fehler beim Laden. Bitte versuche es erneut.':lang==='el'?'Σφάλμα φόρτωσης. Δοκίμασε ξανά.':lang==='it'?'Errore di caricamento. Riprova.':'Error loading. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const title = lang==='bg'?'Soul Match AI':lang==='es'?'Soul Match IA':lang==='fr'?'Soul Match IA':lang==='de'?'Soul Match KI':lang==='el'?'AI Soul Match':lang==='it'?'IA Soul Match':'Soul Match AI';
  const subtitle = lang==='bg'?`Ти и ${profile.name} • ${profile.compat}% съвместимост`:lang==='es'?`Tú y ${profile.name} • ${profile.compat}% compatibilidad`:lang==='fr'?`Vous et ${profile.name} • ${profile.compat}% compatibilité`:lang==='de'?`Du und ${profile.name} • ${profile.compat}% Kompatibilität`:lang==='el'?`Εσύ και ${profile.name} • ${profile.compat}% συμβατότητα`:lang==='it'?`Tu e ${profile.name} • ${profile.compat}% compatibilità`:`You & ${profile.name} • ${profile.compat}% compatibility`;

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-soul-match-modal" onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <div style={{textAlign:'center',marginBottom:14}}>
          <div style={{fontSize:'2rem',marginBottom:6}}>🔮</div>
          <h3 className="ts-serif" style={{color:'var(--gold)',margin:0,fontSize:'1.1rem'}}>{title}</h3>
          <p style={{color:'var(--muted)',fontSize:'0.78rem',margin:'4px 0 0'}}>{subtitle}</p>
        </div>
        {plan !== 'elite' ? (
          <div style={{textAlign:'center',padding:'0.75rem 0'}}>
            <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:'1rem'}}>
              {lang==='bg'?'Само за Elite — разбери защо сте Soul Match':lang==='es'?'Solo Elite: descubre por qué sois Soul Match':lang==='fr'?'Réservé à Elite — découvrez pourquoi vous êtes Soul Match':lang==='de'?'Nur Elite — entdecke, warum ihr ein Soul Match seid':lang==='el'?'Μόνο για Elite — ανακάλυψε γιατί είστε Soul Match':lang==='it'?'Solo Elite — scopri perché siete un Soul Match':'Elite only — discover why you\'re a Soul Match'}
            </p>
            <button className="ts-btn-gold-full" onClick={()=>{ onClose(); onNeedPremium&&onNeedPremium('soulMatch'); }}>
              💎 {lang==='bg'?'Надгради до Elite':lang==='es'?'Mejora a Elite':lang==='fr'?'Passer à Elite':lang==='de'?'Auf Elite upgraden':lang==='el'?'Αναβάθμιση σε Elite':lang==='it'?'Passa a Elite':'Upgrade to Elite'}
            </button>
          </div>
        ) : loading ? (
          <div style={{textAlign:'center',padding:'1.5rem 0',color:'var(--muted)',fontSize:'0.85rem'}}>
            <div className="ts-soul-match-spinner"/>
            {lang==='bg'?'AI анализира съвместимостта...':lang==='es'?'La IA está analizando vuestra compatibilidad...':lang==='fr'?'L\'IA analyse votre compatibilité...':lang==='de'?'Die KI analysiert eure Kompatibilität...':lang==='el'?'Το AI αναλύει τη συμβατότητά σας...':lang==='it'?'L\'IA sta analizzando la vostra compatibilità...':'AI is analyzing your compatibility...'}
          </div>
        ) : (
          <div style={{background:'rgba(255,215,0,0.05)',border:'1px solid rgba(255,215,0,0.18)',borderRadius:12,padding:'1rem',lineHeight:1.65,color:'var(--text)',fontSize:'0.88rem',whiteSpace:'pre-wrap'}}>
            {explanation}
          </div>
        )}
      </div>
    </div>
  );
}

function matchCategory(attrCat, filter) {
  if (!filter || filter === 'all') return true;
  const cat = (attrCat || '').toLowerCase();
  const maps = {
    nature:  ['nature','природ','natural','natur','natür','плаж','море','планин','парк','forest','beach','mountain'],
    culture: ['cultur','култур','histor','museum','музей','храм','church','palace','дворец','monument','архитект'],
    food:    ['food','храна','gastro','restaurant','ресторант','кухн','cuisine','market','пазар','wine','вино','tapas','бира','beer','café','кафе','bakery','пекарна','street food','улична храна','морски','seafood','риба'],
    hidden:  ['hidden','скрит','gem','secret','тайн','local','locals','малко','непознат','off the beaten','insider'],
  };
  return maps[filter]?.some(keyword => cat.includes(keyword)) ?? true;
}

const geocode = async (city) => {
  const { accessToken } = await getAuthTokenSafe();
  if (!accessToken) return null;
  const res = await fetch(`${API_BASE}/api/ors/geocode?text=${encodeURIComponent(city)}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  const data = await res.json();
  return data.features?.[0]?.geometry?.coordinates;
};

const getRouteDistance = async (fromCity, toCity, transport) => {
  try {
    const from = await geocode(fromCity);
    const to = await geocode(toCity);
    if (!from || !to) return null;
    const profile = transport === 'car' ? 'driving-car'
                  : transport === 'train' || transport === 'bus' ? 'driving-car'
                  : null;
    if (!profile) return null;
    const { accessToken } = await getAuthTokenSafe();
    if (!accessToken) return null;
    const res = await fetch(`${API_BASE}/api/ors/directions?profile=${profile}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: JSON.stringify({ coordinates: [from, to] }),
    });
    const data = await res.json();
    const summary = data.routes?.[0]?.summary;
    if (!summary) return null;
    return {
      distanceKm: Math.round(summary.distance / 1000),
      durationHours: Math.round(summary.duration / 3600 * 10) / 10,
    };
  } catch(e) {
    return null;
  }
};

function AiTripPlannerModal({ lang, onClose }) {
  const ft = FT[lang] || FT.en;
  const [form, setForm] = useState({ dest:'', budget:2000, days:7, styles:[], group:2, start_date:'', end_date:'', sd_day:'', sd_mon:'', sd_year:'', from_city:'', transport:'plane' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [saved, setSaved] = useState(false);
  const [attractionFilter, setAttractionFilter] = useState('all');
  const [usedORS, setUsedORS] = useState(false);

  function toggleStyle(s) { setForm(f => ({...f, styles: f.styles.includes(s) ? f.styles.filter(x=>x!==s) : [...f.styles,s]})); }

  useEffect(() => {
    if (form.start_date && form.days) {
      const end = new Date(form.start_date);
      end.setDate(end.getDate() + form.days);
      setForm(f => ({ ...f, end_date: end.toISOString().split('T')[0] }));
    }
  }, [form.start_date, form.days]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Run in Supabase SQL editor to create trips table:
  // CREATE TABLE IF NOT EXISTS trips (
  //   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  //   user_id uuid REFERENCES profiles(id),
  //   title text, destination text, start_date date, end_date date,
  //   days int, budget numeric, group_size int, styles text[],
  //   plan jsonb, spots int, status text DEFAULT 'upcoming',
  //   created_at timestamptz DEFAULT now()
  // );
  // ALTER TABLE trips ENABLE ROW LEVEL SECURITY;
  // CREATE POLICY "trips_public_read" ON trips FOR SELECT USING (true);
  // CREATE POLICY "trips_own_insert" ON trips FOR INSERT WITH CHECK (auth.uid() = user_id);
  async function saveTrip() {
    if (!result || result.error) return;
    setSaved(true);
    const { userId } = getAuthToken();
    const displayDate = form.start_date || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString(LOCALE_MAP[lang]||'en-GB', { day:'numeric', month:'short', year:'numeric' });
    if (supabase && userId) {
      const { error } = await supabase.from('trips').insert({
        user_id: userId,
        title: result.title || form.dest,
        destination: form.dest,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        days: form.days,
        budget: form.budget,
        group_size: form.group,
        styles: form.styles,
        plan: result,
        spots: Math.max(0, form.group - 1),
        status: 'upcoming',
      });
      if (error) console.error('Trip Supabase save error:', error);
    }
    const entry = {
      id: Date.now(),
      name: result.title || form.dest,
      location: form.dest,
      image: 'https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=400&q=80',
      date: displayDate,
      members: form.group,
      aiPlan: result,
      savedAt: new Date().toISOString(),
      status: 'planned',
    };
    const existing = JSON.parse(localStorage.getItem('travelsoul_saved_trips') || '[]');
    localStorage.setItem('travelsoul_saved_trips', JSON.stringify([entry, ...existing]));
    setTimeout(() => { setSaved(false); onClose(); }, 1200);
  }

  const generate = async () => {
    if (!form.dest) return;
    setLoading(true);
    setResult(null);
    setUsedORS(false);
    let orsData = null;
    if (form.transport !== 'plane' && form.from_city) {
      orsData = await getRouteDistance(form.from_city, form.dest, form.transport);
      if (orsData) setUsedORS(true);
    }
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          max_tokens: 4000,
          messages: [{
            role: 'system',
            content: 'You are a travel planner. Always respond with valid JSON only. No markdown, no backticks.',
          }, {
            role: 'user',
            content: (()=>{
              const responseLang = responseLanguageName(lang);
              const promptDays = form.days <= 14
                ? `Return exactly ${form.days} days, each with day/theme/morning/afternoon/evening/cost.`
                : `Return exactly ${form.days} days. Days 1-7: include day/theme/morning/afternoon/evening/cost. Days 8-${form.days}: include ONLY day/theme/cost (no morning/afternoon/evening, keep theme under 8 words).`;
              const orsHint = orsData ? ` Real distance data: ${orsData.distanceKm}km, ${orsData.durationHours} hours by ${form.transport}. Use these EXACT values for distanceKm and travelTimeHours in the travel object.` : '';
              return `Create a ${form.days}-day travel plan for "${form.dest}". Budget: €${form.budget} total for ${form.group} people. Travel style: ${form.styles.length ? form.styles.join(', ') : 'mixed'}. Response language: ${responseLang}. ${promptDays} Also return top 8 must-visit attractions for this destination. Write ALL attraction names, descriptions, tips and travel section in the response language (${responseLang}). IMPORTANT: In the attractions array, always use English for the category field: nature, culture, food, or hidden. Also return a travel section with estimated data from "${form.from_city||'your location'}" to "${form.dest}" by ${form.transport||'plane'}.${orsHint} Return this exact JSON: {"title":"trip title","highlight":"one sentence","days":[{"day":1,"theme":"Arrival & old town","morning":"activity","afternoon":"activity","evening":"activity","cost":80},{"day":8,"theme":"Beach day","cost":60}],"budget":{"flights":0,"hotel":0,"food":0,"activities":0},"tips":["tip1","tip2","tip3"],"travel":{"from":"${form.from_city||'your location'}","to":"${form.dest}","transport":"${form.transport||'plane'}","distanceKm":0,"travelTimeHours":0,"totalSightseeingHours":0,"transportTips":"one practical tip"},"visas":{"required":false,"type":"none","where":"","processingDays":0,"cost":0},"vaccines":{"required":[],"recommended":[],"healthTips":"one health tip"},"attractions":[{"name":"attraction name","category":"nature","description":"2 sentence description.","price":"free","bestTime":"morning","visitHours":2,"officialSite":"https://real-official-site.com","mapLink":"https://www.google.com/maps/search/attraction+name+${encodeURIComponent(form.dest)}","wikiLink":"https://en.wikipedia.org/wiki/Attraction_Name"}]}. For each attraction, 'officialSite' must be the REAL official website for tickets or visitor information (e.g. Bran Castle→https://www.bran-castle.com, Eiffel Tower→https://www.toureiffel.paris, Colosseum→https://www.coopculture.it, Acropolis→https://hhticket.gr, Sagrada Familia→https://www.sagradafamilia.org, Grand Palace Bangkok→https://www.royalgrandpalace.th). Always use the official ticket/visitor site, NOT Wikipedia or tourist blogs. If no official site exists, use empty string. Base visa and vaccine info on traveler origin: ${form.from_city||'Europe'}.`;
            })(),
          }],
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      try {
        const raw = data.choices[0].message.content;
        const plan = JSON.parse(raw);
        setResult(plan);
      } catch(e) {
        console.error('Parse error, raw was:', data.choices[0]?.message?.content);
        setResult({ error: true, message: ft.aiError||'AI response was cut off. Try a shorter destination or fewer days.' });
      }
    } catch(e) {
      console.error('AI Planner error:', e);
      setResult({ error: true, message: e.message });
    }
    setLoading(false);
  };

  const budgetColors = { flights:'#c9a84c', hotel:'#e07b39', food:'#1abc9c', activities:'#8e44ad' };

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-planner-modal" style={{...MODAL_S, maxWidth:480, maxHeight:'88vh', overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{ft.aiPlannerBtn}</h2>

        {/* ── Form ── */}
        {!loading && !result && (
          <div className="ts-planner-form">
            <label className="ts-filter-label">{ft.destination}
              <input className="ts-input" value={form.dest} onChange={e=>setForm(f=>({...f,dest:e.target.value}))} placeholder={ft.destPlaceholder||'Tokyo, Bali, Paris...'} />
            </label>
            <label className="ts-filter-label">{ft.budget}: €{form.budget}
              <input type="range" className="ts-range" min="500" max="5000" step="100" value={form.budget} onChange={e=>setForm(f=>({...f,budget:+e.target.value}))} />
            </label>
            <label className="ts-filter-label">{ft.duration}</label>
            <div className="ts-duration-btns">
              {[3,7,14,30].map(d=><button key={d} className={`ts-distance-btn ${form.days===d?'active':''}`} onClick={()=>setForm(f=>({...f,days:d}))}>{d} {ft.days||'days'}</button>)}
            </div>
            <div style={{display:'flex',gap:'0.5rem',marginTop:'0.25rem',alignItems:'flex-end'}}>
              <label className="ts-filter-label" style={{flex:1}}>{ft.fromDate}
                <div style={{display:'flex',gap:4,marginTop:4}}>
                  <select value={form.sd_day||''} onChange={e=>setForm(f=>{const d=e.target.value,m=f.sd_mon||'',y=f.sd_year||'';const full=d&&m&&y?`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`:'';return{...f,sd_day:d,start_date:full};})} style={{background:'#161b22',border:'1px solid #30363d',color:'#f0f6fc',borderRadius:8,padding:'8px 4px',flex:1,outline:'none',cursor:'pointer'}}>
                    <option value="">DD</option>
                    {Array.from({length:31},(_,i)=><option key={i+1} value={String(i+1)}>{i+1}</option>)}
                  </select>
                  <select value={form.sd_mon||''} onChange={e=>setForm(f=>{const m=e.target.value,d=f.sd_day||'',y=f.sd_year||'';const full=d&&m&&y?`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`:'';return{...f,sd_mon:m,start_date:full};})} style={{background:'#161b22',border:'1px solid #30363d',color:'#f0f6fc',borderRadius:8,padding:'8px 4px',flex:1,outline:'none',cursor:'pointer'}}>
                    <option value="">MM</option>
                    {(MONTH_NAMES[lang]||MONTH_NAMES.en).map((mn,i)=><option key={i} value={String(i+1).padStart(2,'0')}>{mn}</option>)}
                  </select>
                  <select value={form.sd_year||''} onChange={e=>setForm(f=>{const y=e.target.value,d=f.sd_day||'',m=f.sd_mon||'';const full=d&&m&&y?`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`:'';return{...f,sd_year:y,start_date:full};})} style={{background:'#161b22',border:'1px solid #30363d',color:'#f0f6fc',borderRadius:8,padding:'8px 4px',flex:1,outline:'none',cursor:'pointer'}}>
                    <option value="">YYYY</option>
                    {['2025','2026','2027','2028'].map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </label>
              <div style={{flex:1}}>
                <p className="ts-filter-label" style={{marginBottom:4}}>{ft.toDate}</p>
                <div style={{background:'#0d1117',border:'1px solid #21262d',borderRadius:10,padding:'9px 10px',fontSize:13,color:form.end_date?'var(--gold)':'#8b949e',minHeight:38}}>
                  {form.end_date ? `📅 ${new Date(form.end_date).toLocaleDateString(localeForLang(lang),{day:'numeric',month:'short',year:'numeric'})}` : '—'}
                </div>
              </div>
            </div>
            {form.start_date && form.end_date && <p style={{fontSize:'0.75rem',color:'var(--gold)',marginTop:'0.25rem'}}>📅 {form.days} {ft.days||'days'}</p>}
            <label className="ts-filter-label">{ft.travelStyle}</label>
            <div className="ts-dna-checks">
              {STYLE_OPTS.map(s=><label key={s} className={`ts-dna-check ${form.styles.includes(s)?'active':''}`}><input type="checkbox" hidden checked={form.styles.includes(s)} onChange={()=>toggleStyle(s)}/>{(ft.styleLabels&&ft.styleLabels[s])||s}</label>)}
            </div>
            <label className="ts-filter-label">{ft.groupSize}: {form.group}
              <input type="range" className="ts-range" min="1" max="10" value={form.group} onChange={e=>setForm(f=>({...f,group:+e.target.value}))} />
            </label>
            <label className="ts-filter-label" style={{marginTop:'0.75rem'}}>{ft.travelCalc||'🧭 Calculator'}
              <div style={{display:'flex',gap:8,marginTop:4}}>
                <input className="ts-input" style={{flex:1}} value={form.from_city} onChange={e=>setForm(f=>({...f,from_city:e.target.value}))} placeholder={ft.fromCity||'From city'} />
                <select value={form.transport} onChange={e=>setForm(f=>({...f,transport:e.target.value}))} style={{background:'#161b22',border:'1px solid #30363d',color:'#f0f6fc',borderRadius:8,padding:'8px 6px',minWidth:110,outline:'none',cursor:'pointer'}}>
                  <option value="plane">{ft.plane||'✈️ Plane'}</option>
                  <option value="car">{ft.car||'🚗 Car'}</option>
                  <option value="train">{ft.train||'🚂 Train'}</option>
                  <option value="bus">{ft.bus||'🚌 Bus'}</option>
                </select>
              </div>
            </label>
            <button className="ts-btn-gold-full" style={{marginTop:'1rem'}} onClick={generate} disabled={!form.dest}>{ft.generateTrip}</button>
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div className="ts-verify-processing">
            <div className="ts-loading-dots"><span/><span/><span/></div>
            <p style={{color:'var(--muted)',textAlign:'center',marginTop:'0.75rem'}}>{ft.generating}</p>
          </div>
        )}

        {/* ── Error ── */}
        {result?.error && (
          <div style={{textAlign:'center',padding:'1rem'}}>
            <p style={{color:'#f85149',marginBottom:'1rem'}}>⚠️ {result.message}</p>
            <button className="ts-btn-ghost-sm" onClick={()=>setResult(null)}>← Back</button>
          </div>
        )}

        {/* ── Result ── */}
        {result && !result.error && (
          <div className="ts-planner-result">
            {/* Title + highlight */}
            <div style={{marginBottom:'1rem'}}>
              <h3 className="ts-serif" style={{color:'var(--gold)',fontSize:'1.1rem',marginBottom:'0.35rem'}}>✈️ {result.title}</h3>
              <p style={{color:'#8b949e',fontSize:'0.85rem',lineHeight:1.5}}>{localizeKnownTripContent(result.highlight, lang)}</p>
              {result.bestTime && <span style={{display:'inline-block',marginTop:'0.4rem',background:'rgba(201,168,76,0.12)',border:'1px solid var(--gold)',borderRadius:20,padding:'2px 10px',fontSize:'0.75rem',color:'var(--gold)'}}>📅 {result.bestTime}</span>}
            </div>

            {/* Travel calculator card */}
            {result.travel && (
              <div style={{background:'#0d1117',border:'1px solid #c9a84c44',borderRadius:12,padding:'12px 14px',marginBottom:'1rem'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.6rem'}}>
                  <p style={{color:'var(--gold)',fontSize:'0.8rem',fontWeight:700,margin:0}}>{ft.travelCalc||'🧭 Calculator'}</p>
                  <span style={{fontSize:'0.68rem',fontWeight:700,padding:'2px 8px',borderRadius:20,background:usedORS?'rgba(63,185,80,0.15)':'rgba(201,168,76,0.12)',color:usedORS?'#3fb950':'#c9a84c',border:`1px solid ${usedORS?'#3fb95044':'#c9a84c44'}`}}>
                    {usedORS ? (ft.realData||'📡 Real data') : (ft.aiEstimate||'🤖 AI estimate')}
                  </span>
                </div>
                <p style={{color:'#8b949e',fontSize:'0.78rem',marginBottom:'0.6rem'}}>
                  {result.travel.from} → {result.travel.to} &nbsp;
                  <span style={{color:'var(--gold)'}}>{result.travel.transport==='plane'?'✈️':result.travel.transport==='car'?'🚗':result.travel.transport==='train'?'🚂':'🚌'}</span>
                </p>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',marginBottom:'0.6rem'}}>
                  <div style={{background:'#161b22',borderRadius:8,padding:'8px 10px'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:2}}>{ft.distanceKm||'Distance'}</p>
                    <p style={{color:'var(--gold)',fontSize:'0.9rem',fontWeight:700}}>{result.travel.distanceKm} {ft.km||'km'}</p>
                  </div>
                  <div style={{background:'#161b22',borderRadius:8,padding:'8px 10px'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:2}}>{ft.travelTime||'Travel time'}</p>
                    <p style={{color:'var(--gold)',fontSize:'0.9rem',fontWeight:700}}>{result.travel.travelTimeHours} {ft.hours||'h.'}</p>
                  </div>
                  <div style={{background:'#161b22',borderRadius:8,padding:'8px 10px'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:2}}>{ft.sightseeingTime||'Sightseeing'}</p>
                    <p style={{color:'var(--gold)',fontSize:'0.9rem',fontWeight:700}}>{result.travel.totalSightseeingHours} {ft.hours||'h.'}</p>
                  </div>
                  <div style={{background:'#161b22',borderRadius:8,padding:'8px 10px'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:2}}>{ft.transportTips||'Tip'}</p>
                    <p style={{color:'#f0f6fc',fontSize:'0.72rem',lineHeight:1.3}}>{result.travel.transportTips}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Visa card */}
            {result.visas && (
              <div style={{background:'#0d1117',border:`1px solid ${result.visas.required?'#f8514944':'#3fb95044'}`,borderRadius:12,padding:'12px 14px',marginBottom:'1rem'}}>
                <p style={{color:'var(--gold)',fontSize:'0.8rem',fontWeight:700,marginBottom:'0.6rem'}}>{ft.visaInfo||'🛂 Visa Information'}</p>
                <span style={{display:'inline-block',marginBottom:'0.6rem',padding:'3px 12px',borderRadius:20,fontSize:'0.78rem',fontWeight:700,background:result.visas.required?'rgba(248,81,73,0.15)':'rgba(63,185,80,0.15)',color:result.visas.required?'#f85149':'#3fb950',border:`1px solid ${result.visas.required?'#f8514944':'#3fb95044'}`}}>{result.visas.required?`⚠️ ${ft.visaRequired||'Visa required'}`:`✅ ${ft.visaNotRequired||'No visa required'}`}</span>
                {result.visas.required && (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.4rem',marginTop:'0.4rem'}}>
                    {result.visas.type && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaType||'Visa type'}</p><p style={{color:'#f0f6fc',fontSize:'0.78rem'}}>{result.visas.type}</p></div>}
                    {result.visas.processingDays > 0 && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaDays||'Processing'}</p><p style={{color:'var(--gold)',fontSize:'0.78rem'}}>{result.visas.processingDays} {ft.days||'days'}</p></div>}
                    {result.visas.cost > 0 && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaCost||'Cost'}</p><p style={{color:'var(--gold)',fontSize:'0.78rem'}}>€{result.visas.cost}</p></div>}
                    {result.visas.where && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px',gridColumn:'1/-1'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaWhere||'Where to apply'}</p>{result.visas.where.startsWith('http')?<a href={result.visas.where} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(result.visas.where, e)} style={{color:'#79c0ff',fontSize:'0.75rem'}}>{result.visas.where}</a>:<p style={{color:'#f0f6fc',fontSize:'0.75rem'}}>{result.visas.where}</p>}</div>}
                  </div>
                )}
              </div>
            )}

            {/* Vaccine card */}
            {result.vaccines && (
              <div style={{background:'#0d1117',border:'1px solid #1abc9c44',borderRadius:12,padding:'12px 14px',marginBottom:'1rem'}}>
                <p style={{color:'var(--gold)',fontSize:'0.8rem',fontWeight:700,marginBottom:'0.6rem'}}>{ft.vaccines||'💉 Vaccines'}</p>
                {result.vaccines.required?.length > 0 && (
                  <div style={{marginBottom:'0.5rem'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:'0.3rem'}}>{ft.vaccinesRequired||'Required'}</p>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>{result.vaccines.required.map((v,i)=><span key={i} style={{fontSize:'0.72rem',padding:'2px 9px',borderRadius:12,background:'rgba(248,81,73,0.15)',color:'#f85149',border:'1px solid #f8514944'}}>{v}</span>)}</div>
                  </div>
                )}
                {result.vaccines.recommended?.length > 0 && (
                  <div style={{marginBottom:'0.5rem'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:'0.3rem'}}>{ft.vaccinesRecommended||'Recommended'}</p>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>{result.vaccines.recommended.map((v,i)=><span key={i} style={{fontSize:'0.72rem',padding:'2px 9px',borderRadius:12,background:'rgba(201,168,76,0.12)',color:'var(--gold)',border:'1px solid #c9a84c44'}}>{v}</span>)}</div>
                  </div>
                )}
                {result.vaccines.healthTips && <p style={{color:'#8b949e',fontSize:'0.75rem',fontStyle:'italic',marginTop:'0.35rem'}}>💡 {result.vaccines.healthTips}</p>}
              </div>
            )}

            {/* Day cards — detailed (days 1-7) or summary (days 8+) */}
            {(result.days||[]).map(d => {
              const isDetailed = !!d.morning;
              if (!isDetailed) {
                return (
                  <div key={d.day} style={{background:'#161b22',borderRadius:10,marginBottom:'0.5rem',border:'1px solid #21262d',padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:'0.88rem',color:'#f0f6fc'}}><strong style={{color:'var(--gold)'}}>{ft.day||'Day'} {d.day}</strong> — {localizeKnownTripContent(d.theme||d.summary, lang)}</span>
                    <span style={{color:'var(--muted)',fontSize:'0.75rem',flexShrink:0,marginLeft:8}}>{d.cost ? `€${d.cost}` : ''}</span>
                  </div>
                );
              }
              return (
                <div key={d.day} style={{background:'#161b22',borderRadius:10,marginBottom:'0.5rem',overflow:'hidden',border:'1px solid #21262d'}}>
                  <button style={{width:'100%',background:'none',border:'none',color:'#f0f6fc',padding:'10px 14px',textAlign:'left',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'0.9rem'}}
                    onClick={()=>setExpandedDay(expandedDay===d.day?null:d.day)}>
                    <span><strong style={{color:'var(--gold)'}}>{ft.day||'Day'} {d.day}</strong> — {localizeKnownTripContent(d.theme, lang)}</span>
                    <span style={{color:'var(--muted)',fontSize:'0.75rem'}}>{expandedDay===d.day?'▲':'▼'} {d.cost ? `€${d.cost}` : ''}</span>
                  </button>
                  {expandedDay===d.day && (
                    <div style={{padding:'0 14px 12px',borderTop:'1px solid #21262d'}}>
                      <div style={{color:'#8b949e',fontSize:'0.82rem',marginTop:'0.5rem'}}>🌅 <strong>{ft.morning||'Morning'}:</strong> {localizeKnownTripContent(d.morning, lang)}</div>
                      <div style={{color:'#8b949e',fontSize:'0.82rem',marginTop:'0.35rem'}}>🏛️ <strong>{ft.afternoon||'Afternoon'}:</strong> {localizeKnownTripContent(d.afternoon, lang)}</div>
                      <div style={{color:'#8b949e',fontSize:'0.82rem',marginTop:'0.35rem'}}>🍽️ <strong>{ft.evening||'Evening'}:</strong> {localizeKnownTripContent(d.evening, lang)}</div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Budget bars */}
            {result.budget && (() => {
              const BUDGET_KEYS = ['flights','hotel','food','activities'];
              const BUDGET_LABELS = { flights:ft.flights||'Flights', hotel:ft.hotel||'Hotel', food:ft.food||'Food', activities:ft.activities||'Activities' };
              const bTotal = BUDGET_KEYS.reduce((s,k)=>s+(+result.budget[k]||0),0);
              if (bTotal === 0) return null;
              return (
                <div style={{marginTop:'1rem'}}>
                  <p style={{color:'var(--muted)',fontSize:'0.78rem',marginBottom:'0.5rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>{ft.budgetBreakdown||'BUDGET BREAKDOWN'}</p>
                  {BUDGET_KEYS.map(k => {
                    const v = +result.budget[k] || 0;
                    const pct = bTotal > 0 ? Math.round((v/bTotal)*100) : 0;
                    return (
                      <div key={k} style={{marginBottom:'0.45rem'}}>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:'0.78rem',marginBottom:3}}>
                          <span style={{color:'#8b949e'}}>{BUDGET_LABELS[k]}</span>
                          <span style={{color:budgetColors[k]||'var(--gold)'}}>€{v}</span>
                        </div>
                        <div style={{height:5,borderRadius:3,background:'#21262d',overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${pct}%`,background:budgetColors[k]||'var(--gold)',borderRadius:3,transition:'width 0.6s'}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Tips */}
            {result.tips?.length > 0 && (
              <div style={{marginTop:'1rem',background:'rgba(201,168,76,0.06)',border:'1px solid #c9a84c33',borderRadius:10,padding:'12px 14px'}}>
                <p style={{color:'var(--gold)',fontSize:'0.78rem',fontWeight:700,marginBottom:'0.5rem'}}>{ft.localTips||'💡 Local Tips'}</p>
                {result.tips.map((tip,i)=><p key={i} style={{color:'#8b949e',fontSize:'0.82rem',marginBottom:'0.3rem'}}>• {localizeKnownTripContent(tip, lang)}</p>)}
              </div>
            )}

            {/* Attractions */}
            {result.attractions?.length > 0 && (()=>{
              const catIcon = {nature:'🏔️',culture:'🏛️',food:'🍜',nightlife:'🌙',hidden:'💎'};
              const displayBestTime = (time) => {
                if (!time) return '';
                const t = time.toLowerCase();
                if (t.includes('morning') || t.includes('сутрин')) return ft.morning||time;
                if (t.includes('afternoon') || t.includes('следобед')) return ft.afternoon||time;
                if (t.includes('evening') || t.includes('вечер')) return ft.evening||time;
                return localizeKnownTripContent(time, lang);
              };
              const displayPrice = (price) => {
                if (!price) return '';
                if (price.toLowerCase() === 'free' || price === 'Безплатно') return ft.free||price;
                return localizeKnownTripContent(price, lang);
              };
              const cats = [
                {key:'all',label:ft.allCat||'All'},
                {key:'nature',label:ft.natureCat||'🏔️ Nature'},
                {key:'culture',label:ft.cultureCat||'🏛️ Culture'},
                {key:'food',label:ft.foodCat||'🍜 Food'},
                {key:'hidden',label:ft.hiddenCat||'💎 Hidden Gems'},
              ];
              const filtered = result.attractions.filter(a=>matchCategory(a.category, attractionFilter));
              return (
                <div style={{marginTop:'1.25rem'}}>
                  <p style={{color:'var(--gold)',fontSize:'0.85rem',fontWeight:700,marginBottom:'0.6rem'}}>{ft.topPlaces||'🗺️ Top Places'}</p>
                  {/* Category filter */}
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:'0.75rem'}}>
                    {cats.map(c=>(
                      <button key={c.key} onClick={()=>setAttractionFilter(c.key)} style={{fontSize:'0.72rem',padding:'3px 10px',borderRadius:20,border:`1px solid ${attractionFilter===c.key?'var(--gold)':'#30363d'}`,background:attractionFilter===c.key?'rgba(201,168,76,0.15)':'transparent',color:attractionFilter===c.key?'var(--gold)':'#8b949e',cursor:'pointer'}}>{c.label}</button>
                    ))}
                  </div>
                  {/* Horizontal scroll cards */}
                  <div style={{display:'flex',gap:10,overflowX:'auto',paddingBottom:8}}>
                    {filtered.map((a,i)=>(
                      <div key={i} style={{minWidth:210,maxWidth:210,background:'#161b22',borderRadius:12,border:'1px solid #21262d',padding:'10px 12px',flexShrink:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                          <span style={{fontSize:'1.3rem'}}>{catIcon[a.category]||'📍'}</span>
                          <span style={{fontSize:'0.85rem',fontWeight:700,color:'#f0f6fc',lineHeight:1.2}}>{localizeAttractionField(a, 'name', lang)}</span>
                        </div>
                        <p style={{fontSize:'0.75rem',color:'#8b949e',marginBottom:6,lineHeight:1.4}}>{localizeAttractionField(a, 'description', lang)}</p>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                          <span style={{fontSize:'0.7rem',background:'rgba(201,168,76,0.12)',color:'var(--gold)',borderRadius:10,padding:'1px 7px',border:'1px solid #c9a84c44'}}>{displayPrice(a.price)||ft.free||'free'}</span>
                          <span style={{fontSize:'0.7rem',color:'#8b949e'}}>⏰ {displayBestTime(a.bestTime)}</span>
                          {a.visitHours && <span style={{fontSize:'0.7rem',color:'#8b949e'}}>⏱️ {a.visitHours}{ft.hours||'h.'}</span>}
                        </div>
                        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                          <a href={a.mapLink} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(a.mapLink, e)} style={{flex:1,fontSize:'0.72rem',textAlign:'center',padding:'4px 0',borderRadius:8,background:'rgba(201,168,76,0.1)',border:'1px solid #c9a84c44',color:'var(--gold)',textDecoration:'none'}}>{ft.mapBtn||'📍 Map'}</a>
                          <a href={a.wikiLink} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(a.wikiLink, e)} style={{flex:1,fontSize:'0.72rem',textAlign:'center',padding:'4px 0',borderRadius:8,background:'rgba(79,139,255,0.1)',border:'1px solid #4f8bff44',color:'#79c0ff',textDecoration:'none'}}>{ft.infoBtn||'📖 Info'}</a>
                          {a.officialSite && <a href={a.officialSite} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(a.officialSite, e)} title={a.officialSite} style={{width:'100%',fontSize:'0.72rem',textAlign:'center',padding:'5px 0',borderRadius:8,background:'linear-gradient(135deg,#c9a84c22,#c9a84c44)',border:'1px solid #c9a84c66',color:'#c9a84c',textDecoration:'none',fontWeight:600,display:'block'}}>{ft.ticketsInfo||'🎟️ Tickets & Info'}</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="ts-planner-actions" style={{marginTop:'1rem',display:'flex',gap:8}}>
              <button className="ts-btn-ghost-sm" onClick={()=>setResult(null)}>{ft.newPlan||'← New'}</button>
              <button className="ts-btn-gold-full" style={{flex:1, background: saved ? '#3fb950' : undefined }} onClick={saveTrip} disabled={saved}>
                {saved ? (ft.saved||'✅ Saved!') : ft.saveJourney}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SplitCostsModal({ lang, onClose }) {
  const ft = FT[lang] || FT.en;
  const [people, setPeople] = useState(4);
  const [expenses, setExpenses] = useState([{ id:1, name:'', amount:'' }]);
  function addExpense() { setExpenses(e=>[...e,{id:Date.now(),name:'',amount:''}]); }
  function upd(id,f,v) { setExpenses(e=>e.map(x=>x.id===id?{...x,[f]:v}:x)); }
  const total = expenses.reduce((a,e)=>a+(+e.amount||0),0);
  const per = people>0 ? (total/people).toFixed(2) : 0;
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={MODAL_S} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{ft.splitCostsBtn}</h2>
        <label className="ts-filter-label">{ft.people} {people}<input type="range" className="ts-range" min="2" max="20" value={people} onChange={e=>setPeople(+e.target.value)}/></label>
        {expenses.map(ex=><div key={ex.id} className="ts-split-row"><input className="ts-input" style={{flex:2}} placeholder={ft.itemName} value={ex.name} onChange={e=>upd(ex.id,'name',e.target.value)}/><input className="ts-input" style={{flex:1}} placeholder="€" type="number" value={ex.amount} onChange={e=>upd(ex.id,'amount',e.target.value)}/></div>)}
        <button className="ts-btn-ghost-sm" style={{marginTop:'0.5rem'}} onClick={addExpense}>{ft.addItem}</button>
        {total>0 && <div className="ts-split-result"><div>Total: <strong>€{total.toFixed(2)}</strong></div><div>Each pays: <strong className="ts-gold-text">€{per}</strong></div></div>}
        <button className="ts-btn-gold-full" style={{marginTop:'1rem'}} onClick={onClose}>{ft.sendToGroupChat}</button>
      </div>
    </div>
  );
}

const COIN_PACKAGES = [
  { coins:100,  price:'0.99€',  bonus:'',          priceKey:'coins_100'  },
  { coins:500,  price:'3.99€',  bonus:'+50 bonus',  priceKey:'coins_500'  },
  { coins:1200, price:'7.99€',  bonus:'+200 bonus', priceKey:'coins_1200' },
  { coins:3000, price:'14.99€', bonus:'+500 bonus', priceKey:'coins_3000' },
];

function getCoinPurchaseFailedMessage(lang) {
  if (lang === 'bg') return 'Покупката не бе завършена. Опитай отново.';
  if (lang === 'es') return 'La compra no se completó. Inténtalo de nuevo.';
  if (lang === 'fr') return "L'achat n'a pas abouti. Réessayez.";
  if (lang === 'de') return 'Der Kauf wurde nicht abgeschlossen. Bitte versuche es erneut.';
  if (lang === 'el') return 'Η αγορά δεν ολοκληρώθηκε. Δοκίμασε ξανά.';
  if (lang === 'it') return "L'acquisto non è stato completato. Riprova.";
  return 'The purchase did not complete. Please try again.';
}

function CoinsPackagesModal({ lang, onClose, coinBalance, setCoinBalance }) {
  const title = lang==='bg'?'Купи монети':lang==='es'?'Comprar monedas':lang==='fr'?'Acheter pièces':lang==='de'?'Münzen kaufen':lang==='el'?'Αγορά νομισμάτων':lang==='it'?'Compra monete':'Buy Coins';
  const addLabel = lang==='bg'?'Добави монети':lang==='es'?'Añadir monedas':lang==='fr'?'Ajouter pièces':lang==='de'?'Münzen hinzufügen':lang==='el'?'Προσθήκη νομισμάτων':lang==='it'?'Aggiungi monete':'Add Coins';
  const isWebBuyer = !Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android';
  const webHint = lang==='bg'
    ? 'Покупките стават през Android приложението в Google Play. Ще те отведем там — после се върни тук и влез със същия акаунт през „Вход с QR код от телефона“.'
    : lang==='es'
    ? 'Las compras se realizan en la app de Android a través de Google Play. Te llevaremos allí — luego vuelve aquí e inicia sesión con la misma cuenta usando "Iniciar sesión con código QR desde el teléfono".'
    : lang==='fr'
    ? 'Les achats se font dans l\'application Android via Google Play. Nous allons vous y rediriger — revenez ensuite ici et connectez-vous avec le même compte via « Connexion par code QR depuis le téléphone ».'
    : lang==='de'
    ? 'Käufe erfolgen in der Android-App über Google Play. Wir leiten dich dorthin weiter — melde dich danach hier mit demselben Konto über „Mit QR-Code vom Smartphone anmelden“ an.'
    : lang==='el'
    ? 'Οι αγορές γίνονται στην εφαρμογή Android μέσω Google Play. Θα σε μεταφέρουμε εκεί — μετά επέστρεψε εδώ και συνδέσου με τον ίδιο λογαριασμό μέσω «Σύνδεση με κωδικό QR από το τηλέφωνο».'
    : lang==='it'
    ? 'Gli acquisti avvengono nell\'app Android tramite Google Play. Ti porteremo lì — poi torna qui e accedi con lo stesso account tramite "Accedi con codice QR dal telefono".'
    : 'Purchases happen in the Android app through Google Play. We\'ll take you there — then come back and log in here with the same account using "Log in with QR code from phone".';
  const [busyKey, setBusyKey] = useState(null);

  async function buyCoins(pkg) {
    if (busyKey) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      window.open(GOOGLE_PLAY_URL, '_blank', 'noopener');
      return;
    }
    setBusyKey(pkg.priceKey);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id || !session.access_token) {
        alert(getCoinPurchaseFailedMessage(lang));
        return;
      }
      googlePlayStore.applicationUsername = session.user.id;
      googlePlayStore.obfuscator = 'disabled';
      const verifyCoinPurchase = async (productId, purchaseToken) => {
        const response = await fetch(`${API_BASE}/api/google-play/verify-coins`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ productId, purchaseToken }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          return { verified: false, error: payload?.error || 'google_play_coin_verification_request_failed' };
        }
        return payload;
      };
      const result = await purchaseGooglePlayCoins(pkg.priceKey, verifyCoinPurchase);
      if (result?.status === 'verified') {
        if (typeof result.coins === 'number') {
          setCoinBalance && setCoinBalance(result.coins);
          localStorage.setItem('travelsoul_coins', String(result.coins));
        }
        onClose();
      } else if (result?.status !== 'cancelled') {
        alert(getCoinPurchaseFailedMessage(lang));
      }
    } catch (err) {
      alert(getCoinPurchaseFailedMessage(lang));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-gift-modal ts-coins-modal" onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif" style={{color:'var(--gold)',textAlign:'center',marginBottom:'1.25rem',fontSize:'1.15rem'}}>
          <CoinIcon size="1.4em" style={{marginRight:'6px'}}/>{title}
        </h2>
        {isWebBuyer && (
          <p style={{fontSize:'0.78rem',color:'var(--muted)',textAlign:'center',marginBottom:'1rem',lineHeight:1.4}}>{webHint}</p>
        )}
        <div className="ts-coins-packages">
          {COIN_PACKAGES.map(pkg => (
            <div key={pkg.coins} className="ts-coin-pkg">
              <div className="ts-coin-pkg-amount"><CoinIcon size="1.2em" style={{marginRight:'4px'}}/>{pkg.coins}</div>
              {pkg.bonus && <div className="ts-coin-pkg-bonus">{pkg.bonus}</div>}
              <div className="ts-coin-pkg-price">{pkg.price}</div>
              <button className="ts-buy-coins-btn" style={{marginTop:'8px',padding:'6px 10px',fontSize:'0.72rem',width:'100%'}} disabled={busyKey===pkg.priceKey} onClick={()=>buyCoins(pkg)}>{addLabel}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GiftPickerModal({ lang, coinBalance, setCoinBalance, onSend, onClose }) {
  const ft = FT[lang] || FT.en;
  const [showBuyCoins, setShowBuyCoins] = useState(false);
  const balanceLabel = lang==='bg'?'Баланс':lang==='es'?'Saldo':lang==='fr'?'Solde':lang==='de'?'Guthaben':lang==='el'?'Υπόλοιπο':lang==='it'?'Saldo':'Balance';
  if (showBuyCoins) return <CoinsPackagesModal lang={lang} onClose={()=>setShowBuyCoins(false)} coinBalance={coinBalance} setCoinBalance={setCoinBalance} />;
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-gift-modal" onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif" style={{color:'var(--gold)',textAlign:'center',marginBottom:'0.75rem',fontSize:'1.15rem'}}>
          {ft.sendGift}
        </h2>
        <div className="ts-gift-balance-bar"><CoinIcon size="1.2em" style={{marginRight:'4px'}}/>{balanceLabel}: <strong>{coinBalance ?? 0}</strong></div>
        <div className="ts-gift-grid-new ts-gift-grid-scroll">
          {GIFTS.map(g => {
            const canAfford = (coinBalance ?? 0) >= g.price;
            return (
              <div key={g.name}
                className={`ts-gift-card${canAfford ? '' : ' ts-gift-card--locked'}`}
                style={{background:`linear-gradient(135deg,${g.color}1a,${g.color}33)`,borderColor:`${g.color}55`}}
                onClick={()=>canAfford && onSend(g)}>
                {!canAfford && <div className="ts-gift-lock-overlay">🔒</div>}
                <div className="ts-gift-emoji">{g.emoji}</div>
                <div className="ts-gift-label">{(ft.gifts||{})[g.name]||g.name}</div>
                <div className="ts-gift-price-coin"><CoinIcon size="1.2em" style={{marginRight:'4px'}}/>{g.price}</div>
              </div>
            );
          })}
        </div>
        <div className="ts-gift-footer">
          <button className="ts-buy-coins-btn" onClick={()=>setShowBuyCoins(true)}>
            <CoinIcon size="1.2em" style={{marginRight:'4px'}}/>{lang==='bg'?'Купи монети':lang==='es'?'Comprar monedas':lang==='fr'?'Acheter pièces':lang==='de'?'Münzen kaufen':lang==='el'?'Αγορά νομισμάτων':lang==='it'?'Compra monete':'Buy Coins'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AiIcebreakerModal({ profile, lang, onUse, onClose }) {
  const ft = FT[lang] || FT.en;
  const [sugg, setSugg] = useState(() => getIcebreakerSuggestions(lang, profile));
  function refresh() { setSugg(getIcebreakerSuggestions(lang, profile)); }
  return (
    <div className="ts-modal-overlay" style={{...OVERLAY_S, zIndex:99999}} onClick={onClose}>
      <div className="ts-modal" style={MODAL_S} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{ft.icebreaker}</h2>
        <p className="ts-modal-sub">{ft.icebreakerTitle}</p>
        {sugg.map((s,i)=>(
          <div key={i} className="ts-icebreaker-item" onClick={()=>onUse(s)}>
            <p>{s}</p><span className="ts-icebreaker-use">{ft.icebreakerUse}</span>
          </div>
        ))}
        <button className="ts-btn-ghost-sm" style={{marginTop:'0.75rem'}} onClick={refresh}>{ft.icebreakerRefresh}</button>
      </div>
    </div>
  );
}

function WeeklyChallengeCard({ lang, t }) {
  const ft = FT[lang] || FT.en;
  const challenges = t?.challenges || WEEKLY_CHALLENGES;
  const ch = challenges[new Date().getDate() % challenges.length];
  const daysLeft = 7 - (new Date().getDay() || 7);
  const [gone, setGone] = useState(false);
  if (gone) return null;
  return (
    <div className="ts-challenge-card">
      <button className="ts-challenge-x" onClick={()=>setGone(true)}>✕</button>
      <div className="ts-challenge-top">
        <span className="ts-challenge-title">{ch.title}</span>
        <span className="ts-challenge-days">{daysLeft} {ft.challengeDays}</span>
      </div>
      <p className="ts-challenge-desc">{ch.desc}</p>
      <div className="ts-challenge-bar-wrap"><div className="ts-challenge-bar"><div className="ts-challenge-fill" style={{width:'0%'}}/></div><span>0/{ch.goal}</span></div>
      <div className="ts-challenge-reward">🏆 {ft.challengeReward}: <strong>{ch.reward}</strong></div>
    </div>
  );
}

function DreamDestSection() {
  return null;
}

// ── Custom Badge constants (used by creator + ProfileCard) ────────────────────
const BADGE_EMOJIS = ['✈️','🌍','🏔️','🏖️','🗺️','🧭','🌴','🦁','🐬','🌸','🎭','🍜','🏯','🗼','🎪'];
const BADGE_COLORS = [
  { key:'gold',   hex:'#E8C870', bg:'rgba(232,200,112,0.15)', border:'rgba(232,200,112,0.45)' },
  { key:'purple', hex:'#b47fff', bg:'rgba(180,127,255,0.15)', border:'rgba(180,127,255,0.45)' },
  { key:'blue',   hex:'#60a5fa', bg:'rgba(96,165,250,0.15)',  border:'rgba(96,165,250,0.45)' },
  { key:'green',  hex:'#34d399', bg:'rgba(52,211,153,0.15)',  border:'rgba(52,211,153,0.45)' },
  { key:'red',    hex:'#f87171', bg:'rgba(248,113,113,0.15)', border:'rgba(248,113,113,0.45)' },
];

// ── Custom Badge Creator Modal ─────────────────────────────────────────────────
function CustomBadgeCreatorModal({ lang, plan, existingBadge, onClose, onSaved }) {
  const isBg = lang === 'bg';
  const isElite = plan === 'elite';

  const [emoji, setEmoji] = useState(existingBadge?.emoji || '✈️');
  const [name, setName] = useState(existingBadge?.name || '');
  const [color, setColor] = useState(existingBadge?.color || 'gold');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const col = BADGE_COLORS.find(c => c.key === color) || BADGE_COLORS[0];

  async function save() {
    if (!name.trim()) return;
    setSaving(true); setError('');
    const badge = { emoji, name: name.trim(), color };
    const { userId } = getAuthToken();
    if (!userId) { setError(lang==='bg'?'Не сте влезли в профила':lang==='es'?'No has iniciado sesión':lang==='fr'?'Vous n\'êtes pas connecté':lang==='de'?'Du bist nicht angemeldet':lang==='el'?'Δεν έχεις συνδεθεί':lang==='it'?'Non hai effettuato l\'accesso':'Not authenticated'); setSaving(false); return; }
    try {
      await sbFetch('PATCH', 'profiles', `id=eq.${userId}`, { custom_badge: badge }, { Prefer: 'return=minimal' });
      try {
        const lsP = JSON.parse(localStorage.getItem('travelsoul_profile') || '{}');
        setProfileLocalStorage({ ...lsP, custom_badge: badge });
      } catch {}
      onSaved && onSaved(badge);
      onClose();
    } catch {
      setError(lang==='bg'?'Грешка при запазване.':lang==='es'?'Error al guardar.':lang==='fr'?'Erreur lors de l\'enregistrement.':lang==='de'?'Fehler beim Speichern.':lang==='el'?'Σφάλμα αποθήκευσης.':lang==='it'?'Errore durante il salvataggio.':'Save error.');
    }
    setSaving(false);
  }

  return (
    <div style={{...OVERLAY_S, zIndex:10500}} onClick={onClose}>
      <div style={{...MODAL_S, maxWidth:380, padding:'24px 20px 20px'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center',marginBottom:'0.25rem'}}>
          🎨 {isBg?'Създай своя значка':lang==='es'?'Crea tu Insignia':lang==='fr'?'Créer votre Badge':lang==='de'?'Abzeichen erstellen':lang==='el'?'Δημιουργία κονκάρδας':lang==='it'?'Crea distintivo':'Create Your Badge'}
        </h2>
        <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.78rem',marginBottom:'1rem'}}>
          {lang==='bg'?'Персонализирана значка за твоя профил':lang==='es'?'Insignia personalizada para tu perfil':lang==='fr'?'Badge personnalisé pour ton profil':lang==='de'?'Individuelles Abzeichen für dein Profil':lang==='el'?'Εξατομικευμένη κονκάρδα για το προφίλ σου':lang==='it'?'Distintivo personalizzato per il tuo profilo':'A custom badge shown on your profile'}
        </p>

        {!isElite ? (
          <div style={{textAlign:'center',padding:'1.5rem 0'}}>
            <span style={{fontSize:'2rem',display:'block',marginBottom:'0.5rem'}}>💎</span>
            <p style={{color:'var(--gold)',fontWeight:700,fontSize:'0.9rem',marginBottom:'0.25rem'}}>{lang==='bg'?'Elite функция':lang==='es'?'Función Elite':lang==='fr'?'Fonction Elite':lang==='de'?'Elite-Funktion':lang==='el'?'Λειτουργία Elite':lang==='it'?'Funzione Elite':'Elite Feature'}</p>
            <p style={{color:'var(--muted)',fontSize:'0.8rem',marginBottom:'0.85rem',padding:'0 1rem'}}>{lang==='bg'?'Само Elite потребители могат да създават персонализирани значки':lang==='es'?'Solo los usuarios Elite pueden crear insignias personalizadas':lang==='fr'?'Seuls les utilisateurs Elite peuvent créer des badges personnalisés':lang==='de'?'Nur Elite-Nutzer können individuelle Abzeichen erstellen':lang==='el'?'Μόνο οι χρήστες Elite μπορούν να δημιουργήσουν εξατομικευμένες κονκάρδες':lang==='it'?'Solo gli utenti Elite possono creare distintivi personalizzati':'Only Elite users can create custom badges'}</p>
            <button className="ts-btn-gold-full" style={{padding:'0.45rem 1.4rem',fontSize:'0.83rem'}} onClick={onClose}>💎 {lang==='bg'?'Upgrade към Elite':lang==='es'?'Mejorar a Elite':lang==='fr'?'Passer à Elite':lang==='de'?'Auf Elite upgraden':lang==='el'?'Αναβάθμιση σε Elite':lang==='it'?'Passa a Elite':'Upgrade to Elite'}</button>
          </div>
        ) : (
          <>
            {/* Live preview */}
            <div style={{display:'flex',justifyContent:'center',marginBottom:'1.1rem'}}>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,background:col.bg,border:`2px solid ${col.border}`,borderRadius:14,padding:'12px 24px',minWidth:110,textAlign:'center',transition:'all 0.2s'}}>
                <span style={{fontSize:'1.8rem',lineHeight:1}}>{emoji}</span>
                <span style={{fontSize:'0.76rem',fontWeight:700,color:col.hex,lineHeight:1.2}}>{name||(lang==='bg'?'Моята значка':lang==='es'?'Mi insignia':lang==='fr'?'Mon badge':lang==='de'?'Mein Abzeichen':lang==='el'?'Η κονκάρδα μου':lang==='it'?'Il mio distintivo':'My Badge')}</span>
              </div>
            </div>

            {/* Emoji grid */}
            <p className="ts-label" style={{marginBottom:'0.4rem'}}>{lang==='bg'?'Емоджи:':lang==='es'?'Emoji:':lang==='fr'?'Emoji :':lang==='de'?'Emoji:':lang==='el'?'Emoji:':lang==='it'?'Emoji:':'Emoji:'}</p>
            <div className="ts-badge-emoji-grid">
              {BADGE_EMOJIS.map(e => (
                <button key={e} className={`ts-badge-emoji-btn${emoji===e?' active':''}`} onClick={()=>setEmoji(e)}>{e}</button>
              ))}
            </div>

            {/* Name */}
            <p className="ts-label" style={{marginTop:'0.9rem',marginBottom:'0.4rem'}}>{lang==='bg'?'Название (макс. 20 знака):':lang==='es'?'Nombre (máx. 20 caracteres):':lang==='fr'?'Nom (max 20 caractères) :':lang==='de'?'Name (max. 20 Zeichen):':lang==='el'?'Όνομα (έως 20 χαρακτήρες):':lang==='it'?'Nome (max 20 caratteri):':'Name (max 20 chars):'}</p>
            <input className="ts-input" value={name} onChange={e=>setName(e.target.value.slice(0,20))}
              placeholder={lang==='bg'?'Моята значка…':lang==='es'?'Mi insignia…':lang==='fr'?'Mon badge…':lang==='de'?'Mein Abzeichen…':lang==='el'?'Η κονκάρδα μου…':lang==='it'?'Il mio distintivo…':'My badge…'} maxLength={20} autoFocus />
            <p style={{textAlign:'right',fontSize:'0.68rem',color:'var(--muted)',marginTop:2}}>{name.length}/20</p>

            {/* Color */}
            <p className="ts-label" style={{marginTop:'0.75rem',marginBottom:'0.5rem'}}>{lang==='bg'?'Цвят:':lang==='es'?'Color:':lang==='fr'?'Couleur :':lang==='de'?'Farbe:':lang==='el'?'Χρώμα:':lang==='it'?'Colore:':'Color:'}</p>
            <div style={{display:'flex',gap:10,alignItems:'center'}}>
              {BADGE_COLORS.map(c => (
                <button key={c.key} onClick={()=>setColor(c.key)} title={c.key}
                  style={{width:28,height:28,borderRadius:'50%',background:c.hex,border:`3px solid ${color===c.key?'#fff':'transparent'}`,cursor:'pointer',outline:'none',transition:'border 0.15s'}} />
              ))}
            </div>

            {error && <p style={{color:'#f87171',fontSize:'0.8rem',marginTop:'0.75rem'}}>{error}</p>}
            <button className="ts-btn-gold-full" style={{marginTop:'1.25rem'}} onClick={save} disabled={saving||!name.trim()}>
              {saving ? '…' : (lang==='bg'?'💾 Запази значката':lang==='es'?'💾 Guardar insignia':lang==='fr'?'💾 Enregistrer le badge':lang==='de'?'💾 Abzeichen speichern':lang==='el'?'💾 Αποθήκευση κονκάρδας':lang==='it'?'💾 Salva distintivo':'💾 Save Badge')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function TravelBadges({ ft, badgesT }) {
  return (
    <div className="ts-badges-wrap">
      <h3 className="ts-badges-section-title">✅ {ft.earned}</h3>
      <div className="ts-badges-grid">
        {BADGES_DATA.filter(b=>b.earned).map(b=>(
          <div key={b.id} className="ts-badge-item">
            <div className="ts-badge-circle earned"><span>{b.icon}</span></div>
            <p className="ts-badge-name">{badgesT?.[b.key]?.name || b.name}</p>
          </div>
        ))}
      </div>
      <h3 className="ts-badges-section-title">🔒 {ft.locked}</h3>
      <div className="ts-badges-grid">
        {BADGES_DATA.filter(b=>!b.earned).map(b=>(
          <div key={b.id} className="ts-badge-item">
            <div className="ts-badge-circle locked"><span>🔒</span></div>
            <p className="ts-badge-name ts-muted">{badgesT?.[b.key]?.name || b.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Existing components ───────────────────────────────────────────────────────
function Starfield() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); let raf; const stars = [];
    function resize() { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; }
    function init() { stars.length = 0; for (let i=0;i<220;i++) stars.push({ x:Math.random()*canvas.width, y:Math.random()*canvas.height, r:Math.random()*1.4+0.2, alpha:Math.random(), da:(Math.random()*0.008+0.002)*(Math.random()<0.5?1:-1) }); }
    function draw() { ctx.clearRect(0,0,canvas.width,canvas.height); stars.forEach(s=>{ s.alpha+=s.da; if(s.alpha>=1){s.alpha=1;s.da=-Math.abs(s.da);} if(s.alpha<=0.05){s.alpha=0.05;s.da=Math.abs(s.da);} ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2); ctx.fillStyle=`rgba(201,168,76,${s.alpha*0.7})`; ctx.fill(); }); raf=requestAnimationFrame(draw); }
    const ro = new ResizeObserver(()=>{resize();init();}); ro.observe(canvas);
    resize(); init(); draw();
    return ()=>{cancelAnimationFrame(raf);ro.disconnect();};
  }, []);
  return <canvas ref={ref} className="ts-starfield" />;
}

function PulseBar({ bars = 7 }) {
  return (
    <div className="ts-pulse-bar" aria-hidden="true">
      {Array.from({length:bars},(_,i)=>(
        <span key={i} className="ts-pulse-bar-item" style={{animationDelay:`${i*0.13}s`}} />
      ))}
    </div>
  );
}

function LangPicker({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e){if(ref.current&&!ref.current.contains(e.target))setOpen(false);}
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  }, []);
  return (
    <div className="ts-lang-picker" ref={ref}>
      <button className="ts-lang-toggle" onClick={()=>setOpen(o=>!o)}>
        {LANG_META[lang]?.flag || LANG_META.en.flag} {LANG_META[lang]?.label || LANG_META.en.label} <span className="ts-chevron">{open?'▴':'▾'}</span>
      </button>
      {open && (
        <div className="ts-lang-drop">
          {Object.keys(T).map(l=>(
            <button key={l} className={`ts-lang-opt ${l===lang?'active':''}`} onClick={()=>{setLang(l);setOpen(false);}}>
              {LANG_META[l].flag} {LANG_META[l].label} {LANG_META[l].name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MyRewardsModal({ lang, coinBalance, onClose }) {
  const { userId } = getAuthToken();
  const history = (() => { try { return JSON.parse(localStorage.getItem(spinHistoryKey(userId)) || '[]'); } catch { return []; } })();
  const isBg = lang === 'bg';
  const title = isBg?'🏆 Моите Награди':lang==='es'?'🏆 Mis Premios':lang==='fr'?'🏆 Mes Récompenses':lang==='de'?'🏆 Meine Belohnungen':lang==='el'?'🏆 Οι ανταμοιβές μου':lang==='it'?'🏆 I miei premi':'🏆 My Rewards';
  const emptyMsg = isBg?'Все още нямаш награди. Завърти Колелото на късмета! 🎡':lang==='es'?'Sin premios aún. ¡Gira la Rueda! 🎡':lang==='fr'?'Pas encore de récompenses. Faites tourner la roue! 🎡':lang==='de'?'Noch keine Belohnungen. Dreh das Glücksrad! 🎡':lang==='el'?'Καμία ανταμοιβή ακόμα. Γύρισε τον Τροχό της Τύχης! 🎡':lang==='it'?'Ancora nessun premio. Gira la Ruota della Fortuna! 🎡':'No rewards yet. Spin the Wheel of Fortune! 🎡';
  const howLabel = lang==='bg'?'Как да използваш наградите':lang==='es'?'Cómo usar los premios':lang==='fr'?'Comment utiliser les récompenses':lang==='de'?'Wie du die Preise nutzt':lang==='el'?'Πώς να χρησιμοποιήσεις τις ανταμοιβές':lang==='it'?'Come usare i premi':'How to use prizes';
  const howToUse = [
    { emoji:'💰', label:lang==='bg'?'Монети':lang==='es'?'Monedas':lang==='fr'?'Pièces':lang==='de'?'Münzen':lang==='el'?'Νομίσματα':lang==='it'?'Monete':'Coins',    desc:lang==='bg'?'Използват се автоматично за изпращане на подаръци и суперлайкове':lang==='es'?'Se usan automáticamente para enviar regalos y súper likes':lang==='fr'?'Utilisées automatiquement pour envoyer des cadeaux et des super likes':lang==='de'?'Werden automatisch zum Senden von Geschenken und Super Likes verwendet':lang==='el'?'Χρησιμοποιούνται αυτόματα για αποστολή δώρων και super likes':lang==='it'?'Vengono usate automaticamente per inviare regali e super like':'Used automatically for sending gifts and super likes' },
    { emoji:'👑', label:'Premium',                 desc:lang==='bg'?'Активира се автоматично - проверете короната в хедъра':lang==='es'?'Se activa automáticamente: revisa la corona en el encabezado':lang==='fr'?'S\'active automatiquement — vérifie la couronne dans l\'en-tête':lang==='de'?'Wird automatisch aktiviert – prüfe die Krone in der Kopfzeile':lang==='el'?'Ενεργοποιείται αυτόματα — έλεγξε το στέμμα στην κορυφή':lang==='it'?'Si attiva automaticamente: controlla la corona nell\'intestazione':'Activated automatically - check the crown in the header' },
  ];
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:420}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{title}</h2>
        {/* Coin balance */}
        <div style={{background:'linear-gradient(135deg,#c9a84c22,#e8b92344)',border:'1px solid var(--gold)',borderRadius:14,padding:'12px 20px',marginBottom:'1.25rem',display:'flex',alignItems:'center',justifyContent:'center',gap:12}}>
          <CoinIcon size="2rem"/>
          <div>
            <div style={{fontSize:'1.6rem',fontWeight:800,color:'var(--gold)',lineHeight:1}}>{coinBalance ?? 0}</div>
            <div style={{fontSize:'0.72rem',color:'var(--muted)'}}>{lang==='bg'?'монети в баланса':lang==='es'?'monedas en el saldo':lang==='fr'?'pièces dans le solde':lang==='de'?'Münzen im Guthaben':lang==='el'?'νομίσματα στο υπόλοιπο':lang==='it'?'monete nel saldo':'coins in balance'}</div>
          </div>
        </div>
        {/* Prize history */}
        {history.length === 0 ? (
          <p style={{color:'var(--muted)',textAlign:'center',padding:'1rem 0',fontSize:'0.85rem'}}>{emptyMsg}</p>
        ) : (
          <div style={{marginBottom:'1rem'}}>
            {history.map((h,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #21262d'}}>
                <span style={{fontSize:'1.5rem'}}>{h.emoji}</span>
                <span style={{flex:1,fontSize:'0.85rem',color:'var(--text)',textAlign:'left'}}>{h.prize}</span>
                <span style={{fontSize:'0.72rem',color:'var(--muted)'}}>{h.date}</span>
              </div>
            ))}
          </div>
        )}
        {/* How to use */}
        <div style={{background:'rgba(255,255,255,0.04)',borderRadius:12,padding:'12px 16px',marginTop:'0.5rem'}}>
          <p style={{fontSize:'0.72rem',fontWeight:700,color:'var(--muted)',marginBottom:'0.6rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>{howLabel}</p>
          {howToUse.map((item,i) => (
            <div key={i} style={{display:'flex',gap:10,marginBottom:'0.5rem',alignItems:'flex-start'}}>
              <span style={{fontSize:'1.1rem',flexShrink:0}}>{item.emoji}</span>
              <div style={{textAlign:'left'}}>
                <span style={{fontSize:'0.8rem',fontWeight:700,color:'var(--text)'}}>{item.label}</span>
                <span style={{fontSize:'0.75rem',color:'var(--muted)'}}> — {item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VerificationReviewList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reasonById, setReasonById] = useState({});
  const [duplicateById, setDuplicateById] = useState({});
  const [noteById, setNoteById] = useState({});

  async function loadPending() {
    setLoading(true);
    try {
      const rows = await sbFetch('GET', 'profiles', 'verification_status=eq.pending&select=id,name,age,photo_url,verification_selfie_url,verification_front_url,verification_submitted_at&order=verification_submitted_at.asc&limit=100');
      setItems(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setItems([]);
    }
    setLoading(false);
  }

  useEffect(() => { loadPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function review(profileId, decision, duplicate = false) {
    setBusyId(profileId);
    try {
      const reason = duplicate
        ? 'Този профил не беше одобрен. Ако смяташ, че има грешка, свържи се с поддръжка.'
        : (reasonById[profileId] || 'Този профил не беше одобрен. Ако смяташ, че има грешка, свържи се с поддръжка.');
      const res = await sbFetch('POST', 'rpc/review_profile_verification', '', {
        target_profile_id: profileId,
        decision,
        rejection_reason: decision === 'rejected' ? reason : null,
        duplicate_profile_id: duplicate ? (duplicateById[profileId] || null) : null,
        duplicate_note: duplicate ? (noteById[profileId] || null) : null,
      });
      await ensureSbWriteOk(res, 'review profile verification');
      let status = null;
      try {
        const body = await res.text();
        status = body ? JSON.parse(body) : null;
      } catch (e) {}
      if (status !== decision) throw new Error(status || 'review_failed');
      setItems(current => current.filter(item => item.id !== profileId));
    } catch (e) {
      alert('Грешка при преглед: ' + (e?.message || e));
    }
    setBusyId(null);
  }

  if (loading) return <div style={{textAlign:'center',color:'#8b949e',padding:30,fontSize:14}}>Зарежда...</div>;
  if (items.length === 0) return <div style={{textAlign:'center',color:'#8b949e',padding:24}}>Няма чакащи верификации ✅</div>;

  return (
    <div>
      {/* Future: integrate compliant liveness/KYC provider for automated duplicate-person detection after legal/privacy review. */}
      {items.map(profile => (
        <div key={profile.id} style={{background:'#161b22',border:'1px solid #30363d',borderRadius:14,padding:12,marginBottom:12}}>
          <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:10}}>
            <img src={getProfilePhotoUrl(profile.photo_url, profile.verification_selfie_url || '/default-avatar.png')} alt="" style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',background:'#21262d'}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:'#f0f6fc',fontWeight:700,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{profile.name || '—'}{profile.age ? `, ${profile.age}` : ''}</div>
              <div style={{color:'#8b949e',fontSize:11}}>{profile.verification_submitted_at ? new Date(profile.verification_submitted_at).toLocaleString() : '—'}</div>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
            {[['Селфи', profile.verification_selfie_url], ['Анфас', profile.verification_front_url]].map(([label, url]) => (
              <a key={label} href={url || '#'} target="_blank" rel="noreferrer" style={{display:'block',textDecoration:'none',color:'#c9a84c',fontSize:12}}>
                {url ? <img src={url} alt={label} style={{width:'100%',height:120,objectFit:'cover',borderRadius:10,border:'1px solid #30363d',display:'block',marginBottom:4}}/> : <div style={{height:120,borderRadius:10,border:'1px dashed #30363d',display:'flex',alignItems:'center',justifyContent:'center',color:'#8b949e'}}>—</div>}
                {label}
              </a>
            ))}
          </div>
          <input value={reasonById[profile.id] || ''} onChange={e=>setReasonById(s=>({...s,[profile.id]:e.target.value}))} placeholder="Причина при отказ..." style={{width:'100%',background:'#0d1117',border:'1px solid #30363d',borderRadius:8,padding:'8px 10px',color:'#f0f6fc',fontSize:12,boxSizing:'border-box',marginBottom:8}}/>
          <input value={duplicateById[profile.id] || ''} onChange={e=>setDuplicateById(s=>({...s,[profile.id]:e.target.value.trim()}))} placeholder="Duplicate of profile id (по избор)" style={{width:'100%',background:'#0d1117',border:'1px solid #30363d',borderRadius:8,padding:'8px 10px',color:'#f0f6fc',fontSize:12,boxSizing:'border-box',marginBottom:8}}/>
          <input value={noteById[profile.id] || ''} onChange={e=>setNoteById(s=>({...s,[profile.id]:e.target.value}))} placeholder="Вътрешна бележка за дубликат..." style={{width:'100%',background:'#0d1117',border:'1px solid #30363d',borderRadius:8,padding:'8px 10px',color:'#f0f6fc',fontSize:12,boxSizing:'border-box',marginBottom:10}}/>
          <div style={{display:'flex',gap:6}}>
            <button disabled={busyId===profile.id} onClick={()=>review(profile.id, 'approved')} style={{flex:1,background:'#2ea043',border:'none',borderRadius:8,color:'#fff',padding:'8px 10px',fontSize:12,fontWeight:700,cursor:'pointer',opacity:busyId===profile.id?0.6:1}}>✅ Одобри</button>
            <button disabled={busyId===profile.id} onClick={()=>review(profile.id, 'rejected')} style={{flex:1,background:'#f59e0b',border:'none',borderRadius:8,color:'#111',padding:'8px 10px',fontSize:12,fontWeight:700,cursor:'pointer',opacity:busyId===profile.id?0.6:1}}>↩ Откажи</button>
            <button disabled={busyId===profile.id} onClick={()=>review(profile.id, 'rejected', true)} style={{flex:1,background:'#f85149',border:'none',borderRadius:8,color:'#fff',padding:'8px 10px',fontSize:12,fontWeight:700,cursor:'pointer',opacity:busyId===profile.id?0.6:1}}>⛔ Дубликат</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModeratorPanelModal({ lang, onClose }) {
  const [tab, setTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [reportedNames, setReportedNames] = useState({});
  const [announcement, setAnnouncement] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { loadTab(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTab(t) {
    setLoading(true);
    try {
      if (t === 'reports') {
        const data = await sbFetch('GET', 'reports', 'select=*&reviewed=eq.false&order=created_at.desc&limit=100');
        const reps = Array.isArray(data) ? data : [];
        setReports(reps);
        const ids = [...new Set(reps.map(r => r.reported_id).filter(Boolean))];
        if (ids.length) {
          const profiles = await sbFetch('GET', 'profiles', `id=in.(${ids.join(',')})&select=id,name`);
          const map = {};
          (Array.isArray(profiles) ? profiles : []).forEach(p => { map[p.id] = p.name; });
          setReportedNames(map);
        }
      } else {
        const data = await sbFetch('GET', 'announcements', 'select=*&order=created_at.desc&limit=5');
        setAnnouncements(Array.isArray(data) ? data : []);
      }
    } catch {}
    setLoading(false);
  }

  async function markReviewed(reportId) {
    // Audit finding: a direct client UPDATE here silently did nothing —
    // `reports` has no RLS UPDATE policy, so Postgres denies it for every
    // caller. Now goes through a SECURITY DEFINER RPC that independently
    // re-checks is_admin/is_moderator server-side before writing.
    const { data, error } = await supabase.rpc('moderation_mark_report_reviewed', { p_report_id: reportId });
    if (error || data !== 'reviewed') { console.error('[ModeratorPanel] markReviewed failed:', error || data); return; }
    setReports(rs => rs.filter(r => r.id !== reportId));
  }

  async function banReported(reportId, reportedId) {
    // Audit finding: same issue — profiles.UPDATE RLS only allows
    // auth.uid() = id, so a moderator banning someone ELSE's profile was
    // silently rejected by RLS, not actually banning anyone.
    const { data, error } = await supabase.rpc('moderation_ban_from_report', { p_report_id: reportId, p_reported_id: reportedId });
    if (error || data !== 'banned') { console.error('[ModeratorPanel] banReported failed:', error || data); return; }
    setReports(rs => rs.filter(r => r.id !== reportId));
  }

  async function sendAnnouncement() {
    if (!announcement.trim() || sending) return;
    setSending(true);
    try {
      const { accessToken } = getAuthToken();
      const resp = await fetch(`${API_BASE}/api/moderator/announce`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: announcement.trim() }),
      });
      if (!resp.ok) { const e = await resp.json().catch(()=>({})); alert('Грешка при изпращане: ' + (e.error || resp.status)); }
      else {
        const r = await resp.json().catch(()=>({}));
        setAnnouncement('');
        loadTab('announcements');
        alert(`✅ Изпратено до ${r.recipients || 0} потребителя`);
      }
    } catch (err) { alert('Грешка при изпращане. Опитай пак.'); }
    setSending(false);
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.88)',zIndex:99997,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#0d1117',border:'1px solid #1f6feb',borderRadius:20,width:'100%',maxWidth:520,maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 8px 48px rgba(31,111,235,0.2)'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid #21262d',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <span style={{color:'#58a6ff',fontWeight:700,fontSize:16}}>🛡️ Модераторски панел</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#8b949e',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
        </div>
        <div style={{display:'flex',borderBottom:'1px solid #21262d',flexShrink:0}}>
          {[['reports','⚠️ Доклади'],['verification','✅ Верификации'],['announcements','💬 Съобщения']].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} style={{flex:1,padding:'12px 8px',background:'none',border:'none',borderBottom:tab===k?'2px solid #58a6ff':'2px solid transparent',color:tab===k?'#58a6ff':'#8b949e',fontWeight:tab===k?700:400,fontSize:12,cursor:'pointer'}}>{l}</button>
          ))}
        </div>
        <div style={{flex:1,overflowY:'auto',padding:16}}>
          {loading ? (
            <div style={{textAlign:'center',color:'#8b949e',padding:40,fontSize:14}}>Зарежда...</div>
          ) : tab === 'verification' ? (
            <VerificationReviewList />
          ) : tab === 'reports' ? (
            <>
              {reports.length===0 && <div style={{textAlign:'center',color:'#8b949e',padding:24}}>Няма активни доклади ✅</div>}
              {reports.map(r=>(
                <div key={r.id} style={{background:'#161b22',borderRadius:12,padding:'12px 14px',marginBottom:10,border:'1px solid #30363d'}}>
                  <div style={{color:'#f85149',fontSize:13,fontWeight:600}}>⚠️ {r.reason}</div>
                  <div style={{color:'#8b949e',fontSize:11,marginTop:4}}>Докладван: <b style={{color:'#f0f6fc'}}>{reportedNames[r.reported_id]||r.reported_id?.slice(0,8)+'…'}</b></div>
                  <div style={{color:'#8b949e',fontSize:11}}>{r.created_at?new Date(r.created_at).toLocaleDateString():'—'}</div>
                  <div style={{display:'flex',gap:8,marginTop:10}}>
                    <button onClick={()=>markReviewed(r.id)} style={{background:'#2ea043',border:'none',borderRadius:8,color:'#fff',padding:'6px 12px',fontSize:12,cursor:'pointer',flex:1}}>✅ Разгледан</button>
                    <button onClick={()=>banReported(r.id,r.reported_id)} style={{background:'#f85149',border:'none',borderRadius:8,color:'#fff',padding:'6px 12px',fontSize:12,cursor:'pointer',flex:1}}>🚫 Бан</button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <textarea value={announcement} onChange={e=>setAnnouncement(e.target.value)} placeholder="Напиши съобщение до всички потребители..." rows={4} style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:10,padding:'10px 12px',color:'#f0f6fc',fontSize:13,resize:'vertical',boxSizing:'border-box',outline:'none',marginBottom:10}} />
              <button onClick={sendAnnouncement} disabled={sending||!announcement.trim()} style={{width:'100%',background:'linear-gradient(90deg,#1f6feb,#58a6ff)',border:'none',borderRadius:10,color:'#fff',fontWeight:700,padding:'11px',fontSize:14,cursor:'pointer',marginBottom:16,opacity:sending||!announcement.trim()?0.5:1}}>
                {sending?'Изпраща...':'📢 Изпрати до всички'}
              </button>
              {announcements.length>0 && <div style={{fontSize:12,color:'#8b949e',marginBottom:8}}>Последни съобщения:</div>}
              {announcements.map(a=>(
                <div key={a.id||a.created_at} style={{background:'#161b22',borderRadius:10,padding:'10px 12px',marginBottom:8,border:'1px solid #30363d'}}>
                  <div style={{color:'#f0f6fc',fontSize:13}}>{a.content}</div>
                  <div style={{color:'#8b949e',fontSize:11,marginTop:4}}>{a.created_at?new Date(a.created_at).toLocaleDateString():'—'}</div>
                </div>
              ))}
              {announcements.length===0 && <div style={{textAlign:'center',color:'#8b949e',padding:16,fontSize:13}}>Няма изпратени съобщения</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminPanelModal({ lang, onClose, onAccessLost, isAdmin }) {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTab(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function verifyCurrentAdmin() {
    const closeAdminAccess = () => {
      if (onAccessLost) onAccessLost();
      else if (onClose) onClose();
    };
    if (!isAdmin) {
      closeAdminAccess();
      return false;
    }
    try {
      const activeProfileId = await getActiveProfileId({ forceFresh: true });
      if (!activeProfileId) throw new Error('No active profile');
      const rows = await sbFetch('GET', 'profiles', `id=eq.${activeProfileId}&select=is_admin&limit=1`);
      const ok = Array.isArray(rows) && rows[0]?.is_admin === true;
      if (!ok) {
        closeAdminAccess();
        alert(lang==='bg'?'Админ достъпът вече не е наличен.':lang==='es'?'El acceso de administrador ya no está disponible.':lang==='fr'?'L\'accès administrateur n\'est plus disponible.':lang==='de'?'Der Admin-Zugriff ist nicht mehr verfügbar.':lang==='el'?'Η πρόσβαση διαχειριστή δεν είναι πλέον διαθέσιμη.':lang==='it'?'L\'accesso admin non è più disponibile.':'Admin access is no longer available.');
      }
      return ok;
    } catch (e) {
      closeAdminAccess();
      return false;
    }
  }

  async function loadTab(t) {
    if (!(await verifyCurrentAdmin())) return;
    setLoading(true);
    try {
      if (t === 'users') {
        const data = await sbFetch('GET', 'profiles', 'select=id,name,photo_url,plan,created_at,is_blocked&order=created_at.desc&limit=200');
        setUsers(Array.isArray(data) ? data : []);
      } else if (t === 'reports') {
        const data = await sbFetch('GET', 'reports', 'select=*&reviewed=eq.false&order=created_at.desc&limit=100');
        setReports(Array.isArray(data) ? data : []);
      } else if (t === 'statistics') {
        const all = await sbFetch('GET', 'profiles', 'select=plan&limit=10000');
        const rows = Array.isArray(all) ? all : [];
        setStats({
          total: rows.length,
          premium: rows.filter(r => r.plan === 'premium').length,
          elite: rows.filter(r => r.plan === 'elite').length,
        });
      }
    } catch {}
    setLoading(false);
  }

  async function blockUser(userId) {
    if (!(await verifyCurrentAdmin())) return;
    const u = users.find(x => x.id === userId);
    const willBlock = !u?.is_blocked;
    const { accessToken } = getAuthToken();
    if (!accessToken) { alert('Сесията изтече. Влез отново.'); return; }
    try {
      const resp = await fetch(`${API_BASE}/api/admin/block-user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: userId, blocked: willBlock }),
      });
      if (!resp.ok) { const e = await resp.json().catch(()=>({})); alert('Грешка при блокиране: ' + (e.error || resp.status)); return; }
      setUsers(us => us.map(x => x.id === userId ? { ...x, is_blocked: willBlock } : x));
    } catch (err) { alert('Грешка при блокиране.'); }
  }

  async function deleteUser(userId) {
    if (!(await verifyCurrentAdmin())) return;
    if (!window.confirm('Изтрий потребителя ЗАВИНАГИ? Това трие всички данни и auth акаунта (GDPR). Необратимо.')) return;
    const { accessToken } = getAuthToken();
    if (!accessToken) { alert('Сесията изтече. Влез отново.'); return; }
    try {
      const resp = await fetch(`${API_BASE}/api/admin/delete-user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: userId }),
      });
      if (!resp.ok) { const e = await resp.json().catch(()=>({})); alert('Грешка при изтриване: ' + (e.error || resp.status)); return; }
      setUsers(us => us.filter(u => u.id !== userId));
    } catch (err) { alert('Грешка при изтриване.'); }
  }

  async function markReviewed(reportId) {
    if (!(await verifyCurrentAdmin())) return;
    // Audit finding: a direct client UPDATE here silently did nothing —
    // `reports` has no RLS UPDATE policy, so Postgres denies it for every
    // caller. Now goes through a SECURITY DEFINER RPC that independently
    // re-checks is_admin/is_moderator server-side before writing.
    const { data, error } = await supabase.rpc('moderation_mark_report_reviewed', { p_report_id: reportId });
    if (error || data !== 'reviewed') { console.error('[AdminPanel] markReviewed failed:', error || data); return; }
    setReports(rs => rs.filter(r => r.id !== reportId));
  }

  async function banFromReport(reportId, reportedId) {
    if (!(await verifyCurrentAdmin())) return;
    // Audit finding: same issue — profiles.UPDATE RLS only allows
    // auth.uid() = id, so an admin banning someone ELSE's profile was
    // silently rejected by RLS, not actually banning anyone.
    const { data, error } = await supabase.rpc('moderation_ban_from_report', { p_report_id: reportId, p_reported_id: reportedId });
    if (error || data !== 'banned') { console.error('[AdminPanel] banFromReport failed:', error || data); return; }
    setReports(rs => rs.filter(r => r.id !== reportId));
  }

  const filtered = users.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()));
  const PLAN_CLR = { premium: '#c9a84c', elite: '#a855f7', free: '#8b949e' };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.88)',zIndex:99997,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#0d1117',border:'1px solid #30363d',borderRadius:20,width:'100%',maxWidth:560,maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 8px 48px rgba(0,0,0,0.7)'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid #21262d',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <span style={{color:'#c9a84c',fontWeight:700,fontSize:16}}>🛡️ Админ панел</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#8b949e',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
        </div>
        <div style={{display:'flex',borderBottom:'1px solid #21262d',flexShrink:0}}>
          {[['users','👥 Потребители'],['verification','✅ Верификации'],['reports','⚠️ Доклади'],['statistics','📊 Статистики']].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} style={{flex:1,padding:'12px 6px',background:'none',border:'none',borderBottom:tab===k?'2px solid #c9a84c':'2px solid transparent',color:tab===k?'#c9a84c':'#8b949e',fontWeight:tab===k?700:400,fontSize:12,cursor:'pointer',transition:'color 0.2s'}}>{l}</button>
          ))}
        </div>
        <div style={{flex:1,overflowY:'auto',padding:16}}>
          {loading ? (
            <div style={{textAlign:'center',color:'#8b949e',padding:40,fontSize:14}}>Зарежда...</div>
          ) : tab === 'verification' ? (
            <VerificationReviewList />
          ) : tab === 'users' ? (
            <>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Търси по име..." style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:10,padding:'8px 12px',color:'#f0f6fc',fontSize:13,marginBottom:12,boxSizing:'border-box',outline:'none'}} />
              {filtered.map(u=>(
                <div key={u.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderBottom:'1px solid #21262d'}}>
                  <div style={{width:36,height:36,borderRadius:'50%',overflow:'hidden',flexShrink:0,background:'#21262d',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>
                    {getProfilePhotoUrl(u.photo_url, '') ? <img src={getProfilePhotoUrl(u.photo_url, '')} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : '👤'}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:'#f0f6fc',fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.name||'—'}</div>
                    <div style={{fontSize:11,color:PLAN_CLR[u.plan]||'#8b949e'}}>{u.plan||'free'} · {u.created_at?new Date(u.created_at).toLocaleDateString():'—'}</div>
                  </div>
                  <div style={{display:'flex',gap:4,flexShrink:0}}>
                    <button onClick={()=>blockUser(u.id)} title={u.is_blocked?'Отблокирай':'Блокирай'} style={{background:u.is_blocked?'#f85149':'#21262d',border:'1px solid #f85149',borderRadius:8,padding:'4px 7px',cursor:'pointer',fontSize:13,color:u.is_blocked?'#fff':'#f85149'}}>{u.is_blocked?'✅':'🚫'}</button>
                    {isAdmin && <button onClick={()=>deleteUser(u.id)} title="Изтрий" style={{background:'#21262d',border:'1px solid #6e7681',borderRadius:8,padding:'4px 7px',cursor:'pointer',fontSize:13,color:'#6e7681'}}>🗑️</button>}
                  </div>
                </div>
              ))}
              {filtered.length===0 && <div style={{textAlign:'center',color:'#8b949e',padding:24}}>Няма потребители</div>}
            </>
          ) : tab === 'reports' ? (
            <>
              {reports.length===0 && <div style={{textAlign:'center',color:'#8b949e',padding:24}}>Няма активни доклади ✅</div>}
              {reports.map(r=>(
                <div key={r.id} style={{background:'#161b22',borderRadius:12,padding:'12px 14px',marginBottom:10,border:'1px solid #30363d'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10}}>
                    <div style={{minWidth:0}}>
                      <div style={{color:'#f85149',fontSize:13,fontWeight:600}}>⚠️ {r.reason}</div>
                      <div style={{color:'#8b949e',fontSize:11,marginTop:4,wordBreak:'break-all'}}>От: {r.reporter_id?.slice(0,8)}… → {r.reported_id?.slice(0,8)}…</div>
                      <div style={{color:'#8b949e',fontSize:11}}>{r.created_at?new Date(r.created_at).toLocaleDateString():'—'}</div>
                    </div>
                    <div style={{display:'flex',gap:6,flexShrink:0}}>
                      <button onClick={()=>markReviewed(r.id)} style={{background:'#2ea043',border:'none',borderRadius:8,color:'#fff',padding:'6px 10px',fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>✅ Разгледан</button>
                      <button onClick={()=>banFromReport(r.id,r.reported_id)} style={{background:'#f85149',border:'none',borderRadius:8,color:'#fff',padding:'6px 10px',fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>🚫 Бан</button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : stats ? (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,paddingTop:8}}>
              {[
                {label:'Общо потребители',value:stats.total,color:'#58a6ff'},
                {label:'Premium',value:stats.premium,color:'#c9a84c'},
                {label:'Elite',value:stats.elite,color:'#a855f7'},
                {label:'Free',value:Math.max(0,stats.total-stats.premium-stats.elite),color:'#8b949e'},
              ].map(({label,value,color})=>(
                <div key={label} style={{background:'#161b22',border:`1px solid ${color}44`,borderRadius:14,padding:'20px 16px',textAlign:'center'}}>
                  <div style={{fontSize:36,fontWeight:800,color,lineHeight:1}}>{value}</div>
                  <div style={{fontSize:12,color:'#8b949e',marginTop:6}}>{label}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ── Landing page download QR code ────────────────────────────────────────────
// Pure marketing QR: just encodes a store URL, no auth/session involved at
// all. Shown under each store button on the public landing page, before
// anyone has logged in. When `url` is null (App Store, until a real App
// Store ID exists), shows a placeholder instead of generating a dead code.
function AppDownloadQrCode({ url, disabledLabel }) {
  const [dataUrl, setDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!url) { setDataUrl(null); return undefined; }
    QRCode.toDataURL(url, { width: 120, margin: 1, color: { dark: '#0d1117', light: '#ffffff' } })
      .then(d => { if (!cancelled) setDataUrl(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);

  if (!url) {
    return (
      <div style={{width:96,height:96,borderRadius:10,background:'rgba(255,255,255,0.05)',border:'1px dashed rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',padding:6}}>
        <span style={{fontSize:10,color:'var(--muted)',textAlign:'center',lineHeight:1.3}}>{disabledLabel}</span>
      </div>
    );
  }
  return dataUrl
    ? <img src={dataUrl} alt="QR" width={96} height={96} style={{borderRadius:10}} />
    : <div style={{width:96,height:96,borderRadius:10,background:'rgba(255,255,255,0.05)'}} />;
}

// ── QR web login: Android side (generate + confirm) ─────────────────────────
// Shown only on native Android (Capacitor). Generates a short-lived token,
// renders it as a QR code, and polls the server for whether ITS OWN token
// has been scanned by some camera — if so, shows an explicit confirm/deny
// prompt. Only an explicit tap here ever lets the web side log in; scanning
// alone (e.g. a photographed/shared QR) never does.
const WEB_LOGIN_T = {
  bg: { title: 'Вход в уеб версията', instructions: 'Отвори travelsoul.love в браузъра си и сканирай този код', expiresIn: 'Изтича след', expired: 'Кодът изтече', newCode: 'Нов код', confirmTitle: 'Потвърди вход?', confirmBody: 'Някой се опитва да влезе в акаунта ти през уеб версията с този код. Ако си ти — потвърди.', approve: 'Потвърди', deny: 'Отказ', close: 'Затвори', confirmed: 'Потвърдено! Влез в браузъра.' },
  en: { title: 'Log in on the web', instructions: 'Open travelsoul.love in your browser and scan this code', expiresIn: 'Expires in', expired: 'Code expired', newCode: 'New code', confirmTitle: 'Confirm login?', confirmBody: 'Someone is trying to log into your account on the web using this code. If that’s you, confirm below.', approve: 'Confirm', deny: 'Deny', close: 'Close', confirmed: 'Confirmed! Continue in your browser.' },
  es: { title: 'Iniciar sesión en la web', instructions: 'Abre travelsoul.love en tu navegador y escanea este código', expiresIn: 'Expira en', expired: 'Código expirado', newCode: 'Nuevo código', confirmTitle: '¿Confirmar inicio de sesión?', confirmBody: 'Alguien intenta iniciar sesión en tu cuenta desde la web con este código. Si eres tú, confirma abajo.', approve: 'Confirmar', deny: 'Rechazar', close: 'Cerrar', confirmed: '¡Confirmado! Continúa en tu navegador.' },
  fr: { title: 'Connexion sur le web', instructions: 'Ouvre travelsoul.love dans ton navigateur et scanne ce code', expiresIn: 'Expire dans', expired: 'Code expiré', newCode: 'Nouveau code', confirmTitle: 'Confirmer la connexion ?', confirmBody: 'Quelqu’un tente de se connecter à ton compte sur le web avec ce code. Si c’est toi, confirme ci-dessous.', approve: 'Confirmer', deny: 'Refuser', close: 'Fermer', confirmed: 'Confirmé ! Continue dans ton navigateur.' },
  de: { title: 'Im Web anmelden', instructions: 'Öffne travelsoul.love in deinem Browser und scanne diesen Code', expiresIn: 'Läuft ab in', expired: 'Code abgelaufen', newCode: 'Neuer Code', confirmTitle: 'Anmeldung bestätigen?', confirmBody: 'Jemand versucht, sich mit diesem Code im Web in dein Konto einzuloggen. Wenn du das bist, bestätige unten.', approve: 'Bestätigen', deny: 'Ablehnen', close: 'Schließen', confirmed: 'Bestätigt! Fahre in deinem Browser fort.' },
  el: { title: 'Σύνδεση στο web', instructions: 'Άνοιξε το travelsoul.love στον browser σου και σάρωσε αυτόν τον κωδικό', expiresIn: 'Λήγει σε', expired: 'Ο κωδικός έληξε', newCode: 'Νέος κωδικός', confirmTitle: 'Επιβεβαίωση σύνδεσης;', confirmBody: 'Κάποιος προσπαθεί να συνδεθεί στον λογαριασμό σου στο web με αυτόν τον κωδικό. Αν είσαι εσύ, επιβεβαίωσε παρακάτω.', approve: 'Επιβεβαίωση', deny: 'Απόρριψη', close: 'Κλείσιμο', confirmed: 'Επιβεβαιώθηκε! Συνέχισε στον browser σου.' },
  it: { title: 'Accedi dal web', instructions: 'Apri travelsoul.love nel tuo browser e scansiona questo codice', expiresIn: 'Scade tra', expired: 'Codice scaduto', newCode: 'Nuovo codice', confirmTitle: 'Confermare l\'accesso?', confirmBody: 'Qualcuno sta cercando di accedere al tuo account dal web con questo codice. Se sei tu, conferma qui sotto.', approve: 'Conferma', deny: 'Rifiuta', close: 'Chiudi', confirmed: 'Confermato! Continua nel tuo browser.' },
};

function WebLoginGenerateModal({ lang, onClose }) {
  const wt = WEB_LOGIN_T[lang] || WEB_LOGIN_T.en;
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [plainToken, setPlainToken] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [pendingTokenId, setPendingTokenId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmedOk, setConfirmedOk] = useState(false);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  async function generate() {
    setQrDataUrl(null);
    setPlainToken(null);
    setPendingTokenId(null);
    setConfirmedOk(false);
    try {
      const { accessToken } = await getAuthTokenSafe();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE}/api/web-login/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.token) return;
      setPlainToken(data.token);
      setSecondsLeft(data.expiresInSeconds || 60);
      const dataUrl = await QRCode.toDataURL(data.token, { width: 260, margin: 1, color: { dark: '#0d1117', light: '#ffffff' } });
      setQrDataUrl(dataUrl);
    } catch (e) { console.error('[web-login] generate failed:', e); }
  }

  useEffect(() => { generate(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // Countdown
  useEffect(() => {
    if (!plainToken) return undefined;
    countdownRef.current = setInterval(() => {
      setSecondsLeft(s => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [plainToken]);

  // Poll for this token having been scanned (needs a confirm prompt)
  useEffect(() => {
    if (!plainToken || secondsLeft <= 0 || confirmedOk) return undefined;
    let cancelled = false;
    async function poll() {
      try {
        const { accessToken } = await getAuthTokenSafe();
        if (!accessToken) return;
        const res = await fetch(`${API_BASE}/api/web-login/pending-for-me`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        });
        const data = await res.json();
        if (!cancelled && data?.pendingTokenId) setPendingTokenId(data.pendingTokenId);
      } catch (e) {}
    }
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [plainToken, secondsLeft, confirmedOk]);

  async function respond(approve) {
    if (!pendingTokenId || confirmBusy) return;
    setConfirmBusy(true);
    try {
      const { accessToken } = await getAuthTokenSafe();
      const res = await fetch(`${API_BASE}/api/web-login/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ tokenId: pendingTokenId, approve }),
      });
      const data = await res.json();
      if (data?.success && approve) setConfirmedOk(true);
      setPendingTokenId(null);
    } catch (e) {
      console.error('[web-login] confirm failed:', e);
    }
    setConfirmBusy(false);
  }

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth: 340, textAlign: 'center'}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h3 className="ts-serif" style={{color: 'var(--gold)', marginBottom: 10}}>{wt.title}</h3>

        {pendingTokenId ? (
          <div style={{padding: '8px 0'}}>
            <p style={{fontSize: '1.6rem', marginBottom: 8}}>🔐</p>
            <p style={{fontWeight: 700, marginBottom: 6}}>{wt.confirmTitle}</p>
            <p style={{color: 'var(--muted)', fontSize: '0.85rem', marginBottom: 16}}>{wt.confirmBody}</p>
            <div style={{display: 'flex', gap: 10}}>
              <button className="ts-btn-ghost-sm" style={{flex: 1}} disabled={confirmBusy} onClick={() => respond(false)}>{wt.deny}</button>
              <button className="ts-btn-gold-full" style={{flex: 1}} disabled={confirmBusy} onClick={() => respond(true)}>{wt.approve}</button>
            </div>
          </div>
        ) : confirmedOk ? (
          <div style={{padding: '16px 0'}}>
            <p style={{fontSize: '2rem', marginBottom: 8}}>✅</p>
            <p style={{fontWeight: 600}}>{wt.confirmed}</p>
            <button className="ts-btn-gold-full" style={{marginTop: 16, width: '100%'}} onClick={onClose}>{wt.close}</button>
          </div>
        ) : (
          <>
            <p style={{color: 'var(--muted)', fontSize: '0.85rem', marginBottom: 14}}>{wt.instructions}</p>
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR" style={{width: 220, height: 220, borderRadius: 12, opacity: secondsLeft <= 0 ? 0.3 : 1}} />
            ) : (
              <div style={{width: 220, height: 220, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <div className="ts-soul-match-spinner" />
              </div>
            )}
            <p style={{marginTop: 12, fontSize: '0.8rem', color: secondsLeft <= 10 ? '#f87171' : 'var(--muted)'}}>
              {secondsLeft > 0 ? `${wt.expiresIn} ${secondsLeft}s` : wt.expired}
            </p>
            {secondsLeft <= 0 && (
              <button className="ts-btn-gold-full" style={{marginTop: 10, width: '100%'}} onClick={generate}>{wt.newCode}</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Header({ t, lang, setLang, screen, onLogoClick, plan, onUpgrade, incognito, onLogin, onLogout, onProfile, notifications, onClearNotifications, coinBalance, setCoinBalance, isAdmin, onAdmin, isModerator, onMod }) {
  const logoClicks = useRef(0);
  const logoTimer = useRef(null);
  const [showSpin, setShowSpin] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const [showCoinShop, setShowCoinShop] = useState(false);
  const [showWebLoginQr, setShowWebLoginQr] = useState(false);
  const [verified, setVerified] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('travelsoul_user') || '{}'); return !!u.verified; } catch { return false; }
  });
  const [showVerify, setShowVerify] = useState(false);
  const [canSpin, setCanSpin] = useState(false);
  const [headerPage, setHeaderPage] = useState(0);
  useEffect(() => {
    // Direct Supabase query — bypasses event system and localStorage timing issues
    const readStoredCanSpin = () => {
      const { userId } = getAuthToken();
      return !spunToday(getStoredSpinLastDate(userId));
    };
    if (supabase) {
      const { userId } = getAuthToken();
      if (userId) {
        supabase.from('profiles').select('spin_last_date').eq('id', userId).single()
          .then(({ data }) => {
            if (data?.spin_last_date) {
              const spinDate = String(data.spin_last_date).slice(0, 10);
              setStoredSpinLastDate(userId, spinDate);
              setCanSpin(!spunToday(spinDate));
            } else {
              setStoredSpinLastDate(userId, null);
              setCanSpin(true);
            }
          })
          .catch(() => {
            setCanSpin(readStoredCanSpin());
          });
      } else {
        setCanSpin(readStoredCanSpin());
      }
    } else {
      setCanSpin(readStoredCanSpin());
    }
    const sync = () => setCanSpin(readStoredCanSpin());
    window.addEventListener('ts-spin-synced', sync);
    return () => window.removeEventListener('ts-spin-synced', sync);
  }, []);
  const photo = localStorage.getItem('travelsoul_photo');
  async function logout() {
    // Clear local storage FIRST — this is what actually ends the session (token lives here).
    // signOut() can hang on the Supabase auth lock, so we never await it unbounded.
    const storedLang = localStorage.getItem('travelsoul_lang');
    if (supabase) {
      // Fire signOut with a 3s cap; don't let a hung auth lock block logout.
      Promise.race([
        supabase.auth.signOut({ scope: 'local' }),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]).catch(() => {});
    }
    clearAuthTokenCache();
    removeSupabaseAuthStorage();
    safeRemoveTravelSoulData();
    if (storedLang && !VALID_LANGS.includes(storedLang)) {
      try { localStorage.removeItem('travelsoul_lang'); } catch {}
    }
    if (onLogout) onLogout();
    else window.location.reload();
  }
  function handleLogoClick() {
    onLogoClick();
    logoClicks.current++;
    if (logoTimer.current) clearTimeout(logoTimer.current);
    logoTimer.current = setTimeout(()=>{ logoClicks.current=0; }, 600);
    if (logoClicks.current>=3) { logoClicks.current=0; onUpgrade&&onUpgrade('cycle'); }
  }
  return (
    <>
    <header className="ts-header">
      <button className={`ts-brand ${plan==='elite'?'ts-brand-elite':plan==='premium'?'ts-brand-premium':''}`} onClick={handleLogoClick}>
        <span className="ts-brand-icon">✈</span>
        <span className="ts-brand-name">{t.brand}</span>
        {screen==='app' && <EliteBadge plan={plan} />}
      </button>
      <div className="ts-header-right">
        {screen==='app' && headerPage === 1 && (
          <button type="button" className="ts-header-page-arrow" onClick={()=>setHeaderPage(0)} aria-label={lang==='bg'?'Първа страница с бутони':lang==='es'?'Primera página de botones':lang==='fr'?'Première page de boutons':lang==='de'?'Erste Schaltflächenseite':lang==='el'?'Πρώτη σελίδα κουμπιών':lang==='it'?'Prima pagina pulsanti':'First header button page'}>‹</button>
        )}
        <div className={`ts-header-page ts-header-page-main ${headerPage===0?'active':''}`}>
          {incognito && screen==='app' && <span className="ts-incognito-indicator" title={t.connect?.incognitoOn}>🙈</span>}
          {screen==='app' && (
            <button className="ts-lucky-wheel-btn" onClick={()=>setShowSpin(true)}
              title={lang==='bg'?'Колелото на късмета':lang==='es'?'Rueda de la Fortuna':lang==='fr'?'Roue de la Fortune':lang==='de'?'Glücksrad':lang==='el'?'Τροχός της Τύχης':lang==='it'?'Ruota della Fortuna':'Wheel of Fortune'}>
              🎡{canSpin&&<span className="ts-spin-dot"/>}
            </button>
          )}
          {screen==='app' && (
            <button className="ts-rewards-header-btn" onClick={()=>setShowRewards(true)}>
              🏆 {lang==='bg'?'Награди':lang==='es'?'Premios':lang==='fr'?'Récompenses':lang==='de'?'Preise':lang==='el'?'Τιμές':lang==='it'?'Prezzi':'Rewards'}
            </button>
          )}
          {screen==='app' && <NotificationBell t={t} lang={lang} notifications={notifications||[]} onClearAll={onClearNotifications} />}
          {screen==='app' && coinBalance != null && <span className="ts-coin-balance-badge" style={{cursor:'pointer'}} onClick={()=>setShowCoinShop(true)}><CoinIcon size="1em" style={{marginRight:'3px'}}/>{coinBalance}</span>}
        </div>
        {screen==='app' && headerPage === 0 && (
          <button type="button" className="ts-header-page-arrow" onClick={()=>setHeaderPage(1)} aria-label={lang==='bg'?'Втора страница с бутони':lang==='es'?'Segunda página de botones':lang==='fr'?'Deuxième page de boutons':lang==='de'?'Zweite Schaltflächenseite':lang==='el'?'Δεύτερη σελίδα κουμπιών':lang==='it'?'Seconda pagina pulsanti':'Second header button page'}>›</button>
        )}
        <div className={`ts-header-page ts-header-page-extra ${screen!=='app'||headerPage===1?'active':''}`}>
          {screen==='app' && <button className={`ts-verify-header-btn ${verified?'done':''}`} onClick={()=>plan==='elite'?setShowVerify(true):onUpgrade&&onUpgrade('pricing')} title={t.videoIntro?.recordIntro||(FT[lang]||FT.en).verifyPhoto}>{verified?'🎥✓':'🎥'}</button>}
          {screen==='app' && plan==='free' && <button className="ts-btn-upgrade" onClick={()=>onUpgrade&&onUpgrade('pricing')}>{t.extras?.upgradeBtn||'💎 Upgrade'}</button>}
          {screen==='app' && <button className="ts-btn-ghost ts-header-premium" onClick={()=>onUpgrade&&onUpgrade('pricing')} title={t.premium?.title||'Premium'} style={{background:'#1a1a2e',border:'2px solid #c9a84c',borderRadius:'50%',width:36,height:36,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:16}}>👑</button>}
          {screen==='app' && isAdmin && (
            <button onClick={onAdmin} title="Админ панел" style={{background:'none',border:'1px solid #c9a84c44',borderRadius:8,padding:'4px 8px',cursor:'pointer',fontSize:16,color:'#c9a84c',lineHeight:1}}>🛡️</button>
          )}
          {screen==='app' && isModerator && !isAdmin && (
            <button onClick={onMod} title="Модераторски панел" style={{background:'none',border:'1px solid #58a6ff44',borderRadius:8,padding:'4px 8px',cursor:'pointer',fontSize:16,color:'#58a6ff',lineHeight:1}}>🛡️</button>
          )}
          <LangPicker lang={lang} setLang={setLang} />
          {screen==='app' && (
            <button className="ts-avatar-btn" onClick={onProfile} title={t.profile?.myProfile}>
              {photo ? <img src={photo} alt="me" className="ts-avatar-thumb"/> : <span>👤</span>}
            </button>
          )}
          {screen==='app' && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && (
            <button
              type="button"
              className="ts-mobile-logout-btn"
              onClick={() => setShowWebLoginQr(true)}
              title={lang==='bg'?'Вход в уеб версията':lang==='es'?'Iniciar sesión en la web':lang==='fr'?'Connexion sur le web':lang==='de'?'Im Web anmelden':lang==='el'?'Σύνδεση στο web':lang==='it'?'Accedi sul web':'Log in on the web'}
              aria-label={lang==='bg'?'Вход в уеб версията':lang==='es'?'Iniciar sesión en la web':lang==='fr'?'Connexion sur le web':lang==='de'?'Im Web anmelden':lang==='el'?'Σύνδεση στο web':lang==='it'?'Accedi sul web':'Log in on the web'}
            >
              📱
            </button>
          )}
          {screen==='app' && (
            <button
              type="button"
              className="ts-mobile-logout-btn"
              onClick={logout}
              title={lang==='bg'?'Изход':lang==='es'?'Salir':lang==='fr'?'Déconnexion':lang==='de'?'Abmelden':lang==='el'?'Αποσύνδεση':lang==='it'?'Esci':'Logout'}
              aria-label={lang==='bg'?'Изход':lang==='es'?'Salir':lang==='fr'?'Déconnexion':lang==='de'?'Abmelden':lang==='el'?'Αποσύνδεση':lang==='it'?'Esci':'Logout'}
            >
              ⎋
            </button>
          )}
        </div>
        {screen==='landing' && <button className="ts-btn-ghost-sm" onClick={onLogin}>{t.login}</button>}
      </div>
      {showWebLoginQr && (
        <WebLoginGenerateModal lang={lang} onClose={() => setShowWebLoginQr(false)} />
      )}
    </header>
    {showSpin && <SpinWheelModal lang={lang} onClose={()=>{ setShowSpin(false); setCanSpin(!spunToday(getStoredSpinLastDate(getAuthToken().userId))); }} coinBalance={coinBalance} setCoinBalance={setCoinBalance} />}
    {showRewards && <MyRewardsModal lang={lang} coinBalance={coinBalance} onClose={()=>setShowRewards(false)} />}
    {showVerify && <VideoIntroModal t={t} lang={lang} onClose={()=>setShowVerify(false)} onVerified={()=>setVerified(true)} />}
    {showCoinShop && <CoinsPackagesModal lang={lang} onClose={()=>setShowCoinShop(false)} coinBalance={coinBalance} setCoinBalance={setCoinBalance} />}
    </>
  );
}

function DnaBar({ label, value, color }) {
  return (
    <div className="ts-dna-row">
      <span className="ts-dna-lbl">{label}</span>
      <div className="ts-dna-track"><div className="ts-dna-fill" style={{width:`${value}%`,background:color}} /></div>
      <span className="ts-dna-val">{value}</span>
    </div>
  );
}

function DnaQuiz({ t, onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  function pick(i) {
    const next=[...answers,i];
    if(step<t.quiz.length-1){setAnswers(next);setStep(step+1);}
    else setResult(calcDNA(next));
  }
  const dominant = result ? DNA_TRAIT_KEYS[result.indexOf(Math.max(...result))] : null;
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={MODAL_S} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-modal-title ts-serif">{t.dna.title}</h2>
        <p className="ts-modal-sub">{t.dna.sub}</p>
        {!result ? (
          <>
            <div className="ts-quiz-progress">{t.quiz.map((_,i)=><div key={i} className={`ts-quiz-dot ${i<step?'done':i===step?'active':''}`}/>)}</div>
            <p className="ts-quiz-q">{t.quiz[step].q}</p>
            <div className="ts-quiz-opts">{t.quiz[step].opts.map((opt,i)=><button key={i} className="ts-quiz-opt" onClick={()=>pick(i)}>{opt}</button>)}</div>
          </>
        ) : (
          <div className="ts-dna-result">
            <p className="ts-dna-result-label">{t.dna.result}</p>
            <div className="ts-dna-dominant">{t.dna.traits[dominant]}</div>
            <div className="ts-dna-bars">{DNA_TRAIT_KEYS.map((k,i)=><DnaBar key={k} label={t.dna.traits[k]} value={result[i]} color={DNA_COLORS[i]}/>)}</div>
            <button className="ts-btn-ghost-sm" style={{marginTop:'1.5rem'}} onClick={()=>{setStep(0);setAnswers([]);setResult(null);}}>{t.dna.retake}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TravelDnaModal ────────────────────────────────────────────────────────────
function TravelDnaModal({ lang, plan, onClose, onSaved }) {
  const DNA_QUESTIONS = [
    { q: lang==='bg'?'Предпочитан тип пътуване':lang==='es'?'Tipo de viaje preferido':lang==='fr'?'Type de voyage préféré':lang==='de'?'Bevorzugter Reisetyp':lang==='el'?'Προτιμώμενος τύπος ταξιδιού':lang==='it'?'Tipo di viaggio preferito':'Preferred travel type', opts: ['🏖 '+(lang==='bg'?'Плаж':lang==='es'?'Playa':lang==='fr'?'Plage':lang==='de'?'Strand':lang==='el'?'Παραλία':lang==='it'?'Spiaggia':'Beach'), '⛰ '+(lang==='bg'?'Планини':lang==='es'?'Montañas':lang==='fr'?'Montagnes':lang==='de'?'Berge':lang==='el'?'Βουνά':lang==='it'?'Montagna':'Mountains'), '🏙 '+(lang==='bg'?'Град':lang==='es'?'Ciudad':lang==='fr'?'Ville':lang==='de'?'Stadt':lang==='el'?'Πόλη':lang==='it'?'Città':'City'), '🏜 '+(lang==='bg'?'Пустиня':lang==='es'?'Desierto':lang==='fr'?'Désert':lang==='de'?'Wüste':lang==='el'?'Έρημος':lang==='it'?'Deserto':'Desert')] },
    { q: lang==='bg'?'Стил на пътуване':lang==='es'?'Estilo de viaje':lang==='fr'?'Style de voyage':lang==='de'?'Reisestil':lang==='el'?'Στυλ ταξιδιού':lang==='it'?'Stile di viaggio':'Travel style', opts: ['💎 '+(lang==='bg'?'Луксозно':lang==='es'?'Lujo':lang==='fr'?'Luxe':lang==='de'?'Luxus':lang==='el'?'Πολυτέλεια':lang==='it'?'Lusso':'Luxury'), '💰 '+(lang==='bg'?'Бюджетно':lang==='es'?'Económico':lang==='fr'?'Économique':lang==='de'?'Günstig':lang==='el'?'Οικονομικό':lang==='it'?'Economico':'Budget'), '🧗 '+(lang==='bg'?'Авантюра':lang==='es'?'Aventura':lang==='fr'?'Aventure':lang==='de'?'Abenteuer':lang==='el'?'Περιπέτεια':lang==='it'?'Avventura':'Adventure'), '🏛 '+(lang==='bg'?'Културно':lang==='es'?'Cultural':lang==='fr'?'Culturel':lang==='de'?'Kulturell':lang==='el'?'Πολιτιστικό':lang==='it'?'Culturale':'Cultural')] },
    { q: lang==='bg'?'С кого пътуваш?':lang==='es'?'¿Con quién viajas?':lang==='fr'?'Avec qui voyages-tu?':lang==='de'?'Mit wem reist du?':lang==='el'?'Με ποιον ταξιδεύεις;':lang==='it'?'Con chi viaggi?':'Who do you travel with?', opts: ['🧍 '+(lang==='bg'?'Сам':lang==='es'?'Solo':lang==='fr'?'Seul':lang==='de'?'Allein':lang==='el'?'Μόνος/η':lang==='it'?'Da solo/a':'Solo'), '💑 '+(lang==='bg'?'Двойка':lang==='es'?'Pareja':lang==='fr'?'Couple':lang==='de'?'Paar':lang==='el'?'Ζευγάρι':lang==='it'?'Coppia':'Couple'), '👥 '+(lang==='bg'?'Група':lang==='es'?'Grupo':lang==='fr'?'Groupe':lang==='de'?'Gruppe':lang==='el'?'Παρέα':lang==='it'?'Gruppo':'Group'), '👨‍👩‍👧 '+(lang==='bg'?'Семейство':lang==='es'?'Familia':lang==='fr'?'Famille':lang==='de'?'Familie':lang==='el'?'Οικογένεια':lang==='it'?'Famiglia':'Family')] },
    { q: lang==='bg'?'Темпо на пътуване':lang==='es'?'Ritmo de viaje':lang==='fr'?'Rythme de voyage':lang==='de'?'Reisetempo':lang==='el'?'Ρυθμός ταξιδιού':lang==='it'?'Ritmo di viaggio':'Travel pace', opts: ['🐌 '+(lang==='bg'?'Бавно':lang==='es'?'Lento':lang==='fr'?'Lent':lang==='de'?'Langsam':lang==='el'?'Αργός':lang==='it'?'Lento':'Slow'), '🚶 '+(lang==='bg'?'Умерено':lang==='es'?'Moderado':lang==='fr'?'Modéré':lang==='de'?'Moderat':lang==='el'?'Μέτριος':lang==='it'?'Moderato':'Moderate'), '🏃 '+(lang==='bg'?'Бързо':lang==='es'?'Rápido':lang==='fr'?'Rapide':lang==='de'?'Schnell':lang==='el'?'Γρήγορος':lang==='it'?'Veloce':'Fast'), '🎲 '+(lang==='bg'?'Спонтанно':lang==='es'?'Espontáneo':lang==='fr'?'Spontané':lang==='de'?'Spontan':lang==='el'?'Αυθόρμητος':lang==='it'?'Spontaneo':'Spontaneous')] },
    { q: lang==='bg'?'Предпочитана храна':lang==='es'?'Preferencia de comida':lang==='fr'?'Préférence alimentaire':lang==='de'?'Essensvorliebe':lang==='el'?'Προτίμηση φαγητού':lang==='it'?'Preferenza alimentare':'Food preference', opts: ['🍜 '+(lang==='bg'?'Местна':lang==='es'?'Local':lang==='fr'?'Locale':lang==='de'?'Lokal':lang==='el'?'Τοπική κουζίνα':lang==='it'?'Locale':'Local'), '🍝 '+(lang==='bg'?'Международна':lang==='es'?'Internacional':lang==='fr'?'Internationale':lang==='de'?'International':lang==='el'?'Διεθνής κουζίνα':lang==='it'?'Internazionale':'International'), '🥗 '+(lang==='bg'?'Вегетарианска':lang==='es'?'Vegetariana':lang==='fr'?'Végétarienne':lang==='de'?'Vegetarisch':lang==='el'?'Χορτοφαγική':lang==='it'?'Vegetariana':'Vegetarian'), '🍷 '+(lang==='bg'?'Гурме':lang==='es'?'Gourmet':lang==='fr'?'Gastronomique':lang==='de'?'Gourmet':lang==='el'?'Γκουρμέ':lang==='it'?'Gourmet':'Gourmet')] },
    { q: lang==='bg'?'Тип настаняване':lang==='es'?'Alojamiento':lang==='fr'?'Hébergement':lang==='de'?'Unterkunft':lang==='el'?'Διαμονή':lang==='it'?'Alloggio':'Accommodation', opts: ['🏨 '+(lang==='bg'?'Хостел':lang==='es'?'Hostal':lang==='fr'?'Auberge':lang==='de'?'Hostel':lang==='el'?'Ξενώνας':lang==='it'?'Ostello':'Hostel'), '🏩 '+(lang==='bg'?'Хотел':lang==='es'?'Hotel':lang==='fr'?'Hôtel':lang==='de'?'Hotel':lang==='el'?'Ξενοδοχείο':lang==='it'?'Hotel':'Hotel'), '🏠 AirBnB', '⛺ '+(lang==='bg'?'Кемпинг':lang==='es'?'Camping':lang==='fr'?'Camping':lang==='de'?'Camping':lang==='el'?'Κάμπινγκ':lang==='it'?'Campeggio':'Camping')] },
    { q: lang==='bg'?'Предпочитани активности':lang==='es'?'Actividades preferidas':lang==='fr'?'Activités préférées':lang==='de'?'Bevorzugte Aktivitäten':lang==='el'?'Προτιμώμενες δραστηριότητες':lang==='it'?'Attività preferite':'Preferred activities', opts: ['⚽ '+(lang==='bg'?'Спорт':lang==='es'?'Deportes':lang==='fr'?'Sports':lang==='de'?'Sport':lang==='el'?'Αθλητισμός':lang==='it'?'Sport':'Sports'), '🎭 '+(lang==='bg'?'Култура':lang==='es'?'Cultura':lang==='fr'?'Culture':lang==='de'?'Kultur':lang==='el'?'Πολιτισμός':lang==='it'?'Cultura':'Culture'), '🧘 '+(lang==='bg'?'Релакс':lang==='es'?'Relax':lang==='fr'?'Relaxation':lang==='de'?'Entspannung':lang==='el'?'Χαλάρωση':lang==='it'?'Relax':'Relaxation'), '🎉 '+(lang==='bg'?'Нощен живот':lang==='es'?'Vida nocturna':lang==='fr'?'Vie nocturne':lang==='de'?'Nachtleben':lang==='el'?'Νυχτερινή ζωή':lang==='it'?'Vita notturna':'Nightlife')] },
    { q: lang==='bg'?'Бюджет на пътуване':lang==='es'?'Presupuesto de viaje':lang==='fr'?'Budget de voyage':lang==='de'?'Reisebudget':lang==='el'?'Ταξιδιωτικός προϋπολογισμός':lang==='it'?'Budget di viaggio':'Travel budget', opts: ['€500', '€1 000', '€2 000', '€5 000+'] },
  ];

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  async function pick(i) {
    const next = [...answers, { q: DNA_QUESTIONS[step].q, a: DNA_QUESTIONS[step].opts[i] }];
    if (step < DNA_QUESTIONS.length - 1) {
      setAnswers(next);
      setStep(step + 1);
    } else {
      setAnswers(next);
      await generateDna(next);
    }
  }

  async function generateDna(allAnswers) {
    setLoading(true);
    setError('');
    try {
      const summary = allAnswers.map(a => `${a.q}: ${a.a}`).join('; ');
      const prompt = `Ти си експерт по пътнически личности. На базата на следните отговори: ${summary}.\nГенерирай САМО следното:\n1. ЗАГЛАВИЕ: Креативно заглавие на пътническата личност на български (макс. 8 думи, напр. "Авантюристичен Номад с Душа на Изследовател")\n2. ОПИСАНИЕ: Три кратки параграфа на български, описващи тази пътническа личност, нейните силни страни, идеален тип пътуване и съвет за идеалния партньор. Всеки параграф - 2-3 изречения.\n\nФормат:\nЗАГЛАВИЕ: [заглавието]\nОПИСАНИЕ: [параграф 1]\n\n[параграф 2]\n\n[параграф 3]`;
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role: 'user', content: prompt }], max_tokens: 450 }),
      });
      const data = await res.json();
      const text = data.choices?.[0]?.message?.content || '';
      const titleMatch = text.match(/ЗАГЛАВИЕ:\s*(.+)/);
      const descMatch = text.match(/ОПИСАНИЕ:\s*([\s\S]+)/);
      const title = (titleMatch ? titleMatch[1] : '').trim() || (lang==='bg'?'Пътешественик с Уникална Душа':lang==='es'?'Viajero de Alma Única':lang==='fr'?'Voyageur à l\'Âme Unique':lang==='de'?'Reisender mit einzigartiger Seele':lang==='el'?'Ταξιδιώτης με μοναδική ψυχή':lang==='it'?'Viaggiatore con un\'anima unica':'Unique Soul Traveler');
      const description = (descMatch ? descMatch[1] : text).trim();
      setResult({ title, description });

      const { userId } = getAuthToken();
      if (userId) {
        await sbFetch('PATCH', 'profiles', `id=eq.${userId}`, { dna_title: title, dna_result: description }, { Prefer: 'return=minimal' });
        setSaved(true);
        try {
          const lsProf = JSON.parse(localStorage.getItem('travelsoul_profile') || '{}');
          setProfileLocalStorage({ ...lsProf, dna_title: title, dna_result: description });
        } catch {}
        onSaved && onSaved(title);
      }
    } catch {
      setError(lang==='bg'?'Грешка при генериране. Провери връзката.':lang==='es'?'Error de generación. Comprueba tu conexión.':lang==='fr'?'Erreur de génération. Vérifiez votre connexion.':lang==='de'?'Fehler bei der Erstellung. Überprüfe deine Verbindung.':lang==='el'?'Σφάλμα δημιουργίας. Έλεγξε τη σύνδεσή σου.':lang==='it'?'Errore nella creazione. Controlla la tua connessione.':'Generation error. Check your connection.');
    }
    setLoading(false);
  }

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:400, maxHeight:'88vh', textAlign:'left'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center'}}>🧬 {lang==='bg'?'Пътнически ДНК тест':lang==='es'?'Test de ADN de viajero':lang==='fr'?'Test d\'ADN de voyageur':lang==='de'?'Reise-DNA-Test':lang==='el'?'Τεστ Ταξιδιωτικού DNA':lang==='it'?'Test del DNA di viaggio':'Travel DNA Test'}</h2>

        {loading && (
          <div style={{textAlign:'center',padding:'2rem 0'}}>
            <div className="ts-soul-match-spinner" style={{width:36,height:36,borderWidth:4}}/>
            <p style={{color:'var(--muted)',fontSize:'0.88rem',marginTop:14}}>{lang==='bg'?'AI генерира твоя ДНК профил…':lang==='es'?'La IA está generando tu perfil de ADN…':lang==='fr'?'L\'IA génère votre profil ADN…':lang==='de'?'Die KI erstellt dein DNA-Profil…':lang==='el'?'Το AI δημιουργεί το προφίλ DNA σου…':lang==='it'?'L\'IA sta creando il tuo profilo DNA…':'AI is generating your DNA profile…'}</p>
          </div>
        )}

        {!loading && !result && (
          <>
            <div style={{background:'rgba(255,255,255,0.06)',borderRadius:4,height:4,marginBottom:'1.25rem',overflow:'hidden'}}>
              <div style={{width:`${(step/DNA_QUESTIONS.length)*100}%`,height:'100%',background:'linear-gradient(90deg,#b47fff,#e8c870)',transition:'width 0.3s'}}/>
            </div>
            <p style={{fontSize:'0.72rem',color:'var(--muted)',marginBottom:'0.4rem'}}>{step+1}/{DNA_QUESTIONS.length}</p>
            <p style={{fontSize:'1rem',color:'var(--text)',fontWeight:600,marginBottom:'1.25rem',lineHeight:1.4}}>{DNA_QUESTIONS[step].q}</p>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {DNA_QUESTIONS[step].opts.map((opt, i) => (
                <button key={i} className="ts-dna-opt-btn" onClick={()=>pick(i)}>{opt}</button>
              ))}
            </div>
          </>
        )}

        {!loading && result && (
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:'2.5rem',marginBottom:'0.5rem'}}>🧬</div>
            <h3 style={{background:'linear-gradient(90deg,#b47fff,#e8c870)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',fontSize:'1.1rem',fontWeight:700,marginBottom:'0.75rem',lineHeight:1.4}}>{result.title}</h3>
            {saved && <p style={{fontSize:'0.72rem',color:'var(--green)',marginBottom:'0.5rem'}}>✓ {lang==='bg'?'Запазено в профила':lang==='es'?'Guardado en el perfil':lang==='fr'?'Enregistré dans le profil':lang==='de'?'Im Profil gespeichert':lang==='el'?'Αποθηκεύτηκε στο προφίλ':lang==='it'?'Salvato nel profilo':'Saved to profile'}</p>}
            {error && <p style={{fontSize:'0.72rem',color:'#f85149',marginBottom:'0.5rem'}}>{error}</p>}
            {plan==='elite' ? (
              <div style={{textAlign:'left',fontSize:'0.88rem',color:'var(--text)',lineHeight:1.75,whiteSpace:'pre-wrap',marginTop:'0.75rem'}}>{result.description}</div>
            ) : (
              <div style={{background:'rgba(180,127,255,0.08)',border:'1px solid rgba(180,127,255,0.2)',borderRadius:12,padding:'0.85rem',fontSize:'0.85rem',color:'var(--muted)',marginTop:'0.5rem'}}>
                🔒 {lang==='bg'?'Надгради до Elite за да видиш пълното AI описание на твоята пътническа личност.':lang==='es'?'Mejora a Elite para desbloquear la descripción completa de tu personalidad viajera generada por IA.':lang==='fr'?'Passez à Elite pour débloquer la description complète de votre personnalité de voyageur générée par l\'IA.':lang==='de'?'Upgrade auf Elite, um die vollständige KI-Beschreibung deiner Reisepersönlichkeit freizuschalten.':lang==='el'?'Αναβάθμισε σε Elite για να ξεκλειδώσεις την πλήρη περιγραφή AI της ταξιδιωτικής σου προσωπικότητας.':lang==='it'?'Passa a Elite per sbloccare la descrizione IA completa della tua personalità di viaggio.':'Upgrade to Elite to unlock the full AI description of your travel personality.'}
              </div>
            )}
            <button className="ts-btn-ghost-sm" style={{marginTop:'1.25rem'}} onClick={()=>{setStep(0);setAnswers([]);setResult(null);setSaved(false);}}>
              ↩ {lang==='bg'?'Повтори теста':lang==='es'?'Repetir el test':lang==='fr'?'Refaire le test':lang==='de'?'Test wiederholen':lang==='el'?'Επανάληψη τεστ':lang==='it'?'Ripeti il test':'Retake quiz'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Love Story PDF generator ─────────────────────────────────────────────────
async function generateLoveStoryPDF(capsuleData, userProfile, matchProfile, lang, extraPhotos = []) {
  const { jsPDF } = await import('jspdf');
  const html2canvas = (await import('html2canvas')).default;

  const myDisplayName = userProfile?.name || [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'Ти';
  const matchDisplayName = matchProfile?.name || [matchProfile?.first_name, matchProfile?.last_name].filter(Boolean).join(' ') || 'Partner';

  async function toBase64(url) {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  }
  const userPhotoUrl = userProfile?.avatar_url || userProfile?.photo || getProfilePhotoUrl(userProfile?.photo_url, null);
  const matchPhotoUrl = matchProfile?.avatar_url || matchProfile?.photo || getProfilePhotoUrl(matchProfile?.photo_url, null);
  const userPhotoB64 = userPhotoUrl ? await toBase64(userPhotoUrl) : null;
  const matchPhotoB64 = matchPhotoUrl ? await toBase64(matchPhotoUrl) : null;

  // Preload Google Fonts so html2canvas can render them
  if (!document.getElementById('ts-pdf-fonts')) {
    const fontLink = document.createElement('link');
    fontLink.id = 'ts-pdf-fonts';
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;600;700&family=Sacramento&family=Pacifico&display=swap';
    document.head.appendChild(fontLink);
    // Wait for fonts to load
    await new Promise(resolve => setTimeout(resolve, 2000));
  } else {
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  const div = document.createElement('div');
  div.style.cssText = 'position:fixed;left:-9999px;top:0;width:794px;background:#fff;';

  const msgColors = ['#c0392b','#8e44ad','#2980b9','#27ae60','#e67e22','#d81b60','#1565c0','#2e7d32','#6a1b9a','#ad1457'];
  const seen = new Set();
  const cleanMsgs = (capsuleData?.messages||[])
    .filter(msg => { const txt=msg.content||''; if(!txt||txt.startsWith('http')||seen.has(txt)) return false; seen.add(txt); return true; })
    .slice(0,10);

  const userAvatarHTML = userPhotoB64
    ? '<div style="width:108px;height:108px;border-radius:50%;background-image:url(' + userPhotoB64 + ');background-size:cover;background-position:center;background-repeat:no-repeat;"></div>'
    : '<div style="width:108px;height:108px;border-radius:50%;background:linear-gradient(135deg,#ff6b6b,#ff8e53);display:flex;align-items:center;justify-content:center;font-size:40px;font-weight:900;color:#fff;">' + escapeHtml((myDisplayName||'U')[0].toUpperCase()) + '</div>';

  const matchAvatarHTML = matchPhotoB64
    ? '<div style="width:108px;height:108px;border-radius:50%;background-image:url(' + matchPhotoB64 + ');background-size:cover;background-position:center;background-repeat:no-repeat;"></div>'
    : '<div style="width:108px;height:108px;border-radius:50%;background:linear-gradient(135deg,#ffa500,#ff6b6b);display:flex;align-items:center;justify-content:center;font-size:40px;font-weight:900;color:#fff;">' + escapeHtml((matchDisplayName||'P')[0].toUpperCase()) + '</div>';

  div.innerHTML = `
  <div style="width:794px;background:linear-gradient(160deg,#fff9f5 0%,#fff0f8 50%,#f5f0ff 100%);font-family:'Dancing Script',Georgia,cursive;position:relative;">

    <!-- BORDER -->
    <div style="position:absolute;top:0;left:0;right:0;bottom:0;border:10px solid #ff6b6b;pointer-events:none;z-index:10;box-sizing:border-box;"></div>
    <div style="position:absolute;top:14px;left:14px;right:14px;bottom:14px;border:2px dashed rgba(255,107,107,0.4);pointer-events:none;z-index:10;"></div>

    <!-- CORNER ROSES -->
    <div style="position:absolute;top:6px;left:6px;font-size:24px;z-index:11;line-height:1;">🌹</div>
    <div style="position:absolute;top:6px;right:6px;font-size:24px;z-index:11;line-height:1;">🌹</div>

    <!-- HEART BACKGROUND -->
    <div style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;opacity:0.04;font-size:20px;line-height:36px;overflow:hidden;word-break:break-all;">
      💕⭐💗✨💖🌟💓⭐💝✨💕🌟💗⭐💖✨💓🌟💝⭐💕✨💗🌟💖⭐💓✨💝🌟💕⭐💗✨💖🌟💓⭐💝✨💕🌟💗⭐💖✨💓🌟💝⭐💕✨💗🌟💖⭐💓✨💝🌟💕⭐💗✨💖🌟💓⭐💝✨💕🌟💗⭐💖✨💓🌟💝⭐💕✨💗🌟💖⭐💓✨💝🌟💕⭐💗✨💖🌟💓⭐💝✨💕🌟💗⭐💖✨💓🌟💝⭐💕✨💗🌟💖⭐💓✨💝🌟
    </div>

    <div style="padding:48px 52px;position:relative;">

      <!-- HEADER -->
      <div style="text-align:center;margin-bottom:22px;">
        <div style="font-size:34px;font-weight:900;color:#e84393;letter-spacing:2px;">✈ TravelSoul</div>
        <div style="font-size:10px;color:#c9a;letter-spacing:5px;text-transform:uppercase;margin-top:4px;">travelsoul.love</div>
        <div style="font-size:16px;margin-top:8px;letter-spacing:6px;">💕 ⭐ 💕 ⭐ 💕</div>
      </div>

      <!-- DIVIDER -->
      <div style="text-align:center;font-size:18px;margin-bottom:18px;letter-spacing:8px;color:#ff6b6b;">～～～～～～～～～～</div>

      <!-- TITLE -->
      <div style="text-align:center;margin-bottom:28px;">
        <div style="font-size:66px;color:#c0392b;line-height:1.1;font-weight:700;">
          ${lang==='bg'?'Нашата История':lang==='es'?'Nuestra Historia':lang==='fr'?'Notre Histoire':lang==='de'?'Unsere Geschichte':lang==='el'?'Η ιστορία μας':lang==='it'?'La nostra storia':'Our Story'}
        </div>
        <div style="width:180px;height:4px;background:linear-gradient(90deg,transparent,#ff6b6b,#ffa500,#ff6b6b,transparent);margin:10px auto;border-radius:2px;"></div>
        <div style="font-size:22px;color:#888;font-style:italic;">${escapeHtml(myDisplayName)} &amp; ${escapeHtml(matchDisplayName)}</div>
        <div style="font-size:16px;margin-top:8px;letter-spacing:4px;">🌹 💕 💍 💕 🌹</div>
      </div>

      <!-- DIVIDER -->
      <div style="text-align:center;font-size:18px;margin-bottom:22px;letter-spacing:8px;color:#8e44ad;">～～～～～～～～～～</div>

      <!-- AVATARS -->
      <div style="display:flex;justify-content:center;align-items:center;gap:32px;margin-bottom:28px;">
        <div style="text-align:center;">
          <div id="pdf-user-avatar" style="width:116px;height:116px;border-radius:50%;padding:4px;display:inline-block;box-sizing:border-box;background:linear-gradient(135deg,#ff6b6b,#ffa500);">
            ${userAvatarHTML}
          </div>
          <div style="font-size:22px;color:#c0392b;margin-top:8px;font-weight:700;">${escapeHtml(myDisplayName)}</div>
          <div style="font-size:12px;color:#bbb;margin-top:2px;">📍 ${userProfile?.city?escapeHtml(userProfile.city)+', ':''}${escapeHtml(userProfile?.country||'')}</div>
        </div>

        <div style="text-align:center;padding:0 16px;">
          <div style="font-size:12px;color:#aaa;margin-bottom:8px;font-style:italic;">${lang==='bg'?'завинаги':lang==='es'?'para siempre':lang==='fr'?'pour toujours':lang==='de'?'für immer':lang==='el'?'για πάντα':lang==='it'?'per sempre':'forever'}</div>
          <div style="font-size:26px;line-height:1.6;">🕊️</div>
          <div style="font-size:24px;line-height:1.6;">💍</div>
          <div style="font-size:28px;line-height:1.6;">💕</div>
          <div style="font-size:24px;line-height:1.6;">💍</div>
          <div style="font-size:26px;line-height:1.6;">🕊️</div>
        </div>

        <div style="text-align:center;">
          <div id="pdf-match-avatar" style="width:116px;height:116px;border-radius:50%;padding:4px;display:inline-block;box-sizing:border-box;background:linear-gradient(135deg,#ffa500,#e84393);">
            ${matchAvatarHTML}
          </div>
          <div style="font-size:22px;color:#e67e22;margin-top:8px;font-weight:700;">${escapeHtml(matchDisplayName)}</div>
          <div style="font-size:12px;color:#bbb;margin-top:2px;">📍 ${matchProfile?.city?escapeHtml(matchProfile.city)+', ':''}${escapeHtml(matchProfile?.country||'')}</div>
        </div>
      </div>

      <!-- STATS -->
      <div style="display:flex;justify-content:center;gap:14px;margin-bottom:26px;">
        <div style="background:rgba(255,107,107,0.1);border:2px solid rgba(255,107,107,0.35);border-radius:16px;padding:13px 18px;text-align:center;min-width:88px;">
          <div style="font-size:20px;margin-bottom:3px;">📅</div>
          <div style="font-size:19px;font-weight:700;color:#ff6b6b;">${escapeHtml(capsuleData?.matchDate||'—')}</div>
          <div style="font-size:9px;color:#aaa;letter-spacing:1px;text-transform:uppercase;margin-top:2px;">${lang==='bg'?'Дата на среща':lang==='es'?'Fecha':lang==='fr'?'Date':lang==='de'?'Datum':lang==='el'?'Ημερομηνία':lang==='it'?'Data':'Met on'}</div>
        </div>
        <div style="background:rgba(255,165,0,0.1);border:2px solid rgba(255,165,0,0.35);border-radius:16px;padding:13px 18px;text-align:center;min-width:88px;">
          <div style="font-size:20px;margin-bottom:3px;">💌</div>
          <div style="font-size:19px;font-weight:700;color:#ffa500;">${capsuleData?.messages?.length||0}</div>
          <div style="font-size:9px;color:#aaa;letter-spacing:1px;text-transform:uppercase;margin-top:2px;">${lang==='bg'?'Съобщения':lang==='es'?'Mensajes':lang==='fr'?'Messages':lang==='de'?'Nachrichten':lang==='el'?'Μηνύματα':lang==='it'?'Messaggi':'Messages'}</div>
        </div>
        <div style="background:rgba(142,68,173,0.1);border:2px solid rgba(142,68,173,0.35);border-radius:16px;padding:13px 18px;text-align:center;min-width:88px;">
          <div style="font-size:20px;margin-bottom:3px;">💑</div>
          <div style="font-size:19px;font-weight:700;color:#8e44ad;">${escapeHtml(capsuleData?.daysTogether||'—')}</div>
          <div style="font-size:9px;color:#aaa;letter-spacing:1px;text-transform:uppercase;margin-top:2px;">${lang==='bg'?'Дни заедно':lang==='es'?'Días juntos':lang==='fr'?'Jours ensemble':lang==='de'?'Tage zusammen':lang==='el'?'Μέρες μαζί':lang==='it'?'Giorni insieme':'Days together'}</div>
        </div>
      </div>

      <!-- TIMELINE -->
      <div style="margin-bottom:26px;padding:18px;background:rgba(255,255,255,0.8);border-radius:18px;border:1px solid rgba(255,107,107,0.2);">
        <div style="font-size:22px;color:#c0392b;text-align:center;margin-bottom:14px;font-weight:700;">
          🌟 ${lang==='bg'?'Нашият път заедно':lang==='es'?'Nuestro camino':lang==='fr'?'Notre chemin':lang==='de'?'Unser Weg':lang==='el'?'Η διαδρομή μας':lang==='it'?'Il nostro percorso':'Our journey'} 🌟
        </div>
        <div style="display:flex;align-items:center;justify-content:center;">
          <div style="text-align:center;min-width:90px;">
            <div style="font-size:26px;">💫</div>
            <div style="font-size:13px;color:#ff6b6b;font-weight:700;">${lang==='bg'?'Среща':lang==='es'?'Encuentro':lang==='fr'?'Rencontre':lang==='de'?'Begegnung':lang==='el'?'Γνωριμία':lang==='it'?'Incontro':'First met'}</div>
            <div style="font-size:10px;color:#bbb;">${escapeHtml(capsuleData?.matchDate||'—')}</div>
          </div>
          <div style="flex:1;height:3px;background:linear-gradient(90deg,#ff6b6b,#ffa500);"></div>
          <div style="text-align:center;min-width:90px;">
            <div style="font-size:26px;">💕</div>
            <div style="font-size:13px;color:#ffa500;font-weight:700;">${lang==='bg'?'Влюбване':lang==='es'?'Amor':lang==='fr'?'Amour':lang==='de'?'Verliebt':lang==='el'?'Ερωτευμένοι':lang==='it'?'Innamorati':'In love'}</div>
          </div>
          <div style="flex:1;height:3px;background:linear-gradient(90deg,#ffa500,#8e44ad);"></div>
          <div style="text-align:center;min-width:90px;">
            <div style="font-size:26px;">🌍</div>
            <div style="font-size:13px;color:#8e44ad;font-weight:700;">${lang==='bg'?'Мечта':lang==='es'?'Sueño':lang==='fr'?'Rêve':lang==='de'?'Traum':lang==='el'?'Όνειρο':lang==='it'?'Sogno':'Dream'}</div>
            <div style="font-size:10px;color:#bbb;">${escapeHtml(capsuleData?.pactDestination||'∞')}</div>
          </div>
          <div style="flex:1;height:3px;background:linear-gradient(90deg,#8e44ad,#e84393);"></div>
          <div style="text-align:center;min-width:90px;">
            <div style="font-size:26px;">💍</div>
            <div style="font-size:13px;color:#e84393;font-weight:700;">${lang==='bg'?'Завинаги':lang==='es'?'Siempre':lang==='fr'?'Toujours':lang==='de'?'Immer':lang==='el'?'Για πάντα':lang==='it'?'Sempre':'Forever'}</div>
          </div>
        </div>
      </div>

      <!-- DIVIDER -->
      <div style="text-align:center;font-size:18px;margin-bottom:22px;letter-spacing:6px;color:#e84393;">～ 💕 ～ 💕 ～ 💕 ～</div>

      <!-- DESTINATION -->
      ${capsuleData?.pactDestination?`
      <div style="background:linear-gradient(135deg,#ff6b6b,#ffa500);border-radius:20px;padding:22px;margin-bottom:26px;text-align:center;color:#fff;">
        <div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;opacity:0.9;">✈️ ${lang==='bg'?'НАШАТА ДЕСТИНАЦИЯ':lang==='es'?'NUESTRO DESTINO':lang==='fr'?'NOTRE DESTINATION':lang==='de'?'UNSER ZIEL':lang==='el'?'Ο ΣΤΟΧΟΣ ΜΑΣ':lang==='it'?'LA NOSTRA META':'OUR DESTINATION'} ✈️</div>
        <div style="font-size:50px;font-weight:700;margin-top:6px;">${escapeHtml(capsuleData.pactDestination)}</div>
        <div style="font-size:16px;margin-top:8px;opacity:0.9;">🌍 💕 ✈️ 💕 🌍</div>
      </div>`:''}

      <!-- DIVIDER -->
      <div style="text-align:center;font-size:18px;margin-bottom:22px;letter-spacing:6px;color:#2980b9;">～ ⭐ ～ ⭐ ～ ⭐ ～</div>

      <!-- MESSAGES -->
      <div style="margin-bottom:26px;">
        <div style="font-size:38px;color:#c0392b;text-align:center;margin-bottom:18px;font-weight:700;">
          💌 ${lang==='bg'?'Нашите думи':lang==='es'?'Nuestras palabras':lang==='fr'?'Nos mots':lang==='de'?'Unsere Worte':lang==='el'?'Τα λόγια μας':lang==='it'?'Le nostre parole':'Our words'}
        </div>
        ${cleanMsgs.map((msg,i)=>`
          <div style="margin-bottom:16px;padding:14px 18px;background:#fff;border-radius:14px;
            box-shadow:0 3px 12px rgba(0,0,0,0.08);
            border-left:5px solid ${msgColors[i%msgColors.length]};
            transform:rotate(${i%2===0?'-0.3':'0.3'}deg);">
            <div style="font-size:10px;color:#ccc;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">
              ${escapeHtml(msg.senderName)} · ${msg.created_at?new Date(msg.created_at).toLocaleDateString():''}
            </div>
            <div style="font-size:19px;color:${msgColors[i%msgColors.length]};line-height:1.5;font-style:italic;">
              "${escapeHtml(msg.content)}"
            </div>
          </div>
        `).join('')}
      </div>

      <!-- EXTRA PHOTOS -->
      ${extraPhotos.length>0?`
      <div style="margin-bottom:26px;">
        <div style="font-size:38px;color:#8e44ad;text-align:center;margin-bottom:18px;font-weight:700;">
          📸 ${lang==='bg'?'Нашите моменти':lang==='es'?'Nuestros momentos':lang==='fr'?'Nos moments':lang==='de'?'Unsere Momente':lang==='el'?'Οι στιγμές μας':lang==='it'?'I nostri momenti':'Our moments'}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:12px;justify-content:center;">
          ${extraPhotos.map((photo,i)=>`
            <img src="${photo}" style="width:210px;height:158px;object-fit:cover;border-radius:14px;
              box-shadow:0 5px 20px rgba(0,0,0,0.15);border:3px solid #fff;
              transform:rotate(${((i%3)-1)*2.5}deg);display:block;"/>
          `).join('')}
        </div>
      </div>`:''}

      <!-- DIVIDER -->
      <div style="text-align:center;font-size:18px;margin-bottom:26px;letter-spacing:6px;color:#27ae60;">～ 🌹 ～ 🌹 ～ 🌹 ～</div>

      <!-- CHAMPAGNE FINALE -->
      <div style="text-align:center;padding:32px 20px;background:linear-gradient(135deg,rgba(255,215,0,0.12),rgba(255,107,107,0.12),rgba(142,68,173,0.12));border-radius:24px;margin-bottom:24px;border:2px solid rgba(255,215,0,0.4);">
        <div style="font-size:16px;letter-spacing:4px;margin-bottom:8px;">🌟 ✨ 💫 ⭐ 🌟 ✨ 💫 ⭐ 🌟</div>
        <div style="font-size:48px;margin-bottom:4px;">🎆</div>
        <div style="font-size:32px;margin-bottom:4px;letter-spacing:8px;">🎇 🎆 🎇</div>
        <div style="font-size:48px;margin-bottom:10px;">🎆</div>
        <div style="font-size:16px;margin-bottom:14px;letter-spacing:4px;">✨ 💫 🌟 ⭐ ✨ 💫 🌟 ⭐ ✨</div>
        <div style="font-size:66px;line-height:1;margin-bottom:10px;">🥂</div>
        <div style="font-size:22px;margin-bottom:18px;letter-spacing:6px;">💕 💗 💖 💓 💝</div>
        <div style="font-size:22px;color:#c0392b;margin-bottom:10px;font-weight:700;line-height:1.4;">
          💕 ${lang==='bg'?'За вас двамата — за любов, смях и незабравими пътешествия!':lang==='es'?'¡Para vosotros — amor, risas y viajes inolvidables!':lang==='fr'?'Pour vous deux — amour, rires et voyages inoubliables!':lang==='de'?'Für euch beide — Liebe, Lachen und Reisen!':lang==='el'?'Για εσάς τους δύο — αγάπη, γέλιο και ταξίδια!':lang==='it'?'Per voi due — amore, risate e viaggi!':'For you two — love, laughter and unforgettable journeys!'} 💕
        </div>
        <div style="font-size:19px;color:#8e44ad;margin-bottom:8px;">
          🌹 ${lang==='bg'?'Нека всеки ден е ново приключение заедно...':lang==='es'?'Que cada día sea una nueva aventura juntos...':lang==='fr'?'Que chaque jour soit une nouvelle aventure...':lang==='de'?'Möge jeder Tag ein Abenteuer sein...':lang==='el'?'Είθε κάθε μέρα να είναι μια περιπέτεια...':lang==='it'?'Che ogni giorno sia un\'avventura...':'May every day be a new adventure together...'} 🌹
        </div>
        <div style="font-size:19px;color:#2980b9;margin-bottom:8px;">
          ✈️ ${lang==='bg'?'Светът е по-красив, когато го откриваш с любовта си!':lang==='es'?'¡El mundo es más bello con tu amor!':lang==='fr'?'Le monde est plus beau avec son amour!':lang==='de'?'Die Welt ist schöner mit deiner Liebe!':lang==='el'?'Ο κόσμος είναι πιο όμορφος με την αγάπη σου!':lang==='it'?'Il mondo è più bello con il tuo amore!':'The world is more beautiful with your love!'} ✈️
        </div>
        <div style="font-size:19px;color:#27ae60;margin-bottom:8px;">
          🌍 ${lang==='bg'?'Открийте заедно всички чудеса на света!':lang==='es'?'¡Descubrid juntos las maravillas del mundo!':lang==='fr'?'Découvrez ensemble les merveilles du monde!':lang==='de'?'Entdeckt gemeinsam die Wunder der Welt!':lang==='el'?'Ανακαλύψτε μαζί τα θαύματα του κόσμου!':lang==='it'?'Scoprite insieme le meraviglie del mondo!':'Discover together all the wonders of the world!'} 🌍
        </div>
        <div style="font-size:20px;color:#e84393;font-weight:700;">
          💍 ${lang==='bg'?'Да бъдете щастливи завинаги!':lang==='es'?'¡Felices para siempre!':lang==='fr'?'Heureux pour toujours!':lang==='de'?'Für immer glücklich!':lang==='el'?'Ευτυχισμένοι για πάντα!':lang==='it'?'Felici per sempre!':'Live happily ever after!'} 💍
        </div>
        <div style="font-size:24px;margin-top:16px;letter-spacing:4px;">💕 💗 💖 💓 💝 💕 💗 💖 💓 💝</div>
      </div>

      <!-- FOOTER -->
      <div style="text-align:center;padding-top:14px;border-top:3px dashed rgba(255,107,107,0.3);">
        <div style="font-size:14px;margin-bottom:6px;letter-spacing:4px;">🌹 💕 ✈️ 💕 🌹</div>
        <div style="font-size:15px;color:#bbb;font-style:italic;">
          ${lang==='bg'?'Създадено с любов от TravelSoul':lang==='es'?'Hecho con amor por TravelSoul':lang==='fr'?'Fait avec amour par TravelSoul':lang==='de'?'Mit Liebe von TravelSoul':lang==='el'?'Με αγάπη από το TravelSoul':lang==='it'?'Con amore da TravelSoul':'Made with love by TravelSoul'} · travelsoul.love
        </div>
        <div style="font-size:12px;margin-top:6px;opacity:0.4;letter-spacing:4px;">💕 💗 💖 💓 💝</div>
        <div style="position:absolute;bottom:6px;left:6px;font-size:24px;line-height:1;">🌹</div>
        <div style="position:absolute;bottom:6px;right:6px;font-size:24px;line-height:1;">🌹</div>
      </div>

    </div>
  </div>
  `;

  document.body.appendChild(div);
  try {
    // Step 1: Render layout to canvas
    const layoutCanvas = await html2canvas(div, {
      scale:2, useCORS:false, allowTaint:false,
      backgroundColor:'#fff9f0',
      logging: false
    });

    // Step 2: Create final canvas and copy layout
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = layoutCanvas.width;
    finalCanvas.height = layoutCanvas.height;
    const ctx = finalCanvas.getContext('2d');
    ctx.drawImage(layoutCanvas, 0, 0);

    // Step 3: Draw circular photos on final canvas
    async function drawPhoto(b64, cx, cy, r) {
      if (!b64) return;
      return new Promise(resolve => {
        const img = new window.Image();
        img.onload = () => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
          ctx.restore();
          resolve();
        };
        img.onerror = () => resolve();
        img.src = b64;
      });
    }

    const sc = 2;
    const r = 58 * sc;
    // Get positions dynamically from DOM
    const userAvatarEl = div.querySelector('#pdf-user-avatar');
    const matchAvatarEl = div.querySelector('#pdf-match-avatar');
    const userCX = userAvatarEl ? (userAvatarEl.offsetLeft + 58) * sc : 476;
    const userCY = userAvatarEl ? (userAvatarEl.offsetTop + 58) * sc : 1062;
    const matchCX = matchAvatarEl ? (matchAvatarEl.offsetLeft + 58) * sc : 1068;
    const matchCY = matchAvatarEl ? (matchAvatarEl.offsetTop + 58) * sc : 1062;
    await drawPhoto(userPhotoB64, userCX, userCY, r);
    await drawPhoto(matchPhotoB64, matchCX, matchCY, r);

    // Single long page - no page breaks, no black lines
    const pageWidth = 210; // A4 width in mm
    const imgHeight = (finalCanvas.height * pageWidth) / finalCanvas.width;
    const imgData = finalCanvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pageWidth, imgHeight]  // custom height = full content
    });
    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, imgHeight);
    await saveOrSharePdf(pdf, 'TravelSoul-LoveStory-' + (myDisplayName||'us') + '.pdf');
  } finally {
    document.body.removeChild(div);
  }
}

// ── CapsuleModal ──────────────────────────────────────────────────────────────
function CapsuleModal({ pact, myProfile, profile, lang, plan, t, onClose }) {
  const [capsuleId, setCapsuleId] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [partnerEntries, setPartnerEntries] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfPhotos, setPdfPhotos] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const SUPA_URL = process.env.REACT_APP_SUPABASE_URL;
  const { userId } = getAuthToken();

  async function fetchEntries(cid) {
    let entries;
    try { entries = await sbFetch('GET', 'capsule_entries', `capsule_id=eq.${cid}&order=created_at.asc`); } catch (e) { return; }
    if (Array.isArray(entries)) {
      setMyEntries(entries.filter(e => e.author_id === userId));
      setPartnerEntries(entries.filter(e => e.author_id !== userId));
    }
  }

  useEffect(() => {
    if (!pact?.id || !userId) { setLoading(false); return; }
    async function loadCapsule() {
      setLoading(true);
      try {
        // 1. Try to fetch existing capsule
        const partnerId = pact.sender_id === userId ? pact.receiver_id : pact.sender_id;
        let rows = await sbFetch('GET', 'capsules', `pact_id=eq.${pact.id}&limit=1`);
        // Fallback: find by user IDs (receiver opening capsule created by sender)
        if (!Array.isArray(rows) || !rows[0]) {
          rows = await sbFetch('GET', 'capsules',
            `or=(and(user1_id.eq.${userId},user2_id.eq.${partnerId}),and(user1_id.eq.${partnerId},user2_id.eq.${userId}))&limit=1`
          );
        }

        let capsule = Array.isArray(rows) && rows[0] ? rows[0] : null;
        // 2. Create if absent — always fetch after INSERT, never trust return value
        if (!capsule) {
          await sbFetch('POST', 'capsules', '', {
            pact_id: pact.id,
            user1_id: userId,
            user2_id: pact.sender_id === userId ? pact.receiver_id : pact.sender_id,
            unlock_date: new Date(Date.now() + 180*24*60*60*1000).toISOString(),
            status: 'locked',
          }, { Prefer: 'return=minimal' });
          let retry;
          try { retry = await sbFetch('GET', 'capsules', `pact_id=eq.${pact.id}&limit=1`); } catch (e) { retry = []; }
          capsule = Array.isArray(retry) && retry[0] ? retry[0] : null;
        }
        if (!capsule?.id) { setLoading(false); return; }
        setCapsuleId(capsule.id);
        await fetchEntries(capsule.id);
      } catch(e) {}
      setLoading(false);
    }
    loadCapsule();
  }, [pact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!capsuleId) return;
    const interval = setInterval(() => {
      fetchEntries(capsuleId);
    }, 15000);
    return () => clearInterval(interval);
  }, [capsuleId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function sendMsg() {
    if (!text.trim() || !capsuleId) return;
    if (plan !== 'elite' && myEntries.length >= 5) return;
    try {
      await sbFetch('POST', 'capsule_entries', '', {
        capsule_id: capsuleId,
        author_id: userId,
        type: 'text',
        content: text.trim(),
      }, { Prefer: 'return=minimal' });
      setText('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await fetchEntries(capsuleId);
    } catch(e) {}
  }

  async function uploadMedia(file, type) {
    if (!file || !capsuleId) return;
    if (type === 'photo' && !isAllowedImageFile(file)) {
      alert(lang==='bg'?'Само JPEG/PNG/WebP снимки до 10MB':lang==='es'?'Solo se permiten imágenes JPEG/PNG/WebP de hasta 10MB':lang==='fr'?'Seules les images JPEG/PNG/WebP jusqu\'à 10 Mo sont autorisées':lang==='de'?'Nur JPEG/PNG/WebP-Bilder bis 10MB sind erlaubt':lang==='el'?'Επιτρέπονται μόνο εικόνες JPEG/PNG/WebP έως 10MB':lang==='it'?'Sono consentite solo immagini JPEG/PNG/WebP fino a 10MB':'Only JPEG/PNG/WebP images up to 10MB are allowed');
      return;
    }
    if (type === 'video' && file.size > 30 * 1024 * 1024) {
      alert(lang==='bg'?'Максимум 30MB за видео':lang==='es'?'Máximo 30MB para video':lang==='fr'?'Maximum 30 Mo pour la vidéo':lang==='de'?'Maximal 30MB für Video':lang==='el'?'Μέγιστο 30MB για βίντεο':lang==='it'?'Massimo 30MB per i video':'Max 30MB for video');
      return;
    }
    setUploading(true);
    try {
      const { accessToken } = getAuthToken();
      const ext = file.name.split('.').pop().toLowerCase();
      const filePath = `capsule-media/${capsuleId}/${userId}_${Date.now()}.${ext}`;
      const res = await fetch(`${SUPA_URL}/storage/v1/object/${filePath}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': file.type,
        },
        body: file,
      });
      if (res.ok) {
        const publicUrl = `${SUPA_URL}/storage/v1/object/public/${filePath}`;
        await sbFetch('POST', 'capsule_entries', '', {
          capsule_id: capsuleId,
          author_id: userId,
          type,
          content: publicUrl,
        }, { Prefer: 'return=minimal' });
        await fetchEntries(capsuleId);
      }
    } catch(e) { console.error('upload error', e); }
    setUploading(false);
  }

  const maxReached = plan !== 'elite' && myEntries.length >= 5;

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.9)',zIndex:10001,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{background:'#0d1117',border:'1px solid #c9a84c',boxShadow:'0 0 12px rgba(201,168,76,0.3)',borderRadius:20,padding:'28px 22px',width:'100%',maxWidth:400,maxHeight:'80vh',overflowY:'auto',position:'relative'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <div style={{textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:'2rem'}}>🫙</div>
          <h3 className="ts-serif" style={{margin:'6px 0 2px'}}>{t?.pact?.capsuleTitle || (lang==='bg'?'Времева Капсула':lang==='es'?'Cápsula de Viaje':lang==='fr'?'Capsule de Voyage':lang==='de'?'Reisekapsel':lang==='el'?'Ταξιδιωτική κάψουλα':lang==='it'?'Capsula di viaggio':'Travel Capsule')}</h3>
          <p style={{fontSize:'0.75rem',color:'var(--muted)',margin:0}}>{pact?.destination}{pact?.target_month ? ` · ${pact.target_month}` : ''}</p>
        </div>

        {loading ? (
          <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.82rem',padding:'12px 0'}}>⏳ …</p>
        ) : (
          <>
            {/* ── Download PDF ─────────────────────────────────────── */}
            <button
              disabled={pdfLoading}
              onClick={async () => {
                setPdfLoading(true);
                try {
                  const lsPhoto = localStorage.getItem('travelsoul_photo');
                  const partnerId = pact?.sender_id === userId ? pact?.receiver_id : pact?.sender_id;
                  let freshPartnerData = profile || {};
                  if (partnerId && supabase) {
                    const { data: fp } = await supabase
                      .from('profiles')
                      .select('id, name, photo_url, city, country')
                      .eq('id', partnerId)
                      .single();
                    if (fp) freshPartnerData = fp;
                  }
                  const userForPDF = {
                    ...myProfile,
                    name: [myProfile?.first_name, myProfile?.last_name].filter(Boolean).join(' ').trim() || myProfile?.name || myProfile?.full_name || '',
                    photo: myProfile?.avatar_url || myProfile?.photo || getProfilePhotoUrl(myProfile?.photo_url, lsPhoto || null),
                  };
                  const partnerForPDF = {
                    ...freshPartnerData,
                    name: [freshPartnerData?.first_name, freshPartnerData?.last_name].filter(Boolean).join(' ').trim() || freshPartnerData?.name || profile?.name || '',
                    photo: freshPartnerData?.avatar_url || freshPartnerData?.photo || getProfilePhotoUrl(freshPartnerData?.photo_url, profile?.photo || null),
                    city: freshPartnerData?.city || profile?.city || '',
                    country: freshPartnerData?.country || profile?.country || '',
                    flag: profile?.flag || '',
                  };
                  const myName = userForPDF.name;
                  const partnerName = partnerForPDF.name;
                  const allMessages = [
                    ...myEntries.map(e => ({ ...e, isOwn: true, senderName: myName })),
                    ...partnerEntries.map(e => ({ ...e, isOwn: false, senderName: partnerName })),
                  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                  await generateLoveStoryPDF({
                    matchDate: pact?.created_at ? new Date(pact.created_at).toLocaleDateString() : '—',
                    messages: allMessages,
                    pactDestination: pact?.destination || '',
                    daysTogether: pact?.created_at ? Math.floor((Date.now() - new Date(pact.created_at)) / (24*60*60*1000)) : 0,
                  }, userForPDF, partnerForPDF, lang, pdfPhotos);
                } finally {
                  setPdfLoading(false);
                }
              }}
              style={{
                display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
                background: pdfLoading ? 'rgba(255,107,107,0.5)' : 'linear-gradient(135deg,#ff6b6b,#ffa500)',
                color:'#fff', border:'none', borderRadius:'12px',
                padding:'12px 20px', fontSize:'14px', fontWeight:'700',
                cursor: pdfLoading ? 'not-allowed' : 'pointer',
                margin:'0 0 12px', width:'100%', boxSizing:'border-box',
                boxShadow: pdfLoading ? 'none' : '0 4px 15px rgba(255,107,107,0.4)',
                transition:'all 0.3s'
              }}
            >
              {pdfLoading ? (
                <>
                  <span style={{
                    display:'inline-block', width:'16px', height:'16px',
                    border:'2px solid rgba(255,255,255,0.3)',
                    borderTop:'2px solid #fff',
                    borderRadius:'50%',
                    animation:'spin 1s linear infinite'
                  }}/>
                  {lang==='bg'?'Генерира се...':lang==='es'?'Generando...':lang==='fr'?'Génération...':lang==='de'?'Wird erstellt...':lang==='el'?'Δημιουργείται...':lang==='it'?'Creazione in corso...':'Generating...'}
                </>
              ) : (
                <>💌 {t?.messages?.downloadLoveStory || (lang==='bg'?'Свали Нашата История':lang==='es'?'Descargar Nuestra Historia':lang==='fr'?'Télécharger Notre Histoire':lang==='de'?'Unsere Geschichte herunterladen':lang==='el'?'Λήψη της ιστορίας μας':lang==='it'?'Scarica la nostra storia':'Download Our Story')}</>
              )}
            </button>
            <label style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',
              background:'rgba(255,255,255,0.07)',border:'2px dashed rgba(255,107,107,0.4)',
              borderRadius:'12px',padding:'9px 16px',cursor:'pointer',
              fontSize:'12px',color:'#ffb3b3',fontWeight:'600',margin:'0 0 12px',width:'100%',boxSizing:'border-box'}}>
              📸 {lang==='bg'?'Добави снимки към PDF':lang==='en'?'Add photos to PDF':lang==='es'?'Añadir fotos al PDF':lang==='fr'?'Ajouter des photos':lang==='de'?'Fotos hinzufügen':lang==='el'?'Προσθήκη φωτογραφιών':lang==='it'?'Aggiungi foto':'Add photos to PDF'}
              {pdfPhotos.length>0 && <span style={{marginLeft:'6px',fontSize:'11px',color:'#aaa'}}>✅ {pdfPhotos.length}</span>}
              <input type="file" accept="image/*" multiple style={{display:'none'}}
                onChange={e=>{
                  const files=Array.from(e.target.files);
                  Promise.all(files.map(f=>new Promise(res=>{const r=new FileReader();r.onload=()=>res(r.result);r.readAsDataURL(f);}))).then(newPhotos=>{
                    setPdfPhotos(prev=>[...prev, ...newPhotos].slice(0,6));
                  });
                }}
              />
            </label>
            {/* ── My messages ─────────────────────────────────────── */}
            <p style={{fontSize:'0.68rem',color:'#8b949e',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>
              {lang==='bg'?'Моите съобщения':lang==='es'?'Mis mensajes':lang==='fr'?'Mes messages':lang==='de'?'Meine Nachrichten':lang==='el'?'Τα μηνύματά μου':lang==='it'?'I miei messaggi':'My messages'}
            </p>
            <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,padding:12,marginBottom:12,minHeight:56,maxHeight:160,overflowY:'auto'}}>
              {myEntries.length === 0 ? (
                <p style={{color:'var(--muted)',fontSize:'0.8rem',textAlign:'center',margin:0}}>{lang==='bg'?'Все още няма съобщения…':lang==='es'?'Aún no hay mensajes…':lang==='fr'?'Pas encore de messages…':lang==='de'?'Noch keine Nachrichten…':lang==='el'?'Δεν υπάρχουν μηνύματα ακόμα…':lang==='it'?'Ancora nessun messaggio…':'No messages yet…'}</p>
              ) : myEntries.map((e,i)=>(
                <div key={e.id||i} style={{marginBottom:8}}>
                  {e.type === 'photo' && <img src={e.content} onClick={()=>openExternalUrl(e.content)} style={{width:'120px',height:'90px',objectFit:'cover',borderRadius:8,cursor:'pointer'}} alt="" />}
                  {e.type === 'video' && <video src={e.content} controls style={{width:'100%',maxHeight:'180px',borderRadius:8}} />}
                  {(e.type === 'text' || e.type === 'wish' || !e.type) && (
                    <p style={{margin:'0 0 2px',fontSize:'0.82rem',color:'var(--text)'}}>{e.content}</p>
                  )}
                  <span style={{fontSize:'0.63rem',color:'var(--muted)'}}>{new Date(e.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>

            {/* ── Partner's messages ───────────────────────────────── */}
            <p style={{fontSize:'0.68rem',color:'#8b949e',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>
              {lang==='bg'?`Съобщения от ${profile?.name||'партньора'}`:lang==='es'?`Mensajes de ${profile?.name||'tu pareja'}`:lang==='fr'?`Messages de ${profile?.name||'votre partenaire'}`:lang==='de'?`Nachrichten von ${profile?.name||'Partner'}`:lang==='el'?`Μηνύματα από ${profile?.name||'σύντροφο'}`:lang==='it'?`Messaggi da ${profile?.name||'partner'}`:`Messages from ${profile?.name||'partner'}`}
            </p>
            <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,padding:12,marginBottom:12,minHeight:40}}>
              {partnerEntries.length === 0 ? (
                <p style={{color:'var(--muted)',fontSize:'0.8rem',textAlign:'center',margin:0}}>{lang==='bg'?'Партньорът не е оставил съобщение':lang==='es'?'Tu pareja aún no ha dejado ningún mensaje':lang==='fr'?'Le partenaire n\'a pas encore laissé de message':lang==='de'?'Der Partner hat noch keine Nachricht hinterlassen':lang==='el'?'Ο/Η σύντροφος δεν έχει αφήσει ακόμα μήνυμα':lang==='it'?'Il partner non ha ancora lasciato un messaggio':'Partner has left no message yet'}</p>
              ) : partnerEntries.map((e,i)=>(
                <div key={e.id||i} style={{marginBottom:8}}>
                  {e.type === 'photo' && <img src={e.content} onClick={()=>openExternalUrl(e.content)} style={{width:'120px',height:'90px',objectFit:'cover',borderRadius:8,cursor:'pointer'}} alt="" />}
                  {e.type === 'video' && <video src={e.content} controls style={{width:'100%',maxHeight:'180px',borderRadius:8}} />}
                  {(e.type === 'text' || e.type === 'wish' || !e.type) && (
                    <p style={{margin:'0 0 2px',fontSize:'0.82rem',color:'var(--text)'}}>{e.content}</p>
                  )}
                  <span style={{fontSize:'0.63rem',color:'var(--muted)'}}>{new Date(e.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>

            {/* ── Input ───────────────────────────────────────────── */}
            {maxReached ? (
              <p style={{fontSize:'0.75rem',color:'var(--muted)',textAlign:'center',margin:'0 0 4px'}}>
                {lang==='bg'?'Надгради до Elite за неограничени съобщения':lang==='es'?'Mejora a Elite para mensajes ilimitados':lang==='fr'?'Passez à Elite pour des messages illimités':lang==='de'?'Upgrade auf Elite für unbegrenzte Nachrichten':lang==='el'?'Αναβάθμιση σε Elite για απεριόριστα μηνύματα':lang==='it'?'Passa a Elite per messaggi illimitati':'Upgrade to Elite for unlimited messages'}
              </p>
            ) : (
              <>
                {saved && <p style={{fontSize:'0.72rem',color:'var(--green)',textAlign:'center',marginBottom:4}}>✓ {lang==='bg'?'Запазено':lang==='es'?'Guardado':lang==='fr'?'Enregistré':lang==='de'?'Gespeichert':lang==='el'?'Αποθηκεύτηκε':lang==='it'?'Salvato':'Saved'}</p>}
                <input ref={photoRef} type="file" accept="image/*" style={{display:'none'}} onChange={e=>{if(e.target.files[0])uploadMedia(e.target.files[0],'photo');}} />
                <input ref={videoRef} type="file" accept="video/*" style={{display:'none'}} onChange={e=>{if(e.target.files[0])uploadMedia(e.target.files[0],'video');}} />
                {uploading && <p style={{fontSize:'0.72rem',color:'var(--gold)',textAlign:'center',marginBottom:4}}>{lang==='bg'?'Качва се...':lang==='es'?'Subiendo...':lang==='fr'?'Téléchargement...':lang==='de'?'Wird hochgeladen...':lang==='el'?'Μεταφορτώνεται...':lang==='it'?'Caricamento in corso...':'Uploading...'}</p>}
                <div style={{display:'flex',gap:6}}>
                  <button onClick={()=>photoRef.current?.click()} disabled={uploading||maxReached}
                    style={{background:'rgba(201,168,76,0.2)',border:'1px solid rgba(201,168,76,0.5)',borderRadius:8,padding:'8px 10px',cursor:'pointer',fontSize:'1rem'}}>📷</button>
                  <button onClick={()=>videoRef.current?.click()} disabled={uploading||maxReached}
                    style={{background:'rgba(201,168,76,0.2)',border:'1px solid rgba(201,168,76,0.5)',borderRadius:8,padding:'8px 10px',cursor:'pointer',fontSize:'1rem'}}>🎥</button>
                  <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendMsg()}
                    placeholder={lang==='bg'?'Съобщение до бъдещето…':lang==='es'?'Mensaje para el futuro…':lang==='fr'?'Message pour l\'avenir…':lang==='de'?'Nachricht an die Zukunft…':lang==='el'?'Μήνυμα στο μέλλον…':lang==='it'?'Messaggio al futuro…':'Message to the future…'}
                    style={{flex:1,background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:8,padding:'8px 12px',color:'var(--text)',fontSize:'0.82rem',outline:'none'}}
                  />
                  <button onClick={sendMsg} disabled={!text.trim()||uploading} style={{background:'var(--gold)',border:'none',borderRadius:8,padding:'8px 14px',color:'#000',fontWeight:700,cursor:'pointer',fontSize:'0.9rem'}}>→</button>
                </div>
              </>
            )}
            {plan !== 'elite' && <p style={{fontSize:'0.65rem',color:'var(--muted)',textAlign:'center',marginTop:5}}>{myEntries.length}/5</p>}
          </>
        )}
      </div>
    </div>
  );
}

function ProfileModal({ profile, t, lang, plan, onClose, onLike, onPass, onNeedPremium, onUseIcebreaker, onShowReport, onShowIce, onMessage, onBlock, initialLiked = false, initialMatched = false, socialAllowed = true, onRequireVerification }) {
  const [showMap, setShowMap] = useState(false);
  const [isLiked, setIsLiked] = useState(Boolean(initialLiked));
  const [isMatched, setIsMatched] = useState(initialMatched);
  const [likeSaving, setLikeSaving] = useState(false);
  const [showPact, setShowPact] = useState(false);
  const [showCapsule, setShowCapsule] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockDone, setBlockDone] = useState(false);
  const [existingPact, setExistingPact] = useState(null);
  const [myProfileData, setMyProfileData] = useState(null);
  const [pactKey, setPactKey] = useState(0);
  const ft = FT[lang] || FT.en;
  const pt = t?.pact || {};
  const ti = t?.info || {};
  const blockLabels = getBlockLabels(lang);

  useEffect(() => {
    setIsLiked(Boolean(initialLiked));
    setIsMatched(Boolean(initialMatched));
  }, [initialLiked, initialMatched, profile?.id]);

  async function handleModalLike() {
    if (isLiked || likeSaving) return;
    setLikeSaving(true);
    try {
      const saved = onLike ? await onLike(profile, 'profile-modal') : false;
      if (saved?.liked || saved === true) setIsLiked(true);
      if (saved?.matched) setIsMatched(true);
    } catch (e) {
      console.error('[Like] persist failed:', e);
    } finally {
      setLikeSaving(false);
    }
  }
  async function handleModalBlock() {
    setBlockBusy(true);
    try {
      if (onBlock) await onBlock(profile);
      else await blockProfile(profile.id);
      setBlockDone(true);
      setShowBlockConfirm(false);
      setTimeout(onClose, 900);
    } catch (e) {
      alert(blockLabels.unavailable);
    } finally {
      setBlockBusy(false);
    }
  }
  useEffect(() => {
    async function loadPact() {
      const { userId } = getAuthToken();
      if (!userId || !profile?.id) return;
      try {
        const r1 = await sbFetch('GET', 'pacts', `sender_id=eq.${userId}&receiver_id=eq.${profile.id}&limit=1`);
        const r2 = await sbFetch('GET', 'pacts', `sender_id=eq.${profile.id}&receiver_id=eq.${userId}&limit=1`);
        const foundPact = (Array.isArray(r1) && r1[0]) || (Array.isArray(r2) && r2[0]) || null;
        if (foundPact) setExistingPact(foundPact);
        const myRows = await sbFetch('GET', 'profiles', `id=eq.${userId}&select=id,name,travel_dna&limit=1`);
        if (Array.isArray(myRows) && myRows[0]) setMyProfileData(myRows[0]);
      } catch (e) {}
    }
    loadPact();
  }, [profile?.id, pactKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
    {showMap && <TravelMapModal bucketList={profile.bucket_list||[]} lang={lang} plan={plan} isOwn={false} profileName={profile.name} onClose={()=>setShowMap(false)} />}
    {showPact && <PactModal profile={profile} myProfile={myProfileData} lang={lang} plan={plan} t={t} onClose={()=>{ setShowPact(false); setPactKey(k=>k+1); }} onPactSent={(pact)=>setExistingPact(pact)} onNeedPremium={onNeedPremium} />}
    {showCapsule && existingPact && <CapsuleModal pact={existingPact} myProfile={myProfileData} profile={profile} lang={lang} plan={plan} t={t} onClose={()=>setShowCapsule(false)} />}
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',flexDirection:'column',justifyContent:'flex-end'}} onClick={onClose}>
      <div style={{background:'#0d1117',borderRadius:'20px 20px 0 0',display:'flex',flexDirection:'column',height:'75vh',maxHeight:'75vh'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <div style={{flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch', padding:'12px 16px 8px'}}>
          <div className="ts-pm-header">
            <div style={{position:'relative',display:'inline-flex',flexShrink:0}}>
              {profile.photo
              ? <img src={profile.photo} alt={profile.name} className="ts-pm-photo" />
              : <div className="ts-pm-photo ts-avatar-initials">{(profile.name||'?')[0].toUpperCase()}</div>}
              {isMatched && <span className="ts-verified-dot ts-match-dot" title={lang==='bg'?'Съвпадение':lang==='es'?'Coincidencia':lang==='fr'?'Match':lang==='de'?'Übereinstimmung':lang==='el'?'Ταίριασμα':lang==='it'?'Corrispondenza':'Matched'}>✓</span>}
              {!isMatched && profile.verified && <span className="ts-verified-dot ts-verified-dot--verified" title={lang==='bg'?'Верифициран':lang==='es'?'Verificado':lang==='fr'?'Vérifié':lang==='de'?'Verifiziert':lang==='el'?'Επαληθευμένο':lang==='it'?'Verificato':'Verified'}>✓</span>}
            </div>
            <div className="ts-pm-info">
              <h2 className="ts-serif">{profile.name}{profile.age ? `, ${profile.age}` : ''}
                {profile.is_admin && <span style={{background:'#c0392b',color:'#fff',borderRadius:20,padding:'2px 10px',fontSize:'0.72rem',fontWeight:700,marginLeft:6,verticalAlign:'middle'}}>🛡 Admin</span>}
                {profile.is_moderator && <span style={{background:'#2471a3',color:'#fff',borderRadius:20,padding:'2px 10px',fontSize:'0.72rem',fontWeight:700,marginLeft:6,verticalAlign:'middle'}}>👮 Mod</span>}
              </h2>
              <p className="ts-pm-location">{profile.flag} {profile.country}</p>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                <span className={`ts-online-badge ${profile.online?'on':'off'}`}><span className="ts-online-dot"/>{profile.online?t.connect.online:t.connect.offline}</span>
                {isMatched && <span className="ts-matched-pill">✓ {lang==='bg'?'Съвпадение':lang==='es'?'Coincidencia':lang==='fr'?'Match':lang==='de'?'Übereinstimmung':lang==='el'?'Ταίριασμα':lang==='it'?'Corrispondenza':'Matched'}</span>}
              </div>
            </div>
          </div>
          <div className="ts-pm-compat">
            <span className="ts-pm-compat-label">{t.connect.match}</span>
            <div className="ts-compat-track"><div className="ts-compat-fill" style={{width:`${profile.compat}%`}}/></div>
            <span className="ts-pm-compat-val">{profile.compat}%</span>
          </div>
          <div className="ts-pm-traits">{profile.traits.map(k=><span key={k} className="ts-trait-pill" style={{borderColor:DNA_COLORS[DNA_TRAIT_KEYS.indexOf(k)]}}>{getDnaTraitLabel(k, profile, lang, t)}</span>)}</div>
          <div className="ts-pm-dna"><p className="ts-pm-dna-title">{t.connect.dnaSection}</p>{DNA_TRAIT_KEYS.map((k,i)=><DnaBar key={k} label={getDnaTraitLabel(k, profile, lang, t)} value={profile.scores[i]} color={DNA_COLORS[i]}/>)}</div>
          <div className="ts-pm-dreams">{profile.dreams.flatMap(d=>String(d).split(',').map(s=>s.trim()).filter(Boolean)).map((tag,i)=>{const match=COUNTRIES.find(c=>Object.values(c.name).some(n=>n.toLowerCase()===tag.toLowerCase()));const label=match?getCountryDisplayName(match, lang):tag;return <span key={tag+i} className="ts-dream-tag">{label}</span>;})}</div>
          {/* ── Lifestyle & Partner Preferences ─────────────────────── */}
          {(profile.height||profile.body_type||profile.smoking||profile.alcohol||profile.children||profile.relationship_goal||(profile.preferred_smoking&&profile.preferred_smoking!=='ok')||(profile.preferred_alcohol&&profile.preferred_alcohol!=='ok')||profile.preferred_height_min||(profile.preferred_body_type&&profile.preferred_body_type.length)) && (
            <div style={{marginTop:12}}>
              {(profile.height||profile.body_type||profile.smoking||profile.alcohol||profile.children||profile.occupation||profile.education) && (
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>
                  {profile.height && <span className="ts-dream-tag">📏 {profile.height}cm</span>}
                  {profile.body_type && <span className="ts-dream-tag">🏃 {BODY_TYPE_LABELS[lang]?.[profile.body_type]||profile.body_type}</span>}
                  {profile.occupation && <span className="ts-dream-tag">💼 {profile.occupation}</span>}
                  {profile.education && <span className="ts-dream-tag">🎓 {EDUCATION_LABELS[lang]?.[profile.education]||profile.education}</span>}
                  {profile.smoking && SMOKING_LABELS[lang]?.[profile.smoking] && <span className="ts-dream-tag">🚬 {SMOKING_LABELS[lang][profile.smoking]}</span>}
                  {profile.alcohol && ALCOHOL_LABELS[lang]?.[profile.alcohol] && <span className="ts-dream-tag">🍷 {ALCOHOL_LABELS[lang][profile.alcohol]}</span>}
                  {profile.children&&profile.children!=='any'&&<span className="ts-dream-tag">👶 {CHILDREN_LABELS[lang]?.[profile.children]||profile.children}</span>}
                  {profile.zodiac_sign && <span className="ts-dream-tag">⭐ {profile.zodiac_sign}</span>}
                </div>
              )}
              {(profile.relationship_goal||(profile.preferred_smoking&&profile.preferred_smoking!=='ok')||(profile.preferred_alcohol&&profile.preferred_alcohol!=='ok')||profile.preferred_height_min||(profile.preferred_body_type&&profile.preferred_body_type.length)) && (
                <div style={{marginTop:4}}>
                  <p style={{fontSize:'0.68rem',color:'#8b949e',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:5}}>{ti.partnerPrefs||'Partner preferences'}</p>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {profile.relationship_goal && <span className="ts-dream-tag">🎯 {GOAL_LABELS[lang]?.[profile.relationship_goal]||profile.relationship_goal}</span>}
                    {profile.preferred_smoking&&profile.preferred_smoking!=='ok'&&SMOKING_LABELS[lang]?.[profile.preferred_smoking]&&<span className="ts-dream-tag">🚬 {SMOKING_LABELS[lang][profile.preferred_smoking]}</span>}
                    {profile.preferred_alcohol&&profile.preferred_alcohol!=='ok'&&ALCOHOL_LABELS[lang]?.[profile.preferred_alcohol]&&<span className="ts-dream-tag">🍷 {ALCOHOL_LABELS[lang][profile.preferred_alcohol]}</span>}
                    {profile.preferred_height_min && profile.preferred_height_max && <span className="ts-dream-tag">📏 {profile.preferred_height_min}–{profile.preferred_height_max}cm</span>}
                    {profile.preferred_body_type && profile.preferred_body_type.map(bt=><span key={bt} className="ts-dream-tag">🏃 {BODY_TYPE_LABELS[lang]?.[bt]||bt}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="ts-pm-action-footer" style={{flexShrink:0, borderTop:'1px solid #30363d', background:'#0d1117', padding:'12px 16px 16px'}}>
          <div className="ts-pm-extra-btns" style={{marginBottom:8}}>
            <button className="ts-pm-extra-btn" onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('compatReport'):onShowReport&&onShowReport()}>{ft.compatReport}</button>
            <button className="ts-pm-extra-btn" onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('icebreaker'):onShowIce&&onShowIce()}>{ft.icebreaker}</button>
            {profile.map_public && (profile.bucket_list||[]).length > 0 && (
              <button className="ts-pm-extra-btn" onClick={()=>setShowMap(true)}>
                🗺️ {lang==='bg'?'Виж картата':lang==='es'?'Ver mapa':lang==='fr'?'Voir la carte':lang==='de'?'Karte ansehen':lang==='el'?'Προβολή χάρτη':lang==='it'?'Visualizza mappa':'View Map'}
              </button>
            )}
            <button className="ts-pm-extra-btn"
              onClick={()=>{
                if (!socialAllowed) { onRequireVerification && onRequireVerification(); return; }
                plan==='free'?onNeedPremium&&onNeedPremium('travelPact'):setShowPact(true);
              }}>
              {pt.pactBtn||'🤝 Make a Pact'}
            </button>
          </div>
          {existingPact && <div style={{marginBottom:8}}><PactBadge pact={existingPact} t={t} /></div>}
          {existingPact?.status === 'active' && (
            <button className="ts-btn-ghost-sm" style={{width:'100%',marginBottom:8}}
              onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('travelCapsule'):setShowCapsule(true)}>
              🫙 {t?.pact?.capsuleBtn||(lang==='bg'?'Времева капсула':lang==='es'?'Cápsula de viaje':lang==='fr'?'Capsule de voyage':lang==='de'?'Reisekapsel':lang==='el'?'Ταξιδιωτική κάψουλα':lang==='it'?'Capsula di viaggio':'Travel Capsule')}
            </button>
          )}
          {plan==='elite' && existingPact?.status==='fulfilled' && (
            <div style={{background:'rgba(201,168,76,0.15)',border:'1px solid #c9a84c',borderRadius:10,padding:'8px 12px',marginBottom:8}}>
              <div style={{color:'#c9a84c',fontSize:'0.78rem',fontWeight:700}}>{pt.pactTrophy||'🏆 Traveled Together'} · {existingPact.destination}</div>
            </div>
          )}
          <div className="ts-pm-actions">
            {isMatched && onMessage && (
              <button className="ts-btn-message" onClick={()=>onMessage(profile)}>
                💬 {lang==='bg'?'Напиши съобщение':lang==='es'?'Escribir mensaje':lang==='fr'?'Écrire un message':lang==='de'?'Nachricht schreiben':lang==='el'?'Γράψε μήνυμα':lang==='it'?'Scrivi un messaggio':'Write message'}
              </button>
            )}
            <button className="ts-btn-pass" onClick={()=>onPass&&onPass(profile)}>{t.connect.pass}</button>
            <button className="ts-btn-like" style={isLiked?{background:'#21262d',border:'1px solid var(--gold)',color:'var(--gold)'}:{}}
              disabled={likeSaving}
              onClick={handleModalLike}>
              {isLiked ? `✓ ${t.connect.liked||t.connect.like}` : `♥ ${t.connect.like}`}
            </button>
          </div>
          <div style={{marginTop:8,display:'flex',justifyContent:'center'}}>
            {blockDone
              ? <span style={{color:'#f85149',fontSize:12}}>✓ {blockLabels.blocked}</span>
              : <button className="ts-btn-ghost-sm" style={{fontSize:11,color:'#f85149',borderColor:'#f8514933',padding:'4px 10px'}} onClick={()=>setShowBlockConfirm(true)}>🚫 {blockLabels.blockShort}</button>
            }
          </div>
        </div>
      </div>
      {showBlockConfirm && <BlockConfirmModal lang={lang} busy={blockBusy} onCancel={()=>setShowBlockConfirm(false)} onConfirm={handleModalBlock} />}
    </div>
    </>
  );
}

// ── TravelMapModal ─────────────────────────────────────────────────────────────
function TravelMapModal({ bucketList, lang, plan, isOwn, profileName, onClose, joinedTrips }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [geocoding, setGeocoding] = useState(bucketList.length > 0);
  const [coords, setCoords] = useState([]);
  const [mapPublic, setMapPublic] = useState(() => {
    try { return JSON.parse(localStorage.getItem('travelsoul_profile')||'{}').map_public || false; } catch { return false; }
  });
  const [sharing, setSharing] = useState(false);
  const [mapTab, setMapTab] = useState('map');

  const visitedCount = bucketList.filter(i => i.status === 'visited').length;

  useEffect(() => {
    if (bucketList.length === 0) return;
    async function init() {
      if (!window.L) {
        await new Promise(resolve => {
          if (!document.querySelector('link[href*="leaflet"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
          }
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = resolve;
          script.onerror = resolve;
          document.head.appendChild(script);
        });
      }
      const results = [];
      const { accessToken: bucketGeocodeToken } = await getAuthTokenSafe();
      for (const item of bucketList) {
        try {
          if (!bucketGeocodeToken) continue;
          const r = await fetch(`${API_BASE}/api/ors/geocode?text=${encodeURIComponent(item.destination)}`, {
            headers: { 'Authorization': `Bearer ${bucketGeocodeToken}` },
          });
          const d = await r.json();
          const f = d?.features?.[0];
          if (f) {
            const [lng, lat] = f.geometry.coordinates;
            results.push({ ...item, lat, lng });
          }
        } catch {}
      }
      setCoords(results);
      setGeocoding(false);
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (geocoding || !mapRef.current || mapInstanceRef.current || !window.L || coords.length === 0) return;
    const L = window.L;
    const map = L.map(mapRef.current, { center: [20, 0], zoom: 2 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);
    const STATUS_COLOR = { visited: '#22c55e', planned: '#3b82f6', want: '#f59e0b' };
    const markers = coords.map(item => {
      const m = L.circleMarker([item.lat, item.lng], {
        radius: 8, fillColor: STATUS_COLOR[item.status] || '#f59e0b',
        color: '#fff', weight: 2, opacity: 1, fillOpacity: 0.85,
      }).addTo(map);
      m.bindPopup(`<b>${item.destination}</b>`);
      return m;
    });
    try { map.fitBounds(L.featureGroup(markers).getBounds().pad(0.3)); } catch {}
    mapInstanceRef.current = map;
  }, [geocoding, coords]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, []);

  async function togglePublic() {
    setSharing(true);
    const newVal = !mapPublic;
    const { userId } = getAuthToken();
    if (userId) {
      await sbFetch('PATCH', 'profiles', `id=eq.${userId}`, { map_public: newVal }, { Prefer: 'return=minimal' });
      try {
        const lsP = JSON.parse(localStorage.getItem('travelsoul_profile')||'{}');
        setProfileLocalStorage({ ...lsP, map_public: newVal });
      } catch {}
    }
    setMapPublic(newVal);
    setSharing(false);
  }

  const title = isOwn
    ? (lang==='bg'?'🗺️ Моята карта':lang==='es'?'🗺️ Mi mapa':lang==='fr'?'🗺️ Ma carte':lang==='de'?'🗺️ Meine Karte':lang==='el'?'🗺️ Ο χάρτης μου':lang==='it'?'🗺️ La mia mappa':'🗺️ My Map')
    : (lang==='bg'?`🗺️ Картата на ${profileName||''}`:lang==='es'?`🗺️ Mapa de ${profileName||''}`:lang==='fr'?`🗺️ Carte de ${profileName||''}`:lang==='de'?`🗺️ Karte von ${profileName||''}`:lang==='el'?`🗺️ Ο χάρτης του/της ${profileName||''}`:lang==='it'?`🗺️ Mappa di ${profileName||''}`:`🗺️ ${profileName||''}'s Map`);

  return (
    <div style={{...OVERLAY_S, zIndex:10500}} onClick={onClose}>
      <div style={{...MODAL_S, maxWidth:520, maxHeight:'90vh', textAlign:'left', padding:'20px 20px 16px'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center',marginBottom:'0.25rem'}}>{title}</h2>
        {isOwn && (
          <div style={{display:'flex',gap:6,justifyContent:'center',marginBottom:'0.75rem'}}>
            {[{key:'map',label:lang==='bg'?'🗺️ Карта':lang==='es'?'🗺️ Mapa':lang==='fr'?'🗺️ Carte':lang==='de'?'🗺️ Karte':lang==='el'?'🗺️ Χάρτης':lang==='it'?'🗺️ Mappa':'🗺️ Map'},{key:'joined',label:lang==='bg'?'🧳 Присъединени':lang==='es'?'🧳 Unido':lang==='fr'?'🧳 Rejoint':lang==='de'?'🧳 Beigetreten':lang==='el'?'🧳 Συμμετέχεις':lang==='it'?'🧳 Partecipa':'🧳 Joined'}].map(tab=>(
              <button key={tab.key} onClick={()=>setMapTab(tab.key)} style={{padding:'5px 14px',fontSize:'0.78rem',borderRadius:20,border:'1px solid',borderColor:mapTab===tab.key?'var(--gold)':'var(--border)',background:mapTab===tab.key?'rgba(201,168,76,0.12)':'transparent',color:mapTab===tab.key?'var(--gold)':'var(--muted)',cursor:'pointer',transition:'all 0.15s'}}>
                {tab.label}
              </button>
            ))}
          </div>
        )}
        {(!isOwn || mapTab === 'map') && <>
          {visitedCount > 0 && (
            <p style={{textAlign:'center',color:'#22c55e',fontSize:'0.82rem',marginBottom:'0.6rem'}}>
              {lang==='bg'?`Посетил си ${visitedCount} дестинации`:lang==='es'?`Has visitado ${visitedCount} destino${visitedCount!==1?'s':''}`:lang==='fr'?`Vous avez visité ${visitedCount} destination${visitedCount!==1?'s':''}`:lang==='de'?`Du hast ${visitedCount} Reiseziel${visitedCount!==1?'e':''} besucht`:lang==='el'?`Έχεις επισκεφθεί ${visitedCount} προορισμό${visitedCount!==1?'ύς':''}`:lang==='it'?`Hai visitato ${visitedCount} destinazion${visitedCount!==1?'i':'e'}`:`Visited ${visitedCount} destination${visitedCount!==1?'s':''}`}
            </p>
          )}
          <div style={{display:'flex',gap:12,justifyContent:'center',marginBottom:'0.75rem',flexWrap:'wrap'}}>
            {[
              {s:'visited',c:'#22c55e',l:lang==='bg'?'Посетено':lang==='es'?'Visitado':lang==='fr'?'Visité':lang==='de'?'Besucht':lang==='el'?'Επισκέφθηκα':lang==='it'?'Visitato':'Visited'},
              {s:'planned',c:'#3b82f6',l:lang==='bg'?'Планирано':lang==='es'?'Planeado':lang==='fr'?'Prévu':lang==='de'?'Geplant':lang==='el'?'Προγραμματισμένο':lang==='it'?'Pianificato':'Planned'},
              {s:'want',   c:'#f59e0b',l:lang==='bg'?'Искам':lang==='es'?'Quiero':lang==='fr'?'Je veux':lang==='de'?'Ich möchte':lang==='el'?'Θέλω να':lang==='it'?'Voglio':'Want'},
            ].map(({s,c,l})=>(
              <span key={s} style={{display:'flex',alignItems:'center',gap:4,fontSize:'0.75rem',color:'var(--muted)'}}>
                <span style={{width:10,height:10,borderRadius:'50%',background:c,display:'inline-block',border:'2px solid rgba(255,255,255,0.3)'}}/>
                {l}
              </span>
            ))}
          </div>
          {bucketList.length === 0 ? (
            <p style={{color:'var(--muted)',textAlign:'center',padding:'2rem 0',fontSize:'0.88rem'}}>
              {lang==='bg'?'Добави дестинации в Bucket List за да видиш картата':lang==='es'?'Añade destinos a tu lista de deseos para verlos en el mapa':lang==='fr'?'Ajoutez des destinations à votre liste pour les voir sur la carte':lang==='de'?'Füge Reiseziele zu deiner Bucket List hinzu, um sie auf der Karte zu sehen':lang==='el'?'Πρόσθεσε προορισμούς στη λίστα επιθυμιών σου για να τους δεις στον χάρτη':lang==='it'?'Aggiungi destinazioni alla tua bucket list per vederle sulla mappa':'Add destinations to your Bucket List to see them on the map'}
            </p>
          ) : geocoding ? (
            <div style={{textAlign:'center',padding:'3rem 0',color:'var(--muted)'}}>
              <div style={{width:32,height:32,margin:'0 auto 0.75rem',borderRadius:'50%',border:'3px solid rgba(180,127,255,0.2)',borderTopColor:'#b47fff',animation:'ts-spin 0.9s linear infinite'}}/>
              <p style={{fontSize:'0.85rem'}}>{lang==='bg'?'Зареждам картата…':lang==='es'?'Cargando mapa…':lang==='fr'?'Chargement de la carte…':lang==='de'?'Karte wird geladen…':lang==='el'?'Ο χάρτης φορτώνει…':lang==='it'?'Caricamento mappa…':'Loading map…'}</p>
            </div>
          ) : (
            <div ref={mapRef} style={{width:'100%',height:320,borderRadius:12,overflow:'hidden',border:'1px solid var(--border)'}} />
          )}
          {isOwn && plan==='elite' && bucketList.length > 0 && (
            <div style={{marginTop:'0.75rem'}}>
              <button className="ts-btn-ghost-sm" style={{fontSize:'0.78rem',width:'100%'}} onClick={togglePublic} disabled={sharing}>
                {sharing ? '…' : mapPublic
                  ? (lang==='bg'?'✅ Картата е публична — кликни за скриване':lang==='es'?'✅ El mapa es público; haz clic para hacerlo privado':lang==='fr'?'✅ La carte est publique — cliquez pour la rendre privée':lang==='de'?'✅ Die Karte ist öffentlich — klicke, um sie privat zu machen':lang==='el'?'✅ Ο χάρτης είναι δημόσιος — πάτα για να τον κάνεις ιδιωτικό':lang==='it'?'✅ La mappa è pubblica — clicca per renderla privata':'✅ Map is public — click to make private')
                  : (lang==='bg'?'🔗 Сподели картата публично':lang==='es'?'🔗 Compartir mapa públicamente':lang==='fr'?'🔗 Partager la carte publiquement':lang==='de'?'🔗 Karte öffentlich teilen':lang==='el'?'🔗 Κοινοποίηση χάρτη δημόσια':lang==='it'?'🔗 Condividi mappa pubblicamente':'🔗 Share map publicly')
                }
              </button>
            </div>
          )}
        </>}
        {isOwn && mapTab === 'joined' && (
          (joinedTrips||[]).length === 0 ? (
            <p style={{color:'var(--muted)',textAlign:'center',padding:'2rem 0',fontSize:'0.88rem'}}>
              {lang==='bg'?'Все още не си се присъединил към пътувания':lang==='es'?'Aún no te has unido a ningún viaje':lang==='fr'?'Vous n\'avez encore rejoint aucun voyage':lang==='de'?'Du bist noch keiner Reise beigetreten':lang==='el'?'Δεν έχεις συμμετάσχει ακόμα σε κανένα ταξίδι':lang==='it'?'Non hai ancora partecipato a nessun viaggio':'You haven\'t joined any trips yet'}
            </p>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10,maxHeight:340,overflowY:'auto'}}>
              {(joinedTrips||[]).map(trip=>(
                <div key={trip.id} style={{display:'flex',gap:12,alignItems:'center',background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'0.6rem 0.9rem'}}>
                  <img src={trip.image} alt={trip.name} style={{width:44,height:44,borderRadius:8,objectFit:'cover',flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{color:'var(--text)',fontSize:'0.88rem',fontWeight:600,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{trip.name}</p>
                    <p style={{color:'var(--muted)',fontSize:'0.75rem'}}>📍 {trip.location}{trip.start_date?` · 📅 ${new Date(trip.start_date).toLocaleDateString()}`:''}</p>
                  </div>
                  <span style={{fontSize:'1.1rem',flexShrink:0}}>✈️</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ── BucketMatchModal ──────────────────────────────────────────────────────────
function BucketMatchModal({ matchingItems, profileName, lang, onClose }) {
  const STATUS_ICON = { want:'🌍', planned:'✈️', visited:'✅' };
  const STATUS_LBL  = {
    bg: { want:'Искам да посетя', planned:'Планирано', visited:'Посетено' },
    en: { want:'Want to visit',   planned:'Planned',   visited:'Visited'  },
  };
  const sl = s => (STATUS_LBL[lang] || STATUS_LBL.en)[s] || s;
  return (
    <div className="ts-modal-overlay" style={{...OVERLAY_S, zIndex:11000}} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:340}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">🎯 {lang==='bg'?'Съвпадащи дестинации':lang==='es'?'Destinos coincidentes':lang==='fr'?'Destinations correspondantes':lang==='de'?'Passende Reiseziele':lang==='el'?'Ταιριαστοί προορισμοί':lang==='it'?'Destinazioni corrispondenti':'Matching Destinations'}</h2>
        <p style={{color:'var(--muted)',fontSize:'0.82rem',marginBottom:'1rem'}}>
          {lang==='bg'?`Ти и ${profileName} искате да посетите:`:lang==='es'?`Tú y ${profileName} queréis visitar:`:lang==='fr'?`Vous et ${profileName} souhaitez visiter :`:lang==='de'?`Du und ${profileName} möchten besuchen:`:lang==='el'?`Εσύ και ο/η ${profileName} θέλετε να επισκεφθείτε:`:lang==='it'?`Tu e ${profileName} volete visitare:`:`You and ${profileName} both want to visit:`}
        </p>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {matchingItems.map(item => (
            <div key={item.destination} style={{display:'flex',alignItems:'center',gap:10,background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'0.6rem 0.9rem'}}>
              <span style={{fontSize:'1.1rem'}}>{STATUS_ICON[item.status]||'🌍'}</span>
              <span style={{flex:1,color:'var(--text)',fontSize:'0.9rem'}}>{item.destination}</span>
              <span style={{fontSize:'0.7rem',color:'var(--muted)'}}>{sl(item.status)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Highlighted Story Modal (pinned diary entry viewer) ───────────────────────
function HighlightedStoryModal({ story, lang, onClose }) {
  return (
    <div className="ts-story-viewer" onClick={onClose} style={{zIndex:10500}}>
      <div className="ts-story-vignette"/>
      <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,#0d1117 0%,#1a0533 50%,#0d1117 100%)',display:'flex',alignItems:'center',justifyContent:'center',padding:'3rem 1.5rem'}}>
        <div style={{maxWidth:380,width:'100%',textAlign:'center'}}>
          <div style={{fontSize:'0.8rem',color:'var(--gold)',fontWeight:700,letterSpacing:'0.06em',marginBottom:'1.25rem',textTransform:'uppercase'}}>
            ✈ {story.destination}{story.date ? ` · ${story.date}` : ''}
          </div>
          <p style={{color:'#f0f6fc',fontSize:'1.05rem',lineHeight:1.75,textShadow:'0 1px 6px rgba(0,0,0,0.7)',fontStyle:'italic'}}>
            "{story.story.length > 500 ? story.story.slice(0, 497) + '…' : story.story}"
          </p>
          <div style={{marginTop:'1.5rem',display:'flex',alignItems:'center',justifyContent:'center',gap:10}}>
            {story.authorPhoto && <img src={story.authorPhoto} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',border:'2px solid var(--gold)'}}/>}
            <span style={{color:'rgba(240,246,252,0.7)',fontSize:'0.82rem'}}>{story.authorName}</span>
          </div>
        </div>
      </div>
      <button className="ts-story-close" onClick={onClose} style={{cursor:'pointer'}}>✕</button>
    </div>
  );
}

function ProfileCard({ profile, t, lang, onExpand, onLike, isLiked, isMatched, onPlayVideo, onLightbox, plan, onSoulMatch, diaryPostCount, onShowStories, onShowHighlight, myBucketList, onShowBucketMatch }) {
  return (
    <div className={`ts-profile-card${isMatched?' matched':''}`} onClick={()=>onExpand(profile)}>
      <div className={`ts-pc-photo-wrap${profile.highlighted_story ? ' ts-has-story' : ''}`}>
        {profile.photo
          ? <img src={profile.photo} alt={profile.name} className="ts-pc-photo" />
          : <div className="ts-pc-photo ts-avatar-initials">{(profile.name||'?')[0].toUpperCase()}</div>}
        {profile.online && <span className="ts-pc-online-dot"/>}
        {isMatched && <span className="ts-verified-dot ts-match-dot" title={lang==='bg'?'Съвпадение':lang==='es'?'Coincidencia':lang==='fr'?'Match':lang==='de'?'Übereinstimmung':lang==='el'?'Ταίριασμα':lang==='it'?'Corrispondenza':'Matched'}>✓</span>}
        {!isMatched && profile.verified && <span className="ts-verified-dot ts-verified-dot--verified" title={lang==='bg'?'Верифициран':lang==='es'?'Verificado':lang==='fr'?'Vérifié':lang==='de'?'Verifiziert':lang==='el'?'Επαληθευμένο':lang==='it'?'Verificato':'Verified'}>✓</span>}
        {profile.highlighted_story && (
          <button className="ts-pc-story-badge" onClick={e=>{e.stopPropagation();onShowHighlight&&onShowHighlight(profile);}}>
            📌 Story
          </button>
        )}
        <div
          className={`ts-pc-compat-badge${plan==='elite'?' ts-compat-badge-elite':''}`}
          onClick={e=>{e.stopPropagation();onSoulMatch&&onSoulMatch(profile);}}
          title={plan==='elite'?'Soul Match AI ✨':undefined}
        >{profile.compat}%</div>
        {profile.video_url && (
          <button
            onClick={e=>{e.stopPropagation();onPlayVideo&&onPlayVideo(profile);}}
            style={{position:'absolute',bottom:'8px',left:'8px',background:'rgba(0,0,0,0.7)',border:'none',color:'#fff',borderRadius:20,padding:'4px 10px',fontSize:'0.72rem',cursor:'pointer',letterSpacing:'0.02em'}}
            title={t.videoIntro?.playIntro||'Play Intro Video'}
          >{t.videoIntro?.playIntro||'▶ Intro'}</button>
        )}
      </div>
      <div className="ts-pc-body">
        <div className="ts-pc-row">
          <span className="ts-pc-name">{profile.name}{profile.age ? `, ${profile.age}` : ''}</span>
          <span className="ts-pc-flag">{profile.flag}</span>
          {profile.is_admin && <span style={{background:'#c0392b',color:'#fff',borderRadius:20,padding:'1px 7px',fontSize:'0.65rem',fontWeight:700,flexShrink:0}}>🛡 Admin</span>}
          {profile.is_moderator && <span style={{background:'#2471a3',color:'#fff',borderRadius:20,padding:'1px 7px',fontSize:'0.65rem',fontWeight:700,flexShrink:0}}>👮 Mod</span>}
        </div>
        {isMatched && <div className="ts-pc-matched-badge">✓ {lang==='bg'?'Съвпадение':lang==='es'?'Coincidencia':lang==='fr'?'Match':lang==='de'?'Übereinstimmung':lang==='el'?'Ταίριασμα':lang==='it'?'Corrispondenza':'Matched'}</div>}
        <div className="ts-pc-traits">
          {profile.traits.map(k=><span key={k} className="ts-trait-pill sm" style={{borderColor:DNA_COLORS[DNA_TRAIT_KEYS.indexOf(k)]}}>{getDnaTraitLabel(k, profile, lang, t)}</span>)}
          {(profile.plan==='elite'||profile.plan==='premium') && <EliteBadge plan={profile.plan}/>}
        </div>
        {profile.dna_title && (
          <div className="ts-pc-dna-badge" title={profile.dna_title}>🧬 {profile.dna_title.length>24?profile.dna_title.slice(0,24)+'…':profile.dna_title}</div>
        )}
        {profile.custom_badge && (() => {
          const cb = profile.custom_badge;
          const cbCol = BADGE_COLORS.find(c=>c.key===cb.color)||BADGE_COLORS[0];
          return (
            <div className="ts-pc-custom-badge" style={{background:cbCol.bg,border:`1px solid ${cbCol.border}`}}>
              <span>{cb.emoji}</span>
              <span style={{color:cbCol.hex}}>{cb.name.length>16?cb.name.slice(0,16)+'…':cb.name}</span>
            </div>
          );
        })()}
        {(() => {
          const myDests = new Set((myBucketList||[]).map(i=>i.destination.toLowerCase()));
          const matching = (profile.bucket_list||[]).filter(i=>myDests.has(i.destination.toLowerCase()));
          return matching.length > 0 ? (
            <button className="ts-bucket-match-badge" onClick={e=>{e.stopPropagation();onShowBucketMatch&&onShowBucketMatch(matching);}}>
              🎯 {matching.length} {lang==='bg'?'съвпадения':lang==='es'?'coincidencias':lang==='fr'?'correspondances':lang==='de'?'Übereinstimmungen':lang==='el'?'Ταιριάσματα':lang==='it'?'Corrispondenze':'matches'}
            </button>
          ) : null;
        })()}
        {diaryPostCount > 0 && (
          <button
            className="ts-diary-stories-badge"
            onClick={e=>{e.stopPropagation();onShowStories&&onShowStories();}}
          >✈️ {diaryPostCount} {lang==='bg'?'истории':lang==='es'?'historias':lang==='fr'?'histoires':lang==='de'?'Reisen':lang==='el'?'Ταξίδια':lang==='it'?'Viaggi':'stories'}</button>
        )}
        <div className="ts-pc-compat-bar"><div className="ts-compat-track"><div className="ts-compat-fill" style={{width:`${profile.compat}%`}}/></div></div>
        <button className={`ts-like-btn ${isLiked?'liked':''}`}
          style={isLiked?{background:'transparent',border:'1px solid var(--gold)',color:'var(--gold)'}:{}}
          onClick={e=>{e.stopPropagation(); if (!isLiked) onLike&&onLike(profile, 'profile-card');}}>
          {isLiked ? `✓ ${t.connect.liked||t.connect.like}` : `♡ ${t.connect.like}`}
        </button>
      </div>
    </div>
  );
}

// ── EliteBadge ────────────────────────────────────────────────────────────────
function EliteBadge({ plan }) {
  if (plan==='elite')   return <span className="ts-elite-badge" style={{fontSize:'20px'}}>💎</span>;
  if (plan==='premium') return <span className="ts-premium-badge" style={{fontSize:'20px'}}>👑</span>;
  return null;
}

// ── PactBadge ─────────────────────────────────────────────────────────────────
function PactBadge({ pact, t }) {
  if (!pact || pact.status === 'declined') return null;
  const pt = t?.pact || {};
  const cfg = {
    pending:   { bg:'rgba(245,158,11,0.15)',  border:'rgba(245,158,11,0.4)',  color:'#f59e0b', label: pt.pactPending   || '⏳ Pact Pending' },
    active:    { bg:'rgba(63,185,80,0.15)',   border:'rgba(63,185,80,0.4)',   color:'#3fb950', label: pt.pactActive    || '✈️ Pact Active' },
    fulfilled: { bg:'rgba(201,168,76,0.2)',   border:'#c9a84c',               color:'#c9a84c', label: pt.pactTrophy    || '🏆 Traveled Together' },
  };
  const c = cfg[pact.status];
  if (!c) return null;
  return (
    <div style={{display:'inline-flex',alignItems:'center',gap:4,background:c.bg,border:`1px solid ${c.border}`,borderRadius:20,padding:'3px 10px',fontSize:'0.75rem',color:c.color,fontWeight:600,marginTop:6}}>
      {c.label}{pact.destination ? ` · ${pact.destination}` : ''}
    </div>
  );
}

// ── PactModal ─────────────────────────────────────────────────────────────────
function PactModal({ profile, myProfile, lang, plan, t, onClose, onPactSent, onNeedPremium }) {
  const pt = t?.pact || {};
  const ft = FT[lang] || FT.en;
  const [dest, setDest] = useState('');
  const [month, setMonth] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');

  const monthOptions = {
    bg: ['Следващия месец','След 3 месеца','След 6 месеца','Това лято','Тази зима','Отворена дата'],
    en: ['Next month','In 3 months','In 6 months','This summer','This winter','Open date'],
    es: ['El mes que viene','En 3 meses','En 6 meses','Este verano','Este invierno','Fecha abierta'],
    fr: ['Le mois prochain','Dans 3 mois','Dans 6 mois','Cet été','Cet hiver','Date ouverte'],
    de: ['Nächsten Monat','In 3 Monaten','In 6 Monaten','Diesen Sommer','Diesen Winter','Offen'],
  };

  async function aiSuggest() {
    setAiLoading(true);
    try {
      const senderTraits = (myProfile?.travel_dna || myProfile?.traits || []).join(', ');
      const receiverTraits = (profile?.traits || []).join(', ');
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', max_tokens: 50, messages: [
          { role: 'system', content: 'Reply with ONLY the destination name, nothing else.' },
          { role: 'user', content: `Suggest ONE perfect travel destination for two travelers. Traveler 1 DNA: ${senderTraits}. Traveler 2 DNA: ${receiverTraits}.` },
        ]}),
      });
      const data = await res.json();
      const suggestion = data.choices?.[0]?.message?.content?.trim();
      if (suggestion) setAiSuggestion(suggestion);
    } catch {}
    setAiLoading(false);
  }

  async function sendPact() {
    if (!dest.trim()) return;
    const { userId } = getAuthToken();
    if (!userId) return;
    let activeProfile = null;
    try { activeProfile = await getActiveSocialProfile(); } catch (e) {}
    if (!canUseSocialFeatures(activeProfile)) {
      alert(getVerificationRequiredMessage(lang));
      return;
    }
    if (plan === 'free') { onNeedPremium && onNeedPremium('travelPact'); return; }
    if (plan === 'premium') {
      let existing;
      try { existing = await sbFetch('GET', 'pacts', `sender_id=eq.${userId}&status=in.(pending,active)&limit=2`); } catch (e) { existing = []; }
      if (Array.isArray(existing) && existing.length >= 1) {
        onNeedPremium && onNeedPremium('travelPact'); return;
      }
    }
    setSending(true);
    try {
      await sbFetch('POST', 'pacts', 'on_conflict=sender_id%2Creceiver_id', {
        sender_id: userId, receiver_id: profile.id,
        destination: dest.trim(), target_month: month || null, status: 'pending',
      }, { Prefer: 'return=minimal,resolution=merge-duplicates' });
    } catch (e) {
      // Audit finding (7th audit): the client-side plan/limit checks above are
      // now mirrored by a server-side trigger (enforce_pact_limits), so a
      // stale client plan/count can still be rejected here even though the
      // checks above passed — surface that instead of a silent/unhandled
      // failure.
      setSending(false);
      const msg = String(e?.message || e || '').toLowerCase();
      if (msg.includes('premium_required_for_pact') || msg.includes('pact_limit_reached')) {
        onNeedPremium && onNeedPremium('travelPact');
      } else {
        alert(lang==='bg'?'Пактът не можа да бъде изпратен. Опитай пак.':lang==='es'?'No se pudo enviar el pacto. Inténtalo de nuevo.':lang==='fr'?"Le pacte n'a pas pu être envoyé. Réessayez.":lang==='de'?'Der Pakt konnte nicht gesendet werden. Versuche es erneut.':lang==='el'?'Η συμφωνία δεν στάλθηκε. Δοκίμασε ξανά.':lang==='it'?'Il patto non è stato inviato. Riprova.':'The pact could not be sent. Please try again.');
      }
      return;
    }
    setSending(false);
    setSuccess(true);
    onPactSent && onPactSent({ sender_id: userId, receiver_id: profile.id, destination: dest.trim(), target_month: month || null, status: 'pending' });
    setTimeout(onClose, 2000);
  }

  return (
    <div style={{...OVERLAY_S, zIndex:10600}} onClick={onClose}>
      <div style={{...MODAL_S, maxWidth:360, textAlign:'left'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center'}}>{pt.pactTitle||'Travel Pact'}</h2>
        <p style={{color:'#8b949e',fontSize:'0.82rem',textAlign:'center',marginBottom:16}}>{pt.pactSub||'Commit to a journey together'}</p>
        {success ? (
          <div style={{textAlign:'center',color:'#3fb950',fontSize:'0.88rem',padding:'1rem 0'}}>{pt.pactSuccess||'Pact sent!'}</div>
        ) : (<>
          <label style={{color:'#8b949e',fontSize:'0.78rem',display:'block',marginBottom:4}}>{pt.pactDestLabel||'Destination'}</label>
          <input value={dest} onChange={e=>setDest(e.target.value)}
            placeholder={ft.destPlaceholder||'Tokyo, Bali, Paris...'}
            style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:8,padding:'8px 10px',color:'#f0f6fc',fontSize:'0.85rem',marginBottom:aiSuggestion?6:12,boxSizing:'border-box'}}
          />
          {aiSuggestion && (
            <button onClick={()=>{setDest(aiSuggestion);setAiSuggestion('');}}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(139,92,246,0.15)',border:'1px solid rgba(139,92,246,0.4)',borderRadius:20,padding:'3px 12px',color:'#a78bfa',fontSize:'0.78rem',cursor:'pointer',marginBottom:10}}>
              ✨ {aiSuggestion} →
            </button>
          )}
          <label style={{color:'#8b949e',fontSize:'0.78rem',display:'block',marginBottom:4}}>{pt.pactMonthLabel||'When'}</label>
          <select value={month} onChange={e=>setMonth(e.target.value)}
            style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:8,padding:'8px 10px',color:month?'#f0f6fc':'#8b949e',fontSize:'0.85rem',marginBottom:14,boxSizing:'border-box'}}>
            <option value="">—</option>
            {(monthOptions[lang]||monthOptions.en).map((o,i)=><option key={i} value={o}>{o}</option>)}
          </select>
          {plan === 'elite' && (
            <button onClick={aiSuggest} disabled={aiLoading}
              style={{width:'100%',padding:'8px',background:'rgba(139,92,246,0.12)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:10,color:'#a78bfa',fontSize:'0.82rem',fontWeight:600,cursor:'pointer',marginBottom:10,opacity:aiLoading?0.6:1}}>
              {aiLoading ? '...' : pt.pactAiSuggest||'✨ AI Suggest'}
            </button>
          )}
          <button onClick={sendPact} disabled={sending||!dest.trim()} className="ts-btn-gold-full"
            style={{width:'100%',opacity:!dest.trim()?0.5:1}}>
            {sending ? '...' : pt.pactSend||'Send Pact'}
          </button>
        </>)}
      </div>
    </div>
  );
}

// ── PremiumModal (reusable) ───────────────────────────────────────────────────
const PREMIUM_FEATURE_META = {
  boost:    { icon:'🔥', bullets:['Profile shown first for 30 min','3× more profile views','Exclusive gold ring indicator'] },
  rewind:   { icon:'↩️', bullets:['Undo your last pass','Second chances with great matches','Available once per day'] },
  passport: { icon:'🌍', bullets:['Browse profiles in any city','Match with travelers worldwide','Change location anytime'] },
  incognito:{ icon:'👀', bullets:["Browse invisibly — no one knows","Your views aren't recorded","Turn on/off anytime"] },
  filters:  { icon:'🎯', bullets:['Filter by age, DNA, compatibility','Online-only & verified filter','Distance control'] },
  stats:    { icon:'📊', bullets:['Full travel statistics dashboard','Monthly trip chart','World footprint map'] },
  likes:    { icon:'♥',  bullets:['Unlimited likes every day','No daily caps or wait times','Like everyone you want'] },
  whoViewed:{ icon:'👁️', bullets:['See everyone who viewed you','Send them a message first','Real-time notifications'] },
  videoChat:  { icon:'📹', bullets:['HD video calls with matches','Available on all devices','Elite-only feature'] },
  aiPlanner:  { icon:'🗺️', bullets:['AI day-by-day itinerary','Custom budget planning','Share with your travel group'] },
  nightMode:  { icon:'🌙', bullets:["See who's active tonight","Special night-only profiles","Exclusive meetup filter"] },
  compatReport:{ icon:'📝', bullets:['Full compatibility analysis','5 dimensions scored','Downloadable PDF report'] },
  voiceMsg:   { icon:'🎤', bullets:['Send voice messages','Hear your match first','Playable in-chat audio'] },
  gifts:      { icon:'🎁', bullets:['Send virtual gifts','Animated gift delivery','Show you care beyond words'] },
  icebreaker: { icon:'🗣️', bullets:['AI-personalized openers','Based on travel DNA','3 suggestions + refresh'] },
  dreamMatch: { icon:'🔮', bullets:['Match on dream destinations','Find your co-traveler','Based on your wishlist'] },
  liveLocation:{ icon:'📍', bullets:['Share real-time location','Group coordination','1-hour expiring share'] },
  splitCosts: { icon:'💰', bullets:['Auto per-person calc','Track all trip expenses','Send to group chat'] },
  hotelMatch: { icon:'🏨', bullets:['See who booked nearby','Coordinate pickups','Meet before the trip'] },
  aiWriter:   { icon:'✨', bullets:['3 AI-written bio options','Adventurous/romantic/witty','Stand out from the crowd'] },
  travelPact: { icon:'🤝', bullets:['Commit to a journey together','Mutual destination pact','Unlimited pacts with Elite'] },
  multiTrip:  { icon:'🌍', bullets:['Plan multiple destinations','Route, dates and transport in one flow','Save your full journey'] },
  horoscope:  { icon:'🔮', bullets:['Weekly travel horoscope','Personal destination guidance','Compatible travel signs'] },
};

const PREMIUM_FALLBACK_BULLETS = {
  bg: {
    likes:['Неограничени харесвания всеки ден','Без дневни лимити','Харесвай всеки, когото искаш'],
    travelPact:['Обещайте си пътуване заедно','Взаимен пакт за дестинация','Неограничени пактове с Elite'],
    multiTrip:['Планирай няколко дестинации','Маршрут, дати и транспорт на едно място','Запази цялото пътешествие'],
    horoscope:['Седмичен пътнически хороскоп','Лични насоки за дестинация','Съвместими пътнически знаци'],
  },
  en: {
    likes:['Unlimited likes every day','No daily caps or wait times','Like everyone you want'],
    travelPact:['Commit to a journey together','Mutual destination pact','Unlimited pacts with Elite'],
    multiTrip:['Plan multiple destinations','Route, dates and transport in one flow','Save your full journey'],
    horoscope:['Weekly travel horoscope','Personal destination guidance','Compatible travel signs'],
  },
  es: {
    likes:['Likes ilimitados cada día','Sin límites diarios ni esperas','Dale like a quien quieras'],
    travelPact:['Comprométanse a viajar juntos','Pacto mutuo de destino','Pactos ilimitados con Elite'],
    multiTrip:['Planifica varios destinos','Ruta, fechas y transporte en un solo flujo','Guarda todo tu viaje'],
    horoscope:['Horóscopo de viaje semanal','Guía personal de destino','Signos viajeros compatibles'],
  },
  fr: {
    likes:['Likes illimités chaque jour','Sans limites quotidiennes ni attente','Aime qui tu veux'],
    travelPact:['Engagez-vous à voyager ensemble','Pacte mutuel de destination','Pactes illimités avec Elite'],
    multiTrip:['Planifie plusieurs destinations','Itinéraire, dates et transport au même endroit','Sauvegarde tout ton voyage'],
    horoscope:['Horoscope de voyage hebdomadaire','Conseils personnalisés de destination','Signes de voyage compatibles'],
  },
  de: {
    likes:['Unbegrenzte Likes jeden Tag','Keine Tageslimits oder Wartezeiten','Like wen du möchtest'],
    travelPact:['Verpflichtet euch zu einer gemeinsamen Reise','Gemeinsamer Ziel-Pakt','Unbegrenzte Pakte mit Elite'],
    multiTrip:['Plane mehrere Reiseziele','Route, Daten und Transport in einem Ablauf','Speichere deine ganze Reise'],
    horoscope:['Wöchentliches Reisehoroskop','Persönliche Ziel-Empfehlung','Kompatible Reisezeichen'],
  },
  el: {
    likes:['Απεριόριστα likes κάθε μέρα','Χωρίς ημερήσια όρια ή αναμονές','Κάνε like σε όποιον θέλεις'],
    travelPact:['Δεσμευτείτε για κοινό ταξίδι','Αμοιβαίο pact προορισμού','Απεριόριστα pacts με Elite'],
    multiTrip:['Σχεδίασε πολλούς προορισμούς','Διαδρομή, ημερομηνίες και μεταφορά σε ένα flow','Αποθήκευσε όλο το ταξίδι'],
    horoscope:['Εβδομαδιαίο ταξιδιωτικό ωροσκόπιο','Προσωπική καθοδήγηση προορισμού','Συμβατά ταξιδιωτικά ζώδια'],
  },
  it: {
    likes:['Like illimitati ogni giorno','Nessun limite giornaliero o attesa','Metti like a chi vuoi'],
    travelPact:['Impegnatevi per un viaggio insieme','Patto reciproco di destinazione','Patti illimitati con Elite'],
    multiTrip:['Pianifica più destinazioni','Itinerario, date e trasporti in un solo flusso','Salva tutto il viaggio'],
    horoscope:['Oroscopo di viaggio settimanale','Guida personale alla destinazione','Segni di viaggio compatibili'],
  },
};

function PremiumModal({ feature, t, lang, onClose, onUpgrade }) {
  const meta = PREMIUM_FEATURE_META[feature] || PREMIUM_FEATURE_META.likes;
  const bullets = (t.premiumBullets && t.premiumBullets[feature]) || PREMIUM_FALLBACK_BULLETS[lang]?.[feature] || PREMIUM_FALLBACK_BULLETS[lang]?.likes || PREMIUM_FALLBACK_BULLETS.en[feature] || meta.bullets;
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-premium-modal" style={MODAL_S} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <div className="ts-premium-modal-icon">{meta.icon}</div>
        <h2 className="ts-serif ts-premium-modal-title">{t.connect.premiumFeature}</h2>
        <ul className="ts-premium-bullets">
          {bullets.map((b,i)=><li key={i}>✓ {b}</li>)}
        </ul>
        <button className="ts-btn-gold-full" onClick={onUpgrade} style={{marginTop:'1.2rem'}}>{t.connect.upgrade} ⭐</button>
        <button className="ts-btn-ghost-sm ts-premium-later-btn" onClick={onClose}>{t.connect.seePlans}</button>
      </div>
    </div>
  );
}

// ── PricingPage ───────────────────────────────────────────────────────────────
function PricingPage({ lang, setLang, t, onBack, onNavigate, onEntitlementVerified }) {
  const [billing, setBilling] = useState('monthly');
  const [googlePlayProducts, setGooglePlayProducts] = useState([]);
  const [googlePlayReady, setGooglePlayReady] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingMessage, setBillingMessage] = useState('');
  const [, setRestoreDiagnostics] = useState(null);
  const p = PRICING_T[lang];
  const legal = legalUi(lang);
  const ps = legal.pricingSummary;
  const isAndroidBilling = isCapacitorAndroid();
  const billingMessages = {
    bg:{ loading:'Google Play се зарежда…', unavailable:'Google Play плащанията не са достъпни.', pending:'Покупката чака потвърждение.', cancelled:'Покупката е отменена.', failed:'Покупката не бе потвърдена.', replacementUnavailable:'Активният Премиум абонамент не може да бъде актуализиран безопасно. Опитайте отново.', downgradeUnavailable:'Активният Елит абонамент не може да бъде понижен безопасно. Опитайте отново.', success:'Абонаментът е потвърден.', restore:'Възстанови покупки', restored:'Покупките са възстановени.', none:'Няма покупки за възстановяване.' },
    en:{ loading:'Loading Google Play…', unavailable:'Google Play billing is unavailable.', pending:'The purchase is pending.', cancelled:'The purchase was cancelled.', failed:'The purchase could not be verified.', replacementUnavailable:'The active Premium subscription could not be upgraded safely. Please try again.', downgradeUnavailable:'The active Elite subscription could not be downgraded safely. Please try again.', success:'Subscription verified.', restore:'Restore purchases', restored:'Purchases restored.', none:'No purchases to restore.' },
    es:{ loading:'Cargando Google Play…', unavailable:'Los pagos de Google Play no están disponibles.', pending:'La compra está pendiente.', cancelled:'La compra fue cancelada.', failed:'No se pudo verificar la compra.', replacementUnavailable:'No se pudo actualizar de forma segura la suscripción Premium activa. Inténtalo de nuevo.', downgradeUnavailable:'No se pudo bajar de forma segura la suscripción Elite activa. Inténtalo de nuevo.', success:'Suscripción verificada.', restore:'Restaurar compras', restored:'Compras restauradas.', none:'No hay compras para restaurar.' },
    fr:{ loading:'Chargement de Google Play…', unavailable:'La facturation Google Play est indisponible.', pending:'L’achat est en attente.', cancelled:'L’achat a été annulé.', failed:'L’achat n’a pas pu être vérifié.', replacementUnavailable:'L’abonnement Premium actif n’a pas pu être mis à niveau en toute sécurité. Réessayez.', downgradeUnavailable:'L’abonnement Elite actif n’a pas pu être rétrogradé en toute sécurité. Réessayez.', success:'Abonnement vérifié.', restore:'Restaurer les achats', restored:'Achats restaurés.', none:'Aucun achat à restaurer.' },
    de:{ loading:'Google Play wird geladen…', unavailable:'Google Play-Zahlungen sind nicht verfügbar.', pending:'Der Kauf steht noch aus.', cancelled:'Der Kauf wurde abgebrochen.', failed:'Der Kauf konnte nicht bestätigt werden.', replacementUnavailable:'Das aktive Premium-Abonnement konnte nicht sicher aktualisiert werden. Bitte versuche es erneut.', downgradeUnavailable:'Das aktive Elite-Abonnement konnte nicht sicher herabgestuft werden. Bitte versuche es erneut.', success:'Abonnement bestätigt.', restore:'Käufe wiederherstellen', restored:'Käufe wiederhergestellt.', none:'Keine Käufe zum Wiederherstellen.' },
    el:{ loading:'Φόρτωση Google Play…', unavailable:'Οι πληρωμές Google Play δεν είναι διαθέσιμες.', pending:'Η αγορά εκκρεμεί.', cancelled:'Η αγορά ακυρώθηκε.', failed:'Η αγορά δεν επαληθεύτηκε.', replacementUnavailable:'Δεν ήταν δυνατή η ασφαλής αναβάθμιση της ενεργής συνδρομής Premium. Δοκιμάστε ξανά.', downgradeUnavailable:'Δεν ήταν δυνατή η ασφαλής υποβάθμιση της ενεργής συνδρομής Elite. Δοκιμάστε ξανά.', success:'Η συνδρομή επαληθεύτηκε.', restore:'Επαναφορά αγορών', restored:'Οι αγορές επαναφέρθηκαν.', none:'Δεν υπάρχουν αγορές για επαναφορά.' },
    it:{ loading:'Caricamento Google Play…', unavailable:'I pagamenti Google Play non sono disponibili.', pending:'L’acquisto è in sospeso.', cancelled:'L’acquisto è stato annullato.', failed:'Impossibile verificare l’acquisto.', replacementUnavailable:'Non è stato possibile aggiornare in sicurezza l’abbonamento Premium attivo. Riprova.', downgradeUnavailable:'Non è stato possibile eseguire in sicurezza il downgrade dell’abbonamento Elite attivo. Riprova.', success:'Abbonamento verificato.', restore:'Ripristina acquisti', restored:'Acquisti ripristinati.', none:'Nessun acquisto da ripristinare.' },
  };
  const bm = billingMessages[lang] || billingMessages.en;
  const samePlanMessage = {
    bg: 'Този абонамент вече е активен.',
    en: 'This subscription is already active.',
    es: 'Esta suscripción ya está activa.',
    fr: 'Cet abonnement est déjà actif.',
    de: 'Dieses Abonnement ist bereits aktiv.',
    el: 'Αυτή η συνδρομή είναι ήδη ενεργή.',
    it: 'Questo abbonamento è già attivo.',
  }[lang] || 'This subscription is already active.';
  const goPolicy = (screen) => (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate(screen);
  };

  useEffect(() => {
    if (!isAndroidBilling) return undefined;
    let active = true;
    setBillingMessage(bm.loading);
    initializeGooglePlayBilling()
      .then(async () => {
        try {
          return await getGooglePlayProducts();
        } catch {
          return [];
        }
      })
      .then((products) => {
        if (!active) return;
        setGooglePlayProducts(products);
        setGooglePlayReady(true);
        setBillingMessage('');
      })
      .catch(() => {
        if (!active) return;
        setGooglePlayReady(false);
        setBillingMessage(bm.unavailable);
      });
    return () => { active = false; };
  }, [isAndroidBilling]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedBasePlanId = billing === 'annual' ? 'yearly' : 'monthly';
  const playPriceFor = (planKey) => {
    const product = googlePlayProducts.find(({ id }) => id === `travelsoul_${planKey}`);
    const basePlan = product?.basePlans?.find(({ id }) => id === selectedBasePlanId);
    const phases = basePlan?.pricingPhases || [];
    return phases.length ? phases[phases.length - 1].displayPrice : null;
  };
  const authenticatedSession = async () => {
    if (!supabase) throw new Error('billing_auth_required');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id || !session.access_token) throw new Error('billing_auth_required');
    googlePlayStore.applicationUsername = session.user.id;
    googlePlayStore.obfuscator = 'disabled';
    return session;
  };

  const verifyPurchaseToken = async (purchaseToken, session) => {
    const response = await fetch(`${API_BASE}/api/google-play/verify-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ purchaseToken }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      return {
        google_play_verification_error:
          payload?.error || 'google_play_verification_request_failed',
      };
    }
    return payload;
  };

  const verifyCurrentGooglePlaySubscription = async (purchaseToken, session) => {
    const response = await fetch(
      `${API_BASE}/api/google-play/verify-subscription/preflight`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ purchaseToken }),
      },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      return {
        google_play_verification_error:
          payload?.error || 'google_play_verification_request_failed',
      };
    }
    return payload;
  };

  const purchasePlan = async (planKey) => {
    if (!isAndroidBilling || billingBusy || !googlePlayReady) return;
    setBillingBusy(true);
    setBillingMessage('');
    try {
      const session = await authenticatedSession();
      const result = await purchaseGooglePlaySubscription(
        `travelsoul_${planKey}`,
        selectedBasePlanId,
        (currentPurchaseToken) =>
          verifyCurrentGooglePlaySubscription(currentPurchaseToken, session),
      );
      if (!result?.purchaseToken) {
        setBillingMessage(result?.status === 'pending'
          ? bm.pending
          : result?.status === 'cancelled'
            ? bm.cancelled
            : result?.code === 'premium_replacement_token_missing'
              ? bm.replacementUnavailable
              : result?.code === 'elite_replacement_token_missing'
                ? bm.downgradeUnavailable
                : result?.code === 'same_product_base_plan_already_owned'
                  ? samePlanMessage
                : bm.failed);
        return;
      }
      const verification = await verifyPurchaseToken(result.purchaseToken, session);
      if (!verification?.google_play_subscription) {
        setBillingMessage(bm.failed);
        return;
      }
      await onEntitlementVerified?.();
      setBillingMessage(bm.success);
    } catch {
      setBillingMessage(bm.failed);
    } finally {
      setBillingBusy(false);
    }
  };
  const restorePurchases = async () => {
    if (!isAndroidBilling || billingBusy || !googlePlayReady) return;
    setBillingBusy(true);
    setBillingMessage('');
    setRestoreDiagnostics(null);
    try {
      const session = await authenticatedSession();
      const restoreResult = await restoreGooglePlayPurchases(
        (purchaseToken) => verifyPurchaseToken(purchaseToken, session),
      );
      setRestoreDiagnostics(restoreResult.diagnostics);
      if (restoreResult.status === 'verified') {
        await onEntitlementVerified?.();
        setBillingMessage(bm.restored);
      } else if (restoreResult.code === 'google_play_restore_no_candidate') {
        setBillingMessage(`${bm.none} (${restoreResult.code})`);
      } else {
        const verificationError =
          restoreResult.diagnostics?.googlePlayVerificationError;
        setBillingMessage(
          `${bm.failed} (${restoreResult.code}${
            verificationError ? `: ${verificationError}` : ''
          })`,
        );
      }
    } catch {
      setBillingMessage(`${bm.failed} (google_play_restore_unexpected_error)`);
    } finally {
      setBillingBusy(false);
    }
  };
  const plans = [
    {
      key:'premium', icon:'⭐', name:p.premium||'Premium', price:'€7.99', priceAnnual:'€79.99',
      badge:p.mostPopular, promo:p.promo,
      features: p.premiumFeatures || ['Unlimited likes & messages','See who liked you','1 Boost per day','Rewind last pass','Passport mode','Incognito browsing','Advanced filters','Travel Stats','Real-time translation'],
      missing:   p.premiumMissing  || ['Video chat, Concierge'],
    },
    {
      key:'elite', icon:'💎', name:p.elite||'Elite', price:'€14.99', priceAnnual:'€149',
      badge:null, promo:p.promo,
      features: p.eliteFeatures || ['Everything in Premium','Video chat','Elite 💎 badge','Organize group trips','VIP concierge','AI trip planner'],
      missing:[],
    },
  ];
  return (
    <div className="ts-app ts-pricing-page">
      <div className="ts-pricing-header">
        <button className="ts-btn-ghost-sm" onClick={onBack}>{p.back}</button>
        <h1 className="ts-serif ts-pricing-title">{p.title}</h1>
        {setLang && <LangPicker lang={lang} setLang={setLang} />}
        <div className="ts-billing-toggle">
          <button className={`ts-billing-btn ${billing==='monthly'?'active':''}`} onClick={()=>setBilling('monthly')}>{p.monthly}</button>
          <button className={`ts-billing-btn ${billing==='annual'?'active':''}`} onClick={()=>setBilling('annual')}>{p.annual} <span className="ts-save-badge">{p.save}</span></button>
        </div>
      </div>
      <div className="ts-pricing-grid">
        {plans.map(plan=>{
          const playPrice = isAndroidBilling ? playPriceFor(plan.key) : null;
          const displayedPrice = isAndroidBilling
            ? (playPrice || '—')
            : (billing==='annual' ? plan.priceAnnual : plan.price);
          return (
          <div key={plan.key} className={`ts-plan-card ts-plan-${plan.key}`}>
            {plan.badge && <div className="ts-plan-badge">{plan.badge}</div>}
            <div className="ts-plan-icon">{plan.icon}</div>
            <h2 className="ts-plan-name ts-serif">{plan.name}</h2>
            <div className="ts-plan-price">
              <span className="ts-plan-amount">{displayedPrice}</span>
              <span className="ts-plan-period">{billing==='annual'?p.perYear:p.perMonth}</span>
            </div>
            {plan.promo && <div className="ts-plan-promo">{plan.promo}</div>}
            <ul className="ts-plan-features">
              {plan.features.map((f,i)=><li key={i} className="ts-pf-yes">✓ {f}</li>)}
              {plan.missing.map((f,i)=><li key={i} className="ts-pf-no">✗ {f}</li>)}
            </ul>
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,padding:12,margin:'12px 0',fontSize:'0.8rem',lineHeight:1.55,color:'#c9d1d9'}}>
              <p style={{margin:'0 0 4px'}}><strong>{ps.plan}:</strong> {plan.name}</p>
              <p style={{margin:'0 0 4px'}}><strong>{ps.price}:</strong> {displayedPrice}{isAndroidBilling ? '' : ' EUR'}</p>
              <p style={{margin:'0 0 4px'}}><strong>{ps.billingPeriod}:</strong> {billing==='annual'?ps.annual:ps.monthly}</p>
              <p style={{margin:'0 0 4px'}}><strong>{ps.autoRenewal}:</strong> {ps.paidLaunch}</p>
              <p style={{margin:'0 0 4px'}}><strong>{ps.cancellation}:</strong> {ps.cancellationText}</p>
              <p style={{margin:'0 0 4px'}}><strong>{ps.refund}:</strong> {ps.refundText}</p>
              <p style={{margin:0}}>
                <a href="/terms" onClick={goPolicy('terms')}>{legal.footerLinks.terms}</a> · <a href="/privacy" onClick={goPolicy('privacy')}>{legal.footerLinks.privacy}</a> · <a href="/refund" onClick={goPolicy('refund')}>{legal.footerLinks.refund}</a>
              </p>
            </div>
            {isAndroidBilling ? (
              <button
                className="ts-btn-gold-full"
                disabled={billingBusy || !googlePlayReady}
                onClick={()=>purchasePlan(plan.key)}
                style={{opacity:(billingBusy || !googlePlayReady)?0.55:1,cursor:(billingBusy || !googlePlayReady)?'not-allowed':'pointer'}}
              >{ps.paymentsSoon}</button>
            ) : (
              <>
                <button
                  className="ts-btn-gold-full"
                  onClick={()=>window.open(GOOGLE_PLAY_URL, '_blank', 'noopener')}
                >{ps.paymentsSoon}</button>
                <p style={{fontSize:'0.78rem',color:'var(--muted)',textAlign:'center',marginTop:8,lineHeight:1.4}}>{ps.webToAndroidHint}</p>
              </>
            )}
          </div>
          );
        })}
      </div>
      {isAndroidBilling && (
        <div style={{textAlign:'center',margin:'18px auto',maxWidth:420}}>
          <button className="ts-btn-ghost-sm" disabled={billingBusy || !googlePlayReady} onClick={restorePurchases}>{bm.restore}</button>
          {billingMessage && <p role="status" style={{color:'var(--muted)',fontSize:'0.85rem',marginTop:10}}>{billingMessage}</p>}
        </div>
      )}
      <ComplianceNotice lang={lang} />
      <PublicFooter lang={lang} onNavigate={onNavigate} />
    </div>
  );
}

// ── PassportModal ─────────────────────────────────────────────────────────────
function PassportModal({ t, selectedCity, onSelect, onClose }) {
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-passport-modal" style={{...MODAL_S, maxWidth:520}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">🌍 {t.connect.passport}</h2>
        <p className="ts-modal-sub">{t.connect.passportDesc}</p>
        <div className="ts-city-grid">
          {CITIES.map(c=>(
            <div key={c.city} className={`ts-city-card ${selectedCity===c.city?'active':''}`} onClick={()=>{onSelect(c.city===selectedCity?null:c);onClose();}}>
              <img src={c.photo} alt={c.city} className="ts-city-img" />
              <div className="ts-city-info"><span className="ts-city-flag">{c.flag}</span><span className="ts-city-name">{c.city}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── FiltersPanel ──────────────────────────────────────────────────────────────
function FiltersPanel({ t, filters, onChange, onClose, lang }) {
  const [local, setLocal] = useState({...filters});
  const dna = DNA_TRAIT_KEYS;
  function toggleDna(k) { setLocal(f=>({...f,dnaTypes:f.dnaTypes.includes(k)?f.dnaTypes.filter(x=>x!==k):[...f.dnaTypes,k]})); }
  function apply() { onChange(local); onClose(); }
  function clear() { setLocal({...DEFAULT_FILTERS}); onChange({...DEFAULT_FILTERS}); onClose(); }
  return (
    <div className="ts-filters-panel">
      <div className="ts-filters-header">
        <h3 className="ts-serif">{t.connect.filtersBtn}</h3>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{t.connect.filters?.age||'Age:'} {local.ageMin} – {local.ageMax}</label>
        <div className="ts-range-row">
          <input type="range" min="18" max="65" value={local.ageMin} onChange={e=>setLocal(f=>({...f,ageMin:Math.min(+e.target.value,f.ageMax-1)}))} className="ts-range" />
          <input type="range" min="18" max="65" value={local.ageMax} onChange={e=>setLocal(f=>({...f,ageMax:Math.max(+e.target.value,f.ageMin+1)}))} className="ts-range" />
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{t.connect.filters?.travelDna||'Travel DNA'}</label>
        <div className="ts-dna-checks">
          {dna.map(k=>(
            <label key={k} className={`ts-dna-check ${local.dnaTypes.includes(k)?'active':''}`}>
              <input type="checkbox" checked={local.dnaTypes.includes(k)} onChange={()=>toggleDna(k)} hidden />
              {t.dna.traits[k]}
            </label>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{t.connect.filters?.minCompat||'Min compatibility:'} {local.compatMin}%</label>
        <input type="range" min="50" max="100" value={local.compatMin} onChange={e=>setLocal(f=>({...f,compatMin:+e.target.value}))} className="ts-range" />
      </div>
      <div className="ts-filter-group ts-filter-toggles">
        <label className="ts-filter-toggle-row"><input type="checkbox" checked={local.onlineOnly} onChange={e=>setLocal(f=>({...f,onlineOnly:e.target.checked}))} /> {t.connect.filters?.onlineOnly||'Online only'}</label>
        <label className="ts-filter-toggle-row"><input type="checkbox" checked={local.verifiedOnly} onChange={e=>setLocal(f=>({...f,verifiedOnly:e.target.checked}))} /> {t.connect.filters?.verifiedOnly||'Verified only'}</label>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{t.connect.filters?.distance||'Distance'}</label>
        <div className="ts-distance-btns">
          {[{v:'50km',l:t.connect.filters?.dist50||'50km'},{v:'500km',l:t.connect.filters?.dist500||'500km'},{v:'worldwide',l:t.connect.filters?.distWorld||'worldwide'}].map(d=>(
            <button key={d.v} className={`ts-distance-btn ${local.distance===d.v?'active':''}`} onClick={()=>setLocal(f=>({...f,distance:d.v}))}>{d.l}</button>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Ръст':lang==='es'?'Altura':lang==='fr'?'Taille':lang==='de'?'Größe':lang==='el'?'Ύψος':lang==='it'?'Altezza':'Height'}: {local.heightMin} – {local.heightMax} {lang==='bg'?'см':lang==='es'?'cm':lang==='fr'?'cm':lang==='de'?'cm':lang==='el'?'εκ.':lang==='it'?'cm':'cm'}</label>
        <div className="ts-range-row">
          <input type="range" min={150} max={210} value={local.heightMin} onChange={e=>setLocal(f=>({...f,heightMin:Math.min(+e.target.value,f.heightMax-5)}))} className="ts-range"/>
          <input type="range" min={150} max={210} value={local.heightMax} onChange={e=>setLocal(f=>({...f,heightMax:Math.max(+e.target.value,f.heightMin+5)}))} className="ts-range"/>
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Телосложение':lang==='es'?'Tipo de cuerpo':lang==='fr'?'Morphologie':lang==='de'?'Körperbau':lang==='el'?'Σωματότυπος':lang==='it'?'Corporatura':'Body Type'}</label>
        <div className="ts-distance-btns" style={{flexWrap:'wrap'}}>
          {[{k:'athletic',bg:'💪 Атлетично',en:'💪 Athletic',es:'💪 Atlético',fr:'💪 Athlétique',de:'💪 Athletisch',el:'💪 Αθλητικό',it:'💪 Atletico'},{k:'slim',bg:'🪶 Слабо',en:'🪶 Slim',es:'🪶 Delgado',fr:'🪶 Mince',de:'🪶 Schlank',el:'🪶 Λεπτό',it:'🪶 Snello'},{k:'average',bg:'🧍 Средно',en:'🧍 Average',es:'🧍 Promedio',fr:'🧍 Moyen',de:'🧍 Durchschnittlich',el:'🧍 Μέτριο',it:'🧍 Medio'},{k:'large',bg:'🐻 Едро',en:'🐻 Large',es:'🐻 Grande',fr:'🐻 Grand',de:'🐻 Groß',el:'🐻 Μεγαλόσωμο',it:'🐻 Robusto'}].map(b=>(
            <button key={b.k} className={`ts-distance-btn ${local.bodyType.includes(b.k)?'active':''}`}
              onClick={()=>setLocal(f=>({...f,bodyType:f.bodyType.includes(b.k)?f.bodyType.filter(x=>x!==b.k):[...f.bodyType,b.k]}))}>
              {lang==='bg'?b.bg:lang==='es'?b.es:lang==='fr'?b.fr:lang==='de'?b.de:lang==='el'?b.el:lang==='it'?b.it:b.en}
            </button>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Цел':lang==='es'?'Objetivo':lang==='fr'?'Objectif':lang==='de'?'Ziel':lang==='el'?'Στόχος':lang==='it'?'Obiettivo':'Goal'}</label>
        <div className="ts-distance-btns" style={{flexWrap:'wrap'}}>
          {[{k:'',l:lang==='bg'?'Всички':lang==='es'?'Todos':lang==='fr'?'Tous':lang==='de'?'Alle':lang==='el'?'Όλα':lang==='it'?'Tutti':'All'},{k:'serious',l:'💑 '+(lang==='bg'?'Връзка':lang==='es'?'Relación':lang==='fr'?'Sérieuse':lang==='de'?'Beziehung':lang==='el'?'Σχέση':lang==='it'?'Relazione':'Serious')},{k:'travel',l:'🌍 '+(lang==='bg'?'Пътуване':lang==='es'?'Viaje':lang==='fr'?'Voyage':lang==='de'?'Reise':lang==='el'?'Ταξίδι':lang==='it'?'Viaggio':'Travel')},{k:'friends',l:'👫 '+(lang==='bg'?'Приятелство':lang==='es'?'Amistad':lang==='fr'?'Amis':lang==='de'?'Freundschaft':lang==='el'?'Φιλία':lang==='it'?'Amicizia':'Friends')},{k:'flirt',l:'🔥 '+(lang==='bg'?'Флирт':lang==='es'?'Ligar':lang==='fr'?'Flirt':lang==='de'?'Flirten':lang==='el'?'Φλερτ':lang==='it'?'Flirt':'Flirt')}].map(x=>(
            <button key={x.k} className={`ts-distance-btn ${local.goal===x.k?'active':''}`} onClick={()=>setLocal(f=>({...f,goal:x.k}))}>{x.l}</button>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Пуши':lang==='es'?'Fuma':lang==='fr'?'Fume':lang==='de'?'Raucht':lang==='el'?'Καπνίζει':lang==='it'?'Fuma':'Smoking'}</label>
        <div className="ts-distance-btns">
          {[{v:'any',l:lang==='bg'?'Без значение':lang==='es'?'Cualquiera':lang==='fr'?'Peu importe':lang==='de'?'Egal':lang==='el'?'Αδιάφορο':lang==='it'?'Indifferente':'Any'},{v:'no',l:'🚭 '+(lang==='bg'?'Не пуши':lang==='es'?'No fuma':lang==='fr'?'Ne fume pas':lang==='de'?'Nichtraucher':lang==='el'?'Μη καπνιστής':lang==='it'?'Non fumatore':'Non-smoker')},{v:'sometimes',l:'🌿 '+(lang==='bg'?'Понякога':lang==='es'?'A veces':lang==='fr'?'Parfois':lang==='de'?'Manchmal':lang==='el'?'Μερικές φορές':lang==='it'?'A volte':'Occasionally')}].map(x=>(
            <button key={x.v} className={`ts-distance-btn ${local.smoking===x.v?'active':''}`} onClick={()=>setLocal(f=>({...f,smoking:x.v}))}>{x.l}</button>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Алкохол':lang==='es'?'Alcohol':lang==='fr'?'Alcool':lang==='de'?'Alkohol':lang==='el'?'Αλκοόλ':lang==='it'?'Alcol':'Alcohol'}</label>
        <div className="ts-distance-btns">
          {[{v:'any',l:lang==='bg'?'Без значение':lang==='es'?'Cualquiera':lang==='fr'?'Peu importe':lang==='de'?'Egal':lang==='el'?'Αδιάφορο':lang==='it'?'Indifferente':'Any'},{v:'no',l:'🚱 '+(lang==='bg'?'Не пие':lang==='es'?'No bebe':lang==='fr'?'Ne boit pas':lang==='de'?'Trinkt nicht':lang==='el'?'Δεν πίνω':lang==='it'?'Non beve':'Non-drinker')},{v:'social',l:'🥂 '+(lang==='bg'?'Социално':lang==='es'?'Socialmente':lang==='fr'?'Occasionnellement':lang==='de'?'Gesellig':lang==='el'?'Κοινωνικά':lang==='it'?'Socievole':'Socially')}].map(x=>(
            <button key={x.v} className={`ts-distance-btn ${local.alcohol===x.v?'active':''}`} onClick={()=>setLocal(f=>({...f,alcohol:x.v}))}>{x.l}</button>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Деца':lang==='es'?'Hijos':lang==='fr'?'Enfants':lang==='de'?'Kinder':lang==='el'?'Παιδιά':lang==='it'?'Figli':'Children'}</label>
        <div className="ts-distance-btns">
          {[{v:'any',l:lang==='bg'?'Без значение':lang==='es'?'Cualquiera':lang==='fr'?'Peu importe':lang==='de'?'Egal':lang==='el'?'Αδιάφορο':lang==='it'?'Indifferente':'Any'},{v:'has',l:'👶 '+(lang==='bg'?'Има':lang==='es'?'Tiene':lang==='fr'?'A':lang==='de'?'Hat':lang==='el'?'Έχει':lang==='it'?'Ha':'Has')},{v:'none',l:'🚫 '+(lang==='bg'?'Няма':lang==='es'?'Ninguno':lang==='fr'?'Aucun':lang==='de'?'Keine':lang==='el'?'Κανένα':lang==='it'?'Nessuno':'None')},{v:'wants',l:'💭 '+(lang==='bg'?'Иска':lang==='es'?'Quiere':lang==='fr'?'Veut':lang==='de'?'Möchte':lang==='el'?'Θέλει':lang==='it'?'Vuole':'Wants')}].map(x=>(
            <button key={x.v} className={`ts-distance-btn ${local.children===x.v?'active':''}`} onClick={()=>setLocal(f=>({...f,children:x.v}))}>{x.l}</button>
          ))}
        </div>
      </div>
      <div className="ts-filter-group">
        <label className="ts-filter-label">{lang==='bg'?'Зодия':lang==='es'?'Signo zodiacal':lang==='fr'?'Signe zodiacal':lang==='de'?'Sternzeichen':lang==='el'?'Ζώδιο':lang==='it'?'Segno zodiacale':'Zodiac'}</label>
        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
          {ZODIAC_CALC.map(z=>(
            <button key={z.sign} className={`ts-distance-btn ${local.zodiac.includes(z.sign)?'active':''}`}
              title={lang==='bg'?z.bgName:lang==='es'?z.esName:lang==='fr'?z.frName:lang==='de'?z.deName:lang==='el'?z.elName:lang==='it'?z.itName:z.enName}
              onClick={()=>setLocal(f=>({...f,zodiac:f.zodiac.includes(z.sign)?f.zodiac.filter(s=>s!==z.sign):[...f.zodiac,z.sign]}))}>
              {z.sign}
            </button>
          ))}
        </div>
      </div>
      <div className="ts-filter-actions">
        <button className="ts-btn-ghost-sm" onClick={clear}>{t.connect.filters?.clear||'Clear'}</button>
        <button className="ts-btn-gold-full" style={{flex:1}} onClick={apply}>{t.connect.filters?.apply||'Apply'}</button>
      </div>
    </div>
  );
}

// ── TravelStats ───────────────────────────────────────────────────────────────
function TravelStats({ t, lang, plan, onNeedPremium }) {
  if (plan==='free') return (
    <div className="ts-stats-locked">
      <div className="ts-stats-blur-wrap">
        <div className="ts-stats-blur-content">
          {[
            {icon:'🌍',label:t.journeys.stats.countries,val:7},
            {icon:'✈️',label:t.journeys.stats.trips,val:12},
            {icon:'🗺️',label:t.journeys.stats.km,val:'48,320'},
            {icon:'💫',label:t.journeys.stats.matches,val:34},
          ].map(s=>(
            <div key={s.label} className="ts-stat-card ts-stat-blur">
              <span className="ts-stat-icon">{s.icon}</span>
              <span className="ts-stat-val">{s.val}</span>
              <span className="ts-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
        <div className="ts-stats-lock-overlay">
          <div className="ts-stats-lock-icon">📊</div>
          <p>{t.connect.premiumFeature}</p>
          <button className="ts-btn-gold-lg" onClick={onNeedPremium}>{t.connect.upgrade} ⭐</button>
        </div>
      </div>
    </div>
  );
  const maxBar = Math.max(...STATS_DATA.monthly);
  return (
    <div className="ts-stats-wrap">
      <div className="ts-stats-grid">
        {[
          {icon:'🌍',label:t.journeys.stats.countries,val:STATS_DATA.countries},
          {icon:'✈️',label:t.journeys.stats.trips,val:STATS_DATA.trips},
          {icon:'🗺️',label:t.journeys.stats.km,val:STATS_DATA.km.toLocaleString()},
          {icon:'💫',label:t.journeys.stats.matches,val:STATS_DATA.matches},
          {icon:'⭐',label:t.journeys.stats.superLikes||'Super Likes',val:STATS_DATA.superLikes},
          {icon:'🏆',label:t.journeys.stats.leaderboard||'Leaderboard',val:`#${STATS_DATA.rank}`},
        ].map(s=>(
          <div key={s.label} className="ts-stat-card">
            <span className="ts-stat-icon">{s.icon}</span>
            <span className="ts-stat-val">{s.val}</span>
            <span className="ts-stat-label">{s.label}</span>
          </div>
        ))}
      </div>
      <div className="ts-stats-section-title">{t.journeys.stats.footprint||'🌍 Travel Footprint'}</div>
      <div className="ts-footprint">{STATS_DATA.footprint.map((f,i)=><span key={i} className="ts-fp-flag">{f}</span>)}</div>
      <div className="ts-stats-section-title">{t.journeys.stats.tripsPerMonth||'📅 Trips per Month'}</div>
      <div className="ts-month-chart">
        {STATS_DATA.monthly.map((v,i)=>(
          <div key={i} className="ts-month-col">
            <div className="ts-month-bar-wrap">
              <div className="ts-month-bar" style={{height:`${maxBar>0?(v/maxBar)*100:0}%`}}>{v>0&&<span className="ts-month-val">{v}</span>}</div>
            </div>
            <span className="ts-month-lbl">{monthLabels(lang)[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── NEW F1+F2: Swipe Card + Overlays ─────────────────────────────────────────
function MatchOverlay({ profile, matchPair, t, onSendMessage, onKeepSwiping }) {
  useEffect(() => { const id = setTimeout(onKeepSwiping, 4000); return () => clearTimeout(id); }, [onKeepSwiping]);
  const me = matchPair?.me || {};
  const match = matchPair?.match || profile || {};
  const myInitial = (me.name || '?')[0].toUpperCase();
  const matchInitial = (match.name || '?')[0].toUpperCase();
  return (
    <div className="ts-fullscreen-overlay ts-match-overlay">
      <div className="ts-match-content">
        <div className="ts-match-title">{t.connect.itsAMatch}</div>
        <div className="ts-match-photos">
          <div className="ts-match-photo-ring">{me.photo ? <img src={me.photo} alt={me.name || 'You'} /> : <div className="ts-avatar-initials">{myInitial}</div>}</div>
          <div className="ts-match-heart">💫</div>
          <div className="ts-match-photo-ring">{match.photo ? <img src={match.photo} alt={match.name} /> : <div className="ts-avatar-initials">{matchInitial}</div>}</div>
        </div>
        <p className="ts-match-name">{match.name}</p>
        <div className="ts-match-actions">
          <button className="ts-btn-gold-lg" onClick={onSendMessage}>{t.connect.sendMsg}</button>
          <button className="ts-btn-ghost-sm" style={{color:'#fff',borderColor:'rgba(255,255,255,0.3)'}} onClick={onKeepSwiping}>{t.connect.keepGoing}</button>
        </div>
      </div>
    </div>
  );
}

function SuperLikeOverlay({ profile, t, onDismiss }) {
  useEffect(() => { const id = setTimeout(onDismiss, 2000); return () => clearTimeout(id); }, [onDismiss]);
  return (
    <div className="ts-fullscreen-overlay ts-superlike-overlay" onClick={onDismiss}>
      <div className="ts-superlike-content">
        <div className="ts-superlike-star">⭐</div>
        <div className="ts-superlike-title">{t.connect.superLikeSent}</div>
        {profile.photo ? <img src={profile.photo} alt={profile.name} className="ts-superlike-photo" /> : <div className="ts-superlike-photo ts-avatar-initials ts-avatar-initials--lg">{(profile.name||'?')[0].toUpperCase()}</div>}
        <p className="ts-superlike-name">{profile.name}</p>
      </div>
    </div>
  );
}

function SwipeView({ profiles, t, lang, onNavigateMessages, onLike, onPass, onSuperLike, onPlayVideo }) {
  const [stack, setStack] = useState([...profiles].reverse());
  const [swipeDir, setSwipeDir] = useState(null);
  const [matchProfile, setMatchProfile] = useState(null);
  const [superLikeProfile, setSuperLikeProfile] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const swipedProfileRef = useRef(null);

  const top = stack[stack.length - 1];
  const profileSignature = profiles.map(p => profileIdKey(p.id)).join('|');

  useEffect(() => {
    setStack(current => {
      const nextById = new Map(profiles.map(p => [profileIdKey(p.id), p]));
      const preserved = current
        .map(p => nextById.get(profileIdKey(p.id)))
        .filter(Boolean);
      const preservedIds = new Set(preserved.map(p => profileIdKey(p.id)));
      const added = profiles.filter(p => !preservedIds.has(profileIdKey(p.id)));
      return [...added].reverse().concat(preserved);
    });
  }, [profileSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  function swipe(dir) {
    if (!top) return;
    swipedProfileRef.current = top;
    setSwipeDir(dir);
    const sl = t.connect.swipeLabels || {};
    if (dir === 'right') setOverlay(sl.liked||'LIKED ♥');
    else if (dir === 'up') setOverlay(sl.super||'SUPER ⭐');
    else setOverlay(sl.nope||'NOPE ✗');
  }

  async function onAnimEnd() {
    const p = swipedProfileRef.current || top;
    let saved = true;
    if (swipeDir === 'right') {
      saved = onLike ? await onLike(p, 'swipe-card') : true;
      if (saved?.matched) { setMatchProfile(saved.matchPair || { match: p }); playSound('match'); }
    } else if (swipeDir === 'up') {
      saved = onSuperLike ? await onSuperLike(p, 'swipe-superlike') : true;
      if (saved) setSuperLikeProfile(p);
    } else if (swipeDir === 'left') {
      if (onPass) onPass(p);
    }
    if (!saved) {
      swipedProfileRef.current = null;
      setSwipeDir(null);
      setOverlay(null);
      return;
    }
    const swipedProfileId = profileIdKey(p?.id);
    setStack(s => s.filter(item => profileIdKey(item.id) !== swipedProfileId));
    swipedProfileRef.current = null;
    setSwipeDir(null);
    setOverlay(null);
  }

  if (matchProfile) return <MatchOverlay profile={matchProfile.match || matchProfile} matchPair={matchProfile} t={t} onSendMessage={()=>{ const profile = matchProfile.match || matchProfile; setMatchProfile(null); onNavigateMessages(profile?.id); }} onKeepSwiping={()=>setMatchProfile(null)} />;
  if (superLikeProfile) return <SuperLikeOverlay profile={superLikeProfile} t={t} onDismiss={()=>setSuperLikeProfile(null)} />;

  if (!top) return <div className="ts-swipe-empty"><p>{t.connect.seenAll}</p></div>;

  return (
    <div className="ts-swipe-area">
      {/* Background card hint */}
      {stack.length > 1 && <div className="ts-swipe-card ts-swipe-bg-card" />}

      <div
        key={top.id}
        className={`ts-swipe-card ${swipeDir ? `swipe-${swipeDir}` : ''}`}
        onAnimationEnd={swipeDir ? onAnimEnd : undefined}
      >
        {top.photo
          ? <img src={top.photo} alt={top.name} className="ts-swipe-photo" />
          : <div className="ts-swipe-photo ts-avatar-initials ts-avatar-initials--lg">{(top.name||'?')[0].toUpperCase()}</div>}
        <div className="ts-swipe-gradient" />
        {overlay && <div className={`ts-swipe-overlay-label ${swipeDir==='right'||swipeDir==='up'?'positive':'negative'}`}>{overlay}</div>}
        {top.video_url && (
          <button
            onClick={e=>{e.stopPropagation();onPlayVideo&&onPlayVideo(top);}}
            style={{position:'absolute',top:'12px',right:'12px',background:'rgba(0,0,0,0.7)',border:'none',color:'#fff',borderRadius:20,padding:'6px 14px',fontSize:'0.82rem',cursor:'pointer',zIndex:10,letterSpacing:'0.02em'}}
            title={t.videoIntro?.playIntro||'Play Intro Video'}
          >{t.videoIntro?.playIntro||'▶ Intro'}</button>
        )}
        <div className="ts-swipe-info">
          <div className="ts-swipe-name">{top.name}{top.age ? `, ${top.age}` : ''} {top.flag}</div>
          <div className="ts-swipe-compat">{t.connect.match}: <strong>{top.compat}%</strong></div>
          <div className="ts-swipe-traits">
            {top.traits.map(k => <span key={k} className="ts-trait-pill sm" style={{borderColor:DNA_COLORS[DNA_TRAIT_KEYS.indexOf(k)]}}>{getDnaTraitLabel(k, top, lang, t)}</span>)}
          </div>
        </div>
      </div>

      <div className="ts-swipe-buttons">
        <button className="ts-swipe-btn pass"  onClick={() => swipe('left')}>✗</button>
        <button className="ts-swipe-btn super" onClick={() => swipe('up')}>⭐</button>
        <button className="ts-swipe-btn like"  onClick={() => swipe('right')}>♥</button>
      </div>
    </div>
  );
}

// ── NEW F3: Who Viewed Row ────────────────────────────────────────────────────
function WhoViewedRow({ t, lang, plan }) {
  const [viewers, setViewers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    async function load() {
      const { userId } = getAuthToken();
      if (!userId) { setLoaded(true); return; }
      const limit = plan === 'free' ? 5 : 20;
      let data, profiles;
      try {
        data = await sbFetch('GET', 'profile_views',
          `viewed_id=eq.${userId}&select=viewer_id,viewed_at&order=viewed_at.desc&limit=${limit}`
        );
      } catch (e) { setLoaded(true); return; }
      if (!Array.isArray(data) || !data.length) { setLoaded(true); return; }
      const ids = [...new Set(data.map(r => r.viewer_id))];
      try {
        profiles = await sbFetch('GET', 'profiles',
          `id=in.(${ids.join(',')})&select=id,name,photo_url`
        );
      } catch (e) { setLoaded(true); return; }
      const profileMap = {};
      if (Array.isArray(profiles)) profiles.forEach(p => { profileMap[p.id] = p; });
      setViewers(data.map(r => ({ ...r, profile: profileMap[r.viewer_id] || null })));
      setLoaded(true);
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loaded || !viewers.length) return null;

  const label = lang === 'bg' ? 'Кой те е видял' : lang === 'es' ? 'Quién te vio' : lang === 'fr' ? 'Qui vous a vu' : lang === 'de' ? 'Wer hat dich gesehen' : lang === 'el' ? 'Ποιος σε είδε' : lang === 'it' ? 'Chi ti ha visto' : 'Who viewed you';
  const countLabel = `${viewers.length} ${lang === 'bg' ? 'души' : lang === 'es' ? 'personas' : lang === 'fr' ? 'personnes' : lang === 'de' ? 'Personen' : lang === 'el' ? 'άτομα' : lang === 'it' ? 'persone' : viewers.length === 1 ? 'person' : 'people'}`;

  return (
    <div className={`ts-who-viewed-section${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="ts-who-viewed-row"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span className="ts-who-viewed-label">👁️ {label}</span>
        <span className="ts-who-viewed-count">{countLabel}</span>
        <span className="ts-who-viewed-chevron" aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>
      </button>
      {expanded && (
        <div className="ts-who-viewed-avatars">
          {viewers.slice(0, plan === 'free' ? 5 : 20).map((v, i) => {
            const pr = v.profile;
            const isBlurred = plan === 'free' && i >= 2;
            return (
              <div key={i} className={`ts-who-viewed-avatar${isBlurred ? ' ts-who-viewed-blurred' : ''}`}
                title={isBlurred ? '🔒 Premium' : (pr?.name || '?')}>
                {getProfilePhotoUrl(pr?.photo_url, '')
                  ? <img src={getProfilePhotoUrl(pr.photo_url, '')} alt="" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',display:'block'}}/>
                  : <div style={{width:32,height:32,borderRadius:'50%',background:'#30363d',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>👤</div>
                }
              </div>
            );
          })}
          {plan === 'free' && viewers.length > 2 && (
            <div className="ts-who-viewed-more">+{viewers.length - 2}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── NEW F4: Stories Bar + Viewer ──────────────────────────────────────────────
function resizeImageToBase64(file, maxW = 800) {
  return new Promise(resolve => {
    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.src = objUrl;
  });
}

function StoryAddModal({ lang, onAdd, onClose }) {
  const [mode, setMode] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [base64, setBase64] = useState(null);
  const fileRef = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [scale, setScale] = useState(1);
  const dragRef = useRef(null);
  const photoLbl  = lang==='bg'?'📷 Снимка':lang==='es'?'📷 Foto':lang==='fr'?'📷 Photo':lang==='de'?'📷 Foto':lang==='el'?'📷 Φωτογραφία':lang==='it'?'📷 Foto':'📷 Photo';
  const statusLbl = lang==='bg'?'✍️ Статус':lang==='es'?'✍️ Estado':lang==='fr'?'✍️ Statut':lang==='de'?'✍️ Status':lang==='el'?'✍️ Κατάσταση':lang==='it'?'✍️ Stato':'✍️ Status';
  const addLbl    = lang==='bg'?'Добави':lang==='es'?'Añadir':lang==='fr'?'Ajouter':lang==='de'?'Hinzufügen':lang==='el'?'Προσθήκη':lang==='it'?'Aggiungi':'Add';
  const titleLbl  = lang==='bg'?'Добави история':lang==='es'?'Añadir historia':lang==='fr'?'Ajouter une story':lang==='de'?'Story hinzufügen':lang==='el'?'Προσθήκη Story':lang==='it'?'Aggiungi storia':'Add Story';

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    const b64 = await resizeImageToBase64(file);
    setBase64(b64);
    setOffsetX(0); setOffsetY(0); setScale(1);
  }
  function onMouseDown(e) {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startOX: offsetX, startOY: offsetY };
    function onMove(ev) {
      setOffsetX(dragRef.current.startOX + ev.clientX - dragRef.current.startX);
      setOffsetY(dragRef.current.startOY + ev.clientY - dragRef.current.startY);
    }
    function onUp() { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }
  function onTouchStart(e) {
    if (e.touches.length !== 1) return;
    const t0 = e.touches[0];
    dragRef.current = { startX: t0.clientX, startY: t0.clientY, startOX: offsetX, startOY: offsetY };
  }
  function onTouchMove(e) {
    if (!dragRef.current || e.touches.length !== 1) return;
    const t0 = e.touches[0];
    setOffsetX(dragRef.current.startOX + t0.clientX - dragRef.current.startX);
    setOffsetY(dragRef.current.startOY + t0.clientY - dragRef.current.startY);
  }
  function submit() {
    if (mode==='photo' && base64) onAdd({ type:'photo', content:base64, previewUrl, cropX:offsetX, cropY:offsetY, cropScale:scale, timestamp:Date.now() });
    else if (mode==='text' && statusText.trim()) onAdd({ type:'text', content:statusText.trim(), timestamp:Date.now() });
  }
  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={MODAL_S} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{titleLbl}</h2>
        {!mode && (
          <div style={{display:'flex',gap:12,marginTop:'1.25rem',justifyContent:'center'}}>
            <button className="ts-story-type-btn" onClick={()=>setMode('photo')}>{photoLbl}</button>
            <button className="ts-story-type-btn" onClick={()=>setMode('text')}>{statusLbl}</button>
          </div>
        )}
        {mode==='photo' && (
          <div style={{marginTop:'1rem'}}>
            <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleFile}/>
            {!previewUrl
              ? <button className="ts-btn-gold-full" onClick={()=>fileRef.current.click()}>{photoLbl}</button>
              : (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'0.75rem'}}>
                  <div
                    style={{width:280,height:280,borderRadius:'50%',overflow:'hidden',position:'relative',cursor:'grab',userSelect:'none',border:'2px solid var(--gold)',flexShrink:0}}
                    onMouseDown={onMouseDown}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                  >
                    <img
                      src={previewUrl} alt="preview" draggable={false}
                      style={{position:'absolute',top:'50%',left:'50%',transform:`translate(-50%,-50%) translate(${offsetX}px,${offsetY}px) scale(${scale})`,transformOrigin:'center',width:'100%',height:'auto',maxWidth:'none',pointerEvents:'none',userSelect:'none'}}
                    />
                  </div>
                  <div style={{width:'100%',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span style={{fontSize:'0.75rem',color:'var(--muted)'}}>🔍</span>
                    <input type="range" min={1} max={3} step={0.1} value={scale} onChange={e=>setScale(parseFloat(e.target.value))} style={{flex:1,accentColor:'var(--gold)'}}/>
                    <span style={{fontSize:'0.75rem',color:'var(--muted)',minWidth:'2.5rem'}}>{scale.toFixed(1)}×</span>
                  </div>
                  <button className="ts-btn-gold-full" style={{width:'100%'}} onClick={submit}>{addLbl}</button>
                </div>
              )
            }
          </div>
        )}
        {mode==='text' && (
          <div style={{marginTop:'1rem',textAlign:'left'}}>
            <textarea className="ts-input" maxLength={100} rows={3}
              placeholder={lang==='bg'?'Напиши статус...':lang==='es'?'Escribe un estado...':lang==='fr'?'Écrivez un statut...':lang==='de'?'Status schreiben...':lang==='el'?'Γράψε κατάσταση...':lang==='it'?'Scrivi uno stato...':'Write a status...'}
              value={statusText} onChange={e=>setStatusText(e.target.value)}
              style={{border:'1px solid var(--gold)',resize:'none',width:'100%',boxSizing:'border-box'}}/>
            <div style={{textAlign:'right',fontSize:'0.72rem',color:'var(--muted)',marginBottom:'0.5rem'}}>{statusText.length}/100</div>
            <button className="ts-btn-gold-full" disabled={!statusText.trim()} onClick={submit}>{addLbl}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StoryViewer({ stories, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const DURATION = 4000;

  useEffect(() => {
    setProgress(0);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timerRef.current);
        if (idx < stories.length - 1) { setIdx(i => i + 1); }
        else onClose();
      }
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  function tap(e) {
    const x = e.clientX;
    const half = window.innerWidth / 2;
    if (x < half) { if (idx > 0) setIdx(i => i - 1); }
    else { if (idx < stories.length - 1) setIdx(i => i + 1); else onClose(); }
  }

  const s = stories[idx];
  const bgSrc = s.type === 'photo' ? (s.url || s.story) : s.story;
  return (
    <div className="ts-story-viewer" onClick={onClose}>
      <div className="ts-story-card" onClick={tap}>
        {s.type === 'text'
          ? <div className="ts-story-text-bg"><p className="ts-story-text-content">{s.text}</p></div>
          : <img src={bgSrc} alt="" className="ts-story-bg"
              style={s.cropScale ? {transform:`translate(${s.cropX||0}px,${s.cropY||0}px) scale(${s.cropScale})`,transformOrigin:'center'} : undefined}/>
        }
        <div className="ts-story-vignette" />
        <div className="ts-story-progress-bar">
          {stories.map((_, i) => (
            <div key={i} className="ts-story-progress-seg">
              <div className="ts-story-progress-fill" style={{ width: i < idx ? '100%' : i === idx ? `${progress}%` : '0%' }} />
            </div>
          ))}
        </div>
        <div className="ts-story-header">
          {s.photo ? <img src={s.photo} alt={s.name} className="ts-story-avatar" /> : <span className="ts-story-avatar" style={{fontSize:'1.4rem',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(255,255,255,0.15)',borderRadius:'50%'}}>👤</span>}
          <span className="ts-story-author">{s.flag} {s.name}</span>
        </div>
        <button className="ts-story-close" onClick={e => { e.stopPropagation(); onClose(); }}>✕</button>
      </div>
    </div>
  );
}

function StoriesBar({ t, lang }) {
  const [viewing, setViewing] = useState(null);
  const [myStories, setMyStories] = useState([]);
  const [dbStories, setDbStories] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const userPhoto = localStorage.getItem('travelsoul_photo');
  const myLabel   = lang==='bg'?'Моята история':lang==='es'?'Mi historia':lang==='fr'?'Ma story':lang==='de'?'Meine Story':lang==='el'?'Το Story μου':lang==='it'?'La mia storia':'My Story';
  const addLabel  = t?.extras?.addStory || 'Add';
  const { userId } = getAuthToken();

  useEffect(() => {
    loadDbStories();
    const interval = setInterval(loadDbStories, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDbStories() {
    try {
      const rows = await sbFetch('GET', 'stories',
        `expires_at=gt.${new Date().toISOString()}&select=*&order=created_at.desc&limit=50`);
      if (!Array.isArray(rows) || rows.length === 0) return;
      const userIds = [...new Set(rows.map(r => r.user_id))];
      const profiles = await sbFetch('GET', 'profiles',
        `id=in.(${userIds.join(',')})&select=id,name,photo_url`) || [];
      const profileMap = {};
      profiles.forEach(p => { profileMap[p.id] = p; });
      const normalized = rows.map(r => {
        const prof = profileMap[r.user_id] || {};
        return {
          id: r.id, user_id: r.user_id, type: r.type, content: r.content,
          timestamp: new Date(r.created_at).getTime(),
          name: prof.name || 'Traveler', photo: getProfilePhotoUrl(prof.photo_url, ''), flag: '',
          story: r.type === 'photo' ? r.content : null,
          url:   r.type === 'photo' ? r.content : null,
          text:  r.type === 'text'  ? r.content : null,
        };
      });
      setDbStories(normalized);
      if (userId) {
        const mine = normalized.filter(s => s.user_id === userId);
        if (mine.length > 0) setMyStories(mine);
      }
    } catch(e) {
      console.error('[Stories] load error:', e);
    }
  }

  async function addStory(data) {
    const { userId: uid } = getAuthToken();
    const expiresAt = new Date(Date.now() + 86400000).toISOString();
    try {
      await sbFetch('POST', 'stories', '', {
        user_id: uid, type: data.type, content: data.content, expires_at: expiresAt,
      }, { 'Prefer': 'return=minimal' });
    } catch(e) {
      console.error('[Stories] save error:', e);
    }
    setMyStories(prev => [...prev, {
      ...data,
      story: data.type==='photo' ? data.content : null,
      url:   data.type==='photo' ? data.content : null,
      text:  data.type==='text'  ? data.content : null,
      timestamp: Date.now(),
    }]);
    setShowAdd(false);
    loadDbStories();
  }

  const activeMyStories = myStories.filter(s => Date.now() - s.timestamp < 86400000);
  const hasMyStory = activeMyStories.length > 0;
  const myViewerStories = activeMyStories.map(s => ({ ...s, name: myLabel, flag: '', photo: userPhoto || '' }));
  const otherDbStories = dbStories.filter(s => s.user_id !== userId);
  const allStories = [...myViewerStories, ...otherDbStories];

  return (
    <>
      <div className="ts-stories-bar">
        <div className="ts-story-item add-story" style={{cursor:'pointer'}} onClick={() => hasMyStory ? setViewing(0) : setShowAdd(true)}>
          {hasMyStory ? (
            <>
              <div className="ts-story-ring" style={{border:'2px solid var(--gold)',background:'transparent'}}>
                {userPhoto ? <img src={userPhoto} alt="me" className="ts-story-thumb"/> : <span style={{fontSize:'1.6rem'}}>👤</span>}
              </div>
              <span className="ts-story-name" style={{color:'var(--gold)'}}>{myLabel}</span>
            </>
          ) : (
            <>
              <div className="ts-story-ring add" onClick={e=>{e.stopPropagation();setShowAdd(true);}}>
                <span className="ts-story-add-icon">+</span>
              </div>
              <span className="ts-story-name">{addLabel}</span>
            </>
          )}
        </div>
        {otherDbStories.map((s, i) => (
          <div key={s.id} className="ts-story-item" onClick={() => setViewing(myViewerStories.length + i)}>
            <div className="ts-story-ring">
              {s.photo
                ? <img src={s.photo} alt={s.name} className="ts-story-thumb" />
                : <span style={{fontSize:'1.4rem',display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>👤</span>}
            </div>
            <span className="ts-story-name">{(s.name||'').slice(0,8)}</span>
          </div>
        ))}
      </div>
      {showAdd && <StoryAddModal lang={lang} onAdd={addStory} onClose={() => setShowAdd(false)} />}
      {viewing !== null && <StoryViewer stories={allStories} startIdx={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

// ── Destination Info Modal ────────────────────────────────────────────────────
function DestinationInfoModal({ dest, lang, t, onClose, onPlan }) {
  const ex = exploreCopy(lang);
  const getName = (d) => localizeDestinationName(d, lang);
  const getDesc = (d) => lang === 'bg' ? d.descBg : d.desc;
  const getCountry = (d) => lang === 'bg' ? (d.countryBg || localizeCountry(d.country, lang)) : localizeCountry(d.country, lang);
  const getTag = (d) => {
    if (lang === 'bg' && d.tagBg) return d.tagBg;
    const raw = d.tag || '';
    const match = String(raw).match(/^(\S+)\s+(.+)$/);
    if (!match) return raw;
    return `${match[1]} ${ex.tags?.[match[2]] || match[2]}`;
  };
  const [aiInfo, setAiInfo] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const highlights = {
    Santorini: { facts: ['🌅 Caldera — volcanic lagoon', '🍷 Famous for white Assyrtiko wine', '🏛️ Over 3500 years of history', '📸 Oia — most photographed place in the world'], factsBg: ['🌅 Калдера — вулканична лагуна', '🍷 Известно с бялото вино Асиртико', '🏛️ Над 3500 г. история', '📸 Оя — най-снимано място в света'], weather: 'Средиземноморски климат', currency: 'EUR €', currencyBg: 'Евро €', language: 'Гръцки', timezone: 'UTC+2', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Санторини', tourismUrl: 'https://www.visitgreece.gr' },
    Kyoto: { facts: ['⛩️ Over 1600 Buddhist temples', '🌸 Cherry blossom season: March–April', '🍵 Birthplace of tea ceremony', '🦌 Sacred deer in Nara'], factsBg: ['⛩️ Над 1600 будистки храма', '🌸 Сезон на сакурата: март-април', '🍵 Роденото място на чайната церемония', '🦌 Свещени елени в Нара'], weather: 'Умерен с 4 сезона', currency: 'JPY ¥', currencyBg: 'Японска йена ¥', language: 'Японски', timezone: 'UTC+9', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Киото', tourismUrl: 'https://kyoto.travel/en' },
    Bali: { facts: ['🌺 Island of the Gods', '🏄 World surfing capital', '🕍 10,000+ Hindu temples', '🌿 Organic rice terraces'], factsBg: ['🌺 Остров на боговете', '🏄 Световна столица на сърфа', '🕍 10 000+ индуистки храма', '🌿 Оризови тераси на ЮНЕСКО'], weather: 'Тропически', currency: 'IDR Rp', currencyBg: 'Индонезийска рупия', language: 'Балийски/Индонезийски', timezone: 'UTC+8', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Бали', tourismUrl: 'https://www.indonesia.travel' },
    Patagonia: { facts: ['🏔️ Home of Torres del Paine', '🐧 Magellanic penguin colonies', '💧 Purest water in the world', '🦅 Andean condor 3m wingspan'], factsBg: ['🏔️ Дом на Торес дел Пайне', '🐧 Колонии от магеланови пингвини', '💧 Най-чистата вода в света', '🦅 Андски кондор с 3м размах'], weather: 'Субполярен', currency: 'ARS $', currencyBg: 'Аржентинско песо', language: 'Испански', timezone: 'UTC-3', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Патагония', tourismUrl: 'https://www.patagonia-argentina.com' },
    Marrakech: { facts: ['🏺 Djemaa el-Fna — UNESCO square', '🌹 Majorelle Gardens — blue wonder', '🛍️ 11th century Medina', '🐪 Sahara desert safaris'], factsBg: ['🏺 Джема ел-Фна — ЮНЕСКО площад', '🌹 Градини Мажорел — синьо чудо', '🛍️ Медина от 11 век', '🐪 Сафарита до Сахара'], weather: 'Полупустинен', currency: 'MAD Dh', currencyBg: 'Мароканска дирхама', language: 'Арабски/Берберски', timezone: 'UTC+1', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Маракеш', tourismUrl: 'https://www.visitmorocco.com' },
    Iceland: { facts: ['🌋 Land of fire and ice', '♨️ 130 volcanoes', '☀️ Midnight sun 24h', '🐋 Whale watching tourism'], factsBg: ['🌋 Страната на огъня и леда', '♨️ 130 вулкана', '☀️ Среднощно слънце 24 ч.', '🐋 Китолов туризъм'], weather: 'Субарктически', currency: 'ISK kr', currencyBg: 'Исландска крона', language: 'Исландски', timezone: 'UTC+0', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Исландия', tourismUrl: 'https://www.visiticeland.com' },
    Maldives: { facts: ['🏝️ 1192 islands', '🐠 Bioluminescent beaches', '🦈 Whale sharks year-round', '🌊 Lowest country in the world'], factsBg: ['🏝️ 1192 острова', '🐠 Биолуминесцентни плажове', '🦈 Китови акули целогодишно', '🌊 Най-ниската страна в света'], weather: 'Тропически', currency: 'MVR Rf', currencyBg: 'Малдивска руфия', language: 'Дивехи', timezone: 'UTC+5', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Малдиви', tourismUrl: 'https://visitmaldives.com' },
    Lisbon: { facts: ['🚃 Legendary Tuk-Tuk trams', '🎵 Fado — UNESCO heritage', '🐟 900 bacalhau recipes', '🌉 Bridge like Golden Gate'], factsBg: ['🚃 Легендарните трамваи', '🎵 Фадо — ЮНЕСКО наследство', '🐟 900 рецепти с бакалао', '🌉 Мост като Голдън Гейт'], weather: 'Средиземноморски', currency: 'EUR €', currencyBg: 'Евро €', language: 'Португалски', timezone: 'UTC+0', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Лисабон', tourismUrl: 'https://www.visitlisboa.com' },
    'Machu Picchu': { facts: ['🏔️ Lost city of the Incas', '🌿 Cloud forest ecosystem', '⭐ 7th wonder of the world', '🦙 Wild llamas roam freely'], factsBg: ['🏔️ Изгубеният град на инките', '☁ Градът в облаците на 2430м', '🦙 Диви лами из руините', '🌄 Слънчевата порта Интипунку'], weather: 'Умерен', currency: 'PEN S/', currencyBg: 'Перуански сол', language: 'Испански/Кечуа', timezone: 'UTC-5', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Мачу_Пикчу', tourismUrl: 'https://www.peru.travel' },
    Dubai: { facts: ['🏗️ Burj Khalifa — 828m', '🎿 Ski slope inside a mall', '🌡️ 50°C summer heat', '🛳️ Palm artificial islands'], factsBg: ['🏗️ Бурж Халифа — 828м', '🎿 Ски писта в мол', '🌡️ 50°C лято', '🛳️ Изкуствени острови Палм'], weather: 'Пустинен', currency: 'AED د.إ', currencyBg: 'Дирхама ОАЕ', language: 'Арабски', timezone: 'UTC+4', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Дубай', tourismUrl: 'https://www.visitdubai.com' },
    'Amalfi Coast': { facts: ['🚤 Boat-only villages', '🍋 World famous limoncello', '🏰 Medieval watchtowers', '🌊 Crystal clear blue sea'], factsBg: ['🏰 Средновековни кули за отбрана', '🍋 Лимончело от местни лимони', '⛵ Само с лодка до някои села', '🌊 Тиренско море с кристална вода'], weather: 'Средиземноморски', currency: 'EUR €', currencyBg: 'Евро €', language: 'Италиански', timezone: 'UTC+1', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Амалфийско_крайбрежие', tourismUrl: 'https://www.italiantourism.com' },
    'New Zealand': { facts: ['🎬 Filmed Lord of the Rings', '🥝 Only country with kiwi bird', '🌋 Active Whakaari volcano', '🏉 Home of the All Blacks'], factsBg: ['🎬 Заснет е Властелинът на пръстените', '🥝 Единствената страна с птицата киви', '🌋 Активен вулкан Вакаари', '🏉 Родина на Ол Блекс'], weather: 'Умерен океански', currency: 'NZD $', currencyBg: 'Новозеландски долар', language: 'Английски/Маори', timezone: 'UTC+12', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Нова_Зеландия', tourismUrl: 'https://www.newzealand.com' },
    Prague: { facts: ['🍺 Most beer per capita', '🎻 Mozart lived here', '🌉 Charles Bridge from 1357', '👻 Ghost night tours'], factsBg: ['🍺 Най-много бира на глава в света', '🌉 Карловият мост от 1357г.', '🏰 Пражкият замък — най-голям в света', '👻 Призрачни нощни обиколки'], weather: 'Умерен континентален', currency: 'CZK Kč', currencyBg: 'Чешка крона', language: 'Чешки', timezone: 'UTC+1', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Прага', tourismUrl: 'https://www.prague.eu' },
    Thailand: { facts: ['🙏 Land of smiles', '🐘 Elephant sanctuaries', '🌴 4000+ islands', '🍜 Street food paradise'], factsBg: ['🙏 Страната на усмивките', '🐘 Слонски светилища', '🌺 Фестивал на водата Сонгкран', '🍜 Пад Тай — национално ястие'], weather: 'Тропически', currency: 'THB ฿', currencyBg: 'Тайландски бат', language: 'Тайландски', timezone: 'UTC+7', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Тайланд', tourismUrl: 'https://www.tourismthailand.org' },
    Tuscany: { facts: ['🍷 Chianti wine region', '🌻 Sunflower & cypress valleys', '🎨 Birthplace of Renaissance', '🏰 Medieval hilltop towns'], factsBg: ['🍷 Кианти — емблематично червено вино', '🌻 Безкрайни полета от слънчогледи', '🎨 Роден край на Леонардо да Винчи', '🏰 Средновековни кули в Сан Джиминяно'], weather: 'Средиземноморски', currency: 'EUR €', currencyBg: 'Евро €', language: 'Италиански', timezone: 'UTC+1', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Тоскана', tourismUrl: 'https://www.visittuscany.com' },
    'Safari Kenya': { facts: ['🦁 Big Five in Masai Mara', '🦩 Millions of flamingos', '🏔️ Mount Kenya 5199m', '🌍 Cradle of humanity'], factsBg: ['🦁 Голямата петорка в естествена среда', '🦓 Великата миграция на зебри', '☕ Кафето от Кения е сред най-добрите', '🌅 Залези над безкрайната савана'], weather: 'Тропически', currency: 'KES KSh', currencyBg: 'Кенийски шилинг', language: 'Суахили/Английски', timezone: 'UTC+3', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Кения', tourismUrl: 'https://magicalkenya.com' },
    Havana: { facts: ['🚗 1950s American classic cars', '🎺 Birthplace of salsa & rumba', '🏛️ UNESCO Old Havana', '🍹 Home of mojito & daiquiri'], factsBg: ['🚗 Ретро коли от 50-те години', '🎺 Роден край на салсата и сона', '🌴 Тропически климат целогодишно', '🏖️ Варадеро — един от най-добрите плажове'], weather: 'Тропически', currency: 'CUP $', currencyBg: 'Кубински песо', language: 'Испански', timezone: 'UTC-5', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Хавана', tourismUrl: 'https://www.cuba.travel' },
    'Swiss Alps': { facts: ['🏔️ 4000m+ peaks', '🍫 Finest chocolate & cheese', '⌚ Luxury watchmaking', '🚞 Scenic mountain railways'], factsBg: ['⛷️ Давос — световен икономически форум', '🍫 Швейцарски шоколад с 500г. история', '⌚ Столица на часовникарството', '🏔️ Матерхорн — емблемата на Алпите'], weather: 'Алпийски', currency: 'CHF Fr', currencyBg: 'Швейцарски франк', language: 'Немски/Френски', timezone: 'UTC+1', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Алпи', tourismUrl: 'https://www.myswitzerland.com' },
    Petra: { facts: ['🏛️ Rose-red city carved in rock', '🐪 Ancient Nabataean kingdom', '⭐ 7th wonder of the world', '🌅 Best at sunset — golden glow'], factsBg: ['🌹 Розово-червен пясъчник', '🏛️ Набатейска столица от 4 в. пр. Хр.', '🎬 Заснета е в Индиана Джоунс', '🌄 Хазне — изсечена в скалата фасада'], weather: 'Пустинен', currency: 'JOD JD', currencyBg: 'Йордански динар', language: 'Арабски', timezone: 'UTC+3', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Петра', tourismUrl: 'https://www.visitjordan.com' },
    Amsterdam: { facts: ['🚲 More bikes than people', '🌷 17M tulips in Keukenhof', '🏠 Narrowest house: 2.02m', '🎨 Rembrandt & Van Gogh homeland'], factsBg: ['🚲 Повече велосипеди отколкото хора', '🌷 Keукенхоф — 7 млн. лалета', '🎨 Рембранд и Ван Гог са от тук', '🏠 Накланящи се наклонени сгради'], weather: 'Умерен морски', currency: 'EUR €', currencyBg: 'Евро €', language: 'Нидерландски', timezone: 'UTC+1', wikipediaUrl: 'https://bg.wikipedia.org/wiki/Амстердам', tourismUrl: 'https://www.iamsterdam.com' },
  };

  const info = highlights[dest.name] || {
    facts: dest.facts || [getDesc(dest)],
    factsBg: dest.factsBg || [getDesc(dest)],
    weather: dest.weather || '—',
    currency: dest.currency || '—',
    language: dest.language || '—',
    timezone: dest.timezone || '—',
    wikipediaUrl: dest.wikipediaUrl || '',
    tourismUrl: dest.tourismUrl || '',
  };
  const _cityCountryEn = CITY_TO_COUNTRY[(dest.nameBg||'').toLowerCase().trim()] || CITY_TO_COUNTRY[(dest.name||'').toLowerCase().trim()];
  // Map the English country (from CITY_TO_COUNTRY) to its Bulgarian COUNTRY_INFO key when available,
  // so the BG interface shows Bulgarian values.
  const _enToBgCountry = { 'Thailand':'Тайланд','Spain':'Испания','Italy':'Италия','France':'Франция','Greece':'Гърция','Portugal':'Португалия','Turkey':'Турция','Croatia':'Хърватия','Germany':'Германия','Austria':'Австрия','Netherlands':'Холандия','Belgium':'Белгия','Switzerland':'Швейцария','United Kingdom':'Великобритания' };
  const _cityCountryBg = _cityCountryEn ? _enToBgCountry[_cityCountryEn] : null;
  const countryFallback = COUNTRY_INFO[dest.nameBg] || COUNTRY_INFO[dest.name] || COUNTRY_INFO[dest.country] || COUNTRY_INFO[dest.countryBg]
    || (_cityCountryBg && COUNTRY_INFO[_cityCountryBg]) || (_cityCountryEn && COUNTRY_INFO[_cityCountryEn]) || {};
  const tourismLookup = TOURISM_URLS[dest.nameBg] || TOURISM_URLS[dest.name] || TOURISM_URLS[dest.countryBg] || TOURISM_URLS[dest.country] || '';
  const finalInfo = {
    weather: (info.weather && info.weather !== '—') ? info.weather : (countryFallback.weather || '—'),
    currency: (info.currency && info.currency !== '—') ? info.currency : (countryFallback.currency || '—'),
    currencyBg: info.currencyBg || countryFallback.currencyBg || info.currency || '—',
    language: (info.language && info.language !== '—') ? info.language : (countryFallback.language || '—'),
    timezone: (info.timezone && info.timezone !== '—') ? info.timezone : (countryFallback.timezone || '—'),
    bestTime: (dest.bestTime && dest.bestTime !== '—') ? dest.bestTime : (countryFallback.bestTime || '—'),
    bestTimeBg: (dest.bestTimeBg && dest.bestTimeBg !== '—') ? dest.bestTimeBg : (countryFallback.bestTime || '—'),
    visitors: (dest.visitors && dest.visitors !== '—') ? dest.visitors : (countryFallback.visitors || '—'),
    facts: (info.facts && info.facts.length) ? info.facts : (dest.facts || [getDesc(dest)]),
    factsBg: (info.factsBg && info.factsBg.length) ? info.factsBg : (dest.factsBg || dest.facts || [getDesc(dest)]),
    wikipediaUrl: info.wikipediaUrl || dest.wikipediaUrl || '',
    tourismUrl: info.tourismUrl || dest.tourismUrl || tourismLookup,
  };
  const destKey = dest.name || dest.nameBg || '';
  // Reset AI-fetched info whenever the destination changes, so one city's data
  // never leaks into another (e.g. showing "Euro/Bulgarian" for Benghazi).
  React.useEffect(() => { setAiInfo(null); }, [destKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const missing = ['weather','currency','language','timezone'].some(k => finalInfo[k] === '—');
    if (!missing || aiInfo || aiLoading) return;
    setAiLoading(true);
    const destName = dest.nameEn || dest.name || dest.nameBg || '';
    fetch(`${API_BASE}/api/openai/plan`, {
      method:'POST',
      headers:{ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
      body: JSON.stringify({
        model:'gpt-4o-mini',
        messages:[
          {role:'system', content:'Return only raw JSON, no markdown, no explanation.'},
          {role:'user', content:`Provide travel destination info for "${destName}" as JSON with these keys: weather (climate type, 2-4 words), currency (name and symbol), language (main language(s)), timezone (UTC offset), bestTime (best months to visit, e.g. "Mar–May"), visitors (annual tourists, e.g. "2M/yr"). Keep all values short.`},
        ],
        max_tokens:300,
      }),
    })
      .then(r => r.json())
      .then(data => {
        try {
          const text = (data.choices?.[0]?.message?.content || '{}').replace(/```json|```/g,'').trim();
          const parsed = JSON.parse(text);
          // Coerce every field to a plain string — AI sometimes returns objects/arrays which crash React (#31)
          const toStr = (v) => {
            if (v == null) return '';
            if (typeof v === 'string') return v;
            if (typeof v === 'number') return String(v);
            if (Array.isArray(v)) return v.map(toStr).filter(Boolean).join(', ');
            if (typeof v === 'object') return Object.values(v).map(toStr).filter(Boolean).join(' ');
            return String(v);
          };
          setAiInfo({
            weather: toStr(parsed.weather),
            currency: toStr(parsed.currency),
            language: toStr(parsed.language),
            timezone: toStr(parsed.timezone),
            bestTime: toStr(parsed.bestTime),
            visitors: toStr(parsed.visitors),
          });
        } catch {}
      })
      .catch(() => {})
      .finally(() => setAiLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destKey]);

  const displayInfo = aiInfo ? {
    ...finalInfo,
    weather: (finalInfo.weather !== '—') ? finalInfo.weather : (aiInfo.weather || '—'),
    currency: (finalInfo.currency !== '—') ? finalInfo.currency : (aiInfo.currency || '—'),
    currencyBg: (finalInfo.currencyBg && finalInfo.currencyBg !== '—') ? finalInfo.currencyBg : (aiInfo.currency || '—'),
    language: (finalInfo.language !== '—') ? finalInfo.language : (aiInfo.language || '—'),
    timezone: (finalInfo.timezone !== '—') ? finalInfo.timezone : (aiInfo.timezone || '—'),
    bestTime: (finalInfo.bestTime !== '—') ? finalInfo.bestTime : (aiInfo.bestTime || '—'),
    bestTimeBg: (finalInfo.bestTimeBg !== '—') ? finalInfo.bestTimeBg : (aiInfo.bestTime || '—'),
    visitors: (finalInfo.visitors !== '—') ? finalInfo.visitors : (aiInfo.visitors || '—'),
  } : finalInfo;
  const detailCopy = DESTINATION_DETAIL_I18N[destinationKeyFor(dest)] || null;
  const localizedWeather = detailCopy?.weather?.[lang] || displayInfo.weather;
  const localizedLanguage = detailCopy?.language?.[lang] || displayInfo.language;
  const localizedFacts = detailCopy?.facts?.[lang] || (lang==='bg' ? (displayInfo.factsBg.length ? displayInfo.factsBg : displayInfo.facts) : displayInfo.facts);
  const localizedBestTime = lang === 'bg'
    ? (displayInfo.bestTimeBg || localizeMonthRange(displayInfo.bestTime, lang))
    : localizeMonthRange(displayInfo.bestTime, lang);
  const localizedVisitors = localizeVisitors(displayInfo.visitors, lang);

  const wikiLink = displayInfo.wikipediaUrl;
  const BG_TO_EN_DEST = {
    'София':'Sofia','Пловдив':'Plovdiv','Варна':'Varna',
    'Бургас':'Burgas','Банско':'Bansko','Велико Търново':'Veliko Tarnovo',
    'Созопол':'Sozopol','Несебър':'Nessebar','Рила':'Rila Monastery',
    'Сребърна':'Srebarna','Габрово':'Gabrovo','Перник':'Pernik',
    'Русе':'Ruse','Плевен':'Pleven','Стара Загора':'Stara Zagora',
    'Шумен':'Shumen','Добрич':'Dobrich','Враца':'Vratsa',
    'Ловеч':'Lovech','Монтана':'Montana','Видин':'Vidin',
    'Сливен':'Sliven','Ямбол':'Yambol','Хасково':'Haskovo',
    'Кърджали':'Kardzhali','Пазарджик':'Pazardzhik','Благоевград':'Blagoevgrad',
    'Кюстендил':'Kyustendil','Търговище':'Targovishte','Разград':'Razgrad',
    'Силистра':'Silistra','Копривщица':'Koprivshtitsa','Трявна':'Tryavna',
    'Арбанаси':'Arbanasi','Котел':'Kotel','Жеравна':'Zheravna',
    'Боровец':'Borovets','Пампорово':'Pamporovo','Приморско':'Primorsko',
    'Китен':'Kiten','Поморие':'Pomorie','Обзор':'Obzor',
  };
  const englishDestName = dest.nameEn || dest.englishName || BG_TO_EN_DEST[dest.name] || BG_TO_EN_DEST[dest.nameBg] || dest.name;
  const googleLangMap = { 'bg':'bg','en':'en','es':'es','fr':'fr','de':'de','it':'it','pt':'pt','ru':'ru','tr':'tr','ar':'ar' };
  const googleLang = googleLangMap[lang] || 'en';
  const tourismLink = displayInfo.tourismUrl || `https://www.google.com/travel/search?q=${encodeURIComponent(englishDestName)}&hl=${googleLang}&gl=${googleLang === 'bg' ? 'BG' : googleLang.toUpperCase()}`;
  const allPhotos = dest.photos?.length ? dest.photos : [dest.img];
  const [photoIdx, setPhotoIdx] = useState(0);

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#0d1117',border:'1px solid #30363d',borderRadius:24,maxWidth:500,width:'100%',maxHeight:'90vh',overflowY:'auto',position:'relative'}}>
        <div style={{position:'relative',height:220}}>
          <img src={allPhotos[photoIdx]} alt={getName(dest)} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'24px 24px 0 0',transition:'opacity .3s'}} />
          <div style={{position:'absolute',inset:0,background:'linear-gradient(to top,#0d1117 0%,transparent 60%)',borderRadius:'24px 24px 0 0'}} />
          {allPhotos.length > 1 && (
            <>
              <button onClick={()=>setPhotoIdx(i=>(i-1+allPhotos.length)%allPhotos.length)} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',background:'rgba(0,0,0,0.6)',border:'none',borderRadius:'50%',width:32,height:32,color:'#fff',fontSize:16,cursor:'pointer'}}>‹</button>
              <button onClick={()=>setPhotoIdx(i=>(i+1)%allPhotos.length)} style={{position:'absolute',right:48,top:'50%',transform:'translateY(-50%)',background:'rgba(0,0,0,0.6)',border:'none',borderRadius:'50%',width:32,height:32,color:'#fff',fontSize:16,cursor:'pointer'}}>›</button>
              <div style={{position:'absolute',bottom:70,right:12,background:'rgba(0,0,0,0.6)',borderRadius:20,padding:'2px 8px',color:'#fff',fontSize:11}}>{photoIdx+1}/{allPhotos.length}</div>
            </>
          )}
          <button onClick={onClose} style={{position:'absolute',top:12,right:12,background:'rgba(0,0,0,0.6)',border:'none',borderRadius:'50%',width:36,height:36,color:'#fff',fontSize:18,cursor:'pointer'}}>✕</button>
          <div style={{position:'absolute',bottom:16,left:20}}>
            <div style={{color:'#c9a84c',fontSize:12,marginBottom:4}}>{getTag(dest)}</div>
            <div style={{color:'#fff',fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700}}>{getName(dest)}</div>
            <div style={{color:'#8b949e',fontSize:13}}>{getCountry(dest)} • {dest.temp} • {localizeVisitors(dest.visitors, lang)}</div>
          </div>
        </div>
        <div style={{padding:'20px 24px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
            {[
              {icon:'🌤️', label: ex.weather, val: aiLoading && displayInfo.weather==='—' ? '...' : localizedWeather},
              {icon:'💰', label: ex.currency, val: aiLoading && displayInfo.currency==='—' ? '...' : (lang==='bg'?displayInfo.currencyBg:displayInfo.currency)},
              {icon:'🗣️', label: ex.language, val: aiLoading && displayInfo.language==='—' ? '...' : localizedLanguage},
              {icon:'⏰', label: ex.timezone, val: aiLoading && displayInfo.timezone==='—' ? '...' : displayInfo.timezone},
              {icon:'📅', label: ex.bestSeason, val: aiLoading && displayInfo.bestTime==='—' ? '...' : localizedBestTime},
              {icon:'👥', label: ex.visitors, val: aiLoading && displayInfo.visitors==='—' ? '...' : localizedVisitors},
            ].map((item,i) => (
              <div key={i} style={{background:'#161b22',borderRadius:12,padding:'10px 14px'}}>
                <div style={{fontSize:18,marginBottom:2}}>{item.icon}</div>
                <div style={{color:'#8b949e',fontSize:10,marginBottom:2}}>{item.label}</div>
                <div style={{color:'#f0f6fc',fontSize:12,fontWeight:600}}>{item.val}</div>
              </div>
            ))}
          </div>
          <div style={{marginBottom:20}}>
            <div style={{color:'#c9a84c',fontWeight:700,fontSize:13,marginBottom:10}}>
              {ex.facts}
            </div>
            {localizedFacts.map((fact,i) => (
              <div key={i} style={{color:'#f0f6fc',fontSize:13,padding:'8px 0',borderBottom:'1px solid #21262d'}}>{fact}</div>
            ))}
          </div>
          {(wikiLink || tourismLink) && (
            <div style={{display:'flex',gap:10,marginBottom:16}}>
              {wikiLink && (
                <a href={wikiLink} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(wikiLink, e)} style={{
                  flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,
                  background:'#161b22',border:'1px solid #30363d',borderRadius:12,
                  color:'#f0f6fc',fontSize:12,fontWeight:600,padding:'10px',
                  textDecoration:'none',transition:'border-color .2s',
                }}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#c9a84c'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#30363d'}>
                  📖 {lang==='bg'?'Уикипедия':lang==='es'?'Wikipedia':lang==='fr'?'Wikipédia':lang==='de'?'Wikipedia':lang==='el'?'Βικιπαίδεια':lang==='it'?'Wikipedia':'Wikipedia'}
                </a>
              )}
              {tourismLink && (
                <a href={tourismLink} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(tourismLink, e)} style={{
                  flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,
                  background:'linear-gradient(135deg,#c9a84c22,#e8b92311)',
                  border:'1px solid #c9a84c44',borderRadius:12,
                  color:'#c9a84c',fontSize:12,fontWeight:600,padding:'10px',
                  textDecoration:'none',transition:'all .2s',
                }}
                onMouseEnter={e=>e.currentTarget.style.background='linear-gradient(135deg,#c9a84c33,#e8b92322)'}
                onMouseLeave={e=>e.currentTarget.style.background='linear-gradient(135deg,#c9a84c22,#e8b92311)'}>
                  🌐 {ex.tourismSite}
                </a>
              )}
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
            <a
              href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(dest.name)}&aid=2311236&label=travelsoul`}
              target="_blank" rel="noopener noreferrer"
              onClick={e=>openExternalUrl(`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(dest.name)}&aid=2311236&label=travelsoul`, e)}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,background:'#003580',border:'none',borderRadius:12,color:'#fff',fontSize:12,fontWeight:700,padding:'12px 8px',textDecoration:'none',cursor:'pointer'}}>
              🏨 {ex.hotels}
            </a>
            <a
              href={`https://www.skyscanner.net/transport/flights-from/${encodeURIComponent(dest.name)}/`}
              target="_blank" rel="noopener noreferrer"
              onClick={e=>openExternalUrl(`https://www.skyscanner.net/transport/flights-from/${encodeURIComponent(dest.name)}/`, e)}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,background:'#0770e3',border:'none',borderRadius:12,color:'#fff',fontSize:12,fontWeight:700,padding:'12px 8px',textDecoration:'none',cursor:'pointer'}}>
              ✈️ {ex.flights}
            </a>
            <a
              href={`https://www.agoda.com/search?city=${encodeURIComponent(dest.name)}&tag=532102`}
              target="_blank" rel="noopener noreferrer"
              onClick={e=>openExternalUrl(`https://www.agoda.com/search?city=${encodeURIComponent(dest.name)}&tag=532102`, e)}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,background:'#e2001a',border:'none',borderRadius:12,color:'#fff',fontSize:12,fontWeight:700,padding:'12px 8px',textDecoration:'none',cursor:'pointer'}}>
              🌏 {lang==='bg'?'Агода':lang==='es'?'Agoda':lang==='fr'?'Agoda':lang==='de'?'Agoda':lang==='el'?'Agoda':lang==='it'?'Agoda':'Agoda'}
            </a>
            <a
              href={`https://www.vrbo.com/search/keywords:${encodeURIComponent(dest.name)}`}
              target="_blank" rel="noopener noreferrer"
              onClick={e=>openExternalUrl(`https://www.vrbo.com/search/keywords:${encodeURIComponent(dest.name)}`, e)}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,background:'#1a1a2e',border:'1px solid #30363d',borderRadius:12,color:'#fff',fontSize:12,fontWeight:700,padding:'12px 8px',textDecoration:'none',cursor:'pointer'}}>
              🏠 {ex.apartments}
            </a>
            <a href={`https://www.klook.com/en-US/search/?query=${encodeURIComponent(dest.name)}&utm_medium=affiliate&utm_source=travelsoul&aff_adid=532102`} target="_blank" rel="noopener noreferrer"
              onClick={e=>openExternalUrl(`https://www.klook.com/en-US/search/?query=${encodeURIComponent(dest.name)}&utm_medium=affiliate&utm_source=travelsoul&aff_adid=532102`, e)}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'10px 14px',borderRadius:12,background:'rgba(255,90,0,0.15)',border:'1px solid rgba(255,90,0,0.3)',color:'#ff5a00',fontWeight:700,fontSize:12,textDecoration:'none',gridColumn:'1 / -1'}}>
              🎡 {ex.activities}
            </a>
          </div>
          <button onClick={() => { onClose(); onPlan(getName(dest)); }} style={{
            width:'100%',background:'linear-gradient(90deg,#c9a84c,#e8b923)',
            border:'none',borderRadius:14,color:'#000',fontWeight:700,
            fontSize:15,padding:'14px',cursor:'pointer',
          }}>
            🗺️ {ex.planTripTo} {getName(dest)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Wikipedia Destination Fetch ───────────────────────────────────────────────
function isGoodPhoto(url) {
  if (!url) return false;
  const bad = ['map', 'Map', 'location', 'Location', 'flag', 'Flag', '.svg', 'SVG', 'locator', 'blank'];
  return !bad.some(b => url.includes(b));
}

const DEST_PHOTOS = {
  'Малдиви':['https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80','https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=80','https://images.unsplash.com/photo-1540202404-a2f29016b523?w=800&q=80'],
  'Maldives':['https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80','https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=80','https://images.unsplash.com/photo-1540202404-a2f29016b523?w=800&q=80'],
  'Мароко':['https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=800&q=80','https://images.unsplash.com/photo-1572204097183-e1ab140342dc?w=800&q=80'],
  'Morocco':['https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=800&q=80','https://images.unsplash.com/photo-1572204097183-e1ab140342dc?w=800&q=80'],
  'Маракеш':['https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=800&q=80','https://images.unsplash.com/photo-1572204097183-e1ab140342dc?w=800&q=80'],
  'Marrakech':['https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=800&q=80'],
  'България':['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80','https://images.unsplash.com/photo-1578469645742-46cae010e5d4?w=800&q=80'],
  'Bulgaria':['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'],
  'Гърция':['https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80','https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&q=80'],
  'Greece':['https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80'],
  'Турция':['https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80','https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=800&q=80'],
  'Turkey':['https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80'],
  'Египет':['https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=800&q=80','https://images.unsplash.com/photo-1568322445389-f64ac2515020?w=800&q=80'],
  'Egypt':['https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=800&q=80'],
  'Индия':['https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=800&q=80','https://images.unsplash.com/photo-1532664189809-02133fee698d?w=800&q=80'],
  'India':['https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=800&q=80'],
  'Китай':['https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=800&q=80','https://images.unsplash.com/photo-1537797585139-4d01f4f40f96?w=800&q=80'],
  'China':['https://images.unsplash.com/photo-1508804185872-d7badad00f7d?w=800&q=80'],
  'Япония':['https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80','https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80'],
  'Japan':['https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80'],
  'Тайланд':['https://images.unsplash.com/photo-1506665531195-3566af2b4dfa?w=800&q=80','https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80'],
  'Thailand':['https://images.unsplash.com/photo-1506665531195-3566af2b4dfa?w=800&q=80'],
  'Виетнам':['https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80','https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=800&q=80'],
  'Vietnam':['https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80'],
  'Испания':['https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=800&q=80','https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=800&q=80'],
  'Spain':['https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=800&q=80'],
  'Барселона':['https://images.unsplash.com/photo-1583422409516-2895a77efded?w=800&q=80','https://images.unsplash.com/photo-1511527661048-7fe73d85e9a4?w=800&q=80'],
  'Barcelona':['https://images.unsplash.com/photo-1583422409516-2895a77efded?w=800&q=80'],
  'Португалия':['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80','https://images.unsplash.com/photo-1536992266094-82847e1fd431?w=800&q=80'],
  'Portugal':['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'],
  'Лисабон':['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80','https://images.unsplash.com/photo-1585208798174-6cedd4454619?w=800&q=80'],
  'Lisbon':['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'],
  'Италия':['https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=800&q=80','https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?w=800&q=80'],
  'Italy':['https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=800&q=80'],
  'Рим':['https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80','https://images.unsplash.com/photo-1515542622106-078bda21703a?w=800&q=80'],
  'Rome':['https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80'],
  'Франция':['https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&q=80','https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80'],
  'France':['https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&q=80'],
  'Париж':['https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80','https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&q=80'],
  'Paris':['https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80'],
  'Германия':['https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&q=80'],
  'Germany':['https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&q=80'],
  'Берлин':['https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&q=80'],
  'Berlin':['https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&q=80'],
  'Австрия':['https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=800&q=80'],
  'Austria':['https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=800&q=80'],
  'Виена':['https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=800&q=80','https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&q=80'],
  'Vienna':['https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=800&q=80'],
  'Швейцария':['https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80','https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80'],
  'Switzerland':['https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80'],
  'Нидерландия':['https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&q=80','https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=800&q=80'],
  'Netherlands':['https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&q=80'],
  'Амстердам':['https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&q=80','https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=800&q=80'],
  'Amsterdam':['https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&q=80'],
  'Норвегия':['https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80','https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=800&q=80'],
  'Norway':['https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80'],
  'Хърватия':['https://images.unsplash.com/photo-1555990793-da11153b2473?w=800&q=80','https://images.unsplash.com/photo-1599928350218-3d81862ef478?w=800&q=80'],
  'Croatia':['https://images.unsplash.com/photo-1555990793-da11153b2473?w=800&q=80'],
  'Дубровник':['https://images.unsplash.com/photo-1555990793-da11153b2473?w=800&q=80'],
  'Dubrovnik':['https://images.unsplash.com/photo-1555990793-da11153b2473?w=800&q=80'],
  'Румъния':['https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=800&q=80'],
  'Romania':['https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=800&q=80'],
  'Унгария':['https://images.unsplash.com/photo-1570503028544-c543fba6f7ba?w=800&q=80'],
  'Hungary':['https://images.unsplash.com/photo-1570503028544-c543fba6f7ba?w=800&q=80'],
  'Будапеща':['https://images.unsplash.com/photo-1570503028544-c543fba6f7ba?w=800&q=80','https://images.unsplash.com/photo-1580806740087-c79f8aad21e1?w=800&q=80'],
  'Budapest':['https://images.unsplash.com/photo-1570503028544-c543fba6f7ba?w=800&q=80'],
  'Сингапур':['https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80','https://images.unsplash.com/photo-1508964942454-1a56651d54ac?w=800&q=80'],
  'Singapore':['https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80'],
  'Австралия':['https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800&q=80','https://images.unsplash.com/photo-1523428096881-5bd79d043006?w=800&q=80'],
  'Australia':['https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800&q=80'],
  'Сидни':['https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800&q=80'],
  'Sydney':['https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800&q=80'],
  'Куба':['https://images.unsplash.com/photo-1500759285222-a95626b934cb?w=800&q=80'],
  'Cuba':['https://images.unsplash.com/photo-1500759285222-a95626b934cb?w=800&q=80'],
  'Бразилия':['https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=800&q=80'],
  'Brazil':['https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=800&q=80'],
  'Рио':['https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=800&q=80'],
  'Rio':['https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=800&q=80'],
  'Мексико':['https://images.unsplash.com/photo-1518638150340-f706e86654de?w=800&q=80'],
  'Mexico':['https://images.unsplash.com/photo-1518638150340-f706e86654de?w=800&q=80'],
  'Южна Африка':['https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800&q=80'],
  'South Africa':['https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800&q=80'],
  'Кейптаун':['https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800&q=80'],
  'Cape Town':['https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800&q=80'],
  'Непал':['https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80'],
  'Nepal':['https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80'],
  'Шри Ланка':['https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80'],
  'Sri Lanka':['https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80'],
};

const COUNTRY_TRANSLATIONS = {
  // Bulgarian countries
  'българия':'Bulgaria','германия':'Germany','франция':'France','испания':'Spain','италия':'Italy',
  'япония':'Japan','китай':'China','русия':'Russia','бразилия':'Brazil','австралия':'Australia',
  'канада':'Canada','индия':'India','мексико':'Mexico','заир':'Congo','египет':'Egypt',
  'гърция':'Greece','турция':'Turkey','куба':'Cuba','тайланд':'Thailand','португалия':'Portugal',
  'холандия':'Netherlands','белгия':'Belgium','швейцария':'Switzerland','австрия':'Austria',
  'швеция':'Sweden','норвегия':'Norway','дания':'Denmark','финландия':'Finland','полша':'Poland',
  'чехия':'Czech Republic','румъния':'Romania','унгария':'Hungary','сърбия':'Serbia',
  'хърватия':'Croatia','мароко':'Morocco','тунис':'Tunisia','аржентина':'Argentina',
  'чили':'Chile','перу':'Peru','колумбия':'Colombia','венецуела':'Venezuela',
  'малдиви':'Maldives','сащ':'United States','великобритания':'United Kingdom',
  'южна корея':'South Korea','северна корея':'North Korea','южна африка':'South Africa',
  'нова зеландия':'New Zealand','саудитска арабия':'Saudi Arabia',
  'обединени арабски емирства':'United Arab Emirates',
  // Bulgarian cities → qualified "City, Bulgaria" for unambiguous Wikipedia lookup
  'софия':'Sofia, Bulgaria','пловдив':'Plovdiv, Bulgaria',
  'варна':'Varna, Bulgaria','бургас':'Burgas, Bulgaria',
  'велико търново':'Veliko Tarnovo, Bulgaria','несебър':'Nessebar, Bulgaria',
  'созопол':'Sozopol, Bulgaria','банско':'Bansko, Bulgaria',
  'стара загора':'Stara Zagora, Bulgaria','русе':'Ruse, Bulgaria','плевен':'Pleven, Bulgaria',
  'исландия':'Iceland','лондон':'London',
  // Bulgarian regions/cities/islands
  'аляска':'United States','хавай':'United States','тексас':'United States',
  'калифорния':'United States','флорида':'United States','ню йорк':'United States',
  'нюйорк':'United States','лос анджелис':'United States','лас вегас':'United States',
  'чикаго':'United States','маями':'United States','сан франциско':'United States',
  'бостон':'United States','сиатъл':'United States','вашингтон':'United States',
  'бали':'Indonesia','ломбок':'Indonesia','ява':'Indonesia',
  'сицилия':'Italy','сардиния':'Italy','тоскана':'Italy',
  'рим':'Rome','милано':'Milan','венеция':'Venice','флоренция':'Florence','неапол':'Naples',
  'корсика':'Corsica','прованс':'Provence','париж':'Paris','ница':'Nice','лион':'Lyon','марсилия':'Marseille',
  'каталония':'Spain','андалусия':'Spain','каталуния':'Spain',
  'барселона':'Spain','мадрид':'Spain','севиля':'Spain','валенсия':'Spain',
  'канарски':'Spain','канарски острови':'Spain','майорка':'Spain','ибиса':'Spain',
  'бавария':'Germany','берлин':'Germany','мюнхен':'Germany','хамбург':'Germany','франкфурт':'Germany',
  'патагония':'Argentina','буенос айрес':'Argentina','сантяго':'Chile','валпараисо':'Chile',
  'амазония':'Brazil','рио де жанейро':'Brazil','сао пауло':'Brazil','рио':'Brazil','салвадор':'Brazil',
  'юкатан':'Mexico','мексико сити':'Mexico','канкун':'Mexico','гуадалахара':'Mexico',
  'хонконг':'Hong Kong','макао':'Macao','тайван':'Taiwan',
  'шотландия':'United Kingdom','уелс':'United Kingdom','ирландия':'Ireland',
  'фиджи':'Fiji','сейшели':'Seychelles','мавриций':'Mauritius','реюнион':'France',
  'крит':'Greece','корфу':'Greece','санторини':'Greece','сантория':'Greece',
  'атина':'Greece','солун':'Greece','родос':'Greece',
  'дубай':'Dubai','абу даби':'Abu Dhabi',
  'доха':'Qatar','рияд':'Saudi Arabia','мека':'Saudi Arabia',
  'пукет':'Thailand','самуи':'Thailand','чианг май':'Thailand','банкок':'Thailand','паттая':'Thailand','краби':'Thailand',
  'борнео':'Malaysia','пенанг':'Malaysia','лангкави':'Malaysia',
  'палаван':'Philippines','боракай':'Philippines','себу':'Philippines',
  'сингапур':'Singapore',
  'квебек':'Canada','онтарио':'Canada','британска колумбия':'Canada',
  'торонто':'Canada','монреал':'Canada','ванкувър':'Canada',
  'сидни':'Australia','мелбърн':'Australia','бризбейн':'Australia','перт':'Australia',
  'окланд':'New Zealand','уелингтън':'New Zealand','крайстчърч':'New Zealand',
  'богота':'Colombia','медельин':'Colombia','картахена':'Colombia',
  'лима':'Peru','куско':'Peru','мачу пикчу':'Peru',
  'монтевидео':'Uruguay','асунсион':'Paraguay','ла пас':'Bolivia','кито':'Ecuador',
  'каракас':'Venezuela','хавана':'Cuba','варадеро':'Cuba',
  'кингстон':'Jamaica','монтего бей':'Jamaica','насау':'Bahamas',
  'виена':'Austria','залцбург':'Austria','инсбрук':'Austria',
  'прага':'Czech Republic','брно':'Czech Republic',
  'будапеща':'Hungary','варшава':'Poland','краков':'Poland','гданск':'Poland',
  'амстердам':'Netherlands','ротердам':'Netherlands','хага':'Netherlands',
  'брюксел':'Belgium','брюж':'Belgium','гент':'Belgium',
  'цюрих':'Switzerland','женева':'Switzerland','берн':'Switzerland',
  'стокхолм':'Sweden','осло':'Norway','копенхаген':'Denmark','хелзинки':'Finland',
  'москва':'Russia','санкт петербург':'Russia','сочи':'Russia',
  'киев':'Ukraine','лвив':'Ukraine','одеса':'Ukraine',
  'токио':'Tokyo','осака':'Osaka','киото':'Kyoto','фуджи':'Mount Fuji',
  'пекин':'China','шанхай':'China','гуанджоу':'China','шенжен':'China',
  'сеул':'South Korea','бусан':'South Korea','джеджу':'South Korea',
  'мумбай':'India','делхи':'India','гоа':'India','джайпур':'India',
  'хо ши мин':'Vietnam','ханой':'Vietnam','хой ан':'Vietnam','да нанг':'Vietnam',
  'сием реп':'Cambodia','пном пен':'Cambodia',
  'янгон':'Myanmar','баган':'Myanmar',
  'катманду':'Nepal','покхара':'Nepal',
  'коломбо':'Sri Lanka','канди':'Sri Lanka',
  'малé':'Maldives','хулхумале':'Maldives',
  'найроби':'Kenya','момбаса':'Kenya','масай мара':'Kenya',
  'дар ес салам':'Tanzania','занзибар':'Tanzania','аруша':'Tanzania','килиманджаро':'Tanzania',
  'кейптаун':'South Africa','йоханесбург':'South Africa','дърбан':'South Africa',
  'лагос':'Nigeria','абуджа':'Nigeria','акра':'Ghana',
  'адис абеба':'Ethiopia','лалибела':'Ethiopia',
  'луанда':'Angola','мапуто':'Mozambique','антананариво':'Madagascar',
  'лисабон':'Portugal','порто':'Portugal','алгарве':'Portugal',
  'истанбул':'Turkey','анталия':'Turkey','кападокия':'Turkey',
  'маракеш':'Morocco','фес':'Morocco','казабланка':'Morocco','каир':'Egypt','луксор':'Egypt','хургада':'Egypt','шарм ел шейх':'Egypt',
  // Spanish
  'alemania':'Germany','francia':'France','españa':'Spain','italia':'Italy','japón':'Japan',
  'rusia':'Russia','brasil':'Brazil','canadá':'Canada','méxico':'Mexico','egipto':'Egypt',
  'grecia':'Greece','turquía':'Turkey','tailandia':'Thailand','marruecos':'Morocco',
  // French
  'allemagne':'Germany','espagne':'Spain','italie':'Italy','japon':'Japan','russie':'Russia',
  'brésil':'Brazil','australie':'Australia','inde':'India','egypte':'Egypt','grèce':'Greece',
  'turquie':'Turkey','thaïlande':'Thailand','maroc':'Morocco',
  // German
  'deutschland':'Germany','frankreich':'France','spanien':'Spain','italien':'Italy',
  'russland':'Russia','brasilien':'Brazil','australien':'Australia','indien':'India',
  'ägypten':'Egypt','griechenland':'Greece','türkei':'Turkey','marokko':'Morocco',
  // English regions/cities
  'alaska':'United States','hawaii':'United States','texas':'United States',
  'california':'United States','florida':'United States','bali':'Indonesia',
  'sicily':'Italy','sardinia':'Italy','corsica':'France','catalonia':'Spain',
  'bavaria':'Germany','tuscany':'Italy','provence':'France',
  'patagonia':'Argentina','amazon':'Brazil','yucatan':'Mexico',
  'hong kong':'Hong Kong','macau':'Macao','taiwan':'Taiwan',
  'scotland':'United Kingdom','wales':'United Kingdom',
  'fiji':'Fiji','maldives':'Maldives','seychelles':'Seychelles',
  'dubai':'United Arab Emirates','abu dhabi':'United Arab Emirates',
  'phuket':'Thailand','samui':'Thailand','chiang mai':'Thailand',
  'lombok':'Indonesia','java':'Indonesia','borneo':'Malaysia',
  'palawan':'Philippines','boracay':'Philippines','cebu':'Philippines',
  'zanzibar':'Tanzania','kilimanjaro':'Tanzania',
  'marrakech':'Morocco','casablanca':'Morocco','fez':'Morocco',
  'cairo':'Egypt','luxor':'Egypt','hurghada':'Egypt',
  'istanbul':'Turkey','antalya':'Turkey','cappadocia':'Turkey',
  'athens':'Greece','santorini':'Greece','mykonos':'Greece','rhodes':'Greece',
  'lisbon':'Portugal','porto':'Portugal','algarve':'Portugal',
  'barcelona':'Spain','madrid':'Spain','seville':'Spain',
  'rome':'Italy','milan':'Italy','venice':'Italy','florence':'Italy',
  'paris':'France','nice':'France','lyon':'France',
  'berlin':'Germany','munich':'Germany','hamburg':'Germany',
  'vienna':'Austria','salzburg':'Austria',
  'prague':'Czech Republic','budapest':'Hungary','warsaw':'Poland','krakow':'Poland',
  'amsterdam':'Netherlands','brussels':'Belgium','zurich':'Switzerland',
  'stockholm':'Sweden','oslo':'Norway','copenhagen':'Denmark','helsinki':'Finland',
  'moscow':'Russia','st petersburg':'Russia','sochi':'Russia',
  'kyiv':'Ukraine','lviv':'Ukraine','odessa':'Ukraine',
  'tokyo':'Japan','osaka':'Japan','kyoto':'Japan',
  'beijing':'China','shanghai':'China','guangzhou':'China',
  'seoul':'South Korea','busan':'South Korea','jeju':'South Korea',
  'mumbai':'India','delhi':'India','goa':'India','jaipur':'India',
  'bangkok':'Thailand','pattaya':'Thailand','krabi':'Thailand',
  'ho chi minh':'Vietnam','hanoi':'Vietnam','hoi an':'Vietnam',
  'siem reap':'Cambodia','kathmandu':'Nepal','colombo':'Sri Lanka',
  'nairobi':'Kenya','mombasa':'Kenya','masai mara':'Kenya',
  'cape town':'South Africa','johannesburg':'South Africa','durban':'South Africa',
  'lagos':'Nigeria','accra':'Ghana','addis ababa':'Ethiopia',
  'new york':'United States','los angeles':'United States','las vegas':'United States',
  'chicago':'United States','miami':'United States','san francisco':'United States',
  'toronto':'Canada','montreal':'Canada','vancouver':'Canada',
  'sydney':'Australia','melbourne':'Australia','brisbane':'Australia',
  'auckland':'New Zealand','wellington':'New Zealand',
  'mexico city':'Mexico','cancun':'Mexico',
  'bogota':'Colombia','medellin':'Colombia','cartagena':'Colombia',
  'lima':'Peru','cusco':'Peru','machu picchu':'Peru',
  'santiago':'Chile','rio':'Brazil','havana':'Cuba',
  'kingston':'Jamaica','nassau':'Bahamas',
  // Peninsulas, island groups, remote regions - Bulgarian
  'камчатка':'Russia','кола':'Russia','ямал':'Russia','таймир':'Russia',
  'чукотка':'Russia','сахалин':'Russia','курили':'Russia','карелия':'Russia',
  'сибир':'Russia','урал':'Russia','крим':'Ukraine',
  'скандинавия':'Norway','лапландия':'Finland','финмарк':'Norway',
  'ютланд':'Denmark','бретан':'France','нормандия':'France','елзас':'France',
  'корнуол':'United Kingdom','йоркшир':'United Kingdom','хайлендс':'United Kingdom',
  'пиренеи':'Spain','андора':'Andorra','монако':'Monaco','лихтенщайн':'Liechtenstein',
  'лусон':'Philippines','минданао':'Philippines','висаяс':'Philippines',
  'суматра':'Indonesia','сулавеси':'Indonesia','папуа':'Papua New Guinea',
  'нова гвинея':'Papua New Guinea','тасмания':'Australia','тазмания':'Australia',
  'нюфаундленд':'Canada','лабрадор':'Canada','юкон':'Canada','нунавут':'Canada',
  'баха калифорния':'Mexico','юкатански полуостров':'Mexico',
  'флоридски полуостров':'United States','кейп код':'United States',
  'алеутски острови':'United States','прибилови острови':'United States',
  'галапагос':'Ecuador','огнена земя':'Argentina',
  'фолклендски острови':'United Kingdom','антарктика':'Antarctica',
  'гренландия':'Denmark','шпицберген':'Norway','фарьорски острови':'Denmark',
  'азорски острови':'Portugal','мадейра':'Portugal','кабо верде':'Cape Verde',
  'сао томе':'Sao Tome and Principe','коморски острови':'Comoros','мадагаскар':'Madagascar',
  'малдивски острови':'Maldives','андамански острови':'India','лакшадвип':'India',
  'шри ланка':'Sri Lanka','цейлон':'Sri Lanka',
  'калимантан':'Indonesia','тимор':'Timor-Leste','источен тимор':'Timor-Leste',
  'молукски острови':'Indonesia','банда':'Indonesia',
  'соломонови':'Solomon Islands','соломонови острови':'Solomon Islands','вануату':'Vanuatu','тонга':'Tonga',
  'самоа':'Samoa','американска самоа':'American Samoa','таити':'France',
  'нова каледония':'France','гуам':'United States','марианските острови':'United States',
  'маршалови острови':'Marshall Islands','микронезия':'Micronesia',
  'палау':'Palau','кирибати':'Kiribati','тувалу':'Tuvalu','науру':'Nauru',
  'хаити':'Haiti','доминиканска република':'Dominican Republic',
  'пуерто рико':'United States','тринидад':'Trinidad and Tobago',
  'барбадос':'Barbados','сейнт лусия':'Saint Lucia','гренада':'Grenada',
  'антигуа':'Antigua and Barbuda','доминика':'Dominica',
  'бахами':'Bahamas','туркс и кайкос':'United Kingdom',
  'кайманови острови':'United Kingdom','бермуда':'United Kingdom',
  'гибралтар':'United Kingdom','малта':'Malta','кипър':'Cyprus',
  'лесбос':'Greece','хиос':'Greece','самос':'Greece',
  'закинтос':'Greece','кефалония':'Greece','лефкада':'Greece',
  'миконос':'Greece','наксос':'Greece','парос':'Greece','тира':'Greece',
  'кос':'Greece','лерос':'Greece','патмос':'Greece',
  'пантелерия':'Italy','балеарски острови':'Spain','менорка':'Spain',
  'форментера':'Spain','лансароте':'Spain','фуертевентура':'Spain',
  'гран канария':'Spain','тенерифе':'Spain','ла палма':'Spain',
  'ел иеро':'Spain','ла гомера':'Spain',
  // English peninsulas and regions
  'kamchatka':'Russia','crimea':'Ukraine','sakhalin':'Russia',
  'siberia':'Russia','ural':'Russia','karelia':'Russia',
  'scandinavia':'Norway','lapland':'Finland','jutland':'Denmark',
  'brittany':'France','normandy':'France','alsace':'France',
  'cornwall':'United Kingdom','highlands':'United Kingdom',
  'pyrenees':'Spain','andorra':'Andorra','monaco':'Monaco',
  'luzon':'Philippines','mindanao':'Philippines','visayas':'Philippines',
  'sumatra':'Indonesia','sulawesi':'Indonesia','papua':'Papua New Guinea',
  'tasmania':'Australia','newfoundland':'Canada','yukon':'Canada',
  'galapagos':'Ecuador','tierra del fuego':'Argentina',
  'falkland islands':'United Kingdom','falklands':'United Kingdom',
  'greenland':'Denmark','svalbard':'Norway','faroe islands':'Denmark',
  'azores':'Portugal','madeira':'Portugal','cape verde':'Cape Verde',
  'madagascar':'Madagascar','reunion':'France','mauritius':'Mauritius',
  'comoros':'Comoros','sri lanka':'Sri Lanka','ceylon':'Sri Lanka',
  'east timor':'Timor-Leste','timor':'Timor-Leste',
  'solomon islands':'Solomon Islands','vanuatu':'Vanuatu',
  'tonga':'Tonga','samoa':'Samoa','tahiti':'France',
  'guam':'United States','palau':'Palau','kiribati':'Kiribati',
  'cuba':'Cuba','haiti':'Haiti','puerto rico':'United States',
  'barbados':'Barbados','bahamas':'Bahamas','bermuda':'United Kingdom',
  'gibraltar':'United Kingdom','malta':'Malta','cyprus':'Cyprus',
  'crete':'Greece',
  'majorca':'Spain','mallorca':'Spain','ibiza':'Spain','menorca':'Spain',
  'tenerife':'Spain','gran canaria':'Spain','lanzarote':'Spain',
};
const translateQuery = (q) => COUNTRY_TRANSLATIONS[q.toLowerCase().trim()] || q;

// Explicit city → country (English key in CITY_COUNTRY_DATA) for reliable structured data.
// Covers BG and EN spellings of popular cities so climate/currency/timezone are never empty.
const CITY_TO_COUNTRY = {
  // Thailand
  'патая':'Thailand','pattaya':'Thailand','phuket':'Thailand','банкок':'Thailand','bangkok':'Thailand','чианг май':'Thailand','chiang mai':'Thailand','краби':'Thailand','krabi':'Thailand',
  // Spain
  'барселона':'Spain','barcelona':'Spain','мадрид':'Spain','madrid':'Spain','севиля':'Spain','seville':'Spain','валенсия':'Spain','valencia':'Spain','малага':'Spain','malaga':'Spain',
  // Italy
  'рим':'Italy','rome':'Italy','милано':'Italy','milan':'Italy','венеция':'Italy','venice':'Italy','флоренция':'Italy','florence':'Italy','неапол':'Italy','naples':'Italy',
  // France
  'париж':'France','paris':'France','ница':'France','nice':'France','марсилия':'France','marseille':'France','лион':'France','lyon':'France',
  // Greece
  'атина':'Greece','athens':'Greece','санторини':'Greece','santorini':'Greece','миконос':'Greece','mykonos':'Greece','крит':'Greece','crete':'Greece','солун':'Greece','thessaloniki':'Greece',
  // Portugal
  'лисабон':'Portugal','lisbon':'Portugal','порто':'Portugal','porto':'Portugal','фару':'Portugal','faro':'Portugal',
  // Turkey
  'истанбул':'Turkey','istanbul':'Turkey','анталия':'Turkey','antalya':'Turkey','бодрум':'Turkey','bodrum':'Turkey','кападокия':'Turkey','cappadocia':'Turkey',
  // Croatia
  'дубровник':'Croatia','dubrovnik':'Croatia','сплит':'Croatia','split':'Croatia','загреб':'Croatia','zagreb':'Croatia',
  // Germany / Austria / Netherlands / Belgium / Switzerland
  'берлин':'Germany','berlin':'Germany','мюнхен':'Germany','munich':'Germany','виена':'Austria','vienna':'Austria','амстердам':'Netherlands','amsterdam':'Netherlands','брюксел':'Belgium','brussels':'Belgium','цюрих':'Switzerland','zurich':'Switzerland',
  // UK / Bulgaria are already handled elsewhere; add a few staples
  'лондон':'United Kingdom','london':'United Kingdom',
  // France extra
  'сен тропе':'France','saint-tropez':'France','st tropez':'France','кан':'France','cannes':'France','монако':'Monaco','monaco':'Monaco',
  // More Italy
  'позитано':'Italy','positano':'Italy','амалфи':'Italy','amalfi':'Italy','капри':'Italy','capri':'Italy','сиена':'Italy','siena':'Italy',
  // More Spain
  'ибиса':'Spain','ibiza':'Spain','палма':'Spain','palma':'Spain','гранада':'Spain','granada':'Spain','билбао':'Spain','bilbao':'Spain',
  // More Greece
  'родос':'Greece','rhodes':'Greece','корфу':'Greece','corfu':'Greece','закинтос':'Greece','zakynthos':'Greece','наксос':'Greece','naxos':'Greece',
  // Middle East / Asia
  'дубай':'United Arab Emirates','dubai':'United Arab Emirates','абу даби':'United Arab Emirates','abu dhabi':'United Arab Emirates','доха':'Qatar','doha':'Qatar','маракеш':'Morocco','marrakech':'Morocco','кайро':'Egypt','cairo':'Egypt','хургада':'Egypt','hurghada':'Egypt','шарм ел шейх':'Egypt','sharm el sheikh':'Egypt',
  'токио':'Japan','tokyo':'Japan','осака':'Japan','osaka':'Japan','сеул':'South Korea','seoul':'South Korea','сингапур':'Singapore','singapore':'Singapore','хонг конг':'Hong Kong','hong kong':'Hong Kong','бали':'Indonesia','bali':'Indonesia',
  // Americas
  'ню йорк':'United States','new york':'United States','маями':'United States','miami':'United States','лос анджелис':'United States','los angeles':'United States','лас вегас':'United States','las vegas':'United States','хавана':'Cuba','havana':'Cuba','рио де жанейро':'Brazil','rio de janeiro':'Brazil','канкун':'Mexico','cancun':'Mexico',
};
const cityCountryData = (q) => {
  const key = (q || '').toLowerCase().trim();
  const country = CITY_TO_COUNTRY[key];
  return country ? CITY_COUNTRY_DATA[country] : null;
};

const CURRENCY_NAMES_BG = {
  'Euro':'Евро','United States dollar':'Щатски долар','British pound sterling':'Британски паунд',
  'Japanese yen':'Японска йена','Chinese yuan':'Китайски юан','Russian ruble':'Руска рубла',
  'United Arab Emirates dirham':'Дирхам (ОАЕ)','Swiss franc':'Швейцарски франк',
  'Canadian dollar':'Канадски долар','Australian dollar':'Австралийски долар',
  'New Zealand dollar':'Новозеландски долар','Swedish krona':'Шведска крона',
  'Norwegian krone':'Норвежка крона','Danish krone':'Датска крона',
  'Turkish lira':'Турска лира','Indian rupee':'Индийска рупия',
  'Brazilian real':'Бразилски реал','South African rand':'Южноафрикански ранд',
  'Mexican peso':'Мексиканско песо','Argentine peso':'Аржентинско песо',
  'Thai baht':'Тайландски бат','Indonesian rupiah':'Индонезийска рупия',
  'Malaysian ringgit':'Малайзийски рингит','Philippine peso':'Филипинско песо',
  'Singapore dollar':'Сингапурски долар','Hong Kong dollar':'Хонконгски долар',
  'South Korean won':'Южнокорейски вон','Polish złoty':'Полска злота',
  'Czech koruna':'Чешка крона','Hungarian forint':'Унгарски форинт',
  'Romanian leu':'Румънска лея','Bulgarian lev':'Евро €',
  'Croatian kuna':'Хърватска куна','Serbian dinar':'Сръбски динар',
  'Egyptian pound':'Египетска лира','Moroccan dirham':'Мароканска дирхама',
  'Kenyan shilling':'Кенийски шилинг','Tanzanian shilling':'Танзанийски шилинг',
  'Maldivian rufiyaa':'Малдивска руфия','Sri Lankan rupee':'Шриланкийска рупия',
};
const LANGUAGE_NAMES_BG = {
  'Arabic':'Арабски','English':'Английски','French':'Френски','German':'Немски',
  'Spanish':'Испански','Italian':'Италиански','Portuguese':'Португалски',
  'Russian':'Руски','Chinese':'Китайски','Japanese':'Японски','Korean':'Корейски',
  'Hindi':'Хинди','Turkish':'Турски','Dutch':'Нидерландски','Swedish':'Шведски',
  'Norwegian':'Норвежки','Danish':'Датски','Finnish':'Финландски','Polish':'Полски',
  'Greek':'Гръцки','Bulgarian':'Български','Romanian':'Румънски','Hungarian':'Унгарски',
  'Czech':'Чешки','Thai':'Тайски','Vietnamese':'Виетнамски','Indonesian':'Индонезийски',
  'Malay':'Малайски','Swahili':'Суахили','Amharic':'Амхарски',
};

const CITY_COUNTRY_DATA = {
  'Bulgaria':  { weather:'Умерено-континентален', currency:'Euro €', currencyBg:'Евро €', language:'Bulgarian', languageBg:'Български', timezone:'UTC+2 / UTC+3 (лято)', bestSeason:'Юни - Септември', visitors:'9M/year' },
  'Greece':    { weather:'Средиземноморски',       currency:'Euro €',    currencyBg:'Евро €',     language:'Greek',     languageBg:'Гръцки',     timezone:'UTC+2 / UTC+3 (лято)', bestSeason:'Апр - Окт',       visitors:'32M/year' },
  'Italy':     { weather:'Средиземноморски',       currency:'Euro €',    currencyBg:'Евро €',     language:'Italian',   languageBg:'Италиански',  timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Апр - Юни',       visitors:'57M/year' },
  'France':    { weather:'Умерен океански',        currency:'Euro €',    currencyBg:'Евро €',     language:'French',    languageBg:'Френски',     timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Апр - Окт',       visitors:'89M/year' },
  'Germany':   { weather:'Умерен',                 currency:'Euro €',    currencyBg:'Евро €',     language:'German',    languageBg:'Немски',      timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Май - Сеп',       visitors:'40M/year' },
  'Spain':     { weather:'Средиземноморски',       currency:'Euro €',    currencyBg:'Евро €',     language:'Spanish',   languageBg:'Испански',    timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Март - Май',      visitors:'85M/year' },
  'Turkey':    { weather:'Средиземноморски',       currency:'Turkish Lira', currencyBg:'Турска лира', language:'Turkish', languageBg:'Турски',   timezone:'UTC+3',                bestSeason:'Апр - Окт',       visitors:'51M/year' },
  'Portugal':  { weather:'Средиземноморски',       currency:'Euro €',    currencyBg:'Евро €',     language:'Portuguese',languageBg:'Португалски', timezone:'UTC+0 / UTC+1 (лято)', bestSeason:'Март - Окт',      visitors:'26M/year' },
  'Croatia':   { weather:'Средиземноморски',       currency:'Euro €',    currencyBg:'Евро €',     language:'Croatian',  languageBg:'Хърватски',   timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Юни - Сеп',       visitors:'20M/year' },
};
function translateWikiDesc(desc) {
  if (!desc) return desc;
  return desc
    .replace(/\bCity in\b/g, 'Град в').replace(/\bTown in\b/g, 'Град в').replace(/\bVillage in\b/g, 'Село в')
    .replace(/\bCapital( city)? of\b/g, 'Столица на').replace(/\bMunicipality in\b/g, 'Община в')
    .replace(/\bBulgaria\b/g, 'България').replace(/\bGreece\b/g, 'Гърция').replace(/\bItaly\b/g, 'Италия')
    .replace(/\bFrance\b/g, 'Франция').replace(/\bGermany\b/g, 'Германия').replace(/\bSpain\b/g, 'Испания')
    .replace(/\bTurkey\b/g, 'Турция').replace(/\bPortugal\b/g, 'Португалия').replace(/\bCroatia\b/g, 'Хърватия');
}
async function fetchRestCountry(query, lang) {
  const knownCountryFallback = () => {
    const raw = (query || '').trim();
    if (!raw) return null;
    const key = raw.toLowerCase();
    const translated = translateQuery(raw);
    const canonicalAliases = {
      'румъния':'Romania','romania':'Romania',
      'българия':'Bulgaria','bulgaria':'Bulgaria',
      'гърция':'Greece','greece':'Greece',
      'турция':'Turkey','turkey':'Turkey',
      'италия':'Italy','italy':'Italy',
      'испания':'Spain','spain':'Spain',
      'франция':'France','france':'France',
      'германия':'Germany','germany':'Germany',
      'австрия':'Austria','austria':'Austria',
      'сърбия':'Serbia','serbia':'Serbia',
      'хърватия':'Croatia','croatia':'Croatia',
      'черна гора':'Montenegro','montenegro':'Montenegro',
      'албания':'Albania','albania':'Albania',
      'северна македония':'North Macedonia','north macedonia':'North Macedonia',
      'португалия':'Portugal','portugal':'Portugal',
      'нидерландия':'Netherlands','холандия':'Netherlands','netherlands':'Netherlands',
      'великобритания':'United Kingdom','обединено кралство':'United Kingdom','united kingdom':'United Kingdom','uk':'United Kingdom',
      'сащ':'United States','usa':'United States','us':'United States','united states':'United States',
      'тайланд':'Thailand','thailand':'Thailand',
      'виетнам':'Vietnam','vietnam':'Vietnam',
      'индонезия':'Indonesia','indonesia':'Indonesia',
      'шри ланка':'Sri Lanka','sri lanka':'Sri Lanka',
      'филипини':'Philippines','philippines':'Philippines',
    };
    const countryData = {
      'Romania': { bg:'Румъния', region:'Europe / Eastern Europe', regionBg:'Европа / Източна Европа', weather:'Умерен континентален', currency:'Romanian leu', currencyBg:'Румънска лея', language:'Romanian', languageBg:'Румънски', timezone:'UTC+2 / UTC+3 (лято)', bestSeason:'Май–Сеп', visitors:'9M/год.', tourism:'https://www.romaniatourism.com' },
      'Bulgaria': { bg:'България', region:'Europe / Eastern Europe', regionBg:'Европа / Източна Европа', weather:'Умерено-континентален', currency:'Euro €', currencyBg:'Евро €', language:'Bulgarian', languageBg:'Български', timezone:'UTC+2 / UTC+3 (лято)', bestSeason:'Юни–Сеп', visitors:'9M/год.' },
      'Greece': { bg:'Гърция', region:'Europe / Southern Europe', regionBg:'Европа / Южна Европа', weather:'Средиземноморски', currency:'Euro €', currencyBg:'Евро €', language:'Greek', languageBg:'Гръцки', timezone:'UTC+2 / UTC+3 (лято)', bestSeason:'Апр–Окт', visitors:'32M/год.' },
      'Turkey': { bg:'Турция', region:'Asia / Europe', regionBg:'Азия / Европа', weather:'Средиземноморски', currency:'Turkish lira', currencyBg:'Турска лира', language:'Turkish', languageBg:'Турски', timezone:'UTC+3', bestSeason:'Апр–Окт', visitors:'51M/год.' },
      'Italy': { bg:'Италия', region:'Europe / Southern Europe', regionBg:'Европа / Южна Европа', weather:'Средиземноморски', currency:'Euro €', currencyBg:'Евро €', language:'Italian', languageBg:'Италиански', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Апр–Юни', visitors:'57M/год.' },
      'Spain': { bg:'Испания', region:'Europe / Southern Europe', regionBg:'Европа / Южна Европа', weather:'Средиземноморски', currency:'Euro €', currencyBg:'Евро €', language:'Spanish', languageBg:'Испански', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Март–Май', visitors:'85M/год.' },
      'France': { bg:'Франция', region:'Europe / Western Europe', regionBg:'Европа / Западна Европа', weather:'Умерен океански', currency:'Euro €', currencyBg:'Евро €', language:'French', languageBg:'Френски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Апр–Окт', visitors:'89M/год.' },
      'Germany': { bg:'Германия', region:'Europe / Western Europe', regionBg:'Европа / Западна Европа', weather:'Умерен', currency:'Euro €', currencyBg:'Евро €', language:'German', languageBg:'Немски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Май–Сеп', visitors:'40M/год.' },
      'Austria': { bg:'Австрия', region:'Europe / Central Europe', regionBg:'Европа / Централна Европа', weather:'Алпийски/Умерен', currency:'Euro €', currencyBg:'Евро €', language:'German', languageBg:'Немски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Юни–Авг', visitors:'31M/год.' },
      'Serbia': { bg:'Сърбия', region:'Europe / Balkans', regionBg:'Европа / Балкани', weather:'Умерен континентален', currency:'Serbian dinar', currencyBg:'Сръбски динар', language:'Serbian', languageBg:'Сръбски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Май–Сеп', visitors:'2M/год.', tourism:'https://serbia.travel' },
      'Croatia': { bg:'Хърватия', region:'Europe / Balkans', regionBg:'Европа / Балкани', weather:'Средиземноморски', currency:'Euro €', currencyBg:'Евро €', language:'Croatian', languageBg:'Хърватски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Юни–Сеп', visitors:'21M/год.' },
      'Montenegro': { bg:'Черна гора', region:'Europe / Balkans', regionBg:'Европа / Балкани', weather:'Средиземноморски', currency:'Euro €', currencyBg:'Евро €', language:'Montenegrin', languageBg:'Черногорски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Май–Сеп', visitors:'2M/год.', tourism:'https://www.montenegro.travel' },
      'Albania': { bg:'Албания', region:'Europe / Balkans', regionBg:'Европа / Балкани', weather:'Средиземноморски', currency:'Albanian lek', currencyBg:'Албански лек', language:'Albanian', languageBg:'Албански', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Май–Сеп', visitors:'7M/год.', tourism:'https://albania.al' },
      'North Macedonia': { bg:'Северна Македония', region:'Europe / Balkans', regionBg:'Европа / Балкани', weather:'Умерен континентален', currency:'Macedonian denar', currencyBg:'Македонски денар', language:'Macedonian', languageBg:'Македонски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Май–Сеп', visitors:'1M/год.', tourism:'https://macedonia-timeless.com' },
      'Portugal': { bg:'Португалия', region:'Europe / Southern Europe', regionBg:'Европа / Южна Европа', weather:'Средиземноморски', currency:'Euro €', currencyBg:'Евро €', language:'Portuguese', languageBg:'Португалски', timezone:'UTC+0 / UTC+1 (лято)', bestSeason:'Март–Окт', visitors:'26M/год.' },
      'Netherlands': { bg:'Нидерландия', region:'Europe / Western Europe', regionBg:'Европа / Западна Европа', weather:'Умерен морски', currency:'Euro €', currencyBg:'Евро €', language:'Dutch', languageBg:'Нидерландски', timezone:'UTC+1 / UTC+2 (лято)', bestSeason:'Апр–Май', visitors:'20M/год.' },
      'United Kingdom': { bg:'Великобритания', region:'Europe / Northern Europe', regionBg:'Европа / Северна Европа', weather:'Умерен океански', currency:'British pound sterling', currencyBg:'Британски паунд', language:'English', languageBg:'Английски', timezone:'UTC+0 / UTC+1 (лято)', bestSeason:'Май–Сеп', visitors:'39M/год.' },
      'United States': { bg:'САЩ', region:'Americas / North America', regionBg:'Америка / Северна Америка', weather:'Разнообразен', currency:'United States dollar', currencyBg:'Щатски долар', language:'English', languageBg:'Английски', timezone:'UTC-5 to UTC-10', bestSeason:'Апр–Окт', visitors:'79M/год.', tourism:'https://www.visittheusa.com' },
      'Thailand': { bg:'Тайланд', region:'Asia / Southeast Asia', regionBg:'Азия / Югоизточна Азия', weather:'Тропически', currency:'Thai baht', currencyBg:'Тайландски бат', language:'Thai', languageBg:'Тайски', timezone:'UTC+7', bestSeason:'Ное–Апр', visitors:'39M/год.' },
      'Vietnam': { bg:'Виетнам', region:'Asia / Southeast Asia', regionBg:'Азия / Югоизточна Азия', weather:'Тропически', currency:'Vietnamese dong', currencyBg:'Виетнамски донг', language:'Vietnamese', languageBg:'Виетнамски', timezone:'UTC+7', bestSeason:'Фев–Апр', visitors:'18M/год.' },
      'Indonesia': { bg:'Индонезия', region:'Asia / Southeast Asia', regionBg:'Азия / Югоизточна Азия', weather:'Тропически', currency:'Indonesian rupiah', currencyBg:'Индонезийска рупия', language:'Indonesian', languageBg:'Индонезийски', timezone:'UTC+7 to UTC+9', bestSeason:'Май–Сеп', visitors:'16M/год.' },
      'Sri Lanka': { bg:'Шри Ланка', region:'Asia / South Asia', regionBg:'Азия / Южна Азия', weather:'Тропически', currency:'Sri Lankan rupee', currencyBg:'Шриланкийска рупия', language:'Sinhala / Tamil', languageBg:'Синхала / Тамилски', timezone:'UTC+5:30', bestSeason:'Дек–Апр', visitors:'2M/год.' },
      'Philippines': { bg:'Филипини', region:'Asia / Southeast Asia', regionBg:'Азия / Югоизточна Азия', weather:'Тропически', currency:'Philippine peso', currencyBg:'Филипинско песо', language:'Filipino / English', languageBg:'Филипински / Английски', timezone:'UTC+8', bestSeason:'Дек–Май', visitors:'8M/год.' },
    };
    const canonical = canonicalAliases[key] || canonicalAliases[translated.toLowerCase()] || (countryData[translated] ? translated : null);
    const data = canonical ? countryData[canonical] : null;
    if (!data) return null;
    const photos = DEST_PHOTOS[data.bg] || DEST_PHOTOS[canonical] || [];
    const fallbackImg = photos[0] || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80';
    const bgName = data.bg || raw;
    const desc = lang === 'bg'
      ? `${bgName} е популярна туристическа дестинация.`
      : `${canonical} is a popular travel destination.`;
    return {
      name: canonical, nameEn: canonical, nameBg: bgName,
      country: data.region, countryBg: lang === 'bg' ? data.regionBg : data.region,
      desc, descBg: desc,
      img: fallbackImg, photos: photos.length ? photos : [fallbackImg],
      tag: '🌍 Country', tagBg: '🌍 Държава',
      visitors: data.visitors || '—', bestTime: data.bestSeason || '—', bestTimeBg: data.bestSeason || '—',
      temp: data.regionBg || data.region || '—',
      weather: data.weather || '—',
      currency: data.currency || '—', currencyBg: data.currencyBg || data.currency || '—',
      language: lang === 'bg' ? (data.languageBg || data.language || '—') : (data.language || '—'),
      timezone: data.timezone || '—',
      wikipediaUrl: `https://en.wikipedia.org/wiki/${encodeURIComponent(canonical.replace(/\s+/g, '_'))}`,
      tourismUrl: data.tourism || TOURISM_URLS[bgName] || TOURISM_URLS[canonical] || '',
      isWikiResult: true,
      facts: [desc],
      factsBg: [desc],
    };
  };
  try {
    const translated = translateQuery(query);
    const res = await fetch(`https://restcountries.com/v3.1/name/${encodeURIComponent(translated)}?fullText=false&fields=name,capital,region,subregion,population,currencies,languages,timezones,flags,maps`);
    // If restcountries doesn't know this name (it's a city, not a country), fall back to Wikipedia directly
    if (!res.ok || res.status === 404) {
      // Build BG display name from original query (capitalize each word)
      const displayNameBg = query.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      const isCyrillic = /[Ѐ-ӿ]/.test(query);
      const wikiBase = isCyrillic ? 'bg.wikipedia.org' : 'en.wikipedia.org';
      // 1) Prefix cleanup — strip leading place-type words and trailing country qualifier so Wikipedia
      //    gets a clean article title. "село Тополи, България" → "Тополи". Original query kept for display.
      const _stripPfx = (s) => s
        .replace(/^(село|град|village|town|city)\s+/i, '')
        .replace(/,\s*(България|Bulgaria|Испания|Spain|Италия|Italy|Франция|France|Гърция|Greece|Португалия|Portugal|Турция|Turkey|Германия|Germany|Австрия|Austria|Холандия|Netherlands|Белгия|Belgium|Швейцария|Switzerland|Великобритания|United Kingdom|САЩ|United States|Япония|Japan|Китай|China|Египет|Egypt|Мароко|Morocco|Тайланд|Thailand|Хърватия|Croatia|Сърбия|Serbia|Румъния|Romania)$/i, '')
        .trim();
      const cleanedTerm = _stripPfx(isCyrillic ? displayNameBg : translated);
      let wikiRes = await fetch(`https://${wikiBase}/api/rest_v1/page/summary/${encodeURIComponent(cleanedTerm)}`);
      // Fall back to en.wikipedia.org if bg returns nothing useful
      if (!wikiRes.ok && isCyrillic) wikiRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(_stripPfx(translated))}`);
      // 2) Wikipedia Search API — resolves the exact article title when direct REST 404s
      if (!wikiRes.ok) {
        try {
          const _wikiSearch = async (base, q) => {
            const sr = await fetch(`https://${base}/w/rest.php/v1/search/title?q=${encodeURIComponent(q)}&limit=1`);
            if (!sr.ok) return null;
            const sd = await sr.json();
            const title = sd?.pages?.[0]?.key || sd?.pages?.[0]?.title;
            if (!title) return null;
            const sumr = await fetch(`https://${base}/api/rest_v1/page/summary/${encodeURIComponent(title)}`);
            return sumr.ok ? sumr : null;
          };
          const found = await _wikiSearch(wikiBase, cleanedTerm)
            || (isCyrillic ? await _wikiSearch('en.wikipedia.org', _stripPfx(translated)) : null);
          if (found) wikiRes = found;
        } catch (_) {}
      }
      // 3) AI fallback — last resort when both Wikipedia paths fail
      if (!wikiRes.ok) {
        try {
          const aiRes = await fetch(`${API_BASE}/api/openai/plan`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
            body: JSON.stringify({ model: 'gpt-4o-mini', messages: [
              { role: 'system', content: 'Return only raw JSON, no markdown, no explanation.' },
              { role: 'user', content: `Provide travel destination info for "${query}" as JSON with keys: country (e.g. "Bulgaria"), countryBg (Bulgarian name), weather (climate, 2-4 words), currency (name+symbol), currencyBg, language, languageBg, timezone (UTC offset), bestSeason (e.g. "Jun–Sep"), visitors (e.g. "9M/yr"), facts (array of 3-4 short factual strings about the place). Keep values short.` }
            ], max_tokens: 400 }) });
          if (aiRes.ok) {
            const aiData = await aiRes.json();
            const aiText = (aiData.choices?.[0]?.message?.content || '{}').replace(/```json|```/g, '').trim();
            const parsed = JSON.parse(aiText);
            const toStr = (v) => { if (v == null) return ''; if (typeof v === 'string') return v; if (typeof v === 'number') return String(v); if (Array.isArray(v)) return v.map(toStr).filter(Boolean).join(', '); if (typeof v === 'object') return Object.values(v).map(toStr).filter(Boolean).join(' '); return String(v); };
            const aiFacts = Array.isArray(parsed.facts) ? parsed.facts.map(toStr) : [toStr(parsed.facts) || '—'];
            const fallbackImg = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80';
            return {
              name: displayNameBg, nameEn: translated, nameBg: displayNameBg,
              country: toStr(parsed.country), countryBg: toStr(parsed.countryBg) || toStr(parsed.country),
              desc: aiFacts[0] || displayNameBg, descBg: aiFacts[0] || displayNameBg,
              img: fallbackImg, photos: [fallbackImg],
              tag: '🏙️ City', tagBg: '🏙️ Град',
              visitors: toStr(parsed.visitors) || '—', bestTime: toStr(parsed.bestSeason) || '—', bestTimeBg: toStr(parsed.bestSeason) || '—', temp: '—',
              weather: toStr(parsed.weather) || '—',
              currency: toStr(parsed.currency) || '—', currencyBg: toStr(parsed.currencyBg) || toStr(parsed.currency) || '—',
              language: lang === 'bg' ? (toStr(parsed.languageBg) || toStr(parsed.language) || '—') : (toStr(parsed.language) || '—'),
              timezone: toStr(parsed.timezone) || '—',
              wikipediaUrl: '', tourismUrl: '',
              isWikiResult: true, facts: aiFacts, factsBg: aiFacts,
            };
          }
        } catch (_) {}
        return knownCountryFallback();
      }
      let wikiData = await wikiRes.json();
      // If Wikipedia returns a disambiguation page, retry with the translated name (already qualified e.g. "Varna, Bulgaria")
      if (wikiData.type === 'disambiguation' || (wikiData.extract || '').includes('may refer to')) {
        const retryRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(translated)}`);
        if (retryRes.ok) { const retryData = await retryRes.json(); if (retryData.type !== 'disambiguation') wikiData = retryData; }
      }
      // Detect country from translated query suffix e.g. "Varna, Bulgaria" → "Bulgaria"
      const countrySuffix = translated.includes(', ') ? translated.split(', ').pop() : null;
      // Search both description and extract — small towns may only mention country in the longer text
      const wikiText = (wikiData?.description || '') + ' ' + (wikiData?.extract || '');
      const wikiCountry = Object.keys(CITY_COUNTRY_DATA).find(c => wikiText.includes(c));
      // Strong Bulgaria signal only — the generic word "област" matched foreign cities (e.g. Benghazi)
      // and wrongly gave them Bulgarian data. Require an explicit country mention.
      const isBgText = wikiText.includes('България') || wikiText.includes('в България');
      const cityFallback = cityCountryData(query) || (countrySuffix && CITY_COUNTRY_DATA[countrySuffix]) || (wikiCountry && CITY_COUNTRY_DATA[wikiCountry]) || (isBgText && CITY_COUNTRY_DATA['Bulgaria']) || {};
      const rawImg = wikiData?.thumbnail?.source || '';
      const destPhotos = DEST_PHOTOS[translated] || DEST_PHOTOS[query] || [];
      const goodImg = isGoodPhoto(rawImg) ? rawImg : (destPhotos[0] || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80');
      const allPhotos = destPhotos.length > 0 ? destPhotos : (isGoodPhoto(rawImg) ? [rawImg] : [goodImg]);
      const extract = wikiData?.extract || '';
      const facts = extract ? extract.split(/(?<=\.)\s+/).slice(0, 4).filter(Boolean) : ['—'];
      const wikiDesc = wikiData.description || '';
      return {
        name: wikiData.title || translated, nameEn: translated.includes(', ') ? translated.split(',')[0].trim() : (/[Ѐ-ӿ]/.test(wikiData.title||'') ? '' : (wikiData.title || '')), nameBg: displayNameBg || wikiData.title || translated,
        country: wikiDesc, countryBg: lang === 'bg' ? translateWikiDesc(wikiDesc) : wikiDesc,
        desc: extract ? extract.slice(0, 120) + '...' : translated,
        descBg: extract ? extract.slice(0, 120) + '...' : translated,
        img: goodImg, photos: allPhotos,
        tag: '🏙️ City', tagBg: '🏙️ Град',
        visitors: cityFallback.visitors || '—', bestTime: cityFallback.bestSeason || '—', bestTimeBg: cityFallback.bestSeason || '—', temp: '—',
        weather: cityFallback.weather || '—',
        currency: cityFallback.currency || '—', currencyBg: cityFallback.currencyBg || '—',
        language: lang === 'bg' ? (cityFallback.languageBg || '—') : (cityFallback.language || '—'),
        timezone: cityFallback.timezone || '—',
        wikipediaUrl: wikiData?.content_urls?.desktop?.page || '',
        tourismUrl: '',
        isWikiResult: true, facts, factsBg: facts,
      };
    }
    const countries = await res.json();
    if (!Array.isArray(countries) || !countries.length) return knownCountryFallback();
    const c = countries[0];

    const wikiRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(c.name.common)}`);
    let wikiData = null;
    if (wikiRes.ok) wikiData = await wikiRes.json();

    const rawImg = wikiData?.thumbnail?.source || '';
    const destPhotos = DEST_PHOTOS[c.name.common] || [];
    const goodImg = isGoodPhoto(rawImg) ? rawImg : (destPhotos[0] || c.flags?.png || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80');
    const allPhotos = destPhotos.length > 0 ? destPhotos : (isGoodPhoto(rawImg) ? [rawImg] : [goodImg]);

    const currencyEntry = Object.values(c.currencies || {})[0];
    const currencyStr = currencyEntry ? `${currencyEntry.name}${currencyEntry.symbol ? ' ' + currencyEntry.symbol : ''}` : '—';
    const currencyBgName = lang === 'bg' ? (CURRENCY_NAMES_BG[currencyEntry?.name] || currencyEntry?.name || '') : '';
    const currencyBgStr = currencyBgName ? `${currencyBgName}${currencyEntry?.symbol ? ' ' + currencyEntry.symbol : ''}` : currencyStr;

    const languageList = Object.values(c.languages || {});
    const languageStr = languageList.join(', ') || '—';
    const languageBgStr = lang === 'bg' ? (languageList.map(l => LANGUAGE_NAMES_BG[l] || l).join(', ') || '—') : languageStr;

    const population = c.population ? c.population.toLocaleString() : '—';
    const extract = wikiData?.extract || '';
    const facts = extract ? extract.split(/(?<=\.)\s+/).slice(0, 4).filter(Boolean) : ['—'];

    return {
      name: c.name.common, nameBg: c.name.common,
      country: [c.region, c.subregion].filter(Boolean).join(' / '),
      countryBg: [c.region, c.subregion].filter(Boolean).join(' / '),
      desc: extract ? extract.slice(0, 120) + '...' : c.name.common,
      descBg: extract ? extract.slice(0, 120) + '...' : c.name.common,
      img: goodImg, photos: allPhotos,
      tag: `🌍 ${c.region || 'Country'}`, tagBg: `🌍 ${c.region || 'Държава'}`,
      visitors: population, bestTime: '—', bestTimeBg: '—',
      temp: c.subregion || c.region || '—',
      weather: '—', currency: currencyStr, currencyBg: currencyBgStr,
      language: languageBgStr, timezone: c.timezones?.[0] || '—',
      wikipediaUrl: wikiData?.content_urls?.desktop?.page || '',
      tourismUrl: c.maps?.googleMaps || '',
      isWikiResult: true, facts, factsBg: facts,
    };
  } catch(e) { return knownCountryFallback(); }
}

function normalizeBulgarianDestinationMetadata(destination, query = '') {
  if (!destination) return destination;
  const text = [
    query,
    destination.name,
    destination.nameEn,
    destination.nameBg,
    destination.country,
    destination.countryBg,
    destination.desc,
    destination.descBg,
    ...(Array.isArray(destination.facts) ? destination.facts : []),
    ...(Array.isArray(destination.factsBg) ? destination.factsBg : []),
  ].filter(Boolean).join(' ').toLowerCase();
  const hasBulgarianSignal = [
    'българия',
    'bulgaria',
    'обл. варна',
    'област варна',
    'община варна',
    'североизточна българия',
    'софия',
    'пловдив',
    'варна',
    'бургас',
    'село в българия',
    'village in bulgaria',
  ].some(signal => text.includes(signal));
  if (!hasBulgarianSignal) return destination;
  return {
    ...destination,
    country: 'Bulgaria',
    countryBg: 'България',
    currency: 'Euro €',
    currencyBg: 'Евро €',
    language: 'Bulgarian',
    languageBg: 'Български',
    timezone: 'UTC+2 / UTC+3 (лято)',
    timezoneBg: 'UTC+2 / UTC+3 (лято)',
  };
}

async function fetchDestinationBackendFallback(query, lang) {
  const raw = (query || '').trim();
  if (!raw || !API_BASE) return null;
  const responseLang = responseLanguageName(lang);
  try {
    const aiRes = await fetch(`${API_BASE}/api/openai/plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        max_tokens: 450,
        messages: [
          { role: 'system', content: 'Return only raw JSON, no markdown, no explanation. If the place is not a real travel destination, return {"found":false}.' },
          { role: 'user', content: `Look up the worldwide travel destination "${raw}". It may be a country, city, town, village, settlement, island, region, or popular place. Respond in ${responseLang}. Return JSON with keys: found (boolean), name, nameEn, nameBg, country, countryBg, description, weather, currency, currencyBg, language, languageBg, timezone, bestSeason, visitors, facts (array of 3-4 short facts).` },
        ],
      }),
    });
    if (!aiRes.ok) return null;
    const aiData = await aiRes.json();
    const aiText = (aiData.choices?.[0]?.message?.content || '{}').replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(aiText);
    if (parsed.found === false) return null;
    const toStr = (v) => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (Array.isArray(v)) return v.map(toStr).filter(Boolean).join(', ');
      if (typeof v === 'object') return Object.values(v).map(toStr).filter(Boolean).join(' ');
      return String(v);
    };
    const name = toStr(parsed.name) || raw;
    const nameEn = toStr(parsed.nameEn) || name;
    const nameBg = toStr(parsed.nameBg) || name;
    const facts = Array.isArray(parsed.facts) ? parsed.facts.map(toStr).filter(Boolean) : [toStr(parsed.facts)].filter(Boolean);
    const desc = toStr(parsed.description) || facts[0] || name;
    const fallbackImg = (DEST_PHOTOS[nameBg] || DEST_PHOTOS[nameEn] || DEST_PHOTOS[name] || [])[0]
      || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80';
    return normalizeBulgarianDestinationMetadata({
      name, nameEn, nameBg,
      country: toStr(parsed.country) || '—',
      countryBg: toStr(parsed.countryBg) || toStr(parsed.country) || '—',
      desc, descBg: desc,
      img: fallbackImg, photos: [fallbackImg],
      tag: '🏙️ Destination', tagBg: '🏙️ Дестинация',
      visitors: toStr(parsed.visitors) || '—',
      bestTime: toStr(parsed.bestSeason) || '—',
      bestTimeBg: toStr(parsed.bestSeason) || '—',
      temp: '—',
      weather: toStr(parsed.weather) || '—',
      currency: toStr(parsed.currency) || '—',
      currencyBg: toStr(parsed.currencyBg) || toStr(parsed.currency) || '—',
      language: lang === 'bg' ? (toStr(parsed.languageBg) || toStr(parsed.language) || '—') : (toStr(parsed.language) || '—'),
      timezone: toStr(parsed.timezone) || '—',
      wikipediaUrl: '',
      tourismUrl: '',
      isWikiResult: true,
      facts: facts.length ? facts : [desc],
      factsBg: facts.length ? facts : [desc],
    }, raw);
  } catch (_) {
    return null;
  }
}

function findLocalDestination(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return null;
  return TRENDING_DESTINATIONS.find(d => [
    d.name, d.nameBg, d.country, d.countryBg,
  ].filter(Boolean).some(v => String(v).toLowerCase() === q)) || null;
}

async function fetchDestinationProxy(query, lang) {
  const raw = (query || '').trim();
  if (!raw || !API_BASE) return null;
  try {
    const res = await fetch(`${API_BASE}/api/destination/lookup?q=${encodeURIComponent(raw)}&lang=${encodeURIComponent(lang || 'en')}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data?.name || data?.nameBg ? normalizeBulgarianDestinationMetadata(data, raw) : null;
  } catch (_) {
    return null;
  }
}

async function fetchCreateDestination(query, lang) {
  const local = findLocalDestination(query);
  if (local) return local;
  const proxy = await fetchDestinationProxy(query, lang);
  if (proxy) return proxy;
  const countryOrWeb = await fetchRestCountry(query, lang);
  if (countryOrWeb) return normalizeBulgarianDestinationMetadata(countryOrWeb, query);
  const backendFallback = await fetchDestinationBackendFallback(query, lang);
  return normalizeBulgarianDestinationMetadata(backendFallback, query);
}

// ── Trending Destinations Row ─────────────────────────────────────────────────
function TrendingDestinations({ t, lang, onPlanTrip, onDestClick }) {
  const ex = exploreCopy(lang);
  const getTag = (d) => {
    if (lang === 'bg' && d.tagBg) return d.tagBg;
    const raw = d[`tag${lang.charAt(0).toUpperCase()}${lang.slice(1)}`] || d.tag || '';
    const match = String(raw).match(/^(\S+)\s+(.+)$/);
    if (!match) return raw;
    return `${match[1]} ${ex.tags?.[match[2]] || match[2]}`;
  };
  const getName = (d) => localizeDestinationName(d, lang);
  const getCountry = (d) => lang === 'bg' ? (d.countryBg || localizeCountry(d.country, lang)) : localizeCountry(d.country, lang);
  const getBestTime = (d) => lang === 'bg' ? (d.bestTimeBg||localizeMonthRange(d.bestTime, lang)) : localizeMonthRange(d.bestTime, lang);
  const [shown, setShown] = useState(() => {
    const shuffled = [...TRENDING_DESTINATIONS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 4);
  });
  const [rotating, setRotating] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);

  async function handleSearch() {
    if (!searchQ.trim()) return;
    setSearching(true);
    setSearchError(false);
    const result = await fetchCreateDestination(searchQ.trim(), lang);
    setSearching(false);
    if (result) {
      setSearchError(false);
      setShown(prev => [result, ...prev.slice(0, 3)]);
      onDestClick && onDestClick(result);
    } else {
      setSearchError(true);
    }
  }

  function refresh() {
    setRotating(true);
    setTimeout(() => {
      const shuffled = [...TRENDING_DESTINATIONS].sort(() => Math.random() - 0.5);
      setShown(shuffled.slice(0, 4));
      setRotating(false);
    }, 300);
  }

  useEffect(() => {
    const interval = setInterval(refresh, 45000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{marginBottom: 24}}>
      <div style={{display:'flex', gap:8, marginBottom:16}}>
        <input
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder={ex.searchPlaceholder}
          style={{flex:1, background:'#161b22', border:'1px solid #30363d', borderRadius:12,
            color:'#f0f6fc', padding:'10px 14px', fontSize:13, outline:'none'}}
        />
        <button onClick={handleSearch} disabled={searching} style={{
          background:'linear-gradient(90deg,#c9a84c,#e8b923)', border:'none',
          borderRadius:12, padding:'10px 16px', cursor:'pointer',
          fontWeight:700, fontSize:14, color:'#000', minWidth:44,
        }}>
          {searching ? '⏳' : '🔍'}
        </button>
      </div>
      {searchError && (
        <div style={{color:'#f85149', fontSize:12, marginBottom:8, textAlign:'center'}}>
          {ex.searchNotFound}
        </div>
      )}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <span style={{fontSize:18}}>🌍</span>
          <span style={{color:'var(--gold)', fontWeight:700, fontSize:13, letterSpacing:1}}>
            {t.explore.aiPicks}
          </span>
        </div>
        <button onClick={refresh} style={{
          background:'none', border:'1px solid #30363d', borderRadius:20,
          color:'var(--gold)', fontSize:11, padding:'4px 10px', cursor:'pointer',
          transition:'all .2s', opacity: rotating ? 0.5 : 1,
        }}>🔄 {t.explore.refresh}</button>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10,
        opacity: rotating ? 0 : 1, transition:'opacity .3s'}}>
        {shown.map((d, i) => (
          <div key={i} onClick={() => onDestClick && onDestClick(d)}
            style={{borderRadius:16, overflow:'hidden', position:'relative',
              cursor:'pointer', border: d.isWikiResult ? '2px solid #c9a84c' : '1px solid #30363d',
              transition:'transform .2s', boxShadow:'0 4px 16px rgba(0,0,0,0.3)'}}
            onMouseEnter={e => e.currentTarget.style.transform='translateY(-4px)'}
            onMouseLeave={e => e.currentTarget.style.transform=''}>
            <img src={d.img} alt={getName(d)} style={{width:'100%', height:110, objectFit:'cover', display:'block'}} />
            <div style={{position:'absolute', top:8, left:8, background:'rgba(0,0,0,0.7)',
              borderRadius:20, padding:'2px 8px', fontSize:10, color:'#fff'}}>{getTag(d)}</div>
            <div style={{padding:'10px 12px', background:'#0d1117'}}>
              <div style={{color:'#f0f6fc', fontWeight:700, fontSize:14}}>{getName(d)}</div>
              <div style={{color:'var(--muted)', fontSize:11, marginBottom:6}}>{getCountry(d)} • {d.temp}</div>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                {d.visitors !== '—' && <span style={{color:'var(--gold)', fontSize:10}}>👥 {localizeVisitors(d.visitors, lang)}</span>}
                {d.bestTime !== '—' && <span style={{color:'#7b9e87', fontSize:10}}>📅 {getBestTime(d)}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Trip Card (with onJoin callback) ──────────────────────────────────────────
function TripCard({ trip, t, lang, plan, onJoin, onNeedPremium, onSelect, onMatchCreator }) {
  const [joined, setJoined] = useState(false);
  const ft = FT[lang] || FT.en;
  const tripName = localizeKnownTripContent(trip.name, lang);
  const tripLocation = localizeKnownTripContent(trip.location, lang);
  function handleJoin() {
    const next = !joined;
    setJoined(next);
    if (next && onJoin) onJoin(trip);
  }
  return (
    <div className="ts-trip-card">
      <div className="ts-trip-img-wrap" style={onSelect?{cursor:'pointer'}:{}} onClick={onSelect?()=>onSelect(trip):undefined}>
        <img src={trip.image} alt={tripName} className="ts-trip-img" />
        <div className="ts-trip-gradient" style={{background:trip.gradient}} />
        {trip.hot && <span className="ts-trip-hot">{t.explore.hot} 🔥</span>}
        <div className="ts-trip-overlay-text">
          <h3 className="ts-trip-name ts-serif">{tripName}</h3>
          <p className="ts-trip-loc">📍 {tripLocation}</p>
        </div>
      </div>
      <div className="ts-trip-body">
        {trip.start_date && <p style={{fontSize:'0.75rem',color:'var(--gold)',marginBottom:'0.35rem'}}>📅 {new Date(trip.start_date).toLocaleDateString(LOCALE_MAP[lang]||'en-GB',{day:'numeric',month:'short',year:'numeric'})}</p>}
        <div className="ts-trip-meta">
          <span className="ts-trip-days">⏱ {trip.days} {ft.days||t.explore.days}</span>
          <span className="ts-trip-spots">{trip.spots} {ft.spotsLeft||t.explore.spots}</span>
          <span className="ts-trip-price">{trip.price}</span>
        </div>
        <div style={{display:'flex',gap:'0.4rem'}}>
          <button className={`ts-join-btn ${joined?'joined':''}`} style={{flex:1}} onClick={handleJoin}>
            {joined ? (t.explore.joined || '✓ Joined') : `✈ ${t.explore.join}`}
          </button>
          <button className="ts-hotel-btn" onClick={()=>onMatchCreator&&trip.user_id?onMatchCreator(trip.user_id):null}>{ft.hotelMatch}</button>
        </div>
      </div>
    </div>
  );
}

// ── Mood Travel ───────────────────────────────────────────────────────────────
const MOOD_DEFS = [
  { key:'happy',       emoji:'😊', labelBg:'Щастлив',        labelEn:'Happy',        dests:['Малдиви','Бали','Гърция'],           img:'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80' },
  { key:'sad',         emoji:'😔', labelBg:'Тъжен',          labelEn:'Sad',          dests:['Норвегия','Исландия','Шотландия'],    img:'https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=800&q=80' },
  { key:'stressed',    emoji:'😤', labelBg:'Стресиран',      labelEn:'Stressed',     dests:['Коста Рика','Нова Зеландия'],        img:'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80' },
  { key:'excited',     emoji:'🤩', labelBg:'Развълнуван',    labelEn:'Excited',      dests:['Патагония','Непал','Танзания'],       img:'https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=800&q=80' },
  { key:'tired',       emoji:'😴', labelBg:'Уморен',         labelEn:'Tired',        dests:['Малдиви','Санторини','Дубай'],        img:'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80' },
  { key:'romantic',    emoji:'🥰', labelBg:'Романтичен',     labelEn:'Romantic',     dests:['Париж','Венеция','Прага'],            img:'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80' },
  { key:'curious',     emoji:'🤔', labelBg:'Любопитен',      labelEn:'Curious',      dests:['Япония','Мароко','Перу'],             img:'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80' },
  { key:'adventurous', emoji:'😎', labelBg:'Авантюристичен', labelEn:'Adventurous',  dests:['Аляска','Амазония','Сахара'],        img:'https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=800&q=80' },
];

function MoodTravelModal({ lang, onClose }) {
  const ex = exploreCopy(lang);
  const savedRec = (() => { try { return JSON.parse(localStorage.getItem('ts_mood_travel')||'null'); } catch { return null; } })();

  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(savedRec);
  const [error,   setError]   = useState('');

  async function selectMood(m) {
    setResult(null); setError(''); setLoading(true);
    const destHint = m.dests.join(', ');
    const moodName = ex.moods?.[m.key] || m.labelEn;
    const responseLang = responseLanguageName(lang);
    const prompt = lang === 'bg'
      ? `Чувствам се ${m.labelBg.toLowerCase()} днес. Препоръчай ми точно ЕДНА идеална дестинация от: ${destHint} (или подобна). Отговори САМО в този формат:\nДЕСТИНАЦИЯ: [само името]\nОПИСАНИЕ: [3-4 изречения защо е идеална за моето настроение — топло, лично, поетично]`
      : `I'm feeling ${moodName.toLowerCase()} today. Recommend exactly ONE destination from: ${destHint} (or similar). Reply in ${responseLang} ONLY in this format:\nDESTINATION: [name only]\nDESCRIPTION: [3-4 sentences why it is perfect for my mood — warm, personal, poetic]`;
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model:'gpt-4o-mini', messages:[{ role:'user', content:prompt }], max_tokens:260 }),
      });
      const data = await res.json();
      const text = data.choices?.[0]?.message?.content || '';
      const dKey  = lang==='bg'?'ДЕСТИНАЦИЯ':lang==='es'?'DESTINO':lang==='fr'?'DESTINATION':lang==='de'?'REISEZIEL':lang==='el'?'ΠΡΟΟΡΙΣΜΟΣ':lang==='it'?'DESTINAZIONE':'DESTINATION';
      const descK = lang==='bg'?'ОПИСАНИЕ':lang==='es'?'DESCRIPCIÓN':lang==='fr'?'DESCRIPTION':lang==='de'?'BESCHREIBUNG':lang==='el'?'ΠΕΡΙΓΡΑΦΗ':lang==='it'?'DESCRIZIONE':'DESCRIPTION';
      const destM = text.match(new RegExp(`${dKey}:\\s*(.+)`));
      const descM = text.match(new RegExp(`${descK}:\\s*([\\s\\S]+)`));
      const destination = (destM ? destM[1] : m.dests[0]).trim();
      const description = (descM ? descM[1] : text).trim();
      const imgUrl = (DEST_PHOTOS[destination] || DEST_PHOTOS[destination.split(',')[0]?.trim()] || [])[0] || m.img;
      const rec = { mood:m.key, moodEmoji:m.emoji, moodLabel:moodName, destination, description, imgUrl };
      setResult(rec);
      localStorage.setItem('ts_mood_travel', JSON.stringify({ ...rec, savedAt: new Date().toISOString() }));
    } catch {
      setError(ex.generationError);
    }
    setLoading(false);
  }

  const moodLabel = m => ex.moods?.[m.key] || m.labelEn;

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth:400, maxHeight:'90vh', overflowY:'auto', textAlign:'left'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center'}}>
          🎭 {ex.moodTitle}
        </h2>
        <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.85rem',marginBottom:'1.25rem'}}>
          {ex.moodSubtitle}
        </p>

        {!loading && !result && (
          <div className="ts-mood-grid">
            {MOOD_DEFS.map(m => (
              <button key={m.key} className="ts-mood-btn" onClick={()=>selectMood(m)}>
                <span style={{fontSize:'1.8rem',display:'block',marginBottom:4}}>{m.emoji}</span>
                <span style={{fontSize:'0.76rem',color:'var(--text)',lineHeight:1.2}}>{moodLabel(m)}</span>
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div style={{textAlign:'center',padding:'2rem 0'}}>
            <div className="ts-soul-match-spinner" style={{width:36,height:36,borderWidth:4}}/>
            <p style={{color:'var(--muted)',fontSize:'0.88rem',marginTop:14}}>
              {ex.moodLoading}
            </p>
          </div>
        )}

        {!loading && result && (
          <div>
            <div style={{position:'relative',borderRadius:14,overflow:'hidden',marginBottom:'1rem',height:210}}>
              <img src={result.imgUrl} alt={result.destination} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} onError={e=>{e.target.src='https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80';}} />
              <div style={{position:'absolute',inset:0,background:'linear-gradient(to top,rgba(0,0,0,0.82) 0%,transparent 55%)',display:'flex',flexDirection:'column',justifyContent:'flex-end',padding:'1rem'}}>
                <p style={{margin:0,fontSize:'1.2rem',fontWeight:700,color:'#fff',fontFamily:"'Playfair Display',serif"}}>{result.destination}</p>
                <p style={{margin:'3px 0 0',fontSize:'0.75rem',color:'rgba(255,255,255,0.8)'}}>{result.moodEmoji} {ex.moods?.[result.mood] || result.moodLabel}</p>
              </div>
            </div>
            <p style={{fontSize:'0.88rem',color:'var(--text)',lineHeight:1.75,marginBottom:'1rem'}}>{result.description}</p>
            {error && <p style={{color:'#f85149',fontSize:'0.8rem',marginBottom:'0.5rem'}}>{error}</p>}
            <button className="ts-btn-ghost-sm" style={{width:'100%'}} onClick={()=>setResult(null)}>
              ← {ex.changeMood}
            </button>
          </div>
        )}

        {error && !result && !loading && <p style={{color:'#f85149',fontSize:'0.8rem',textAlign:'center',marginTop:'0.5rem'}}>{error}</p>}
      </div>
    </div>
  );
}

// ── Travel Horoscope ─────────────────────────────────────────────────────────
const ZODIAC_SIGNS = [
  { key:'aries',       emoji:'♈', nameBg:'Овен',      nameEn:'Aries',       dates:'21.03–19.04' },
  { key:'taurus',      emoji:'♉', nameBg:'Телец',     nameEn:'Taurus',      dates:'20.04–20.05' },
  { key:'gemini',      emoji:'♊', nameBg:'Близнаци',  nameEn:'Gemini',      dates:'21.05–20.06' },
  { key:'cancer',      emoji:'♋', nameBg:'Рак',       nameEn:'Cancer',      dates:'21.06–22.07' },
  { key:'leo',         emoji:'♌', nameBg:'Лъв',       nameEn:'Leo',         dates:'23.07–22.08' },
  { key:'virgo',       emoji:'♍', nameBg:'Дева',      nameEn:'Virgo',       dates:'23.08–22.09' },
  { key:'libra',       emoji:'♎', nameBg:'Везни',     nameEn:'Libra',       dates:'23.09–22.10' },
  { key:'scorpio',     emoji:'♏', nameBg:'Скорпион',  nameEn:'Scorpio',     dates:'23.10–21.11' },
  { key:'sagittarius', emoji:'♐', nameBg:'Стрелец',   nameEn:'Sagittarius', dates:'22.11–21.12' },
  { key:'capricorn',   emoji:'♑', nameBg:'Козирог',   nameEn:'Capricorn',   dates:'22.12–19.01' },
  { key:'aquarius',    emoji:'♒', nameBg:'Водолей',   nameEn:'Aquarius',    dates:'20.01–18.02' },
  { key:'pisces',      emoji:'♓', nameBg:'Риби',      nameEn:'Pisces',      dates:'19.02–20.03' },
];

function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

function HoroscopeModal({ lang, plan, onClose, onNeedPremium }) {
  const isBg = lang === 'bg';
  const ex = exploreCopy(lang);
  const isUnlocked = plan === 'premium' || plan === 'elite';

  const [sign, setSign] = useState(() => {
    try { return localStorage.getItem('ts_horoscope_sign') || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  function getWeekKey() {
    const now = new Date();
    return `${now.getFullYear()}-W${getISOWeek(now)}`;
  }

  function getSignData(key) {
    return ZODIAC_SIGNS.find(s => s.key === key) || null;
  }

  function parseHoroscope(text) {
    const get = (key) => { const m = text.match(new RegExp(`${key}:\\s*(.+)`)); return m ? m[1].trim() : ''; };
    if (isBg) return { destination: get('ДЕСТИНАЦИЯ'), advice: get('СЪВЕТ'), compatible: get('СЪВМЕСТИМИ ЗНАЦИ'), vibe: get('ВИБРАЦИЯ') };
    return { destination: get('DESTINATION'), advice: get('ADVICE'), compatible: get('COMPATIBLE SIGNS'), vibe: get('VIBE') };
  }

  async function loadHoroscope(signKey) {
    const s = getSignData(signKey);
    if (!s) return;
    setLoading(true); setError('');
    const now = new Date();
    const weekNum = getISOWeek(now);
    const year = now.getFullYear();
    const signName = ex.zodiac?.[s.key] || s.nameEn;
    const langName = responseLanguageName(lang);
    const prompt = isBg
      ? `Ти си мистичен пътнически астролог. Тази седмица е ${weekNum}-та на ${year}. За знак ${s.emoji} ${signName} напиши кратък седмичен пътнически хороскоп. Отговори ТОЧНО в този формат:\nДЕСТИНАЦИЯ: [само едно място]\nСЪВЕТ: [2-3 изречения]\nСЪВМЕСТИМИ ЗНАЦИ: [2-3 знака]\nВИБРАЦИЯ: [1-3 думи]`
      : `You are a mystical travel astrologer. This is week ${weekNum} of ${year}. For zodiac sign ${s.emoji} ${signName} write a short weekly travel horoscope in ${langName}. Respond EXACTLY:\nDESTINATION: [one place only]\nADVICE: [2-3 sentences]\nCOMPATIBLE SIGNS: [2-3 signs]\nVIBE: [1-3 words]`;
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role: 'user', content: prompt }], max_tokens: 200 }),
      });
      const data = await res.json();
      const text = data.choices?.[0]?.message?.content?.trim() || '';
      const parsed = parseHoroscope(text);
      const rec = { ...parsed, sign: signKey, weekKey: getWeekKey() };
      setResult(rec);
      try { localStorage.setItem('ts_horoscope', JSON.stringify(rec)); } catch {}
    } catch {
      setError(ex.horoscopeError);
    }
    setLoading(false);
  }

  function pickSign(key) {
    localStorage.setItem('ts_horoscope_sign', key);
    setSign(key);
    if (!isUnlocked) return;
    try {
      const cached = JSON.parse(localStorage.getItem('ts_horoscope') || 'null');
      if (cached && cached.sign === key && cached.weekKey === getWeekKey()) { setResult(cached); return; }
    } catch {}
    loadHoroscope(key);
  }

  // On mount: if sign already saved, load/check cache
  useEffect(() => {
    if (!sign || !isUnlocked) return;
    try {
      const cached = JSON.parse(localStorage.getItem('ts_horoscope') || 'null');
      if (cached && cached.sign === sign && cached.weekKey === getWeekKey()) { setResult(cached); return; }
    } catch {}
    loadHoroscope(sign);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signData = sign ? getSignData(sign) : null;
  const zodiacName = (s) => ex.zodiac?.[s?.key] || s?.nameEn;
  const nowWeek = getISOWeek(new Date());
  const nowYear = new Date().getFullYear();

  return (
    <div style={{...OVERLAY_S, zIndex:10200}} onClick={onClose}>
      <div style={{...MODAL_S, maxWidth:400, textAlign:'left', padding:'24px 20px 20px'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center',color:'var(--gold)',marginBottom:'0.2rem'}}>
          🔮 {ex.horoscopeTitle}
        </h2>
        <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.75rem',marginBottom:'1rem'}}>
          {ex.week} {nowWeek}, {nowYear}
        </p>

        {!sign ? (
          <>
            <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.83rem',marginBottom:'0.85rem'}}>
              {ex.chooseSign}
            </p>
            <div className="ts-zodiac-grid">
              {ZODIAC_SIGNS.map(s => (
                <button key={s.key} className="ts-zodiac-btn" onClick={()=>pickSign(s.key)}>
                  <span className="ts-zodiac-emoji">{s.emoji}</span>
                  <span className="ts-zodiac-name">{zodiacName(s)}</span>
                  <span className="ts-zodiac-dates">{s.dates}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:'1rem'}}>
              <span style={{fontSize:'2rem'}}>{signData?.emoji}</span>
              <div style={{flex:1}}>
                <p style={{color:'var(--gold)',fontWeight:700,fontSize:'1rem',margin:0}}>{zodiacName(signData)}</p>
                <p style={{color:'var(--muted)',fontSize:'0.72rem',margin:0}}>{signData?.dates}</p>
              </div>
              <button onClick={()=>{setSign(null);setResult(null);setError('');}}
                style={{background:'none',border:'1px solid var(--border)',color:'var(--muted)',cursor:'pointer',fontSize:'0.72rem',padding:'4px 8px',borderRadius:8}}>
                ↩ {ex.change}
              </button>
            </div>

            {/* Free — blurred preview */}
            {!isUnlocked && (
              <div style={{position:'relative',marginTop:'0.25rem'}}>
                <div style={{filter:'blur(5px)',pointerEvents:'none',userSelect:'none',borderRadius:14,overflow:'hidden'}}>
                  <div className="ts-horoscope-card">
                    <div className="ts-horoscope-dest">✈️ {ex.destinationThisWeek}<br/><strong style={{color:'var(--gold)',fontSize:'1.05rem'}}>Малдиви</strong></div>
                    <p style={{fontSize:'0.87rem',color:'var(--text)',lineHeight:1.65,margin:'0.75rem 0'}}>Звездите те насочват към мистични брегове тази седмица. Твоята пътническа енергия е на върха — следвай интуицията си.</p>
                    <div className="ts-horoscope-tags"><span className="ts-horoscope-tag">♌ Лъв</span><span className="ts-horoscope-tag">♐ Стрелец</span></div>
                    <div style={{marginTop:'0.5rem',textAlign:'center'}}><span className="ts-horoscope-vibe">Вибрация: Мистика & Приключение</span></div>
                  </div>
                </div>
                <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(13,17,23,0.78)',borderRadius:14}}>
                  <span style={{fontSize:'1.8rem',marginBottom:'0.35rem'}}>🔮</span>
                  <p style={{color:'var(--gold)',fontWeight:700,fontSize:'0.9rem',margin:'0 0 0.2rem'}}>{ex.premiumFeature}</p>
                  <p style={{color:'var(--muted)',fontSize:'0.78rem',margin:'0 0 0.7rem',textAlign:'center',padding:'0 1rem'}}>{ex.unlockHoroscope}</p>
                  <button className="ts-btn-gold-full" style={{padding:'0.45rem 1.4rem',fontSize:'0.83rem'}} onClick={()=>{onClose();onNeedPremium&&onNeedPremium('horoscope');}}>
                    ✨ {ex.upgradeNow}
                  </button>
                </div>
              </div>
            )}

            {/* Premium/Elite — full horoscope */}
            {isUnlocked && (
              loading ? (
                <div style={{textAlign:'center',padding:'2.5rem 0',color:'var(--muted)'}}>
                  <div style={{width:32,height:32,margin:'0 auto 0.75rem',borderRadius:'50%',border:'3px solid rgba(232,200,112,0.2)',borderTopColor:'var(--gold)',animation:'ts-spin 0.9s linear infinite'}}/>
                  <p style={{fontSize:'0.85rem'}}>{ex.readingStars}</p>
                </div>
              ) : error ? (
                <div style={{textAlign:'center',padding:'1.5rem 0'}}>
                  <p style={{color:'#f87171',fontSize:'0.85rem',marginBottom:'0.75rem'}}>{error}</p>
                  <button className="ts-btn-ghost-sm" onClick={()=>loadHoroscope(sign)}>{ex.tryAgain}</button>
                </div>
              ) : result ? (
                <div className="ts-horoscope-card">
                  {result.destination && (
                    <div className="ts-horoscope-dest">
                      ✈️ {ex.destinationThisWeek}<br/>
                      <strong style={{color:'var(--gold)',fontSize:'1.05rem'}}>{result.destination}</strong>
                    </div>
                  )}
                  {result.advice && <p style={{fontSize:'0.87rem',color:'var(--text)',lineHeight:1.65,margin:'0.75rem 0'}}>{result.advice}</p>}
                  {result.compatible && (
                    <div style={{marginBottom:'0.5rem'}}>
                      <span style={{fontSize:'0.74rem',color:'var(--muted)'}}>{ex.compatibleSigns}</span>
                      <div className="ts-horoscope-tags" style={{marginTop:'0.3rem'}}>
                        {result.compatible.split(',').map((s,i) => <span key={i} className="ts-horoscope-tag">{s.trim()}</span>)}
                      </div>
                    </div>
                  )}
                  {result.vibe && <div style={{textAlign:'center',marginTop:'0.4rem'}}><span className="ts-horoscope-vibe">{ex.vibe}{result.vibe}</span></div>}
                  <p style={{textAlign:'center',fontSize:'0.68rem',color:'var(--muted)',marginTop:'0.75rem',opacity:0.55}}>{ex.updatesWeekly}</p>
                </div>
              ) : null
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Multi-Destination Trip Planner Modal ──────────────────────────────────────
function MultiTripPlannerModal({ lang, plan, onClose, onNeedPremium, visible }) {
  const ex = exploreCopy(lang);
  const MT = {
    bg: { title:'🌍 Мулти Трип Планиране', addDest:'+ Добави дестинация', findTickets:'Намери билети', saveTrip:'Запази пътешествието', aiSuggest:'🤖 AI препоръча маршрут', totalDays:'Общо дни', dests:'Дестинации', from:'От', to:'До', nights:'нощи', city:'Град / дестинация', transport:'Транспорт', timeline:'Маршрут', savedMsg:'✅ Запазено!', savingMsg:'Запазване...', datePh:'ДД/ММ/ГГГГ' },
    en: { title:'🌍 Create Journey', addDest:'+ Add destination', findTickets:'Find tickets', saveTrip:'Save trip', aiSuggest:'🤖 AI suggest route', totalDays:'Total days', dests:'Destinations', from:'From', to:'To', nights:'nights', city:'City / destination', transport:'Transport', timeline:'Route', savedMsg:'✅ Saved!', savingMsg:'Saving...', datePh:'DD/MM/YYYY' },
    es: { title:'🌍 Crear Viaje', addDest:'+ Agregar destino', findTickets:'Buscar billetes', saveTrip:'Guardar viaje', aiSuggest:'🤖 IA sugerir ruta', totalDays:'Días totales', dests:'Destinos', from:'Desde', to:'Hasta', nights:'noches', city:'Ciudad / destino', transport:'Transporte', timeline:'Ruta', savedMsg:'✅ Guardado!', savingMsg:'Guardando...', datePh:'DD/MM/AAAA' },
    fr: { title:'🌍 Créer Voyage', addDest:'+ Ajouter destination', findTickets:'Trouver billets', saveTrip:'Sauvegarder', aiSuggest:'🤖 IA suggérer itinéraire', totalDays:'Jours totaux', dests:'Destinations', from:'De', to:'À', nights:'nuits', city:'Ville / destination', transport:'Transport', timeline:'Itinéraire', savedMsg:'✅ Sauvegardé!', savingMsg:'Sauvegarde...', datePh:'JJ/MM/AAAA' },
    de: { title:'🌍 Reise erstellen', addDest:'+ Ziel hinzufügen', findTickets:'Tickets finden', saveTrip:'Reise speichern', aiSuggest:'🤖 KI Route vorschlagen', totalDays:'Gesamttage', dests:'Ziele', from:'Von', to:'Bis', nights:'Nächte', city:'Stadt / Ziel', transport:'Transport', timeline:'Route', savedMsg:'✅ Gespeichert!', savingMsg:'Speichern...', datePh:'TT/MM/JJJJ' },
  };
  const mt = MT[lang] || MT.en;
  const routeLabels = {
    bg: { mustDo: '⭐ Задължително:', aiRoute: '🧭 AI Маршрут' },
    en: { mustDo: '⭐ Must-do:', aiRoute: '🧭 AI Route' },
    es: { mustDo: '⭐ Imprescindible:', aiRoute: '🧭 Ruta IA' },
    fr: { mustDo: '⭐ Incontournable:', aiRoute: '🧭 Itinéraire IA' },
    de: { mustDo: '⭐ Unbedingt:', aiRoute: '🧭 KI-Route' },
  };
  function translateDuration(duration, l) {
    if (!duration) return duration;
    return duration
      .replace(/flight/gi, l==='bg'?'полет':l==='es'?'vuelo':l==='fr'?'vol':l==='de'?'Flug':'flight')
      .replace(/min/gi, l==='bg'?'мин':l==='de'?'Min':'min')
      .replace(/hour/gi, l==='bg'?'час':l==='es'?'hora':l==='fr'?'heure':l==='de'?'Std':'hour');
  }
  const isElite = plan === 'elite';
  const maxDests = isElite ? 10 : 3;

  const [destinations, setDestinations] = useState([
    { id: 1, city: '', from: '', to: '', transport: 'plane' },
    { id: 2, city: '', from: '', to: '', transport: 'plane' },
  ]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lookupStatus, setLookupStatus] = useState({});

  const TRANSPORTS = [
    { key:'plane', icon:'✈️', label:lang==='bg'?'Самолет':lang==='de'?'Flugzeug':lang==='el'?'Αεροπλάνο':lang==='it'?'Aereo':lang==='fr'?'Avion':lang==='es'?'Avión':'Plane' },
    { key:'train', icon:'🚂', label:lang==='bg'?'Влак':lang==='de'?'Zug':lang==='el'?'Τρένο':lang==='it'?'Treno':lang==='fr'?'Train':lang==='es'?'Tren':'Train' },
    { key:'bus',   icon:'🚌', label:lang==='bg'?'Автобус':lang==='de'?'Bus':lang==='el'?'Λεωφορείο':lang==='it'?'Autobus':lang==='fr'?'Bus':lang==='es'?'Autobús':'Bus' },
    { key:'car',   icon:'🚗', label:lang==='bg'?'Кола':lang==='de'?'Auto':lang==='el'?'Αυτοκίνητο':lang==='it'?'Auto':lang==='fr'?'Voiture':lang==='es'?'Coche':'Car' },
  ];


  function buildUrl(from, to, date, transport) {
    const d=(date||new Date().toISOString().slice(0,10)).replace(/-/g,'');
    if (transport==='train') return `https://www.thetrainline.com/search#outward-${encodeURIComponent(from)}-${encodeURIComponent(to)}`;
    if (transport==='bus')   return `https://www.flixbus.com/bus-routes`;
    if (transport==='car')   return `https://www.rentalcars.com/en/?from=${encodeURIComponent(from)}`;
    return `https://www.skyscanner.net/transport/flights/${encodeURIComponent(from)}/${encodeURIComponent(to)}/${d}/`;
  }

  function buildMultiCityUrl(dests) {
    const filled = dests.filter(d => d.city && d.city.trim());
    if (filled.length < 2) return null;
    const from = encodeURIComponent(filled[0].city);
    const to = encodeURIComponent(filled[filled.length - 1].city);
    const date = filled[0].from ? filled[0].from.replace(/-/g, '') : '';
    if (date) {
      return `https://www.skyscanner.net/transport/flights/${from}/${to}/${date}/?adults=1&currency=EUR`;
    }
    return `https://www.skyscanner.net/transport/flights/${from}/${to}/?adults=1&currency=EUR`;
  }

  function calcNights(from, to) {
    if (!from || !to) return 0;
    return Math.max(0, Math.round((new Date(to) - new Date(from)) / 86400000));
  }

  const totalDays = destinations.reduce((s, d) => s + calcNights(d.from, d.to), 0);

  function addDest() {
    if (destinations.length >= maxDests) return;
    setDestinations(prev => [...prev, { id: Date.now(), city: '', from: '', to: '', transport: 'plane' }]);
  }

  function updateDest(id, field, value) {
    setDestinations(prev => prev.map(d => d.id === id ? {...d, [field]: value, ...(field === 'city' ? { destinationInfo: null } : {})} : d));
    if (field === 'city') setLookupStatus(prev => ({ ...prev, [id]: null }));
  }

  function moveDest(idx, dir) {
    const arr = [...destinations];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setDestinations(arr);
  }

  const destinationDisplayName = (info, fallback) => (
    lang === 'bg'
      ? (info?.nameBg || info?.name || fallback)
      : (info?.nameEn || info?.name || info?.nameBg || fallback)
  );

  async function resolveDestinationRow(row, { updateState = true } = {}) {
    const city = (row?.city || '').trim();
    if (!city) return { ...row, destinationInfo: null };
    if (row.destinationInfo) return row;
    if (updateState) setLookupStatus(prev => ({ ...prev, [row.id]: { state: 'loading', query: city } }));
    const info = await fetchCreateDestination(city, lang);
    if (!info) {
      if (updateState) setLookupStatus(prev => ({ ...prev, [row.id]: { state: 'error', query: city } }));
      return row;
    }
    const resolved = { ...row, city: destinationDisplayName(info, city), destinationInfo: info };
    if (updateState) {
      setDestinations(prev => prev.map(d => d.id === row.id ? resolved : d));
      setLookupStatus(prev => ({ ...prev, [row.id]: { state: 'ok', query: city } }));
    }
    return resolved;
  }

  async function resolveFilledDestinationRows(rows) {
    const resolved = [];
    for (const row of rows) {
      resolved.push(await resolveDestinationRow(row));
    }
    return resolved;
  }

  async function aiSuggest() {
    const filled = await resolveFilledDestinationRows(destinations.filter(d => d.city));
    if (filled.length < 2) return;
    if (plan === 'free') { onNeedPremium && onNeedPremium('aiPlanner'); return; }
    setAiLoading(true); setAiResult(null);
    const orderedDests = filled.map((d,i) => `${i+1}. ${d.city}`).join(' → ');
    const responseLang = responseLanguageName(lang);
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method:'POST', headers:{ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model:'gpt-4o-mini', max_tokens:1500, messages:[
          { role:'system', content:'You are a travel expert. Respond in JSON only, no markdown, no backticks.' },
          { role:'user', content:`You are a travel expert. The user plans a multi-destination trip in this EXACT order: ${orderedDests}. Total ${totalDays} days. Do NOT reorder the destinations. Respect this exact sequence. Respond in ${responseLang}. Translate ALL text including transport durations, season names, and activity descriptions to ${responseLang}. Never use English words unless the destination name is in English. Return ONLY valid JSON: {"summary":"2-3 sentence overview","order":["city1","city2"],"legs":[{"from":"city1","to":"city2","duration":"~3h flight","tip":"detailed practical tip with best options","highlight":"what makes this segment special"}],"bestSeason":"best time of year for this combined route","totalBudgetEstimate":"€X-Y per person estimate","mustDo":["top activity in city1","top activity in city2"]}` },
        ]}),
      });
      const data = await res.json();
      const raw = data.choices?.[0]?.message?.content;
      if (raw) {
        const cleaned = raw.replace(/```json|```/g,'').trim();
        setAiResult(JSON.parse(cleaned));
      }
    } catch { setAiResult({ error: true }); }
    setAiLoading(false);
  }

  async function saveTrip() {
    const filled = await resolveFilledDestinationRows(destinations.filter(d => d.city));
    if (!filled.length) return;
    setSaving(true);
    const { userId } = getAuthToken();
    if (supabase && userId) {
      await supabase.from('trips').insert({
        user_id: userId,
        title: '🌍 ' + filled.map(d => d.city).join(' → '),
        destination: filled.map(d => d.city).join(', '),
        start_date: filled[0]?.from || null,
        end_date: filled[filled.length-1]?.to || null,
        days: totalDays,
        group_size: 1,
        plan: { multiTrip: true, destinations: filled, aiResult },
        spots: 3,
        status: 'upcoming',
      });

    }
    setSaved(true); setSaving(false);
    setTimeout(onClose, 1200);
  }

  const filledCities = destinations.filter(d => d.city);

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.88)',zIndex:10000,display:visible?'flex':'none',alignItems:'flex-start',justifyContent:'center',overflowY:'auto',padding:'16px 0'}}>
      <div style={{background:'linear-gradient(135deg,#0d1117,#1a1a2e)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:20,width:'min(640px,96vw)',padding:'24px 18px',position:'relative',margin:'auto'}} onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} style={{position:'absolute',top:14,right:16,background:'none',border:'none',color:'#8b949e',fontSize:22,cursor:'pointer',lineHeight:1}}>✕</button>
        <h2 style={{color:'#f59e0b',fontFamily:"'Playfair Display',serif",fontSize:'1.3rem',margin:'0 0 4px'}}>{mt.title}</h2>
        <p style={{color:'#8b949e',fontSize:'0.78rem',margin:'0 0 18px'}}>{mt.dests}: {destinations.length} · {mt.totalDays}: {totalDays}</p>

        {destinations.map((dest, idx) => {
          const status = lookupStatus[dest.id];
          return (
          <div key={dest.id}>
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:14,padding:'12px',marginBottom:6}}>
              <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
                <span style={{color:'#f59e0b',fontWeight:700,fontSize:'0.82rem',minWidth:20}}>{idx+1}.</span>
                <input value={dest.city} onChange={e=>updateDest(dest.id,'city',e.target.value)} onBlur={()=>resolveDestinationRow(dest)}
                  onKeyDown={e=>{ if (e.key === 'Enter') { e.preventDefault(); resolveDestinationRow(dest); } }}
                  placeholder={mt.city}
                  style={{flex:1,background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:8,color:'#f0f6fc',padding:'7px 10px',fontSize:'0.85rem'}} />
                <button onClick={()=>moveDest(idx,-1)} disabled={idx===0} style={{background:'none',border:'none',color:idx===0?'#2a2a3a':'#8b949e',cursor:idx===0?'default':'pointer',fontSize:15,padding:'0 3px'}}>↑</button>
                <button onClick={()=>moveDest(idx,1)} disabled={idx===destinations.length-1} style={{background:'none',border:'none',color:idx===destinations.length-1?'#2a2a3a':'#8b949e',cursor:idx===destinations.length-1?'default':'pointer',fontSize:15,padding:'0 3px'}}>↓</button>
                {destinations.length > 1 && <button onClick={()=>setDestinations(prev=>prev.filter(d=>d.id!==dest.id))} style={{background:'none',border:'none',color:'#f85149',cursor:'pointer',fontSize:15,padding:'0 3px'}}>✕</button>}
              </div>
              {status?.state === 'loading' && (
                <div style={{color:'#8b949e',fontSize:'0.72rem',margin:'-2px 0 8px 28px'}}>
                  {ex.checkingDestination}
                </div>
              )}
              {status?.state === 'ok' && dest.destinationInfo && (
                <div style={{color:'#3fb950',fontSize:'0.72rem',margin:'-2px 0 8px 28px'}}>
                  {ex.destinationFound}
                </div>
              )}
              {status?.state === 'error' && (
                <div style={{color:'#f85149',fontSize:'0.72rem',margin:'-2px 0 8px 28px'}}>
                  {ex.destinationNotFound}
                </div>
              )}
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {(['from','to']).map(field => {
                  const isoVal = dest[field] || '';
                  const displayVal = isoVal ? isoVal.split('-').reverse().join('/') : '';
                  const inputStyle = {width:'100%',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:8,color:'#f0f6fc',padding:'6px 8px',fontSize:'0.8rem'};
                  return (
                    <div key={field} style={{flex:1,minWidth:110}}>
                      <div style={{color:'#8b949e',fontSize:'0.72rem',marginBottom:3}}>{field==='from'?mt.from:mt.to}</div>
                      <input
                        type="text"
                        placeholder={mt.datePh}
                        maxLength={10}
                        value={displayVal}
                        onChange={e => {
                          let val = e.target.value.replace(/[^0-9/]/g,'');
                          if (val.length === 2 && !val.includes('/')) val = val + '/';
                          if (val.length === 5 && val.split('/').length === 2) val = val + '/';
                          if (val.length === 10) {
                            const parts = val.split('/');
                            if (parts.length === 3 && parts[2].length === 4) {
                              const iso = `${parts[2]}-${parts[1]}-${parts[0]}`;
                              updateDest(dest.id, field, iso);
                              return;
                            }
                          }
                          updateDest(dest.id, field, val.length === 10 ? '' : val.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
                        }}
                        style={inputStyle}
                      />
                    </div>
                  );
                })}
                {calcNights(dest.from,dest.to) > 0 && (
                  <div style={{alignSelf:'flex-end',paddingBottom:6}}>
                    <span style={{color:'#f59e0b',fontSize:'0.78rem',fontWeight:600,whiteSpace:'nowrap'}}>{calcNights(dest.from,dest.to)} {mt.nights}</span>
                  </div>
                )}
              </div>
            </div>

            {idx < destinations.length - 1 && (
              <div style={{margin:'4px 0 6px',padding:'10px 12px',background:'rgba(245,158,11,0.04)',border:'1px solid rgba(245,158,11,0.15)',borderRadius:12}}>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom: dest.city && destinations[idx+1]?.city ? 8 : 0}}>
                  {TRANSPORTS.map(tr => (
                    <button key={tr.key} onClick={()=>updateDest(dest.id,'transport',tr.key)}
                      style={{padding:'4px 10px',borderRadius:8,border:`1px solid ${dest.transport===tr.key?'#f59e0b':'rgba(255,255,255,0.1)'}`,background:dest.transport===tr.key?'rgba(245,158,11,0.15)':'transparent',color:dest.transport===tr.key?'#f59e0b':'#8b949e',cursor:'pointer',fontSize:'0.75rem'}}>
                      {tr.icon} {tr.label}
                    </button>
                  ))}
                </div>
                {dest.city && destinations[idx+1]?.city && (
                  <a href={buildUrl(dest.city, destinations[idx+1].city, dest.to, dest.transport)} target="_blank" rel="noopener noreferrer"
                    onClick={e=>openExternalUrl(buildUrl(dest.city, destinations[idx+1].city, dest.to, dest.transport), e)}
                    style={{display:'inline-block',padding:'5px 12px',background:'linear-gradient(135deg,#f59e0b,#e07b39)',borderRadius:8,color:'#0d1117',fontWeight:700,fontSize:'0.75rem',textDecoration:'none'}}>
                    🔍 {mt.findTickets}: {dest.city} → {destinations[idx+1].city}
                  </a>
                )}
              </div>
            )}
          </div>
          );
        })}

        {destinations.length < maxDests ? (
          <button onClick={addDest} style={{width:'100%',padding:'9px',background:'rgba(245,158,11,0.06)',border:'1px dashed rgba(245,158,11,0.35)',borderRadius:12,color:'#f59e0b',cursor:'pointer',fontSize:'0.85rem',margin:'4px 0 14px'}}>
            {mt.addDest}
          </button>
        ) : !isElite ? (
          <div style={{textAlign:'center',padding:'8px 12px',margin:'4px 0 14px',background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.2)',borderRadius:12,color:'#a78bfa',fontSize:'0.78rem'}}>
            💎 {lang==='bg'?'Надгради до Elite за до 10 дестинации':lang==='de'?'Elite upgraden für bis zu 10 Ziele':lang==='el'?'Αναβάθμιση σε Elite για έως 10 προορισμούς':lang==='it'?'Passa a Elite per un massimo di 10 destinazioni':lang==='fr'?'Passez à Elite pour 10 destinations':lang==='es'?'Actualiza a Elite para 10 destinos':'Upgrade to Elite for up to 10 destinations'}
          </div>
        ) : null}

        {filledCities.length > 0 && (
          <div style={{background:'rgba(255,255,255,0.03)',borderRadius:12,padding:'10px 14px',marginBottom:12}}>
            <div style={{color:'#8b949e',fontSize:'0.72rem',marginBottom:6}}>{mt.timeline}</div>
            <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:4}}>
              {filledCities.map((d,i)=>(
                <span key={d.id} style={{display:'flex',alignItems:'center',gap:4}}>
                  <span style={{background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:8,padding:'2px 8px',color:'#f59e0b',fontSize:'0.75rem',fontWeight:600}}>{d.city}</span>
                  {i<filledCities.length-1 && <span style={{color:'#444',fontSize:'0.82rem'}}>→</span>}
                </span>
              ))}
            </div>
            <div style={{color:'#8b949e',fontSize:'0.7rem',marginTop:5}}>{mt.totalDays}: <b style={{color:'#f0f6fc'}}>{totalDays}</b></div>
          </div>
        )}

        {aiResult && !aiResult.error && (
          <div style={{background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.25)',borderRadius:12,padding:'14px',marginBottom:12}}>
            <div style={{color:'#a78bfa',fontSize:'0.78rem',fontWeight:700,marginBottom:8}}>{(routeLabels[lang]||routeLabels.en).aiRoute}</div>
            {aiResult.summary && <p style={{color:'#c9d1d9',fontSize:'0.8rem',margin:'0 0 10px',lineHeight:1.5}}>{aiResult.summary}</p>}
            {aiResult.order && <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10}}>{aiResult.order.map((c,i)=><span key={i} style={{background:'rgba(139,92,246,0.12)',borderRadius:6,padding:'2px 8px',color:'#a78bfa',fontSize:'0.72rem'}}>{i+1}. {c}</span>)}</div>}
            {aiResult.legs?.map((leg,i)=>(
              <div key={i} style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'8px 10px',marginBottom:6}}>
                <div style={{color:'#e2c8ff',fontSize:'0.78rem',fontWeight:600,marginBottom:3}}>{leg.from} → {leg.to} · {translateDuration(leg.duration, lang)}</div>
                {leg.highlight && <div style={{color:'#c9d1d9',fontSize:'0.73rem',marginBottom:2}}>✨ {leg.highlight}</div>}
                {leg.tip && <div style={{color:'#8b949e',fontSize:'0.72rem'}}>💡 {leg.tip}</div>}
              </div>
            ))}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}>
              {aiResult.bestSeason && <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:8,padding:'5px 10px',color:'#f59e0b',fontSize:'0.72rem'}}>🌤 {aiResult.bestSeason}</div>}
              {aiResult.totalBudgetEstimate && <div style={{background:'rgba(52,211,153,0.08)',border:'1px solid rgba(52,211,153,0.2)',borderRadius:8,padding:'5px 10px',color:'#34d399',fontSize:'0.72rem'}}>💰 {aiResult.totalBudgetEstimate}</div>}
            </div>
            {aiResult.mustDo?.length > 0 && (
              <div style={{marginTop:10}}>
                <div style={{color:'#a78bfa',fontSize:'0.72rem',fontWeight:600,marginBottom:4}}>{(routeLabels[lang]||routeLabels.en).mustDo}</div>
                {aiResult.mustDo.map((item,i)=><div key={i} style={{color:'#8b949e',fontSize:'0.72rem',marginBottom:2}}>• {item}</div>)}
              </div>
            )}
          </div>
        )}

        {filledCities.length >= 2 && buildMultiCityUrl(destinations) && (
          <a href={buildMultiCityUrl(destinations)} target="_blank" rel="noopener noreferrer"
            onClick={e=>openExternalUrl(buildMultiCityUrl(destinations), e)}
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',padding:'11px',marginBottom:10,background:'linear-gradient(135deg,rgba(7,112,227,0.15),rgba(7,112,227,0.08))',border:'1px solid rgba(7,112,227,0.4)',borderRadius:12,color:'#4fa3f7',fontWeight:700,fontSize:'0.85rem',textDecoration:'none',boxSizing:'border-box'}}>
            🔍 {lang==='bg'?'Намери всички полети наведнъж':lang==='de'?'Alle Flüge auf einmal suchen':lang==='el'?'Αναζήτηση όλων των πτήσεων ταυτόχρονα':lang==='it'?'Cerca tutti i voli contemporaneamente':lang==='fr'?'Trouver tous les vols en une fois':lang==='es'?'Buscar todos los vuelos de una vez':'Find all flights at once'}
          </a>
        )}

        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          {isElite && (
            <button onClick={aiSuggest} disabled={aiLoading || filledCities.length < 2}
              style={{flex:1,padding:'10px',background:'rgba(139,92,246,0.12)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:12,color:'#a78bfa',cursor:'pointer',fontSize:'0.82rem',fontWeight:600,opacity:filledCities.length<2?0.45:1}}>
              {aiLoading ? '...' : mt.aiSuggest}
            </button>
          )}
          <button onClick={saveTrip} disabled={saving||saved||!filledCities.length}
            style={{flex:1,padding:'10px',background:'linear-gradient(135deg,#f59e0b,#e07b39)',border:'none',borderRadius:12,color:'#0d1117',cursor:'pointer',fontSize:'0.82rem',fontWeight:700,opacity:!filledCities.length?0.45:1}}>
            {saved ? mt.savedMsg : saving ? mt.savingMsg : mt.saveTrip}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Explore Tab (with Stories + AI Picks) ─────────────────────────────────────
function ExploreTab({ t, lang, onJoinTrip, plan, onNeedPremium }) {
  const ft = FT[lang] || FT.en;
  const ex = exploreCopy(lang);
  const [supabaseTrips, setSupabaseTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showPlanner, setShowPlanner] = useState(false);
  const [destModal, setDestModal] = useState(null);
  const [showMood, setShowMood] = useState(false);
  const [showHoroscope, setShowHoroscope] = useState(false);
  const [showMultiTrip, setShowMultiTrip] = useState(false);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [showCreatorReport, setShowCreatorReport] = useState(false);
  const [showCreatorIce, setShowCreatorIce] = useState(false);
  const [myCompatProfile, setMyCompatProfile] = useState(null);

  async function handleMatchCreator(userId) {
    if (!userId || !supabase) return;
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (data) {
        const myCompat = await fetchMyCompatibilityProfile();
        setCreatorProfile(mapSupabaseProfile(data, myCompat));
      }
    } catch {}
  }
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const { data, error } = await supabase
          .from('trips')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (data && data.length > 0) {
          const mapped = data.map(mapSupabaseTrip);
          setSupabaseTrips(mapped);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    if (supabase) fetchTrips(); else setLoading(false);
  }, []);

  useEffect(() => {
    if (!showCreatorReport || myCompatProfile) return;
    fetchMyCompatibilityProfile().then(p => { if (p) setMyCompatProfile(p); });
  }, [showCreatorReport]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ts-tab-content">
      {creatorProfile && <ProfileModal profile={creatorProfile} t={t} lang={lang} plan={plan} onClose={()=>setCreatorProfile(null)} onLike={()=>{}} onPass={()=>setCreatorProfile(null)} onNeedPremium={onNeedPremium} onUseIcebreaker={()=>setShowCreatorIce(true)} onShowReport={()=>setShowCreatorReport(true)} onShowIce={()=>setShowCreatorIce(true)} />}
      {showCreatorReport && creatorProfile && <CompatibilityReportModal profile={creatorProfile} myProfile={myCompatProfile} lang={lang} onClose={()=>setShowCreatorReport(false)} />}
      {showCreatorIce && creatorProfile && <AiIcebreakerModal profile={creatorProfile} lang={lang} onUse={()=>setShowCreatorIce(false)} onClose={()=>setShowCreatorIce(false)} />}
      {selectedTrip && <JourneyDetailModal journey={selectedTrip} lang={lang} onClose={()=>setSelectedTrip(null)} onJoin={trip=>{ if(onJoinTrip) onJoinTrip(trip); }} />}
      {showPlanner && <AiTripPlannerModal lang={lang} onClose={()=>{ setShowPlanner(false); }} />}
      {destModal && <DestinationInfoModal dest={destModal} lang={lang} t={t} onClose={()=>setDestModal(null)} onPlan={()=>{ setDestModal(null); setShowPlanner(true); }} />}
      {showMood && <MoodTravelModal lang={lang} onClose={()=>setShowMood(false)} />}
      {showHoroscope && <HoroscopeModal lang={lang} plan={plan} onClose={()=>setShowHoroscope(false)} onNeedPremium={onNeedPremium} />}
      <MultiTripPlannerModal lang={lang} plan={plan} onClose={()=>setShowMultiTrip(false)} onNeedPremium={onNeedPremium} visible={showMultiTrip} />
      <StoriesBar t={t} lang={lang} />
      <button className="ts-horoscope-fab" onClick={()=>setShowHoroscope(true)}>
        <span>🔮</span>
        <span>{ex.horoscopeFab}</span>
        <span className="ts-horoscope-fab-badge">{ex.premium}</span>
      </button>
      <button className="ts-mood-fab" onClick={()=>setShowMood(true)}>
        <span>🎭</span>
        <span>{ex.moodToday}</span>
        <span style={{marginLeft:'auto',fontSize:'0.78rem',opacity:0.6}}>→</span>
      </button>
      <TrendingDestinations t={t} lang={lang} onPlanTrip={() => { setShowPlanner(true); }} onDestClick={(d) => setDestModal(d)} />
      <button onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('multiTrip'):setShowMultiTrip(true)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%',padding:'14px 18px',margin:'12px 0',background:'linear-gradient(135deg,rgba(245,158,11,0.15),rgba(224,123,57,0.1))',border:'1px solid rgba(245,158,11,0.4)',borderRadius:16,cursor:'pointer',boxShadow:'0 0 20px rgba(245,158,11,0.1)'}}>
        <span style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:'1.4rem'}}>✈️</span>
          <span>
            <span style={{display:'block',color:'#f59e0b',fontWeight:700,fontSize:'0.95rem',textAlign:'left'}}>{ex.multiTripTitle}</span>
            <span style={{display:'block',color:'#8b949e',fontSize:'0.75rem',textAlign:'left'}}>{ex.multiTripSub}</span>
          </span>
        </span>
        <span style={{background:'linear-gradient(135deg,#f59e0b,#e07b39)',borderRadius:8,padding:'4px 10px',color:'#0d1117',fontSize:'0.72rem',fontWeight:700,flexShrink:0}}>{ex.premium}</span>
      </button>
      <h2 className="ts-tab-title ts-serif">{t.explore.title}</h2>
      {loading ? (
        <div className="ts-loading-grid">{[1,2,3,4,5,6].map(i=><div key={i} className="ts-skeleton ts-skeleton-trip"/>)}</div>
      ) : supabaseTrips.length === 0 ? (
        <div style={{textAlign:'center',padding:'3rem 1rem'}}>
          <p style={{fontSize:'2.5rem',marginBottom:'0.5rem'}}>✈️</p>
          <p className="ts-serif" style={{color:'var(--gold)',fontSize:'1.1rem',marginBottom:'0.5rem'}}>{ex.noTripsTitle}</p>
          <p style={{color:'#8b949e',fontSize:'0.88rem',marginBottom:'1.5rem'}}>{ex.noTripsSub}</p>
          <button className="ts-btn-gold-full" style={{maxWidth:220,margin:'0 auto'}} onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('aiPlanner'):setShowPlanner(true)}>{ft.aiPlannerBtn||'🗺️ AI Planner'}</button>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:20,width:'100%'}}>{supabaseTrips.map((trip,i)=><TripCard key={`${trip.id}_${i}`} trip={trip} t={t} lang={lang} plan={plan} onJoin={onJoinTrip} onNeedPremium={onNeedPremium} onSelect={trip.aiPlan?setSelectedTrip:undefined} onMatchCreator={handleMatchCreator}/>)}</div>
      )}
    </div>
  );
}

// ── Connect Tab (with Who Viewed + Swipe toggle) ───────────────────────────────
function ConnectTab({ t, lang, onNavigateMessages, plan, onNeedPremium, onLightbox }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalProfile, setModalProfile] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [myCompatProfile, setMyCompatProfile] = useState(null);
  const [showIce, setShowIce] = useState(false);
  const [showDna, setShowDna] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [likedProfileIds, setLikedProfileIds] = useState(new Set());
  const [reverseLikedProfileIds, setReverseLikedProfileIds] = useState(new Set());
  const [matchedProfileIds, setMatchedProfileIds] = useState(new Set());
  const [activeSocialProfile, setActiveSocialProfile] = useState(null);
  const [passed, setPassed] = useState(new Set());
  const [lastPassed, setLastPassed] = useState(null);
  const [matchProfile, setMatchProfile] = useState(null);
  const [toast, setToast] = useState(null);
  // Premium features
  const [boostRemaining, setBoostRemaining] = useState(0); // seconds
  const [passportCity, setPassportCity] = useState(null);
  const [showPassport, setShowPassport] = useState(false);
  const [incognito, setIncognito] = useState(() => localStorage.getItem('ts_incognito') === '1');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({...DEFAULT_FILTERS});
  const [nightMode, setNightMode] = useState(false);
  const [, setIcebreakerText] = useState(null);
  const [videoProfile, setVideoProfile] = useState(null);
  const [lastMinuteDismissed, setLastMinuteDismissed] = useState(false);
  const [lastMinuteDest, setLastMinuteDest] = useState('');
  const [soulMatchProfile, setSoulMatchProfile] = useState(null);
  const [diaryPostCounts, setDiaryPostCounts] = useState({});
  const [showStoriesProfile, setShowStoriesProfile] = useState(null);
  const [bucketMatchItems, setBucketMatchItems] = useState(null);
  const [highlightStory, setHighlightStory] = useState(null);
  const [myBucketList] = useState(() => {
    try { return JSON.parse(localStorage.getItem('travelsoul_profile')||'{}').bucket_list || []; } catch { return []; }
  });
  const boostRef = useRef(null);
  const likeSavingIdsRef = useRef(new Set());
  const activeProfileIdRef = useRef(null);
  const ft = FT[lang] || FT.en;

  function isRealMatchedProfile(profileId) {
    const id = profileIdKey(profileId);
    if (!id) return false;
    return matchedProfileIds.has(id) || (likedProfileIds.has(id) && reverseLikedProfileIds.has(id));
  }

  async function reloadRelationshipState(debugProfile = null) {
    const activeProfileId = await getActiveProfileId({ forceFresh: true });
    activeProfileIdRef.current = activeProfileId;
    if (!activeProfileId) return null;
    let activeProfileName = null;
    let activeProfileRow = null;
    try {
      const rows = await sbFetch('GET', 'profiles', `id=eq.${activeProfileId}&select=id,name,verified,verification_status,is_admin,is_moderator,verification_rejection_reason&limit=1`);
      activeProfileRow = Array.isArray(rows) && rows[0] ? rows[0] : null;
      activeProfileName = activeProfileRow?.name || null;
      setActiveSocialProfile(activeProfileRow);
    } catch (e) {}

    const [likeRows, reverseRows, matchRows] = await Promise.all([
      sbFetch('GET', 'likes', `from_user=eq.${activeProfileId}&select=to_user`).catch(() => []),
      sbFetch('GET', 'likes', `to_user=eq.${activeProfileId}&select=from_user`).catch(() => []),
      sbFetch('GET', 'matches', `or=(user1.eq.${activeProfileId},user2.eq.${activeProfileId})&select=id,user1,user2`).catch(() => []),
    ]);

    const nextLiked = new Set((Array.isArray(likeRows) ? likeRows : []).map(row => profileIdKey(row.to_user)).filter(Boolean));
    const nextReverseLiked = new Set((Array.isArray(reverseRows) ? reverseRows : []).map(row => profileIdKey(row.from_user)).filter(Boolean));
    const nextMatched = new Set(
      (Array.isArray(matchRows) ? matchRows : [])
        .map(row => profileIdKey(row.user1) === activeProfileId ? row.user2 : row.user1)
        .map(profileIdKey)
        .filter(Boolean)
    );

    setLikedProfileIds(nextLiked);
    setReverseLikedProfileIds(nextReverseLiked);
    setMatchedProfileIds(nextMatched);

    if (debugProfile?.id) {
      const targetProfileId = profileIdKey(debugProfile.id);
      const liked = nextLiked.has(targetProfileId);
      const reverseLiked = nextReverseLiked.has(targetProfileId);
      const matchRow = matchRowForPair(matchRows, activeProfileId, targetProfileId);
      logAdiMatchDebug({
        activeProfileId,
        activeProfileName,
        targetProfileId,
        targetProfileName: debugProfile.name || null,
        targetIsAdmin: debugProfile.is_admin,
        liked,
        reverseLiked,
        matchRow,
        isRealMatched: !!matchRow || (liked && reverseLiked),
      });
    }

    return { activeProfileId, activeProfileName, likedProfileIds: nextLiked, reverseLikedProfileIds: nextReverseLiked, matchedProfileIds: nextMatched, matchRows };
  }

  useEffect(() => {
    if (!showReport || myCompatProfile) return;
    fetchMyCompatibilityProfile().then(p => { if (p) setMyCompatProfile(p); });
  }, [showReport]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function load() {
      const { accessToken } = await getAuthTokenSafe();
      const activeProfileId = await getActiveProfileId();
      if (accessToken) {
        let data;
        try {
          data = await sbRpc('get_visible_discovery_profiles', { limit_count: 50 });
        } catch (e) {
          const q = activeProfileId ? `select=${PROFILE_PUBLIC_SELECT}&id=neq.${activeProfileId}&limit=50` : `select=${PROFILE_PUBLIC_SELECT}&limit=50`;
          try { data = await sbFetch('GET', 'profiles', q); } catch (fallbackError) { setLoading(false); return; }
        }
        if (data && data.length > 0) {
          const dbBlocked = await getBlockedProfileIds(activeProfileId).catch(() => new Set());
          const localBlocked = JSON.parse(localStorage.getItem('travelsoul_blocked') || '[]').map(profileIdKey);
          const filtered = data.filter(p => !dbBlocked.has(profileIdKey(p.id)) && !localBlocked.includes(profileIdKey(p.id)));
          // Audit finding: mapSupabaseProfile hardcoded compat:75 for every
          // real profile — the real calcRealCompatibility() scorer existed
          // but was only ever invoked inside the detailed CompatibilityReportModal,
          // never for the swipe deck/match list/compat filter, so every card
          // showed an identical fake 75% regardless of who it was. Fetch the
          // current user's compat-relevant fields once and compute a real
          // per-pair score for every mapped profile.
          const myCompat = await fetchMyCompatibilityProfile();
          setProfiles(filtered.map(p => mapSupabaseProfile(p, myCompat)).sort((a, b) => a.priority - b.priority));
          setLoading(false);
          return;
        }
      }
      // No real users yet — show empty grid instead of fake profiles
      setProfiles([]);
      setLoading(false);
    }
    load();
    reloadRelationshipState();
  }, []);

  // Batch-fetch diary post counts for all visible profiles
  useEffect(() => {
    if (!profiles.length) return;
    (async () => {
      try {
        const ids = profiles.map(p => p.id).join(',');
        const data = await sbFetch('GET', 'diary_posts', `user_id=in.(${ids})&select=user_id`);
        if (Array.isArray(data)) {
          const counts = {};
          data.forEach(row => { counts[row.user_id] = (counts[row.user_id] || 0) + 1; });
          setDiaryPostCounts(counts);
        }
      } catch {}
    })();
  }, [profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Last Minute banner destination — runs once profiles are loaded
  useEffect(() => {
    if (plan !== 'elite' || lastMinuteDest) return;
    (async () => {
      const { userId } = getAuthToken();
      // 1. Try real upcoming trips from other users
      try {
        const q = userId
          ? `status=eq.upcoming&user_id=neq.${userId}&select=destination,title&limit=8`
          : `status=eq.upcoming&select=destination,title&limit=8`;
        const data = await sbFetch('GET', 'trips', q);
        if (Array.isArray(data) && data.length > 0) {
          const dests = data.map(t => t.destination || t.title).filter(d => d && d.length > 1);
          if (dests.length > 0) { setLastMinuteDest(dests[Math.floor(Math.random() * dests.length)]); return; }
        }
      } catch {}
      // 2. Dream destinations from profiles already loaded in the feed
      const profileDreams = profiles.flatMap(p => p.dreams || []).filter(Boolean);
      if (profileDreams.length > 0) { setLastMinuteDest(profileDreams[Math.floor(Math.random() * profileDreams.length)]); return; }
      // 3. Current user's own dream destinations from localStorage
      try {
        const myDreams = (JSON.parse(localStorage.getItem('travelsoul_profile') || '{}').dream_destinations || []).filter(Boolean);
        if (myDreams.length > 0) { setLastMinuteDest(myDreams[Math.floor(Math.random() * myDreams.length)]); return; }
      } catch {}
      // 4. Curated fallback list
      const fallbacks = ['Токио','Бали','Исландия','Марокко','Патагония','Барселона','Киото','Дубай'];
      setLastMinuteDest(fallbacks[Math.floor(Math.random() * fallbacks.length)]);
    })();
  }, [profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Boost countdown
  const boostActive = boostRemaining > 0;
  useEffect(() => {
    if (!boostActive) { clearInterval(boostRef.current); return; }
    boostRef.current = setInterval(() => setBoostRemaining(s => { if (s<=1){clearInterval(boostRef.current);return 0;} return s-1; }), 1000);
    return () => clearInterval(boostRef.current);
  }, [boostActive]); // eslint-disable-line react-hooks/exhaustive-deps

  function showToast(msg) { setToast(msg); setTimeout(()=>setToast(null), 1800); }

  async function ensureSocialAllowed() {
    let profile = activeSocialProfile;
    if (!profile) {
      try {
        profile = await getActiveSocialProfile();
        if (profile) setActiveSocialProfile(profile);
      } catch (e) {}
    }
    if (canUseSocialFeatures(profile)) return true;
    showToast(getVerificationRequiredMessage(lang));
    return false;
  }

  function snapshotLikeTarget(profile, source) {
    const id = profileIdKey(profile?.id);
    if (!id) return null;
    const canonical = profiles.find(p => profileIdKey(p.id) === id) || profile;
    return {
      ...canonical,
      id,
      name: canonical?.name || profile?.name || '',
      is_admin: !!(canonical?.is_admin ?? profile?.is_admin),
      __likeSource: source,
    };
  }

  async function persistLike(profile, isSuperLike = false, source = 'connect-like') {
    const targetProfile = snapshotLikeTarget(profile, source);
    if (!targetProfile) return { liked: false, matched: false };
    const result = await persistLikeAndMatch(targetProfile, { source, isSuperLike });
    if (result.matched) {
      const myName = (() => { try { return JSON.parse(localStorage.getItem('travelsoul_user') || '{}').firstName || 'Someone'; } catch { return 'Someone'; } })();
      sendPush(targetProfile.id, '💫 New Match!', `You matched with ${myName}! Say hello 👋`);
    }
    return result;
  }

  async function buildFreshMatchPair(targetProfile) {
    const activeProfileId = await getActiveProfileId({ forceFresh: true });
    const targetProfileId = profileIdKey(targetProfile?.id);
    let activeProfile = null;
    let freshTargetProfile = null;
    try {
      const ids = [activeProfileId, targetProfileId].filter(Boolean);
      if (ids.length > 0) {
        const rows = await sbFetch('GET', 'profiles', `id=in.(${ids.join(',')})&select=id,name,photo_url&limit=2`);
        if (Array.isArray(rows)) {
          activeProfile = rows.find(row => profileIdKey(row.id) === activeProfileId) || null;
          freshTargetProfile = rows.find(row => profileIdKey(row.id) === targetProfileId) || null;
        }
      }
    } catch (e) {}
    const storedMePhoto = (() => {
      try { return localStorage.getItem('travelsoul_photo') || ''; } catch { return ''; }
    })();
    const storedMe = (() => {
      try { return JSON.parse(localStorage.getItem('travelsoul_user') || '{}'); } catch { return {}; }
    })();
    const me = activeProfile || {};
    const match = { ...targetProfile, ...(freshTargetProfile || {}) };
    return {
      me: {
        ...me,
        name: me.name || [storedMe.firstName, storedMe.lastName].filter(Boolean).join(' ') || 'You',
        photo: getDisplayProfilePhoto(me, storedMePhoto),
      },
      match: {
        ...match,
        name: match.name || targetProfile?.name || 'Traveler',
        photo: getDisplayProfilePhoto(match, targetProfile?.photo || ''),
      },
    };
  }

  async function handleLike(profile, source = 'profile-card') {
    if (!profile?.id || likeSavingIdsRef.current.has(profile.id)) return false;
    if (!(await ensureSocialAllowed())) return false;
    likeSavingIdsRef.current.add(profile.id);
    setModalProfile(null);
    try {
      if (plan === 'free') {
        const data = getLikesData();
        const today = new Date().toDateString();
        const count = data.date===today ? data.count : 0;
        if (count >= FREE_LIKE_LIMIT) { onNeedPremium('likes'); return false; }
      }
      const result = await persistLike(profile, false, source);
      if (!result?.liked) return false;
      trackLike();
      setLikedProfileIds(s => new Set([...s, profileIdKey(profile.id)]));
      await reloadRelationshipState(profile);
      if (result.matched) {
        result.matchPair = await buildFreshMatchPair(profile);
        setMatchProfile(result.matchPair);
        playSound('match');
      }
      return result;
    } catch (e) {
      console.error('[Like] persist failed:', e);
      showToast(getLikeSaveFailureToast(e, lang));
      return false;
    } finally {
      likeSavingIdsRef.current.delete(profile.id);
    }
  }

  async function handleSuperLike(profile, source = 'profile-card-superlike') {
    if (!profile?.id || likeSavingIdsRef.current.has(profile.id)) return false;
    if (!(await ensureSocialAllowed())) return false;
    likeSavingIdsRef.current.add(profile.id);
    try {
      const result = await persistLike(profile, true, source);
      if (!result?.liked) return false;
      // Audit finding (7th audit): Super Like never called trackLike(), so it
      // never incremented likes_today_count/likes_last_date — the counters
      // the server-side enforce_daily_like_limit trigger reads. The trigger
      // now also enforces its own super_likes_remaining spend independently,
      // but a free user's Super Like still needs to count toward their daily
      // total so it can't be used as a second, uncapped like channel.
      trackLike();
      setLikedProfileIds(s => new Set([...s, profileIdKey(profile.id)]));
      await reloadRelationshipState(profile);
      return result;
    } catch (e) {
      console.error('[Like] persist failed:', e);
      showToast(getLikeSaveFailureToast(e, lang));
      return false;
    } finally {
      likeSavingIdsRef.current.delete(profile.id);
    }
  }

  function handlePass(profile) {
    setModalProfile(null);
    setPassed(s => new Set([...s, profile.id]));
    setLastPassed(profile);
    showToast(t.connect.passedToast);
  }

  function handleRewind() {
    if (plan === 'free') { onNeedPremium('rewind'); return; }
    if (!lastPassed) { showToast(t.connect.nothingToRewind); return; }
    setPassed(s => { const n=new Set(s); n.delete(lastPassed.id); return n; });
    setLastPassed(null);
  }

  async function handleBoost() {
    if (plan === 'free') { onNeedPremium('boost'); return; }
    if (boostRemaining > 0) return;
    // Audit fix (8th audit): this used to be a pure client-side countdown
    // with zero server interaction — profiles.boost_until was never set, so
    // the button had no actual effect on discovery visibility. Now it calls
    // activate_boost(), which plan-gates and sets boost_until server-side;
    // get_visible_discovery_profiles() orders boost_until>now() profiles
    // first, so this is what actually makes Boost do something.
    try {
      const activeProfileId = await getActiveProfileId();
      await sbRpc('activate_boost', { p_user_id: activeProfileId });
      setBoostRemaining(30 * 60);
    } catch (e) {
      const msg = String(e?.message || e || '').toLowerCase();
      if (msg.includes('boost_already_active')) {
        setBoostRemaining(30 * 60);
      } else if (msg.includes('premium_required_for_boost')) {
        onNeedPremium('boost');
      } else {
        showToast(lang==='bg'?'Boost-ът не можа да се активира. Опитай пак.':lang==='es'?'No se pudo activar el Boost. Inténtalo de nuevo.':lang==='fr'?"Le Boost n'a pas pu être activé. Réessayez.":lang==='de'?'Boost konnte nicht aktiviert werden. Versuche es erneut.':lang==='el'?'Το Boost δεν ενεργοποιήθηκε. Δοκίμασε ξανά.':lang==='it'?'Il boost non è stato attivato. Riprova.':'Boost could not be activated. Please try again.');
      }
    }
  }

  function handlePassport() {
    if (plan === 'free') { onNeedPremium('passport'); return; }
    setShowPassport(true);
  }

  function handleIncognito() {
    if (plan === 'free') { onNeedPremium('incognito'); return; }
    setIncognito(v => {
      const next = !v;
      localStorage.setItem('ts_incognito', next ? '1' : '0');
      patchProfile({ anonymous_mode: next });
      return next;
    });
  }

  function handleExpandProfile(profile) {
    if (!incognito) {
      const { userId } = getAuthToken();
      if (userId && profile.id) {
        sbFetch('POST', 'profile_views', '', { viewer_id: userId, viewed_id: profile.id }, { 'Prefer': 'return=minimal' });
      }
    }
    reloadRelationshipState(profile).catch(() => {});
    setModalProfile(profile);
  }

  function openDirectChat(profileId) {
    const targetProfileId = profileIdKey(profileId);
    if (targetProfileId && onNavigateMessages) onNavigateMessages(targetProfileId);
  }

  async function handleMessageMatchedProfile(profile) {
    const targetProfileId = profileIdKey(profile?.id);
    if (!targetProfileId) return;
    if (!(await ensureSocialAllowed())) return;
    const activeProfileId = activeProfileIdRef.current || await getActiveProfileId({ forceFresh: true });
    if (await isProfilePairBlocked(activeProfileId, targetProfileId).catch(() => false)) {
      showToast(getBlockLabels(lang).unavailable);
      setProfiles(list => list.filter(p => profileIdKey(p.id) !== targetProfileId));
      setModalProfile(null);
      return;
    }
    const relationshipState = await reloadRelationshipState(profile);
    const state = await ensureReciprocalMatchRow(activeProfileId, targetProfileId, profile?.name, {
      activeProfileName: relationshipState?.activeProfileName || null,
      targetIsAdmin: profile?.is_admin,
    });
    await reloadRelationshipState(profile);
    if (!state.isRealMatched) {
      showToast(lang==='bg'?'Няма реално съвпадение с този профил.':lang==='es'?'Todavía no hay una coincidencia real con este perfil.':lang==='fr'?'Pas encore de véritable correspondance avec ce profil.':lang==='de'?'Noch keine echte Übereinstimmung mit diesem Profil.':lang==='el'?'Δεν υπάρχει ακόμα πραγματικό ταίριασμα με αυτό το προφίλ.':lang==='it'?'Ancora nessuna vera corrispondenza con questo profilo.':'No real match with this profile yet.');
      return;
    }
    setModalProfile(null);
    setShowReport(false);
    setShowIce(false);
    openDirectChat(targetProfileId);
  }

  async function handleBlockProfile(profile) {
    const targetProfileId = profileIdKey(profile?.id);
    if (!targetProfileId) return;
    await blockProfile(targetProfileId);
    setProfiles(list => list.filter(p => profileIdKey(p.id) !== targetProfileId));
    setPassed(s => new Set([...s, targetProfileId]));
    setLikedProfileIds(s => { const n = new Set(s); n.delete(targetProfileId); return n; });
    setReverseLikedProfileIds(s => { const n = new Set(s); n.delete(targetProfileId); return n; });
    setMatchedProfileIds(s => { const n = new Set(s); n.delete(targetProfileId); return n; });
    setModalProfile(null);
    setShowReport(false);
    setShowIce(false);
    showToast(getBlockLabels(lang).blocked);
  }

  function handleFilters() {
    if (plan === 'free') { onNeedPremium('filters'); return; }
    setShowFilters(v => !v);
  }

  const activeFilterCount = countActiveFilters(filters);
  const basePassed = profiles.filter(p => !passed.has(p.id));
  const visibleProfiles = applyFilters(basePassed, filters);
  const onlineCount = visibleProfiles.filter(p => p.online).length;

  const boostMins = Math.floor(boostRemaining/60);
  const boostSecs = String(boostRemaining%60).padStart(2,'0');

  return (
    <div className={`ts-tab-content ${nightMode?'ts-night-mode':''}`}>
      <WeeklyChallengeCard lang={lang} t={t} />
      <WhoViewedRow t={t} lang={lang} plan={plan} />
      <DreamDestSection ft={ft} plan={plan} onNeedPremium={onNeedPremium} onNavigateMessages={onNavigateMessages} />

      {/* Incognito / Passport banners */}
      {incognito && <div className="ts-incognito-banner">👀 {t.connect.incognitoOn}</div>}
      {passportCity && <div className="ts-passport-banner">📍 {t.connect.viewingIn} {passportCity.city} {passportCity.flag} <button className="ts-passport-clear" onClick={()=>setPassportCity(null)}>✕</button></div>}
      {/* Last Minute Companion Alert — Elite only */}
      {plan === 'elite' && !lastMinuteDismissed && (
        <div className="ts-lastminute-banner">
          🔔{' '}
          {lang==='bg'?'Някой пътува до':lang==='es'?'Alguien viaja a':lang==='fr'?"Quelqu'un voyage à":lang==='de'?'Jemand reist nach':lang==='el'?'Κάποιος ταξιδεύει προς':lang==='it'?'Qualcuno sta viaggiando verso':'Someone is traveling to'}{' '}
          <strong style={{color:'#e8c870'}}>{lastMinuteDest||'…'}</strong>{' '}
          {lang==='bg'?'скоро — провери!':lang==='es'?'pronto — ¡compruébalo!':lang==='fr'?'bientôt — regarde !':lang==='de'?'bald — schau rein!':lang==='el'?'σύντομα — ρίξε μια ματιά!':lang==='it'?'presto — dai un\'occhiata!':'soon — check it out!'}
          <button className="ts-passport-clear" onClick={()=>setLastMinuteDismissed(true)}>✕</button>
        </div>
      )}

      <div className="ts-connect-header">
        <div>
          <h2 className="ts-tab-title ts-serif">{t.connect.title}</h2>
          <div className="ts-live-row"><PulseBar /><span className="ts-live-count">{onlineCount} {t.connect.sub}</span></div>
        </div>
        <div className="ts-connect-header-btns">
          {/* Premium action buttons */}
          <div className="ts-premium-actions">
            <button className={`ts-prem-btn ${boostRemaining>0?'active':''}`} onClick={handleBoost} title={t.connect.boost}>
              🔥 {boostRemaining>0 ? `${boostMins}:${boostSecs}` : t.connect.boost}
            </button>
            <button className="ts-prem-btn" onClick={handleRewind} title={t.connect.rewindBtn}>↩️</button>
            <button className={`ts-prem-btn ${passportCity?'active':''}`} onClick={handlePassport} title={t.connect.passport}>🌍</button>
            <button className={`ts-prem-btn ${incognito?'active':''}`} onClick={handleIncognito} title={t.connect.incognito}>👀</button>
          </div>
          <div className="ts-view-toggle">
            <button className={`ts-view-btn ${viewMode==='grid'?'active':''}`} onClick={()=>setViewMode('grid')}>⊞ {t.connect.gridMode}</button>
            <button className={`ts-view-btn ${viewMode==='swipe'?'active':''}`} onClick={()=>setViewMode('swipe')}>♠ {t.connect.swipeMode}</button>
            <button className={`ts-view-btn ${showFilters?'active':''}`} onClick={handleFilters}>
              {t.connect.filtersBtn}{activeFilterCount>0?` (${activeFilterCount})`:''}
            </button>
            <button className={`ts-view-btn ${nightMode?'active':''}`} onClick={()=>plan==='free'?onNeedPremium('nightMode'):setNightMode(m=>!m)}>
              {ft.nightMode}
            </button>
          </div>
          <button className="ts-dna-trigger-btn" onClick={()=>setShowDna(true)}>🧬 {t.dna.start}</button>
        </div>
      </div>

      {showFilters && <FiltersPanel t={t} lang={lang} filters={filters} onChange={setFilters} onClose={()=>setShowFilters(false)} />}

      {loading ? (
        <div className="ts-loading-grid">{[1,2,3,4,5,6].map(i=><div key={i} className="ts-skeleton ts-skeleton-profile"/>)}</div>
      ) : viewMode === 'grid' ? (
        <div className="ts-profiles-grid">
          {visibleProfiles.map(p=><ProfileCard key={p.id} profile={p} t={t} lang={lang} onExpand={handleExpandProfile} onLike={handleLike} isLiked={likedProfileIds.has(profileIdKey(p.id))} isMatched={isRealMatchedProfile(p.id)} onPlayVideo={setVideoProfile} onLightbox={onLightbox} plan={plan} onSoulMatch={setSoulMatchProfile} diaryPostCount={diaryPostCounts[p.id]||0} onShowStories={()=>setShowStoriesProfile(p)} onShowHighlight={pr=>setHighlightStory(pr.highlighted_story ? { ...pr.highlighted_story, authorName: pr.name, authorPhoto: pr.photo } : null)} myBucketList={myBucketList} onShowBucketMatch={items=>setBucketMatchItems(items)}/>)}
        </div>
      ) : (
        <SwipeView profiles={visibleProfiles} t={t} lang={lang} onNavigateMessages={(profileId)=>{ const profile = profiles.find(p => profileIdKey(p.id) === profileIdKey(profileId)); if (profile) handleMessageMatchedProfile(profile); }} onLike={handleLike} onPass={handlePass} onSuperLike={handleSuperLike} onPlayVideo={setVideoProfile} />
      )}

      {matchProfile && (
        <MatchOverlay profile={matchProfile.match || matchProfile} matchPair={matchProfile} t={t}
          onSendMessage={()=>{ const profile = matchProfile.match || matchProfile; setMatchProfile(null); handleMessageMatchedProfile(profile); }}
          onKeepSwiping={()=>setMatchProfile(null)} />
      )}
      {modalProfile && <ProfileModal profile={modalProfile} t={t} lang={lang} plan={plan} onClose={()=>{setModalProfile(null);setShowReport(false);setShowIce(false);}} onLike={handleLike} onPass={handlePass} onNeedPremium={onNeedPremium} onUseIcebreaker={s=>setIcebreakerText(s)} onShowReport={()=>setShowReport(true)} onShowIce={()=>setShowIce(true)} onMessage={handleMessageMatchedProfile} onBlock={handleBlockProfile} initialLiked={likedProfileIds.has(profileIdKey(modalProfile.id))} initialMatched={isRealMatchedProfile(modalProfile.id)} socialAllowed={canUseSocialFeatures(activeSocialProfile)} onRequireVerification={()=>showToast(getVerificationRequiredMessage(lang))}/>}
      {showReport && modalProfile && <CompatibilityReportModal profile={modalProfile} myProfile={myCompatProfile} lang={lang} onClose={()=>setShowReport(false)} />}
      {showIce && modalProfile && <AiIcebreakerModal profile={modalProfile} lang={lang} onUse={s=>{setIcebreakerText(s);setShowIce(false);}} onClose={()=>setShowIce(false)} />}
      {showDna && <DnaQuiz t={t} onClose={()=>setShowDna(false)}/>}
      {showPassport && <PassportModal t={t} selectedCity={passportCity?.city} onSelect={setPassportCity} onClose={()=>setShowPassport(false)} />}
      {videoProfile && <VideoPlayerModal profile={videoProfile} t={t} onClose={()=>setVideoProfile(null)} />}
      {soulMatchProfile && <SoulMatchModal profile={soulMatchProfile} plan={plan} lang={lang} onClose={()=>setSoulMatchProfile(null)} onNeedPremium={onNeedPremium} />}
      {showStoriesProfile && <SharedStoriesModal profile={showStoriesProfile} lang={lang} onClose={()=>setShowStoriesProfile(null)} />}
      {bucketMatchItems && <BucketMatchModal matchingItems={bucketMatchItems} profileName={''} lang={lang} onClose={()=>setBucketMatchItems(null)} />}
      {highlightStory && <HighlightedStoryModal story={highlightStory} lang={lang} onClose={()=>setHighlightStory(null)} />}
      {toast && <div className="ts-toast">{toast}</div>}
    </div>
  );
}

// ── NEW F7: Leaderboard Section ───────────────────────────────────────────────
function LeaderboardSection({ t }) {
  return (
    <div className="ts-leaderboard">
      <p style={{textAlign:'center',color:'var(--muted)',padding:'2rem 1rem',fontSize:'0.9rem'}}>
        🏆 {t.journeys.stats?.leaderboard || 'Leaderboard'} — coming soon
      </p>
    </div>
  );
}

// ── Journey Detail Modal ───────────────────────────────────────────────────────
function JourneyDetailModal({ journey, lang, onClose, onDelete, onJoin }) {
  const journeyName = localizeKnownTripContent(journey.name || journey.location, lang);
  const journeyLocation = localizeKnownTripContent(journey.location, lang);
  const ft = FT[lang] || FT.en;
  const [expandedDay, setExpandedDay] = useState(null);
  const [attractionFilter, setAttractionFilter] = useState('all');
  const [linkCopied, setLinkCopied] = useState(false);
  const plan = journey.aiPlan || journey.plan || {};
  const budgetColors = { flights:'#c9a84c', hotel:'#e07b39', food:'#1abc9c', activities:'#8e44ad' };
  const totalBudget = plan?.budget ? Object.values(plan.budget).reduce((a,v)=>a+(+v||0),0) : 0;
  const tr = (value) => localizeKnownTripContent(value, lang);

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
      <div style={{background:'#0d1117',borderRadius:'20px 20px 0 0',width:'100%',maxWidth:480,maxHeight:'90vh',overflowY:'auto',padding:'1.5rem 1rem 2rem'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
          <h3 className="ts-serif" style={{color:'var(--gold)',fontSize:'1.1rem',margin:0}}>✈️ {journeyName}</h3>
          <button style={{background:'none',border:'none',color:'#8b949e',fontSize:'1.4rem',cursor:'pointer',lineHeight:1}} onClick={onClose}>×</button>
        </div>

        <p style={{color:'#8b949e',fontSize:'0.85rem',marginBottom:'0.25rem'}}>📍 {journeyLocation}</p>
        {journey.start_date && <p style={{color:'#8b949e',fontSize:'0.82rem',marginBottom:'0.5rem'}}>📅 {new Date(journey.start_date).toLocaleDateString(LOCALE_MAP[lang]||'en-GB',{day:'numeric',month:'short',year:'numeric'})}</p>}
        {journey.members && <p style={{color:'#8b949e',fontSize:'0.82rem',marginBottom:'1rem'}}>👥 {journey.members} {ft.travelers||'travelers'}</p>}

        {plan && !plan.error && (
          <div>
            {plan.highlight && <p style={{color:'#8b949e',fontSize:'0.85rem',lineHeight:1.5,marginBottom:'1rem'}}>{tr(plan.highlight)}</p>}

            {/* Visa card */}
            {plan.visas && (
              <div style={{background:'#0d1117',border:`1px solid ${plan.visas.required?'#f8514944':'#3fb95044'}`,borderRadius:12,padding:'12px 14px',marginBottom:'1rem'}}>
                <p style={{color:'var(--gold)',fontSize:'0.8rem',fontWeight:700,marginBottom:'0.6rem'}}>{ft.visaInfo||'🛂 Visa Information'}</p>
                <span style={{display:'inline-block',marginBottom:'0.6rem',padding:'3px 12px',borderRadius:20,fontSize:'0.78rem',fontWeight:700,background:plan.visas.required?'rgba(248,81,73,0.15)':'rgba(63,185,80,0.15)',color:plan.visas.required?'#f85149':'#3fb950',border:`1px solid ${plan.visas.required?'#f8514944':'#3fb95044'}`}}>{plan.visas.required?`⚠️ ${ft.visaRequired||'Visa required'}`:`✅ ${ft.visaNotRequired||'No visa required'}`}</span>
                {plan.visas.required && (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.4rem',marginTop:'0.4rem'}}>
                    {plan.visas.type && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaType||'Visa type'}</p><p style={{color:'#f0f6fc',fontSize:'0.78rem'}}>{tr(plan.visas.type)}</p></div>}
                    {plan.visas.processingDays > 0 && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaDays||'Processing'}</p><p style={{color:'var(--gold)',fontSize:'0.78rem'}}>{plan.visas.processingDays} {ft.days||'days'}</p></div>}
                    {plan.visas.cost > 0 && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaCost||'Cost'}</p><p style={{color:'var(--gold)',fontSize:'0.78rem'}}>€{plan.visas.cost}</p></div>}
                    {plan.visas.where && <div style={{background:'#161b22',borderRadius:8,padding:'7px 10px',gridColumn:'1/-1'}}><p style={{color:'#8b949e',fontSize:'0.68rem',marginBottom:2}}>{ft.visaWhere||'Where to apply'}</p>{plan.visas.where.startsWith('http')?<a href={plan.visas.where} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(plan.visas.where, e)} style={{color:'#79c0ff',fontSize:'0.75rem'}}>{plan.visas.where}</a>:<p style={{color:'#f0f6fc',fontSize:'0.75rem'}}>{tr(plan.visas.where)}</p>}</div>}
                  </div>
                )}
              </div>
            )}

            {/* Vaccine card */}
            {plan.vaccines && (
              <div style={{background:'#0d1117',border:'1px solid #1abc9c44',borderRadius:12,padding:'12px 14px',marginBottom:'1rem'}}>
                <p style={{color:'var(--gold)',fontSize:'0.8rem',fontWeight:700,marginBottom:'0.6rem'}}>{ft.vaccines||'💉 Vaccines'}</p>
                {plan.vaccines.required?.length > 0 && (
                  <div style={{marginBottom:'0.5rem'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:'0.3rem'}}>{ft.vaccinesRequired||'Required'}</p>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>{plan.vaccines.required.map((v,i)=><span key={i} style={{fontSize:'0.72rem',padding:'2px 9px',borderRadius:12,background:'rgba(248,81,73,0.15)',color:'#f85149',border:'1px solid #f8514944'}}>{tr(v)}</span>)}</div>
                  </div>
                )}
                {plan.vaccines.recommended?.length > 0 && (
                  <div style={{marginBottom:'0.5rem'}}>
                    <p style={{color:'#8b949e',fontSize:'0.7rem',marginBottom:'0.3rem'}}>{ft.vaccinesRecommended||'Recommended'}</p>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>{plan.vaccines.recommended.map((v,i)=><span key={i} style={{fontSize:'0.72rem',padding:'2px 9px',borderRadius:12,background:'rgba(201,168,76,0.12)',color:'var(--gold)',border:'1px solid #c9a84c44'}}>{tr(v)}</span>)}</div>
                  </div>
                )}
                {plan.vaccines.healthTips && <p style={{color:'#8b949e',fontSize:'0.75rem',fontStyle:'italic',marginTop:'0.35rem'}}>💡 {tr(plan.vaccines.healthTips)}</p>}
              </div>
            )}

            {(plan.days||[]).map(d => {
              const isDetailed = !!d.morning;
              if (!isDetailed) {
                return (
                  <div key={d.day} style={{background:'#161b22',borderRadius:10,marginBottom:'0.5rem',border:'1px solid #21262d',padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:'0.88rem',color:'#f0f6fc'}}><strong style={{color:'var(--gold)'}}>{ft.day||'Day'} {d.day}</strong> — {tr(d.theme||d.summary)}</span>
                    <span style={{color:'var(--muted)',fontSize:'0.75rem',flexShrink:0,marginLeft:8}}>{d.cost ? `€${d.cost}` : ''}</span>
                  </div>
                );
              }
              return (
                <div key={d.day} style={{background:'#161b22',borderRadius:10,marginBottom:'0.5rem',overflow:'hidden',border:'1px solid #21262d'}}>
                  <button style={{width:'100%',background:'none',border:'none',color:'#f0f6fc',padding:'10px 14px',textAlign:'left',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'0.9rem'}}
                    onClick={()=>setExpandedDay(expandedDay===d.day?null:d.day)}>
                    <span><strong style={{color:'var(--gold)'}}>{ft.day||'Day'} {d.day}</strong> — {tr(d.theme)}</span>
                    <span style={{color:'var(--muted)',fontSize:'0.75rem'}}>{expandedDay===d.day?'▲':'▼'} {d.cost ? `€${d.cost}` : ''}</span>
                  </button>
                  {expandedDay===d.day && (
                    <div style={{padding:'0 14px 12px',borderTop:'1px solid #21262d'}}>
                      <div style={{color:'#8b949e',fontSize:'0.82rem',marginTop:'0.5rem'}}>🌅 <strong>{ft.morning||'Morning'}:</strong> {tr(d.morning)}</div>
                      <div style={{color:'#8b949e',fontSize:'0.82rem',marginTop:'0.35rem'}}>🏛️ <strong>{ft.afternoon||'Afternoon'}:</strong> {tr(d.afternoon)}</div>
                      <div style={{color:'#8b949e',fontSize:'0.82rem',marginTop:'0.35rem'}}>🍽️ <strong>{ft.evening||'Evening'}:</strong> {tr(d.evening)}</div>
                    </div>
                  )}
                </div>
              );
            })}

            {plan.budget && totalBudget > 0 && (() => {
              const BUDGET_KEYS = ['flights','hotel','food','activities'];
              const BUDGET_LABELS = { flights:ft.flights||'Flights', hotel:ft.hotel||'Hotel', food:ft.food||'Food', activities:ft.activities||'Activities' };
              const bTotal = BUDGET_KEYS.reduce((s,k)=>s+(+plan.budget[k]||0),0);
              if (bTotal === 0) return null;
              return (
                <div style={{marginTop:'1rem'}}>
                  <p style={{color:'var(--muted)',fontSize:'0.78rem',marginBottom:'0.5rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>{ft.budgetBreakdown||'BUDGET BREAKDOWN'}</p>
                  {BUDGET_KEYS.map(k => {
                    const v = +plan.budget[k] || 0;
                    const pct = bTotal > 0 ? Math.round((v/bTotal)*100) : 0;
                    return (
                      <div key={k} style={{marginBottom:'0.45rem'}}>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:'0.78rem',marginBottom:3}}>
                          <span style={{color:'#8b949e'}}>{BUDGET_LABELS[k]}</span>
                          <span style={{color:budgetColors[k]||'var(--gold)'}}>€{v}</span>
                        </div>
                        <div style={{height:5,borderRadius:3,background:'#21262d',overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${pct}%`,background:budgetColors[k]||'var(--gold)',borderRadius:3}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {plan.tips?.length > 0 && (
              <div style={{marginTop:'1rem',background:'rgba(201,168,76,0.06)',border:'1px solid #c9a84c33',borderRadius:10,padding:'12px 14px'}}>
                <p style={{color:'var(--gold)',fontSize:'0.78rem',fontWeight:700,marginBottom:'0.5rem'}}>{ft.localTips||'💡 Local Tips'}</p>
                {plan.tips.map((tip,i)=><p key={i} style={{color:'#8b949e',fontSize:'0.82rem',marginBottom:'0.3rem'}}>• {tr(tip)}</p>)}
              </div>
            )}

            {(()=>{
              const attractions = plan.attractions || [];
              if (attractions.length === 0) return (
                <div style={{marginTop:'1.25rem',background:'rgba(201,168,76,0.06)',border:'1px solid #c9a84c33',borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
                  <p style={{color:'var(--gold)',fontSize:'0.78rem',fontWeight:700,marginBottom:'0.3rem'}}>{ft.topPlaces||'🗺️ Top Places'}</p>
                  <p style={{color:'#8b949e',fontSize:'0.78rem'}}>{ft.noAttractionsMsg||'Generate a new trip to see top places'}</p>
                </div>
              );
              const catIcon = {nature:'🏔️',culture:'🏛️',food:'🍜',nightlife:'🌙',hidden:'💎'};
              const displayBestTime = (time) => {
                if (!time) return '';
                const t = time.toLowerCase();
                if (t.includes('morning') || t.includes('сутрин')) return ft.morning||time;
                if (t.includes('afternoon') || t.includes('следобед')) return ft.afternoon||time;
                if (t.includes('evening') || t.includes('вечер')) return ft.evening||time;
                return time;
              };
              const displayPrice = (price) => {
                if (!price) return '';
                if (price.toLowerCase() === 'free' || price === 'Безплатно') return ft.free||price;
                return price;
              };
              const cats = [
                {key:'all',label:ft.allCat||'All'},
                {key:'nature',label:ft.natureCat||'🏔️ Nature'},
                {key:'culture',label:ft.cultureCat||'🏛️ Culture'},
                {key:'food',label:ft.foodCat||'🍜 Food'},
                {key:'hidden',label:ft.hiddenCat||'💎 Hidden Gems'},
              ];
              const filtered = attractions.filter(a=>matchCategory(a.category, attractionFilter));
              return (
                <div style={{marginTop:'1.25rem'}}>
                  <p style={{color:'var(--gold)',fontSize:'0.85rem',fontWeight:700,marginBottom:'0.6rem'}}>{ft.topPlaces||'🗺️ Top Places'}</p>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:'0.75rem'}}>
                    {cats.map(c=>(
                      <button key={c.key} onClick={()=>setAttractionFilter(c.key)} style={{fontSize:'0.72rem',padding:'3px 10px',borderRadius:20,border:`1px solid ${attractionFilter===c.key?'var(--gold)':'#30363d'}`,background:attractionFilter===c.key?'rgba(201,168,76,0.15)':'transparent',color:attractionFilter===c.key?'var(--gold)':'#8b949e',cursor:'pointer'}}>{c.label}</button>
                    ))}
                  </div>
                  <div style={{display:'flex',gap:10,overflowX:'auto',paddingBottom:8}}>
                    {filtered.length === 0 ? (
                      <p style={{color:'#8b949e',fontSize:'0.82rem',padding:'0.5rem 0'}}>{ft.noAttrInCat||'No attractions in this category.'}</p>
                    ) : filtered.map((a,i)=>(
                      <div key={i} style={{minWidth:210,maxWidth:210,background:'#161b22',borderRadius:12,border:'1px solid #21262d',padding:'10px 12px',flexShrink:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                          <span style={{fontSize:'1.3rem'}}>{catIcon[a.category]||'📍'}</span>
                          <span style={{fontSize:'0.85rem',fontWeight:700,color:'#f0f6fc',lineHeight:1.2}}>{localizeAttractionField(a, 'name', lang)}</span>
                        </div>
                        <p style={{fontSize:'0.75rem',color:'#8b949e',marginBottom:6,lineHeight:1.4}}>{localizeAttractionField(a, 'description', lang)}</p>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                          <span style={{fontSize:'0.7rem',background:'rgba(201,168,76,0.12)',color:'var(--gold)',borderRadius:10,padding:'1px 7px',border:'1px solid #c9a84c44'}}>{displayPrice(a.price)||ft.free||'free'}</span>
                          <span style={{fontSize:'0.7rem',color:'#8b949e'}}>⏰ {displayBestTime(a.bestTime)}</span>
                          {a.visitHours && <span style={{fontSize:'0.7rem',color:'#8b949e'}}>⏱️ {a.visitHours}{ft.hours||'h.'}</span>}
                        </div>
                        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                          <a href={a.mapLink} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(a.mapLink, e)} style={{flex:1,fontSize:'0.72rem',textAlign:'center',padding:'4px 0',borderRadius:8,background:'rgba(201,168,76,0.1)',border:'1px solid #c9a84c44',color:'var(--gold)',textDecoration:'none'}}>{ft.mapBtn||'📍 Map'}</a>
                          <a href={a.wikiLink} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(a.wikiLink, e)} style={{flex:1,fontSize:'0.72rem',textAlign:'center',padding:'4px 0',borderRadius:8,background:'rgba(79,139,255,0.1)',border:'1px solid #4f8bff44',color:'#79c0ff',textDecoration:'none'}}>{ft.infoBtn||'📖 Info'}</a>
                          {a.officialSite && <a href={a.officialSite} target="_blank" rel="noopener noreferrer" onClick={e=>openExternalUrl(a.officialSite, e)} title={a.officialSite} style={{width:'100%',fontSize:'0.72rem',textAlign:'center',padding:'5px 0',borderRadius:8,background:'linear-gradient(135deg,#c9a84c22,#c9a84c44)',border:'1px solid #c9a84c66',color:'#c9a84c',textDecoration:'none',fontWeight:600,display:'block'}}>{ft.ticketsInfo||'🎟️ Tickets & Info'}</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Booking buttons */}
        {(() => {
          const tripDest = journey.destination || journey.location || journey.name || '';
          const savedDests = journey.plan?.destinations || [];
          const isMultiTrip = journey.plan?.multiTrip && savedDests.length >= 2;
          const firstCity = isMultiTrip ? (savedDests[0]?.city || tripDest) : tripDest;
          const lastCity = isMultiTrip ? (savedDests[savedDests.length-1]?.city || firstCity) : firstCity;
          const firstDate = isMultiTrip ? (savedDests[0]?.from?.replace(/-/g,'') || '') : '';
          const buildSavedMultiUrl = () => {
            const from = encodeURIComponent(firstCity);
            const to = encodeURIComponent(lastCity);
            return firstDate
              ? `https://www.skyscanner.net/transport/flights/${from}/${to}/${firstDate}/?adults=1&currency=EUR`
              : `https://www.skyscanner.net/transport/flights/${from}/${to}/?adults=1&currency=EUR`;
          };
          const googleLangMap = { bg:'bg', en:'en', es:'es', fr:'fr', de:'de', el:'el', it:'it' };
          const gl = googleLangMap[lang] || 'en';
          const glUpper = gl === 'bg' ? 'BG' : gl.toUpperCase();
          const wikiLabel    = { bg:'Уикипедия', en:'Wikipedia',    es:'Wikipedia',   fr:'Wikipédia',  de:'Wikipedia', el:'Βικιπαίδεια', it:'Wikipedia' };
          const tourLabel    = { bg:'Туристически', en:'Travel',    es:'Viajes',      fr:'Voyages',    de:'Reisen', el:'Ταξίδια', it:'Viaggi' };
          const hotelLabel   = { bg:'Хотели',     en:'Hotels',      es:'Hoteles',     fr:'Hôtels',     de:'Hotels', el:'Ξενοδοχεία', it:'Hotel' };
          const flightLabel  = { bg:'Полети',      en:'Flights',     es:'Vuelos',      fr:'Vols',       de:'Flüge', el:'Πτήσεις', it:'Voli' };
          const agodaLabel   = { bg:'Агода',       en:'Agoda',       es:'Agoda',       fr:'Agoda',      de:'Agoda', el:'Agoda', it:'Agoda' };
          const vrboLabel    = { bg:'Апартаменти', en:'Apartments',  es:'Apartamentos',fr:'Appartements',de:'Apartments', el:'Διαμερίσματα', it:'Appartamenti' };
          const allFlightsLabel = { bg:'🔍 Открий всички полети заедно', en:'🔍 Find all flights together', es:'🔍 Encontrar todos los vuelos', fr:'🔍 Trouver tous les vols', de:'🔍 Alle Flüge finden', el:'🔍 Βρες όλες τις πτήσεις μαζί', it:'🔍 Trova tutti i voli insieme' };
          const l = m => m[lang] || m.en;
          const btnBase = { display:'flex', alignItems:'center', justifyContent:'center', gap:5, borderRadius:10, fontSize:12, fontWeight:700, padding:'10px 6px', textDecoration:'none', cursor:'pointer', border:'none' };
          return (
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:7, marginTop:'1.25rem', marginBottom:8}}>
              <a href={`https://en.wikipedia.org/wiki/${encodeURIComponent(firstCity)}`} target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://en.wikipedia.org/wiki/${encodeURIComponent(firstCity)}`, e)}
                style={{...btnBase, background:'#161b22', border:'1px solid #30363d', color:'#f0f6fc'}}>
                📖 {l(wikiLabel)}
              </a>
              <a href={`https://www.google.com/travel/search?q=${encodeURIComponent(firstCity)}&hl=${gl}&gl=${glUpper}`} target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://www.google.com/travel/search?q=${encodeURIComponent(firstCity)}&hl=${gl}&gl=${glUpper}`, e)}
                style={{...btnBase, background:'#161b22', border:'1px solid #30363d', color:'#c9a84c'}}>
                🌐 {l(tourLabel)}
              </a>
              <a href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(firstCity)}&aid=2311236&label=travelsoul`} target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(firstCity)}&aid=2311236&label=travelsoul`, e)}
                style={{...btnBase, background:'#003580', color:'#fff'}}>
                🏨 {l(hotelLabel)}
              </a>
              <a href={`https://www.skyscanner.net/?origin=${encodeURIComponent(firstCity)}&adults=1`}
                target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://www.skyscanner.net/?origin=${encodeURIComponent(firstCity)}&adults=1`, e)}
                style={{...btnBase, background:'#1a73e8', color:'#fff'}}>
                ✈️ {l(flightLabel)}
              </a>
              <a href={`https://www.agoda.com/search?city=${encodeURIComponent(firstCity)}&tag=532102&adults=2`} target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://www.agoda.com/search?city=${encodeURIComponent(firstCity)}&tag=532102&adults=2`, e)}
                style={{...btnBase, background:'#e2001a', color:'#fff'}}>
                🌏 {l(agodaLabel)}
              </a>
              <a href={`https://www.vrbo.com/search/keywords:${encodeURIComponent(firstCity)}`} target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://www.vrbo.com/search/keywords:${encodeURIComponent(firstCity)}`, e)}
                style={{...btnBase, background:'#1a1a2e', border:'1px solid #30363d', color:'#f0f6fc'}}>
                🏠 {l(vrboLabel)}
              </a>
              <a href={`https://www.klook.com/en-US/search/?query=${encodeURIComponent(firstCity)}&utm_medium=affiliate&utm_source=travelsoul&aff_adid=532102`} target="_blank" rel="noopener noreferrer"
                onClick={e=>openExternalUrl(`https://www.klook.com/en-US/search/?query=${encodeURIComponent(firstCity)}&utm_medium=affiliate&utm_source=travelsoul&aff_adid=532102`, e)}
                style={{...btnBase, background:'rgba(255,90,0,0.15)', border:'1px solid rgba(255,90,0,0.3)', color:'#ff5a00'}}>
                🎡 {lang==='bg'?'Активности':lang==='es'?'Actividades':lang==='fr'?'Activités':lang==='de'?'Aktivitäten':lang==='el'?'Δραστηριότητες':lang==='it'?'Attività':'Activities'}
              </a>
              {isMultiTrip && (
                <a href={buildSavedMultiUrl()} target="_blank" rel="noopener noreferrer"
                  onClick={e=>openExternalUrl(buildSavedMultiUrl(), e)}
                  style={{...btnBase, gridColumn:'1 / -1', background:'linear-gradient(135deg,#c9a84c,#e8b923)', color:'#0d1117', padding:'12px 8px'}}>
                  {l(allFlightsLabel)}
                </a>
              )}
            </div>
          );
        })()}

        {(() => {
          const _dests = journey.plan?.destinations || [];
          const _isMulti = journey.plan?.multiTrip && _dests.length >= 2;
          if (!_isMulti) return null;
          const _first = encodeURIComponent(_dests[0]?.city || '');
          const _last = encodeURIComponent(_dests[_dests.length-1]?.city || '');
          const _allFlightsUrl = `https://www.skyscanner.net/?origin=${_first}&destination=${_last}&adults=1`;
          const _label = { bg:'🔍 Открий всички полети наведнъж', en:'🔍 Find all flights at once', es:'🔍 Encontrar todos los vuelos', fr:'🔍 Trouver tous les vols', de:'🔍 Alle Flüge finden', el:'🔍 Βρες όλες τις πτήσεις μαζί', it:'🔍 Trova tutti i voli insieme' };
          return (
            <a href={_allFlightsUrl} target="_blank" rel="noopener noreferrer"
              onClick={e=>openExternalUrl(_allFlightsUrl, e)}
              style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',padding:'12px',marginTop:'0.5rem',marginBottom:4,background:'linear-gradient(135deg,#c9a84c,#e8b923)',border:'none',borderRadius:12,color:'#0d1117',fontWeight:700,fontSize:'0.85rem',textDecoration:'none',boxSizing:'border-box'}}>
              {_label[lang] || _label.en}
            </a>
          );
        })()}
        <div style={{display:'flex',gap:8,marginTop:'0.5rem'}}>
          {onJoin
            ? <button className="ts-btn-gold-full" style={{flex:1}} onClick={()=>{ onJoin(journey); onClose(); }}>✈ {ft.joinTrip||'Join Trip'}</button>
            : <button className="ts-btn-gold-full" style={{flex:1}} onClick={()=>{
                const dest = localizeTripText(journey.destination || journey.title || journey.name || 'a trip', lang);
                const shareTexts = {
                  bg: `✈️ Присъедини се към моето пътуване до ${dest} в ТравелСоул!`,
                  en: `✈️ Join me on a trip to ${dest} on TravelSoul!`,
                  es: `✈️ ¡Únete a mi viaje a ${dest} en TravelSoul!`,
                  fr: `✈️ Rejoins-moi pour un voyage à ${dest} sur TravelSoul !`,
                  de: `✈️ Begleite mich auf einer Reise nach ${dest} auf TravelSoul!`,
                  el: `✈️ Έλα μαζί μου σε ένα ταξίδι στην/στο ${dest} στο TravelSoul!`,
                  it: `✈️ Unisciti a me in un viaggio a ${dest} su TravelSoul!`,
                };
                const text = shareTexts[lang] || shareTexts.en;
                if (navigator.share) {
                  navigator.share({ title: 'TravelSoul Trip', text, url: window.location.href }).catch(()=>{});
                } else {
                  navigator.clipboard?.writeText(`${text} ${window.location.href}`);
                  setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000);
                }
              }}>{ft.inviteFriends||'Invite Friends'}</button>
          }
          {onDelete && <button className="ts-btn-ghost-sm" style={{color:'#f85149',borderColor:'#f85149'}} onClick={()=>{ onDelete(journey.id); onClose(); }}>{ft.deleteTrip||'Delete'}</button>}
        </div>
        {linkCopied && <div style={{textAlign:'center',color:'#3fb950',fontSize:'0.8rem',marginTop:8}}>{ft.copyLink||'✅ Link copied!'}</div>}
      </div>
    </div>
  );
}

// ── AI Travel Diary (Elite only) ─────────────────────────────────────────────
function TravelDiaryModal({ lang, plan, onClose, onNeedPremium }) {
  const [form, setForm] = useState({ destination: '', date: '', note: '' });
  const [loading, setLoading] = useState(false);
  const [story, setStory] = useState('');
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [sharedIds, setSharedIds] = useState(new Set());
  const [sharingId, setSharingId] = useState(null);
  const [pinnedEntryId, setPinnedEntryId] = useState(null);
  const [pinning, setPinning] = useState(false);

  useEffect(() => {
    if (plan !== 'elite' || !supabase) return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const [{ data: diaryData }, { data: postsData }, { data: profileRow }] = await Promise.all([
        supabase.from('diary_entries').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false }).limit(20),
        supabase.from('diary_posts').select('destination,travel_date').eq('user_id', session.user.id).catch(() => ({ data: [] })),
        supabase.from('profiles').select('highlighted_story').eq('id', session.user.id).single(),
      ]);
      if (diaryData) setEntries(diaryData.map(r => ({ id: r.id, destination: r.destination, date: r.travel_date || '', story: r.story, createdAt: r.created_at })));
      if (postsData) setSharedIds(new Set(postsData.map(p => `${p.destination}::${p.travel_date||''}`)));
      if (profileRow?.highlighted_story?.entry_id) setPinnedEntryId(profileRow.highlighted_story.entry_id);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (plan !== 'elite') {
    return (
      <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
        <div className="ts-modal" style={{...MODAL_S, maxWidth:380}} onClick={e=>e.stopPropagation()}>
          <button className="ts-modal-close" onClick={onClose}>✕</button>
          <div style={{padding:'1.5rem 0',textAlign:'center'}}>
            <div style={{fontSize:'2.5rem',marginBottom:12}}>✈️</div>
            <h3 style={{color:'var(--gold)',marginBottom:8}}>{lang==='bg'?'AI Пътнически дневник':lang==='es'?'Diario de viaje con IA':lang==='fr'?'Journal de voyage IA':lang==='de'?'KI-Reisetagebuch':lang==='el'?'Ταξιδιωτικό ημερολόγιο AI':lang==='it'?'Diario di viaggio IA':'AI Travel Diary'}</h3>
            <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:'1.5rem'}}>{lang==='bg'?'Само за Elite — надгради за AI истории от твоите пътувания':lang==='es'?'Función Elite: mejora tu plan para crear historias de viaje con IA':lang==='fr'?'Fonctionnalité Elite — passez à Elite pour créer des récits de voyage par IA':lang==='de'?'Elite-Funktion — upgrade, um KI-gestützte Reisegeschichten zu erstellen':lang==='el'?'Λειτουργία Elite — αναβάθμισε για να δημιουργήσεις ταξιδιωτικές ιστορίες με AI':lang==='it'?'Funzione Elite — passa a Elite per creare storie di viaggio con l\'IA':'Elite feature — upgrade to create AI-powered travel stories'}</p>
            <button className="ts-btn-gold-full" onClick={()=>{onClose();onNeedPremium&&onNeedPremium('diary');}}>💎 {lang==='bg'?'Надгради до Elite':lang==='es'?'Mejora a Elite':lang==='fr'?'Passer à Elite':lang==='de'?'Auf Elite upgraden':lang==='el'?'Αναβάθμιση σε Elite':lang==='it'?'Passa a Elite':'Upgrade to Elite'}</button>
          </div>
        </div>
      </div>
    );
  }

  const responseLang = responseLanguageName(lang);
  const L = {
    title:    lang==='bg'?'✈ Пътнически дневник':lang==='es'?'✈ Diario de viaje':lang==='fr'?'✈ Journal de voyage':lang==='de'?'✈ Reisetagebuch':lang==='el'?'✈ Ταξιδιωτικό ημερολόγιο':lang==='it'?'✈ Diario di viaggio':'✈ Travel Diary',
    dest:     lang==='bg'?'Дестинация':lang==='es'?'Destino':lang==='fr'?'Destination':lang==='de'?'Reiseziel':lang==='el'?'Προορισμός':lang==='it'?'Destinazione':'Destination',
    date:     lang==='bg'?'Дата на пътуването':lang==='es'?'Fecha del viaje':lang==='fr'?'Date du voyage':lang==='de'?'Reisedatum':lang==='el'?'Ημερομηνία ταξιδιού':lang==='it'?'Data del viaggio':'Trip date',
    note:     lang==='bg'?'Твой опит (незадължително)':lang==='es'?'Tu experiencia (opcional)':lang==='fr'?'Ton expérience (optionnel)':lang==='de'?'Deine Erfahrung (optional)':lang==='el'?'Η εμπειρία σου (προαιρετικό)':lang==='it'?'La tua esperienza (opzionale)':'Your experience (optional)',
    ph:       lang==='bg'?'Любим момент, хора, усещания...':lang==='es'?'Momento favorito, personas conocidas, sensaciones...':lang==='fr'?'Moment préféré, personnes rencontrées, émotions...':lang==='de'?'Lieblingsmoment, getroffene Leute, Gefühle...':lang==='el'?'Αγαπημένη στιγμή, άνθρωποι που γνώρισες, συναισθήματα...':lang==='it'?'Momento preferito, persone incontrate, emozioni...':'Favorite moment, people met, feelings...',
    generate: lang==='bg'?'✨ Генерирай история':lang==='es'?'✨ Generar historia':lang==='fr'?'✨ Générer histoire':lang==='de'?'✨ Geschichte erstellen':lang==='el'?'✨ Δημιουργία ιστορίας':lang==='it'?'✨ Crea storia':'✨ Generate Story',
    writing:  lang==='bg'?'Пишем...':lang==='es'?'Escribiendo...':lang==='fr'?'Écriture...':lang==='de'?'Schreiben...':lang==='el'?'Γράφεται...':lang==='it'?'Scrittura in corso...':'Writing...',
    past:     lang==='bg'?'Предишни записи':lang==='es'?'Entradas anteriores':lang==='fr'?'Entrées précédentes':lang==='de'?'Frühere Einträge':lang==='el'?'Προηγούμενες καταχωρήσεις':lang==='it'?'Voci precedenti':'Past Entries',
    newEntry: lang==='bg'?'+ Нов запис':lang==='es'?'+ Nueva entrada':lang==='fr'?'+ Nouvelle entrée':lang==='de'?'+ Neuer Eintrag':lang==='el'?'+ Νέα καταχώρηση':lang==='it'?'+ Nuova voce':'+ New Entry',
    share:    lang==='bg'?'📤 Сподели':lang==='es'?'📤 Compartir':lang==='fr'?'📤 Partager':lang==='de'?'📤 Teilen':lang==='el'?'📤 Κοινοποίηση':lang==='it'?'📤 Condividi':'📤 Share',
    shared:   lang==='bg'?'✅ Споделено':lang==='es'?'✅ Compartido':lang==='fr'?'✅ Partagé':lang==='de'?'✅ Geteilt':lang==='el'?'✅ Κοινοποιήθηκε':lang==='it'?'✅ Condiviso':'✅ Shared',
    sharing:  lang==='bg'?'...':lang==='es'?'...':lang==='fr'?'...':lang==='de'?'...':lang==='el'?'...':lang==='it'?'...':'...',
  };

  async function generate() {
    if (!form.destination.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          max_tokens: 600,
          messages: [
            { role: 'system', content: `You are a poetic travel writer. Write vivid, personal travel diary entries in ${responseLang}. Be emotional and descriptive. Write exactly 2-3 paragraphs. No titles, no headers, no bullet points — pure flowing prose only.` },
            { role: 'user',   content: `Write a travel diary entry for my trip to ${form.destination.trim()}${form.date ? ` on ${form.date}` : ''}${form.note.trim() ? `. My experience: ${form.note.trim()}` : ''}. Write in ${responseLang}.` },
          ],
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      const text = data.choices[0].message.content.trim();
      setStory(text);
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: row } = await supabase
            .from('diary_entries')
            .insert({ user_id: session.user.id, destination: form.destination.trim(), travel_date: form.date || null, note: form.note.trim() || null, story: text })
            .select()
            .single();
          const entry = row
            ? { id: row.id, destination: row.destination, date: row.travel_date || '', story: row.story, createdAt: row.created_at }
            : { id: Date.now(), destination: form.destination.trim(), date: form.date, story: text, createdAt: new Date().toISOString() };
          setEntries(prev => [entry, ...prev]);
        }
      } else {
        setEntries(prev => [{ id: Date.now(), destination: form.destination.trim(), date: form.date, story: text, createdAt: new Date().toISOString() }, ...prev]);
      }
    } catch(e) {
      setError(e.message || 'Error generating story');
    }
    setLoading(false);
  }

  async function shareEntry(entry) {
    const key = `${entry.destination}::${entry.date||''}`;
    if (sharedIds.has(key) || sharingId === entry.id || !supabase) return;
    setSharingId(entry.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.from('diary_posts').insert({
          user_id: session.user.id,
          destination: entry.destination,
          travel_date: entry.date || null,
          story: entry.story.slice(0, 300),
        });
        setSharedIds(s => new Set([...s, key]));
      }
    } catch {}
    setSharingId(null);
  }

  async function pinEntry(entry) {
    if (!supabase || pinning) return;
    setPinning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setPinning(false); return; }
      if (pinnedEntryId === entry.id) {
        await supabase.from('profiles').update({ highlighted_story: null }).eq('id', session.user.id);
        setPinnedEntryId(null);
      } else {
        const storyData = { entry_id: entry.id, destination: entry.destination, date: entry.date || null, story: entry.story };
        await supabase.from('profiles').update({ highlighted_story: storyData }).eq('id', session.user.id);
        setPinnedEntryId(entry.id);
      }
    } catch {}
    setPinning(false);
  }

  return (
    <div className="ts-modal-overlay ts-diary-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-planner-modal ts-diary-modal" style={{...MODAL_S, maxWidth:480, maxHeight:'85vh', overflowY:'auto', textAlign:'left'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{background:'linear-gradient(90deg,#b47fff,#e8c870)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',textAlign:'center'}}>{L.title}</h2>

        {story ? (
          <div>
            <div className="ts-diary-card">
              <div className="ts-diary-dest">✈ {form.destination}{form.date ? ` · ${form.date}` : ''}</div>
              <p className="ts-diary-story">{story}</p>
            </div>
            <button className="ts-btn-gold-full" style={{marginTop:'1rem'}} onClick={()=>{setStory('');setForm({destination:'',date:'',note:''});}}>
              {L.newEntry}
            </button>
          </div>
        ) : (
          <div className="ts-planner-form">
            <label className="ts-filter-label">{L.dest}
              <input className="ts-input" value={form.destination} onChange={e=>setForm(f=>({...f,destination:e.target.value}))} placeholder="Paris, Tokyo, Bali..." />
            </label>
            <label className="ts-filter-label">{L.date}
              <input type="date" className="ts-input" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} />
            </label>
            <label className="ts-filter-label">{L.note}
              <textarea className="ts-input" rows={3} style={{resize:'vertical'}} value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} placeholder={L.ph} />
            </label>
            {error && <p style={{color:'#f85149',fontSize:13,margin:'0.25rem 0'}}>{error}</p>}
            <button className="ts-btn-gold-full" onClick={generate} disabled={loading || !form.destination.trim()}>
              {loading ? L.writing : L.generate}
            </button>
          </div>
        )}

        {entries.length > 0 && !story && (
          <div style={{marginTop:'1.5rem'}}>
            <h4 style={{color:'var(--gold)',marginBottom:'0.75rem',fontSize:'0.88rem',fontWeight:600}}>{L.past}</h4>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {entries.slice(0,5).map(e => {
                const isOpen = expandedId === e.id;
                return (
                  <div key={e.id} className="ts-diary-past-card" style={{cursor:'pointer'}} onClick={()=>setExpandedId(isOpen ? null : e.id)}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <div className="ts-diary-dest" style={{fontSize:'0.78rem'}}>✈ {e.destination}{e.date ? ` · ${e.date}` : ''}</div>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        {(() => {
                          const key = `${e.destination}::${e.date||''}`;
                          const isShared = sharedIds.has(key);
                          const isSendingThis = sharingId === e.id;
                          const isPinned = pinnedEntryId === e.id;
                          const isPinningThis = pinning && pinnedEntryId !== e.id;
                          return (
                            <>
                              <button
                                onClick={ev => { ev.stopPropagation(); shareEntry(e); }}
                                disabled={isShared || isSendingThis}
                                style={{background:'none',border:'1px solid rgba(255,215,0,0.3)',borderRadius:6,color:isShared?'var(--gold)':'var(--muted)',fontSize:'0.65rem',padding:'2px 7px',cursor:isShared?'default':'pointer',opacity:isSendingThis?0.5:1,whiteSpace:'nowrap'}}
                              >{isSendingThis ? L.sharing : isShared ? L.shared : L.share}</button>
                              <button
                                onClick={ev => { ev.stopPropagation(); pinEntry(e); }}
                                disabled={pinning}
                                style={{background:isPinned?'rgba(232,200,112,0.15)':'none',border:`1px solid ${isPinned?'rgba(232,200,112,0.5)':'rgba(255,255,255,0.12)'}`,borderRadius:6,color:isPinned?'var(--gold)':'var(--muted)',fontSize:'0.65rem',padding:'2px 7px',cursor:'pointer',opacity:(pinning&&!isPinned)?0.5:1,whiteSpace:'nowrap'}}
                                title={isPinned?(lang==='bg'?'Откачи Story':lang==='es'?'Desfijar story':lang==='fr'?'Détacher la story':lang==='de'?'Story loslösen':lang==='el'?'Αφαίρεση από Story':lang==='it'?'Rimuovi dalla storia':'Unpin Story'):(lang==='bg'?'Закачи като Story':lang==='es'?'Fijar como story':lang==='fr'?'Épingler comme story':lang==='de'?'Als Story anheften':lang==='el'?'Καρφίτσωμα στο Story':lang==='it'?'Fissa come storia':'Pin as Story')}
                              >{isPinningThis ? '…' : isPinned ? '📌 '+(lang==='bg'?'Закачено':lang==='es'?'Fijado':lang==='fr'?'Épinglé':lang==='de'?'Angeheftet':lang==='el'?'Καρφιτσωμένο':lang==='it'?'Fissato':'Pinned') : '📌 '+(lang==='bg'?'Закачи':lang==='es'?'Fijar':lang==='fr'?'Épingler':lang==='de'?'Anheften':lang==='el'?'Καρφίτσωμα':lang==='it'?'Fissa':'Pin')}</button>
                            </>
                          );
                        })()}
                        <span style={{fontSize:'0.7rem',color:'var(--muted)'}}>{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    <p className="ts-diary-story" style={{fontSize:'0.8rem',opacity:0.8,...(!isOpen && {display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'})}}>{e.story}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared Travel Stories Modal ───────────────────────────────────────────────
function SharedStoriesModal({ profile, lang, onClose }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await sbFetch('GET', 'diary_posts', `user_id=eq.${profile.id}&order=created_at.desc&limit=20`);
        setPosts(Array.isArray(data) ? data : []);
      } catch { setPosts([]); }
      setLoading(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const title = lang==='bg'?`✈️ Истории от ${profile.name}`:lang==='es'?`✈️ Historias de ${profile.name}`:lang==='fr'?`✈️ Histoires de ${profile.name}`:lang==='de'?`✈️ Reisen von ${profile.name}`:lang==='el'?`✈️ Ιστορίες από ${profile.name}`:lang==='it'?`✈️ Storie di ${profile.name}`:`✈️ Stories by ${profile.name}`;
  const emptyMsg = lang==='bg'?'Все още няма споделени истории':lang==='es'?'Sin historias compartidas aún':lang==='fr'?'Pas encore d\'histoires partagées':lang==='de'?'Noch keine geteilten Reisen':lang==='el'?'Δεν υπάρχουν κοινοποιημένα ταξίδια ακόμα':lang==='it'?'Ancora nessun viaggio condiviso':'No shared stories yet';

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-shared-stories-modal" onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        {profile.photo && <img src={profile.photo} alt={profile.name} style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',display:'block',margin:'0 auto 8px',border:'2px solid var(--gold)'}}/>}
        <h3 className="ts-serif" style={{color:'var(--gold)',textAlign:'center',marginBottom:14,fontSize:'1rem'}}>{title}</h3>
        {loading ? (
          <div style={{textAlign:'center',padding:'1rem',color:'var(--muted)',fontSize:'0.85rem'}}>
            <div className="ts-soul-match-spinner" style={{margin:'0 auto 8px'}}/>
          </div>
        ) : posts.length === 0 ? (
          <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem',padding:'0.5rem 0'}}>{emptyMsg}</p>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {posts.map((p, i) => {
              const isOpen = expandedIdx === i;
              return (
                <div key={i} style={{background:'rgba(255,215,0,0.04)',border:'1px solid rgba(255,215,0,0.12)',borderRadius:10,padding:'10px 12px',cursor:'pointer'}} onClick={()=>setExpandedIdx(isOpen?null:i)}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <span style={{color:'var(--gold)',fontSize:'0.78rem',fontWeight:600}}>✈ {p.destination}{p.travel_date ? ` · ${p.travel_date}` : ''}</span>
                    <span style={{fontSize:'0.68rem',color:'var(--muted)'}}>{isOpen?'▲':'▼'}</span>
                  </div>
                  <p style={{color:'var(--text)',fontSize:'0.8rem',lineHeight:1.55,margin:0,...(!isOpen&&{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'})}}>{p.story}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const escapeHtml = (s) => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

function renderMarkdown(text) {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<strong style="display:block;margin-bottom:2px">$1</strong>')
    .replace(/^## (.+)$/gm, '<strong style="display:block;margin-bottom:2px;font-size:1.02em">$1</strong>')
    .replace(/^# (.+)$/gm, '<strong style="display:block;margin-bottom:4px;font-size:1.05em">$1</strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

// ── VIP Concierge Modal (Elite only) ─────────────────────────────────────────
const CONCIERGE_COPY = {
  bg: {
    lockedTitle: 'ВИП Личен асистент',
    lockedSub: 'Само за Elite — надгради за персонален AI асистент',
    upgrade: 'Надгради до Elite',
    title: '💎 ВИП Личен асистент',
    placeholder: 'Попитай своя личен асистент...',
    greeting: 'Здравейте! Аз съм вашият ВИП Личен асистент. Как мога да ви помогна днес?',
    clearTitle: 'Изчисти историята',
    clear: '🗑 Изчисти',
    send: 'Изпрати',
    error: 'Възникна грешка. Опитайте отново.',
    previousOtherLang: 'Този предишен отговор е създаден на друг език. Попитайте отново и ще отговоря на български.',
    responseLanguage: 'Bulgarian',
  },
  en: {
    lockedTitle: 'VIP Concierge',
    lockedSub: 'Elite feature — upgrade for a personal AI travel concierge',
    upgrade: 'Upgrade to Elite',
    title: '💎 VIP Concierge',
    placeholder: 'Ask your concierge...',
    greeting: "Hello! I'm your personal VIP concierge. How can I assist you today?",
    clearTitle: 'Clear history',
    clear: '🗑 Clear',
    send: 'Send',
    error: 'Something went wrong. Please try again.',
    previousOtherLang: 'This previous reply was created in another language. Ask again and I will answer in English.',
    responseLanguage: 'English',
  },
  es: {
    lockedTitle: 'Concierge VIP',
    lockedSub: 'Función Elite — mejora para tener un concierge de viajes con IA personal',
    upgrade: 'Mejorar a Elite',
    title: '💎 Concierge VIP',
    placeholder: 'Pregunta a tu concierge...',
    greeting: '¡Hola! Soy su concierge VIP personal. ¿Cómo puedo ayudarle hoy?',
    clearTitle: 'Borrar historial',
    clear: '🗑 Borrar',
    send: 'Enviar',
    error: 'Ha ocurrido un error. Inténtalo de nuevo.',
    previousOtherLang: 'Esta respuesta anterior se creó en otro idioma. Pregunta de nuevo y responderé en español.',
    responseLanguage: 'Spanish',
  },
  fr: {
    lockedTitle: 'Conciergerie VIP',
    lockedSub: 'Fonction Elite — passez à un concierge de voyage IA personnel',
    upgrade: 'Passer à Elite',
    title: '💎 Conciergerie VIP',
    placeholder: 'Demandez à votre concierge...',
    greeting: 'Bonjour ! Je suis votre concierge VIP personnel. Comment puis-je vous aider ?',
    clearTitle: 'Effacer l’historique',
    clear: '🗑 Effacer',
    send: 'Envoyer',
    error: 'Une erreur est survenue. Réessayez.',
    previousOtherLang: 'Cette réponse précédente a été créée dans une autre langue. Posez à nouveau la question et je répondrai en français.',
    responseLanguage: 'French',
  },
  de: {
    lockedTitle: 'VIP-Concierge',
    lockedSub: 'Elite-Funktion — Upgrade für einen persönlichen KI-Reiseconcierge',
    upgrade: 'Auf Elite upgraden',
    title: '💎 VIP-Concierge',
    placeholder: 'Fragen Sie Ihren Concierge...',
    greeting: 'Guten Tag! Ich bin Ihr persönlicher VIP-Concierge. Wie kann ich Ihnen helfen?',
    clearTitle: 'Verlauf löschen',
    clear: '🗑 Löschen',
    send: 'Senden',
    error: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    previousOtherLang: 'Diese frühere Antwort wurde in einer anderen Sprache erstellt. Fragen Sie erneut und ich antworte auf Deutsch.',
    responseLanguage: 'German',
  },
  el: {
    lockedTitle: 'VIP Concierge',
    lockedSub: 'Λειτουργία Elite — αναβάθμισε για προσωπικό AI travel concierge',
    upgrade: 'Αναβάθμιση σε Elite',
    title: '💎 VIP Concierge',
    placeholder: 'Ρώτησε τον concierge σου...',
    greeting: 'Γεια σας! Είμαι ο προσωπικός σας VIP concierge. Πώς μπορώ να βοηθήσω σήμερα;',
    clearTitle: 'Καθαρισμός ιστορικού',
    clear: '🗑 Καθαρισμός',
    send: 'Αποστολή',
    error: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.',
    previousOtherLang: 'Αυτή η προηγούμενη απάντηση δημιουργήθηκε σε άλλη γλώσσα. Ρωτήστε ξανά και θα απαντήσω στα ελληνικά.',
    responseLanguage: 'Greek',
  },
  it: {
    lockedTitle: 'Concierge VIP',
    lockedSub: 'Funzione Elite — passa a un concierge di viaggio AI personale',
    upgrade: 'Passa a Elite',
    title: '💎 Concierge VIP',
    placeholder: 'Chiedi al tuo concierge...',
    greeting: 'Ciao! Sono il tuo concierge VIP personale. Come posso aiutarti oggi?',
    clearTitle: 'Cancella cronologia',
    clear: '🗑 Cancella',
    send: 'Invia',
    error: 'Qualcosa è andato storto. Riprova.',
    previousOtherLang: 'Questa risposta precedente è stata creata in un’altra lingua. Chiedi di nuovo e risponderò in italiano.',
    responseLanguage: 'Italian',
  },
};

const CONCIERGE_LANG_MARK_RE = /^\[\[ts-lang:([a-z]{2})\]\]\s*/;

function getConciergeCopy(lang) {
  return CONCIERGE_COPY[lang] || CONCIERGE_COPY.en;
}

function markConciergeReply(reply, lang) {
  return `[[ts-lang:${lang}]] ${reply}`;
}

function parseConciergeMessage(message) {
  const content = String(message?.content || '');
  const match = content.match(CONCIERGE_LANG_MARK_RE);
  return match ? { ...message, lang: match[1], content: content.replace(CONCIERGE_LANG_MARK_RE, '') } : message;
}

function VipConciergeModal({ lang, plan, onClose, onNeedPremium }) {
  const cc = getConciergeCopy(lang);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    if (!supabase) return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data } = await supabase
        .from('concierge_messages')
        .select('role, content')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true })
        .limit(50);
      if (data?.length) setMessages(data.map(parseConciergeMessage));
    })();
  }, []);

  async function clearHistory() {
    setMessages([]);
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) await supabase.from('concierge_messages').delete().eq('user_id', session.user.id);
    }
  }

  if (plan !== 'elite') {
    return (
      <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
        <div className="ts-modal" style={{...MODAL_S, maxWidth: 400}} onClick={e => e.stopPropagation()}>
          <button className="ts-modal-close" onClick={onClose}>✕</button>
          <div style={{padding: '1.5rem 0', textAlign: 'center'}}>
            <div style={{fontSize: '2.5rem', marginBottom: 12}}>💎</div>
            <h3 style={{color: 'var(--gold)', marginBottom: 8}}>{cc.lockedTitle}</h3>
            <p style={{color: 'var(--muted)', fontSize: '0.88rem', marginBottom: '1.5rem'}}>
              {cc.lockedSub}
            </p>
            <button className="ts-btn-gold-full" onClick={() => { onClose(); onNeedPremium && onNeedPremium('concierge'); }}>
              💎 {cc.upgrade}
            </button>
          </div>
        </div>
      </div>
    );
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);
    setError('');
    try {
      let userId = null;
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          userId = session.user.id;
          await supabase.from('concierge_messages').insert({ user_id: userId, role: 'user', content: text });
        }
      }
      const conversationForModel = updated.map(m => m.role === 'assistant' ? parseConciergeMessage(m) : m)
        .filter(m => m.role !== 'assistant' || m.lang === lang)
        .map(({ role, content }) => ({ role, content }));
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          max_tokens: 800,
          messages: [
            { role: 'system', content: `You are a luxury travel concierge for TravelSoul Elite members. Help with trip planning, visa info, hotel recommendations, restaurants, and finding travel companions. Be warm, professional and helpful. Respond ONLY in ${cc.responseLanguage}, regardless of the language the user writes in. Do not mix languages.` },
            ...conversationForModel,
          ],
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      const reply = data.choices[0].message.content.trim();
      setMessages(prev => [...prev, { role: 'assistant', content: reply, lang }]);
      if (supabase && userId) {
        await supabase.from('concierge_messages').insert({ user_id: userId, role: 'assistant', content: markConciergeReply(reply, lang) });
      }
    } catch (e) {
      setError(cc.error);
    }
    setLoading(false);
  }

  return (
    <div className="ts-modal-overlay ts-concierge-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal ts-concierge-modal" style={{...MODAL_S, maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', textAlign: 'left'}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: '0.75rem'}}>
          <h2 className="ts-serif ts-modal-title" style={{background: 'linear-gradient(90deg,#b47fff,#e8c870)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', margin: 0}}>{cc.title}</h2>
          {messages.length > 0 && <button onClick={clearHistory} title={cc.clearTitle} style={{background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: 'var(--muted)', fontSize: '0.72rem', padding: '2px 7px', cursor: 'pointer', whiteSpace: 'nowrap'}}>{cc.clear}</button>}
        </div>

        <div style={{flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: '0.5rem 0', minHeight: 200, maxHeight: 400}}>
          <div style={{alignSelf: 'flex-start', background: 'rgba(180,127,255,0.12)', borderRadius: 12, padding: '0.6rem 0.9rem', fontSize: '0.88rem', color: 'var(--text)', maxWidth: '85%'}}>
            {cc.greeting}
          </div>
          {messages.map((m, i) => (
            m.role === 'assistant'
              ? <div key={i} style={{alignSelf: 'flex-start', background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '0.6rem 0.9rem', fontSize: '0.88rem', color: 'var(--text)', maxWidth: '85%'}} dangerouslySetInnerHTML={{ __html: renderMarkdown(m.lang === lang ? m.content : cc.previousOtherLang) }} />
              : <div key={i} style={{alignSelf: 'flex-end', background: 'linear-gradient(135deg,#b47fff,#e8c870)', borderRadius: 12, padding: '0.6rem 0.9rem', fontSize: '0.88rem', color: '#000', maxWidth: '85%', whiteSpace: 'pre-wrap'}}>{m.content}</div>
          ))}
          {loading && <div style={{alignSelf: 'flex-start', color: 'var(--gold)', fontSize: '0.82rem', opacity: 0.7}}>✦ ...</div>}
          {error && <div style={{color: '#f85149', fontSize: '0.82rem'}}>{error}</div>}
          <div ref={bottomRef} />
        </div>

        <div className="ts-concierge-input-row" style={{display: 'flex', gap: 8, marginTop: '0.75rem'}}>
          <input
            className="ts-input"
            style={{flex: 1, fontSize: '0.88rem'}}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder={cc.placeholder}
            disabled={loading}
          />
          <button className="ts-btn-gold-full" style={{padding: '0.5rem 1rem', whiteSpace: 'nowrap'}} onClick={sendMessage} disabled={loading || !input.trim()}>
            {cc.send}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TravelRoomCreateModal ─────────────────────────────────────────────────────
function normalizeTravelRoomCode(value) {
  const code = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  if (/^TS[A-Z0-9]{5}$/.test(code)) return `TS-${code.slice(2)}`;
  return code;
}

async function copyTextToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {}
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch (e) {
    return false;
  }
}

const ROOM_COPY = {
  bg: {
    newRoom: '🚪 Нова стая',
    roomName: 'Име на стаята',
    destination: 'Дестинация',
    maxMembers: n => `Макс. участници: ${n}`,
    maxMembersPlain: 'Макс. участници',
    createRoom: 'Създай стая',
    createRoomError: 'Грешка при създаване',
    messageSendError: 'Съобщението не беше изпратено. Опитай пак.',
    missingRoomFields: 'Моля попълни името и дестинацията',
    sessionExpired: 'Сесията изтече. Влез отново.',
    copyCode: 'Копирай кода',
    copy: 'Копирай',
    noMessages: 'Все още няма съобщения',
    typeMessage: 'Напиши съобщение…',
    send: 'Изпрати',
    loading: 'Зарежда…',
    host: 'домакин',
    traveler: 'Пътешественик',
    dates: 'Дати',
    inviteCode: 'Код за покана',
    roomsTab: '🚪 Стаи',
    joinByCode: 'С код',
    enterCode: 'Въведи код',
    join: 'Присъедини се',
    checkingCodeError: 'Грешка при проверка на кода',
    invalidCode: 'Невалиден код',
    joinRoomError: 'Грешка при влизане в стаята',
    noRooms: 'Няма стаи. Създай или влез с код!',
  },
  en: {
    newRoom: '🚪 New room',
    roomName: 'Room name',
    destination: 'Destination',
    maxMembers: n => `Max members: ${n}`,
    maxMembersPlain: 'Max members',
    createRoom: 'Create room',
    createRoomError: 'Error creating room',
    messageSendError: 'Message failed to send. Try again.',
    missingRoomFields: 'Please fill name and destination',
    sessionExpired: 'Session expired. Please log in again.',
    copyCode: 'Copy code',
    copy: 'Copy',
    noMessages: 'No messages yet',
    typeMessage: 'Type a message…',
    send: 'Send',
    loading: 'Loading…',
    host: 'host',
    traveler: 'Traveler',
    dates: 'Dates',
    inviteCode: 'Invite code',
    roomsTab: '🚪 Rooms',
    joinByCode: 'Join by code',
    enterCode: 'Enter code',
    join: 'Join',
    checkingCodeError: 'Error checking code',
    invalidCode: 'Invalid code',
    joinRoomError: 'Error joining room',
    noRooms: 'No rooms yet. Create one or join by code!',
  },
  es: {
    newRoom: '🚪 Nueva sala',
    roomName: 'Nombre de la sala',
    destination: 'Destino',
    maxMembers: n => `Máx. miembros: ${n}`,
    maxMembersPlain: 'Máx. miembros',
    createRoom: 'Crear sala',
    createRoomError: 'Error al crear la sala',
    messageSendError: 'El mensaje no se pudo enviar. Inténtalo de nuevo.',
    missingRoomFields: 'Completa el nombre y el destino',
    sessionExpired: 'La sesión ha caducado. Inicia sesión de nuevo.',
    copyCode: 'Copiar código',
    copy: 'Copiar',
    noMessages: 'Aún no hay mensajes',
    typeMessage: 'Escribe un mensaje…',
    send: 'Enviar',
    loading: 'Cargando…',
    host: 'anfitrión',
    traveler: 'Viajero',
    dates: 'Fechas',
    inviteCode: 'Código de invitación',
    roomsTab: '🚪 Salas',
    joinByCode: 'Con código',
    enterCode: 'Introducir código',
    join: 'Unirse',
    checkingCodeError: 'Error al comprobar el código',
    invalidCode: 'Código no válido',
    joinRoomError: 'Error al unirse a la sala',
    noRooms: 'Aún no hay salas. ¡Crea una o únete con código!',
  },
  fr: {
    newRoom: '🚪 Nouvelle salle',
    roomName: 'Nom de la salle',
    destination: 'Destination',
    maxMembers: n => `Membres max. : ${n}`,
    maxMembersPlain: 'Membres max.',
    createRoom: 'Créer une salle',
    createRoomError: 'Erreur lors de la création de la salle',
    messageSendError: "Le message n'a pas pu être envoyé. Réessaie.",
    missingRoomFields: 'Renseignez le nom et la destination',
    sessionExpired: 'La session a expiré. Connectez-vous à nouveau.',
    copyCode: 'Copier le code',
    copy: 'Copier',
    noMessages: 'Aucun message pour le moment',
    typeMessage: 'Écrire un message…',
    send: 'Envoyer',
    loading: 'Chargement…',
    host: 'hôte',
    traveler: 'Voyageur',
    dates: 'Dates',
    inviteCode: 'Code d’invitation',
    roomsTab: '🚪 Salles',
    joinByCode: 'Par code',
    enterCode: 'Saisir le code',
    join: 'Rejoindre',
    checkingCodeError: 'Erreur lors de la vérification du code',
    invalidCode: 'Code non valide',
    joinRoomError: 'Erreur lors de l’accès à la salle',
    noRooms: 'Aucune salle pour le moment. Créez-en une ou rejoignez avec un code !',
  },
  de: {
    newRoom: '🚪 Neuer Raum',
    roomName: 'Raumname',
    destination: 'Reiseziel',
    maxMembers: n => `Max. Mitglieder: ${n}`,
    maxMembersPlain: 'Max. Mitglieder',
    createRoom: 'Raum erstellen',
    createRoomError: 'Fehler beim Erstellen des Raums',
    messageSendError: 'Nachricht konnte nicht gesendet werden. Versuch es erneut.',
    missingRoomFields: 'Bitte Name und Reiseziel ausfüllen',
    sessionExpired: 'Die Sitzung ist abgelaufen. Bitte melde dich erneut an.',
    copyCode: 'Code kopieren',
    copy: 'Kopieren',
    noMessages: 'Noch keine Nachrichten',
    typeMessage: 'Nachricht schreiben…',
    send: 'Senden',
    loading: 'Wird geladen…',
    host: 'Gastgeber',
    traveler: 'Reisender',
    dates: 'Daten',
    inviteCode: 'Einladungscode',
    roomsTab: '🚪 Räume',
    joinByCode: 'Mit Code',
    enterCode: 'Code eingeben',
    join: 'Beitreten',
    checkingCodeError: 'Fehler beim Prüfen des Codes',
    invalidCode: 'Ungültiger Code',
    joinRoomError: 'Fehler beim Beitreten zum Raum',
    noRooms: 'Noch keine Räume. Erstelle einen Raum oder tritt per Code bei!',
  },
  el: {
    newRoom: '🚪 Νέα αίθουσα',
    roomName: 'Όνομα αίθουσας',
    destination: 'Προορισμός',
    maxMembers: n => `Μέγ. μέλη: ${n}`,
    maxMembersPlain: 'Μέγ. μέλη',
    createRoom: 'Δημιουργία αίθουσας',
    createRoomError: 'Σφάλμα δημιουργίας αίθουσας',
    messageSendError: 'Το μήνυμα δεν στάλθηκε. Δοκίμασε ξανά.',
    missingRoomFields: 'Συμπλήρωσε όνομα και προορισμό',
    sessionExpired: 'Η συνεδρία έληξε. Συνδέσου ξανά.',
    copyCode: 'Αντιγραφή κωδικού',
    copy: 'Αντιγραφή',
    noMessages: 'Δεν υπάρχουν ακόμη μηνύματα',
    typeMessage: 'Γράψε μήνυμα…',
    send: 'Αποστολή',
    loading: 'Φόρτωση…',
    host: 'οικοδεσπότης',
    traveler: 'Ταξιδιώτης',
    dates: 'Ημερομηνίες',
    inviteCode: 'Κωδικός πρόσκλησης',
    roomsTab: '🚪 Αίθουσες',
    joinByCode: 'Με κωδικό',
    enterCode: 'Εισαγωγή κωδικού',
    join: 'Συμμετοχή',
    checkingCodeError: 'Σφάλμα ελέγχου κωδικού',
    invalidCode: 'Μη έγκυρος κωδικός',
    joinRoomError: 'Σφάλμα συμμετοχής στην αίθουσα',
    noRooms: 'Δεν υπάρχουν ακόμη αίθουσες. Δημιούργησε μία ή μπες με κωδικό!',
  },
  it: {
    newRoom: '🚪 Nuova stanza',
    roomName: 'Nome della stanza',
    destination: 'Destinazione',
    maxMembers: n => `Membri max: ${n}`,
    maxMembersPlain: 'Membri max',
    createRoom: 'Crea stanza',
    createRoomError: 'Errore nella creazione della stanza',
    messageSendError: 'Impossibile inviare il messaggio. Riprova.',
    missingRoomFields: 'Compila nome e destinazione',
    sessionExpired: 'Sessione scaduta. Accedi di nuovo.',
    copyCode: 'Copia codice',
    copy: 'Copia',
    noMessages: 'Ancora nessun messaggio',
    typeMessage: 'Scrivi un messaggio…',
    send: 'Invia',
    loading: 'Caricamento…',
    host: 'host',
    traveler: 'Viaggiatore',
    dates: 'Date',
    inviteCode: 'Codice invito',
    roomsTab: '🚪 Stanze',
    joinByCode: 'Con codice',
    enterCode: 'Inserisci codice',
    join: 'Unisciti',
    checkingCodeError: 'Errore nel controllo del codice',
    invalidCode: 'Codice non valido',
    joinRoomError: 'Errore nell’accesso alla stanza',
    noRooms: 'Ancora nessuna stanza. Creane una o entra con un codice!',
  },
};

function getRoomCopy(lang) {
  return ROOM_COPY[lang] || ROOM_COPY.en;
}

function TravelRoomCreateModal({ lang, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [maxMembers, setMaxMembers] = useState(6);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate() {
    const roomCopy = getRoomCopy(lang);
    if (!name.trim() || !destination.trim()) {
      setError(roomCopy.missingRoomFields);
      return;
    }
    setCreating(true);
    setError('');
    const { userId } = await getAuthTokenSafe({ forceFresh: true });
    if (!userId) {
      setError(roomCopy.sessionExpired);
      setCreating(false);
      return;
    }
    const code = 'TS-' + Math.random().toString(36).substr(2, 5).toUpperCase();
    const room = {
      name: name.trim(),
      destination: destination.trim(),
      creator_id: userId,
      invite_code: code,
      max_members: maxMembers,
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    };
    try {
      const res = await sbFetch('POST', 'travel_rooms', '', room, { Prefer: 'return=representation' });
      await ensureSbWriteOk(res, 'travel room create');
      const [created] = await res.json();
      if (!created?.id) throw new Error('Room was created without an id');
      const memberRes = await sbFetch('POST', 'room_members', '', { room_id: created.id, user_id: userId }, { Prefer: 'return=minimal' });
      await ensureSbWriteOk(memberRes, 'travel room membership create');
      onCreate && onCreate(created);
      onClose();
    } catch (err) {
      console.error('[TravelRoom] create failed:', err);
      const detail = err?.message || '';
      setError(roomCopy.createRoomError + (detail ? `: ${detail}` : ''));
      setCreating(false);
    }
  }

  const L = getRoomCopy(lang);

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth: 380}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title">{L.newRoom}</h2>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <input className="ts-input" placeholder={L.roomName} value={name} onChange={e=>setName(e.target.value)} maxLength={60} />
          <input className="ts-input" placeholder={L.destination} value={destination} onChange={e=>setDestination(e.target.value)} maxLength={80} />
          <input className="ts-input" type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} />
          <input className="ts-input" type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} />
          <label className="ts-filter-label">{L.maxMembers(maxMembers)}<input type="range" className="ts-range" min="2" max="20" value={maxMembers} onChange={e=>setMaxMembers(+e.target.value)}/></label>
          {error && <p style={{color:'#f85149',fontSize:'0.82rem',margin:0}}>{error}</p>}
          <button className="ts-btn-gold-full" onClick={handleCreate} disabled={creating}>{creating ? '…' : L.createRoom}</button>
        </div>
      </div>
    </div>
  );
}

// ── TravelRoomModal ───────────────────────────────────────────────────────────
function TravelRoomModal({ room, lang, onClose }) {
  const [tab, setTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [copied, setCopied] = useState(false);
  const bottomRef = useRef(null);
  const roomEmojiRef = useRef(null);
  const roomInputRef = useRef(null);
  const [showRoomEmoji, setShowRoomEmoji] = useState(false);
  const { userId } = getAuthToken();
  const roomCopy = getRoomCopy(lang);

  async function loadMessages() {
    let data;
    try { data = await sbFetch('GET', 'room_messages', `room_id=eq.${room.id}&order=created_at.asc&limit=100`); } catch (e) { return; }
    if (Array.isArray(data)) setMessages(data);
  }

  async function loadMembers() {
    let data;
    try { data = await sbFetch('GET', 'room_members', `room_id=eq.${room.id}&select=user_id`); } catch (e) { return; }
    if (Array.isArray(data) && data.length) {
      const ids = data.map(m => m.user_id).join(',');
      let profiles;
      try { profiles = await sbFetch('GET', 'profiles', `id=in.(${ids})&select=id,name,photo_url`); } catch (e) { return; }
      setMembers(Array.isArray(profiles) ? profiles : []);
    }
  }

  useEffect(() => {
    loadMessages();
    loadMembers();
    const interval = setInterval(loadMessages, 15000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 0) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, tab]);

  async function sendMessage() {
    if (!msgText.trim() || sending) return;
    setSending(true);
    setSendError(false);
    const textToSend = msgText.trim().slice(0, 500);
    try {
      // Audit finding: sbFetch's POST path returns the raw Response and never
      // throws on non-2xx, so a failed send previously fell through silently —
      // the input still cleared and the room looked like it sent fine. Now we
      // check res.ok ourselves and keep the text + show an error on failure.
      const res = await sbFetch('POST', 'room_messages', '', { room_id: room.id, user_id: userId, message: textToSend }, { Prefer: 'return=minimal' });
      if (!res?.ok) throw new Error('room_messages send failed: ' + res?.status);
      setMsgText('');
      await loadMessages();
    } catch (e) {
      console.error('[TravelRoom] sendMessage failed:', e.message);
      setSendError(true);
    }
    setSending(false);
  }

  async function copyCode() {
    await copyTextToClipboard(room.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function insertRoomEmoji(emoji) {
    const el = roomInputRef.current;
    if (!el) { setMsgText(p => p + emoji); setShowRoomEmoji(false); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setMsgText(msgText.slice(0, start) + emoji + msgText.slice(end));
    setShowRoomEmoji(false);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + emoji.length, start + emoji.length); }, 0);
  }
  useEffect(() => {
    if (!showRoomEmoji) return;
    function handleOutside(e) { if (roomEmojiRef.current && !roomEmojiRef.current.contains(e.target)) setShowRoomEmoji(false); }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showRoomEmoji]);

  const tabLabels = ({
    bg: ['💬 Чат', '👥 Членове', 'ℹ️ Инфо'],
    en: ['💬 Chat', '👥 Members', 'ℹ️ Info'],
    es: ['💬 Chat', '👥 Miembros', 'ℹ️ Info'],
    fr: ['💬 Chat', '👥 Membres', 'ℹ️ Info'],
    de: ['💬 Chat', '👥 Mitglieder', 'ℹ️ Info'],
    el: ['💬 Chat', '👥 Μέλη', 'ℹ️ Πληροφορίες'],
    it: ['💬 Chat', '👥 Membri', 'ℹ️ Info'],
  })[lang] || ['💬 Chat', '👥 Members', 'ℹ️ Info'];

  const isMobile = window.innerWidth <= 768;
  const modalStyle = isMobile ? {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100vw',
    height: '70dvh',
    background: '#0d1117',
    borderRadius: '20px 20px 0 0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9500,
  } : {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'min(600px, 80vw)',
    height: '70vh',
    background: '#0d1117',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9500,
  };
  const headerStyle = {
    flexShrink: 0,
    padding: '16px',
    borderBottom: '1px solid #30363d',
    position: 'relative',
  };
  const messagesStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
    minHeight: 0,
    WebkitOverflowScrolling: 'touch',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };
  const inputBarStyle = {
    flexShrink: 0,
    padding: '12px',
    paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : '12px',
    borderTop: '1px solid #30363d',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    background: '#0d1117',
  };

  return (
    <>
      {/* Backdrop */}
      <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.7)',zIndex:9499}} onClick={onClose} />

      {/* Bottom-sheet modal */}
      <div style={modalStyle}>
        {/* Header: title + tabs */}
        <div style={headerStyle}>
          <button className="ts-modal-close" style={{position:'absolute',top:12,right:12}} onClick={onClose}>✕</button>
          <h2 className="ts-serif" style={{background:'linear-gradient(90deg,#b47fff,#e8c870)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',margin:'0 0 2px',fontSize:'1.1rem'}}>{room.name}</h2>
          <p style={{color:'var(--muted)',fontSize:'0.78rem',margin:'0 0 10px'}}>📍 {room.destination}</p>
          <button className="ts-room-code-pill" onClick={copyCode} title={roomCopy.copyCode}>
            <span>{room.invite_code}</span>
            <strong>{copied ? '✓' : roomCopy.copy}</strong>
          </button>
          <div className="ts-sub-tabs" style={{marginBottom:0}}>
            {tabLabels.map((l,i) => <button key={i} className={`ts-sub-tab${tab===i?' active':''}`} onClick={()=>setTab(i)}>{l}</button>)}
          </div>
        </div>

        {/* Chat tab: messages + input bar as siblings */}
        {tab === 0 && (
          <>
            <div style={messagesStyle}>
              {messages.length === 0 && <p style={{color:'var(--muted)',fontSize:'0.82rem',textAlign:'center',marginTop:40}}>{roomCopy.noMessages}</p>}
              {messages.map((m,i) => (
                <div key={i} style={{alignSelf:m.user_id===userId?'flex-end':'flex-start',background:m.user_id===userId?'linear-gradient(135deg,#b47fff,#e8c870)':'rgba(255,255,255,0.07)',borderRadius:12,padding:'0.5rem 0.8rem',maxWidth:'80%',fontSize:'0.85rem',color:m.user_id===userId?'#000':'var(--text)',whiteSpace:'pre-wrap'}}>
                  {m.message}
                  <span style={{fontSize:10,opacity:0.6,display:'block',marginTop:3,textAlign:m.user_id===userId?'right':'left'}}>{formatNotifTime(m.created_at)}</span>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {sendError && <div style={{color:'#f85149',fontSize:'0.78rem',padding:'0 12px 4px'}}>{roomCopy.messageSendError}</div>}
            <div style={inputBarStyle}>
              <div style={{position:'relative',flex:1,minWidth:0,display:'flex',alignItems:'center',gap:'6px'}}>
                <button onClick={()=>setShowRoomEmoji(p=>!p)} style={{fontSize:'20px',background:'none',border:'none',cursor:'pointer',padding:'4px 8px',flexShrink:0,lineHeight:1}}>😊</button>
                <input ref={roomInputRef} className="ts-input" style={{flex:1,minWidth:0,padding:'10px 14px',fontSize:'14px'}} value={msgText} onChange={e=>setMsgText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMessage()} placeholder={roomCopy.typeMessage} disabled={sending} maxLength={500} />
                {showRoomEmoji && (
                  <div ref={roomEmojiRef} style={{position:'absolute',bottom:'60px',left:0,background:'#1c2128',borderRadius:'12px',padding:'12px',display:'grid',gridTemplateColumns:'repeat(8, 1fr)',gap:'4px',zIndex:100,maxHeight:'200px',overflowY:'auto',boxShadow:'0 8px 24px rgba(0,0,0,0.6)'}}>
                    {['😊','😂','❤️','🔥','✈️','🌍','🏖️','⛰️','🎉','👋','😍','🤣','💫','🌟','😎','🥰','😘','💕','🎒','🗺️','🌅','🏝️','🍜','🍷','🎭','🌺','🦋','🐬','🌈','☀️','🌙','⭐','👍','👏','🙏','💪','🤝','🫂','💌','📸','🎵','🎶','🏄','🚀','🎯','💯','🔮','🌊'].map((emoji,i)=>(
                      <button key={i} onClick={()=>insertRoomEmoji(emoji)} style={{background:'none',border:'none',fontSize:'1.4rem',cursor:'pointer',padding:'4px',borderRadius:6,lineHeight:1}} onMouseEnter={e=>e.target.style.background='#21262d'} onMouseLeave={e=>e.target.style.background='none'}>{emoji}</button>
                    ))}
                  </div>
                )}
              </div>
              <button className="ts-btn-gold-full" style={{flexShrink:0,padding:'10px 16px',fontSize:'14px',whiteSpace:'nowrap',width:'auto'}} onClick={sendMessage} disabled={sending||!msgText.trim()}>{roomCopy.send}</button>
            </div>
          </>
        )}

        {/* Members tab */}
        {tab === 1 && (
          <div style={{...messagesStyle, flexDirection:'column', gap:10}}>
            {members.length === 0 && <p style={{color:'var(--muted)',fontSize:'0.82rem',textAlign:'center',marginTop:30}}>{roomCopy.loading}</p>}
            {members.map(m => (
              <div key={m.id} style={{display:'flex',alignItems:'center',gap:10}}>
                {getProfilePhotoUrl(m.photo_url, '') ? <img src={getProfilePhotoUrl(m.photo_url, '')} alt={m.name} style={{width:36,height:36,borderRadius:'50%',objectFit:'cover'}} /> : <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(180,127,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1rem'}}>👤</div>}
                <span style={{fontSize:'0.9rem',color:'var(--text)'}}>{m.name||roomCopy.traveler}</span>
                {m.id === room.creator_id && <span style={{fontSize:'0.7rem',background:'rgba(232,200,112,0.1)',border:'1px solid rgba(232,200,112,0.25)',borderRadius:10,padding:'1px 7px',color:'var(--gold)'}}>{roomCopy.host}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Info tab */}
        {tab === 2 && (
          <div style={{...messagesStyle, flexDirection:'column', gap:10}}>
            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'0.7rem 1rem'}}>
              <p style={{fontSize:'0.72rem',color:'var(--muted)',margin:'0 0 2px'}}>{roomCopy.destination}</p>
              <p style={{margin:0,color:'var(--text)'}}>{room.destination}</p>
            </div>
            {(room.start_date || room.end_date) && (
              <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'0.7rem 1rem'}}>
                <p style={{fontSize:'0.72rem',color:'var(--muted)',margin:'0 0 2px'}}>{roomCopy.dates}</p>
                <p style={{margin:0,color:'var(--text)'}}>{room.start_date||'—'} → {room.end_date||'—'}</p>
              </div>
            )}
            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'0.7rem 1rem'}}>
              <p style={{fontSize:'0.72rem',color:'var(--muted)',margin:'0 0 4px'}}>{roomCopy.inviteCode}</p>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontFamily:'monospace',fontSize:'1.2rem',color:'var(--gold)',letterSpacing:3,fontWeight:700}}>{room.invite_code}</span>
                <button className="ts-btn-ghost-sm" onClick={copyCode}>{copied ? '✓' : roomCopy.copy}</button>
              </div>
            </div>
            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'0.7rem 1rem'}}>
              <p style={{fontSize:'0.72rem',color:'var(--muted)',margin:'0 0 2px'}}>{roomCopy.maxMembersPlain}</p>
              <p style={{margin:0,color:'var(--text)'}}>{room.max_members}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Journeys Tab (with Leaderboard 4th tab) ────────────────────────────────────
function JourneysTab({ t, lang, plan, onNeedPremium }) {
  const [tab, setTab] = useState(0);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showDiary, setShowDiary] = useState(false);
  const [showConcierge, setShowConcierge] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [showJoinInput, setShowJoinInput] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joiningRoom, setJoiningRoom] = useState(false);
  const [savedTrips, setSavedTrips] = useState(() => {
    try { return JSON.parse(localStorage.getItem('travelsoul_saved_trips') || '[]'); } catch { return []; }
  });
  const ft = FT[lang] || FT.en;
  const roomCopy = getRoomCopy(lang);

  // Load trips from Supabase on mount — syncs across devices
  useEffect(() => {
    if (!supabase) return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data: dbTrips } = await supabase
        .from('trips')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (dbTrips?.length) {
        const mapped = dbTrips.map(row => ({
          id: row.id,
          name: row.title,
          location: row.destination,
          destination: row.destination,
          image: 'https://images.unsplash.com/photo-1476610182048-b716b8518aae?w=400&q=80',
          date: row.start_date || (row.created_at || '').slice(0, 10),
          members: row.group_size,
          aiPlan: row.plan,
          savedAt: row.created_at,
          status: row.status || 'planned',
        }));
        setSavedTrips(mapped);
        localStorage.setItem('travelsoul_saved_trips', JSON.stringify(mapped));
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRooms() {
    const { userId } = await getAuthTokenSafe();
    if (!userId) return;
    let memberships;
    try { memberships = await sbFetch('GET', 'room_members', `user_id=eq.${userId}&select=room_id`); } catch (e) { return; }
    if (!Array.isArray(memberships) || !memberships.length) { setRooms([]); return; }
    const ids = memberships.map(m => m.room_id).join(',');
    let data;
    try { data = await sbFetch('GET', 'travel_rooms', `id=in.(${ids})&order=created_at.desc`); } catch (e) { return; }
    if (Array.isArray(data)) setRooms(data);
  }

  useEffect(() => { loadRooms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleJoin() {
    const code = normalizeTravelRoomCode(joinCode);
    if (!code) return;
    setJoiningRoom(true);
    setJoinError('');
    const { userId } = await getAuthTokenSafe({ forceFresh: true });
    if (!userId) { setJoinError(roomCopy.sessionExpired); setJoiningRoom(false); return; }
    let found;
    try { found = await sbFetch('GET', 'travel_rooms', `invite_code=eq.${encodeURIComponent(code)}&limit=1`); } catch (e) { setJoinError(roomCopy.checkingCodeError); setJoiningRoom(false); return; }
    if (!Array.isArray(found) || !found.length) {
      setJoinError(roomCopy.invalidCode);
      setJoiningRoom(false);
      return;
    }
    const room = found[0];
    let existing;
    try { existing = await sbFetch('GET', 'room_members', `room_id=eq.${room.id}&user_id=eq.${userId}&limit=1`); } catch (e) { existing = []; }
    try {
      if (!Array.isArray(existing) || !existing.length) {
        const memberRes = await sbFetch('POST', 'room_members', '', { room_id: room.id, user_id: userId }, { Prefer: 'return=minimal' });
        await ensureSbWriteOk(memberRes, 'travel room join', [409]);
      }
    } catch (err) {
      if (!isDuplicateWriteError(err)) {
        console.error('[TravelRoom] join failed:', err);
        setJoinError(roomCopy.joinRoomError);
        setJoiningRoom(false);
        return;
      }
    }
    await loadRooms();
    setJoinCode('');
    setShowJoinInput(false);
    setActiveRoom(room);
    setJoiningRoom(false);
  }

  function deleteJourney(id) {
    const updated = savedTrips.filter(j => j.id !== id);
    setSavedTrips(updated);
    localStorage.setItem('travelsoul_saved_trips', JSON.stringify(updated));
    if (supabase) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) supabase.from('trips').delete().eq('id', id).eq('user_id', session.user.id);
      });
    }
  }
  const tabKeys = ['upcoming','planned','completed','leaderboard','stats','badges','rooms'];

  // Show only real user-generated trips (no static demo trips)
  const plannedItems = [...savedTrips];
  const baseItems = { upcoming: [], planned: plannedItems, completed: [] };
  const items = tab < 3 ? baseItems[tabKeys[tab]] : null;
  const roomsLabel = roomCopy.roomsTab;
  const allTabs = [...t.journeys.tabs, t.journeys.statsTab, ft.badgesTab, roomsLabel];

  return (
    <div className="ts-tab-content">
      {showPlanner && <AiTripPlannerModal lang={lang} onClose={()=>{ setShowPlanner(false); try { setSavedTrips(JSON.parse(localStorage.getItem('travelsoul_saved_trips')||'[]')); } catch {} }} />}
      {showSplit && <SplitCostsModal lang={lang} onClose={()=>setShowSplit(false)} />}
      {showDiary && <TravelDiaryModal lang={lang} plan={plan} onClose={()=>setShowDiary(false)} onNeedPremium={onNeedPremium} />}
      {showConcierge && <VipConciergeModal lang={lang} plan={plan} onClose={()=>setShowConcierge(false)} onNeedPremium={onNeedPremium} />}
      {selectedJourney && <JourneyDetailModal journey={selectedJourney} lang={lang} onClose={()=>setSelectedJourney(null)} onDelete={selectedJourney.aiPlan ? deleteJourney : null} />}
      {showCreateRoom && <TravelRoomCreateModal lang={lang} onClose={()=>setShowCreateRoom(false)} onCreate={r=>{setRooms(prev=>[r,...prev.filter(room=>room.id!==r.id)]);setTab(6);setActiveRoom(r);}} />}
      {activeRoom && <TravelRoomModal room={activeRoom} lang={lang} onClose={()=>setActiveRoom(null)} />}
      <div style={{display:'flex',alignItems:'center',gap:'0.5rem',flexWrap:'wrap',marginBottom:'0.5rem'}}>
        <h2 className="ts-tab-title ts-serif" style={{margin:0}}>{t.journeys.title}</h2>
        <button className="ts-hotel-btn" onClick={()=>plan==='free'?onNeedPremium('aiPlanner'):setShowPlanner(true)}>{ft.aiPlannerBtn}</button>
        <button className="ts-hotel-btn" onClick={()=>plan==='free'?onNeedPremium('splitCosts'):setShowSplit(true)}>{ft.splitCostsBtn}</button>
        <button className="ts-hotel-btn" onClick={()=>plan==='elite'?setShowDiary(true):onNeedPremium('diary')}>✈ {lang==='bg'?'Дневник':lang==='es'?'Diario':lang==='fr'?'Journal':lang==='de'?'Tagebuch':lang==='el'?'Ημερολόγιο':lang==='it'?'Diario':'Diary'}</button>
        <button className="ts-hotel-btn" style={{background:'linear-gradient(135deg,rgba(180,127,255,0.18),rgba(232,200,112,0.18))',border:'1px solid rgba(180,127,255,0.4)'}} onClick={()=>setShowConcierge(true)}>💎 {lang==='bg'?'ВИП Личен асистент':lang==='es'?'Concierge VIP':lang==='fr'?'Conciergerie VIP':lang==='de'?'VIP-Concierge':lang==='el'?'VIP Concierge':lang==='it'?'Concierge VIP':'VIP Concierge'}</button>
        {plan==='elite' && <button className="ts-hotel-btn" style={{background:'linear-gradient(135deg,rgba(120,200,120,0.15),rgba(180,127,255,0.15))',border:'1px solid rgba(120,200,120,0.4)'}} onClick={()=>setShowCreateRoom(true)}>🚪 {roomCopy.createRoom}</button>}
        <button className="ts-btn-ghost-sm" style={{fontSize:'0.78rem'}} onClick={()=>{setShowJoinInput(v=>!v);setJoinError('');setJoinCode('');}}>🔑 {roomCopy.joinByCode}</button>
      </div>
      {showJoinInput && (
        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:'0.5rem',flexWrap:'wrap'}}>
          <input className="ts-input" style={{width:'min(160px, 40vw)',letterSpacing:2,textTransform:'uppercase',fontFamily:'monospace',fontSize:'clamp(10px, 2.5vw, 14px)'}} value={joinCode} onChange={e=>setJoinCode(normalizeTravelRoomCode(e.target.value))} placeholder={roomCopy.enterCode} maxLength={10} onKeyDown={e=>e.key==='Enter'&&handleJoin()} autoComplete="off" />
          <button className="ts-btn-gold-full" style={{padding:'0.45rem 1rem'}} onClick={handleJoin} disabled={joiningRoom||!joinCode.trim()}>{joiningRoom?'…':roomCopy.join}</button>
          {joinError && <span style={{color:'#f85149',fontSize:'0.8rem'}}>{joinError}</span>}
        </div>
      )}
      <div className="ts-sub-tabs">
        {allTabs.map((lbl,i)=>(
          <button key={i} className={`ts-sub-tab ${tab===i?'active':''}`} onClick={()=>setTab(i)}>{lbl}</button>
        ))}
      </div>
      {tab === 6 ? (
        rooms.length === 0 ? (
          <div style={{textAlign:'center',padding:'2.5rem 0'}}>
            <p style={{fontSize:'2.5rem',marginBottom:'0.5rem'}}>🚪</p>
            <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:'1rem'}}>{roomCopy.noRooms}</p>
            {plan==='elite' && <button className="ts-btn-gold-full" style={{maxWidth:220,margin:'0 auto'}} onClick={()=>setShowCreateRoom(true)}>🚪 {roomCopy.createRoom}</button>}
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {rooms.map(r => (
              <div key={r.id} className="ts-room-card" onClick={()=>setActiveRoom(r)}>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:'0 0 2px',fontWeight:600,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.name}</p>
                  <p style={{margin:0,color:'var(--muted)',fontSize:'0.82rem'}}>📍 {r.destination}</p>
                  {r.start_date && <p style={{margin:0,color:'var(--muted)',fontSize:'0.75rem'}}>🗓 {r.start_date}</p>}
                </div>
                <span style={{fontFamily:'monospace',background:'rgba(232,200,112,0.1)',border:'1px solid rgba(232,200,112,0.25)',borderRadius:6,padding:'2px 8px',fontSize:'0.75rem',color:'var(--gold)',flexShrink:0}}>{r.invite_code}</span>
              </div>
            ))}
          </div>
        )
      ) : tab === 5 ? (
        <TravelBadges ft={ft} badgesT={t.badges} />
      ) : tab === 4 ? (
        <TravelStats t={t} lang={lang} plan={plan} onNeedPremium={()=>onNeedPremium('stats')} />
      ) : tab === 3 ? (
        <LeaderboardSection t={t} />
      ) : items.length === 0 ? (
        <p className="ts-empty-msg">{t.journeys.empty}</p>
      ) : (
        <div className="ts-journeys-list">
          {items.map(j=>(
            <div key={j.id} className="ts-journey-item" style={{cursor:'pointer'}} onClick={()=>setSelectedJourney(j)}>
              <img src={j.image} alt={localizeKnownTripContent(j.name, lang)} className="ts-journey-img" />
              <div className="ts-journey-info">
                <p className="ts-journey-name ts-serif">{localizeKnownTripContent(j.name, lang)}</p>
                <p className="ts-journey-loc">📍 {localizeKnownTripContent(j.location, lang)}</p>
                <p className="ts-journey-date">{t.journeys.departs}: <strong>{j.date}</strong></p>
                <p className="ts-journey-members">👥 {j.members} {ft.travelers||t.extras?.travelers||'travelers'}</p>
              </div>
              <span className={`ts-journey-badge ${tabKeys[tab]}`}>{t.journeys.tabs[tab]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function isAndroidCapacitorRuntime() {
  return Capacitor?.getPlatform?.() === 'android'
    || (typeof window !== 'undefined' && window?.Capacitor?.platform === 'android');
}

async function requestVideoCallMediaAccess(lang = 'en') {
  if (!isAndroidCapacitorRuntime()) return { ok: true };
  const deniedMessage = lang==='bg'?'Разреши достъп до Камера и Микрофон от Android настройките на приложението TravelSoul, за да използваш видео разговори.':lang==='es'?'Permite el acceso a la cámara y el micrófono en los ajustes de la app TravelSoul en Android para usar videollamadas.':lang==='fr'?'Veuillez autoriser la caméra et le microphone dans les paramètres Android de l\'application TravelSoul pour utiliser les appels vidéo.':lang==='de'?'Erlaube Kamera und Mikrofon in den Android-App-Einstellungen von TravelSoul, um Videoanrufe nutzen zu können.':lang==='el'?'Επίτρεψε την κάμερα και το μικρόφωνο στις ρυθμίσεις της εφαρμογής Android του TravelSoul για να χρησιμοποιήσεις βιντεοκλήσεις.':lang==='it'?'Consenti fotocamera e microfono nelle impostazioni dell\'app Android di TravelSoul per usare le videochiamate.':'Please allow Camera and Microphone in the Android app permissions for TravelSoul to use video calls.';
  if (!navigator?.mediaDevices?.getUserMedia) {
    return { ok: false, message: deniedMessage };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    stream.getTracks().forEach(track => track.stop());
    return { ok: true };
  } catch (_) {
    return { ok: false, message: deniedMessage };
  }
}

// ── VideoCallOverlay ──────────────────────────────────────────────────────────
function VideoCallOverlay({ profile, t, onEnd, currentUserId, notifyPeer = true }) {
  const [showChat, setShowChat] = useState(false);
  const [chatMsg, setChatMsg] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [iframeSrc, setIframeSrc] = useState('');
  const callEndSentRef = useRef(false);

  const callerName = (() => {
    try { const u = JSON.parse(localStorage.getItem('travelsoul_user')||'{}'); return [u.firstName,u.lastName].filter(Boolean).join(' ')||'TravelSoul User'; } catch(e){return 'TravelSoul User';}
  })();

  const rawName = [currentUserId, profile?.id].filter(Boolean).sort().join('').replace(/[^a-z0-9]/gi, '').toLowerCase().slice(0, 35);
  const roomName = rawName || 'travelsoulroom';

  useEffect(() => {
    (async () => {
      try {
        const { accessToken } = await getAuthTokenSafe();
        if (!accessToken) { console.warn('Room creation failed: no auth token'); return; }
        const res = await fetch(`${API_BASE}/api/daily/room`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
          body: JSON.stringify({ roomName, callerName }),
        });
        if (res.ok) {
          const { url } = await res.json();
          setIframeSrc(url);
        }
      } catch(e) {
        console.warn('Room creation failed:', e);
      }
    })();
    if (notifyPeer && currentUserId && profile?.id && supabase) {
      try {
        getVideoCallBroadcastChannel()?.send({
          type: 'broadcast', event: 'incoming-call',
          payload: { from_user: currentUserId, to_user: profile.id, room_name: roomName, caller_name: callerName }
        });
      } catch(e) {}
    }
    return () => {
      sendCallEnded();
      if (window._dailyCallObj) {
        try { window._dailyCallObj.leave(); window._dailyCallObj.destroy(); } catch(e){}
        window._dailyCallObj = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function sendCallEnded() {
    if (callEndSentRef.current || !currentUserId || !profile?.id || !supabase) return;
    callEndSentRef.current = true;
    try {
      getVideoCallBroadcastChannel()?.send({
        type: 'broadcast', event: 'video-call-ended',
        payload: { from_user: currentUserId, to_user: profile.id, room_name: roomName }
      });
    } catch(e) {}
  }

  function handleEnd() {
    playSound('endcall');
    sendCallEnded();
    if (window._dailyCallObj) {
      try { window._dailyCallObj.leave(); window._dailyCallObj.destroy(); } catch(e){}
      window._dailyCallObj = null;
    }
    onEnd();
  }

  return (
    <div style={{position:'fixed',inset:0,zIndex:9998,background:'#000',display:'flex',flexDirection:'column',paddingBottom:'80px'}}>
      {/* Direct Daily iframe - handles camera/mic natively */}
      {iframeSrc ? (
        <iframe
          src={`${iframeSrc}&showLeaveButton=false&showFullscreenButton=false&showLocalVideo=true&showParticipantsBar=false`}
          allow="camera; microphone; autoplay; fullscreen; display-capture"
          allowFullScreen
          style={{flex:1,width:'100%',border:'none',minHeight:0}}
          title="Video Call"
        />
      ) : (
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#8b949e',fontSize:14}}>
          ⏳ Connecting...
        </div>
      )}

      {/* Top status bar */}
      <div style={{position:'absolute',top:0,left:0,right:0,padding:'12px 16px',zIndex:20,background:'linear-gradient(to bottom,rgba(0,0,0,0.7),transparent)',display:'flex',alignItems:'center',gap:10,pointerEvents:'none'}}>
        <img
          src={TRAVELSOUL_LOGO_SRC}
          alt="TravelSoul"
          style={{width:36,height:36,borderRadius:8,objectFit:'contain',background:'#0d1117',border:'1px solid rgba(201,168,76,0.55)',flexShrink:0}}
        />
        <div style={{width:36,height:36,borderRadius:'50%',overflow:'hidden',border:'2px solid #c9a84c'}}>
          {profile.photo
            ? <img src={profile.photo} alt={profile.name} style={{width:'100%',height:'100%',objectFit:'cover'}} />
            : <div style={{width:'100%',height:'100%',background:'#c9a84c',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,color:'#000'}}>{(profile.name||'?')[0]}</div>
          }
        </div>
        <div style={{color:'#fff',fontWeight:700,fontSize:15}}>{profile.name}</div>
      </div>

      {/* Bottom controls */}
      <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'20px 0 36px',zIndex:20,background:'linear-gradient(to top,rgba(0,0,0,0.85),transparent)',display:'flex',flexDirection:'column',alignItems:'center',gap:20}}>
        {showChat && (
          <div style={{width:'90%',maxWidth:400,background:'rgba(13,17,23,0.95)',borderRadius:16,padding:14,marginBottom:4}}>
            <div style={{maxHeight:120,overflowY:'auto',marginBottom:8}}>
              {chatLog.length === 0
                ? <p style={{color:'#8b949e',fontSize:12,textAlign:'center'}}>{t?.messages?.typeMsg||'Type a message...'}</p>
                : chatLog.map((m,i) => (
                    <div key={i} style={{textAlign:m.me?'right':'left',marginBottom:4}}>
                      <span style={{background:m.me?'#c9a84c':'#21262d',color:m.me?'#000':'#f0f6fc',borderRadius:10,padding:'5px 10px',fontSize:12,display:'inline-block'}}>{m.text}</span>
                    </div>
                  ))
              }
            </div>
            <div style={{display:'flex',gap:8}}>
              <input value={chatMsg} onChange={e=>setChatMsg(e.target.value)}
                onKeyDown={e=>{if(e.key==='Enter'&&chatMsg.trim()){setChatLog(l=>[...l,{text:chatMsg,me:true}]);setChatMsg('');}}}
                placeholder={t?.messages?.typeMsg||'Message...'}
                style={{flex:1,background:'#161b22',border:'1px solid #30363d',borderRadius:8,color:'#f0f6fc',padding:'7px 10px',fontSize:12,outline:'none'}}
              />
              <button onClick={()=>{if(chatMsg.trim()){setChatLog(l=>[...l,{text:chatMsg,me:true}]);setChatMsg('');}}}
                style={{background:'#c9a84c',border:'none',borderRadius:8,padding:'7px 12px',cursor:'pointer',fontWeight:700,fontSize:14,color:'#000'}}>→</button>
            </div>
          </div>
        )}
        <div style={{display:'flex',alignItems:'center',gap:24}}>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
            <button onClick={handleEnd} style={{width:68,height:68,borderRadius:'50%',background:'#f85149',border:'none',color:'#fff',fontSize:26,cursor:'pointer',boxShadow:'0 4px 20px rgba(248,81,73,0.5)'}}>
              📵
            </button>
            <span style={{color:'rgba(255,255,255,0.7)',fontSize:11}}>{t?.messages?.endCall||'End'}</span>
          </div>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
            <button onClick={()=>setShowChat(c=>!c)} style={{width:56,height:56,borderRadius:'50%',background:showChat?'rgba(201,168,76,0.3)':'rgba(255,255,255,0.15)',border:showChat?'2px solid #c9a84c':'2px solid rgba(255,255,255,0.3)',color:'#fff',fontSize:22,cursor:'pointer',backdropFilter:'blur(10px)'}}>
              💬
            </button>
            <span style={{color:'rgba(255,255,255,0.7)',fontSize:11}}>{t?.messages?.chat||'Chat'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── NEW F6+F8: Messages Tab (Translator + Group Chats) ────────────────────────
// ── Photo Lightbox ────────────────────────────────────────────────────────────
function PhotoLightbox({ photos, startIndex = 0, name, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const all = photos?.length ? photos : [];
  if (!all.length) return null;
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.95)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column'}}>
      <div onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:600,width:'100%',padding:'0 16px'}}>
        <button onClick={onClose} style={{position:'absolute',top:-48,right:16,background:'none',border:'none',color:'#fff',fontSize:28,cursor:'pointer',zIndex:1}}>✕</button>
        <div style={{textAlign:'center',color:'#c9a84c',fontSize:13,marginBottom:8,fontWeight:600}}>{name} • {idx + 1} / {all.length}</div>
        <img src={all[idx]} alt={name} style={{width:'100%',maxHeight:'70vh',objectFit:'contain',borderRadius:16,display:'block'}} />
        {all.length > 1 && (
          <div style={{display:'flex',justifyContent:'space-between',marginTop:16,gap:12}}>
            <button onClick={() => setIdx(i => (i - 1 + all.length) % all.length)} style={{flex:1,background:'#21262d',border:'1px solid #30363d',borderRadius:12,color:'#f0f6fc',fontSize:20,padding:'10px',cursor:'pointer'}}>←</button>
            <button onClick={() => setIdx(i => (i + 1) % all.length)} style={{flex:1,background:'#21262d',border:'1px solid #30363d',borderRadius:12,color:'#f0f6fc',fontSize:20,padding:'10px',cursor:'pointer'}}>→</button>
          </div>
        )}
        {all.length > 1 && (
          <div style={{display:'flex',gap:8,marginTop:12,justifyContent:'center',flexWrap:'wrap'}}>
            {all.map((p, i) => (
              <img key={i} src={p} alt="" onClick={() => setIdx(i)} style={{width:56,height:56,objectFit:'cover',borderRadius:8,cursor:'pointer',border:i===idx?'2px solid #c9a84c':'2px solid transparent',opacity:i===idx?1:0.6,transition:'all .2s'}} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function compressImageToDataUrl(file, maxDim = 640, quality = 0.78) {
  return new Promise(resolve => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
        else { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
    img.src = objectUrl;
  });
}

function RelationshipTimelineModal({ profile, msgCount, lang, plan = 'free', onClose }) {
  const labels = {
    title:   lang==='bg'?'💝 Нашата история':lang==='es'?'💝 Nuestra historia':lang==='fr'?'💝 Notre histoire':lang==='de'?'💝 Unsere Geschichte':lang==='el'?'💝 Η ιστορία μας':lang==='it'?'💝 La nostra storia':'💝 Our Story',
    met:     lang==='bg'?'Запознахме се в TravelSoul':lang==='es'?'Nos conocimos en TravelSoul':lang==='fr'?'Connectés sur TravelSoul':lang==='de'?'Auf TravelSoul verbunden':lang==='el'?'Συνδεθήκατε στο TravelSoul':lang==='it'?'Connessi su TravelSoul':'Connected on TravelSoul',
    msgs:    lang==='bg'?'Съобщения':lang==='es'?'Mensajes':lang==='fr'?'Messages':lang==='de'?'Nachrichten':lang==='el'?'Μηνύματα':lang==='it'?'Messaggi':'Messages',
    dreams:  lang==='bg'?'Мечтани дестинации':lang==='es'?'Destinos soñados':lang==='fr'?'Destinations rêvées':lang==='de'?'Traumziele':lang==='el'?'Προορισμοί των ονείρων':lang==='it'?'Destinazioni da sogno':'Dream Destinations',
    moments: lang==='bg'?'📸 Споделени моменти':lang==='es'?'📸 Momentos compartidos':lang==='fr'?'📸 Moments partagés':lang==='de'?'📸 Gemeinsame Momente':lang==='el'?'📸 Κοινές στιγμές':lang==='it'?'📸 Momenti insieme':'📸 Shared Moments',
    addPhoto:lang==='bg'?'Добави снимка':lang==='es'?'Añadir foto':lang==='fr'?'Ajouter photo':lang==='de'?'Foto hinzufügen':lang==='el'?'Προσθήκη φωτογραφίας':lang==='it'?'Aggiungi foto':'Add photo',
    caption: lang==='bg'?'Добави надпис...':lang==='es'?'Añade descripción...':lang==='fr'?'Ajouter légende...':lang==='de'?'Beschriftung...':lang==='el'?'Λεζάντα...':lang==='it'?'Didascalia...':'Add caption...',
    uploading:lang==='bg'?'Качване...':lang==='es'?'Subiendo...':lang==='fr'?'Envoi...':lang==='de'?'Hochladen...':lang==='el'?'Μεταφόρτωση...':lang==='it'?'Caricamento...':'Uploading...',
    tooBig:  lang==='bg'?'Снимката е твърде голяма (макс 5MB)':lang==='es'?'Foto demasiado grande (máx 5MB)':lang==='fr'?'Photo trop lourde (max 5Mo)':lang==='de'?'Foto zu groß (max 5MB)':lang==='el'?'Η φωτογραφία είναι πολύ μεγάλη (μέγ. 5MB)':lang==='it'?'Foto troppo grande (max 5MB)':'Photo too large (max 5MB)',
  };

  const { userId } = getAuthToken();

  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editCaptionId, setEditCaptionId] = useState(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [sizeErr, setSizeErr] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!supabase || !userId || !profile?.id) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { data: row } = await supabase.from('profiles').select('timeline_photos').eq('id', userId).single();
        const all = row?.timeline_photos || {};
        setPhotos(all[profile.id] || []);
      } catch { setPhotos([]); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function persist(updated) {
    setPhotos(updated);
    if (!supabase || !userId || !profile?.id) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data: row } = await supabase.from('profiles').select('timeline_photos').eq('id', userId).single();
      const all = row?.timeline_photos || {};
      all[profile.id] = updated;
      await supabase.from('profiles').update({ timeline_photos: all }).eq('id', userId);
    } catch {}
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file || photos.length >= 3) return;
    if (file.size > 5 * 1024 * 1024) { setSizeErr(labels.tooBig); setTimeout(() => setSizeErr(''), 3000); return; }
    if (!supabase || !userId) return;
    setUploading(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      if (!dataUrl) return;
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const ts = Date.now();
      const storagePath = `${userId}/${profile.id}/${ts}.jpg`;
      const { error } = await supabase.storage.from('timeline-photos').upload(storagePath, blob, { contentType: 'image/jpeg', upsert: false });
      if (error) return;
      const { data: urlData } = supabase.storage.from('timeline-photos').getPublicUrl(storagePath);
      const url = urlData?.publicUrl;
      if (url) await persist([...photos, { id: ts, url, caption: '', storagePath }]);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function removePhoto(id) {
    const photo = photos.find(p => p.id === id);
    if (photo?.storagePath && supabase) {
      try { await supabase.storage.from('timeline-photos').remove([photo.storagePath]); } catch {}
    }
    await persist(photos.filter(p => p.id !== id));
  }

  function startEditCaption(photo) { setEditCaptionId(photo.id); setCaptionDraft(photo.caption||''); }

  async function saveCaption(id) {
    await persist(photos.map(p => p.id === id ? {...p, caption: captionDraft} : p));
    setEditCaptionId(null);
  }

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.88)',zIndex:10001,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={onClose}>
      <div style={{background:'#0d1117',border:'1px solid rgba(180,120,255,0.5)',borderRadius:20,padding:'28px 24px',maxWidth:340,width:'100%',position:'relative',maxHeight:'85vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} style={{position:'absolute',top:12,right:14,background:'none',border:'none',color:'#8b949e',fontSize:18,cursor:'pointer'}}>✕</button>
        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontSize:'1.05rem',fontWeight:700,background:'linear-gradient(90deg,#b47fff,#e8c870)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',marginBottom:14}}>{labels.title}</div>
          {profile.photo
            ? <img src={profile.photo} alt={profile.name} style={{width:68,height:68,borderRadius:'50%',objectFit:'cover',border:'2px solid rgba(180,120,255,0.6)',marginBottom:8,display:'block',margin:'0 auto 8px'}}/>
            : <div style={{width:68,height:68,borderRadius:'50%',background:'#21262d',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,margin:'0 auto 8px'}}>👤</div>
          }
          <div style={{color:'#f0f6fc',fontWeight:600}}>{profile.name}</div>
          {profile.country && <div style={{color:'#8b949e',fontSize:12,marginTop:2}}>{profile.flag||''} {profile.country}</div>}
        </div>
        <div className="ts-rel-timeline">
          <div className="ts-rel-timeline-item">
            <span className="ts-rel-dot">💫</span>
            <div><div className="ts-rel-label">{labels.met}</div></div>
          </div>
          <div className="ts-rel-timeline-item">
            <span className="ts-rel-dot">💬</span>
            <div><div className="ts-rel-label">{msgCount} {labels.msgs}</div></div>
          </div>
          {(profile.dreams||[]).length > 0 && (
            <div className="ts-rel-timeline-item">
              <span className="ts-rel-dot">🌍</span>
              <div>
                <div className="ts-rel-label">{labels.dreams}</div>
                <div className="ts-rel-sub">{profile.dreams.slice(0,3).join(' · ')}</div>
              </div>
            </div>
          )}
        </div>

        {/* ── Shared Moments photo section ──────────────────────────────── */}
        <div style={{marginTop:20,paddingTop:16,borderTop:'1px solid rgba(180,120,255,0.2)'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
            <span style={{color:'#f0f6fc',fontWeight:600,fontSize:'0.85rem'}}>{labels.moments}</span>
            {plan === 'elite' && photos.length < 3 && (
              <button
                onClick={()=>fileRef.current?.click()}
                disabled={uploading}
                style={{background:'rgba(180,120,255,0.15)',border:'1px solid rgba(180,120,255,0.4)',borderRadius:8,color:'#d4a0ff',fontSize:'0.75rem',padding:'3px 10px',cursor:'pointer',opacity:uploading?0.6:1}}
              >{uploading ? labels.uploading : `+ ${labels.addPhoto}`}</button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile}/>
          {sizeErr && <div style={{color:'#f85149',fontSize:'0.75rem',marginTop:4,textAlign:'center'}}>{sizeErr}</div>}
          {photos.length === 0 ? (
            <div style={{color:'#8b949e',fontSize:'0.78rem',textAlign:'center',padding:'10px 0'}}>
              {plan === 'elite'
                ? (lang==='bg'?'Добави до 3 снимки от вашата история':lang==='es'?'Añade hasta 3 fotos de tu historia':lang==='fr'?'Ajoutez jusqu\'à 3 photos de votre story':lang==='de'?'Füge bis zu 3 Fotos aus deiner Story hinzu':lang==='el'?'Πρόσθεσε έως 3 φωτογραφίες από το Story σου':lang==='it'?'Aggiungi fino a 3 foto dalla tua storia':'Add up to 3 photos from your story')
                : (lang==='bg'?'Само за Elite — добавяй споделени моменти':lang==='es'?'Solo Elite: añade momentos compartidos':lang==='fr'?'Réservé à Elite — ajoutez des moments partagés':lang==='de'?'Nur Elite — füge gemeinsame Momente hinzu':lang==='el'?'Μόνο για Elite — πρόσθεσε κοινές στιγμές':lang==='it'?'Solo Elite — aggiungi momenti insieme':'Elite only — add shared moments')}
            </div>
          ) : (
            <div className="ts-timeline-photos-grid">
              {photos.map(photo => (
                <div key={photo.id} className="ts-timeline-photo-wrap">
                  <img src={photo.url} alt="" className="ts-timeline-photo-thumb"/>
                  {plan === 'elite' && (
                    <button className="ts-timeline-photo-remove" onClick={()=>removePhoto(photo.id)}>✕</button>
                  )}
                  {editCaptionId === photo.id ? (
                    <div style={{marginTop:4}}>
                      <input
                        autoFocus
                        value={captionDraft}
                        onChange={e=>setCaptionDraft(e.target.value)}
                        onBlur={()=>saveCaption(photo.id)}
                        onKeyDown={e=>{if(e.key==='Enter')saveCaption(photo.id);if(e.key==='Escape')setEditCaptionId(null);}}
                        style={{width:'100%',background:'#161b22',border:'1px solid rgba(180,120,255,0.4)',borderRadius:4,color:'#f0f6fc',fontSize:'0.68rem',padding:'2px 5px',boxSizing:'border-box'}}
                        placeholder={labels.caption}
                        maxLength={60}
                      />
                    </div>
                  ) : (
                    <div
                      onClick={plan==='elite'?()=>startEditCaption(photo):undefined}
                      style={{color:'#8b949e',fontSize:'0.68rem',marginTop:3,textAlign:'center',cursor:plan==='elite'?'text':'default',minHeight:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                    >{photo.caption || (plan==='elite'?'✎ caption':'')}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatProfileModal({ profile, t, lang, plan, msgCount, onClose, onLightbox, onBlocked }) {
  const [isLiked, setIsLiked] = useState(false);
  const [likeSaving, setLikeSaving] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showIcebreaker, setShowIcebreaker] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [icebreakerQ, setIcebreakerQ] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [showReportReasons, setShowReportReasons] = useState(false);
  const [blockDone, setBlockDone] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const ft = FT[lang] || FT.en;
  const blockLabels = getBlockLabels(lang);

  async function handleBlock() {
    setBlockBusy(true);
    try {
      await blockProfile(profile.id);
      setBlockDone(true);
      setShowBlockConfirm(false);
      onBlocked && onBlocked(profile.id);
      setTimeout(onClose, 900);
    } catch (e) {
      alert(blockLabels.unavailable);
    } finally {
      setBlockBusy(false);
    }
  }

  async function handleReport(reason) {
    const { userId } = getAuthToken();
    if (!userId || !supabase) return;
    try { await supabase.from('reports').insert({ reporter_id: userId, reported_id: profile.id, reason, created_at: new Date().toISOString() }); } catch {}
    setReportDone(true);
    setTimeout(() => { setShowReportReasons(false); setReportDone(false); }, 2000);
  }
  const sendLbl = lang==='bg'?'Изпрати съобщение':lang==='es'?'Enviar mensaje':lang==='fr'?'Envoyer un message':lang==='de'?'Nachricht senden':lang==='el'?'Αποστολή μηνύματος':lang==='it'?'Invia messaggio':'Send Message';

  const ICEBREAKER_QS = lang === 'bg' ? [
    'Кое е най-лудото нещо, което си правил по време на пътуване?',
    'Ако можеш да живееш само в 1 страна за цял живот - коя би избрал?',
    'Кой е твоят любим спомен от пътуване?',
    'Предпочиташ ли планиране или спонтанност при пътуване?',
    'Кое място те е изненадало най-много?',
  ] : lang === 'es' ? [
    '¿Cuál es la cosa más loca que has hecho viajando?',
    'Si pudieras vivir en un solo país toda la vida, ¿cuál sería?',
    '¿Cuál es tu recuerdo de viaje favorito?',
    '¿Prefieres planificar o la espontaneidad?',
    '¿Qué lugar te sorprendió más?',
  ] : lang === 'fr' ? [
    'Quelle est la chose la plus folle que tu aies faite en voyage?',
    'Si tu ne pouvais vivre que dans un pays, lequel choisirais-tu?',
    'Quel est ton souvenir de voyage préféré?',
    'Tu préfères planifier ou être spontané?',
    'Quel endroit t\'a le plus surpris?',
  ] : lang === 'de' ? [
    'Was ist das Verrückteste, das du auf Reisen gemacht hast?',
    'In welchem Land würdest du am liebsten dein Leben verbringen?',
    'Was ist deine liebste Reiseerinnerung?',
    'Planst du lieber oder bist du spontan?',
    'Welcher Ort hat dich am meisten überrascht?',
  ] : lang === 'el' ? [
    'Ποιο είναι το πιο τρελό πράγμα που έχεις κάνει ταξιδεύοντας;',
    'Αν μπορούσες να ζήσεις μόνο σε 1 χώρα για πάντα, ποια θα διάλεγες;',
    'Ποια είναι η αγαπημένη σου ταξιδιωτική ανάμνηση;',
    'Προτιμάς τον σχεδιασμό ή τον αυθορμητισμό στα ταξίδια;',
    'Ποιο μέρος σε εξέπληξε περισσότερο;',
  ] : lang === 'it' ? [
    'Qual è la cosa più pazza che hai fatto viaggiando?',
    'Se potessi vivere in un solo paese per tutta la vita, quale sceglieresti?',
    'Qual è il tuo ricordo di viaggio preferito?',
    'Preferisci pianificare o essere spontaneo quando viaggi?',
    'Quale luogo ti ha sorpreso di più?',
  ] : [
    "What's the craziest thing you've done while traveling?",
    'If you could live in only 1 country for life, which would you choose?',
    'What is your favorite travel memory?',
    'Do you prefer planning or spontaneity when traveling?',
    'Which place surprised you the most?',
  ];

  useEffect(() => {
    async function checkLiked() {
      const { userId } = await getAuthTokenSafe();
      if (!userId || !profile?.id) return;
      let data;
      try { data = await sbFetch('GET', 'likes', `from_user=eq.${userId}&to_user=eq.${profile.id}&limit=1`); } catch (e) { return; }
      setIsLiked(Array.isArray(data) && data.length > 0);
    }
    checkLiked();
  }, [profile?.id]);

  async function handleLike() {
    if (isLiked || likeSaving) return;
    setLikeSaving(true);
    try {
      const result = await persistLikeAndMatch({ ...profile, __likeSource: 'chat-profile-modal' }, { source: 'chat-profile-modal' });
      if (result?.liked) setIsLiked(true);
    } catch (e) {
      console.error('[Like] persist failed:', e);
      alert(getLikeSaveFailureToast(e, lang));
    } finally {
      setLikeSaving(false);
    }
  }

  const inlineBox = { background:'#0d1117', border:'1px solid #c9a84c33', borderRadius:12, padding:16, marginTop:12 };
  const copyLbl = lang==='bg'?'📋 Копирай':lang==='es'?'📋 Copiar':lang==='fr'?'📋 Copier':lang==='de'?'📋 Kopieren':lang==='el'?'📋 Αντιγραφή':lang==='it'?'📋 Copia':'📋 Copy';
  const otherLbl = lang==='bg'?'🔄 Друг':lang==='es'?'🔄 Otro':lang==='fr'?'🔄 Autre':lang==='de'?'🔄 Andere':lang==='el'?'🔄 Άλλο':lang==='it'?'🔄 Altro':'🔄 Other';
  const modalRef = useRef(null);

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',flexDirection:'column',justifyContent:'flex-end'}} onClick={onClose}>
      <div style={{background:'#0d1117',borderRadius:'20px 20px 0 0',display:'flex',flexDirection:'column',height:'75vh',maxHeight:'75vh'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <div ref={modalRef} style={{flex:1, overflowY:'auto', WebkitOverflowScrolling:'touch', padding:'32px 24px 16px', scrollBehavior:'smooth'}}>
        <div className="ts-pm-header">
          {profile.photo
            ? <img src={profile.photo} alt={profile.name} className="ts-pm-photo" style={{cursor:'pointer'}} onClick={() => { const photos = profile.photos?.length ? profile.photos : (profile.photo ? [profile.photo] : []); if (photos.length && onLightbox) onLightbox({photos, startIndex:0, name:profile.name}); }} />
            : <div className="ts-pm-photo ts-avatar-initials">{(profile.name||'?')[0].toUpperCase()}</div>}
          <div className="ts-pm-info">
            <h2 className="ts-serif">{profile.name}{profile.age ? `, ${profile.age}` : ''}</h2>
            {(profile.city || profile.country) && <p className="ts-pm-location">{profile.city ? `${profile.city}, ` : ''}{profile.flag} {profile.country}</p>}
            <span className={`ts-online-badge ${profile.online?'on':'off'}`}><span className="ts-online-dot"/>{profile.online ? t.connect.online : t.connect.offline}</span>
          </div>
        </div>
        {profile.bio ? <p style={{fontSize:'0.9rem',color:'var(--muted)',margin:'0.75rem 0'}}>{profile.bio}</p> : null}
        {profile.traits?.length > 0 && (
          <div className="ts-pm-traits">{profile.traits.map(k => <span key={k} className="ts-trait-pill" style={{borderColor:DNA_COLORS[DNA_TRAIT_KEYS.indexOf(k)]}}>{getDnaTraitLabel(k, profile, lang, t)}</span>)}</div>
        )}
        {profile.dreams?.length > 0 && (
          <div className="ts-pm-dreams">{profile.dreams.flatMap(d=>String(d).split(',').map(s=>s.trim()).filter(Boolean)).map((tag,i)=>{const match=COUNTRIES.find(c=>Object.values(c.name).some(n=>n.toLowerCase()===tag.toLowerCase()));const label=match?getCountryDisplayName(match, lang):tag;return <span key={tag+i} className="ts-dream-tag">{label}</span>;})}</div>
        )}
        {showReport && (
          <div style={inlineBox}>
            <div style={{color:'var(--gold)',fontWeight:700,marginBottom:8}}>📊 {ft.reportTitle} {profile.name}</div>
            <div style={{color:'#8b949e',fontSize:13}}>
              {lang==='bg'?`Съвместимост: ${profile.compat}% — Страхотно съвпадение!`:lang==='es'?`Compatibilidad: ${profile.compat}% — ¡Gran coincidencia!`:lang==='fr'?`Compatibilité : ${profile.compat}% — Excellent match !`:lang==='de'?`Kompatibilität: ${profile.compat}% — Tolles Match!`:lang==='el'?`Συμβατότητα: ${profile.compat}% — Εξαιρετικό ταίριασμα!`:lang==='it'?`Compatibilità: ${profile.compat}% — Ottimo abbinamento!`:`Compatibility: ${profile.compat}% — Great match!`}
            </div>
            {profile.traits?.length > 0 && (
              <div style={{color:'#f0f6fc',fontSize:12,marginTop:8}}>
                ✅ {lang==='bg'?'Общи интереси':lang==='es'?'Intereses comunes':lang==='fr'?'Intérêts communs':lang==='de'?'Gemeinsame Interessen':lang==='el'?'Κοινά ενδιαφέροντα':lang==='it'?'Interessi comuni':'Common interests'}: {profile.traits.map(k => getDnaTraitLabel(k, profile, lang, t)).join(', ')}
              </div>
            )}
            <div style={{color:'#f0f6fc',fontSize:12,marginTop:4}}>
              🎯 {lang==='bg'?'Стил: И двамата обичате да изследвате':lang==='es'?'Estilo: a ambos os encanta explorar':lang==='fr'?'Style : vous aimez tous les deux explorer':lang==='de'?'Stil: Ihr liebt es beide zu erkunden':lang==='el'?'Στυλ: Και οι δύο λατρεύετε την εξερεύνηση':lang==='it'?'Stile: a entrambi piace esplorare':'Style: You both love to explore'}
            </div>
            {profile.dreams?.length > 0 && (
              <div style={{color:'#f0f6fc',fontSize:12,marginTop:4}}>
                🌍 {lang==='bg'?'Мечтани дестинации':lang==='es'?'Destinos soñados':lang==='fr'?'Destinations de rêve':lang==='de'?'Traumziele':lang==='el'?'Προορισμοί των ονείρων':lang==='it'?'Destinazioni da sogno':'Dream destinations'}: {profile.dreams.slice(0,3).join(', ')}
              </div>
            )}
          </div>
        )}
        {showIcebreaker && (
          <div style={inlineBox}>
            <div style={{color:'var(--gold)',fontSize:14,marginBottom:12}}>💬 {ICEBREAKER_QS[icebreakerQ]}</div>
            <div style={{display:'flex',gap:8}}>
              <button className="ts-btn-ghost-sm" onClick={() => setIcebreakerQ(q => (q+1) % ICEBREAKER_QS.length)}>{otherLbl}</button>
              <button className="ts-btn-ghost-sm" onClick={() => navigator.clipboard?.writeText(ICEBREAKER_QS[icebreakerQ]).catch(()=>{})}>{copyLbl}</button>
            </div>
          </div>
        )}
        {/* Video intro */}
        {profile.video_intro && (
          <div style={{marginTop:12,marginBottom:4}}>
            <div style={{color:'var(--gold)',fontSize:12,fontWeight:600,marginBottom:6}}>{ft.videoIntro}</div>
            <video src={profile.video_intro} controls style={{width:'100%',borderRadius:12,maxHeight:200,background:'#000',display:'block'}}/>
          </div>
        )}

        {/* Photo gallery */}
        {(() => {
          const galleryPhotos = profile.photos || [];
          if (galleryPhotos.length === 0) return null;
          return (
            <div style={{marginTop:12}}>
              <div style={{color:'var(--gold)',fontSize:12,fontWeight:600,marginBottom:6}}>{ft.photos}</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:6}}>
                {galleryPhotos.map((url, i) => (
                  <img key={url} src={url} alt="" onClick={()=>setLightboxIdx(i)}
                    style={{width:'100%',height:100,objectFit:'cover',borderRadius:8,cursor:'pointer',display:'block'}}/>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Lightbox */}
        {lightboxIdx !== null && (() => {
          const galleryPhotos = profile.photos || [];
          return (
            <div onClick={()=>setLightboxIdx(null)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.92)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <button onClick={()=>setLightboxIdx(null)} style={{position:'absolute',top:16,right:16,background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>✕</button>
              {lightboxIdx > 0 && (
                <button onClick={e=>{e.stopPropagation();setLightboxIdx(i=>i-1);}} style={{position:'absolute',left:16,background:'none',border:'none',color:'#fff',fontSize:32,cursor:'pointer'}}>‹</button>
              )}
              <img src={galleryPhotos[lightboxIdx]} alt="" onClick={e=>e.stopPropagation()} style={{maxWidth:'90vw',maxHeight:'85vh',borderRadius:12,objectFit:'contain'}}/>
              {lightboxIdx < galleryPhotos.length - 1 && (
                <button onClick={e=>{e.stopPropagation();setLightboxIdx(i=>i+1);}} style={{position:'absolute',right:16,background:'none',border:'none',color:'#fff',fontSize:32,cursor:'pointer'}}>›</button>
              )}
            </div>
          );
        })()}
        </div>
        <div className="ts-pm-action-footer" style={{flexShrink:0, borderTop:'1px solid #30363d', background:'#0d1117', padding:'12px 16px 16px'}}>
          <div className="ts-pm-extra-btns" style={{marginBottom:8}}>
            <button className="ts-pm-extra-btn" onClick={e => { e.stopPropagation(); setShowReport(r => !r); setShowIcebreaker(false); setShowTimeline(false); }}>{ft.compatReport}</button>
            <button className="ts-pm-extra-btn" onClick={e => { e.stopPropagation(); setShowIcebreaker(i => !i); setShowReport(false); setShowTimeline(false); }}>{ft.icebreaker}</button>
            {plan === 'elite' && <button className="ts-pm-extra-btn ts-timeline-btn" onClick={e=>{e.stopPropagation();setShowTimeline(v=>!v);setShowReport(false);setShowIcebreaker(false);}}>💝 {lang==='bg'?'Нашата история':lang==='es'?'Nuestra historia':lang==='fr'?'Notre histoire':lang==='de'?'Unsere Geschichte':lang==='el'?'Η ιστορία μας':lang==='it'?'La nostra storia':'Our Story'}</button>}
          </div>
          <div className="ts-pm-actions" style={{gap:8}}>
            <button className="ts-btn-like" style={isLiked?{background:'#21262d',border:'1px solid var(--gold)',color:'var(--gold)'}:{}} disabled={likeSaving} onClick={handleLike}>
              {isLiked ? `✓ ${t.connect.liked||t.connect.like}` : `♥ ${t.connect.like}`}
            </button>
            <button className="ts-btn-gold-full" onClick={onClose}>💬 {sendLbl}</button>
          </div>
          <div style={{marginTop:8,display:'flex',gap:8,justifyContent:'center'}}>
            {blockDone
              ? <span style={{color:'#f85149',fontSize:12}}>✓ {blockLabels.blocked}</span>
              : <button className="ts-btn-ghost-sm" style={{fontSize:11,color:'#f85149',borderColor:'#f8514933',padding:'4px 10px'}} onClick={()=>setShowBlockConfirm(true)}>🚫 {blockLabels.blockShort}</button>
            }
            <button className="ts-btn-ghost-sm" style={{fontSize:11,color:'#e3a008',borderColor:'#e3a00833',padding:'4px 10px'}} onClick={()=>setShowReportReasons(r=>!r)}>⚠️ Докладвай</button>
          </div>
          {showReportReasons && !reportDone && (
            <div style={{marginTop:8,padding:'12px',background:'rgba(248,81,73,0.08)',borderRadius:10,border:'1px solid rgba(248,81,73,0.2)'}}>
              <div style={{fontSize:12,color:'#8b949e',marginBottom:8}}>{lang==='bg'?'Причина за доклад:':lang==='es'?'Motivo del reporte:':lang==='fr'?'Raison du signalement :':lang==='de'?'Grund der Meldung:':lang==='el'?'Λόγος αναφοράς:':lang==='it'?'Motivo della segnalazione:':'Reason for report:'}</div>
              {[lang==='bg'?'Фалшив профил':lang==='es'?'Perfil falso':lang==='fr'?'Faux profil':lang==='de'?'Gefälschtes Profil':lang==='el'?'Ψεύτικο προφίλ':lang==='it'?'Profilo falso':'Fake profile', lang==='bg'?'Неподходящо съдържание':lang==='es'?'Contenido inapropiado':lang==='fr'?'Contenu inapproprié':lang==='de'?'Unangemessener Inhalt':lang==='el'?'Ακατάλληλο περιεχόμενο':lang==='it'?'Contenuto inappropriato':'Inappropriate content', lang==='bg'?'Спам':lang==='es'?'Spam':lang==='fr'?'Spam':lang==='de'?'Spam':lang==='el'?'Spam':lang==='it'?'Spam':'Spam', lang==='bg'?'Тормоз':lang==='es'?'Acoso':lang==='fr'?'Harcèlement':lang==='de'?'Belästigung':lang==='el'?'Παρενόχληση':lang==='it'?'Molestia':'Harassment'].map(reason=>(
                <button key={reason} onClick={()=>handleReport(reason)} style={{display:'block',width:'100%',textAlign:'left',background:'none',border:'1px solid rgba(248,81,73,0.2)',borderRadius:8,padding:'8px 12px',color:'#f0f6fc',fontSize:13,marginBottom:6,cursor:'pointer'}}>{reason}</button>
              ))}
            </div>
          )}
          {reportDone && <div style={{marginTop:8,textAlign:'center',fontSize:12,color:'#3fb950'}}>✓ {lang==='bg'?'Докладът е изпратен':lang==='es'?'Reporte enviado':lang==='fr'?'Signalement envoyé':lang==='de'?'Meldung gesendet':lang==='el'?'Η αναφορά στάλθηκε':lang==='it'?'Segnalazione inviata':'Report sent'}</div>}
        </div>
      </div>
      {showBlockConfirm && <BlockConfirmModal lang={lang} busy={blockBusy} onCancel={()=>setShowBlockConfirm(false)} onConfirm={handleBlock} />}
      {showTimeline && <RelationshipTimelineModal profile={profile} msgCount={msgCount||0} lang={lang} plan={plan} onClose={()=>setShowTimeline(false)} />}
    </div>
  );
}

// ── Travel Phrases Modal ──────────────────────────────────────────────────────
const PHRASE_CATS = [
  { key:'greetings',  emoji:'👋', labelBg:'Поздрави',   labelEn:'Greetings', labelEs:'Saludos',      labelFr:'Salutations',  labelDe:'Begrüßungen',   labelEl:'Χαιρετισμοί', labelIt:'Saluti' },
  { key:'restaurant', emoji:'🍽️', labelBg:'Ресторант',  labelEn:'Restaurant', labelEs:'Restaurante',  labelFr:'Restaurant',   labelDe:'Restaurant',    labelEl:'Εστιατόριο',  labelIt:'Ristorante' },
  { key:'transport',  emoji:'🚌', labelBg:'Транспорт',  labelEn:'Transport', labelEs:'Transporte',   labelFr:'Transport',    labelDe:'Transport',     labelEl:'Μεταφορά',    labelIt:'Trasporto' },
  { key:'romantic',   emoji:'💕', labelBg:'Романтично', labelEn:'Romantic', labelEs:'Romántico',    labelFr:'Romantique',   labelDe:'Romantisch',    labelEl:'Ρομαντικό',   labelIt:'Romantico' },
  { key:'emergency',  emoji:'🆘', labelBg:'Спешни',     labelEn:'Emergency', labelEs:'Emergencia',   labelFr:'Urgence',      labelDe:'Notfall',       labelEl:'Επείγον',     labelIt:'Emergenza' },
];

function TravelPhrasesModal({ lang, plan, targetCountry, onClose, onNeedPremium }) {
  const isBg = lang === 'bg';
  const isUnlocked = plan === 'premium' || plan === 'elite';

  const [dest, setDest] = useState(targetCountry || '');
  const [editingDest, setEditingDest] = useState(!targetCountry);
  const [destInput, setDestInput] = useState(targetCountry || '');
  const [category, setCategory] = useState('greetings');
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function cacheKey(d, cat) {
    return `ts_phrases_${d.toLowerCase().replace(/\s+/g,'_')}_${cat}`;
  }

  async function loadPhrases(d, cat) {
    if (!d) return;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey(d, cat)) || 'null');
      if (Array.isArray(cached) && cached.length) { setPhrases(cached); return; }
    } catch {}
    setLoading(true); setError(''); setPhrases([]);
    const catField = lang==='bg'?'labelBg':lang==='es'?'labelEs':lang==='fr'?'labelFr':lang==='de'?'labelDe':lang==='el'?'labelEl':lang==='it'?'labelIt':'labelEn';
    const catLabel = PHRASE_CATS.find(c => c.key === cat)?.[catField] || cat;
    const prompt = `Generate exactly 10 useful ${catLabel} travel phrases for someone visiting ${d}. Return ONLY a JSON array, no other text:\n[{"original":"phrase in local language","phonetic_latin":"pronunciation in Latin script","phonetic_cyrillic":"pronunciation in Cyrillic script (phonetic approximation for Bulgarian speakers)","bg":"Bulgarian translation","en":"English translation","es":"Spanish translation","fr":"French translation","de":"German translation","el":"Greek translation","it":"Italian translation","context":"when to use, 1 sentence in English"}]`;
    try { Object.keys(localStorage).filter(k=>k.startsWith('ts_phrases_')).forEach(k=>localStorage.removeItem(k)); } catch {}
    try {
      const res = await fetch(`${API_BASE}/api/openai/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken().accessToken}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role: 'user', content: prompt }], max_tokens: 1200 }),
      });
      const data = await res.json();
      const text = data.choices?.[0]?.message?.content?.trim() || '';
      const match = text.match(/\[[\s\S]*\]/);
      if (!match) throw new Error('parse');
      const parsed = JSON.parse(match[0]);
      if (Array.isArray(parsed) && parsed.length) {
        setPhrases(parsed);
        try { localStorage.setItem(cacheKey(d, cat), JSON.stringify(parsed)); } catch {}
      } else {
        throw new Error('empty');
      }
    } catch {
      setError(lang==='bg'?'Грешка при генериране. Опитай отново.':lang==='es'?'Error al generar. Inténtalo de nuevo.':lang==='fr'?'Erreur de génération. Réessayez.':lang==='de'?'Fehler bei der Generierung. Bitte versuche es erneut.':lang==='el'?'Σφάλμα δημιουργίας. Δοκίμασε ξανά.':lang==='it'?'Errore di generazione. Riprova.':'Generation error. Please try again.');
    }
    setLoading(false);
  }

  function applyDest() {
    const d = destInput.trim();
    if (!d) return;
    setDest(d);
    setEditingDest(false);
  }

  useEffect(() => {
    if (!dest) return;
    loadPhrases(dest, category);
  }, [dest, category]); // eslint-disable-line react-hooks/exhaustive-deps

  const visiblePhrases = isUnlocked ? phrases : phrases.slice(0, 3);
  const hiddenCount = phrases.length - visiblePhrases.length;

  return (
    <div style={{...OVERLAY_S, zIndex:10300}} onClick={onClose}>
      <div style={{...MODAL_S, maxWidth:420, maxHeight:'88vh', textAlign:'left', padding:'20px 16px 16px'}} onClick={e=>e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h2 className="ts-serif ts-modal-title" style={{textAlign:'center',marginBottom:'0.75rem'}}>
          🗣️ {isBg?'Фрази за пътника':lang==='es'?'Frases de Viaje':lang==='fr'?'Phrases de Voyage':lang==='de'?'Reisephrasen':lang==='el'?'Ταξιδιωτικές φράσεις':lang==='it'?'Frasi di viaggio':'Travel Phrases'}
        </h2>

        {editingDest ? (
          <div style={{display:'flex',gap:8,marginBottom:'1rem'}}>
            <input className="ts-input" style={{flex:1}} value={destInput} onChange={e=>setDestInput(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&applyDest()}
              placeholder={lang==='bg'?'Страна или дестинация…':lang==='es'?'País o destino…':lang==='fr'?'Pays ou destination…':lang==='de'?'Land oder Reiseziel…':lang==='el'?'Χώρα ή προορισμός…':lang==='it'?'Paese o destinazione…':'Country or destination…'} autoFocus />
            <button className="ts-btn-gold-full" style={{padding:'0.6rem 1rem',whiteSpace:'nowrap'}} onClick={applyDest} disabled={!destInput.trim()}>→</button>
          </div>
        ) : (
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:'1rem',background:'rgba(255,255,255,0.04)',border:'1px solid var(--border)',borderRadius:10,padding:'0.5rem 0.75rem'}}>
            <span>📍</span>
            <span style={{flex:1,fontSize:'0.9rem',fontWeight:600,color:'var(--text)'}}>{dest}</span>
            <button onClick={()=>setEditingDest(true)} style={{background:'none',border:'1px solid var(--border)',color:'var(--muted)',cursor:'pointer',fontSize:'0.72rem',padding:'3px 8px',borderRadius:6}}>
              {lang==='bg'?'Смени':lang==='es'?'Cambiar':lang==='fr'?'Changer':lang==='de'?'Ändern':lang==='el'?'Αλλαγή':lang==='it'?'Cambia':'Change'}
            </button>
          </div>
        )}

        {!editingDest && (
          <div className="ts-phrases-cats">
            {PHRASE_CATS.map(c => (
              <button key={c.key} className={`ts-phrases-cat-btn${category===c.key?' active':''}`} onClick={()=>setCategory(c.key)}>
                {c.emoji} {lang==='bg'?c.labelBg:lang==='es'?c.labelEs:lang==='fr'?c.labelFr:lang==='de'?c.labelDe:lang==='el'?c.labelEl:lang==='it'?c.labelIt:c.labelEn}
              </button>
            ))}
          </div>
        )}

        {!editingDest && (
          loading ? (
            <div style={{textAlign:'center',padding:'2.5rem 0',color:'var(--muted)'}}>
              <div style={{width:32,height:32,margin:'0 auto 0.75rem',borderRadius:'50%',border:'3px solid rgba(180,127,255,0.2)',borderTopColor:'#b47fff',animation:'ts-spin 0.9s linear infinite'}}/>
              <p style={{fontSize:'0.85rem'}}>{lang==='bg'?'Генерираме фрази…':lang==='es'?'Generando frases…':lang==='fr'?'Génération de phrases…':lang==='de'?'Wir erstellen Phrasen…':lang==='el'?'Δημιουργούμε φράσεις…':lang==='it'?'Stiamo generando frasi…':'Generating phrases…'}</p>
            </div>
          ) : error ? (
            <div style={{textAlign:'center',padding:'1.5rem 0'}}>
              <p style={{color:'#f87171',fontSize:'0.85rem',marginBottom:'0.75rem'}}>{error}</p>
              <button className="ts-btn-ghost-sm" onClick={()=>loadPhrases(dest,category)}>{lang==='bg'?'Опитай отново':lang==='es'?'Inténtalo de nuevo':lang==='fr'?'Réessayer':lang==='de'?'Erneut versuchen':lang==='el'?'Δοκίμασε ξανά':lang==='it'?'Riprova':'Try again'}</button>
            </div>
          ) : phrases.length > 0 ? (
            <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:'0.5rem'}}>
              {visiblePhrases.map((p,i) => (
                <div key={i} className="ts-phrase-card">
                  <div className="ts-phrase-original">{p.original}</div>
                  {(p.phonetic_cyrillic || p.phonetic_latin || p.phonetic) && (
                    <div className="ts-phrase-phonetic">
                      /{lang === 'bg' ? (p.phonetic_cyrillic || p.phonetic_latin || p.phonetic) : (p.phonetic_latin || p.phonetic)}/
                    </div>
                  )}
                  <div className="ts-phrase-bg">{p[lang] || p.bg || p.en || ''}</div>
                  {p.context && <div className="ts-phrase-context">💡 {p.context}</div>}
                </div>
              ))}
              {!isUnlocked && hiddenCount > 0 && (
                <div style={{position:'relative',marginTop:4}}>
                  <div style={{filter:'blur(4px)',pointerEvents:'none',userSelect:'none',display:'flex',flexDirection:'column',gap:8}}>
                    {phrases.slice(3, 6).map((p,i) => (
                      <div key={i} className="ts-phrase-card">
                        <div className="ts-phrase-original">{p.original}</div>
                        <div className="ts-phrase-bg">{p.bg}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(13,17,23,0.78)',borderRadius:10}}>
                    <p style={{color:'var(--gold)',fontWeight:700,fontSize:'0.88rem',margin:'0 0 0.2rem'}}>🔒 +{hiddenCount} {lang==='bg'?'фрази':lang==='es'?'frases':lang==='fr'?'phrases':lang==='de'?'Phrasen':lang==='el'?'φράσεις':lang==='it'?'frasi':'phrases'}</p>
                    <p style={{color:'var(--muted)',fontSize:'0.77rem',marginBottom:'0.65rem',textAlign:'center',padding:'0 1rem'}}>{lang==='bg'?'Premium за всички 10 фрази':lang==='es'?'Premium para las 10 frases':lang==='fr'?'Premium pour les 10 phrases':lang==='de'?'Premium für alle 10 Phrasen':lang==='el'?'Premium για όλες τις 10 φράσεις':lang==='it'?'Premium per tutte le 10 frasi':'Get Premium for all 10 phrases'}</p>
                    <button className="ts-btn-gold-full" style={{padding:'0.4rem 1.2rem',fontSize:'0.82rem'}} onClick={()=>{onClose();onNeedPremium&&onNeedPremium('phrases');}}>✨ {lang==='bg'?'Upgrade':lang==='es'?'Mejorar':lang==='fr'?'Améliorer':lang==='de'?'Upgrade':lang==='el'?'Αναβάθμιση':lang==='it'?'Upgrade':'Upgrade Now'}</button>
                  </div>
                </div>
              )}
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

function MessagesTab({ t, lang, groupChats, plan, onNeedPremium, coinBalance, setCoinBalance, incomingCall, setIncomingCall, onLightbox, openProfileId, onOpenProfileConsumed }) {
  const [convos, setConvos] = useState([]);
  const [convosLoading, setConvosLoading] = useState(true);
  const [activeType, setActiveType] = useState('direct');
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeDirectId, setActiveDirectId] = useState(null);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sendMessageError, setSendMessageError] = useState(false);
  const [translatedIds, setTranslatedIds] = useState(new Set());
  const [msgTranslations, setMsgTranslations] = useState({}); // msgId -> translated text
  const [videoCallProfile, setVideoCallProfile] = useState(null);
  const [showGifts, setShowGifts] = useState(false);
  const [giftFloats, setGiftFloats] = useState([]);
  const [showIcebreaker, setShowIcebreaker] = useState(false);
  const [showPhrases, setShowPhrases] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [locationShared, setLocationShared] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState('travel');
  const [stickerTab, setStickerTab] = useState(null);
  const [justSentStickerText, setJustSentStickerText] = useState(null);
  const [exitingStickerMsgId, setExitingStickerMsgId] = useState(null);
  const emojiPickerRef = useRef(null);
  const chatInputRef = useRef(null);
  const voiceTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const voiceChunksRef = useRef([]);
  // eslint-disable-next-line no-unused-vars
  const [voiceUrls, setVoiceUrls] = useState({});
  // Audit finding (5th audit, low): handleVoicePress starts voiceTimerRef's
  // interval and only cleared it when the user pressed the button again or
  // the 30s cap was hit — if this component unmounts mid-recording (user
  // closes the chat / navigates away), the interval kept ticking and could
  // call setState on an unmounted component. Stop the recorder and clear the
  // interval on unmount.
  useEffect(() => {
    return () => {
      clearInterval(voiceTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch {}
        try { mediaRecorderRef.current.stream?.getTracks().forEach(tr => tr.stop()); } catch {}
      }
    };
  }, []);
  const bottomRef = useRef(null);
  const seenMsgIds = useRef(new Set());
  const [chatProfile, setChatProfile] = useState(null);
  const [pendingPacts, setPendingPacts] = useState([]);
  const [outgoingPacts, setOutgoingPacts] = useState([]);
  const [pactProfiles, setPactProfiles] = useState({});
  const [pactsOpen, setPactsOpen] = useState(true);
  const [activeCapsulePact, setActiveCapsulePact] = useState(null);
  const [expandedPactId, setExpandedPactId] = useState(null);
  const [activeSocialProfile, setActiveSocialProfile] = useState(null);
  const [blockedDirectIds, setBlockedDirectIds] = useState(new Set());
  const [blockingProfile, setBlockingProfile] = useState(null);
  const [blockBusy, setBlockBusy] = useState(false);
  const ft = FT[lang] || FT.en;
  const blockLabels = getBlockLabels(lang);

  useEffect(() => {
    async function loadPacts() {
      const { userId } = getAuthToken();
      if (!userId) return;
      const [sent, received] = await Promise.all([
        sbFetch('GET', 'pacts', `sender_id=eq.${userId}&status=neq.declined&order=created_at.desc&limit=20`),
        sbFetch('GET', 'pacts', `receiver_id=eq.${userId}&status=neq.declined&order=created_at.desc&limit=20`),
      ]);
      const sentArr = Array.isArray(sent) ? sent : [];
      const receivedArr = Array.isArray(received) ? received : [];
      // Pending received = show accept/decline; active/fulfilled received = show in outgoing list
      setPendingPacts(receivedArr.filter(p => p.status === 'pending'));
      setOutgoingPacts([...sentArr, ...receivedArr.filter(p => p.status !== 'pending')]);
      const allIds = [
        ...sentArr.map(p => p.receiver_id),
        ...receivedArr.map(p => p.sender_id),
      ].filter(Boolean);
      if (allIds.length > 0) {
        const uniqueIds = [...new Set(allIds)];
        try {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, name, photo_url, city, country')
            .in('id', uniqueIds);
          if (Array.isArray(profilesData)) {
            const map = {};
            profilesData.forEach(p => {
              map[p.id] = { name: p.name||'', photo: getProfilePhotoUrl(p.photo_url, ''), photo_url: getProfilePhotoUrl(p.photo_url, ''), city: p.city||'', country: p.country||'' };
            });
            setPactProfiles(map);
          }
        } catch(e) {}
      }
    }
    loadPacts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function acceptPact(id) {
    await sbFetch('PATCH', 'pacts', `id=eq.${id}`, { status: 'active' }, { Prefer: 'return=minimal' });
    setPendingPacts(prev => prev.filter(p => p.id !== id));
  }
  async function declinePact(id) {
    await sbFetch('PATCH', 'pacts', `id=eq.${id}`, { status: 'declined' }, { Prefer: 'return=minimal' });
    setPendingPacts(prev => prev.filter(p => p.id !== id));
  }

  async function viewProfile(userId) {
    let rows;
    try { rows = await sbFetch('GET', 'profiles', `id=eq.${userId}&select=${PROFILE_PUBLIC_SELECT}&limit=1`); } catch (e) { return; }
    if (rows?.[0]) {
      const myCompat = await fetchMyCompatibilityProfile();
      setChatProfile(mapSupabaseProfile(rows[0], myCompat));
    }
  }

  const { userId: myUserId } = getAuthToken();

  // Load matched conversations + message history on mount
  useEffect(() => {
    let convoTries = 0;
    async function loadConvos() {
      const uid = await getActiveProfileId({ forceFresh: true });
      if (!uid) {
        if (convoTries < 5) { convoTries++; setTimeout(loadConvos, 600); }
        else setConvosLoading(false);
        return;
      }
      try {
        const rows = await sbFetch('GET', 'profiles', `id=eq.${uid}&select=id,name,verified,verification_status,is_admin,is_moderator,verification_rejection_reason&limit=1`);
        setActiveSocialProfile(Array.isArray(rows) && rows[0] ? rows[0] : null);
      } catch (e) {}
      const ownBlockedIds = await getBlockedProfileIds(uid).catch(() => new Set());
      setBlockedDirectIds(ownBlockedIds);

      let matches = [];
      let likeRows = [];
      let reverseRows = [];
      try {
        [matches, likeRows, reverseRows] = await Promise.all([
          sbFetch('GET', 'matches', `or=(user1.eq.${uid},user2.eq.${uid})&select=user1,user2`),
          sbFetch('GET', 'likes', `from_user=eq.${uid}&select=to_user`),
          sbFetch('GET', 'likes', `to_user=eq.${uid}&select=from_user`),
        ]);
      } catch (e) {
        if (convoTries < 5) { convoTries++; setTimeout(loadConvos, 600); return; }
        setConvosLoading(false); return;
      }

      const matchedIds = (Array.isArray(matches) ? matches : [])
        .map(m => profileIdKey(m.user1) === uid ? m.user2 : m.user1)
        .map(profileIdKey)
        .filter(Boolean);
      const likedIds = new Set((Array.isArray(likeRows) ? likeRows : []).map(row => profileIdKey(row.to_user)).filter(Boolean));
      const reverseLikedIds = new Set((Array.isArray(reverseRows) ? reverseRows : []).map(row => profileIdKey(row.from_user)).filter(Boolean));
      const reciprocalIds = [...likedIds].filter(id => reverseLikedIds.has(id));
      const otherIds = [...new Set([...matchedIds, ...reciprocalIds])].filter(id => !ownBlockedIds.has(profileIdKey(id)));
      if (otherIds.length === 0) { setConvosLoading(false); return; }

      let matchedProfiles;
      try {
        matchedProfiles = await sbFetch('GET', 'profiles', `id=in.(${otherIds.join(',')})&select=id,name,country,photo_url,online,verified,verification_status,is_admin,is_moderator`);
      } catch (e) { setConvosLoading(false); return; }
      if (!matchedProfiles || matchedProfiles.length === 0) { setConvosLoading(false); return; }
      const blockedPairs = await Promise.all(matchedProfiles.map(p => isProfilePairBlocked(uid, p.id).catch(() => false)));
      const visibleMatchedProfiles = matchedProfiles.filter((p, idx) => !blockedPairs[idx]);

      const built = await Promise.all(visibleMatchedProfiles.map(async (p, idx) => {
        let sentMsgs = [], recvMsgs = [];
        try {
          [sentMsgs, recvMsgs] = await Promise.all([
            sbFetch('GET', 'messages', `from_user=eq.${uid}&to_user=eq.${p.id}&select=id,from_user,content,created_at,read_at&order=created_at.asc&limit=200`),
            sbFetch('GET', 'messages', `from_user=eq.${p.id}&to_user=eq.${uid}&select=id,from_user,content,created_at,read_at&order=created_at.asc&limit=200`),
          ]);
        } catch (e) {
          try {
            [sentMsgs, recvMsgs] = await Promise.all([
              sbFetch('GET', 'messages', `from_user=eq.${uid}&to_user=eq.${p.id}&select=id,from_user,content,created_at&order=created_at.asc&limit=200`),
              sbFetch('GET', 'messages', `from_user=eq.${p.id}&to_user=eq.${uid}&select=id,from_user,content,created_at&order=created_at.asc&limit=200`),
            ]);
          } catch (fallbackError) { sentMsgs = []; recvMsgs = []; }
        }
        const msgs = [
          ...(Array.isArray(sentMsgs) ? sentMsgs : []),
          ...(Array.isArray(recvMsgs) ? recvMsgs : []),
        ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        const messages = msgs.map(m => {
          const d = new Date(m.created_at);
          return { id: m.id, from: m.from_user === uid ? 'me' : 'them', text: m.content, time: formatNotifTime(d), createdAt: m.created_at, readAt: m.read_at || null };
        });
        if (messages.length === 0) messages.push({ id: `new-${p.id}`, from: 'them', text: '💫 New match! Say hello 👋', time: '', createdAt: null });
        const unreadCount = messages.filter(m => m.from === 'them' && !m.readAt && !String(m.id).startsWith('new-')).length;

        return {
          id: p.id,
          profile: { id: p.id, name: p.name || 'Traveler', photo: getProfilePhotoUrl(p.photo_url, TRIP_FALLBACK_IMAGES[idx % 6]), online: p.online || false, verified: canUseSocialFeatures(p), flag: COUNTRY_FLAGS[p.country] || '🌍', country: p.country || '' },
          messages,
          unreadCount,
        };
      }));

      clearTimeout(watchdog);
      setConvos(sortConversations(built));
      setConvosLoading(false);
    }
    const watchdog = setTimeout(() => {
      if (convoTries < 5) { convoTries++; loadConvos(); }
    }, 8000);
    loadConvos();
    return () => clearTimeout(watchdog);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for new messages every 2s for the active direct conversation
  const activeDirectProfileId = activeType === 'direct' ? (activeDirectId || convos[activeIdx]?.profile?.id) : null;

  async function markIncomingDirectMessagesRead(otherProfileId, source = 'open-direct-chat') {
    const activeProfileId = await getActiveProfileId({ forceFresh: true });
    const otherId = profileIdKey(otherProfileId);
    if (!activeProfileId || !otherId || activeProfileId === otherId) return 0;
    let unreadRows = [];
    try {
      unreadRows = await sbFetch('GET', 'messages', `from_user=eq.${otherId}&to_user=eq.${activeProfileId}&read_at=is.null&select=id&limit=200`);
    } catch (e) {
      unreadRows = [];
    }
    const messageIds = (Array.isArray(unreadRows) ? unreadRows : []).map(row => row.id).filter(Boolean);
    console.info('[READ_RECEIPT_MARK_ATTEMPT]', {
      activeProfileId,
      chatProfileId: otherId,
      candidateMessageIds: messageIds,
      count: messageIds.length,
    });
    let markedCount = 0;
    if (messageIds.length > 0) {
      try {
        const res = await sbFetch('POST', 'rpc/mark_direct_messages_read', '', {
          sender_profile_id: otherId,
          receiver_profile_id: activeProfileId,
        });
        await ensureSbWriteOk(res, 'mark direct messages read');
        try {
          const body = await res.text();
          const parsedCount = body ? JSON.parse(body) : null;
          markedCount = Number.isFinite(parsedCount) ? parsedCount : messageIds.length;
        } catch (parseError) {
          markedCount = messageIds.length;
        }
      } catch (e) {
        console.error('[READ_RECEIPT_MARK_ERROR]', {
          activeProfileId,
          chatProfileId: otherId,
          errorCode: e?.code,
          errorMessage: e?.message,
          errorDetails: e?.details,
          errorHint: e?.hint,
        });
      }
    }
    console.info('[READ_RECEIPT_DEBUG]', {
      activeProfileId,
      otherProfileId: otherId,
      markedCount,
      conversationId: otherId,
      messageIds,
      source,
    });
    if (markedCount > 0) {
      setConvos(cs => cs.map(c => c.profile?.id === otherId ? { ...c, unreadCount: 0 } : c));
    }
    return markedCount;
  }

  async function loadMessages(profileId, options = {}) {
    const uid = await getActiveProfileId({ forceFresh: true });
    if (!uid || !profileId) return;
    if (await isProfilePairBlocked(uid, profileId).catch(() => false)) {
      setBlockedDirectIds(ids => new Set([...ids, profileIdKey(profileId)]));
      setConvos(cs => cs.filter(c => profileIdKey(c.profile?.id) !== profileIdKey(profileId)));
      return;
    }
    if (options.markRead) await markIncomingDirectMessagesRead(profileId, options.source || 'open-direct-chat');

    let sent = [], received = [];
    try {
      [sent, received] = await Promise.all([
        sbFetch('GET', 'messages', `from_user=eq.${uid}&to_user=eq.${profileId}&select=id,from_user,content,created_at,read_at&order=created_at.asc&limit=200`),
        sbFetch('GET', 'messages', `from_user=eq.${profileId}&to_user=eq.${uid}&select=id,from_user,content,created_at,read_at&order=created_at.asc&limit=200`),
      ]);
    } catch (e) {
      [sent, received] = await Promise.all([
        sbFetch('GET', 'messages', `from_user=eq.${uid}&to_user=eq.${profileId}&select=id,from_user,content,created_at&order=created_at.asc&limit=200`),
        sbFetch('GET', 'messages', `from_user=eq.${profileId}&to_user=eq.${uid}&select=id,from_user,content,created_at&order=created_at.asc&limit=200`),
      ]);
    }

    const merged = [
      ...(Array.isArray(sent) ? sent : []),
      ...(Array.isArray(received) ? received : []),
    ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const fresh = merged.map(m => {
      const d = new Date(m.created_at);
      return { id: m.id, from: m.from_user === uid ? 'me' : 'them', text: m.content, time: formatNotifTime(d), createdAt: m.created_at, readAt: m.read_at || null };
    });

    fresh.forEach(m => {
      if (m.from === 'them' && m.text?.startsWith('GIFT:') && !seenMsgIds.current.has(m.id)) {
        const emoji = m.text.split(':')[1];
        if (emoji) {
          const floatId = Date.now() + Math.random();
          setGiftFloats(fs => [...fs, { id: floatId, emoji }]);
          setTimeout(() => setGiftFloats(fs => fs.filter(f => f.id !== floatId)), 2500);
        }
      }
    });
    fresh.forEach(m => seenMsgIds.current.add(m.id));

    setConvos(cs => cs.map(c => {
      if (c.profile?.id !== profileId) return c;
      const confirmCounts = {};
      fresh.filter(m => m.from === 'me').forEach(m => { confirmCounts[m.text] = (confirmCounts[m.text] || 0) + 1; });
      const remaining = { ...confirmCounts };
      const survivingTemps = (c.messages || []).filter(m => {
        if (!m.isTemp) return false;
        if (remaining[m.text] > 0) { remaining[m.text]--; return false; }
        return true;
      });
      const unreadCount = fresh.filter(m => m.from === 'them' && !m.readAt).length;
      return { ...c, messages: [...fresh, ...survivingTemps], unreadCount };
    }).sort(compareConversationsByActivity));
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }

  useEffect(() => {
    if (!activeDirectProfileId || activeType !== 'direct') return;
    const isMobileThreadHidden = window.matchMedia?.('(max-width: 680px)').matches && !mobileThreadOpen;
    if (isMobileThreadHidden) return;
    try { loadMessages(activeDirectProfileId, { markRead: true, source: 'open-direct-chat' }); } catch(e) {}
    const pollInterval = setInterval(() => {
      try { loadMessages(activeDirectProfileId); } catch(e) {}
    }, 3000);
    if (!supabase) return () => clearInterval(pollInterval);
    const msgChannel = supabase.channel('msgs_' + activeDirectProfileId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, payload => {
        const m = payload.new;
        if (m && (m.from_user === activeDirectProfileId || m.to_user === activeDirectProfileId)) {
          loadMessages(activeDirectProfileId, { markRead: m.from_user === activeDirectProfileId, source: 'open-direct-chat' });
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, payload => {
        const m = payload.new;
        if (m && (m.from_user === activeDirectProfileId || m.to_user === activeDirectProfileId)) {
          loadMessages(activeDirectProfileId);
        }
      })
      .subscribe();
    return () => {
      clearInterval(pollInterval);
      supabase.removeChannel(msgChannel);
    };
  }, [activeDirectProfileId, activeType, mobileThreadOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openProfileId || !convos.length) return;
    const idx = convos.findIndex(c => c.profile?.id === openProfileId);
    if (idx < 0) return;
    setActiveType('direct');
    setActiveIdx(idx);
    setActiveDirectId(openProfileId);
    setMobileThreadOpen(true);
    onOpenProfileConsumed && onOpenProfileConsumed();
  }, [openProfileId, convos, onOpenProfileConsumed]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [activeIdx, activeType]);
  useEffect(() => {
    if (!showEmojiPicker) return;
    function handleClickOutside(e) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) setShowEmojiPicker(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  async function send(overrideText) {
    const text = (overrideText || input).trim(); if (!text) return;
    if (activeType === 'direct' && !canUseSocialFeatures(activeSocialProfile)) {
      alert(getVerificationRequiredMessage(lang));
      return;
    }
    const activeProfileIdForSend = activeType === 'direct' ? directConvo?.profile?.id : null;
    if (activeType === 'direct' && activeProfileIdForSend && (blockedDirectIds.has(profileIdKey(activeProfileIdForSend)) || await isProfilePairBlocked(await getActiveProfileId({ forceFresh: true }), activeProfileIdForSend).catch(() => false))) {
      setBlockedDirectIds(ids => new Set([...ids, profileIdKey(activeProfileIdForSend)]));
      alert(blockLabels.unavailable);
      return;
    }
    if (text.startsWith('STICKER:')) {
      setJustSentStickerText(text);
      setTimeout(() => setJustSentStickerText(null), 1500);
    }
    const now = new Date();
    const time = formatNotifTime(now);
    if (activeType === 'direct') {
      // Optimistic local update — tagged isTemp so polling merge can drop it once confirmed
      setConvos(cs => {
        const updated = cs.map(c => c.profile?.id === activeProfileIdForSend ? {...c, messages:[...c.messages,{id:'temp_'+Date.now(),from:'me',text,time,isTemp:true,createdAt:now.toISOString(),readAt:null}]} : c)
          .sort(compareConversationsByActivity);
        const nextIdx = updated.findIndex(c => c.profile?.id === activeProfileIdForSend);
        if (nextIdx >= 0) setActiveIdx(nextIdx);
        return updated;
      });
      // Persist to Supabase
      const senderProfileId = await getActiveProfileId({ forceFresh: true });
      const { accessToken } = await getAuthTokenSafe({ forceFresh: true });
      const convo = directConvo;
      if (senderProfileId && convo?.profile?.id) {
        let token = accessToken;
        if (!token && supabase) {
          const { data: { session } } = await supabase.auth.getSession();
          token = session?.access_token || null;
        }
        if (token) {
          try {
            const res = await sbFetch('POST', 'messages', '', { from_user: senderProfileId, to_user: convo.profile.id, content: text }, { 'Prefer': 'return=minimal' });
            await ensureSbWriteOk(res, 'message insert');
            setSendMessageError(false);
          } catch (e) {
            // Audit finding: on any non-"blocked" failure (network error, 5xx,
            // etc.) this used to roll back the optimistic message and then
            // `throw e` with no caller catching it — the message just vanished
            // with zero feedback. Now a generic failure keeps the text in the
            // input (so nothing typed is lost) and shows a visible error.
            setConvos(cs => cs.map(c => c.profile?.id === activeProfileIdForSend ? { ...c, messages: c.messages.filter(m => !(m.isTemp && m.text === text)) } : c));
            if (isBlockedWriteError(e)) {
              setBlockedDirectIds(ids => new Set([...ids, profileIdKey(convo.profile.id)]));
              alert(blockLabels.unavailable);
              return;
            }
            // Audit fix (8th audit): server-side message rate limit (new
            // trigger, 30 msgs/60s/sender) — give a clear reason instead of
            // the generic send-failed banner.
            if (String(e?.message || e || '').toLowerCase().includes('message_rate_limit_exceeded')) {
              alert(lang==='bg'?'Изпращаш съобщения твърде бързо. Изчакай малко и опитай пак.':lang==='es'?'Estás enviando mensajes demasiado rápido. Espera un momento.':lang==='fr'?"Vous envoyez des messages trop vite. Attendez un instant.":lang==='de'?'Du sendest zu schnell Nachrichten. Warte einen Moment.':lang==='el'?'Στέλνεις μηνύματα πολύ γρήγορα. Περίμενε λίγο.':lang==='it'?'Stai inviando messaggi troppo velocemente. Attendi un momento.':'You are sending messages too fast. Please wait a moment.');
              if (!overrideText) setInput(text);
              return;
            }
            console.error('[Messages] send failed:', e?.message || e);
            setSendMessageError(true);
            if (!overrideText) setInput(text);
            return;
          }
          const senderName = (() => { try { return JSON.parse(localStorage.getItem('travelsoul_user') || '{}').firstName || 'Someone'; } catch { return 'Someone'; } })();
          const pushBody = text.startsWith('GIFT:') ? '🎁 Sent you a gift!' : text.startsWith('STICKER:') ? '🎨 Sent you a sticker!' : text.startsWith('VOICE:') ? '🎙 Sent you a voice message' : text;
          sendPush(convo.profile.id, `💬 ${senderName}`, pushBody);
        }
      }
    }
    playSound('message');
    if (!overrideText) setInput('');
  }

  async function sendGift(gift) {
    if ((coinBalance ?? 0) < gift.price) return;
    const prevBal = coinBalance ?? 0;
    // Audit fix (8th audit): the coin debit and the GIFT: chat message used
    // to be two separate client-driven steps (adjust_profile_counter, then
    // an ordinary send()) with no server-side link between them — a direct
    // REST insert of a GIFT: message could render a fake gift with no debit
    // behind it. send_gift() now does both atomically server-side, using a
    // fixed server-side price/emoji catalog rather than trusting the
    // client's gift.price, so a GIFT: message can only be produced by this
    // RPC after a real, successful debit.
    setCoinBalance(prevBal - gift.price);
    localStorage.setItem('travelsoul_coins', String(prevBal - gift.price));
    let ok = false;
    let newBal = null;
    try {
      const senderProfileId = await getActiveProfileId({ forceFresh: true });
      const convo = directConvo;
      if (senderProfileId && convo?.profile?.id && supabase) {
        const { data, error } = await supabase.rpc('send_gift', {
          p_user_id: senderProfileId, p_to_user: convo.profile.id, p_gift_name: gift.name,
        });
        // TEMP DEBUG (remove after diagnosing gift-send failure):
        if (error || !data?.sent) {
          alert('GIFT DEBUG: ' + JSON.stringify({ error, data }));
        }
        if (!error && data?.sent) {
          ok = true;
          newBal = data.new_coins;
        }
      }
    } catch (e) {
      console.error('[sendGift] send_gift failed:', e);
      // TEMP DEBUG (remove after diagnosing gift-send failure):
      alert('GIFT DEBUG CATCH: ' + (e?.message || JSON.stringify(e)));
    }
    if (!ok) {
      // RPC failed (network error, insufficient balance, blocked pair, etc.)
      // — revert the optimistic local balance so it can't desync, and don't
      // show the gift animation since nothing was actually sent.
      setCoinBalance(prevBal);
      localStorage.setItem('travelsoul_coins', String(prevBal));
      alert(getCoinPurchaseFailedMessage(lang));
      return;
    }
    setCoinBalance(newBal);
    localStorage.setItem('travelsoul_coins', String(newBal));
    setShowGifts(false);
    const floatId = Date.now();
    setGiftFloats(fs => [...fs, { id: floatId, emoji: gift.emoji }]);
    setTimeout(() => setGiftFloats(fs => fs.filter(f => f.id !== floatId)), 2500);
    // The message was already inserted server-side by send_gift(); just
    // reflect it optimistically in the open conversation (mirrors send()'s
    // own optimistic-update shape) instead of calling send() again, which
    // would insert a second, duplicate GIFT: message.
    const now = new Date();
    const time = formatNotifTime(now);
    setConvos(cs => cs.map(c => c.profile?.id === directConvo?.profile?.id ? {...c, messages:[...c.messages,{id:'temp_'+Date.now(),from:'me',text:`GIFT:${gift.emoji}:${gift.name}`,time,isTemp:true,createdAt:now.toISOString(),readAt:null}]} : c).sort(compareConversationsByActivity));
    playSound('message');
  }

  async function handleVoicePress() {
    if (plan === 'free') { onNeedPremium && onNeedPremium('voiceMsg'); return; }
    if (voiceRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream?.getTracks().forEach(tr => tr.stop());
      }
      clearInterval(voiceTimerRef.current);
      setVoiceRecording(false);
      setRecordingSeconds(0);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
          .find(t => MediaRecorder.isTypeSupported(t)) || '';
        const mr = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 16000 });
        voiceChunksRef.current = [];
        mediaRecorderRef.current = mr;
        // eslint-disable-next-line no-unused-vars
        let elapsed = 0;
        mr.ondataavailable = e => { if (e.data && e.data.size > 0) voiceChunksRef.current.push(e.data); };
        mr.onstop = async () => {
          const blob = new Blob(voiceChunksRef.current, { type: mimeType || 'audio/webm' });
          if (blob.size < 1000) return;
          if (!supabase) return;
          const fileName = `voice_${Date.now()}.webm`;
          const { error: uploadError } = await supabase.storage
            .from('voice-messages')
            .upload(fileName, blob, { contentType: 'audio/webm', upsert: false });
          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage
              .from('voice-messages')
              .getPublicUrl(fileName);
            send('VOICE:' + publicUrl);
          } else {
            console.error('Voice upload error:', uploadError);
          }
        };
        mr.start();
        setVoiceRecording(true);
        setRecordingSeconds(0);
        voiceTimerRef.current = setInterval(() => {
          setRecordingSeconds(prev => {
            if (prev >= 30) {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
                mediaRecorderRef.current.stream?.getTracks().forEach(tr => tr.stop());
              }
              clearInterval(voiceTimerRef.current);
              setVoiceRecording(false);
              return 0;
            }
            elapsed = prev + 1;
            return prev + 1;
          });
        }, 1000);
      } catch (err) {
        console.error('[Voice] getUserMedia error:', err);
      }
    }
  }

  function shareLocation() {
    if (plan === 'free') { onNeedPremium && onNeedPremium('liveLocation'); return; }
    setLocationShared(true);
    send(`📍 ${ft.locationSharing} ${t.messages?.expiresIn1h||'— expires in 1h'}`);
    setTimeout(() => setLocationShared(false), 3600000);
  }

  async function toggleTranslate(msgId, text) {
    if (translatedIds.has(msgId)) {
      setTranslatedIds(s => { const n = new Set(s); n.delete(msgId); return n; });
      return;
    }
    setTranslatedIds(s => { const n = new Set(s); n.add(msgId); return n; });
    if (!msgTranslations[msgId]) {
      const translated = await translateText(text, lang);
      setMsgTranslations(prev => ({ ...prev, [msgId]: translated }));
    }
  }

  const directConvo = activeDirectId
    ? convos.find(c => c.profile?.id === activeDirectId) || convos[activeIdx]
    : convos[activeIdx];
  const activeGroupChat = groupChats[activeIdx] || null;
  const currentConvo = activeType === 'direct' ? directConvo : activeGroupChat;
  const currentDirectBlocked = activeType === 'direct' && currentConvo?.profile?.id && blockedDirectIds.has(profileIdKey(currentConvo.profile.id));

  async function confirmBlockFromChat() {
    if (!blockingProfile?.id) return;
    setBlockBusy(true);
    try {
      await blockProfile(blockingProfile.id);
      const blockedId = profileIdKey(blockingProfile.id);
      setBlockedDirectIds(ids => new Set([...ids, blockedId]));
      setConvos(cs => cs.filter(c => profileIdKey(c.profile?.id) !== blockedId));
      setChatProfile(null);
      setBlockingProfile(null);
      setMobileThreadOpen(false);
      setActiveDirectId(null);
      setActiveIdx(0);
    } catch (e) {
      alert(blockLabels.unavailable);
    } finally {
      setBlockBusy(false);
    }
  }

  function getConvoLastMessage(convo) {
    const realMessages = (convo?.messages || []).filter(m => !String(m.id).startsWith('new-'));
    const allMessages = convo?.messages || [];
    return realMessages[realMessages.length - 1] || allMessages[allMessages.length - 1] || null;
  }

  function getConvoActivityMs(convo) {
    const last = getConvoLastMessage(convo);
    if (!last?.createdAt) return 0;
    const value = new Date(last.createdAt).getTime();
    return Number.isFinite(value) ? value : 0;
  }

  function compareConversationsByActivity(a, b) {
    return getConvoActivityMs(b) - getConvoActivityMs(a);
  }

  function sortConversations(items) {
    return [...items].sort(compareConversationsByActivity);
  }

  function getConvoPreview(lastMsg) {
    if (!lastMsg) return '';
    if (lastMsg.text?.startsWith('VOICE:')) return '🎙 Voice message';
    if (lastMsg.text?.startsWith('GIFT:')) return `🎁 ${lastMsg.text.split(':')[2] || ''}`.trim();
    if (lastMsg.text?.startsWith('STICKER:')) return '🎨 Sticker';
    return lastMsg.text?.length > 48 ? `${lastMsg.text.slice(0, 48)}…` : lastMsg.text;
  }

  function getReadReceiptText(readAt) {
    if (!readAt) return '';
    const label = lang === 'bg' ? 'Прочетено'
      : lang === 'es' ? 'Leído'
      : lang === 'fr' ? 'Lu'
      : lang === 'de' ? 'Gelesen'
      : lang === 'el' ? 'Αναγνώστηκε'
      : lang === 'it' ? 'Letto'
      : 'Read';
    return `${label} ${formatNotifTime(new Date(readAt))}`;
  }

  function selectDirectConversation(idx) {
    setActiveType('direct');
    setActiveIdx(idx);
    setActiveDirectId(convos[idx]?.profile?.id || null);
    setMobileThreadOpen(true);
  }

  function selectGroupConversation(idx) {
    setActiveType('group');
    setActiveIdx(idx);
    setMobileThreadOpen(true);
  }

  async function handleVideoCall() {
    if (!canUseSocialFeatures(activeSocialProfile)) {
      alert(getVerificationRequiredMessage(lang));
      return;
    }
    if (plan !== 'elite') { onNeedPremium && onNeedPremium('videoChat'); return; }
    const mediaAccess = await requestVideoCallMediaAccess(lang);
    if (!mediaAccess.ok) {
      alert(mediaAccess.message);
      return;
    }
    if (currentConvo?.profile) setVideoCallProfile(currentConvo.profile);
  }

  const myProfileLS = (() => {
    try {
      const ls = JSON.parse(localStorage.getItem('travelsoul_profile')||'{}');
      const lsUser = JSON.parse(localStorage.getItem('travelsoul_user')||'{}');
      const fullName = [ls.firstName||lsUser.firstName, ls.lastName||lsUser.lastName]
        .filter(Boolean).join(' ').trim() || ls.name || lsUser.name || '';
      const photo = localStorage.getItem('travelsoul_photo') || ls.avatar_url || ls.photo || '';
      return { ...ls, name: fullName, photo, avatar_url: photo,
               city: ls.city||'', country: ls.country||'' };
    } catch { return {}; }
  })();

  const [myProfileFull, setMyProfileFull] = useState(myProfileLS);
  useEffect(() => {
    const ls = myProfileLS;
    if (!ls?.first_name && !ls?.name) {
      const { userId } = getAuthToken();
      supabase?.from('profiles')
        .select('id,name,photo_url,city,country')
        .eq('id', userId || '')
        .single()
        .then(({ data }) => {
          if (data) {
            const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ').trim() || data.name || '';
            setMyProfileFull({ ...data, name: fullName, photo: data.avatar_url || data.photo || getProfilePhotoUrl(data.photo_url, '') });
          }
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`ts-messages-wrap ${mobileThreadOpen ? 'thread-open' : ''}`}>
      {activeCapsulePact && <CapsuleModal pact={activeCapsulePact} myProfile={myProfileFull} profile={pactProfiles[activeCapsulePact.receiver_id]||pactProfiles[activeCapsulePact.sender_id]||{}} lang={lang} plan={plan} t={t} onClose={()=>setActiveCapsulePact(null)} />}
      {/* Sticker center overlay */}
      {justSentStickerText && (() => {
        const [, packKey, idxStr] = justSentStickerText.split(':');
        const pack = STICKER_PACKS.find(p=>p.key===packKey);
        const sticker = pack?.stickers[parseInt(idxStr,10)];
        return sticker ? (
          <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none',zIndex:9999}}>
            <span className="ts-sticker-overlay-emoji" style={{fontSize:120,lineHeight:1,display:'inline-block'}}>{sticker.emoji}</span>
          </div>
        ) : null;
      })()}
      {/* Sidebar */}
      <div className="ts-contact-list">
        <div className="ts-contact-head">
          <h2 className="ts-tab-title ts-serif ts-contact-title">{t.messages.title}</h2>
          <span className="ts-contact-count">{convos.length}</span>
        </div>

        {(pendingPacts.length + outgoingPacts.length) > 0 && (
          <div style={{marginBottom:10}}>
            <button onClick={()=>setPactsOpen(o=>!o)}
              style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',background:'none',border:'none',cursor:'pointer',padding:'4px 0',marginBottom:pactsOpen?6:0}}>
              <p className="ts-msg-section-label" style={{margin:0}}>🤝 {t.pact?.pactTitle||'Pacts'} ({pendingPacts.length + outgoingPacts.length})</p>
              <span style={{color:'var(--muted)',fontSize:'0.75rem'}}>{pactsOpen ? '▲' : '▼'}</span>
            </button>
            {pactsOpen && (
              <>
                {pendingPacts.map(pact => (
                  <div key={pact.id} style={{padding:'10px 12px',background:'rgba(201,168,76,0.08)',border:'1px solid rgba(201,168,76,0.25)',borderRadius:10,marginBottom:6}}>
                    <div style={{color:'#c9a84c',fontSize:'0.8rem',fontWeight:600,marginBottom:2}}>
                      {pactProfiles[pact.sender_id]?.name || '…'} → {pact.destination}{pact.target_month ? ` · ${pact.target_month}` : ''}
                    </div>
                    <div style={{color:'#8b949e',fontSize:'0.72rem',marginBottom:6}}>⏳ {t.pact?.pactPending||'Pact Pending'}</div>
                    <div style={{display:'flex',gap:6}}>
                      <button onClick={()=>acceptPact(pact.id)}
                        style={{flex:1,padding:'5px 8px',background:'rgba(63,185,80,0.15)',border:'1px solid rgba(63,185,80,0.4)',borderRadius:7,color:'#3fb950',fontSize:'0.75rem',fontWeight:600,cursor:'pointer'}}>
                        {t.pact?.pactAccept||'Accept'}
                      </button>
                      <button onClick={()=>declinePact(pact.id)}
                        style={{flex:1,padding:'5px 8px',background:'rgba(248,81,73,0.1)',border:'1px solid rgba(248,81,73,0.3)',borderRadius:7,color:'#f85149',fontSize:'0.75rem',fontWeight:600,cursor:'pointer'}}>
                        {t.pact?.pactDecline||'Decline'}
                      </button>
                    </div>
                  </div>
                ))}
                {outgoingPacts.map(pact => {
                  const sc = { pending:{color:'#f59e0b',label:t.pact?.pactPending||'⏳ Pending'}, active:{color:'#3fb950',label:t.pact?.pactActive||'✈️ Active'}, fulfilled:{color:'#c9a84c',label:t.pact?.pactFulfilled||'✅ Fulfilled'} }[pact.status] || {color:'var(--muted)',label:pact.status};
                  return (
                    <div key={pact.id} style={{marginBottom:6}}>
                      <div style={{padding:'10px 12px',background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:10}}>
                        <div style={{color:'var(--text)',fontSize:'0.8rem',fontWeight:600,marginBottom:2}}>
                          → {pactProfiles[pact.receiver_id === myUserId ? pact.sender_id : pact.receiver_id]?.name || '…'}: {pact.destination}{pact.target_month ? ` · ${pact.target_month}` : ''}
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{color:sc.color,fontSize:'0.72rem',cursor:pact.status==='active'?'pointer':'default'}}
                            onClick={pact.status==='active'?()=>setExpandedPactId(pact.id===expandedPactId?null:pact.id):undefined}>
                            {sc.label}
                          </span>
                          {pact.status==='active' && (
                            <span style={{fontSize:'0.7rem',color:'var(--gold)',cursor:'pointer'}}
                              onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('travelCapsule'):setActiveCapsulePact(pact)}>
                              🫙 {t?.pact?.capsuleBtn||(lang==='bg'?'Отвори капсула':lang==='es'?'Abrir cápsula':lang==='fr'?'Ouvrir la capsule':lang==='de'?'Kapsel öffnen':lang==='el'?'Άνοιγμα κάψουλας':lang==='it'?'Apri capsula':'Open capsule')}
                            </span>
                          )}
                        </div>
                      </div>
                      {expandedPactId === pact.id && (
                        <div style={{background:'rgba(201,168,76,0.08)',border:'1px solid rgba(201,168,76,0.3)',borderRadius:10,padding:12,marginTop:6}}>
                          <p style={{margin:'0 0 4px',fontSize:'0.82rem'}}>✈️ {pact.destination}</p>
                          {pact.target_month && <p style={{margin:'0 0 4px',fontSize:'0.78rem',color:'var(--muted)'}}>📅 {pact.target_month}</p>}
                          <p style={{margin:0,fontSize:'0.75rem',color:'#4caf50'}}>● {lang==='bg'?'Активен':lang==='es'?'Activo':lang==='fr'?'Actif':lang==='de'?'Aktiv':lang==='el'?'Ενεργό':lang==='it'?'Attivo':'Active'}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {!convosLoading && convos.length > 0 && (
          <div className="ts-match-strip-wrap">
            <p className="ts-msg-section-label">{lang==='bg'?'Съвпадения':lang==='es'?'Coincidencias':lang==='fr'?'Matchs':lang==='de'?'Übereinstimmungen':lang==='el'?'Ταιριάσματα':lang==='it'?'Corrispondenze':'Matches'}</p>
            <div className="ts-match-strip" aria-label={lang==='bg'?'Съвпадения':lang==='es'?'Coincidencias':lang==='fr'?'Matchs':lang==='de'?'Übereinstimmungen':lang==='el'?'Ταιριάσματα':lang==='it'?'Corrispondenze':'Matches'}>
              {convos.map((c,i)=>(
                <button key={c.id} className={`ts-match-chip ${activeType==='direct'&&directConvo?.profile?.id===c.profile.id?'active':''}`} onClick={()=>selectDirectConversation(i)} title={c.profile.name}>
                  <span className="ts-match-chip-photo-wrap">
                    <img src={c.profile.photo} alt={c.profile.name} className="ts-match-chip-photo"/>
                    {c.profile.online&&<span className="ts-pc-online-dot"/>}
                  </span>
                  <span>{c.profile.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {convosLoading
          ? <div style={{padding:'1.5rem',color:'var(--muted)',textAlign:'center',fontSize:'0.85rem'}}>💬 …</div>
          : convos.length === 0
            ? <div style={{padding:'1.5rem',color:'var(--muted)',textAlign:'center',fontSize:'0.85rem'}}>💫 {t.connect?.itsAMatch||'Match someone to start chatting!'}</div>
            : <>
                <p className="ts-msg-section-label">{t.messages.directMessages}</p>
                {convos.map((c,i)=>{
                  const lastMsg = getConvoLastMessage(c);
                  return (
                    <button key={c.id} className={`ts-contact-item ${activeType==='direct'&&directConvo?.profile?.id===c.profile.id?'active':''}`} onClick={()=>selectDirectConversation(i)}>
                      <div className="ts-contact-photo-wrap"><img src={c.profile.photo} alt={c.profile.name} className="ts-contact-photo"/>{c.profile.online&&<span className="ts-pc-online-dot"/>}{c.profile.verified&&<span className="ts-verified-dot">✓</span>}</div>
                      <div className="ts-contact-info">
                        <span className="ts-contact-name-row"><span className="ts-contact-name">{c.profile.name}</span>{c.profile.online&&<span className="ts-contact-online">{lang==='bg'?'онлайн':lang==='es'?'en línea':lang==='fr'?'en ligne':lang==='de'?'online':lang==='el'?'συνδεδεμένος/η':lang==='it'?'online':'online'}</span>}</span>
                        <span className="ts-contact-preview">{getConvoPreview(lastMsg)}</span>
                      </div>
                      <div className="ts-contact-meta">
                        <span className="ts-contact-time">{lastMsg?.time || ''}</span>
                        {c.unreadCount > 0 && <span className="ts-unread-dot">{c.unreadCount}</span>}
                      </div>
                    </button>
                  );
                })}
              </>
        }

        {groupChats.length > 0 && <p className="ts-msg-section-label">{t.messages.groupChats}</p>}
        {groupChats.map((g,i)=>(
          <button key={g.id} className={`ts-contact-item ${activeType==='group'&&activeIdx===i?'active':''}`} onClick={()=>selectGroupConversation(i)}>
            <div className="ts-contact-photo-wrap ts-group-icon-wrap"><span className="ts-group-icon">👥</span></div>
            <div className="ts-contact-info"><span className="ts-contact-name">{g.tripName}</span><span className="ts-contact-preview">{g.messages.length > 0 ? getConvoPreview(g.messages[g.messages.length-1]) : '👥 Group chat'}</span></div>
            <div className="ts-contact-meta"><span className="ts-contact-time">{g.messages[g.messages.length - 1]?.time || ''}</span></div>
          </button>
        ))}
      </div>

      {/* Chat window */}
      <div className="ts-chat-window">
        {currentConvo ? (
          <>
            <div className="ts-chat-header">
              <button className="ts-chat-back-btn" onClick={()=>setMobileThreadOpen(false)} title={lang==='bg'?'Назад':lang==='es'?'Atrás':lang==='fr'?'Retour':lang==='de'?'Zurück':lang==='el'?'Πίσω':lang==='it'?'Indietro':'Back'}>‹</button>
              {activeType === 'direct'
                ? <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}} onClick={()=>viewProfile(currentConvo.profile.id)}>
                    <div style={{position:'relative',display:'inline-flex',flexShrink:0}}>
                      <img src={currentConvo.profile.photo} alt={currentConvo.profile.name} className="ts-chat-avatar"/>
                      {currentConvo.profile.verified && <span className="ts-verified-dot">✓</span>}
                    </div>
                    <div><p className="ts-chat-name">{currentConvo.profile.name}</p><p className={`ts-chat-status ${currentConvo.profile.online?'on':'off'}`}>{currentConvo.profile.online?t.connect.online:t.connect.offline}</p></div>
                  </div>
                : <><span className="ts-chat-group-icon">👥</span><div><p className="ts-chat-name">{currentConvo.tripName}</p><p className="ts-chat-status on">📍 {currentConvo.location}</p></div></>
              }
              {activeType === 'direct' && (
                <>
                  <button className="ts-video-call-btn" onClick={handleVideoCall}>
                    <span>📹</span>
                    <span>{t.messages.videoCall}</span>
                  </button>
                  <button className="ts-header-icon-btn" title={lang==='bg'?'Фрази за пътника':lang==='es'?'Frases de viaje':lang==='fr'?'Phrases de voyage':lang==='de'?'Reisephrasen':lang==='el'?'Ταξιδιωτικές φράσεις':lang==='it'?'Frasi di viaggio':'Travel Phrases'} onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('phrases'):setShowPhrases(true)}>🌐</button>
                  <button className="ts-header-icon-btn" title={ft.icebreaker} onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('icebreaker'):setShowIcebreaker(true)}>🗣️</button>
                  {plan === 'elite' && <button className="ts-header-icon-btn ts-timeline-btn" title={lang==='bg'?'Нашата история':lang==='es'?'Nuestra historia':lang==='fr'?'Notre histoire':lang==='de'?'Unsere Geschichte':lang==='el'?'Η ιστορία μας':lang==='it'?'La nostra storia':'Our Story'} onClick={()=>setShowTimeline(v=>!v)}>💝</button>}
                  <button className="ts-header-icon-btn" title={blockLabels.blockShort} onClick={()=>setBlockingProfile(currentConvo.profile)}>🚫</button>
                </>
              )}
              {activeType === 'group' && (
                <button className={`ts-header-icon-btn ${locationShared?'active':''}`} title={ft.shareLocation} onClick={shareLocation}>📍</button>
              )}
            </div>

            <div className="ts-chat-messages">
              {currentConvo.messages.map(m => {
                const isMe = m.from === 'me';
                const isGroup = m.from === 'group';
                const showTranslate = !isMe && !isGroup;
                const isTranslated = translatedIds.has(m.id);
                const isTranslatableMsg = !isMe && !isGroup && m.text && !m.text.startsWith('GIFT:') && !m.text.startsWith('VOICE:') && !m.text.startsWith('STICKER:') && !m.text.startsWith('data:audio');
                const showTrans = isTranslatableMsg && isTranslated;
                return (
                  <div key={m.id} style={{
                    display: 'flex',
                    flexDirection: isMe ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    gap: '8px',
                    marginBottom: '8px',
                    width: '100%',
                  }}>
                    {isGroup && <span className="ts-group-sender-name">{m.sender}</span>}
                    {!isMe && !isGroup && (
                      <img src={activeType==='direct'?currentConvo.profile.photo:''} alt="" style={{
                        width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                      }}/>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', gap: 4, maxWidth: '70%' }}>
                      {m.text.startsWith('GIFT:') ? (() => {
                        const [, gEmoji, gName] = m.text.split(':');
                        return (
                          <div style={{textAlign:'center',background:'linear-gradient(135deg,#c9a84c22,#e8b92344)',border:'1px solid var(--gold)',borderRadius:16,padding:'14px 22px'}}>
                            <div style={{fontSize:60,lineHeight:1}}>{gEmoji}</div>
                            <div style={{color:'var(--gold)',fontSize:'0.82rem',marginTop:'0.4rem'}}>{gEmoji} {gName}</div>
                            <span style={{fontSize:10,opacity:0.6,display:'block',marginTop:4}}>{m.time}</span>
                          </div>
                        );
                      })() : (m.text.startsWith('data:audio') || (m.text.length > 500 && !m.text.includes(' '))) ? (
                        <div style={{padding:'10px 14px',borderRadius:16,background:'rgba(255,255,255,0.08)',color:'#f0f6fc'}}>
                          <p style={{margin:0,fontSize:14}}>🎙 Гласово съобщение</p>
                          <span style={{fontSize:10,opacity:0.6,display:'block',marginTop:4}}>{m.time}</span>
                        </div>
                      ) : m.text.startsWith('VOICE:') ? (() => {
                        const voiceUrl = m.text.replace('VOICE:', '');
                        const isUrl = voiceUrl.startsWith('http');
                        return isUrl ? (
                          <div style={{background:'linear-gradient(90deg,#c9a84c,#e8b923)',borderRadius:16,padding:'10px 16px',display:'inline-block'}}>
                            <audio controls src={voiceUrl} style={{height:36,width:200,display:'block'}} />
                            <span style={{fontSize:10,opacity:0.6,display:'block',marginTop:4,color:'#000'}}>{m.time}</span>
                          </div>
                        ) : (
                          <div style={{padding:'10px 14px',borderRadius:16,background:'rgba(255,255,255,0.08)',color:'#f0f6fc'}}>
                            <p style={{margin:0,fontSize:14}}>🎙 Voice message</p>
                            <span style={{fontSize:10,opacity:0.6,display:'block',marginTop:4}}>{m.time}</span>
                          </div>
                        );
                      })() : m.text.startsWith('STICKER:') ? (() => {
                        const [, packKey, idxStr] = m.text.split(':');
                        const pack = STICKER_PACKS.find(p=>p.key===packKey);
                        const sticker = pack?.stickers[parseInt(idxStr,10)];
                        const isExiting = m.id===exitingStickerMsgId;
                        return sticker ? (
                          <div
                            className={`ts-sticker-wrap${isExiting?' ts-sticker-exiting':''}`}
                            onClick={()=>{ if (!exitingStickerMsgId){ setExitingStickerMsgId(m.id); setTimeout(()=>setExitingStickerMsgId(null),1200); } }}
                            style={{textAlign:'center',padding:'8px 12px',cursor:'pointer'}}
                          >
                            <span className={sticker.anim} style={{fontSize:'3rem',lineHeight:1,display:'inline-block'}}>{sticker.emoji}</span>
                            <span style={{fontSize:10,opacity:0.6,display:'block',marginTop:4}}>{m.time}</span>
                          </div>
                        ) : null;
                      })() : (
                      <div style={{
                        padding: '10px 14px',
                        borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: isMe ? 'linear-gradient(135deg, #c9a84c, #e8b923)' : 'rgba(255,255,255,0.08)',
                        color: isMe ? '#000' : '#f0f6fc',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}>
                        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' }}>{m.text}</p>
                        {showTrans && <p style={{ margin: '4px 0 0', fontSize: 11, fontStyle: 'italic', opacity: 0.7 }}>{msgTranslations[m.id] || '…'}</p>}
                        <span style={{ fontSize: 10, opacity: 0.6, display: 'block', marginTop: 4, textAlign: isMe ? 'right' : 'left' }}>{m.time}</span>
                      </div>
                    )}
                      {isMe && activeType === 'direct' && (
                        <span className={`ts-read-receipt ${m.readAt ? 'read' : 'sent'}`}>
                          <span className="ts-read-checks">{m.readAt ? '✓✓' : '✓'}</span>
                          {m.readAt && <span>{getReadReceiptText(m.readAt)}</span>}
                        </span>
                      )}
                      {showTranslate && (
                        <button className="ts-translate-btn" onClick={()=>toggleTranslate(m.id, m.text)}>
                          🌐 {t.messages.translate}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef}/>
            </div>

            {showIcebreaker && <AiIcebreakerModal profile={currentConvo?.profile} lang={lang} onUse={s=>{setInput(s);setShowIcebreaker(false);}} onClose={()=>setShowIcebreaker(false)} />}
            {showGifts && <GiftPickerModal lang={lang} coinBalance={coinBalance} setCoinBalance={setCoinBalance} onSend={sendGift} onClose={()=>setShowGifts(false)} />}
            {giftFloats.map((f, i) => (
              <div key={f.id} className="ts-gift-float" style={{right: `${20 + i * 50}px`}}>{f.emoji}</div>
            ))}
            {activeType === 'direct' && currentDirectBlocked && (
              <div className="ts-chat-input-row" style={{justifyContent:'center',color:'#8b949e',fontSize:'0.85rem'}}>
                🚫 {blockLabels.blockedState}
              </div>
            )}
            {activeType === 'direct' && sendMessageError && (
              <div style={{color:'#f85149',fontSize:'0.78rem',padding:'0 12px 4px',textAlign:'center'}}>
                {lang==='bg'?'Съобщението не беше изпратено. Опитай пак.':lang==='es'?'No se pudo enviar el mensaje. Inténtalo de nuevo.':lang==='fr'?'Le message n\'a pas pu être envoyé. Réessayez.':lang==='de'?'Nachricht konnte nicht gesendet werden. Versuche es erneut.':lang==='el'?'Το μήνυμα δεν στάλθηκε. Δοκίμασε ξανά.':lang==='it'?'Il messaggio non è stato inviato. Riprova.':'Message failed to send. Try again.'}
              </div>
            )}
            {activeType === 'direct' && !currentDirectBlocked && (
              <div className="ts-chat-input-row">
                {voiceRecording ? (
                  <div className="ts-voice-recording">
                    <span className="ts-voice-dot">●</span>
                    <span>⏺ {recordingSeconds}s</span>
                    <button className="ts-send-btn" onClick={handleVoicePress}>{t.messages.send} ↑</button>
                  </div>
                ) : (
                  <>
                    <button className="ts-input-icon-btn" title={ft.sendGift} onClick={()=>plan==='free'?onNeedPremium&&onNeedPremium('gifts'):setShowGifts(true)}>🎁</button>
                    <button className="ts-input-icon-btn" title={t.messages?.voice||'Voice'} onClick={handleVoicePress}>🎤</button>
                    <div style={{position:'relative',flex:1,display:'flex',alignItems:'center'}}>
                      <input ref={chatInputRef} className="ts-chat-input" style={{flex:1,paddingRight:36}} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()} placeholder={t.messages.placeholder}/>
                      <button onClick={()=>setShowEmojiPicker(p=>!p)} style={{position:'absolute',right:6,background:'none',border:'none',fontSize:'1.1rem',cursor:'pointer',lineHeight:1,padding:0,color:'#8b949e'}}>😊</button>
                      {showEmojiPicker && (
                        <div ref={emojiPickerRef} style={{position:'absolute',bottom:'calc(100% + 8px)',left:0,right:0,background:'#161b22',border:'1px solid #c9a84c44',borderRadius:16,zIndex:999,maxHeight:280,display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 8px 32px rgba(0,0,0,0.6)'}}>
                          {/* Category tabs */}
                          <div style={{display:'flex',borderBottom:'1px solid #21262d',flexShrink:0,overflowX:'auto'}}>
                            {Object.entries(EMOJI_CATEGORIES[lang]||EMOJI_CATEGORIES.en).map(([key,label])=>(
                              <button key={key} onClick={()=>{setEmojiCategory(key);setStickerTab(null);}} style={{flex:'0 0 auto',background:'none',border:'none',padding:'6px 4px',fontSize:'0.65rem',cursor:'pointer',color:!stickerTab&&emojiCategory===key?'var(--gold)':'#8b949e',borderBottom:!stickerTab&&emojiCategory===key?'2px solid var(--gold)':'2px solid transparent',whiteSpace:'nowrap'}}>{label}</button>
                            ))}
                            {STICKER_PACKS.map(pack=>{
                              const locked = pack.key==='elite' ? plan!=='elite' : plan==='free';
                              const active = stickerTab===pack.key;
                              return (
                                <button key={pack.key} onClick={()=>setStickerTab(active?null:pack.key)} style={{flex:'0 0 auto',background:'none',border:'none',padding:'6px 4px',fontSize:'0.65rem',cursor:'pointer',color:active?'#c084fc':'#8b949e',borderBottom:active?'2px solid #c084fc':'2px solid transparent',whiteSpace:'nowrap'}}>
                                  {locked?'🔒 ':''}{pack.tabLabel[lang]||pack.tabLabel.en}
                                </button>
                              );
                            })}
                          </div>
                          {/* Sticker grid or emoji grid */}
                          {stickerTab ? (() => {
                            const pack = STICKER_PACKS.find(p=>p.key===stickerTab);
                            if (!pack) return null;
                            const locked = pack.key==='elite' ? plan!=='elite' : plan==='free';
                            if (locked) return (
                              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'24px 16px',gap:8}}>
                                <div style={{fontSize:'2rem'}}>🔒</div>
                                <p style={{margin:0,color:'#8b949e',fontSize:'0.8rem',textAlign:'center'}}>{pack.key==='elite'?'Upgrade to Elite to use Elite sticker pack':'Upgrade to Premium to use sticker packs'}</p>
                              </div>
                            );
                            return (
                              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6,padding:'10px',overflowY:'auto'}}>
                                {pack.stickers.map((s,i)=>(
                                  <button key={i} onClick={()=>{ send(`STICKER:${pack.key}:${i}`); setShowEmojiPicker(false); setStickerTab(null); }} style={{background:'none',border:'1px solid #21262d',borderRadius:10,padding:'10px 4px',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:4}} onMouseEnter={e=>e.currentTarget.style.borderColor='#c9a84c'} onMouseLeave={e=>e.currentTarget.style.borderColor='#21262d'}>
                                    <span className={s.anim} style={{fontSize:'2rem',lineHeight:1}}>{s.emoji}</span>
                                    <span style={{fontSize:'0.55rem',color:'#8b949e'}}>{s.label}</span>
                                  </button>
                                ))}
                              </div>
                            );
                          })() : (
                          <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:2,padding:'8px',overflowY:'auto'}}>
                            {(EMOJIS[emojiCategory]||[]).map((emoji,i)=>(
                              <button key={i} onClick={()=>{ setInput(prev=>prev+emoji); setShowEmojiPicker(false); chatInputRef.current?.focus(); }} style={{background:'none',border:'none',fontSize:'1.4rem',cursor:'pointer',padding:'4px',borderRadius:6,lineHeight:1}} onMouseEnter={e=>e.target.style.background='#21262d'} onMouseLeave={e=>e.target.style.background='none'}>{emoji}</button>
                            ))}
                          </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button className="ts-send-btn" onClick={()=>send()}>{t.messages.send} ↑</button>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="ts-chat-empty"><p>💬</p><p>{t.messages.title}</p></div>
        )}
      </div>
      {videoCallProfile && <TabErrorBoundary lang={lang} resetKey={videoCallProfile?.id}><VideoCallOverlay profile={videoCallProfile} t={t} onEnd={()=>setVideoCallProfile(null)} currentUserId={getAuthToken().userId} /></TabErrorBoundary>}
      {chatProfile && <ChatProfileModal profile={chatProfile} t={t} lang={lang} plan={plan} msgCount={(directConvo?.messages||[]).filter(m=>!String(m.id).startsWith('new-')).length} onClose={()=>setChatProfile(null)} onLightbox={onLightbox} onBlocked={(profileId)=>{ const blockedId = profileIdKey(profileId); setBlockedDirectIds(ids=>new Set([...ids, blockedId])); setConvos(cs=>cs.filter(c=>profileIdKey(c.profile?.id)!==blockedId)); setActiveDirectId(null); setActiveIdx(0); }} />}
      {blockingProfile && <BlockConfirmModal lang={lang} busy={blockBusy} onCancel={()=>setBlockingProfile(null)} onConfirm={confirmBlockFromChat} />}
      {showTimeline && currentConvo && activeType==='direct' && <RelationshipTimelineModal profile={currentConvo.profile} msgCount={(currentConvo.messages||[]).length} lang={lang} plan={plan} onClose={()=>setShowTimeline(false)} />}
      {showPhrases && currentConvo && activeType==='direct' && (
        <TravelPhrasesModal lang={lang} plan={plan} targetCountry={currentConvo.profile.country||''} onClose={()=>setShowPhrases(false)} onNeedPremium={onNeedPremium} />
      )}
    </div>
  );
}

// ── Bottom Nav ────────────────────────────────────────────────────────────────
const TAB_ICONS = { explore:'🌍', connect:'♥', journeys:'✈', messages:'💬' };

function BottomNav({ tab, setTab, t }) {
  const tabs = ['explore','connect','journeys','messages'];
  return (
    <nav className="ts-bottom-nav">
      {tabs.map(key=>(
        <button key={key} className={`ts-bnav-item ${tab===key?'active':''}`} onClick={()=>setTab(key)}>
          <span className="ts-bnav-icon">{TAB_ICONS[key]}</span>
          <span className="ts-bnav-label">{t.nav[key]}</span>
        </button>
      ))}
    </nav>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
function MainApp({ t, lang, setLang, plan, onNeedPremium, onUpgrade, incognito, setIncognito, onLogout, onProfile, notifications, onClearNotifications, coinBalance, setCoinBalance, joinedTrips, setJoinedTrips, isAdmin, onAdmin, isModerator, onMod }) {
  const [tab, setTab] = useState('explore');
  const [groupChats, setGroupChats] = useState([]);
  const [incomingCall, setIncomingCall] = useState(null);
  const [globalVideoCall, setGlobalVideoCall] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [openChatProfileId, setOpenChatProfileId] = useState(null);
  const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  const isStandalone = window.navigator.standalone === true;
  const [showIOSBanner, setShowIOSBanner] = useState(isIOS && !isStandalone);

  useEffect(() => {
    if (!supabase) return;
    const callChannel = supabase.channel('video-calls')
      .on('broadcast', { event: 'incoming-call' }, ({ payload }) => {
        const { userId } = getAuthToken();
        if (payload.to_user === userId) setIncomingCall(payload);
      })
      .on('broadcast', { event: 'video-call-ended' }, ({ payload }) => {
        const { userId } = getAuthToken();
        if (payload.to_user !== userId) return;
        setIncomingCall(call => call?.from_user === payload.from_user ? null : call);
        setGlobalVideoCall(call => call?.id === payload.from_user ? null : call);
      })
      .subscribe();
    return () => { supabase.removeChannel(callChannel); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!incomingCall || globalVideoCall) return undefined;
    return startIncomingCallRingtone();
  }, [incomingCall, globalVideoCall]);

  async function handleJoinTrip(trip) {
    setGroupChats(prev => {
      if (prev.some(g => g.id === `group-${trip.id}`)) return prev;
      return [...prev, makeGroupChat(trip)];
    });
    if ((joinedTrips || []).some(j => j.id === trip.id)) return;
    const tripData = { id:trip.id, name:trip.name, location:trip.location, image:trip.image, start_date:trip.start_date||null };
    const newList = [...(joinedTrips || []), tripData];
    setJoinedTrips(newList);
    const { userId } = getAuthToken();
    if (userId && supabase) {
      try {
        await supabase.from('profiles').update({ joined_trips: newList }).eq('id', userId);
      } catch {}
    }
  }

  async function handleAnswerIncomingCall() {
    const mediaAccess = await requestVideoCallMediaAccess(lang);
    if (!mediaAccess.ok) {
      alert(mediaAccess.message);
      return;
    }
    setGlobalVideoCall({name:incomingCall.caller_name,photo:null,id:incomingCall.from_user});
    setIncomingCall(null);
  }

  return (
    <div className="ts-app">
      <AnnouncementBanner lang={lang} />
      <Header t={t} lang={lang} setLang={setLang} screen="app" onLogoClick={()=>setTab('explore')} plan={plan} onUpgrade={onUpgrade} incognito={incognito} onLogout={onLogout} onProfile={onProfile} notifications={notifications} onClearNotifications={onClearNotifications} coinBalance={coinBalance} setCoinBalance={setCoinBalance} isAdmin={isAdmin} onAdmin={onAdmin} isModerator={isModerator} onMod={onMod} />
      <main className="ts-main">
        <TabErrorBoundary lang={lang} resetKey={tab}>
          {tab==='explore'  && <ExploreTab  t={t} lang={lang} onJoinTrip={handleJoinTrip} plan={plan} onNeedPremium={onNeedPremium} />}
          {tab==='connect'  && <ConnectTab  t={t} lang={lang} onNavigateMessages={(profileId)=>{ if (profileId) setOpenChatProfileId(profileId); setTab('messages'); }} plan={plan} onNeedPremium={onNeedPremium} onLightbox={setLightbox} />}
          {tab==='journeys' && <JourneysTab t={t} lang={lang} plan={plan} onNeedPremium={onNeedPremium} />}
          {tab==='messages' && <MessagesTab t={t} lang={lang} groupChats={groupChats} plan={plan} onNeedPremium={onNeedPremium} coinBalance={coinBalance} setCoinBalance={setCoinBalance} incomingCall={incomingCall} setIncomingCall={setIncomingCall} onLightbox={setLightbox} openProfileId={openChatProfileId} onOpenProfileConsumed={()=>setOpenChatProfileId(null)} />}
        </TabErrorBoundary>
      </main>
      <BottomNav tab={tab} setTab={setTab} t={t} />
      {showIOSBanner && (
        <div style={{position:'fixed',bottom:80,left:16,right:16,background:'linear-gradient(135deg,#1a1a2e,#16213e)',border:'1.5px solid #f59e0b',borderRadius:16,padding:'14px 16px',zIndex:9998,boxShadow:'0 4px 24px rgba(245,158,11,0.18)'}}>
          <button onClick={()=>setShowIOSBanner(false)} style={{position:'absolute',top:10,right:12,background:'none',border:'none',color:'#8b949e',fontSize:18,cursor:'pointer',lineHeight:1}}>✕</button>
          <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
            <div style={{fontSize:26,flexShrink:0}}>📲</div>
            <div>
              <div style={{color:'#f59e0b',fontWeight:700,fontSize:14,marginBottom:4}}>Инсталирай TravelSoul</div>
              <div style={{color:'#c9d1d9',fontSize:12,lineHeight:1.5}}>
                Отвори в <strong style={{color:'#fff'}}>Safari</strong>, натисни <strong style={{color:'#fff'}}>Сподели</strong> (□↑) и избери <strong style={{color:'#fff'}}>"Добави към началния екран"</strong> за push известия.
              </div>
            </div>
          </div>
        </div>
      )}
      {incomingCall && !globalVideoCall && (
        <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',background:'#0d1117',border:'2px solid #c9a84c',borderRadius:20,padding:'20px 28px',zIndex:9999,textAlign:'center',minWidth:280,boxShadow:'0 8px 32px rgba(201,168,76,0.3)'}}>
          <div style={{fontSize:32,marginBottom:8}}>📞</div>
          <div style={{color:'#f0f6fc',fontWeight:700,fontSize:16,marginBottom:4}}>{incomingCall.caller_name}</div>
          <div style={{color:'#8b949e',fontSize:13,marginBottom:16}}>Incoming video call...</div>
          <div style={{display:'flex',gap:12,justifyContent:'center'}}>
            <button onClick={handleAnswerIncomingCall}
              style={{background:'#2ea043',border:'none',borderRadius:12,color:'#fff',fontWeight:700,padding:'10px 24px',cursor:'pointer',fontSize:15}}>
              ✅ {t?.messages?.connected||'Answer'}
            </button>
            <button onClick={()=>setIncomingCall(null)}
              style={{background:'#f85149',border:'none',borderRadius:12,color:'#fff',fontWeight:700,padding:'10px 24px',cursor:'pointer',fontSize:15}}>
              ❌ {t?.messages?.endCall||'Decline'}
            </button>
          </div>
        </div>
      )}
      {globalVideoCall && <TabErrorBoundary lang={lang} resetKey={globalVideoCall?.id}><VideoCallOverlay profile={globalVideoCall} t={t} onEnd={()=>setGlobalVideoCall(null)} currentUserId={getAuthToken().userId} notifyPeer={false} /></TabErrorBoundary>}
      {lightbox && <PhotoLightbox photos={lightbox.photos} startIndex={lightbox.startIndex} name={lightbox.name} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ── Register Screen ───────────────────────────────────────────────────────────
function pwStrength(pw) {
  if (!pw || pw.length < 6) return 'weak';
  const hasLetters = /[a-zA-Z]/.test(pw);
  const hasNumbers = /[0-9]/.test(pw);
  if (pw.length >= 8 && hasLetters && hasNumbers) return 'strong';
  return 'medium';
}

const isCapacitorAndroid = () => {
  return Capacitor?.getPlatform?.() === 'android'
    || (typeof window !== 'undefined' && window?.Capacitor?.platform === 'android');
};

const openUrlInExternalBrowser = async (url) => {
  if (isCapacitorAndroid()) {
    try {
      await Browser.open({ url });
      return;
    } catch (e) {
      console.error('Browser.open failed, falling back to window.location:', e);
    }
  }
  try {
    const popup = window.open(url, '_blank');
    if (!popup) window.location.href = url;
  } catch (e) {
    window.location.href = url;
  }
};

const openExternalUrl = async (url, event) => {
  if (!url) return;
  if (isCapacitorAndroid()) {
    event?.preventDefault();
    try {
      await Browser.open({ url });
    } catch (e) {
      console.error('External link failed:', e);
      try { window.location.href = url; } catch (_) {}
    }
    return;
  }
  if (event) return;
  try {
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) window.location.href = url;
  } catch (e) {
    window.location.href = url;
  }
};

const handleSocialAuth = async (provider) => {
  if (!supabase) {
    console.error('Social auth unavailable: Supabase client is not configured.');
    return;
  }
  try {
    sessionStorage.setItem('ts_oauth_provider', provider);
    const isAndroidGoogle = provider === 'google' && isCapacitorAndroid();
    const redirectTo = isAndroidGoogle
      ? 'travelsoul://auth-callback'
      : window.location.origin;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        ...(isAndroidGoogle ? { skipBrowserRedirect: true } : {})
      }
    });
    if (error) console.error('Social auth error:', error.message);
    if (isAndroidGoogle) {
      if (data?.url) {
        await openUrlInExternalBrowser(data.url);
      } else {
        console.error('Google Android auth did not return an OAuth URL.', error || data);
      }
    }
  } catch (e) {
    console.error('Social auth failed:', e);
  }
};

const handleFacebookAuth = async () => {
  if (!supabase) return;
  sessionStorage.setItem('ts_oauth_provider', 'facebook');
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'facebook',
    options: { redirectTo: 'https://travelsoul.love/', scopes: 'email,public_profile', queryParams: { auth_type: 'rerequest', display: 'touch' } }
  });
  if (error) console.error('Facebook login error:', error);
};

function SocialAuthButtons({ lang, onSocialAuth }) {
  const labels = {
    google:   { bg:'Продължи с Google',   en:'Continue with Google',   es:'Continuar con Google',   fr:'Continuer avec Google',   de:'Mit Google fortfahren',   el:'Συνέχεια με Google',   it:'Continua con Google' },
    facebook: { bg:'Продължи с Facebook', en:'Continue with Facebook', es:'Continuar con Facebook', fr:'Continuer avec Facebook', de:'Mit Facebook fortfahren', el:'Συνέχεια με Facebook', it:'Continua con Facebook' },
    apple:    { bg:'Продължи с Apple',    en:'Continue with Apple',    es:'Continuar con Apple',    fr:'Continuer avec Apple',    de:'Mit Apple fortfahren',    el:'Συνέχεια με Apple',    it:'Continua con Apple' },
  };
  const soonLabel = { bg:'Скоро', en:'Soon', es:'Pronto', fr:'Bientôt', de:'Bald', el:'Σύντομα', it:'Presto' };
  const appleSoonToast = { bg:'🍎 Влизане с Apple идва скоро!', en:'🍎 Sign in with Apple is coming soon!', es:'🍎 ¡Iniciar sesión con Apple llegará pronto!', fr:'🍎 La connexion avec Apple arrive bientôt !', de:'🍎 Anmeldung mit Apple kommt bald!', el:'🍎 Η σύνδεση με Apple έρχεται σύντομα!', it:'🍎 Accesso con Apple in arrivo!' };
  const [appleSoon, setAppleSoon] = React.useState(false);
  const dividerLabel = { bg:'или с имейл', en:'or with email', es:'o con email', fr:'ou avec email', de:'oder mit E-Mail', el:'ή με email', it:'o con email' };
  return (
    <div style={{width:'100%'}}>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:16}}>
        <button type="button" onClick={()=>onSocialAuth('google')} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,width:'100%',padding:'13px 16px',borderRadius:14,background:'#fff',border:'none',cursor:'pointer',fontSize:15,fontWeight:600,color:'#1a1a1a',boxShadow:'0 2px 12px rgba(0,0,0,0.12)',transition:'all 0.2s'}} onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.2)'}} onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.12)'}}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {labels.google[lang] || labels.google.en}
        </button>
        <button onClick={handleFacebookAuth} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,width:'100%',padding:'13px 16px',borderRadius:14,background:'#1877F2',border:'none',cursor:'pointer',fontSize:15,fontWeight:600,color:'#fff',boxShadow:'0 2px 12px rgba(24,119,242,0.35)',transition:'all 0.2s'}} onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow='0 4px 16px rgba(24,119,242,0.5)'}} onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 2px 12px rgba(24,119,242,0.35)'}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          {labels.facebook[lang] || labels.facebook.en}
        </button>
        <button onClick={()=>{ setAppleSoon(true); setTimeout(()=>setAppleSoon(false), 2600); }} style={{position:'relative',display:'flex',alignItems:'center',justifyContent:'center',gap:12,width:'100%',padding:'13px 16px',borderRadius:14,background:'#000',border:'1px solid rgba(255,255,255,0.15)',cursor:'pointer',fontSize:15,fontWeight:600,color:'#fff',boxShadow:'0 2px 12px rgba(0,0,0,0.4)',transition:'all 0.2s',opacity:0.92}} onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.6)'}} onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.4)'}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.39.07 2.35.74 3.15.8 1.19-.24 2.35-.93 3.56-.84 1.5.12 2.64.72 3.4 1.84-3.1 1.87-2.38 5.98.48 7.13-.57 1.5-1.32 2.99-2.59 3.95zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
          {labels.apple[lang] || labels.apple.en}
          <span style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'linear-gradient(135deg,#c9a84c,#e0c068)',color:'#1a1a1a',fontSize:10,fontWeight:800,padding:'2px 8px',borderRadius:20,letterSpacing:0.3}}>{soonLabel[lang] || soonLabel.en}</span>
        </button>
        {appleSoon && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'10px 14px',borderRadius:12,background:'rgba(201,168,76,0.12)',border:'1px solid rgba(201,168,76,0.35)',color:'#e0c068',fontSize:13.5,fontWeight:600,animation:'tsFadeIn 0.25s ease'}}>
            {appleSoonToast[lang] || appleSoonToast.en}
          </div>
        )}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <div style={{flex:1,height:1,background:'rgba(255,255,255,0.1)'}}/>
        <span style={{color:'#555',fontSize:13}}>{dividerLabel[lang] || dividerLabel.en}</span>
        <div style={{flex:1,height:1,background:'rgba(255,255,255,0.1)'}}/>
      </div>
    </div>
  );
}

function RegisterScreen({ t, lang, setLang, onComplete, onGoLogin, onVerificationRequired, onTerms, onPrivacy }) {
  const [form, setForm] = useState({ name:'', email:'', password:'', confirmPassword:'', country:'', gender:'male', birthDate:'' });
  const [loading, setLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const r = t.register;

  const strength = pwStrength(form.password);
  const confirmTouched = form.confirmPassword.length > 0;
  const passwordsMatch = form.password === form.confirmPassword;
  const userAge = calcAge(form.birthDate);
  const isAdult = userAge >= 18;
  const canSubmit = form.name && form.email && form.country &&
    form.password.length >= 6 && passwordsMatch && !loading && acceptedTerms && form.birthDate && isAdult;

  const strengthColor = strength === 'strong' ? '#3fb950' : strength === 'medium' ? '#e3a008' : '#f85149';
  const strengthWidth = strength === 'strong' ? '100%' : strength === 'medium' ? '60%' : '25%';
  const strengthLabel = strength === 'strong' ? r.strongPassword : strength === 'medium' ? r.mediumPassword : r.weakPassword;

  function handleCountry(e) {
    const code = e.target.value;
    setForm(f => ({...f, country: code}));
    const found = COUNTRIES.find(c => c.code === code);
    if (found) setLang(found.lang);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.country) return;
    if (form.password.length < 6) return;
    if (form.password !== form.confirmPassword) return;
    if (!form.birthDate || calcAge(form.birthDate) < 18) { setRegError(r.ageError); return; }
    setLoading(true);
    const nameParts = form.name.trim().split(' ');
    const userMeta = {
      id: Date.now().toString(),
      email: form.email.trim(),
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(' ') || '',
      country: form.country,
      gender: form.gender || 'male',
      lang,
      birthDate: form.birthDate,
      createdAt: new Date().toISOString(),
    };
    if (supabase) {
      const { data, error } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: { data: { firstName: nameParts[0], lastName: nameParts.slice(1).join(' ') || '', country: form.country, lang, birthDate: form.birthDate }, emailRedirectTo: 'https://travelsoul.love/' },
      });
      if (error) { setRegError(error.message); setLoading(false); return; }
      if (data.user) {
        userMeta.id = data.user.id;
        // Email not yet confirmed — show verification screen, login happens after link click
        if (!data.user.email_confirmed_at) {
          // Save registration data to apply after email verification
          try {
            localStorage.setItem('ts_pending_profile', JSON.stringify({
              name: form.name.trim(),
              country: form.country,
              gender: form.gender || 'male',
              lang,
              birthDate: form.birthDate
            }));
          } catch(e) {}
          setLoading(false);
          onVerificationRequired && onVerificationRequired(form.email.trim());
          return;
        }
        localStorage.setItem('travelsoul_user', JSON.stringify(userMeta));
        localStorage.setItem('travelsoul_lang', lang);
        if (!localStorage.getItem('travelsoul_coins')) localStorage.setItem('travelsoul_coins', '100');
        localStorage.removeItem('travelsoul_email');
        localStorage.removeItem('travelsoul_pass');
        setLoading(false);
        onComplete(false);
        return;
      }
    } else {
      // localStorage fallback when Supabase is unavailable
      localStorage.setItem('travelsoul_email', form.email.trim());
      localStorage.setItem('travelsoul_user', JSON.stringify(userMeta));
      localStorage.setItem('travelsoul_lang', lang);
      if (!localStorage.getItem('travelsoul_coins')) localStorage.setItem('travelsoul_coins', '100');
      setLoading(false);
      onComplete(false);
      return;
    }
    setLoading(false);
    onComplete(false);
  }

  return (
    <div className="ts-app ts-register-bg">
      <Header t={t} lang={lang} setLang={setLang} screen="register" onLogoClick={()=>{}} />
      <div className="ts-register-wrap">
        <div className="ts-register-card">
          <div className="ts-register-icon">✈</div>
          <h1 className="ts-serif ts-register-title">{r.title}</h1>
          <p className="ts-register-sub">{r.sub}</p>
          <SocialAuthButtons lang={lang} onSocialAuth={handleSocialAuth} />
          <form className="ts-form" onSubmit={submit}>
            <label className="ts-label">{r.name}
              <input className="ts-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Jane Doe" required />
            </label>
            <label className="ts-label">{r.email}
              <input className="ts-input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="jane@example.com" required />
            </label>

            {/* Password + strength */}
            <label className="ts-label">{r.password}
              <div style={{position:'relative'}}>
                <input className="ts-input" type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={e=>setForm(f=>({...f,password:e.target.value}))}
                  placeholder="••••••••" style={{paddingRight:'2.5rem'}} required />
                <button type="button" onClick={()=>setShowPass(v=>!v)} tabIndex={-1}
                  style={{position:'absolute',right:'0.5rem',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:'4px',color:'var(--muted)',lineHeight:0}}>
                  {showPass
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </label>
            {form.password.length > 0 && (
              <div style={{marginTop:'-0.5rem', marginBottom:'0.6rem'}}>
                <div style={{height:4, borderRadius:2, background:'var(--border)', overflow:'hidden'}}>
                  <div style={{height:'100%', width:strengthWidth, background:strengthColor, borderRadius:2, transition:'width 0.3s, background 0.3s'}} />
                </div>
                <p style={{fontSize:'0.75rem', color:strengthColor, marginTop:3}}>{strengthLabel}</p>
              </div>
            )}

            {/* Confirm password + match indicator */}
            <label className="ts-label">{r.confirmPassword}
              <div style={{position:'relative'}}>
                <input className="ts-input" type={showConfirm ? 'text' : 'password'} value={form.confirmPassword}
                  onChange={e=>setForm(f=>({...f,confirmPassword:e.target.value}))}
                  placeholder="••••••••"
                  style={{borderColor: confirmTouched ? (passwordsMatch ? '#3fb950' : '#f85149') : undefined,
                          paddingRight: confirmTouched ? '3.8rem' : '2.5rem'}}
                  required />
                {confirmTouched && (
                  <span style={{position:'absolute', right:'2.2rem', top:'50%', transform:'translateY(-50%)', fontSize:'1rem', pointerEvents:'none'}}>
                    {passwordsMatch ? '✅' : '❌'}
                  </span>
                )}
                <button type="button" onClick={()=>setShowConfirm(v=>!v)} tabIndex={-1}
                  style={{position:'absolute',right:'0.5rem',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:'4px',color:'var(--muted)',lineHeight:0}}>
                  {showConfirm
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </label>
            {confirmTouched && !passwordsMatch && (
              <p style={{fontSize:'0.78rem', color:'#f85149', marginTop:'-0.4rem', marginBottom:'0.5rem'}}>{r.passwordMismatch}</p>
            )}
            {confirmTouched && passwordsMatch && form.confirmPassword.length > 0 && (
              <p style={{fontSize:'0.78rem', color:'#3fb950', marginTop:'-0.4rem', marginBottom:'0.5rem'}}>{r.passwordMatch}</p>
            )}

            <label className="ts-label">{r.country}
              <select className="ts-input ts-select" value={form.country} onChange={handleCountry} required>
                <option value="">{r.country}</option>
                {COUNTRIES.map(c=><option key={c.code} value={c.code}>{c.flag} {getCountryDisplayName(c, lang)}</option>)}
              </select>
            </label>
            <label className="ts-label">{r.birthDate}
              <input className="ts-input" type="date" value={form.birthDate}
                max={new Date(new Date().setFullYear(new Date().getFullYear()-18)).toISOString().split('T')[0]}
                onChange={e=>setForm(f=>({...f,birthDate:e.target.value}))} required />
            </label>
            {form.birthDate && !isAdult && (
              <p style={{fontSize:'0.78rem',color:'#f85149',marginTop:'-0.4rem',marginBottom:'0.5rem'}}>{r.ageError}</p>
            )}
            <div>
              <label style={{color:'#8b949e',fontSize:12,marginBottom:6,display:'block'}}>
                {lang==='bg'?'Пол':lang==='es'?'Género':lang==='fr'?'Genre':lang==='de'?'Geschlecht':lang==='el'?'Φύλο':lang==='it'?'Genere':'Gender'}
              </label>
              <select value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))}
                style={{width:'100%',background:'#161b22',border:'1px solid #30363d',borderRadius:10,color:'#f0f6fc',padding:'10px 14px',fontSize:14,outline:'none'}}>
                <option value="male">{lang==='bg'?'👨 Мъж':lang==='es'?'👨 Hombre':lang==='fr'?'👨 Homme':lang==='de'?'👨 Mann':lang==='el'?'👨 Άνδρας':lang==='it'?'👨 Uomo':'👨 Male'}</option>
                <option value="female">{lang==='bg'?'👩 Жена':lang==='es'?'👩 Mujer':lang==='fr'?'👩 Femme':lang==='de'?'👩 Frau':lang==='el'?'👩 Γυναίκα':lang==='it'?'👩 Donna':'👩 Female'}</option>
              </select>
            </div>
            {regError && <p style={{color:'var(--red,#e05)',fontSize:'0.85rem',margin:'-0.25rem 0 0.5rem',textAlign:'center'}}>{regError}</p>}
            <label style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:14,cursor:'pointer',userSelect:'none'}}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={e => setAcceptedTerms(e.target.checked)}
                style={{marginTop:2,width:16,height:16,accentColor:'#c9a84c',flexShrink:0,cursor:'pointer'}}
              />
              <span style={{fontSize:13,color:'#8b949e',lineHeight:'1.5'}}>
                {lang==='bg' && <>Прочетох и приемам <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Условията за ползване</span> и <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Политиката за поверителност</span>. Потвърждавам, че съм на 18+ години.</>}
                {lang==='en' && <>I have read and agree to the <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Terms of Service</span> and <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Privacy Policy</span>. I confirm I am 18+ years old.</>}
                {lang==='es' && <>He leído y acepto los <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Términos de Servicio</span> y la <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Política de Privacidad</span>. Confirmo que tengo 18+ años.</>}
                {lang==='fr' && <>J'ai lu et j'accepte les <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Conditions d'utilisation</span> et la <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Politique de confidentialité</span>. Je confirme avoir 18+ ans.</>}
                {lang==='de' && <>Ich habe die <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Nutzungsbedingungen</span> und <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Datenschutzrichtlinie</span> gelesen und stimme zu. Ich bestätige, dass ich 18+ Jahre alt bin.</>}
                {lang==='el' && <>Έχω διαβάσει και αποδέχομαι τους <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Όρους Χρήσης</span> και την <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Πολιτική Απορρήτου</span>. Επιβεβαιώνω ότι είμαι 18+ ετών.</>}
                {lang==='it' && <>Ho letto e accetto i <span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onTerms&&onTerms();}}>Termini di Servizio</span> e l'<span style={{color:'#c9a84c',cursor:'pointer',textDecoration:'underline'}} onClick={e=>{e.preventDefault();e.stopPropagation();onPrivacy&&onPrivacy();}}>Informativa sulla Privacy</span>. Confermo di avere 18+ anni.</>}
              </span>
            </label>
            <button type="submit" className="ts-btn-gold-full" disabled={!canSubmit}
              style={{opacity: canSubmit ? 1 : 0.45, cursor: canSubmit ? 'pointer' : 'not-allowed'}}>
              {loading ? '...' : r.submit}
            </button>
          </form>
          <p className="ts-register-login" style={{cursor:'pointer'}} onClick={onGoLogin}>{r.login}</p>
        </div>
      </div>
    </div>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────────
// ── QR web login: web side (camera scan + poll) ──────────────────────────────
const WEB_LOGIN_SCAN_T = {
  bg: { title: 'Вход чрез QR код', instructions: 'Сканирай QR кода от Android приложението си', waitingScan: 'Насочи камерата към кода', waitingConfirm: 'Изчакай потвърждение от телефона си…', denied: 'Входът бе отказан', expired: 'Кодът изтече', error: 'Нещо се обърка. Опитай отново.', close: 'Затвори', cameraError: 'Нямаме достъп до камерата' },
  en: { title: 'Log in with QR code', instructions: 'Scan the QR code from your Android app', waitingScan: 'Point your camera at the code', waitingConfirm: 'Waiting for confirmation on your phone…', denied: 'Login was denied', expired: 'Code expired', error: 'Something went wrong. Please try again.', close: 'Close', cameraError: 'Could not access the camera' },
  es: { title: 'Iniciar sesión con QR', instructions: 'Escanea el código QR de tu app Android', waitingScan: 'Apunta la cámara al código', waitingConfirm: 'Esperando confirmación en tu teléfono…', denied: 'Inicio de sesión denegado', expired: 'Código expirado', error: 'Algo salió mal. Inténtalo de nuevo.', close: 'Cerrar', cameraError: 'No se pudo acceder a la cámara' },
  fr: { title: 'Connexion par QR code', instructions: "Scanne le QR code depuis ton app Android", waitingScan: 'Pointe la caméra vers le code', waitingConfirm: 'En attente de confirmation sur ton téléphone…', denied: 'Connexion refusée', expired: 'Code expiré', error: "Une erreur s'est produite. Réessaie.", close: 'Fermer', cameraError: "Impossible d'accéder à la caméra" },
  de: { title: 'Mit QR-Code anmelden', instructions: 'Scanne den QR-Code aus deiner Android-App', waitingScan: 'Richte die Kamera auf den Code', waitingConfirm: 'Warte auf Bestätigung auf deinem Handy…', denied: 'Anmeldung abgelehnt', expired: 'Code abgelaufen', error: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.', close: 'Schließen', cameraError: 'Kein Kamerazugriff möglich' },
  el: { title: 'Σύνδεση με κωδικό QR', instructions: 'Σάρωσε τον κωδικό QR από την εφαρμογή σου Android', waitingScan: 'Στρέψε την κάμερα στον κωδικό', waitingConfirm: 'Αναμονή επιβεβαίωσης στο τηλέφωνό σου…', denied: 'Η σύνδεση απορρίφθηκε', expired: 'Ο κωδικός έληξε', error: 'Κάτι πήγε στραβά. Δοκίμασε ξανά.', close: 'Κλείσιμο', cameraError: 'Δεν υπάρχει πρόσβαση στην κάμερα' },
  it: { title: 'Accedi con codice QR', instructions: 'Scansiona il codice QR dalla tua app Android', waitingScan: 'Punta la fotocamera verso il codice', waitingConfirm: 'In attesa di conferma sul tuo telefono…', denied: 'Accesso negato', expired: 'Codice scaduto', error: 'Qualcosa è andato storto. Riprova.', close: 'Chiudi', cameraError: 'Impossibile accedere alla fotocamera' },
};

function WebLoginScanModal({ lang, onClose, onLoggedIn }) {
  const wt = WEB_LOGIN_SCAN_T[lang] || WEB_LOGIN_SCAN_T.en;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanRafRef = useRef(null);
  const pollRef = useRef(null);
  const sentTokenRef = useRef(null);
  const [phase, setPhase] = useState('scanning'); // scanning | waitingConfirm | denied | expired | error
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        tick();
      } catch (e) {
        console.error('[web-login] camera error:', e);
        if (!cancelled) setCameraError(true);
      }
    }
    function tick() {
      if (cancelled || phase !== 'scanning') return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data && /^[0-9a-f]{64}$/.test(code.data) && sentTokenRef.current !== code.data) {
          sentTokenRef.current = code.data;
          reportScan(code.data);
        }
      }
      scanRafRef.current = requestAnimationFrame(tick);
    }
    startCamera();
    return () => {
      cancelled = true;
      if (scanRafRef.current) cancelAnimationFrame(scanRafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reportScan(token) {
    try {
      await fetch(`${API_BASE}/api/web-login/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      setPhase('waitingConfirm');
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    } catch (e) {
      console.error('[web-login] scan report failed:', e);
    }
  }

  // Poll for the token's status once we've reported a scan
  useEffect(() => {
    if (phase !== 'waitingConfirm' || !sentTokenRef.current) return undefined;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch(`${API_BASE}/api/web-login/poll`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: sentTokenRef.current }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (data?.status === 'confirmed' && data?.access_token && data?.refresh_token) {
          clearInterval(pollRef.current);
          if (supabase) {
            await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token });
          }
          onLoggedIn && onLoggedIn();
        } else if (data?.status === 'denied') {
          clearInterval(pollRef.current);
          setPhase('denied');
        } else if (data?.status === 'expired' || data?.status === 'invalid') {
          clearInterval(pollRef.current);
          setPhase('expired');
        }
      } catch (e) {
        console.error('[web-login] poll failed:', e);
      }
    }
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ts-modal-overlay" style={OVERLAY_S} onClick={onClose}>
      <div className="ts-modal" style={{...MODAL_S, maxWidth: 340, textAlign: 'center'}} onClick={e => e.stopPropagation()}>
        <button className="ts-modal-close" onClick={onClose}>✕</button>
        <h3 className="ts-serif" style={{color: 'var(--gold)', marginBottom: 10}}>{wt.title}</h3>

        {phase === 'scanning' && !cameraError && (
          <>
            <p style={{color: 'var(--muted)', fontSize: '0.85rem', marginBottom: 14}}>{wt.instructions}</p>
            <div style={{position: 'relative', width: 240, height: 240, margin: '0 auto', borderRadius: 12, overflow: 'hidden', background: '#000'}}>
              <video ref={videoRef} playsInline muted style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            </div>
            <canvas ref={canvasRef} style={{display: 'none'}} />
            <p style={{marginTop: 12, fontSize: '0.8rem', color: 'var(--muted)'}}>{wt.waitingScan}</p>
          </>
        )}
        {cameraError && (
          <div style={{padding: '16px 0'}}>
            <p style={{fontSize: '1.8rem', marginBottom: 8}}>📷</p>
            <p style={{color: 'var(--muted)', fontSize: '0.85rem'}}>{wt.cameraError}</p>
          </div>
        )}
        {phase === 'waitingConfirm' && (
          <div style={{padding: '16px 0'}}>
            <div className="ts-soul-match-spinner" style={{margin: '0 auto 12px'}} />
            <p style={{color: 'var(--muted)', fontSize: '0.85rem'}}>{wt.waitingConfirm}</p>
          </div>
        )}
        {phase === 'denied' && (
          <div style={{padding: '16px 0'}}>
            <p style={{fontSize: '1.8rem', marginBottom: 8}}>🚫</p>
            <p style={{fontSize: '0.9rem'}}>{wt.denied}</p>
            <button className="ts-btn-gold-full" style={{marginTop: 16, width: '100%'}} onClick={onClose}>{wt.close}</button>
          </div>
        )}
        {phase === 'expired' && (
          <div style={{padding: '16px 0'}}>
            <p style={{fontSize: '1.8rem', marginBottom: 8}}>⏱️</p>
            <p style={{fontSize: '0.9rem'}}>{wt.expired}</p>
            <button className="ts-btn-gold-full" style={{marginTop: 16, width: '100%'}} onClick={onClose}>{wt.close}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginScreen({ t, lang, setLang, onComplete, onGoRegister }) {
  const [form, setForm] = useState({ email:'', password:'' });
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showQrLogin, setShowQrLogin] = useState(false);
  const isAndroidApp = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const ls = t.loginScreen;
  const qrLoginLabel = lang==='bg' ? '📱 Вход с QR код от телефона' : lang==='es' ? '📱 Iniciar sesión con QR del móvil' : lang==='fr' ? '📱 Connexion par QR depuis le mobile' : lang==='de' ? '📱 Anmeldung per QR vom Handy' : lang==='el' ? '📱 Σύνδεση με κωδικό QR από το τηλέφωνο' : lang==='it' ? '📱 Accedi con codice QR dal telefono' : '📱 Log in with QR from phone';
  async function submit(e) {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      localStorage.removeItem('travelsoul_pass');
      localStorage.removeItem('travelsoul_password');
      if (!supabase) {
        const user = JSON.parse(localStorage.getItem('travelsoul_user') || 'null');
        if (user && user.email === form.email.trim()) { onComplete(); return; }
        setLoginError(ls.wrongPassword || 'Wrong email or password');
        return;
      }
      sessionStorage.removeItem('ts_oauth_provider');
      // signInWithPassword can hang on the Supabase auth lock. Instead of waiting a fixed 8s,
      // poll localStorage every 300ms and proceed the instant a NEW token appears (usually <1s).
      let data, error;
      const _tokenBefore = getAuthToken().accessToken; // remember old token to detect a real change
      const _signInPromise = supabase.auth.signInWithPassword({ email: form.email.trim(), password: form.password });
      const _pollForToken = new Promise((resolve) => {
        let elapsed = 0;
        const iv = setInterval(() => {
          elapsed += 300;
          const cur = getAuthToken();
          if (cur.accessToken && cur.userId && cur.accessToken !== _tokenBefore) {
            clearInterval(iv);
            resolve({ __polled__: true, accessToken: cur.accessToken, userId: cur.userId });
          } else if (elapsed >= 8000) {
            clearInterval(iv);
            resolve('__timeout__');
          }
        }, 300);
      });
      const _raceResult = await Promise.race([_signInPromise, _pollForToken]);
      if (_raceResult && _raceResult.__polled__) {
        // Token appeared in localStorage — login succeeded; build minimal data and continue.
        data = { user: { id: _raceResult.userId, email: form.email.trim(), user_metadata: {}, created_at: new Date().toISOString() } };
        error = null;
      } else if (_raceResult === '__timeout__') {
        const { accessToken, userId } = getAuthToken();
        if (accessToken && userId && accessToken !== _tokenBefore) {
          data = { user: { id: userId, email: form.email.trim(), user_metadata: {}, created_at: new Date().toISOString() } };
          error = null;
        } else {
          setLoginError(ls.wrongPassword || 'Login timed out. Please try again.');
          return;
        }
      } else {
        // signInWithPassword resolved normally (fast path or a real error like wrong password)
        data = _raceResult.data;
        error = _raceResult.error;
        cacheAuthSession(data?.session);
      }
      if (error) {
        setLoginError(ls.wrongPassword || error.message);
        return;
      }
      if (data?.user) {
        const meta = data.user.user_metadata || {};
        localStorage.setItem('travelsoul_user', JSON.stringify({
          id: data.user.id, email: data.user.email,
          firstName: meta.firstName || '', lastName: meta.lastName || '',
          country: meta.country || '', lang: meta.lang || lang,
          createdAt: data.user.created_at,
        }));
        if (meta.lang) { setLang(meta.lang); localStorage.setItem('travelsoul_lang', meta.lang); }
        // Pull avatar (and name) from profile so the header shows the photo immediately,
        // not only after a refresh. Quick, bounded — never blocks login on a hang.
        try {
          const rows = await Promise.race([
            sbFetch('GET', 'profiles', `id=eq.${data.user.id}&select=photo_url,name&limit=1`),
            new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
          ]);
          const prow = Array.isArray(rows) && rows[0];
          if (prow?.photo_url) {
            localStorage.setItem('travelsoul_photo', getProfilePhotoUrl(prow.photo_url, ''));
          }
        } catch (e) { /* best-effort — login proceeds regardless */ }
        // Fire immediately — do NOT wait for profile upsert
        onComplete();
        // Background upsert — hangs are fine, login is already done
        supabase.from('profiles').upsert({
          id: data.user.id,
          name: meta.firstName ? `${meta.firstName} ${meta.lastName || ''}`.trim() : data.user.email.split('@')[0],
          country: meta.country || '',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id', ignoreDuplicates: false })
          .then(({ error }) => { if (error) console.error('Profile upsert error:', error.message); });
      }
    } catch (err) {
      console.error('Login error:', err.message);
      setLoginError(ls.wrongPassword || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }
  async function forgotPassword() {
    if (!form.email) { setLoginError(ls.email + '?'); return; }
    if (supabase) {
      await supabase.auth.resetPasswordForEmail(form.email.trim());
    }
    setResetSent(true);
    setTimeout(() => setResetSent(false), 4000);
  }
  return (
    <div className="ts-app ts-register-bg">
      <Header t={t} lang={lang} setLang={setLang} screen="register" onLogoClick={()=>{}} />
      <div className="ts-register-wrap">
        <div className="ts-register-card">
          <div className="ts-register-icon">🔑</div>
          <h1 className="ts-serif ts-register-title">{ls.title}</h1>
          <p className="ts-register-sub">{ls.sub}</p>
          {resetSent && <p style={{color:'var(--green)',fontSize:'0.85rem',marginBottom:'0.75rem'}}>{ls.resetSent}</p>}
          <SocialAuthButtons lang={lang} onSocialAuth={handleSocialAuth} />
          <form className="ts-form" onSubmit={submit}>
            <label className="ts-label">{ls.email}<input className="ts-input" type="email" name="email" autoComplete="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="jane@example.com" required /></label>
            <label className="ts-label">{ls.password}
              <div style={{position:'relative'}}>
                <input className="ts-input" type={showPass ? 'text' : 'password'} name="password" autoComplete="current-password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="••••••••" style={{paddingRight:'2.5rem'}} required />
                <button type="button" onClick={()=>setShowPass(v=>!v)} tabIndex={-1}
                  style={{position:'absolute',right:'0.5rem',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:'4px',color:'var(--muted)',lineHeight:0}}>
                  {showPass
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </label>
            {loginError && <p style={{color:'var(--red,#e05)',fontSize:'0.85rem',margin:'-0.25rem 0 0.5rem',textAlign:'center'}}>{loginError}</p>}
            <button type="submit" className="ts-btn-gold-full" disabled={loading} style={{opacity:loading?0.65:1}}>{loading ? '...' : ls.submit}</button>
          </form>
          <p className="ts-register-login" style={{cursor:'pointer',marginTop:'0.5rem'}} onClick={forgotPassword}>{ls.forgot}</p>
          <p className="ts-register-login" style={{cursor:'pointer'}} onClick={onGoRegister}>{ls.register}</p>
          {!isAndroidApp && (
            <button
              type="button"
              className="ts-btn-ghost-sm"
              style={{marginTop:'0.75rem', width:'100%'}}
              onClick={() => setShowQrLogin(true)}
            >{qrLoginLabel}</button>
          )}
        </div>
      </div>
      {showQrLogin && (
        <WebLoginScanModal
          lang={lang}
          onClose={() => setShowQrLogin(false)}
          onLoggedIn={() => { setShowQrLogin(false); onComplete(); }}
        />
      )}
    </div>
  );
}

// ── Email Verification Screen ─────────────────────────────────────────────────
const EMAIL_VERIFY_TEXT = {
  bg: { title:'Потвърди имейла си', msg:'Изпратихме верификационен имейл на', instruction:'Отвори линка от имейла, за да потвърдиш акаунта си. След потвърждение — влез отново тук.', resend:'Изпрати отново', back:'Обратно към вход', sent:'Изпратено! Изчакай 60 секунди.' },
  en: { title:'Confirm your email', msg:'We sent a verification email to', instruction:'Open the link in the email to confirm your account. Then sign in here.', resend:'Resend email', back:'Back to login', sent:'Sent! Wait 60 seconds.' },
  es: { title:'Confirma tu email', msg:'Enviamos un email de verificación a', instruction:'Abre el enlace del email para confirmar tu cuenta. Luego inicia sesión aquí.', resend:'Reenviar email', back:'Volver al inicio', sent:'¡Enviado! Espera 60 segundos.' },
  fr: { title:'Confirmez votre email', msg:'Nous avons envoyé un email de vérification à', instruction:"Ouvre le lien dans l'email pour confirmer ton compte. Puis connecte-toi ici.", resend:"Renvoyer l'email", back:'Retour à la connexion', sent:'Envoyé ! Attendez 60 secondes.' },
  de: { title:'E-Mail bestätigen', msg:'Wir haben eine Bestätigungs-E-Mail an gesendet', instruction:'Öffne den Link in der E-Mail, um dein Konto zu bestätigen. Danach hier anmelden.', resend:'Erneut senden', back:'Zurück zur Anmeldung', sent:'Gesendet! Warte 60 Sekunden.' },
  el: { title:'Επιβεβαίωσε το email σου', msg:'Στείλαμε email επιβεβαίωσης στο', instruction:'Άνοιξε τον σύνδεσμο στο email για να επιβεβαιώσεις τον λογαριασμό σου. Μετά συνδέσου εδώ.', resend:'Αποστολή ξανά', back:'Πίσω στη σύνδεση', sent:'Στάλθηκε! Περίμενε 60 δευτερόλεπτα.' },
  it: { title:'Conferma la tua email', msg:'Abbiamo inviato un’email di verifica a', instruction:'Apri il link nell’email per confermare l’account. Poi accedi qui.', resend:'Reinvia email', back:'Torna al login', sent:'Inviata! Attendi 60 secondi.' },
};

function EmailVerificationScreen({ email, lang, onBack }) {
  const [cooldown, setCooldown] = useState(0);
  const [justSent, setJustSent] = useState(false);
  const ev = EMAIL_VERIFY_TEXT[lang] || EMAIL_VERIFY_TEXT.en;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function handleResend() {
    if (cooldown > 0 || !supabase) return;
    await supabase.auth.resend({ type: 'signup', email });
    setJustSent(true);
    setCooldown(60);
    setTimeout(() => setJustSent(false), 5000);
  }

  return (
    <div className="ts-app ts-register-bg">
      <div className="ts-register-wrap">
        <div className="ts-register-card" style={{textAlign:'center'}}>
          <div style={{margin:'0 auto 1.25rem',width:72,height:72,borderRadius:'50%',background:'rgba(201,168,76,0.12)',border:'2px solid var(--gold)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="1.5">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <h1 className="ts-serif ts-register-title">{ev.title}</h1>
          <p style={{color:'var(--muted)',marginBottom:'0.25rem'}}>{ev.msg}</p>
          <p style={{color:'var(--gold)',fontWeight:700,marginBottom:'1.25rem',wordBreak:'break-all'}}>{email}</p>
          <p style={{color:'var(--muted)',fontSize:'0.9rem',marginBottom:'1.5rem',lineHeight:1.5}}>{ev.instruction}</p>
          {justSent && <p style={{color:'#3fb950',fontSize:'0.85rem',marginBottom:'0.75rem'}}>{ev.sent}</p>}
          <button className="ts-btn-gold-full" onClick={handleResend} disabled={cooldown > 0} style={{opacity:cooldown>0?0.6:1,marginBottom:'0.75rem'}}>
            {cooldown > 0 ? `${ev.resend} (${cooldown}s)` : ev.resend}
          </button>
          <button className="ts-btn-ghost-sm" onClick={onBack} style={{width:'100%',padding:'0.65rem'}}>
            {ev.back}
          </button>
        </div>
      </div>
    </div>
  );
}

// Blocking age-verification gate. Shown post-login when a profile has no birth_date
// (e.g. OAuth signup, which collects none) or an existing birth_date implies under-18.
// No back/skip/close control by design — this is a legal requirement, not a UX preference.
function AgeVerificationGate({ t, lang, confirmedUnderage, onVerified, onSignOut }) {
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const ag = t.ageVerification;

  async function submit() {
    if (!birthDate || calcAge(birthDate) < 18) { setError(t.register.ageError); return; }
    setSaving(true);
    await patchProfile({ birth_date: birthDate });
    setSaving(false);
    onVerified();
  }

  if (confirmedUnderage) {
    return (
      <div className="ts-app ts-register-bg">
        <div className="ts-register-wrap">
          <div className="ts-register-card" style={{textAlign:'center'}}>
            <h1 className="ts-serif ts-register-title">{ag.underageTitle}</h1>
            <p style={{color:'var(--muted)',marginBottom:'1.5rem',lineHeight:1.5}}>{ag.underageMsg}</p>
            <button className="ts-btn-gold-full" onClick={onSignOut}>{ag.signOut}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ts-app ts-register-bg">
      <div className="ts-register-wrap">
        <div className="ts-register-card" style={{textAlign:'center'}}>
          <h1 className="ts-serif ts-register-title">{ag.title}</h1>
          <p style={{color:'var(--muted)',marginBottom:'1.25rem',lineHeight:1.5}}>{ag.msg}</p>
          <label className="ts-label" style={{display:'block',marginBottom:'1rem',textAlign:'left'}}>
            {t.register.birthDate}
            <input type="date" className="ts-input" value={birthDate}
              style={{colorScheme:'dark'}}
              onChange={e=>{ setBirthDate(e.target.value); setError(''); }} />
          </label>
          {error && <p style={{color:'#f85149',fontSize:'0.85rem',marginBottom:'0.75rem'}}>{error}</p>}
          <button className="ts-btn-gold-full" onClick={submit} disabled={!birthDate || saving}>
            {saving ? ag.saving : ag.submit}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Landing Screen ────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name:'Maria S.', flag:'🇧🇬', photo:'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=120&q=80', text:'Намерих най-добрия спътник за пътуване до Япония! TravelSoul промени живота ми.', trips:7, rating:5 },
  { name:'Marco R.', flag:'🇮🇹', photo:'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=80', text:"Amazing app! Met my travel partner and now we're planning our 3rd trip together!", trips:3, rating:5 },
  { name:'Léa M.',   flag:'🇫🇷', photo:'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=120&q=80', text:"J'ai trouvé l'amour de ma vie grâce à TravelSoul. On voyage ensemble depuis 1 an!", trips:12, rating:5 },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ts-faq-item" onClick={()=>setOpen(o=>!o)}>
      <div className="ts-faq-q"><span>{q}</span><span className="ts-faq-arrow">{open?'▴':'▾'}</span></div>
      {open && <p className="ts-faq-a">{a}</p>}
    </div>
  );
}

function LandingScreen({ t, lang, setLang, onStart, onLogin, onNavigate }) {
  const ld = t.landing || {};
  const how = ld.how || [];
  const [freeCount, setFreeCount] = useState(0);
  const [realUserCount, setRealUserCount] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');
  const offer = LANDING_OFFER_COPY[lang] || LANDING_OFFER_COPY.en;

  const aboutContent = {
    bg: { title:'За TravelSoul', subtitle:'Свързваме душевни пътешественици от цял свят', story:'TravelSoul се роди от проста идея — пътуването е по-красиво, когато го споделяш с правилния човек. Създадена от екип от пътешественици и влюбени, нашата платформа съчетава най-доброто от dating и travel apps.', mission:'Нашата мисия', missionText:'Да свързваме хора с еднакви пътнически мечти и да създаваме незабравими истории заедно.', team:'Нашият екип', teamText:'Екип от 12 души, работещи от Варна, България. Пътешественици, програмисти и мечтатели.', values:['❤️ Автентичност','✈️ Приключение','🔒 Сигурност','🌍 Разнообразие'], year:'Основана: 2024', location:'📍 Варна, България' },
    en: { title:'About TravelSoul', subtitle:'Connecting soulful travelers from around the world', story:'TravelSoul was born from a simple idea — travel is more beautiful when shared with the right person. Created by a team of travelers and romantics, our platform combines the best of dating and travel apps.', mission:'Our Mission', missionText:'To connect people with matching travel dreams and create unforgettable stories together.', team:'Our Team', teamText:'A team of 12 people working from Varna, Bulgaria. Travelers, developers and dreamers.', values:['❤️ Authenticity','✈️ Adventure','🔒 Safety','🌍 Diversity'], year:'Founded: 2024', location:'📍 Varna, Bulgaria' },
    es: { title:'Sobre TravelSoul', subtitle:'Conectando viajeros del alma de todo el mundo', story:'TravelSoul nació de una simple idea — viajar es más hermoso cuando lo compartes con la persona correcta.', mission:'Nuestra Misión', missionText:'Conectar personas con sueños de viaje compatibles y crear historias inolvidables juntos.', team:'Nuestro Equipo', teamText:'Un equipo de 12 personas trabajando desde Varna, Bulgaria.', values:['❤️ Autenticidad','✈️ Aventura','🔒 Seguridad','🌍 Diversidad'], year:'Fundada: 2024', location:'📍 Varna, Bulgaria' },
    fr: { title:'À propos de TravelSoul', subtitle:'Connecter des voyageurs du monde entier', story:"TravelSoul est né d'une idée simple — voyager est plus beau quand on le partage avec la bonne personne.", mission:'Notre Mission', missionText:'Connecter des personnes ayant des rêves de voyage compatibles et créer des histoires inoubliables ensemble.', team:'Notre Équipe', teamText:'Une équipe de 12 personnes travaillant depuis Varna, Bulgarie.', values:['❤️ Authenticité','✈️ Aventure','🔒 Sécurité','🌍 Diversité'], year:'Fondée: 2024', location:'📍 Varna, Bulgarie' },
    de: { title:'Über TravelSoul', subtitle:'Seelenverwandte Reisende aus aller Welt verbinden', story:'TravelSoul entstand aus einer einfachen Idee — Reisen ist schöner, wenn man es mit der richtigen Person teilt.', mission:'Unsere Mission', missionText:'Menschen mit übereinstimmenden Reiseträumen verbinden und unvergessliche Geschichten zusammen schaffen.', team:'Unser Team', teamText:'Ein Team von 12 Personen, die aus Varna, Bulgarien arbeiten.', values:['❤️ Authentizität','✈️ Abenteuer','🔒 Sicherheit','🌍 Vielfalt'], year:'Gegründet: 2024', location:'📍 Varna, Bulgarien' },
    el: { title:'Σχετικά με το TravelSoul', subtitle:'Συνδέουμε ταξιδιώτες με ψυχή από όλο τον κόσμο', story:'Το TravelSoul γεννήθηκε από μια απλή ιδέα: το ταξίδι είναι πιο όμορφο όταν το μοιράζεσαι με τον κατάλληλο άνθρωπο.', mission:'Η αποστολή μας', missionText:'Να συνδέουμε ανθρώπους με κοινά ταξιδιωτικά όνειρα και να δημιουργούμε αξέχαστες ιστορίες μαζί.', team:'Η ομάδα μας', teamText:'Μια ομάδα 12 ανθρώπων που εργάζεται από τη Βάρνα της Βουλγαρίας.', values:['❤️ Αυθεντικότητα','✈️ Περιπέτεια','🔒 Ασφάλεια','🌍 Ποικιλομορφία'], year:'Ίδρυση: 2024', location:'📍 Βάρνα, Βουλγαρία' },
    it: { title:'Informazioni su TravelSoul', subtitle:'Connettiamo viaggiatori con anima da tutto il mondo', story:'TravelSoul nasce da un’idea semplice: viaggiare è più bello quando lo condividi con la persona giusta.', mission:'La nostra missione', missionText:'Connettere persone con sogni di viaggio compatibili e creare insieme storie indimenticabili.', team:'Il nostro team', teamText:'Un team di 12 persone che lavora da Varna, Bulgaria.', values:['❤️ Autenticità','✈️ Avventura','🔒 Sicurezza','🌍 Diversità'], year:'Fondata: 2024', location:'📍 Varna, Bulgaria' },
  };
  const contactContent = {
    bg: { title:'Свържи се с нас', email:'Имейл', emailVal:'support@travelsoul.love', social:'Социални мрежи', response:'Отговаряме в рамките на 24 часа.', support:'Поддръжка', supportText:'За технически въпроси:', business:'Партньорства:', press:'Медии:' },
    en: { title:'Contact Us', email:'Email', emailVal:'support@travelsoul.love', social:'Social Media', response:'We respond within 24 hours.', support:'Support', supportText:'For technical questions:', business:'Partnerships:', press:'Press:' },
    es: { title:'Contáctanos', email:'Correo', emailVal:'support@travelsoul.love', social:'Redes Sociales', response:'Respondemos en 24 horas.', support:'Soporte', supportText:'Para preguntas técnicas:', business:'Asociaciones:', press:'Prensa:' },
    fr: { title:'Contactez-nous', email:'Email', emailVal:'support@travelsoul.love', social:'Réseaux Sociaux', response:'Nous répondons dans les 24 heures.', support:'Support', supportText:'Pour les questions techniques:', business:'Partenariats:', press:'Presse:' },
    de: { title:'Kontaktiere uns', email:'E-Mail', emailVal:'support@travelsoul.love', social:'Soziale Medien', response:'Wir antworten innerhalb von 24 Stunden.', support:'Support', supportText:'Für technische Fragen:', business:'Partnerschaften:', press:'Presse:' },
    el: { title:'Επικοινώνησε μαζί μας', email:'Email', emailVal:'support@travelsoul.love', social:'Κοινωνικά δίκτυα', response:'Απαντάμε μέσα σε 24 ώρες.', support:'Υποστήριξη', supportText:'Για τεχνικές ερωτήσεις:', business:'Συνεργασίες:', press:'Τύπος:' },
    it: { title:'Contattaci', email:'Email', emailVal:'support@travelsoul.love', social:'Social media', response:'Rispondiamo entro 24 ore.', support:'Supporto', supportText:'Per domande tecniche:', business:'Partnership:', press:'Stampa:' },
  };
  const aboutT = aboutContent[lang] || aboutContent.en;
  const contactT = contactContent[lang] || contactContent.en;
  useEffect(() => {
    if (!supabase) return;
    supabase.from('profiles').select('id', { count: 'exact', head: true })
      .not('is_admin', 'is', true)
      .not('is_moderator', 'is', true)
      .then(({ count }) => {
        const c = count || 0;
        setFreeCount(c);
        setRealUserCount(c);
      });
  }, []);
  return (
    <div className="ts-app ts-landing" style={{overflowY:'auto'}}>
      <Starfield />
      <Header t={t} lang={lang} setLang={setLang} screen="landing" onLogoClick={()=>{}} onLogin={onLogin} />
      <main className="ts-landing-main">

        {/* HERO */}
        <div className="ts-hero">
          <p className="ts-hero-badge">{ld.heroBadge||'✦ Soul-first travel'}</p>
          <div className="ts-hero-trusted">{
            realUserCount === null ? '' :
            lang==='bg' ? `🌟 ${realUserCount.toLocaleString()} пътешественици вече са с нас` :
            lang==='es' ? `🌟 ${realUserCount.toLocaleString()} viajeros ya están con nosotros` :
            lang==='fr' ? `🌟 ${realUserCount.toLocaleString()} voyageurs nous font déjà confiance` :
            lang==='de' ? `🌟 ${realUserCount.toLocaleString()} Reisende sind bereits dabei` :
            lang==='el' ? `🌟 ${realUserCount.toLocaleString()} ταξιδιώτες είναι ήδη μαζί μας` :
            lang==='it' ? `🌟 ${realUserCount.toLocaleString()} viaggiatori sono già con noi` :
            `🌟 ${realUserCount.toLocaleString()} travelers have joined`
          }</div>
          <h1 className="ts-hero-title ts-serif">
            <span className="ts-hero-line line1">{t.tagline[0]}</span>
            <span className="ts-hero-line line2">{t.tagline[1]}</span>
            <span className="ts-hero-line line3 gold">{t.tagline[2]}</span>
          </h1>
          {freeCount < FIRST_1000_FREE_PREMIUM_LIMIT && (
            <div style={{background:'linear-gradient(135deg,#c9a84c,#f0d080)',borderRadius:12,padding:'12px 20px',marginBottom:16,textAlign:'center',color:'#1a1a2e'}}>
              <div style={{fontSize:20,fontWeight:700}}>{offer.title}</div>
              <div style={{fontSize:14,margin:'4px 0'}}>{offer.first} <b>{FIRST_1000_FREE_PREMIUM_LIMIT}</b> {offer.usersGet} <b>{offer.premiumFree}</b> {offer.forDays} {FIRST_1000_FREE_PREMIUM_DAYS} {offer.days}</div>
              <div style={{fontSize:24,fontWeight:800}}>{offer.spotsLeft(FIRST_1000_FREE_PREMIUM_LIMIT-freeCount)}</div>
            </div>
          )}
          <div className="ts-hero-btns">
            <button className="ts-btn-gold-lg" onClick={onStart}>{t.cta} →</button>
            <button className="ts-btn-ghost-sm ts-hero-demo" onClick={()=>setShowDemo(true)}>{ld.watchDemo||'Watch Demo ▶'}</button>
          </div>
        </div>


        {/* FEATURES */}
        <div className="ts-features">
          {t.features.map((f,i)=>(
            <div key={i} className="ts-feature-card" style={{animationDelay:`${i*0.12}s`}}>
              <span className="ts-feature-icon">{f.icon}</span>
              <h3 className="ts-feature-title ts-serif">{f.title}</h3>
              <p className="ts-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>

        <ComplianceNotice lang={lang} />

        {/* HOW IT WORKS */}
        {how.length > 0 && (
          <div className="ts-how-section">
            <h2 className="ts-serif ts-section-heading">{ld.howTitle}</h2>
            <div className="ts-how-steps">
              {how.map((h,i)=>(
                <div key={i} className="ts-how-step">
                  <div className="ts-how-icon">{h.icon}</div>
                  <h3 className="ts-how-title ts-serif">{h.title}</h3>
                  <p className="ts-how-desc">{h.desc}</p>
                  {i < how.length-1 && <div className="ts-how-connector">→</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TESTIMONIALS */}
        <div className="ts-testimonials-section">
          <h2 className="ts-serif ts-section-heading">{ld.travelerStories||'❤️ Traveler Stories'}</h2>
          <div className="ts-testimonials-grid">
            {TESTIMONIALS.map((testimonial,i)=>(
              <div key={i} className="ts-testimonial-card">
                <div className="ts-testimonial-quote">❝</div>
                <p className="ts-testimonial-text">{testimonial.text}</p>
                <div className="ts-testimonial-footer">
                  <img src={testimonial.photo} alt={testimonial.name} className="ts-testimonial-photo"/>
                  <div>
                    <p className="ts-testimonial-name">{testimonial.name} {testimonial.flag}</p>
                    <div className="ts-testimonial-stars">{'★'.repeat(testimonial.rating)}</div>
                  </div>
                  <span className="ts-testimonial-trips">{testimonial.trips} {t.journeys?.tripsLabel||'trips'} ✈️</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="ts-faq-section">
          <h2 className="ts-serif ts-section-heading">{ld.faqTitle||'FAQ'}</h2>
          <div className="ts-faq-list">
            {(FAQ_DATA_BY_LANG[lang] || FAQ_DATA_BY_LANG.en).map((item,i)=><FaqItem key={i} q={item.q} a={item.a}/>)}
          </div>
        </div>

        {/* DOWNLOAD BANNER */}
        <div className="ts-download-banner">
          <p className="ts-download-title">{ld.appStoreBanner||'📱 Coming soon to App Store & Google Play'}</p>
          <div className="ts-app-store-btns" style={{display:'flex',gap:20,flexWrap:'wrap',justifyContent:'center'}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
              <button
                type="button"
                className="ts-app-store-btn"
                disabled={!APP_STORE_URL}
                style={!APP_STORE_URL ? {opacity:0.5,cursor:'not-allowed'} : undefined}
                onClick={() => { if (APP_STORE_URL) window.open(APP_STORE_URL, '_blank', 'noopener'); }}
                title={!APP_STORE_URL ? (lang==='bg'?'Скоро':lang==='es'?'Próximamente':lang==='fr'?'Bientôt':lang==='de'?'Demnächst':lang==='el'?'Σύντομα':lang==='it'?'Prossimamente':'Coming soon') : undefined}
              >
                🍎 App Store{!APP_STORE_URL ? ` · ${lang==='bg'?'Скоро':lang==='es'?'Pronto':lang==='fr'?'Bientôt':lang==='de'?'Bald':lang==='el'?'Σύντομα':lang==='it'?'Presto':'Soon'}` : ''}
              </button>
              <AppDownloadQrCode url={APP_STORE_URL} disabledLabel={lang==='bg'?'Очаквайте скоро':lang==='es'?'Próximamente':lang==='fr'?'Bientôt disponible':lang==='de'?'Demnächst verfügbar':lang==='el'?'Σύντομα διαθέσιμο':lang==='it'?'Presto disponibile':'Coming soon'} />
            </div>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
              <button
                type="button"
                className="ts-app-store-btn"
                onClick={() => window.open(GOOGLE_PLAY_URL, '_blank', 'noopener')}
              >
                🤖 Google Play
              </button>
              <AppDownloadQrCode url={GOOGLE_PLAY_URL} />
            </div>
          </div>
          {/* TODO: CREATE TABLE waitlist (id uuid DEFAULT gen_random_uuid(), email text, lang text, created_at timestamptz DEFAULT now()); */}
          {waitlistDone ? (
            <div style={{textAlign:'center',color:'#4caf50',fontWeight:'700',padding:'12px'}}>
              {lang==='bg'?'✅ Записан си! Ще те уведомим при старта.':lang==='es'?'✅ ¡Apuntado! Te avisaremos en el lanzamiento.':lang==='fr'?'✅ Inscrit ! On te préviendra au lancement.':lang==='de'?'✅ Eingetragen! Wir benachrichtigen dich beim Start.':lang==='el'?'✅ Καταχωρήθηκε! Θα σε ενημερώσουμε κατά την έναρξη.':lang==='it'?'✅ Registrato! Ti avviseremo al lancio.':'✅ You\'re in! We\'ll notify you at launch.'}
            </div>
          ) : (
            <div className="ts-waitlist-row">
              <input
                className="ts-input ts-waitlist-input"
                placeholder="your@email.com"
                value={waitlistEmail}
                onChange={e=>{setWaitlistEmail(e.target.value);setWaitlistError('');}}
              />
              <button
                className="ts-btn-gold-full ts-waitlist-btn"
                disabled={waitlistLoading}
                onClick={async()=>{
                  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistEmail.trim());
                  if (!valid) { setWaitlistError(lang==='bg'?'Невалиден имейл':lang==='es'?'Email inválido':lang==='fr'?'Email invalide':lang==='de'?'Ungültige E-Mail':lang==='el'?'Μη έγκυρο email':lang==='it'?'Email non valida':'Invalid email'); return; }
                  setWaitlistLoading(true);
                  try {
                    if (supabase) await supabase.from('waitlist').insert({ email: waitlistEmail.trim(), lang, created_at: new Date().toISOString() });
                    setWaitlistDone(true);
                  } finally { setWaitlistLoading(false); }
                }}
              >
                {waitlistLoading ? '...' : (ld.joinWaitlist||'Join Waitlist')}
              </button>
            </div>
          )}
          {waitlistError && <p style={{color:'#ff6b6b',fontSize:'13px',textAlign:'center',margin:'4px 0 0'}}>{waitlistError}</p>}
        </div>

        {/* FOOTER */}
        <PublicFooter lang={lang} onNavigate={onNavigate} />

        {/* DEMO VIDEO MODAL */}
        {showDemo && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={()=>setShowDemo(false)}>
            <div style={{background:'#1a1a2e',borderRadius:'20px',width:'100%',maxWidth:'700px',position:'relative',padding:'24px'}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>setShowDemo(false)} style={{position:'absolute',top:'12px',right:'12px',background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'50%',width:'32px',height:'32px',color:'#fff',fontSize:'18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
              <h2 style={{color:'#ffd700',fontWeight:'900',marginBottom:'16px',fontSize:'20px'}}>{lang==='bg'?'Демо видео':lang==='es'?'Video Demo':lang==='fr'?'Vidéo Démo':lang==='de'?'Demo Video':lang==='el'?'Demo βίντεο':lang==='it'?'Video demo':'Demo Video'} ▶</h2>
              {/* TODO: върни демо видео когато е готов YouTube линк */}
              {/* <iframe width="100%" height="315" src="https://www.youtube.com/embed/dQw4w9WgXcQ" title="TravelSoul Demo" frameBorder="0" allowFullScreen style={{borderRadius:12,display:'block'}} /> */}
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'48px 24px',gap:'16px'}}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <h3 style={{color:'#ffd700',fontWeight:'900',fontSize:'28px',margin:0,textAlign:'center'}}>
                  {lang==='bg'?'Скоро':lang==='es'?'Próximamente':lang==='fr'?'Bientôt disponible':lang==='de'?'Demnächst':lang==='el'?'Σύντομα':lang==='it'?'Prossimamente':'Coming soon'}
                </h3>
                <p style={{color:'rgba(255,255,255,0.6)',fontSize:'15px',textAlign:'center',margin:0}}>
                  {lang==='bg'?'Демо видеото идва съвсем скоро!':lang==='es'?'¡El vídeo de demostración llegará muy pronto!':lang==='fr'?'La vidéo de démonstration arrive très bientôt !':lang==='de'?'Das Demo-Video kommt sehr bald!':lang==='el'?'Το demo βίντεο έρχεται πολύ σύντομα!':lang==='it'?'Il video demo arriva molto presto!':'The demo video is coming very soon!'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ABOUT MODAL */}
        {showAbout && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,overflowY:'auto',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}} onClick={()=>setShowAbout(false)}>
            <div style={{background:'#1a1a2e',borderRadius:'20px',maxWidth:'600px',width:'100%',position:'relative'}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>setShowAbout(false)} style={{position:'absolute',top:'16px',right:'16px',background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'50%',width:'32px',height:'32px',color:'#fff',fontSize:'18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
              <div style={{padding:'24px',maxWidth:'600px',margin:'0 auto'}}>
                <h2 style={{fontSize:'24px',fontWeight:'900',color:'#ffd700',marginBottom:'8px'}}>{aboutT.title}</h2>
                <p style={{color:'#aaa',marginBottom:'20px',fontStyle:'italic'}}>{aboutT.subtitle}</p>
                <p style={{color:'#ddd',lineHeight:'1.6',marginBottom:'20px'}}>{aboutT.story}</p>
                <h3 style={{color:'#ffd700',marginBottom:'8px'}}>{aboutT.mission}</h3>
                <p style={{color:'#ddd',lineHeight:'1.6',marginBottom:'20px'}}>{aboutT.missionText}</p>
                <h3 style={{color:'#ffd700',marginBottom:'8px'}}>{aboutT.team}</h3>
                <p style={{color:'#ddd',lineHeight:'1.6',marginBottom:'20px'}}>{aboutT.teamText}</p>
                <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginBottom:'20px'}}>
                  {aboutT.values.map((v,i) => <span key={i} style={{background:'rgba(255,215,0,0.15)',border:'1px solid rgba(255,215,0,0.3)',borderRadius:'20px',padding:'6px 14px',color:'#ffd700',fontSize:'14px'}}>{v}</span>)}
                </div>
                <p style={{color:'#888',fontSize:'13px'}}>{aboutT.year} · {aboutT.location}</p>
              </div>
            </div>
          </div>
        )}

        {/* CONTACT MODAL */}
        {showContact && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,overflowY:'auto',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}} onClick={()=>setShowContact(false)}>
            <div style={{background:'#1a1a2e',borderRadius:'20px',maxWidth:'500px',width:'100%',position:'relative'}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>setShowContact(false)} style={{position:'absolute',top:'16px',right:'16px',background:'rgba(255,255,255,0.1)',border:'none',borderRadius:'50%',width:'32px',height:'32px',color:'#fff',fontSize:'18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
              <div style={{padding:'24px',maxWidth:'500px',margin:'0 auto'}}>
                <h2 style={{fontSize:'24px',fontWeight:'900',color:'#ffd700',marginBottom:'20px'}}>{contactT.title}</h2>
                <div style={{background:'rgba(255,215,0,0.1)',border:'1px solid rgba(255,215,0,0.3)',borderRadius:'16px',padding:'20px',marginBottom:'16px'}}>
                  <div style={{color:'#aaa',fontSize:'13px',marginBottom:'4px'}}>{contactT.email}</div>
                  <a href={'mailto:'+contactT.emailVal} onClick={e=>openExternalUrl('mailto:'+contactT.emailVal, e)} style={{color:'#ffd700',fontSize:'18px',fontWeight:'700',textDecoration:'none'}}>{contactT.emailVal}</a>
                </div>
                <div style={{background:'rgba(255,255,255,0.05)',borderRadius:'16px',padding:'20px',marginBottom:'16px'}}>
                  <div style={{color:'#ffd700',fontWeight:'700',marginBottom:'12px'}}>{contactT.support}</div>
                  <p style={{color:'#aaa',fontSize:'14px',margin:'0 0 6px'}}>{contactT.supportText} <a href="mailto:support@travelsoul.love" onClick={e=>openExternalUrl('mailto:support@travelsoul.love', e)} style={{color:'#ffd700'}}>support@travelsoul.love</a></p>
                  <p style={{color:'#aaa',fontSize:'14px',margin:'0 0 6px'}}>{contactT.business} <a href="mailto:support@travelsoul.love" onClick={e=>openExternalUrl('mailto:support@travelsoul.love', e)} style={{color:'#ffd700'}}>support@travelsoul.love</a></p>
                  <p style={{color:'#aaa',fontSize:'14px',margin:'0'}}>{contactT.press} <a href="mailto:support@travelsoul.love" onClick={e=>openExternalUrl('mailto:support@travelsoul.love', e)} style={{color:'#ffd700'}}>support@travelsoul.love</a></p>
                </div>
                <p style={{color:'#666',fontSize:'13px',textAlign:'center'}}>{contactT.response}</p>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────
// ── Error Boundary ────────────────────────────────────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('TravelSoul Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100vh',background:'#0d1117',color:'#fff',textAlign:'center',padding:20}}>
          <div style={{fontSize:48,marginBottom:16}}>✈️</div>
          <h2 style={{color:'#f5c518',marginBottom:8}}>Oops! Something went wrong</h2>
          <p style={{color:'#8b949e',marginBottom:24}}>We're sorry for the inconvenience. Please refresh the page.</p>
          <button onClick={()=>window.location.reload()} style={{background:'#f5c518',color:'#000',border:'none',borderRadius:12,padding:'12px 24px',fontWeight:700,cursor:'pointer',fontSize:16}}>
            🔄 Refresh Page
          </button>
          <button
            onClick={() => {
              const keys = [
                'travelsoul_user','travelsoul_profile','travelsoul_photo',
                'travelsoul_email','travelsoul_pass',
                'travelsoul_password','ts_plan',
                'ts_last_spin','ts_spin_history','ts_incognito','ts_horoscope','ts_horoscope_sign',
                'ts_mood_travel','travelsoul_saved_trips','travelsoul_blocked'
              ];
              keys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(regs => {
                  regs.forEach(r => r.unregister());
                  window.location.reload();
                });
              } else {
                window.location.reload();
              }
            }}
            style={{ marginLeft: '10px', background:'#30363d',color:'#fff',border:'none',borderRadius:12,padding:'12px 24px',fontWeight:700,cursor:'pointer',fontSize:16 }}
          >
            Reset app data and reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Audit finding: the only ErrorBoundary in the app wraps the ENTIRE <App/>
// tree at the root (src/index.js), full-screen, reload-only. A crash in one
// tab's render (a bad profile field, a malformed message payload, etc.) took
// down the whole app and forced a full reload — losing whatever state the
// person had in other tabs/modals. TabErrorBoundary is a lighter, nested
// variant: it shows a small inline fallback (not full-screen) and can reset
// itself via a `resetKey` prop change (e.g. switching tabs) instead of
// forcing a reload, so one broken tab/modal doesn't take out the others.
export class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('TravelSoul tab error:', error, info); }
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:300,color:'#fff',textAlign:'center',padding:20}}>
          <div style={{fontSize:36,marginBottom:12}}>⚠️</div>
          <p style={{color:'#8b949e',marginBottom:16,fontSize:'0.9rem'}}>{this.props.lang==='bg'?'Нещо се обърка тук. Опитай пак.':this.props.lang==='es'?'Algo salió mal aquí. Por favor, inténtalo de nuevo.':this.props.lang==='fr'?'Une erreur s\'est produite ici. Veuillez réessayer.':this.props.lang==='de'?'Hier ist etwas schiefgelaufen. Bitte versuche es erneut.':this.props.lang==='el'?'Κάτι πήγε στραβά. Δοκίμασε ξανά.':this.props.lang==='it'?'Qualcosa è andato storto. Riprova.':'Something went wrong here. Please try again.'}</p>
          <button onClick={()=>this.setState({ hasError: false })} style={{background:'#f5c518',color:'#000',border:'none',borderRadius:12,padding:'10px 20px',fontWeight:700,cursor:'pointer',fontSize:14}}>
            🔄 {this.props.lang==='bg'?'Опитай пак':this.props.lang==='es'?'Reintentar':this.props.lang==='fr'?'Réessayer':this.props.lang==='de'?'Erneut versuchen':this.props.lang==='el'?'Δοκίμασε ξανά':this.props.lang==='it'?'Riprova':'Retry'}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── GDPR Cookie Consent Banner ────────────────────────────────────────────────
function CookieBanner({ lang, onOpenPrivacy }) {
  const [shown] = useState(() => !localStorage.getItem('ts_cookie_consent'));
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(shown);

  useEffect(() => {
    if (shown) setTimeout(() => setVisible(true), 400);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mounted) return null;

  const hide = (value) => {
    localStorage.setItem('ts_cookie_consent', value);
    setVisible(false);
    setTimeout(() => setMounted(false), 350);
  };

  const MSGS = {
    bg: 'Използваме бисквитки за по-добро изживяване. Приемаш ли?',
    en: 'We use cookies to improve your experience. Do you accept?',
    es: 'Usamos cookies para mejorar tu experiencia. ¿Aceptas?',
    fr: 'Nous utilisons des cookies pour améliorer votre expérience. Acceptez-vous ?',
    de: 'Wir verwenden Cookies für ein besseres Erlebnis. Akzeptierst du?',
    el: 'Χρησιμοποιούμε cookies για να βελτιώσουμε την εμπειρία σου. Αποδέχεσαι;',
    it: 'Usiamo i cookie per migliorare la tua esperienza. Accetti?',
  };
  const ACCEPT = { bg:'Приемам', en:'Accept', es:'Aceptar', fr:'Accepter', de:'Akzeptieren', el:'Αποδοχή', it:'Accetta' };
  const DECLINE = { bg:'Отказвам', en:'Decline', es:'Rechazar', fr:'Refuser', de:'Ablehnen', el:'Απόρριψη', it:'Rifiuta' };
  const PRIVACY = { bg:'Поверителност', en:'Privacy Policy', es:'Privacidad', fr:'Confidentialité', de:'Datenschutz', el:'Πολιτική απορρήτου', it:'Privacy' };

  const l = (map) => map[lang] || map.en;

  return (
    <div className="ts-cookie-banner" style={{
      transform: visible ? 'translateY(0)' : 'translateY(110%)',
      transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      pointerEvents: 'none',
    }}>
      <div style={{
        background:'#161b22', border:'1px solid #30363d', borderRadius:16,
        padding:'16px 20px', maxWidth:600, width:'100%',
        boxShadow:'0 -4px 32px rgba(0,0,0,0.6)',
        pointerEvents:'all',
      }}>
        <p style={{color:'#f0f6fc', fontSize:13, margin:'0 0 12px', lineHeight:1.6}}>
          {l(MSGS)}{' '}
          <button onClick={onOpenPrivacy} style={{
            background:'none', border:'none', color:'#c9a84c', fontSize:13,
            cursor:'pointer', textDecoration:'underline', padding:0,
          }}>{l(PRIVACY)}</button>
        </p>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <button onClick={() => hide('accepted')} style={{
            flex:1, minWidth:100,
            background:'linear-gradient(90deg,#c9a84c,#e8b923)',
            border:'none', borderRadius:10, padding:'10px 16px',
            color:'#000', fontWeight:700, fontSize:13, cursor:'pointer',
          }}>{l(ACCEPT)}</button>
          <button onClick={() => hide('declined')} style={{
            flex:1, minWidth:100,
            background:'#21262d', border:'1px solid #30363d',
            borderRadius:10, padding:'10px 16px',
            color:'#8b949e', fontWeight:600, fontSize:13, cursor:'pointer',
          }}>{l(DECLINE)}</button>
        </div>
      </div>
    </div>
  );
}

const decodeJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
};

const getAuthProvider = (session) => {
  // 1. Provider captured at click time — most reliable for linked accounts
  const captured = sessionStorage.getItem('ts_oauth_provider');
  if (captured === 'google' || captured === 'facebook') return captured;
  // 2. AMR claim: password/otp/email methods mean email login
  if (session?.access_token) {
    const claims = decodeJwt(session.access_token);
    const amr = claims?.amr;
    if (Array.isArray(amr) && amr.length > 0) {
      const m = amr[amr.length - 1]?.method;
      if (m === 'password' || m === 'otp' || m === 'email') return 'email';
    }
  }
  // 3. Single-identity fallback
  const providers = session?.user?.app_metadata?.providers;
  if (Array.isArray(providers) && providers.length === 1) {
    const only = providers[0];
    if (only === 'google' || only === 'facebook') return only;
  }
  const single = session?.user?.app_metadata?.provider;
  if (single === 'google' || single === 'facebook') return single;
  return 'email';
};

const providerLabel = {
  email:    { bg:'имейл',    en:'email',    es:'correo',   fr:'e-mail',  de:'E-Mail', el:'email', it:'email' },
  google:   { bg:'Google',   en:'Google',   es:'Google',   fr:'Google',  de:'Google', el:'Google', it:'Google' },
  facebook: { bg:'Facebook', en:'Facebook', es:'Facebook', fr:'Facebook',de:'Facebook', el:'Facebook', it:'Facebook' },
};
const providerIcon = { email:'✉️', google:'🔵', facebook:'🔷' };
const FIRST_1000_FREE_PREMIUM_LIMIT = 1000;
const FIRST_1000_FREE_PREMIUM_DAYS = 7;

function resolveEffectivePlan(profileRow) {
  if (!profileRow) return null;
  if (profileRow.plan === 'free') return 'free';
  if (!['premium','elite'].includes(profileRow.plan)) return null;
  const premiumUntil = Date.parse(profileRow.premium_until || '');
  if (!Number.isFinite(premiumUntil) || premiumUntil <= Date.now()) return 'free';
  return profileRow.plan;
}

async function grantFirst1000FreePremium() {
  if (!supabase) return null;
  const res = await sbFetch('POST', 'rpc/grant_first_1000_free_premium', '', {
    promo_days: FIRST_1000_FREE_PREMIUM_DAYS,
  });
  await ensureSbWriteOk(res, 'first 1000 premium grant');
  try {
    const body = await res.text();
    return body ? JSON.parse(body) : null;
  } catch (e) {
    return null;
  }
}

async function insertProfileWithRetry(supabase, profileData, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    const { error } = await supabase.from('profiles').insert(profileData);
    if (!error) return true;
    if (error.code === '23505') return true; // duplicate — already exists, fine
    const is401 = error.code === '42501' || error.status === 401 || (error.message||'').includes('row-level security');
    if (is401 && i < maxRetries - 1) {
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
      try { await supabase.auth.refreshSession(); } catch(e) {}
      continue;
    }
    return false;
  }
  return false;
}

export default function App() {
  const getPublicScreenForCurrentLocation = () => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (hash === '#privacy-policy' || path === '/privacy') return 'privacy';
    if (hash === '#terms' || path === '/terms') return 'terms';
    if (hash === '#refund' || path === '/refund') return 'refund';
    if (path === '/account-deletion') return 'accountDeletion';
    if (path === '/child-safety') return 'childSafety';
    if (path === '/pricing') return 'pricing';
    if (path === '/contact') return 'contact';
    return null;
  };
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem('travelsoul_lang');
    if (stored && VALID_LANGS.includes(stored)) return stored;
    if (stored) { try { localStorage.removeItem('travelsoul_lang'); } catch {} }
    return detectLang();
  });
  const [screen, setScreen] = useState(() => {
    return getPublicScreenForCurrentLocation() || 'loading';
  });
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [, setNeedsAgeVerification] = useState(false);
  const [confirmedUnderage, setConfirmedUnderage] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  const [deletedMsg, setDeletedMsg] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(false);
  const [plan, setPlan] = useState('free');
  const [planExpiresAt, setPlanExpiresAt] = useState(null);
  const loadAppDataRef = useRef(null);
  const syncUserFromSessionRef = useRef(null);
  const pricingBackScreenRef = useRef(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [coinBalance, setCoinBalance] = useState(() => {
    const saved = localStorage.getItem('travelsoul_coins');
    if (!saved) { localStorage.setItem('travelsoul_coins', '100'); return 100; }
    return parseInt(saved, 10) || 100;
  });
  const [premiumModal, setPremiumModal] = useState(null);
  const [incognito, setIncognito] = useState(false);
  const [loginToast, setLoginToast] = useState(null);
  const [authProvider, setAuthProvider] = useState('email');
  useEffect(() => {
    if (!['premium','elite'].includes(plan) || !planExpiresAt) return undefined;
    let timerId;
    const expireWhenDue = () => {
      const remainingMs = Date.parse(planExpiresAt) - Date.now();
      if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
        setPlan('free');
        setPlanExpiresAt(null);
        localStorage.setItem('ts_plan', 'free');
        return;
      }
      timerId = setTimeout(expireWhenDue, Math.min(remainingMs, 60 * 60 * 1000));
    };
    expireWhenDue();
    return () => clearTimeout(timerId);
  }, [plan, planExpiresAt]);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setAuthProvider(getAuthProvider(session));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isCapacitorAndroid()) return;
    let listenerHandle = null;
    const onAppUrlOpen = async (event) => {
      const url = event?.url || event?.detail?.url;
      if (typeof url !== 'string' || !url.startsWith('travelsoul://auth-callback')) return;
      try {
        if (!supabase) {
          console.error('Android OAuth callback failed: Supabase client is not available.');
          return;
        }
        const parsed = new URL(url);
        const searchParams = new URLSearchParams(parsed.search);
        const hashParams = new URLSearchParams(parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash);
        const getParam = (key) => searchParams.get(key) || hashParams.get(key);
        const code = getParam('code');
        const accessToken = getParam('access_token');
        const refreshToken = getParam('refresh_token');

        let authResult = null;
        const withTimeout = (promise, ms, label) => Promise.race([
          promise,
          new Promise(resolve => setTimeout(() => resolve({ __timeout: true, label }), ms)),
        ]);
        let callbackSessionApplied = false;
        const applyCallbackSession = async (session, user, source) => {
          if (callbackSessionApplied) {
            return;
          }
          callbackSessionApplied = true;
          const activeSession = session || { user };
          syncUserFromSessionRef.current?.(activeSession);
          setAuthProvider(getAuthProvider(activeSession));
          setIsLoadingPlan(false);

          try {
            const { data: existingProfile } = await supabase.from('profiles').select('id').eq('id', user.id).single();
            if (!existingProfile) {
              let pendingProfile = {};
              try { pendingProfile = JSON.parse(localStorage.getItem('ts_pending_profile') || '{}'); } catch(e) {}
              await insertProfileWithRetry(supabase, {
                id: user.id,
                name: user.user_metadata?.full_name || user.user_metadata?.name ||
                      (user.user_metadata?.firstName ? `${user.user_metadata.firstName} ${user.user_metadata.lastName || ''}`.trim() : null) ||
                      'Traveler',
                photo_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
                country: 'BG',
                gender: 'male',
                birth_date: pendingProfile?.birthDate || null,
                created_at: new Date().toISOString()
              });
            }
            localStorage.removeItem('ts_pending_profile');
          } catch (profileErr) {
            console.error('Android OAuth profile setup failed:', profileErr);
          }

          let isOnboarded = localStorage.getItem('travelsoul_onboarded') === 'true';
          let needsAgeVerification = false;
          if (loadAppDataRef.current) {
            const loaded = await Promise.race([
              loadAppDataRef.current(user.id),
              new Promise(resolve => setTimeout(() => resolve('__timeout__'), 5000)),
            ]);
            if (loaded !== '__timeout__') {
              isOnboarded = !!loaded.isOnboarded;
              needsAgeVerification = !!loaded.needsAgeVerification;
            }
          }
          setScreen(needsAgeVerification ? 'ageVerification' : (isOnboarded ? 'app' : 'onboarding'));
        };
        if (code) {
          try {
            authResult = await supabase.auth.exchangeCodeForSession(code);
          } catch (exchangeErr) {
            console.error('Android OAuth code exchange failed:', exchangeErr);
            return;
          }
        } else if (accessToken && refreshToken) {
          authResult = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        } else {
          console.error('Android OAuth callback failed: no code or token pair in callback URL.');
          return;
        }

        if (authResult?.error) {
          console.error('Android OAuth callback auth error:', authResult.error);
          return;
        }

        const sessionResult = await withTimeout(supabase.auth.getSession(), 5000, 'getSession');
        const session = sessionResult?.__timeout ? null : sessionResult?.data?.session;
        const userResult = await withTimeout(supabase.auth.getUser(), 5000, 'getUser');
        const currentUser = userResult?.__timeout ? null : userResult?.data?.user;
        const userError = userResult?.__timeout ? null : userResult?.error;
        if (userError) console.error('Android OAuth callback getUser error:', userError);
        const user = currentUser || session?.user || authResult?.data?.user || authResult?.data?.session?.user || null;
        if (!user) {
          console.error('Android OAuth callback failed: no user after session exchange.');
          return;
        }
        await applyCallbackSession(session || authResult?.data?.session || null, user, 'callback');
        try { await Browser.close(); } catch (browserErr) {}
      } catch (err) {
        console.error('Android OAuth callback failed:', err);
      }
    };
    CapacitorApp.addListener('appUrlOpen', onAppUrlOpen)
      .then(handle => { listenerHandle = handle; })
      .catch(err => { console.error('Android appUrlOpen listener failed:', err); });
    return () => {
      if (listenerHandle?.remove) listenerHandle.remove();
    };
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [joinedTrips, setJoinedTrips] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showPremiumBonus, setShowPremiumBonus] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [showMod, setShowMod] = useState(false);

  function resetAdminUiState() {
    setIsAdmin(false);
    setIsModerator(false);
    setShowAdmin(false);
    setShowMod(false);
    const clearStaffFields = (key) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const cached = JSON.parse(raw);
        if (!cached || typeof cached !== 'object') return;
        delete cached.is_admin;
        delete cached.is_moderator;
        delete cached.isAdmin;
        delete cached.isModerator;
        delete cached.admin;
        delete cached.moderator;
        if (['admin', 'moderator', 'staff'].includes(String(cached.role || '').toLowerCase())) {
          delete cached.role;
        }
        localStorage.setItem(key, JSON.stringify(cached));
      } catch {}
    };
    clearStaffFields('travelsoul_user');
    clearStaffFields('travelsoul_profile');
    [
      'isAdmin',
      'isModerator',
      'showAdmin',
      'ts_is_admin',
      'ts_is_moderator',
      'ts_show_admin',
      'travelsoul_is_admin',
      'travelsoul_is_moderator',
    ].forEach((key) => {
      try { localStorage.removeItem(key); } catch {}
      try { sessionStorage.removeItem(key); } catch {}
    });
  }

  // Audit finding: this effect read userId fresh every time it ran, but only
  // re-ran on [screen] — if the logged-in user changed (account switch)
  // while screen stayed 'app' throughout, the channel kept its stale
  // subscription filtered to the FIRST user's id forever. Track userId
  // explicitly and depend on it too, so a user change re-subscribes.
  const notifUserId = getAuthToken().userId;
  useEffect(() => {
    if (screen !== 'app') return;
    const { userId } = getAuthToken();
    if (!userId || !supabase) return;
    const fetchNotifs = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data: prof } = await supabase.from('profiles').select('notifications_cleared_at').eq('id', userId).single();
      const clearedAt = prof?.notifications_cleared_at || '2000-01-01T00:00:00Z';
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .gt('created_at', clearedAt)
        .order('created_at', { ascending: false })
        .limit(20);
      if (data) setNotifications(data.map(n => ({
        icon: n.icon || '🔔',
        title: n.title,
        body: formatNotifBody(n.body),
        time: new Date(n.created_at).getTime()
      })));
    };
    fetchNotifs();
    const channel = supabase.channel('notifs_' + userId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        payload => {
          const n = payload.new;
          const formattedBody = formatNotifBody(n.body);
          setNotifications(prev => [{ icon: n.icon||'🔔', title: n.title, body: formattedBody, time: new Date(n.created_at).getTime() }, ...prev]);
          sendNotification(n.title, formattedBody);
        })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [screen, notifUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always read fresh plan from Supabase when entering the app screen
  useEffect(() => {
    if (screen !== 'app' || !supabase) return;
    const { userId } = getAuthToken();
    if (!userId) return;
    supabase.from('profiles').select('plan,premium_until').eq('id', userId).single()
      .then(({ data }) => {
        const effectivePlan = resolveEffectivePlan(data) || 'free';
        setPlan(effectivePlan);
        setPlanExpiresAt(effectivePlan === 'free' ? null : data?.premium_until || null);
        localStorage.setItem('ts_plan', effectivePlan);
      });
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = T[lang];

  useEffect(() => {
    function syncUserFromSession(session) {
      if (!session?.user) return;
      cacheAuthSession(session);
      const meta = session.user.user_metadata || {};
      localStorage.setItem('travelsoul_user', JSON.stringify({
        id: session.user.id, email: session.user.email,
        firstName: meta.firstName || '', lastName: meta.lastName || '',
        country: meta.country || '', lang: meta.lang || '',
        createdAt: session.user.created_at,
      }));
      if (meta.lang) { setLang(meta.lang); localStorage.setItem('travelsoul_lang', meta.lang); }
    }

    async function loadAppData(userId) {
      resetAdminUiState();
      setPlan('free');
      setPlanExpiresAt(null);
      setIsLoadingPlan(true);
      try {
        // Wait for access token to land in localStorage (cross-device verification timing)
        let _tok = (await getAuthTokenSafe()).accessToken;
        for (let _i = 0; _i < 5 && !_tok; _i++) {
          await new Promise(r => setTimeout(r, 400));
          try { await supabase.auth.refreshSession(); } catch(e) {}
          _tok = (await getAuthTokenSafe()).accessToken;
        }
        if (!_tok) {
          // Token missing after all retries — session is gone; sign out locally
          try { await supabase.auth.signOut({ scope: 'local' }); } catch(e) {}
          return { isOnboarded: false, needsAgeVerification: false, confirmedUnderage: false };
        }
        const rows = await sbFetch('GET', 'profiles', `id=eq.${userId}&select=*&limit=1`);
        const row = Array.isArray(rows) && rows[0];
        if (!row) {
          setTimeout(() => window.dispatchEvent(new Event('ts-spin-synced')), 0);
          return { isOnboarded: !!(localStorage.getItem('travelsoul_onboarded') === 'true'), needsAgeVerification: false, confirmedUnderage: false };
        }
        if (row.deleted_at) {
          if (supabase) await supabase.auth.signOut();
          safeRemoveTravelSoulData();
          return { isOnboarded: false, needsAgeVerification: false, confirmedUnderage: false };
        }
        if (row.is_blocked) {
          try { if (supabase) await supabase.auth.signOut({ scope: 'local' }); } catch(e) {}
          safeRemoveTravelSoulData();
          if (typeof window !== 'undefined') {
            window.__ts_blocked = true;
            alert(lang === 'bg' ? '🚫 Този акаунт е блокиран.' : lang === 'es' ? '🚫 Esta cuenta está bloqueada.' : lang === 'fr' ? '🚫 Ce compte est bloqué.' : lang === 'de' ? '🚫 Dieses Konto ist gesperrt.' : lang === 'el' ? '🚫 Αυτός ο λογαριασμός έχει αποκλειστεί.' : lang === 'it' ? '🚫 Questo account è stato bloccato.' : '🚫 This account has been blocked.');
          }
          return { isOnboarded: false, needsAgeVerification: false, confirmedUnderage: false };
        }
        // Age gate: missing birth_date (e.g. OAuth signup, which collects none) or a
        // birth_date that implies under-18 both require verification before the app loads.
        const rowConfirmedUnderage = !!row.birth_date && calcAge(row.birth_date) < 18;
        const rowNeedsAgeVerification = !row.birth_date || rowConfirmedUnderage;
        setNeedsAgeVerification(rowNeedsAgeVerification);
        setConfirmedUnderage(rowConfirmedUnderage);
        let promoGrant = null;
        try {
          promoGrant = await grantFirst1000FreePremium();
          if (promoGrant?.status === 'granted') {
            row.plan = promoGrant.plan || 'premium';
            row.premium_until = promoGrant.premium_until || row.premium_until;
            setShowPremiumBonus(true);
          }
        } catch (promoErr) {
          console.warn('[First1000Premium] grant check failed:', promoErr?.message || promoErr);
        }

        const effectivePlan = resolveEffectivePlan(row);
        const resolvedPlan = effectivePlan || 'free';
        console.log('LOAD APP DATA - effective plan:', resolvedPlan);
        setPlan(resolvedPlan);
        setPlanExpiresAt(resolvedPlan === 'free' ? null : row.premium_until || null);
        localStorage.setItem('ts_plan', resolvedPlan);
        if (row.coins != null) {
          setCoinBalance(row.coins);
          localStorage.setItem('travelsoul_coins', String(row.coins));
        }
        setStoredSpinLastDate(userId, row.spin_last_date ? String(row.spin_last_date).slice(0, 10) : null);
        // Always dispatch so Header re-reads ts_last_spin regardless of whether spin_last_date exists
        setTimeout(() => window.dispatchEvent(new Event('ts-spin-synced')), 0);
        if (row.likes_last_date != null && row.likes_today_count != null) {
          const today = new Date().toDateString();
          const date = new Date(row.likes_last_date).toDateString();
          const count = date === today ? row.likes_today_count : 0;
          localStorage.setItem('ts_likes_today', JSON.stringify({ date: today, count }));
        }
        if (row.anonymous_mode != null) {
          setIncognito(row.anonymous_mode);
          localStorage.setItem('ts_incognito', row.anonymous_mode ? '1' : '0');
        }
        if (row.photo_url) {
          localStorage.setItem('travelsoul_photo', getProfilePhotoUrl(row.photo_url, ''));
        }
        if (row.quiz_completed) {
          localStorage.setItem('travelsoul_onboarded', 'true');
        }
        if (Array.isArray(row.joined_trips)) {
          setJoinedTrips(row.joined_trips);
        }
        setIsAdmin(row.is_admin === true);
        setIsModerator(row.is_moderator === true);
        setIsLoadingPlan(false);
        return {
          isOnboarded: !!(row.quiz_completed || localStorage.getItem('travelsoul_onboarded') === 'true'),
          needsAgeVerification: rowNeedsAgeVerification,
          confirmedUnderage: rowConfirmedUnderage,
        };
      } catch {
        return { isOnboarded: !!(localStorage.getItem('travelsoul_onboarded') === 'true'), needsAgeVerification: false, confirmedUnderage: false };
      }
    }
    loadAppDataRef.current = loadAppData;
    syncUserFromSessionRef.current = syncUserFromSession;

    let mounted = true;

    // Detect successful account deletion redirect (?deleted=1)
    if (new URLSearchParams(window.location.search).get('deleted') === '1') {
      window.history.replaceState(null, '', window.location.pathname);
      setDeletedMsg(true);
      setTimeout(() => setDeletedMsg(false), 6000);
    }

    // Detect a completed coin purchase callback after the provider has credited the DB.
    if (new URLSearchParams(window.location.search).get('coins') === 'success') {
      window.history.replaceState(null, '', window.location.pathname);
      // Reload coin balance from DB (give webhook a moment to complete if needed)
      setTimeout(async () => {
        try {
          const { data: { session: cs } } = await supabase.auth.getSession();
          if (cs?.user?.id) {
            const { data } = await supabase.from('profiles').select('coins').eq('id', cs.user.id).maybeSingle();
            if (data?.coins != null) {
              setCoinBalance(data.coins);
              localStorage.setItem('travelsoul_coins', String(data.coins));
            }
          }
        } catch (_) {}
      }, 1500);
      alert(lang === 'bg' ? '✅ Монетите бяха добавени към баланса ти!' : lang === 'es' ? '✅ ¡Monedas añadidas a tu saldo!' : lang === 'fr' ? '✅ Pièces ajoutées à votre solde !' : lang === 'de' ? '✅ Münzen wurden Ihrem Guthaben hinzugefügt!' : lang === 'el' ? '✅ Τα νομίσματα προστέθηκαν στο υπόλοιπό σου!' : lang === 'it' ? '✅ Le monete sono state aggiunte al tuo saldo!' : '✅ Coins have been added to your balance!');
    }

    // Detect OAuth provider errors BEFORE hasOAuthToken — error_code= contains 'code='
    const _sp = new URLSearchParams(window.location.search);
    const _hp = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '');
    const _oauthErr     = _sp.get('error')             || _hp.get('error');
    const _oauthDesc    = _sp.get('error_description') || _hp.get('error_description');
    const _oauthErrCode = _sp.get('error_code')        || _hp.get('error_code'); // Supabase-specific (OTP/email errors)
    if ((_oauthErr || _oauthDesc) && !_oauthErrCode) {
      // Real OAuth provider error (Facebook/Google) — no error_code means it's not a Supabase/email error
      console.log('[oauth-error]', _oauthDesc || _oauthErr);
      window.history.replaceState(null, '', window.location.pathname);
      setOauthError(lang==='bg'?'Влизането с Facebook не успя. Моля, опитайте с имейл или Google.':lang==='es'?'El inicio de sesión con Facebook falló. Por favor, prueba con correo electrónico o Google.':lang==='fr'?'La connexion Facebook a échoué. Veuillez essayer avec e-mail ou Google.':lang==='de'?'Die Facebook-Anmeldung ist fehlgeschlagen. Bitte versuche es mit E-Mail oder Google.':lang==='el'?'Η σύνδεση μέσω Facebook απέτυχε. Δοκίμασε με email ή Google.':lang==='it'?'L\'accesso con Facebook non è riuscito. Prova con email o Google.':'Facebook login failed. Please try email or Google.');
      setScreen('landing');
      return;
    }
    if (_oauthErrCode) {
      // Supabase-specific error (e.g. expired email verification link) — clean URL, show landing quietly
      console.log('[supabase-error]', _oauthErrCode, _oauthDesc);
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Loading guard: always fires as a safety net. Longer delay for PKCE (?code=) to allow exchange to complete.
    // Cleared by SIGNED_IN handler on success; fires if exchange hangs silently (no error param).
    const hasOAuthToken = window.location.hash.includes('access_token') || window.location.search.includes('code=');
    const loadingGuard = setTimeout(() => {
      if (mounted) {
        setScreen(s => s === 'loading' ? 'landing' : s);
        if (hasOAuthToken) window.history.replaceState(null, '', window.location.pathname);
      }
    }, hasOAuthToken ? 15000 : 10000);

    // Public routes remain public even when a browser already has an authenticated session.
    const publicScreen = getPublicScreenForCurrentLocation();
    if (publicScreen && mounted) setScreen(publicScreen);
    const handleHashChange = () => {
      if (window.location.hash === '#privacy-policy') setScreen('privacy');
      if (window.location.hash === '#terms') setScreen('terms');
    };
    window.addEventListener('hashchange', handleHashChange);

    if (!supabase) {
      // Fallback: check localStorage when Supabase is not configured
      const saved = localStorage.getItem('travelsoul_user');
      if (mounted) {
        if (publicScreen) {
          setScreen(publicScreen);
        } else if (saved) {
          const onboarded = localStorage.getItem('travelsoul_onboarded');
          setScreen(onboarded === 'true' ? 'app' : 'onboarding');
        } else {
          setScreen('landing');
        }
      }
      return () => { mounted = false; clearTimeout(loadingGuard); window.removeEventListener('hashchange', handleHashChange); };
    }

    // getSession() handles both page-reload and implicit-flow tokens (#access_token in hash).
    // With implicit flow the token is processed synchronously during init, so the session
    // is already here when this callback fires — no code exchange needed.
    // WRAPPED: getSession() can hang on the Supabase auth lock; race it against a 6s timeout
    // that falls back to reading the token straight from localStorage (lock-free).
    const _getSessionSafe = async () => {
      // FAST PATH: token already in localStorage — use it immediately, no waiting.
      // getSession() reliably hangs on the auth lock here, so don't block the UI on it.
      const cached = getAuthToken();
      if (cached.accessToken && cached.userId) {
        // Refresh the Supabase client session quietly in the background (best-effort, non-blocking).
        Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve('__timeout__'), 8000)),
        ]).catch(() => {});
        return { data: { session: { access_token: cached.accessToken, user: { id: cached.userId } } } };
      }
      // SLOW PATH: no cached token (first login / logged out) — wait for getSession with a short cap.
      try {
        const race = await Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve('__timeout__'), 4000)),
        ]);
        if (race === '__timeout__') {
          const { accessToken, userId } = getAuthToken();
          if (accessToken && userId) return { data: { session: { access_token: accessToken, user: { id: userId } } } };
          return { data: { session: null } };
        }
        return race;
      } catch (e) {
        const { accessToken, userId } = getAuthToken();
        if (accessToken && userId) return { data: { session: { access_token: accessToken, user: { id: userId } } } };
        return { data: { session: null } };
      }
    };
    _getSessionSafe().then(async ({ data: { session } }) => {
      if (!mounted) return;
      cacheAuthSession(session);
      if (hasOAuthToken && !session?.user) return; // token not yet parsed — onAuthStateChange will handle
      if (hasOAuthToken) window.history.replaceState(null, '', window.location.pathname);
      if (session?.user) {
        resetAdminUiState();
        syncUserFromSession(session);
        setAuthProvider(getAuthProvider(session));
        // For verification/OAuth links: ensure profile exists before loading data.
        // SIGNED_IN handler may not fire for implicit flow (session already set during init).
        if (hasOAuthToken) {
          const { data: existingProfile } = await supabase.from('profiles').select('id').eq('id', session.user.id).single();
          if (!existingProfile) {
            let pendingProfile = {};
            try { pendingProfile = JSON.parse(localStorage.getItem('ts_pending_profile') || '{}'); } catch(e) {}
            await insertProfileWithRetry(supabase, {
              id: session.user.id,
              name: pendingProfile.name || session.user.user_metadata?.full_name || session.user.user_metadata?.name ||
                    (session.user.user_metadata?.firstName ? `${session.user.user_metadata.firstName} ${session.user.user_metadata.lastName || ''}`.trim() : null) ||
                    'Traveler',
              photo_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null,
              country: pendingProfile.country || 'BG',
              gender: pendingProfile.gender || 'male',
              birth_date: pendingProfile?.birthDate || null,
              created_at: new Date().toISOString()
            });
            localStorage.removeItem('ts_pending_profile');
          }
        }
        const appDataResult = await loadAppData(session.user.id);
        if (mounted) {
          setScreen(getPublicScreenForCurrentLocation() || (appDataResult.needsAgeVerification ? 'ageVerification' : (appDataResult.isOnboarded ? 'app' : 'onboarding')));
          clearTimeout(loadingGuard);
          const _pp = new URLSearchParams(window.location.search);
          if (_pp.get('payment') === 'success') {
            window.history.replaceState({}, document.title, window.location.pathname);
            (async () => {
              console.log('PAYMENT SUCCESS HANDLER START');
              const { data: { session: freshSession } } = await supabase.auth.getSession();
              devLog('Session user id:', freshSession?.user?.id);
              if (!freshSession?.user) return;
              alert(lang==='bg'?'🎉 Абонаментът е активиран успешно!':lang==='es'?'🎉 ¡Suscripción activada correctamente!':lang==='fr'?'🎉 Abonnement activé avec succès !':lang==='de'?'🎉 Abonnement erfolgreich aktiviert!':lang==='el'?'🎉 Η συνδρομή ενεργοποιήθηκε με επιτυχία!':lang==='it'?'🎉 Abbonamento attivato con successo!':'🎉 Subscription activated successfully!');
              const waitForPlanUpdate = async (user, expectedNotPlan = 'free', maxAttempts = 5) => {
                for (let i = 0; i < maxAttempts; i++) {
                  await new Promise(r => setTimeout(r, 2000));
                  const { data: profile } = await supabase
                    .from('profiles')
                    .select('plan,premium_until')
                    .eq('id', user.id)
                    .single();
                  const effectivePlan = resolveEffectivePlan(profile);
                  console.log(`Plan check attempt ${i + 1}:`, effectivePlan);
                  if (effectivePlan && effectivePlan !== expectedNotPlan) {
                    return { plan: effectivePlan, premiumUntil: profile.premium_until };
                  }
                }
                return null;
              };
              const updatedEntitlement = await waitForPlanUpdate(freshSession.user);
              if (updatedEntitlement) {
                console.log('Plan updated to:', updatedEntitlement.plan);
                setPlan(updatedEntitlement.plan);
                setPlanExpiresAt(updatedEntitlement.premiumUntil);
                localStorage.setItem('ts_plan', updatedEntitlement.plan);
              }
            })();
          }
        }
      } else {
        if (mounted) { resetAdminUiState(); setScreen(s => s === 'loading' ? 'landing' : s); clearTimeout(loadingGuard); }
      }
    });

    const runDeferredSignedInWork = (session) => {
      setTimeout(async () => {
        if (!mounted || !session?.user) return;
        try {
          await new Promise(r => setTimeout(r, 300));
          // Ensure profile exists BEFORE loadAppData so state is populated on first render
          const user = session.user;
          const { data: existingProfile } = await supabase.from('profiles').select('id').eq('id', user.id).single();
          if (!existingProfile) {
            let pendingProfile = {};
            try { pendingProfile = JSON.parse(localStorage.getItem('ts_pending_profile') || '{}'); } catch(e) {}
            await insertProfileWithRetry(supabase, {
              id: user.id,
              name: pendingProfile.name || user.user_metadata?.full_name || user.user_metadata?.name ||
                    (user.user_metadata?.firstName ? `${user.user_metadata.firstName} ${user.user_metadata.lastName || ''}`.trim() : null) ||
                    'Traveler',
              photo_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
              country: pendingProfile.country || 'BG',
              gender: pendingProfile.gender || 'male',
              birth_date: pendingProfile?.birthDate || null,
              created_at: new Date().toISOString()
            });
            localStorage.removeItem('ts_pending_profile');
          }
          const appDataResult = await loadAppData(session.user.id);
          if (!mounted) return;
          setScreen(getPublicScreenForCurrentLocation() || (appDataResult.needsAgeVerification ? 'ageVerification' : (appDataResult.isOnboarded ? 'app' : 'onboarding')));
          if (appDataResult.isOnboarded && !appDataResult.needsAgeVerification) {
            const provider = getAuthProvider(session);
            const displayName = session.user.user_metadata?.full_name || session.user.user_metadata?.name || 'Traveler';
            setAuthProvider(provider);
            const bannerKey = `provider_banner_shown_${session.user.id}`;
            if (!sessionStorage.getItem(bannerKey)) {
              sessionStorage.setItem(bannerKey, 'true');
              setLoginToast({ name: displayName });
            }
          }
          clearTimeout(loadingGuard);
        } catch (err) {
          console.error('Deferred auth work failed:', err);
        }
      }, 0);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN' && session?.user) {
        resetAdminUiState();
        clearTimeout(loadingGuard);
        if (!session.user.email_confirmed_at) {
          setScreen('emailVerification');
          if (session.user.email) setPendingVerificationEmail(session.user.email);
          return;
        }
        syncUserFromSession(session);
        // Clean OAuth code/token from URL
        if (window.location.hash.includes('access_token') || window.location.search.includes('code=')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        runDeferredSignedInWork(session);
      }
      if (event === 'SIGNED_OUT') {
        resetAdminUiState();
        clearAuthTokenCache();
        // Use the same cleanup as the main logout() path (audit finding:
        // this handler used to only remove 'travelsoul_user', leaving stale
        // coins/likes/onboarding flags behind for whoever signs in next on
        // this browser).
        safeRemoveTravelSoulData();
        sessionStorage.removeItem('ts_oauth_provider');
        Object.keys(sessionStorage).filter(k => k.startsWith('provider_banner_shown_')).forEach(k => sessionStorage.removeItem(k));
        if (mounted) setScreen('landing');
      }
    });

    // Global 401 handler: fires when sbFetch receives 401 from any API call.
    // Checks live session — if truly gone, sign out locally and redirect to landing.
    const handleAuth401 = async () => {
      if (!mounted) return;
      const liveToken = await getAuthTokenSafe();
      if (liveToken.accessToken && liveToken.userId) return;
      // Synchronous expiry check — avoids hanging getSession() on locked localStorage
      const sbKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (sbKey) {
        try {
          const parsed = JSON.parse(localStorage.getItem(sbKey) || '{}');
          const exp = parsed?.expires_at;
          if (exp && exp * 1000 > Date.now()) return; // token still valid — transient 401, ignore
        } catch(e) {}
      }
      try { await supabase.auth.signOut({ scope: 'local' }); } catch(e) {}
      resetAdminUiState();
      localStorage.removeItem('travelsoul_user');
      if (mounted) {
        setScreen('landing');
        setSessionExpiredMsg(true);
        setTimeout(() => setSessionExpiredMsg(false), 5000);
      }
    };
    window.addEventListener('ts-401', handleAuth401);

    return () => {
      mounted = false;
      clearTimeout(loadingGuard);
      subscription?.unsubscribe();
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('ts-401', handleAuth401);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ask for notifications after 3s in app
  useEffect(() => {
    const { userId } = getAuthToken();
    devLog('[push-client] Push useEffect triggered, screen:', screen, 'userId:', userId);
    devLog('[push-client] Notification API available:', typeof Notification !== 'undefined');
    if (typeof Notification !== 'undefined') {
      devLog('[push-client] Notification.permission:', Notification.permission);
    }
    if (screen !== 'app') { devLog('[push-client] Exit: screen is not app'); return; }
    if (typeof Notification === 'undefined') { devLog('[push-client] Exit: Notification API not available'); return; }
    if (Notification.permission === 'granted') {
      devLog('[push-client] Permission already granted — calling registerPush() directly');
      registerPush();
      return;
    }
    if (Notification.permission === 'denied') {
      devLog('[push-client] Permission denied — cannot request again');
      return;
    }
    devLog('[push-client] Permission default — scheduling notification modal in 3s...');
    const id = setTimeout(() => setShowNotifModal(true), 3000);
    return () => clearTimeout(id);
  }, [screen]);

  function handlePublicNavigate(nextScreen) {
    const paths = { landing:'/', privacy:'/privacy', terms:'/terms', refund:'/refund', accountDeletion:'/account-deletion', childSafety:'/child-safety', pricing:'/pricing', contact:'/contact' };
    const path = paths[nextScreen] || '/';
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
    setScreen(nextScreen);
  }


  function handleSetLang(l) {
    setLang(l);
    localStorage.setItem('travelsoul_lang', l);
  }

  function openPricingScreen(fromScreen = screen) {
    const { userId, accessToken } = getAuthToken();
    pricingBackScreenRef.current = userId && accessToken && fromScreen && !['landing','login','register','pricing'].includes(fromScreen)
      ? fromScreen
      : null;
    setScreen('pricing');
  }

  function handlePricingBack() {
    const { userId, accessToken } = getAuthToken();
    if (userId && accessToken) {
      const previous = pricingBackScreenRef.current;
      pricingBackScreenRef.current = null;
      setScreen(previous || 'app');
      return;
    }
    pricingBackScreenRef.current = null;
    handlePublicNavigate('landing');
  }

  async function reloadBillingEntitlement() {
    if (!supabase) throw new Error('billing_auth_required');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) throw new Error('billing_auth_required');
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('plan,premium_until')
      .eq('id', session.user.id)
      .single();
    if (error || !profile) throw new Error('billing_entitlement_reload_failed');
    const effectivePlan = resolveEffectivePlan(profile) || 'free';
    setPlan(effectivePlan);
    setPlanExpiresAt(effectivePlan === 'free' ? null : profile.premium_until || null);
    localStorage.setItem('ts_plan', effectivePlan);
    return effectivePlan;
  }

  function handleUpgrade() {
    openPricingScreen();
  }

  function handleNeedPremium(feature) { setPremiumModal(feature); }

  function afterAuth() {
    setPlan('free');
    setPlanExpiresAt(null);
    setIsLoadingPlan(true);
    const onboarded = localStorage.getItem('travelsoul_onboarded');
    const permState = typeof Notification !== 'undefined' ? Notification.permission : 'unavailable';
    devLog('[push-client] afterAuth — Notification.permission:', permState);
    if (permState === 'granted') {
      devLog('[push-client] afterAuth — permission already granted, calling registerPush()');
      registerPush();
    } else {
      devLog('[push-client] afterAuth — permission NOT granted, skipping registerPush (modal will handle it)');
    }
    setScreen(onboarded === 'true' ? 'app' : 'onboarding');
  }

  if (screen === 'loading') {
    return (
      <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'#0d1117',gap:24}}>
        <style>{`
          @keyframes ts-spin { to { transform: rotate(360deg); } }
          @keyframes ts-pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        `}</style>
        <div style={{position:'relative',width:64,height:64}}>
          <div style={{position:'absolute',inset:0,borderRadius:'50%',border:'3px solid rgba(201,168,76,0.15)'}} />
          <div style={{position:'absolute',inset:0,borderRadius:'50%',border:'3px solid transparent',borderTopColor:'#c9a84c',animation:'ts-spin 0.9s linear infinite'}} />
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26}}>✈️</div>
        </div>
        <div style={{color:'rgba(201,168,76,0.7)',fontSize:13,letterSpacing:'0.08em',animation:'ts-pulse 1.8s ease-in-out infinite'}}>
          TravelSoul
        </div>
      </div>
    );
  }

  return (
    <>
      {oauthError && screen === 'landing' && (
        <div style={{position:'fixed',top:0,left:0,right:0,zIndex:99999,background:'#c0392b',color:'#fff',padding:'12px 20px',textAlign:'center',fontSize:'0.88rem',display:'flex',alignItems:'center',justifyContent:'center',gap:12}}>
          <span>{oauthError}</span>
          <button onClick={()=>setOauthError(null)} style={{background:'none',border:'1px solid rgba(255,255,255,0.5)',borderRadius:6,color:'#fff',padding:'2px 8px',cursor:'pointer',fontSize:'0.8rem',flexShrink:0}}>✕</button>
        </div>
      )}
      {deletedMsg && screen === 'landing' && (
        <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:99999,background:'#1b3a2b',border:'1px solid #2ea043',borderRadius:10,color:'#3fb950',padding:'12px 22px',fontSize:'0.9rem',fontWeight:600,boxShadow:'0 4px 20px rgba(0,0,0,0.4)',whiteSpace:'nowrap'}}>
          &#10003; {lang==='bg'?'Акаунтът е изтрит успешно!':lang==='es'?'¡Cuenta eliminada con éxito!':lang==='fr'?'Compte supprimé avec succès !':lang==='de'?'Konto erfolgreich gelöscht!':lang==='el'?'Ο λογαριασμός διαγράφηκε με επιτυχία!':lang==='it'?'Account eliminato con successo!':'Account deleted successfully!'}
        </div>
      )}
      {sessionExpiredMsg && screen === 'landing' && (
        <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:99999,background:'#2a1a1a',border:'1px solid #e05555',borderRadius:10,color:'#e05555',padding:'12px 22px',fontSize:'0.9rem',fontWeight:600,boxShadow:'0 4px 20px rgba(0,0,0,0.4)',whiteSpace:'nowrap'}}>
          ⚠️ {lang==='bg'?'Сесията изтече — влез отново.':lang==='es'?'Sesión expirada — inicia sesión.':lang==='fr'?'Session expirée — reconnecte-toi.':lang==='de'?'Sitzung abgelaufen — bitte erneut anmelden.':lang==='el'?'Η συνεδρία έληξε — παρακαλώ συνδέσου ξανά.':lang==='it'?'Sessione scaduta — effettua nuovamente l\'accesso.':'Session expired — please log in again.'}
        </div>
      )}
      {loginToast && (() => {
        const p = authProvider;
        const pl = providerLabel[p] || providerLabel.email;
        const pi = providerIcon[p] || '✉️';
        const msg = lang==='bg' ? `Влязъл си с ${pi} ${pl.bg}. Продължи с този акаунт?`
          : lang==='es' ? `Sesión con ${pi} ${pl.es}. ¿Continuar con esta cuenta?`
          : lang==='fr' ? `Connecté avec ${pi} ${pl.fr}. Continuer avec ce compte?`
          : lang==='de' ? `Angemeldet mit ${pi} ${pl.de}. Mit diesem Konto fortfahren?`
          : lang==='el' ? `Συνδέθηκες με ${pi} ${pl.el}. Συνέχεια με αυτόν τον λογαριασμό;`
          : lang==='it' ? `Accesso con ${pi} ${pl.it}. Continuare con questo account?`
          : `Signed in with ${pi} ${pl.en}. Continue with this account?`;
        return (
          <div style={{position:'fixed',top:16,left:'50%',transform:'translateX(-50%)',zIndex:99999,background:'#1a1a2e',border:'1.5px solid #c9a84c',borderRadius:12,padding:'12px 18px',display:'flex',flexDirection:'column',gap:10,boxShadow:'0 4px 24px rgba(0,0,0,0.5)',minWidth:260,maxWidth:360}}>
            <span style={{color:'#f0e6c0',fontSize:'0.87rem'}}>{msg}</span>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={async()=>{ setLoginToast(null); resetAdminUiState(); if(supabase) await supabase.auth.signOut(); setScreen('landing'); }} style={{background:'none',border:'1px solid #c9a84c',borderRadius:6,color:'#c9a84c',padding:'4px 12px',cursor:'pointer',fontSize:'0.8rem'}}>{lang==='bg' ? 'Изход' : lang==='es' ? 'Salir' : lang==='fr' ? 'Déconnexion' : lang==='de' ? 'Abmelden' : lang==='el' ? 'Αποσύνδεση' : lang==='it' ? 'Esci' : 'Logout'}</button>
              <button onClick={()=>setLoginToast(null)} style={{background:'#c9a84c',border:'none',borderRadius:6,color:'#1a1a2e',padding:'4px 12px',cursor:'pointer',fontSize:'0.8rem',fontWeight:600}}>{lang==='bg' ? 'Продължи' : lang==='es' ? 'Continuar' : lang==='fr' ? 'Continuer' : lang==='de' ? 'Weiter' : lang==='el' ? 'Συνέχεια' : lang==='it' ? 'Continua' : 'Continue'}</button>
            </div>
          </div>
        );
      })()}
      {/* Audit finding (4th audit): these 4 pre-auth screens had no
          TabErrorBoundary, unlike onboarding/admin/mod/main tabs — a render
          exception here (e.g. a malformed translation object) crashed to the
          single top-level ErrorBoundary, blanking the whole app for a user
          who isn't even logged in yet. */}
      {screen==='landing'   && <TabErrorBoundary lang={lang} resetKey="landing"><LandingScreen   t={t} lang={lang} setLang={handleSetLang} onStart={()=>setScreen('register')} onLogin={()=>setScreen('login')} onNavigate={handlePublicNavigate} /></TabErrorBoundary>}
      {screen==='register'  && <TabErrorBoundary lang={lang} resetKey="register"><RegisterScreen  t={t} lang={lang} setLang={handleSetLang} onComplete={afterAuth} onGoLogin={()=>setScreen('login')} onVerificationRequired={(email)=>{ setPendingVerificationEmail(email); setScreen('emailVerification'); }} onTerms={()=>setScreen('terms')} onPrivacy={()=>setScreen('privacy')} /></TabErrorBoundary>}
      {screen==='login'     && <TabErrorBoundary lang={lang} resetKey="login"><LoginScreen     t={t} lang={lang} setLang={handleSetLang} onComplete={afterAuth} onGoRegister={()=>setScreen('register')} /></TabErrorBoundary>}
      {screen==='emailVerification' && <TabErrorBoundary lang={lang} resetKey="emailVerification"><EmailVerificationScreen email={pendingVerificationEmail} lang={lang} onBack={()=>setScreen('login')} /></TabErrorBoundary>}
      {screen==='ageVerification' && <AgeVerificationGate t={t} lang={lang} confirmedUnderage={confirmedUnderage} onVerified={()=>{ setNeedsAgeVerification(false); setConfirmedUnderage(false); setScreen(localStorage.getItem('travelsoul_onboarded') === 'true' ? 'app' : 'onboarding'); }} onSignOut={async()=>{ if (supabase) await supabase.auth.signOut(); }} />}
      {screen==='onboarding'&& <TabErrorBoundary lang={lang} resetKey="onboarding"><OnboardingScreen t={t} lang={lang} onComplete={()=>setScreen('app')} /></TabErrorBoundary>}
      {screen==='pricing'   && <PricingPage     lang={lang} setLang={handleSetLang} t={t} onBack={handlePricingBack} onNavigate={handlePublicNavigate} onEntitlementVerified={reloadBillingEntitlement} />}
      {screen==='profile'   && <ProfileScreen   t={t} lang={lang} onBack={()=>setScreen('app')} joinedTrips={joinedTrips} plan={plan} />}
      {screen==='privacy'   && <PrivacyScreen   lang={lang} setLang={handleSetLang} t={t} onBack={()=>handlePublicNavigate('landing')} onNavigate={handlePublicNavigate} />}
      {screen==='terms'     && <TermsScreen     lang={lang} setLang={handleSetLang} t={t} onBack={()=>handlePublicNavigate('landing')} onNavigate={handlePublicNavigate} />}
      {screen==='refund'    && <RefundScreen    lang={lang} setLang={handleSetLang} t={t} onBack={()=>handlePublicNavigate('landing')} onNavigate={handlePublicNavigate} />}
      {screen==='accountDeletion' && <AccountDeletionScreen lang={lang} setLang={handleSetLang} t={t} onBack={()=>handlePublicNavigate('landing')} onNavigate={handlePublicNavigate} />}
      {screen==='childSafety' && <ChildSafetyScreen lang={lang} setLang={handleSetLang} t={t} onBack={()=>handlePublicNavigate('landing')} onNavigate={handlePublicNavigate} />}
      {screen==='contact'   && <ContactScreen   lang={lang} setLang={handleSetLang} t={t} onBack={()=>handlePublicNavigate('landing')} onNavigate={handlePublicNavigate} />}
      {screen==='app'       && <MainApp         t={t} lang={lang} setLang={handleSetLang} plan={plan} onNeedPremium={handleNeedPremium} onUpgrade={handleUpgrade} incognito={incognito} setIncognito={setIncognito} onLogout={()=>{ resetAdminUiState(); setPlan('free'); setPlanExpiresAt(null); setIsLoadingPlan(true); setScreen('landing'); }} onProfile={()=>setScreen('profile')} notifications={notifications} onClearNotifications={async()=>{ const { userId } = getAuthToken(); if (userId && supabase) await supabase.from('profiles').update({ notifications_cleared_at: new Date().toISOString() }).eq('id', userId); setNotifications([]); }} coinBalance={coinBalance} setCoinBalance={setCoinBalance} joinedTrips={joinedTrips} setJoinedTrips={setJoinedTrips} isAdmin={isAdmin} onAdmin={()=>{ if (isAdmin) setShowAdmin(true); }} isModerator={isModerator} onMod={()=>{ if (isModerator) setShowMod(true); }} />}
      {showAdmin && isAdmin && <TabErrorBoundary lang={lang} resetKey="admin"><AdminPanelModal lang={lang} onClose={()=>setShowAdmin(false)} onAccessLost={resetAdminUiState} isAdmin={isAdmin} /></TabErrorBoundary>}
      {showMod && isModerator && <TabErrorBoundary lang={lang} resetKey="mod"><ModeratorPanelModal lang={lang} onClose={()=>setShowMod(false)} /></TabErrorBoundary>}
      {premiumModal && !isLoadingPlan && <PremiumModal feature={premiumModal} t={t} lang={lang} onClose={()=>setPremiumModal(null)} onUpgrade={()=>{ setPremiumModal(null); openPricingScreen(); }} />}
      {showNotifModal && <NotificationPermissionModal t={t} onAllow={()=>{ registerPush(); setShowNotifModal(false); }} onLater={()=>setShowNotifModal(false)} />}
      {showPremiumBonus && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.88)',zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'#0d1117',borderRadius:20,padding:'32px 24px',maxWidth:380,width:'100%',textAlign:'center',border:'2px solid #c9a84c',boxShadow:'0 0 40px rgba(201,168,76,0.3)'}}>
            <div style={{fontSize:'3.5rem',marginBottom:12}}>🎉</div>
            <h2 style={{color:'var(--gold,#c9a84c)',fontFamily:"'Playfair Display',serif",marginBottom:12,fontSize:'1.4rem'}}>Поздравления!</h2>
            <p style={{color:'#f0f6fc',marginBottom:8,lineHeight:1.6}}>Ти си сред първите <b>{FIRST_1000_FREE_PREMIUM_LIMIT}</b> потребители на TravelSoul!</p>
            <p style={{color:'#f0f6fc',marginBottom:24,lineHeight:1.6}}>Получаваш <b style={{color:'var(--gold,#c9a84c)'}}>{FIRST_1000_FREE_PREMIUM_DAYS} дни Premium</b> напълно безплатно! 🎁</p>
            <button className="ts-btn-gold-full" onClick={()=>setShowPremiumBonus(false)}>🚀 Започни да изследваш!</button>
          </div>
        </div>
      )}
      <CookieBanner lang={lang} onOpenPrivacy={() => setScreen('privacy')} />
    </>
  );
}
